/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ The string catalogue. HU is primary; EN is written alongside it.           │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * `decisions.md` R-6: *"HU is the primary locale, EN written alongside from day one."* CLAUDE.md
 * rule 7 makes it stronger — *"HU and EN complete before a task is done — including toast text,
 * validation messages, empty states, `aria-label`s and page titles."*
 *
 * ⚠️ **A plain typed object, not an i18n library.** Nothing here needs runtime plural rules,
 * message compilation or lazy locale loading, and a library would add all three plus a bundle. If
 * a later chunk needs ICU plurals, that is the moment to reach for one — recorded as an addition
 * in `decisions.md` rather than assumed here.
 *
 * ⚠️ **THE TWO LOCALES ARE ONE TYPE, so a missing key is a TYPE ERROR.** `EN` is declared
 * `Catalogue`, which is derived from `HU`. A forgotten English string fails the typecheck instead
 * of rendering a Hungarian sentence to an English reader — which is the failure mode a `Record<
 * string, string>` on both sides would allow, and the one that actually ships.
 *
 * ⚠️ **HUNGARIAN INFLECTS BY VOWEL HARMONY, so a suffix is never glued to a placeholder.**
 * `{name}-től` is wrong for half of all names. Every message below is phrased so the placeholder
 * lands where no suffix follows it, or the sentence is rewritten. This is not a preference; it is
 * the reason `packages/ui` takes strings as props.
 */

import { HU as DEALS_BASE_HU, EN as DEALS_BASE_EN } from './deals/base';
import { HU as FX_HU, EN as FX_EN } from './fx';
import { HU as DURATION_HU, EN as DURATION_EN } from './duration';
import { HU as DEALS_SCENARIO_HU, EN as DEALS_SCENARIO_EN } from './deals/scenario';
import { HU as DEALS_SETTINGS_HU, EN as DEALS_SETTINGS_EN } from './deals/settings';
import { HU as DEALS_BOARD_HU, EN as DEALS_BOARD_EN } from './deals/board';
import { HU as DEALS_RECORD_HU, EN as DEALS_RECORD_EN } from './deals/record';
import { HU as DEALS_PLAN_HU, EN as DEALS_PLAN_EN } from './deals/plan';
import { HU as DEALS_GENERATE_HU, EN as DEALS_GENERATE_EN } from './deals/generate';

export const HU = {
  /* ── the shell ─────────────────────────────────────────────────────────────────────────── */
  'app.name': 'Forecastify',
  'nav.settings': 'Beállítások',
  'nav.permissions': 'Jogosultságok',
  'nav.skipToContent': 'Ugrás a tartalomra',
  'nav.collapseSidebar': 'Oldalsáv összecsukása',
  'nav.expandSidebar': 'Oldalsáv kinyitása',

  /* ── page titles. Rule 7 names these explicitly, and a browser tab is user-facing. ────── */
  'title.permissions': 'Jogosultságok · Beállítások',

  /* ── the Jogosultságok screen ──────────────────────────────────────────────────────────── */
  'permissions.heading': 'Jogosultságok',
  'permissions.levels.title': 'Szintek',
  'permissions.level.missing': 'Ez a jogosultsági szint már nem létezik.',
  /* Two counts in one line, and neither takes a suffix. */
  'permissions.levels.note': '{levels} szint · {actions} jogosultság',
  'permissions.levels.new': 'Új szint',
  'permissions.levels.newHint':
    'Üresen jön létre, vagy egy meglévő szint másolataként. Utána állítod be, mihez érhet hozzá.',
  'permissions.levels.create': 'Szint létrehozása',
  'permissions.levels.name': 'Megnevezés',
  'permissions.levels.namePlaceholder': 'Pl. Projektvezető',
  'permissions.levels.copyFrom': 'Másolás innen',
  'permissions.levels.copyNote':
    'A jogosultságait átmásoljuk. Ezután már csak ezt a szintet érinti, ha módosítod.',
  'permissions.levels.copyNone': 'Üresen indul',
  'permissions.levels.delete': 'Szint törlése',
  'permissions.levels.open': 'Megnyitás',
  'permissions.levels.emptyBody': 'Nincs jogosultsági szint.',
  'permissions.levels.deleteHeld': '{count} dolgozónak ez a szintje – nem törölhető',
  'permissions.levels.error.taken': 'Ilyen nevű szint már van.',
  'permissions.levels.error.inUse': 'Még van dolgozó ezen a szinten.',
  'permissions.levels.actions': 'Szint műveletei',
  'permissions.levels.builtin': 'beépített',
  'permissions.levels.granted': '{granted} / {total}',
  'permissions.levels.members': '{count} dolgozó',
  'permissions.detail.title': '{name}',
  'permissions.detail.note': '{granted} jogosultság bekapcsolva · ennyi dolgozó szintje: {members}',
  'permissions.detail.empty': 'Ez a szint még semmit nem engedélyez.',

  /* ── the scope ladder. R-4's whole point: a checkbox cannot say "which rows". ──────────── */
  'permissions.scope.label': 'Hatókör: {action}',
  'permissions.scope.none': 'Nincs',
  'permissions.scope.own': 'Saját',
  'permissions.scope.workspace': 'Teljes',
  'permissions.scope.none.hint': 'Nem végezheti el.',
  'permissions.scope.own.hint': 'Csak a hozzá tartozó sorokon.',
  'permissions.scope.workspace.hint': 'A munkaterület minden során.',
  'permissions.scope.project.own': 'Saját projektek',
  'permissions.scope.project.workspace': 'Minden projekt',
  'permissions.scope.project.own.hint': 'Csak azokon a projekteken, amelyeknek tagja.',
  'permissions.scope.project.workspace.hint': 'A munkaterület minden projektjén.',

  /* ── field rules ───────────────────────────────────────────────────────────────────────── */
  'permissions.fields.title': 'Mezőszabályok',
  'permissions.fields.note':
    'Egy mező elrejtése erősebb, mint az olvasható: a rejtett mező meg sem jelenik.',
  'permissions.fields.hidden': 'Rejtett',
  'permissions.fields.read': 'Olvasható',
  'permissions.fields.write': 'Írható',
  'permissions.fields.empty': 'Nincs mezőszabály: minden mező írható.',
  'permissions.fields.label': 'Hozzáférés: {field}',

  /* ── the resource groups, in the mockup's own order and wording ─────────────────────────── */
  'permissions.group.task': 'Feladatok',
  'permissions.group.worklog': 'Időlogok',
  'permissions.group.project': 'Projektek és ügyfelek',
  'permissions.group.contract': 'Szerződések',
  'permissions.group.people': 'Dolgozók',
  'permissions.group.leave': 'Szabadság',
  'permissions.group.report': 'Riportok',
  'permissions.group.settings': 'Beállítások',
  'permissions.group.menu': 'Munkaterület menü',
  'permissions.group.dashboard': 'Dashboardok',

  /* ── the actions, verbatim from the mockup's PERM_CATALOG where it has a label ──────────── */
  /* ⚠️ Added, not in the mockup — `decisions.md` records it as an addition. */
  /* ⚠️ Added, as above. */

  /* ── saving ────────────────────────────────────────────────────────────────────────────── */
  'permissions.save': 'Mentés',
  'permissions.saving': 'Mentés…',
  'permissions.discard': 'Elvetés',
  'permissions.saved': 'A szint mentve',
  'permissions.unsaved': 'Nem mentett módosítás',

  /* ── refusals. One per code the server can answer with; clients branch on the code. ────── */
  'error.not_found': 'Ez az elem már nem létezik.',
  'error.permission_denied': 'Ehhez nincs jogosultságod.',
  'error.conflict': 'Valaki más közben módosította. Töltsd újra, majd próbáld ismét.',
  'error.conflict.reload': 'Újratöltés',
  'error.validation_failed': 'A mentés nem sikerült, mert a küldött adat érvénytelen.',
  'error.unauthenticated': 'Lejárt a bejelentkezés. Jelentkezz be újra.',
  'error.unknown': 'Váratlan hiba történt.',
  'error.requestId': 'Hibaazonosító: {id}',

  /* ── the shared states, per state-rules.md ─────────────────────────────────────────────── */
  'state.loading': 'Betöltés…',
  /* ── C17: meghívók ─────────────────────────────────────────────────────────────────────────── */

  'invite.heading': 'Meghívók',
  'invite.count': '{count} folyamatban',
  'invite.empty': 'Nincs kiküldött meghívó.',
  'invite.action': 'Meghívás',
  'invite.by': 'meghívta: {name}',
  'invite.resend': 'Újraküldés',
  'invite.revoke': 'Visszavonás',
  'invite.send': 'Meghívó küldése',

  'invite.state.pending': 'Kiküldve',
  'invite.state.accepted': 'Elfogadva',
  'invite.state.revoked': 'Visszavonva',
  'invite.state.expired': 'Lejárt',

  'invite.dialog.title': 'Munkatárs meghívása',
  'invite.dialog.note': 'E-mailt küldünk a linkkel. A meghívó 14 napig érvényes.',
  'invite.field.email': 'E-mail-cím',
  'invite.field.role': 'Szerepkör',
  'invite.field.pickRole': 'Válassz szerepkört…',
  /* ⚠️ Azt mondja meg, ami a szerver szabálya: a szerepkör a meghíváskor dől el. */
  'invite.error.alreadyMember': 'Ez a cím már tagja a workspace-nek.',
  'invite.error.alreadyInvited': 'Erre a címre már van élő meghívó. Küldd újra, vagy vond vissza.',
  'invite.error.email': 'Ez nem érvényes e-mail-cím.',
  'invite.error.seatLimit':
    'A csomag minden helye betelt – a még el nem fogadott meghívók is helyet foglalnak. Vonj vissza egy régit, vagy kérj több helyet.',
  'people.error.seatLimit':
    'Nincs szabad hely a csomagban, az újraaktiválás pedig helyet foglal. Vonj vissza egy meghívót, vagy kérj több helyet.',

  /* ── a meghívó másik vége (/invite/:token) ─────────────────────────────────────────────────── */

  'invite.accept.title': 'Meghívó',
  'invite.accept.lead': '{inviter} meghívott a {workspace} workspace-be.',
  'invite.accept.leadNoInviter': 'Meghívást kaptál a {workspace} workspace-be.',
  'invite.accept.role': 'Szerepkör: {role}',
  'invite.accept.join': 'Belépés',
  'invite.accept.signIn': 'Bejelentkezés',
  /* ⚠️⚠️ A cím számít: a szerver csak azt az e-mail-címet fogadja el, amire a meghívó szól. */
  'invite.accept.needAccount':
    'Jelentkezz be – vagy regisztrálj – a fenti e-mail-címmel, aztán térj vissza erre a linkre.',
  'invite.accept.wrongAccount':
    'Ez a meghívó a fenti e-mail-címre szól, te viszont másikkal vagy bejelentkezve. Jelentkezz be azzal a címmel, vagy kérj új meghívót.',
  'invite.accept.expired': 'Ez a meghívó lejárt. Kérj újat.',
  'invite.accept.revoked': 'Ezt a meghívót visszavonták.',
  'invite.accept.used': 'Ezt a meghívót már felhasználták.',
  'invite.accept.unknown': 'Ez a link nem érvényes. Kérj új meghívót.',
  'invite.claim.lead': 'Állíts be egy jelszót, és már bent is vagy.',
  'invite.claim.password': 'Jelszó',
  'invite.claim.confirm': 'Jelszó megerősítése',
  'invite.claim.action': 'Belépés a workspace-be',
  'invite.claim.haveAccount': 'Már van fiókom – bejelentkezés',
  /* ⚠️⚠️ Nem elrejtett hiba: a meghívót arra a címre küldtük, tehát a hívó bizonyítottan a címhez
   * tartozik — így elmondható neki, hogy már van fiókja. */
  'invite.claim.already':
    'Ehhez a címhez már tartozik fiók. Jelentkezz be, aztán nyisd meg újra ezt a linket.',
  'invite.claim.weak': 'Ez a jelszó nem felel meg a szabályoknak.',
  'invite.claim.signInFailed':
    'A fiók elkészült, de a bejelentkezés nem sikerült. Jelentkezz be a jelszóval, amit most megadtál.',

  'state.retry': 'Újra',
  'state.denied.title': 'Ehhez nincs hozzáférésed',
  'state.denied.body': 'Kérj hozzáférést attól, aki a munkaterületet kezeli.',
  'state.notInPlan.title': 'Ez nem része a csomagotoknak',
  'state.notInPlan.body':
    'A munkaterület csomagja ezt nem tartalmazza. Aki az előfizetést kezeli, válthat csomagot.',

  /* ── C6: account settings ─────────────────────────────────────────────────────────────── */
  'nav.account': 'Fiókom',
  /* 14 Oct 2026 · the settings panel beside the icon rail, and the rail's flyouts. */
  'nav.settingsSearch': 'Keresés a beállításokban',
  'nav.seeAll': 'Összes megtekintése',
  'title.account': 'Fiókom · Beállítások',
  'account.section.profile': 'Profil',
  /* ── C54 · Skilljeim ──────────────────────────────────────────────────────────────────── */
  'account.section.skills': 'Skilljeim',
  'account.skills.add': 'Skill hozzáadása',
  'account.skills.count': '{count} skill',
  'account.skills.inCategories': '{count} kategóriában',
  'account.skills.none':
    'Még nincs skilled felvéve. A workspace listájából válassz, és add meg a szintedet.',
  /* ⚠️ The mockup's empty state also promised that managers and project leads search on this.
     Nothing does — Phase 5 builds a vocabulary and no matcher — so that half is not shipped. */
  'account.skills.empty': 'Még nincs felvett skilled. Vegyél fel egyet a workspace listájából.',
  'account.skills.hint':
    'Csak a workspace listájában szereplő skilleket veheted fel. Ha hiányzik valami, szólj egy adminnak: a Beállítások › Skill menedzsment alatt vehetik fel.',
  'account.skills.levelOf': '{name} szintje',
  'account.skills.remove': '{name} eltávolítása',
  'account.section.security': 'Biztonság',
  'account.section.preferences': 'Preferenciák',
  'account.section.theme': 'Téma',
  'account.section.notifications': 'Értesítések',
  'account.section.integrations': 'Integrációk',
  'account.section.account': 'Fiók',
  'account.section.unsaved': 'Nem mentett módosítás ezen a lapon',
  'account.avatar.title': 'Profilkép',
  'account.avatar.upload': 'Kép feltöltése',
  'account.avatar.remove': 'Eltávolítás',
  'account.avatar.uploading': 'Feltöltés…',
  'account.avatar.tooLarge': 'Ez a kép túl nagy (legfeljebb 2 MB).',
  'account.avatar.wrongType': 'PNG, JPEG vagy WebP képet tölts fel.',
  'account.avatar.infected': 'A víruskereső visszautasította ezt a fájlt.',
  'account.avatar.scannerDown': 'Most nem tudjuk ellenőrizni a fájlokat. Próbáld újra hamarosan.',
  'account.avatar.failed': 'A feltöltés nem sikerült.',
  'account.avatar.rules': 'PNG vagy JPG, legalább 256×256. A képet nem méretezzük át helyetted.',
  'account.basics.title': 'Alapadatok',
  'account.field.givenName': 'Utónév',
  'account.field.familyName': 'Vezetéknév',
  'account.field.phone': 'Telefon',
  'account.field.optional': 'nem kötelező',
  'account.field.email': 'E-mail cím',
  'account.email.change': 'Cím módosítása',
  'account.email.newLabel': 'Új e-mail cím',
  'account.email.send': 'Megerősítő levél küldése',
  'account.email.cancel': 'Mégsem',
  'account.email.pending': 'Megerősítésre vár: {email}',
  'account.email.bothMustConfirm':
    'A régi és az új címre is küldtünk levelet – mindkettőt meg kell nyitni, addig a régi cím érvényes.',
  'account.save': 'Mentés',
  'account.saving': 'Mentés…',
  'account.saved': 'Elmentve',
  'account.discard': 'Elvetés',
  'account.password.title': 'Jelszó',
  'account.password.current': 'Jelenlegi jelszó',
  'account.password.next': 'Új jelszó',
  'account.password.confirm': 'Új jelszó újra',
  'account.password.submit': 'Jelszó módosítása',
  'account.password.rule.length': 'Legalább {min} karakter',
  'account.password.rule.letters': 'Kis- és nagybetűt is tartalmaz',
  'account.password.rule.digits': 'Számot is tartalmaz',
  'account.password.rule.match': 'A két új jelszó egyezik',
  'account.password.wrongCurrent': 'Ez nem a jelenlegi jelszavad.',
  'account.password.same': 'Az új jelszó nem lehet ugyanaz, mint a régi.',
  'account.password.weak': 'Ez a jelszó nem felel meg a szabályoknak.',
  'account.twofa.title': 'Kétlépcsős azonosítás',
  'account.twofa.enable': 'Bekapcsolás',
  'account.twofa.disable': 'Kikapcsolás',
  'account.twofa.scan': 'Olvasd be a kódot a hitelesítő alkalmazásoddal, majd írd be, amit mutat.',
  'account.twofa.secret': 'Ha nem tudod beolvasni, ezt írd be kézzel:',
  'account.twofa.code': 'Hatjegyű kód',
  'account.twofa.verify': 'Ellenőrzés és bekapcsolás',
  'account.twofa.notOnYet':
    'Amíg nem adsz meg egy érvényes kódot, a kétlépcsős azonosítás nincs bekapcsolva.',
  'account.twofa.qrAlt': 'QR-kód a hitelesítő alkalmazáshoz',
  'account.twofa.on': 'Bekapcsolva',
  'account.twofa.onNote': 'Belépéskor a jelszavad után egy hatjegyű kódot is kérünk.',
  'account.twofa.offNote': 'Egy második lépés a belépéshez, a telefonodon lévő alkalmazásból.',
  'account.twofa.wrongCode': 'Ez a kód nem stimmel. Próbáld újra a következővel.',
  'account.twofa.wrongPassword': 'Ez nem a jelszavad.',
  'account.twofa.disableNote':
    'A kikapcsolás után csak a jelszavad véd. A helyreállítási kulcsaid is törlődnek.',
  'account.twofa.keys.title': 'Helyreállítási kulcsok',
  'account.twofa.keys.once':
    'Ezeket most látod utoljára. Mentsd el őket – ha elveszíted a telefonod, ez az egyetlen út vissza.',
  'account.twofa.keys.saved': 'Elmentettem',
  'account.twofa.keys.download': 'Letöltés',
  'account.twofa.keys.left': '{count} fel nem használt helyreállítási kulcsod van.',
  'account.twofa.keys.regenerate': 'Új kulcsok',
  'account.twofa.keys.regenerateNote':
    'Új kulcsokat készítünk, a régiek pedig azonnal érvénytelenné válnak.',
  'common.copy': 'Másolás',
  'common.copied': 'Kimásolva',
  'account.passkeys.title': 'Passkey-k',
  'account.passkeys.add': 'Passkey hozzáadása',
  'account.passkeys.empty': 'Még nincs passkey-d.',
  /* ⚠️ Item 17 · the field is optional now — the server names it from the authenticator. */
  /* ⚠️ Item 17 · shown while the OS prompt is open, which is the only step there is now. */
  'account.passkeys.adding': 'Várakozás az eszközre…',
  'account.passkeys.created': 'Hozzáadva: {date}',
  'account.passkeys.lastUsed': 'Utolsó használat: {when}',
  'account.passkeys.neverUsed': 'még nem használtad',
  'account.passkeys.remove': 'Törlés',
  'account.passkeys.unsupported':
    'Ez a böngésző nem támogatja a passkey-ket. A jelszavas belépés továbbra is működik.',
  'account.sessions.title': 'Eszközök',
  'account.sessions.current': 'ez az eszköz',
  'account.sessions.revoke': 'Kiléptetés',
  'account.sessions.revokeOthers': 'Minden más eszköz kiléptetése',
  'account.sessions.lastSeen': 'Utolsó aktivitás: {when}',
  'account.prefs.title': 'Nyelv és formátumok',
  'account.prefs.locale': 'Nyelv',
  'account.prefs.locale.hu': 'Magyar',
  'account.prefs.locale.en': 'English',
  'account.prefs.timezone': 'Időzóna',
  'account.prefs.timezoneFilter': 'Szűrés',
  'account.prefs.dateFormat': 'Dátumformátum',
  'account.prefs.weekStart': 'A hét első napja',
  'account.prefs.weekStart.mon': 'Hétfő',
  'account.prefs.weekStart.sun': 'Vasárnap',
  'account.prefs.sample': 'Így fog kinézni: {sample}',
  'account.theme.title': 'Téma',
  'account.theme.system': 'Rendszer szerint',
  'account.theme.systemHint': 'A géped beállítását követi – világos nappal, sötét este.',
  'account.theme.current': 'Kiválasztva',
  'account.theme.noPreview': 'Az előnézet a betöltött stíluslapból készül.',
  'theme.light.label': 'Forecastify Light',
  'theme.light.hint': 'Az alap. Napi munkához.',
  'theme.dark.label': 'Forecastify Dark',
  'theme.dark.hint': 'Ugyanaz a paletta, esti Gantt-hoz.',
  'theme.latte.label': 'Latte Violet',
  'theme.latte.hint': 'Meleg világos, lila akcenttel.',
  'theme.mocha.label': 'Mocha Peach',
  'theme.mocha.hint': 'Lágy sötét, barack akcens.',
  'theme.nord.label': 'Nord Blue',
  'theme.nord.hint': 'Hideg sötét, jégkék akcens.',
  'theme.frappe.label': 'Frappé Rose',
  'theme.frappe.hint': 'Hidegkék sötét, rózsakvarc akcens.',
  'theme.macchiato.label': 'Macchiato Pink',
  'theme.macchiato.hint': 'Mélykék sötét, rózsaszín akcens.',
  'theme.radix.label': 'Dark Violet',
  'theme.radix.hint': 'Majdnem fekete, a márka lilájával.',
  'account.notif.title': 'Értesítések',
  'account.notif.pause': 'Minden értesítés szüneteltetése',
  'account.notif.pauseHint': 'A beállításaid megmaradnak, csak nem küldünk semmit.',
  'account.notif.channelEmail': 'E-mail',
  'account.notif.channelApp': 'Alkalmazás',
  'account.notif.event.assigned': 'Feladat rám osztva',
  'account.notif.event.assignedHint': 'Amint valaki hozzád rendel egy jegyet.',
  'account.notif.event.status': 'Státusz változott a feladataimon',
  'account.notif.event.statusHint': 'A rád osztott jegyek mozgása a táblán.',
  'account.notif.event.comment': 'Komment vagy megjelölés a feladatomon',
  'account.notif.event.commentHint': '@megjelölés és minden új hozzászólás.',
  'account.notif.event.due': 'Közeleg a határidő',
  'account.notif.event.dueHint': 'Csak nyitott, rád osztott jegyekre.',
  'account.notif.lead': 'Hány nappal előre',
  'account.notif.reminder': 'Napi emlékeztető',
  'account.notif.reminderAt': 'Időpont',
  'account.notif.reminderDays': 'Mely napokon',
  'account.notif.weekly': 'Heti összefoglaló',
  'account.notif.day.1': 'H',
  'account.notif.day.2': 'K',
  'account.notif.day.3': 'Sze',
  'account.notif.day.4': 'Cs',
  'account.notif.day.5': 'P',
  'account.notif.day.6': 'Szo',
  'account.notif.day.7': 'V',
  'account.integrations.title': 'Naptár',
  'account.integrations.google': 'Google Calendar',
  'account.integrations.outlook': 'Outlook',
  /* ── C37 · a saját Jira-fiók összekötése ────────────────────────────────────────────────────
   *
   * ⚠️⚠️ **SENKI NEM MÁSOL BE TOKENT.** A gomb az Atlassian saját beleegyező képernyőjére visz, és
   * onnan jön vissza — a szövegek ezért „összekötés”-ről szólnak, nem kulcsokról.
   */
  /* ── C37 · a hoszted óra állapota a soron (§9.3) ────────────────────────────────────────────
   *
   * ⚠️ **Állapot, nem hiba.** Ugyanabban a regiszterben, mint a hiányzó sortétel címkéje: valami
   * igaz a sorról, az okkal a hover alatt — nem piros hibajelzés valakinek a jelenléti ívén, mert
   * az adminisztrátor még nem kötötte össze a Jirát.
   */
  'time.sync.pending': 'küldésre vár',
  'time.sync.failed': 'nem ment át',
  'time.sync.detached': 'A Jira-másolat törölve',
  'account.connections.title': 'Forecastify Integráció Összekötések',
  'account.connections.connect': 'Összekötés',
  'account.connections.disconnect': 'Szétválasztás',
  'account.connections.connected': 'összekötve',
  'account.connections.notConnected': 'nincs összekötve',
  'account.connections.notDiscovered': 'előbb felderítés kell a kapcsolathoz',
  /* ⚠️ A visszatérés kimenetei. A callback kulcsot ad vissza, nem mondatot — nem ismerheti a nyelvet. */
  'account.connections.result.ok': 'Sikeres összekötés',
  'account.connections.result.cancelled': 'Megszakítottad az összekötést',
  /* ⚠️ Nem a felhasználó mondott nemet — az Atlassian utasította el a kérést. */
  'account.connections.result.refused': 'Az Atlassian elutasította a kérést',
  'account.connections.result.state': 'Érvénytelen kérés – jelentsd, ha újra előfordul',
  'account.connections.result.expired': 'Túl sok idő telt el, próbáld újra',
  'account.connections.result.wrong_site':
    'Másik Jirát kötöttél össze, mint amit ez a kapcsolat használ',
  'account.connections.result.no_refresh':
    'Az összekötés egy óra múlva megszűnne – engedélyezd a tartós hozzáférést',
  'account.connections.result.not_configured': 'Ezen a kiszolgálón nincs beállítva az összekötés',
  'account.connections.result.not_found': 'A kapcsolat nem található',
  'account.connections.result.malformed': 'Hiányos válasz érkezett',
  'account.connections.result.failed': 'Nem sikerült az összekötés',
  /* ── 26 Oct 2026 · a visszatérés oldala: összekötve, vagy miért nem ──────────────────────────
   *
   * ⚠️ A `result.*` fenti sorai a varázsló toastjainak címei maradnak; ezek az oldal szövegei. A
   * `wrong_site` a kiemelt eset: az Atlassian minden oldalt felajánl, és nem lehet előre kiválasztani.
   */
  'account.connections.outcome.ok.title': 'Összekötötted a Jira-fiókodat',
  'account.connections.outcome.ok.body':
    'A Jirával szinkronizált feladatokon rögzített órád mostantól a saját neveden kerül fel, és a várakozó idők is kimennek.',
  'account.connections.outcome.cancelled.title': 'Megszakítottad az összekötést',
  'account.connections.outcome.cancelled.body':
    'Semmi nem változott. Ha mégis összekötnéd a fiókodat, próbáld újra.',
  'account.connections.outcome.refused.title': 'Az Atlassian elutasította a kérést',
  'account.connections.outcome.refused.body':
    'Nem te mondtál nemet – az Atlassian nem engedte az összekötést. Ha újra előfordul, szólj a workspace adminisztrátorának.',
  'account.connections.outcome.state.title': 'Érvénytelen kérés',
  'account.connections.outcome.state.body':
    'A visszatérő kérést nem lehetett ellenőrizni, ezért nem jött létre kapcsolat. Indítsd újra az integrációid közül; ha ismét előfordul, jelentsd.',
  'account.connections.outcome.expired.title': 'Lejárt az összekötés ideje',
  'account.connections.outcome.expired.body':
    'Tíz percnél több telt el az Atlassian oldalán, ezért a kérés lejárt. Indítsd újra az integrációid közül.',
  'account.connections.outcome.wrong_site.title': 'Másik Jira-oldalt választottál',
  'account.connections.outcome.wrong_site.body':
    'Az Atlassian az összes oldalt felajánlja, amelyhez a fiókod hozzáfér, és a választást rád bízza. Próbáld újra, és ott a lent megjelölt címet válaszd.',
  'account.connections.outcome.wrong_site.picked': 'Ezt választottad',
  'account.connections.outcome.wrong_site.expected': 'Ezt válaszd',
  'account.connections.outcome.no_refresh.title': 'Nem kaptál tartós hozzáférést',
  'account.connections.outcome.no_refresh.body':
    'Az Atlassian nem adott tartós hozzáférést, ezért az összekötés egy órán belül megszűnne. Próbáld újra; ha ismét előfordul, szólj a workspace adminisztrátorának.',
  'account.connections.outcome.not_configured.title': 'Az összekötés nincs beállítva',
  'account.connections.outcome.not_configured.body':
    'Ezen a kiszolgálón nincs beállítva az összekötéshez szükséges Atlassian-alkalmazás. Szólj a workspace adminisztrátorának.',
  'account.connections.outcome.not_found.title': 'Ez a Jira-kapcsolat már nem létezik',
  'account.connections.outcome.not_found.body':
    'Valaki időközben törölte ezt a kapcsolatot. Az integrációid között látod, mit köthetsz most össze.',
  'account.connections.outcome.malformed.title': 'Hiányos válasz érkezett',
  'account.connections.outcome.malformed.body':
    'Az Atlassian visszairányított, de a válaszból hiányzott, ami az összekötéshez kell. Próbáld újra.',
  'account.connections.outcome.failed.title': 'Nem sikerült az összekötés',
  'account.connections.outcome.failed.body':
    'Az összekötés közben hiba történt, és nem jött létre kapcsolat. Próbáld újra; ha ismét előfordul, szólj a workspace adminisztrátorának.',
  'account.connections.outcome.detail': 'Az Atlassian válasza:',
  'account.connections.outcome.retry': 'Újrapróbálás',
  'account.connections.outcome.back': 'Vissza az integrációkhoz',
  'account.connections.outcome.done': 'Kész',
  'account.tokens.title': 'Személyes hozzáférési kulcsok',
  'account.tokens.create': 'Új kulcs',
  'account.tokens.empty': 'Még nincs kulcsod.',
  'account.tokens.nameLabel': 'Mire használod',
  'account.tokens.scopesLabel': 'Mit nyit ki',
  'account.tokens.scope.read': 'Olvasás',
  'account.tokens.scope.write': 'Írás',
  'account.tokens.scope.contracts': 'Szerződések',
  'account.tokens.namePlaceholder': 'Havi riport szkript',
  'account.tokens.created': 'Létrehozva: {date}',
  'account.tokens.lastUsed': 'Utolsó használat: {when}',
  'account.tokens.never': 'soha',
  'account.tokens.revoke': 'Visszavonás',
  'account.tokens.revoked': 'visszavonva',
  'account.tokens.revealTitle': 'Ez a kulcs, most másold ki',
  'account.tokens.revealHint': 'Csak most látod. Utána már nem tudjuk megmutatni, csak újat adni.',
  'account.tokens.copy': 'Másolás',
  'account.tokens.copied': 'Kimásolva',
  'account.tokens.done': 'Kimásoltam',
  'account.export.title': 'Adatexport',
  'account.export.hint': 'Mindent kiexportálunk, amit látsz, és e-mailben szólunk.',
  'account.export.request': 'Export kérése',
  'account.export.requested': 'Kérés rögzítve – e-mailben szólunk, amikor elkészült.',
  'account.export.running': 'Készül…',
  'account.export.download': 'Letöltés',
  'account.export.notFulfilledYet':
    'Az előállítás még nem épült meg – a kérésedet rögzítjük, de fájl egyelőre nem lesz belőle.',
  'account.delete.title': 'Fiók törlése',
  'account.delete.hint': 'Ez nem visszavonható.',
  'account.delete.blocked':
    'A fiók törlését még nem építettük meg. Szólj a munkaterület kezelőjének.',

  /* ── sign in. ⚠️ The mockup draws no such screen; see `screens/sign-in.tsx`. ─────── */
  'signIn.email': 'E-mail cím',
  'signIn.password': 'Jelszó',
  'signIn.submit': 'Belépés',
  'signIn.working': 'Belépés…',
  'signIn.invalid': 'Ez nem stimmel. Ellenőrizd a címet és a jelszót.',
  'signIn.rateLimited': 'Túl sok próbálkozás. Várj egy kicsit.',
  'signIn.unavailable': 'A bejelentkezés most nem elérhető.',
  'signIn.noWorkspace': 'A fiókod még egyetlen munkaterülethez sem tartozik.',
  'session.signOut': 'Kilépés',
  'session.restoring': 'Munkamenet ellenőrzése…',
  'state.noWorkspace.title': 'Nem tartozol egyetlen munkaterülethez sem',
  'state.noWorkspace.body':
    'A fiókod létezik, de még senki nem hívott meg egy munkaterületre. Szólj annak, aki a munkaterületet kezeli. (Fejlesztés közben ez általában azt jelenti, hogy a teszt-suite kiürítette az adatbázist – futtasd újra a `pnpm seed:dev` parancsot.)',
  'signIn.withPasskey': 'Belépés passkey-jel',
  'signIn.noAccount': 'Még nincs fiókom – regisztráció',

  /* ══ S2: sign-up and the confirmation landing ═════════════════════════════════════════════ */
  'signUp.title': 'Fiók létrehozása',
  'signUp.lede': 'Add meg az e-mail-címed, és küldünk rá egy kódot.',
  'signUp.email': 'E-mail cím',
  'signUp.submit': 'Kód küldése',
  'signUp.working': 'Egy pillanat…',
  'signUp.haveAccount': 'Már van fiókom – bejelentkezés',
  'signUp.code.title': 'Írd be a kódot',
  'signUp.code.lede': 'Elküldtük a hatjegyű kódot ide: {email}',
  'signUp.code.label': 'Kód',
  'signUp.code.submit': 'Tovább',
  'signUp.code.resend': 'Új kód küldése',
  'signUp.code.resent': 'Új kódot küldtünk.',
  'signUp.code.changeEmail': 'Másik címet adok meg',
  'signUp.code.invalid': 'Ez a kód nem jó, vagy lejárt. Kérj újat.',
  'signUp.code.existing': 'Ha ehhez a címhez már tartozik fiók, nem küldünk kódot.',
  'signUp.code.signIn': 'Bejelentkezés',
  'signUp.invalidEmail': 'Ez nem tűnik érvényes e-mail címnek.',
  'signUp.rateLimited': 'Túl sok próbálkozás. Várj egy kicsit.',
  'signUp.unavailable': 'A regisztráció most nem elérhető.',

  'verify.signup.title': 'Az e-mail címed megerősítve',
  'verify.signup.body': 'Már be tudsz jelentkezni, és létrehozhatod az első munkaterületed.',
  'verify.emailChange.title': 'Az új e-mail címed megerősítve',
  'verify.emailChange.body':
    'A címcseréhez mindkét címnek meg kell erősítenie – ha a másik levél még nyitva van, azt is nyisd meg.',
  'verify.other.title': 'A link érvényes volt',
  'verify.other.body': 'A megerősítés megtörtént.',
  'verify.dead.title': 'Ez a link már nem használható',
  /* ⚠️ Nem mondjuk, hogy "lejárt": a GoTrue a lejárt, a már felhasznált és az érvénytelen tokenre
   * ugyanazt a hibát adja (`otp_expired`) — és a leggyakoribb eset az, hogy a link már működött. */
  'verify.dead.body':
    'Lejárt, vagy már felhasználták – ezt nem tudjuk szétválasztani. Ha már megerősítetted a címed, egyszerűen jelentkezz be.',
  'verify.error.title': 'A megerősítés nem sikerült',
  'verify.error.body': 'A szolgáltatás ezt a hibát adta vissza:',
  'verify.unknown.title': 'Ide a megerősítő link vezet',
  'verify.unknown.body':
    'Úgy tűnik, közvetlenül nyitottad meg ezt a címet. Nyisd meg a linket az e-mailből.',
  'verify.toSignIn': 'Bejelentkezés',
  'verify.toAccount': 'Vissza a fiókomhoz',
  'verify.toSignUp': 'Új regisztráció',

  /* ══ C52 · hozzájárulás egy külső alkalmazásnak ═══════════════════════════════════════════
   *
   * ⚠️⚠️ **A KLIENS NEVE ÉS A CÉLGÉP NEVE KÜLÖN SORBAN VAN, MERT CSAK AZ EGYIK MEGBÍZHATÓ.** A
   * nevet a kliens saját metaadat-dokumentuma mondja — bármit állíthat magáról. A hoszt az, ahová
   * a kód ténylegesen megérkezik, és ez az egyetlen adat ezen a lapon, amit egy támadó nem tud
   * meghamisítani. A specifikáció ezt kötelezővé teszi.
   *
   * ⚠️ A `consent.gate` az utolsó mondat, és ez teszi érthetővé az egész képernyőt: bármit is kér
   * az alkalmazás, írni csak úgy tud, ha valaki a Forecastify-ban megnyom egy gombot.
   */
  'consent.title': 'Hozzáférés engedélyezése',
  'consent.signIn': 'Jelentkezz be, hogy el tudd dönteni.',
  'consent.loading': 'Betöltés…',
  'consent.failed.title': 'Ez a kérés már nem érvényes',
  'consent.failed.body':
    'Lejárt, vagy már döntöttél róla. Indítsd újra a csatlakozást abban az alkalmazásban.',
  'consent.lede': 'A(z) {client} hozzáférést kér a Forecastify-fiókodhoz.',
  'consent.field.client': 'Alkalmazás',
  /* ⚠️ "Hová küldjük" és nem "átirányítás": a kérdés az, hogy hová kerül a hozzáférés, nem az,
   * hogy technikailag mi történik. */
  'consent.field.redirect': 'Hová küldjük',
  'consent.localhost':
    'Ez az alkalmazás csak a saját gépedre kér visszairányítást. Ilyenkor a cím nem árulja el, melyik program kapja meg a hozzáférést – csak akkor engedélyezd, ha te indítottad.',
  'consent.scopes': 'Mit fog tudni:',
  'consent.scope.read': 'Megnézni a feladataidat, óráidat és projektjeidet',
  'consent.scope.write': 'Módosításokat javasolni – rögzíteni csak a te jóváhagyásoddal tud',
  'consent.scope.contracts': 'Megnézni a szerződéseket',
  'consent.workspace': 'Melyik munkaterületen',
  'consent.again': 'Ezt az alkalmazást korábban már engedélyezted.',
  'consent.approve': 'Engedélyezem',
  'consent.decline': 'Nem engedélyezem',
  'consent.gate':
    'Ez az alkalmazás önmagában semmit nem ír. Minden módosítás egy kártyaként jelenik meg a Forecastify-ban, és csak akkor történik meg, ha rányomsz.',
  /* ⚠️ A fiók beállításainál, közvetlenül a kulcsok alatt: ugyanaz a kérdés két válasszal — amit én
   * hoztam létre, és amit engedélyeztem. Külön képernyő egy második hely lenne, amit el lehet
   * felejteni megnézni. */
  'account.apps.title': 'Engedélyezett alkalmazások',
  'account.apps.lede':
    'Külső alkalmazások, amelyeknek hozzáférést adtál. A leválasztás azonnal érvényes – az alkalmazás a következő kérésnél már nem jut be.',
  'account.apps.disconnect': 'Leválasztás',

  /* ══ Later: tenancy — az első munkaterület ════════════════════════════════════════════════
   *
   * ⚠️ A képernyő "céget" mond, a szerződés `tenant`-et. A modell szava a `tenant`, de aki
   * regisztrál, nem tudja mi az — a `provisioning/contract.ts` kimondja ezt a szándékos
   * eltérést. */
  /* ── `0202` · the first-run setup flow — the onboarding mockup's copy, HU the design length ── */
  'setup.title': 'Első lépések',
  'setup.stepOf': 'Lépés: {step} / {total}',
  'setup.signedIn': 'Bejelentkezve: {email}',
  'setup.continue': 'Tovább',
  'setup.working': 'Egy pillanat…',
  'setup.skip': 'Kihagyom',
  'setup.back': 'Vissza',
  'setup.signOut': 'Kilépés',
  'setup.upload': 'Kép feltöltése',
  'setup.replace': 'Csere',
  'setup.remove': 'Eltávolítás',
  'setup.error.alreadyMember': 'Ez a fiók már tartozik egy workspace-hez. Töltsd újra az oldalt.',
  'setup.profile.title': 'Állítsd be a profilod',
  'setup.profile.subtitle': 'Így látnak majd a munkatársaid.',
  'setup.profile.family': 'Vezetéknév',
  'setup.profile.given': 'Utónév',
  'setup.profile.avatarHint': 'JPG vagy PNG, legfeljebb 2 MB. Nem kötelező.',
  'setup.profile.errType': 'JPG vagy PNG lehet.',
  'setup.profile.errSize': 'A kép legfeljebb 2 MB lehet.',
  'setup.profile.password': 'Jelszó',
  'setup.profile.confirm': 'Jelszó megerősítése',
  'setup.profile.blocked': 'Add meg a vezeték- és utóneved.',
  'setup.profile.blockedPassword': 'A jelszó még nem felel meg a szabályoknak.',
  'setup.profile.haveAccount': 'Már van fiókom – bejelentkezés',
  'setup.workspace.title': 'Hozd létre a workspace-ed',
  'setup.workspace.subtitle': 'Ezt látja majd mindenki, akit meghívsz.',
  'setup.workspace.name': 'Workspace neve',
  'setup.workspace.namePlaceholder': 'pl. Kovács Digital Kft.',
  'setup.workspace.logoHint': 'PNG vagy JPG, legfeljebb 2 MB. Négyzetes kép mutat a legjobban.',
  'setup.workspace.errType': 'PNG vagy JPG lehet.',
  'setup.workspace.errSize': 'A kép legfeljebb 2 MB lehet.',
  'setup.workspace.address': 'Cím',
  'setup.workspace.checking': 'Cím ellenőrzése…',
  'setup.workspace.available': 'Szabad cím',
  'setup.workspace.taken': 'A(z) {slug} cím foglalt.',
  'setup.workspace.reserved': 'A(z) {slug} cím fenntartott, nem választható.',
  'setup.workspace.suggest': 'Javaslat:',
  'setup.workspace.errChars': 'Csak kisbetű, szám és kötőjel lehet benne.',
  'setup.workspace.errHyphen': 'Nem kezdődhet és nem végződhet kötőjellel.',
  'setup.workspace.errShort': 'Legalább 3 karakter.',
  'setup.workspace.timezone': 'Időzóna',
  'setup.workspace.blockedName': 'Add meg a workspace nevét.',
  'setup.workspace.blockedSlug': 'Adj meg egy címet.',
  'setup.workspace.blockedCheck': 'A cím ellenőrzése folyamatban.',
  'setup.workspace.blockedTaken': 'Válassz szabad címet.',
  'setup.workspace.currency': 'Pénznem',
  'setup.workspace.currencyHint':
    'Ez lesz a workspace alapértelmezett pénzneme. Továbbiakat a Beállításokban adhatsz hozzá.',
  'setup.workspace.currencyLocked':
    'A pénznem itt már nem cserélhető. A Beállítások › Pénznemek alatt módosíthatod.',
  'setup.currency.HUF': 'Magyar forint',
  'setup.currency.EUR': 'Euró',
  'setup.currency.USD': 'Amerikai dollár',
  'setup.currency.GBP': 'Angol font',
  'setup.currency.CHF': 'Svájci frank',
  'setup.currency.RON': 'Román lej',
  'setup.currency.PLN': 'Lengyel zloty',
  'setup.currency.CZK': 'Cseh korona',
  'setup.currency.SEK': 'Svéd korona',
  'setup.currency.NOK': 'Norvég korona',
  'setup.currency.DKK': 'Dán korona',
  'setup.currency.JPY': 'Japán jen',
  'setup.invite.title': 'Hívd meg a csapatod',
  'setup.invite.subtitle': 'A munkatársaid e-mailben kapnak meghívót.',
  'setup.invite.emails': 'E-mail-címek',
  'setup.invite.emailsPlaceholder': 'nev@ceg.hu, vesszővel vagy szóközzel elválasztva',
  'setup.invite.emailsMore': 'További cím…',
  'setup.invite.level': 'Jogosultság mindenkinek',
  'setup.invite.levelAdmin': 'Admin',
  'setup.invite.levelPm': 'Projektmenedzser',
  'setup.invite.levelMember': 'Munkatárs',
  'setup.invite.levelExt': 'Alvállalkozó',
  'setup.invite.levelFor': 'Jogosultság: {email}',
  'setup.invite.seats': '{n} / {m} hely foglalt',
  'setup.invite.over':
    '{k} címmel túllépnéd a csomag {m} helyét. Vegyél ki {k} címet, vagy bővítsd a csomagot.',
  'setup.invite.tagMember': 'már tag',
  'setup.invite.tagSelf': 'te vagy',
  'setup.invite.memberNote': '{list}: már tag, nem megy neki meghívó.',
  'setup.invite.errAt': 'Hiányzik a @ jel.',
  'setup.invite.errDomain': 'Hiányzik a domain vége, pl. .hu.',
  'setup.invite.errIncomplete': 'Hiányos cím.',
  'setup.invite.errChar': 'Érvénytelen karakter van benne.',
  'setup.invite.removeChip': 'Eltávolítás: {email}',
  'setup.invite.send': 'Meghívók küldése',
  'setup.invite.blockedEmpty': 'Adj meg legalább egy címet, vagy hagyd ki ezt a lépést.',
  'setup.invite.blockedInvalid': 'Javítsd vagy töröld a piros címeket.',
  'setup.invite.blockedOver': 'Több a meghívó, mint a szabad hely.',
  'setup.invite.failed':
    'Nem ment ki meghívó ide: {list}. Később az Emberek oldalon újra próbálhatod.',
  'setup.done.title': 'Minden kész',
  'setup.done.subtitle': 'A(z) {ws} workspace elkészült.',
  'setup.done.address': 'Cím',
  'setup.done.invites': 'Meghívók',
  'setup.done.sent': '{n} meghívó elküldve',
  'setup.done.pending': '{n} meghívó — belépéskor küldjük ki',
  'setup.done.none': 'Nem küldtél meghívót. Később az Emberek oldalon megteheted.',
  'setup.done.next': 'Egy rövid bemutatóval segítünk eligazodni.',
  'setup.done.enter': 'Belépés a Forecastify-ba',
  'setup.done.entering': 'Belépés…',
  'setup.preview.yourName': 'A neved',
  'setup.preview.workspace': 'Workspace',
  'setup.preview.task1': 'Kezdőoldal drótváz',
  'setup.preview.task2': 'Árajánlat-sablon frissítése',
  'setup.preview.task3': 'Ügyfél-kickoff előkészítése',
  'setup.preview.task4': 'Heti státuszriport',
  'setup.preview.task5': 'Design rendszer audit',
  'setup.preview.task6': 'Sprinttervezés',
  'setup.preview.task7': 'Szerződés-megújítás egyeztetése',
  'setup.preview.task8': 'Belső wiki rendbetétele',
  'setup.preview.task9': 'Q4 kapacitásterv',
  'setup.preview.project1': 'Weboldal-megújítás',
  'setup.preview.project2': 'Mobilalkalmazás',
  'setup.preview.project3': 'Belső működés',
  'setup.preview.statusBacklog': 'Backlog',
  'setup.preview.statusNext': 'Következik',
  'setup.preview.statusDoing': 'Folyamatban',
  'setup.preview.statusReview': 'Ellenőrzés alatt',
  'setup.preview.statusDone': 'Kész',
  'setup.preview.priorityUrgent': 'Sürgős',
  'setup.preview.priorityHigh': 'Magas',
  'setup.preview.priorityMedium': 'Közepes',
  'setup.preview.priorityLow': 'Alacsony',
  'setup.preview.ownerRole': 'Owner',

  /* ── S3: a felfüggesztett munkaterület. `decisions.md` S-9 dönti el, hogy miért van saját
     képernyője, és miért nem 404. A felfüggesztés OKA szándékosan nem szerepel itt: az az
     üzemeltető belső jegyzete, nem ennek a közönségnek szól. ─────────────────────────────── */
  'suspended.title': 'Ez a munkaterület fel van függesztve',
  'suspended.lede':
    'A hozzáférés ideiglenesen szünetel. Az adatok megvannak és sértetlenek – a munkaterület a felfüggesztés feloldása után pontosan ott folytatódik, ahol abbamaradt.',
  /* ⚠️ Nincs benne e-mail-cím, mert ebben a termékben nincs ilyen: egy kitalált support-cím
     olyan tény lenne a képernyőn, ami nem igaz. Akkor kap címet, amikor van. */
  'suspended.contact': 'Vedd fel a kapcsolatot azzal, aki a fiókot kezeli.',
  'suspended.signedInAs': 'Bejelentkezve: {email}',
  'suspended.signOut': 'Kilépés',

  /* ── C18: what the belépés mockup added. `screens/auth/auth-frame.tsx` holds the geometry. ── */
  'signIn.emailPlaceholder': 'nev@loginet.hu',
  'signIn.forgot': 'Elfelejtetted?',
  'signIn.showPassword': 'Jelszó megjelenítése',
  'signIn.hidePassword': 'Jelszó elrejtése',
  'signIn.remember': 'Maradjak bejelentkezve ezen a gépen',
  'signIn.passkey': 'Passkey',
  /* ⚠️ Explains an insecure origin without saying "insecure origin" — the person reading it opened
     the app over the network, and what they can act on is the address they used. */
  'signIn.passkeyUnavailable':
    'Ez a böngésző vagy ez a cím nem támogatja a passkey-t. Nyisd meg a szokásos címen.',
  'signIn.google': 'Google',
  'signIn.continue': 'Tovább',
  'signIn.changeEmail': 'Módosítás',
  'signIn.loggingInAs': 'Belépés mint',
  'signIn.code': 'Hatjegyű kód',
  'signIn.codeHint': 'A hitelesítő alkalmazásod mutatja.',
  'signIn.useRecoveryKey': 'Helyreállítási kulcsot használok',
  'signIn.recoveryKey': 'Helyreállítási kulcs',
  'signIn.recoveryKeyHint': 'Az egyik elmentett kulcs. Egyszer használható.',
  'signIn.useCode': 'Mégis a kódot használom',
  'signIn.mfaInvalid': 'Ez a kód nem stimmel. Próbáld újra a következővel.',
  'oauth.working': 'Belépés folyamatban…',
  'oauth.expired': 'Ez a belépés lejárt. Kezdd elölről.',
  'oauth.backToSignIn': 'Vissza a belépéshez',
  /* 22 Oct 2026 · Google sign-in: an address with no account, and the account's Google card. */
  'oauth.accountNotFound': 'Nem találtunk fiókot ezzel az e-mail-címmel.',
  'oauth.identityInUse': 'Ez a Google-fiók már egy másik fiókhoz van csatlakoztatva.',
  'oauth.linking': 'Google-fiók csatlakoztatása…',
  'oauth.backToAccount': 'Vissza a fiókomhoz',
  'account.google.title': 'Google-fiók',
  'account.google.lede': 'A csatlakoztatott Google-fiókkal a Google-gombbal is bejelentkezhetsz.',
  'account.google.connectedAs': 'Csatlakoztatva: {email}',
  'account.google.connected': 'Csatlakoztatva',
  'account.google.notConnected': 'Nincs csatlakoztatva',
  'account.google.connect': 'Csatlakoztatás',
  'account.google.disconnect': 'Leválasztás',
  'account.google.linked': 'Google-fiók csatlakoztatva',
  'account.google.unlinked': 'Google-fiók leválasztva',
  'account.google.failed': 'Nem sikerült. Próbáld újra.',
  'account.google.last': 'Ez az egyetlen bejelentkezési módod, ezért nem választható le.',
  /* The mockup's page footer, minus its version number — see `auth-frame.tsx`. */
  'auth.footer.company': 'LogiNet Systems Kft.',
  'auth.footer.privacy': 'Adatkezelés',
  'auth.footer.help': 'Súgó',

  'title.forgot': 'Elfelejtett jelszó',
  'title.reset': 'Új jelszó',

  'forgot.title': 'Elfelejtett jelszó',
  'forgot.lead': 'Add meg az e-mail-címed, és küldünk egy linket, amivel új jelszót állíthatsz be.',
  'forgot.submit': 'Link küldése',
  'forgot.working': 'Küldés…',
  /* ⚠️⚠️ **"HA VAN FIÓK" — A FELTÉTELES MONDAT A LÉNYEG.** A szerver ugyanazt válaszolja létező és
     nem létező címre; egy "elküldtük" itt az esetek felében hazugság lenne, és épp abban a felében,
     ahol valaki hiába várja a levelet. `forgot-password.tsx` és `0028` is ezt őrzi. */
  'forgot.sent': 'Ha tartozik fiók ehhez a címhez, a link már úton van.',
  'forgot.sentNote': 'A link 2 órán át érvényes. Nézd meg a spam mappát is.',
  'forgot.backToSignIn': 'Vissza a belépéshez',

  'reset.title': 'Új jelszó',
  'reset.lead': 'Adj meg egy új jelszót ehhez a fiókhoz.',
  'reset.password': 'Új jelszó',
  'reset.confirm': 'Új jelszó megerősítése',
  'reset.submit': 'Jelszó beállítása',
  'reset.working': 'Mentés…',
  'reset.expired': 'Ez a link lejárt. Kérj újat.',
  'reset.used': 'Ezt a linket már felhasználták. Kérj újat.',
  'reset.unknown': 'Ez a link nem érvényes. Kérj újat.',
  'reset.weak': 'Ez a jelszó nem felel meg a szabályoknak.',
  'reset.requestAnother': 'Új link kérése',
  'reset.done': 'Kész, az új jelszó él.',
  /* ⚠️ Kimondja, hogy a többi eszközről kiléptettük: `0028` minden munkamenetet visszavon, és ha ez
     csak később derül ki, az hibának látszik, nem annak a funkciónak, amit épp használtak. */
  'reset.doneSessions':
    'A többi eszközödön kiléptettünk, biztonsági okból. Lépj be az új jelszóval.',
  'reset.signIn': 'Belépés',

  /* ══ C7: clients, projects and project membership ═════════════════════════════════════════ */

  'nav.projects': 'Projektek',
  'nav.clients': 'Ügyfelek',
  'nav.main': 'Fő navigáció',
  /* ── Item 71 · a ⌘K paletta. ─────────────────────────────────────────────────────────── */
  'palette.placeholder': 'Keress, vagy ugorj valahova…',
  'palette.empty': 'Semmi sem egyezik ezzel: {query}',
  'palette.searching': 'Keresés…',
  'palette.group.recent': 'Legutóbb megnyitva',
  'palette.group.actions': 'Műveletek',
  'palette.group.go': 'Ugrás',
  /* 17 Oct 2026 · the settings palette: the headings inside the sections, and what they hold. */
  'palette.group.headings': 'A beállításokon belül',
  'palette.settings.status': 'Státuszok',
  'palette.settings.priority': 'Prioritások',
  'palette.settings.skill': 'Képességek',
  'palette.settings.skillCategory': 'Képességkategóriák',
  'palette.settings.skillLevel': 'Képességszintek',
  'palette.settings.schedule': 'Munkarendek',
  'palette.settings.rateCard': 'Díjkártyák',
  'palette.settings.currency': 'Pénznemek',
  'palette.settings.leaveType': 'Szabadságtípusok',
  'palette.settings.overtimeType': 'Túlóratípusok',
  'palette.settings.holiday': 'Munkaszüneti napok',
  'palette.settings.metafield': 'Metamezők',
  'palette.settings.projectRole': 'Projekt szerepek',
  'palette.settings.role': 'Szerepkörök',
  'palette.settings.integration': 'Integrációk',
  'palette.settings.token': 'Hozzáférési tokenek',
  'palette.settings.passkey': 'Passkey-k',
  'palette.settings.mySkill': 'Képességeim',
  'palette.group.tasks': 'Feladatok',
  'palette.group.projects': 'Projektek',
  'palette.group.people': 'Emberek',
  'palette.group.reports': 'Riportok',
  'palette.group.contracts': 'Szerződések',
  'palette.group.lineItems': 'Sortételek',
  'palette.action.newTask': 'Új feladat',
  'palette.action.newProject': 'Új projekt',
  'nav.search': 'Keresés…',
  'nav.notifications': 'Értesítések',
  'nav.unread': '{count} olvasatlan',
  'nav.markAllRead': 'Összes olvasottnak',
  /* ⚠️ A C17-ig üres a lista, és a héj ezt kimondja — a csengő nem nyílik üres panelre. */
  'nav.noNotifications': 'Nincs értesítés.',
  /* ── C17: az értesítési központ ────────────────────────────────────────────────────────────── */

  /* ⚠️⚠️ KÉT VÁLTOZAT MINDEN FAJTÁHOZ: névvel és név nélkül. A név nélküli nem tartalék fordítás —
   * azt mondja el, ami akkor történt, ha a kolléga már nincs a workspace-ben (0025: a hivatkozás
   * null-ra áll, a sor megmarad).
   *
   * ⚠️ A NÉV RAGOZATLAN, ÉS AZ ÁLLÍTMÁNY UTÁNA JÖN. A magyar magánhangzó-harmónia miatt
   * a `{actor}-tól` a nevek felénél hibás; ez a szórend minden névvel helyes. */
  'notif.mentioned': 'Megjelöltek egy kommentben',
  'notif.mentioned.by': '{actor} megjelölt egy kommentben',
  'notif.commented': 'Új komment a feladatodon',
  'notif.commented.by': '{actor} kommentelt a feladatodon',
  'notif.assigned': 'Hozzárendeltek egy feladathoz',
  'notif.assigned.by': '{actor} hozzárendelt egy feladathoz',
  'notif.status': 'Változott a státusz a feladatodon',
  /* ⚠️ One key, no actor variant — `0029`'s sweep is time, not a person. See `use-notifications`. */
  'notif.dueSoon': 'Közeleg a határidő',

  /* ── C17/C27: the Agent. `screens/agent/agent-screen.tsx`. ──────────────────────────────── */
  'nav.agent': 'Agent',
  'title.agent': 'Agent',
  /* ⚠️ Impersonal, where the mockup greets by first name — no display name is on the wire. */
  /** ⚠️ The nameless fallback — see `agent.hello.*`. Used when we have no display name. */
  'agent.hello.morning1': 'Jó reggelt, {name}.',
  'agent.hello.morning2': 'Szép jó reggelt, {name}.',
  'agent.hello.morning3': 'Jó reggelt, {name}. Szép napot!',
  'agent.hello.morning4': 'Szia, {name}. Jó, hogy itt vagy.',
  'agent.hello.morning5': 'Jó reggelt, {name}. Remélem, jól indult a nap.',
  'agent.hello.morning6': 'Üdv, {name}. Jó, hogy benéztél.',
  'agent.hello.morning7': 'Jó reggelt, {name}. Hogy vagy ma?',
  'agent.hello.afternoon1': 'Szép délutánt, {name}.',
  'agent.hello.afternoon2': 'Szia, {name}. Örülök, hogy látlak.',
  'agent.hello.afternoon3': 'Szia, {name}. Hogy telik a nap?',
  'agent.hello.afternoon4': 'Kellemes délutánt, {name}.',
  'agent.hello.afternoon5': 'Szia, {name}. Remélem, jól halad a napod.',
  'agent.hello.afternoon6': 'Szia, {name}. Jó látni.',
  'agent.hello.afternoon7': 'Szép délutánt, {name}. Örülök, hogy benéztél.',
  'agent.hello.afternoon8': 'Szia, {name}. Miben segíthetek ma délután?',
  'agent.hello.afternoon9': 'Szia, {name}. Remélem, jó napod van.',
  'agent.hello.evening1': 'Szép estét, {name}.',
  'agent.hello.evening2': 'Jó estét, {name}.',
  'agent.hello.evening3': 'Szia, {name}. Hogy sikerült a nap?',
  'agent.hello.evening4': 'Szép estét, {name}. Jó, hogy benéztél.',
  'agent.hello.evening5': 'Szia, {name}. Még dolgozol?',
  'agent.hello.evening6': 'Jó estét, {name}. Miben segíthetek?',
  'agent.hello.evening7': 'Szia, {name}. Remélem, jó napod volt.',
  'agent.hello.evening8': 'Szép estét, {name}. Örülök, hogy látlak.',
  'agent.hello.evening9': 'Szia, {name}. Zárjuk le szépen a napot?',
  'agent.hello.night1': 'Jó estét, {name}.',
  'agent.hello.night2': 'Szia, {name}. Miben segíthetek?',
  'agent.hello.night3': 'Szia, {name}. Itt vagyok, ha kell valami.',
  'agent.hello.night4': 'Jó estét, {name}. Dolgozzunk egy kicsit?',
  'agent.greeting': 'Jó napot. Min dolgozzunk?',
  /**
   * ⚠️⚠️ **EIGHTEEN GREETINGS, AND THE OLD ONE SAID *JÓ NAPOT* AT TWO IN THE MORNING.**
   * Six times of day, three lines each, the reader's given name in every one. `greeting.ts` holds
   * the rule these were chosen against — in short: never open on the clock.
   */
  'agent.placeholder': 'Miben segíthetek?',
  /* ⚠️ The mockup's second composer has its own placeholder (`markup.html` 1563) — a reply is a
     different invitation from an opening question. */
  'agent.reply.placeholder': 'Válasz írása…',
  'agent.send': 'Küldés',
  'agent.stop': 'Leállítás',
  /* ⚠️⚠️ **STILL HERE, AND STILL TRUE — of a deployment with no key.** C17 wrote this sentence for a
     screen wired to nothing; C27 makes it the `agent_not_configured` branch, unchanged. It says the
     model is NOT CONNECTED rather than the mockup's description of an agent that runs on the
     permission-filtered service layer — that sentence would be the most misleading one in the
     product, because it describes safety properties of something wired to nothing. */
  'agent.notConnected':
    'Az Agent még nincs bekötve – nincs mögötte modell, ezért a küldés ki van kapcsolva.',
  /* ⚠️ The plan gate, which is a different sentence from the one above: the deployment can do this,
     this workspace's csomag cannot. Nothing an operator can fix by setting a variable. */
  'agent.notInPlan': 'Az Agent nem része ennek a workspace-nek a csomagjának.',
  /* ⚠️⚠️ **C27.1's `agent.noData` BECAME FALSE THE MOMENT THE TOOLS LANDED, AND A NOTICE THAT IS NO
     LONGER TRUE IS WORSE THAN NONE.** That string said the agent could not see a single task or hour;
     C27.2 gives it four reads. What the standing notice says now is the two things a person cannot
     otherwise tell: it reads rather than remembers, and it cannot change anything yet.

     ⚠️ The key was renamed with the sentence, deliberately — `agent.noData` would have described the
     opposite of what it holds, and a key that lies about its own contents is how a stale string
     survives three chunks. */
  /* ⚠️ **C27.3 rewrote this sentence and kept the key**, because the key names what the line is FOR —
     the standing statement of what this agent may do — and that has not changed. What changed is the
     answer: it can now propose a write, and the second clause is the promise the mockup made and C17
     deleted for being untrue. */
  'agent.readOnly':
    'Az Agent olvassa a feladatokat és az órákat, írás előtt pedig megerősítést kér. Minden szám igazi lekérdezésből származik.',
  /* ── C27.2 · the tool rows. ⚠️ TWO STATES EACH, and the frame carries the tool's machine NAME —
     the server does not know the reader's locale, so `tasks_search` is mapped here. See the frame
     contract's header, and `agent-screen.tsx`'s `stepLabel` for why each is a literal branch.

     ⚠️ **Vowel harmony: the placeholder is where no suffix follows it.** *"{count} feladat"*, never
     *"{count}-t"* — the count messages in this product all put the number first for this reason. */
  'agent.tool.tasks_search.running': 'Feladatok keresése…',
  'agent.tool.tasks_search.done': '{count} feladat',
  'agent.tool.tasks_get.running': 'Feladat beolvasása…',
  'agent.tool.tasks_get.done': 'Feladat beolvasva',
  /* ⚠️ C49.1 · *"mi történt vele"* — a hozzászólások és a napló egy olvasásban. A `done` NEM számot
     mond, mert a lekérés két listát ad vissza, és egy szám egyikről sem szólna. */
  'agent.tool.tasks_activity.running': 'Előzmények beolvasása…',
  'agent.tool.tasks_activity.done': 'Előzmények beolvasva',
  'agent.tool.worklogs_search.running': 'Időbejegyzések keresése…',
  'agent.tool.worklogs_search.done': '{count} bejegyzés',
  'agent.tool.worklogs_overview.running': 'Óraösszesítő számolása…',
  'agent.tool.worklogs_overview.done': 'Óraösszesítő beolvasva',
  /* ⚠️ The catch-all, for a tool name this bundle does not know — a version skew between the API and
     the browser. `screen-state.tsx`'s rule: a row that renders nothing is the original bug in
     different clothes. */
  /* ⚠️ **C27.3's two write tools, and their *done* state does NOT say the write happened** — the tool
     proposed it. *"Megerősítésre vár"* is the true sentence, and the card underneath is where the
     decision is. */
  'agent.tool.worklogs_log.running': 'Rögzítés előkészítése…',
  'agent.tool.worklogs_log.done': 'Megerősítésre vár',
  'agent.tool.tasks_update.running': 'Módosítás előkészítése…',
  'agent.tool.tasks_update.done': 'Megerősítésre vár',
  'agent.tool.tasks_create.running': 'Új feladat előkészítése…',
  'agent.tool.tasks_create.done': 'Megerősítésre vár',
  'agent.tool.worklogs_update.running': 'Javítás előkészítése…',
  'agent.tool.worklogs_update.done': 'Megerősítésre vár',
  /* ⚠️ C49 · R-36's két törlése. ⚠️ A *done* sor ugyanazt mondja, mint a többi írásnál: a kártya
     fölötti sor nem sugallhatja, hogy bármi eltűnt volna. */
  'agent.tool.worklogs_delete.running': 'Törlés előkészítése…',
  'agent.tool.worklogs_delete.done': 'Megerősítésre vár',
  'agent.tool.tasks_delete.running': 'Törlés előkészítése…',
  'agent.tool.tasks_delete.done': 'Megerősítésre vár',
  /* ⚠️ C27.4's lookups. ⚠️ Vowel harmony: a szám a tagmondat végén áll. */
  'agent.tool.projects_search.running': 'Projektek keresése…',
  'agent.tool.projects_search.done': '{count} projekt',
  'agent.tool.projects_get.running': 'Projekt beolvasása…',
  'agent.tool.projects_get.done': 'Projekt beolvasva',
  'agent.tool.people_search.running': 'Emberek keresése…',
  'agent.tool.people_search.done': '{count} ember',
  /* ── C44 · the contract spine's eight. ⚠️ **The product's own words**: *szerződés* and *sortétel*,
     which is what the Contracts screen says — never *kontraktus* and never *tétel* on its own.
     ⚠️ Vowel harmony: the count leads and no suffix follows it. */
  'agent.tool.contracts_search.running': 'Szerződések keresése…',
  'agent.tool.contracts_search.done': '{count} szerződés',
  'agent.tool.contracts_get.running': 'Szerződés beolvasása…',
  'agent.tool.contracts_get.done': 'Szerződés beolvasva',
  'agent.tool.line_items_search.running': 'Sortételek beolvasása…',
  'agent.tool.line_items_search.done': '{count} sortétel',
  'agent.tool.contracts_worklogs.running': 'Szerződés óráinak beolvasása…',
  'agent.tool.contracts_worklogs.done': '{count} bejegyzés',
  'agent.tool.contracts_create.running': 'Új szerződés előkészítése…',
  'agent.tool.contracts_create.done': 'Megerősítésre vár',
  'agent.tool.contracts_update.running': 'Szerződés módosításának előkészítése…',
  'agent.tool.contracts_update.done': 'Megerősítésre vár',
  'agent.tool.line_items_create.running': 'Új sortétel előkészítése…',
  'agent.tool.line_items_create.done': 'Megerősítésre vár',
  'agent.tool.line_items_update.running': 'Sortétel módosításának előkészítése…',
  'agent.tool.line_items_update.done': 'Megerősítésre vár',
  /* ── Phase 3 · working time. A *szabadságkérés* és a *túlóraigény* a Kérelmek képernyő saját szava;
     az elbírálás jóváhagyást és elutasítást egyaránt jelent, ezért egy kulcs mindkettőre. ─────── */
  'agent.tool.leave_requests_search.running': 'Szabadságkérések keresése…',
  'agent.tool.leave_requests_search.done': '{count} szabadságkérés',
  'agent.tool.leave_requests_get.running': 'Szabadságkérés beolvasása…',
  'agent.tool.leave_requests_get.done': 'Szabadságkérés beolvasva',
  'agent.tool.leave_balances.running': 'Szabadságkeret beolvasása…',
  'agent.tool.leave_balances.done': 'Szabadságkeret beolvasva',
  'agent.tool.leave_requests_decide.running': 'Elbírálás előkészítése…',
  'agent.tool.leave_requests_decide.done': 'Megerősítésre vár',
  'agent.tool.overtime_requests_search.running': 'Túlóraigények keresése…',
  'agent.tool.overtime_requests_search.done': '{count} túlóraigény',
  'agent.tool.overtime_requests_decide.running': 'Elbírálás előkészítése…',
  'agent.tool.overtime_requests_decide.done': 'Megerősítésre vár',
  'agent.tool.holidays_search.running': 'Munkaszüneti napok beolvasása…',
  'agent.tool.holidays_search.done': '{count} naptári bejegyzés',
  'agent.tool.schedules_search.running': 'Munkarendek beolvasása…',
  'agent.tool.schedules_search.done': 'Munkarendek beolvasva',
  /* ── Phase 4 · erőforrás, pénz és riportépítő. ⚠️ Vowel harmony: a szám elöl áll, rag nélkül. ── */
  'agent.tool.allocations_search.running': 'Allokációk keresése…',
  'agent.tool.allocations_search.done': '{count} allokáció',
  'agent.tool.allocations_create.running': 'Allokáció előkészítése…',
  'agent.tool.allocations_create.done': 'Megerősítésre vár',
  /* R-46 · clients, projects and project members. */
  'agent.tool.clients_search.running': 'Ügyfelek keresése…',
  'agent.tool.clients_search.done': '{count} ügyfél',
  'agent.tool.project_members_search.running': 'Projekttagok beolvasása…',
  'agent.tool.project_members_search.done': '{count} projekttag',
  'agent.tool.projects_create.running': 'Projekt előkészítése…',
  'agent.tool.projects_create.done': 'Megerősítésre vár',
  'agent.tool.projects_update.running': 'Projektmódosítás előkészítése…',
  'agent.tool.projects_update.done': 'Megerősítésre vár',
  'agent.tool.project_members_add.running': 'Projekttagok felvételének előkészítése…',
  'agent.tool.project_members_add.done': 'Megerősítésre vár',
  'agent.tool.task_comments_add.running': 'Hozzászólás előkészítése…',
  'agent.tool.task_comments_add.done': 'Megerősítésre vár',
  'agent.tool.task_comments_update.running': 'Hozzászólás javításának előkészítése…',
  'agent.tool.task_comments_update.done': 'Megerősítésre vár',
  'agent.tool.task_links_add.running': 'Feladatkapcsolat előkészítése…',
  'agent.tool.task_links_add.done': 'Megerősítésre vár',
  'agent.tool.task_dependencies_add.running': 'Függőség előkészítése…',
  'agent.tool.task_dependencies_add.done': 'Megerősítésre vár',
  'agent.tool.allocations_update.running': 'Allokáció módosításának előkészítése…',
  'agent.tool.allocations_update.done': 'Megerősítésre vár',
  'agent.tool.external_costs_update.running': 'Külső költség módosításának előkészítése…',
  'agent.tool.external_costs_update.done': 'Megerősítésre vár',
  'agent.tool.overtime_requests_raise.running': 'Túlóraigény előkészítése…',
  'agent.tool.overtime_requests_raise.done': 'Megerősítésre vár',
  'agent.tool.reports_update.running': 'Riport módosításának előkészítése…',
  'agent.tool.reports_update.done': 'Megerősítésre vár',
  'agent.tool.reports_duplicate.running': 'Riport másolásának előkészítése…',
  'agent.tool.reports_duplicate.done': 'Megerősítésre vár',
  'agent.tool.reports_refresh.running': 'Riport frissítésének előkészítése…',
  'agent.tool.reports_refresh.done': 'Megerősítésre vár',
  'agent.tool.clients_create.running': 'Ügyfél előkészítése…',
  'agent.tool.clients_create.done': 'Megerősítésre vár',
  'agent.tool.clients_update.running': 'Ügyfélmódosítás előkészítése…',
  'agent.tool.clients_update.done': 'Megerősítésre vár',
  'agent.tool.project_members_update.running': 'Projekttagság módosításának előkészítése…',
  'agent.tool.project_members_update.done': 'Megerősítésre vár',
  'agent.tool.task_relations_search.running': 'Kapcsolatok beolvasása…',
  'agent.tool.task_relations_search.done': '{count} kapcsolat és függőség',
  'agent.tool.task_links_delete.running': 'Kapcsolat törlésének előkészítése…',
  'agent.tool.task_links_delete.done': 'Megerősítésre vár',
  'agent.tool.task_dependencies_delete.running': 'Függőség törlésének előkészítése…',
  'agent.tool.task_dependencies_delete.done': 'Megerősítésre vár',
  'agent.tool.task_comments_delete.running': 'Hozzászólás törlésének előkészítése…',
  'agent.tool.task_comments_delete.done': 'Megerősítésre vár',
  'agent.tool.allocations_delete.running': 'Allokáció törlésének előkészítése…',
  'agent.tool.allocations_delete.done': 'Megerősítésre vár',
  'agent.tool.external_costs_delete.running': 'Külső költség törlésének előkészítése…',
  'agent.tool.external_costs_delete.done': 'Megerősítésre vár',
  'agent.tool.reports_delete.running': 'Riport törlésének előkészítése…',
  'agent.tool.reports_delete.done': 'Megerősítésre vár',
  'agent.tool.project_members_delete.running': 'Projekttagság törlésének előkészítése…',
  'agent.tool.project_members_delete.done': 'Megerősítésre vár',
  'agent.tool.leave_requests_create.running': 'Szabadságkérés előkészítése…',
  'agent.tool.leave_requests_create.done': 'Megerősítésre vár',
  'agent.tool.invoices_search.running': 'Számlák keresése…',
  'agent.tool.invoices_search.done': '{count} számla',
  'agent.tool.invoices_create.running': 'Számla előkészítése…',
  'agent.tool.invoices_create.done': 'Megerősítésre vár',
  'agent.tool.invoices_set_status.running': 'Státuszmódosítás előkészítése…',
  'agent.tool.invoices_set_status.done': 'Megerősítésre vár',
  'agent.tool.external_costs_search.running': 'Külső költségek keresése…',
  'agent.tool.external_costs_search.done': '{count} külső költség',
  'agent.tool.external_costs_create.running': 'Külső költség előkészítése…',
  'agent.tool.external_costs_create.done': 'Megerősítésre vár',
  'agent.tool.contracts_finance.running': 'Szerződés pénzügyeinek beolvasása…',
  'agent.tool.contracts_finance.done': 'Pénzügyek beolvasva',
  'agent.tool.rate_cards_search.running': 'Díjszabások beolvasása…',
  'agent.tool.rate_cards_search.done': 'Díjszabások beolvasva',
  'agent.tool.reports_run.running': 'Riport futtatása…',
  'agent.tool.reports_run.done': '{count} sor',
  'agent.tool.reports_search.running': 'Mentett riportok keresése…',
  'agent.tool.reports_search.done': '{count} riport',
  'agent.tool.reports_get.running': 'Riport beolvasása…',
  'agent.tool.reports_get.done': 'Riport beolvasva',
  'agent.tool.reports_create.running': 'Riport mentésének előkészítése…',
  'agent.tool.reports_create.done': 'Megerősítésre vár',
  'agent.tool.unknown.running': 'Adat beolvasása…',
  'agent.tool.unknown.done': 'Adat beolvasva',
  /* ⚠️ One sentence for every refusal, and the model's own reply says which — `tool_end` carries a
     CODE, and the useful explanation is the prose the model writes from the refusal it was given. */
  'agent.tool.failed': 'Ez az olvasás nem sikerült',
  /* ⚠️ **One card at a time.** A second proposal in the same turn is refused server-side, and this is
     what that row says — the model prepared one thing and it is waiting for the person. */
  'agent.tool.notRun': 'Nem futott – megerősítésre vár',
  /* ⚠️ The chip on a folded tool row: how many identical calls one line stands for. A count of
     ROWS, never a figure a tool returned — `agent-screen.tsx` `stepRows` argues the fold. */
  'agent.tool.repeated': '{count}×',

  /* ── C27.3 · the confirmation card. R-25 ──────────────────────────────────────────────────
     ⚠️⚠️ **THE SENTENCE ON THE CARD IS NOT HERE, AND THAT IS THE ONE EXCEPTION IN THE PRODUCT.**
     `proposal.summary` arrives from the API already written, in this member's own locale, because the
     words somebody approves and the row that gets written must come from the same call. What IS here
     is everything around it: the heading, the two buttons and the four settled states. */
  /* ── C43 · a file handed to the Agent ──────────────────────────────────────────────────────
     ⚠️ The strip that lists them is `Attachments`, C22's own component, so every string about a
     file — the download, the remove, the refusals — is already in `task.attachment.*`. These two
     are the composer's own control and nothing else. */
  'agent.attach': 'Fájl csatolása',
  /* ⚠️ **The accepted formats are IN the hint**, because the file picker's filter is a suggestion
     the operating system may ignore and says nothing at all on a drag-and-drop. Somebody who
     attaches a PDF should learn why from a sentence, not from a refusal. */
  'agent.attachHint': 'CSV vagy Excel (.xlsx) csatolása – a táblázat sorait tudom elolvasni',

  /* ── C47 · a fájl oda tartozik, ahová küldték — és megmarad ────────────────────────────────
     ⚠️ C43 azt írta ide, hogy minden fájlról szóló szöveg a `task.attachment.*` alatt van, mert a
     csatolmánylistát C22 komponense rajzolta. C47 óta nem: a chip a saját üzenete fölött, a
     szerkesztő fölötti szalag és a böngészőpanel három olyan felület, amit a `task.attachment.*`
     szavai nem tudnak leírni — az egyik a *„még nem küldtem el”*, a másik a *„melyik
     beszélgetésből”*. A letöltés szövege szándékosan külön kulcs: ugyanaz a mondat, de a kettő nem
     ugyanarra a listára mutat, és egy közös kulcs a következő átfogalmazásnál mindkettőt
     elmozdítaná. */
  'agent.files.browse': 'Korábbi fájlok',
  'agent.files.title': 'Fájlok',
  'agent.files.thisThread': 'Ebben a beszélgetésben',
  'agent.files.allThreads': 'Összes feltöltött fájl',
  /* ⚠️ **Ez nem díszítés.** A chip eltűnik a szerkesztő fölül, amint elküldik a kérdést — és ha
     előtte nem mondta meg, hová megy, az eltűnés úgy olvasható, mintha a feltöltés kárba ment
     volna. */
  'agent.files.notSentYet': 'még nem küldted el',
  'agent.files.sent': 'elküldve',
  'agent.files.noneHere': 'Ehhez a beszélgetéshez nincs fájl csatolva.',
  'agent.files.noneAtAll': 'Még nem küldtél fájlt az Agentnek.',
  'agent.files.download': '{name} letöltése',
  'agent.files.remove': '{name} eltávolítása',
  'agent.files.reuse': '{name} csatolása ehhez a beszélgetéshez',
  /* ⚠️ **A gomb azt mondja meg, mi történik, nem azt, hogy mi ő.** Az *„újrafelhasználás”* egy
     kategória; ez egy mondat arról, hogy a fájl a következő kérdéssel megy. */
  'agent.files.reuseHint': 'Csatolás a következő kérdéshez',
  /* ⚠️⚠️ **C48-1 · EZ MONDJA MEG, MIT FOGAD EL, NEM AZT, HOGY „nem sikerült”.** Ugyanez a mondat
     szolgál a böngésző saját szűrésére (behúzott `.pdf`, ami el sem indul) és a szerver
     `unsupported_file_type` elutasítására — egy tény, egy mondat, bárhonnan jön. */
  'agent.files.notTabular':
    'Az Agent táblázatot és képet tud olvasni: CSV, TSV, TXT, .xlsx, PNG, JPG vagy WEBP.',
  /* ⚠️ A behúzott fájl fölé kerülő felirat. Nem *„engedd el”* — az a gomb szövege lenne; ez azt
     mondja meg, hogy ide lehet. */
  'agent.drop.title': 'Húzd ide a fájlt',
  'agent.drop.hint': '{kinds} · legfeljebb {mb} MB',

  'agent.proposal.worklogs_log': 'Időrögzítés megerősítése',
  'agent.proposal.tasks_update': 'Módosítás megerősítése',
  'agent.proposal.tasks_create': 'Új feladat megerősítése',
  'agent.proposal.worklogs_update': 'Javítás megerősítése',
  /* ⚠️ C27.6 · the same four, for a card covering more than one row. A write tool takes a list, and
     *"Új feladat megerősítése"* over six of them is a heading disagreeing with the list beneath it.
     ⚠️ The COUNT is not here and must not be: it is in the server's headline, beside the figures it
     was counted from. */
  'agent.proposal.worklogs_log_many': 'Időrögzítések megerősítése',
  'agent.proposal.tasks_update_many': 'Módosítások megerősítése',
  'agent.proposal.tasks_create_many': 'Új feladatok megerősítése',
  'agent.proposal.worklogs_update_many': 'Javítások megerősítése',
  /* ⚠️⚠️ C49 · R-36 kettő. **A fejléc a *törlés* szót mondja, nem a *megerősítés*-t** — ez az egyetlen
     kártya, amit nem lehet visszavonni, és a művelet neve az, amit először el kell olvasni. */
  'agent.proposal.worklogs_delete': 'Bejegyzés törlése',
  'agent.proposal.worklogs_delete_many': 'Bejegyzések törlése',
  'agent.proposal.tasks_delete': 'Feladat törlése',
  'agent.proposal.tasks_delete_many': 'Feladatok törlése',
  /* ⚠️ C44 · the contract spine's four, singular and plural. */
  'agent.proposal.contracts_create': 'Új szerződés megerősítése',
  'agent.proposal.contracts_create_many': 'Új szerződések megerősítése',
  'agent.proposal.contracts_update': 'Szerződés módosításának megerősítése',
  'agent.proposal.contracts_update_many': 'Szerződés-módosítások megerősítése',
  'agent.proposal.line_items_create': 'Új sortétel megerősítése',
  'agent.proposal.line_items_create_many': 'Új sortételek megerősítése',
  'agent.proposal.line_items_update': 'Sortétel módosításának megerősítése',
  'agent.proposal.line_items_update_many': 'Sortétel-módosítások megerősítése',
  'agent.proposal.unknown': 'Megerősítés',
  /* ⚠️ The mockup's own words for the two buttons (`main.jsx` 3671). */
  'agent.proposal.accept': 'Rögzítés',
  /* ⚠️⚠️ C49 · **A *Rögzítés* egy törlő kártyán a termék legmegtévesztőbb gombja lenne.** Amit valaki
     elolvas a nyomás pillanatában, annak azzal kell megegyeznie, ami utána történik — R-36. */
  'agent.proposal.accept_delete': 'Törlés',
  /* ⚠️ C46 · a correction, and the word is *javítás* rather than *mentés*: nothing is saved by this
     button — a new megerősítő kártya jön vissza, amit ugyanúgy meg kell nyomni. */
  'agent.proposal.revise': 'Javítás',
  /* ⚠️ Why a `Rögzítés` gomb elhalványult: a jóváhagyás a szerveren tárolt értékeket írja be, tehát
     egy megváltoztatott mezővel a régi értéket rögzítené. */
  'agent.proposal.dirty': 'Előbb küldd be a javítást',
  /* ⚠️ §0.4 · a `~` jelölés hozzáférhető neve; az indoklás a `title`-ben van. */
  'agent.proposal.derived': 'Az Agent javaslata',
  'agent.proposal.decline': 'Elvetés',
  'agent.proposal.working': 'Küldés…',
  /* ⚠️ Stated, not counted down: a ticking clock re-renders the thread every second for a number
     nobody watches, and the server's refusal is the real mechanism. */
  'agent.proposal.window': '15 percig érvényes',
  /* ⚠️⚠️ **NEM *"Rögzítve"*.** A jóváhagyás átment; hogy az írás sikerült-e, azt az alatta lévő
     válasz mondja meg — egy zárt hónap vagy egy közben módosult sor elutasítja. Egy kártya, ami azt
     állítja, hogy a sor létezik, az első kitalált visszaigazolás lenne a termékben. */
  'agent.proposal.approved': 'Jóváhagyva',
  'agent.proposal.declined': 'Elvetve',
  'agent.proposal.expired': 'Ez a megerősítés lejárt – kérd újra',
  'agent.proposal.used': 'Erre a megerősítésre már válaszoltál',

  /* ── C27.2 · the data cards. `agent-data-card.tsx` ─────────────────────────────────────── */
  'agent.data.tasks': 'Feladatok',
  'agent.data.worklogs': 'Időbejegyzések',
  'agent.data.overview': 'Óraösszesítő',
  /* ⚠️⚠️ **THE STAMP IS R-26's VISIBLE HALF.** A table in a transcript with no time on it is one
     somebody will read as current — and the sentence above it was written about the moment it was
     read. `/agent`'s `notLiveBecause` is an argument about this string. */
  'agent.data.readAt': 'Beolvasva {at}',
  /* ⚠️ `state-rules.md` §5: a cut list states the TRUE total, never the number of rows shown. */
  'agent.data.of': '{shown} / {total} – szűkíts a kérdésen, ha a többi is kell',
  /* ⚠️ And when nobody has counted, it says so rather than inventing a ratio: the worklog list pages
     with a cursor and `pagination.ts` argues why no envelope carries a count. */
  'agent.data.first': 'Az első {count} sor – van még, de nincs megszámolva',
  'agent.data.empty.title': 'Nincs találat',
  'agent.data.empty.body':
    'Az Agent lefuttatta a keresést, és egy sort sem talált. Ez a válasz, nem hiba.',
  /* ⚠️ The dropped columns are NAMED — the same rule as a truncated total. Five of eight fit a
     válaszban; a projekt általában maga a szűrő, ami a listát adta. */
  'agent.data.tasks.dropped': 'A projekt, a prioritás és a becslés a feladatlistán látszik.',
  /* ⚠️⚠️ **C27.4 · a reopened beszélgetés újraolvassa a sorokat**, és ami közben már nem látszik, azt
     ez a mondat magyarázza meg — nélküle a fölötte lévő válasz tűnik hibásnak. ⚠️ Vowel harmony: a
     szám a tagmondat végén áll, nem ragozva. */
  'agent.data.missing': 'Ebből {count} sor már nem látszik neked.',
  /* ⚠️⚠️ **WHOSE ÓRÁI — a hét összesítése, ami csendben a workspace-é, hibás válasz diagrammal.** A
     frame gép-értéket hoz (`own` / `everyone`), a mondat itt készül, HU-ban és EN-ben. */
  'agent.data.subject.own': 'A te óráid',
  'agent.data.subject.everyone': 'A workspace órái',
  'agent.data.noHours': 'Ezen a héten nincs rögzített óra.',
  'agent.thinking': 'Gondolkodik',
  'agent.thinkingShow': 'Gondolatmenet megjelenítése',
  'agent.thinkingHide': 'Gondolatmenet elrejtése',
  /* ⚠️ Labels for the two sides of a turn. The mockup's own words (`markup.html` 1536). */
  'agent.you': 'Te',
  'agent.agent': 'Agent',
  'agent.working': 'Dolgozom rajta…',
  'agent.untitled': 'Új beszélgetés',
  /* 19 Oct 2026 · AG-1 · the dashboard chat on a project's Áttekintés and Pénzügy tabs. */
  'dashChat.placeholder': 'Kérdezz erről a dashboardról…',
  'dashChat.reply': 'Válasz…',
  'dashChat.title': 'Dashboard-asszisztens',
  'dashChat.empty':
    'Kérdezz bármit arról, ami ezen a dashboardon látszik. Az @ gombbal szerződésre, sortételre, időszakra, emberre, feladatra, számlára vagy külső költségre hivatkozhatsz.',
  'dashChat.history': 'Korábbi beszélgetések',
  'dashChat.historySearch': 'Beszélgetések keresése…',
  'dashChat.historyEmpty': 'Még nincs beszélgetés erről a projektről.',
  'dashChat.new': 'Új beszélgetés',
  'dashChat.mention': 'Hivatkozás (@)',
  'dashChat.send': 'Küldés',
  'dashChat.expand': 'Nagyítás',
  'dashChat.shrink': 'Kicsinyítés',
  'dashChat.minimise': 'Lekicsinyítés',
  'dashChat.thinking': 'Gondolkodik…',
  'dashChat.reading': 'Adatok olvasása…',
  'dashChat.mentions': 'Hivatkozható elemek',
  'dashChat.mentionsEmpty': 'Nincs találat.',
  'dashChat.mentionsLoading': 'Betöltés…',
  'dashChat.more': 'Még {count} megjelenítése…',
  'dashChat.kind.contract': 'Szerződések',
  'dashChat.kind.lineItem': 'Sortételek',
  'dashChat.kind.period': 'Időszakok',
  'dashChat.kind.person': 'Emberek',
  'dashChat.kind.task': 'Feladatok',
  'dashChat.kind.invoice': 'Számlák',
  'dashChat.kind.externalCost': 'Külső költségek',
  /* ── C27.4 · the history menu. ⚠️ The mockup's own words (`markup.html` 261–270), which C17
     declined for having no table behind them. ────────────────────────────────────────────────── */
  'agent.history.label': 'Korábbi beszélgetések',
  'agent.history.new': 'Új beszélgetés',
  'agent.history.empty': 'Még nincs beszélgetés',
  'agent.history.search': 'Keresés a beszélgetésekben…',
  'agent.history.delete': 'Beszélgetés törlése',
  /* ⚠️ **A confirm(), and the sentence has to say what is lost.** A transcript is append-only until
     this; the cascade takes the messages and any megerősítésre váró írás with them. */
  'agent.history.confirmDelete':
    'Törlöd ezt a beszélgetést? A benne lévő válaszok és a megerősítésre váró műveletek is eltűnnek.',
  'agent.history.loadFailed': 'Ezt a beszélgetést nem sikerült megnyitni.',
  /* 29 Oct 2026 · a thread renamed from this menu — the row above *törlés*, which becomes a field. */
  'agent.history.rename': 'Beszélgetés átnevezése',
  'agent.history.renameField': 'A beszélgetés neve',
  'agent.history.renameSave': 'Név mentése',
  'agent.history.renameFailed': 'A beszélgetést nem sikerült átnevezni.',
  /* ┌───────────────────────────────────────────────────────────────────────────┐
     │ ⚠️⚠️ C53 · A HARMADIK FEJLÉC-GOMB, ÉS AMIT KIMÁSOL, AZT EGY GÉP OLVASSA.   │
     └───────────────────────────────────────────────────────────────────────────┘

     A gomb egyetlen mondatot tesz a vágólapra; azt valaki beilleszti a saját agentjébe, az agent
     letölti a `/agent-setup/prompt.md`-t, és onnantól a dokumentum vezeti. ⚠️ **A dokumentum maga
     angol** — a gépnek szól, parancsokat és URL-eket tartalmaz, és két példányban két helyen
     romlana el; ez a mondat viszont a KÉPERNYŐN is látszik, úgyhogy ez kétnyelvű.

     ⚠️ Vowel harmony: az `{url}` a mondat végén áll, rag nélkül. */
  'agent.setup.label': 'Saját agent beállítása',
  'agent.setup.copied': 'Kimásolva, illeszd be',
  'agent.setup.copiedToast': 'MCP-csatlakozási útmutató a vágólapon',
  'agent.setup.copiedHint': 'Illeszd be a választott agentedbe.',
  /* ⚠️ **A vágólap el tud bukni** — nem biztonságos origó, megtagadott engedély, olyan böngésző,
     amiben nincs Clipboard API. Ilyen origón viszont az OAuth sem működne, tehát ez a mondat egy
     olyan telepítést nevez meg, ahol a beállítás amúgy sem jutna el a végéig. */
  'agent.setup.failed': 'A másolás nem sikerült',
  'agent.setup.instruction':
    'Töltsd le és hajtsd végre a Forecastify beállítási utasításait innen: {url}',
  /* ── the stream's refusals. One per code in `AGENT_ERROR_CODES`, plus the transport's own. ── */
  'agent.error.rate_limited':
    'Mára elfogyott az Agent kerete ebben a workspace-ben. Holnap újra elérhető.',
  /* 29 Oct 2026 · már nem a „megtelt beszélgetés” kódja: a hosszú beszélgetést az API összefoglalja.
     Ami maradt, az a túl sok eszközkör és az időkorlát — mindkettő ennek az egy kérdésnek a sorsa. */
  'agent.error.turn_too_long':
    'Ez a kérdés túl sokáig futott, ezért leállt. Próbáld újra, vagy bontsd kisebb kérdésekre – a kérdésed megmaradt a beszélgetésben.',
  'agent.error.upstream_unavailable':
    'A modell nem válaszolt. Próbáld újra – a kérdésed megmaradt a beszélgetésben.',
  'agent.error.conversation_not_found': 'Ez a beszélgetés nem található.',
  /* ⚠️ **Három külön mondat, mert három külön teendő.** *"Kérd újra"* és *"már válaszoltál rá"* nem
     ugyanaz, és a lejárt kártya ott van a képernyőn, amikor a különbség számít. */
  'agent.error.proposal_not_found': 'Ez a megerősítés már nem érvényes.',
  'agent.error.proposal_expired': 'Ez a megerősítés lejárt. Kérd újra az Agentet.',
  'agent.error.proposal_already_used': 'Erre a megerősítésre már válaszoltál.',
  /* 29 Oct 2026 · the menu offers no rename on an MCP channel; this is the server's half. */
  'agent.error.conversation_not_renamable': 'Az MCP-n megnyitott csatorna a kliens nevét viseli.',
  /* ⚠️ The catch-all, and it exists because `screen-state.tsx` is right: a screen that renders
     nothing for an unmapped code is the original bug in different clothes. */
  'agent.error.unknown': 'Az Agent nem tudott válaszolni. Próbáld újra.',
  /* ┌───────────────────────────────────────────────────────────────────────────┐
     │ ⚠️⚠️ A KÉRDÉS ALATT, PIROSSAL — MERT A KÉRDÉS OTT MARAD.                  │
     └───────────────────────────────────────────────────────────────────────────┘

     `beginTurn` szándékosan elmenti a kérdést, mielőtt a modellt hívná, hogy egy külső hiba ne
     egye meg, amit valaki beírt. A következménye viszont az, hogy újratöltés után a kérdés ott van
     válasz nélkül — és két egymás utáni ilyen buborék pontosan úgy fest, mintha az alkalmazás
     elvesztette volna az üzenetet. Ez a mondat az, ami megmondja, hogy nem.

     ⚠️ **Nem mondja meg, MIÉRT**, mert nem tudjuk: a hibakód nem kerül tárolásra. A friss hibát a
     szerkesztő alatti értesítés nevezi meg; ez a jelölés a beszélgetésben marad. */
  'agent.turn.noAnswer': 'Erre a kérdésre nem jött válasz.',
  /* 29 Oct 2026 · a válasz előtt az API összefoglalta a beszélgetés korábbi részét. A modell onnantól
     csak az összefoglalót látja, az olvasó viszont mindent — ez a sor mondja meg, hol a határ. */
  'agent.turn.compacted':
    'Az Agent összefoglalta az eddigi beszélgetést – a korábbi részletekre ebből emlékszik.',
  /* ⚠️ **A fájl maga a válasz, semmi más.** Nem export: nincs benne fejléc, dátum vagy
     workspace-név — ezért „letöltése”, nem „exportálása”. A kettő különbségét az R-33 írja le. */
  'agent.turn.download': 'A válasz letöltése Markdown fájlként',
  /* ⚠️⚠️ **C48 · AZ EXPORT AJÁNLAT SZÖVEGE. Semmi nem készült el, amikor ez megjelenik** — a modell
     összeállított egy szűrőt, és a gomb a termék SAJÁT exportálás párbeszédét nyitja meg kitöltve.
     Ezért „Exportálás”, nem „Letöltés”: hogy letöltés lesz-e vagy e-mail, azt a sorok száma dönti
     el, és azt még a szerver sem tudja. R-34. */
  'agent.toasts': 'Az Agent értesítései',
  'agent.export.run': 'Exportálás',
  /* ⚠️⚠️ **NO GLUED SUFFIX, AND THE GATE IN `routes.test.tsx` IS RIGHT TO REFUSE ONE.** `{day}-tól`
     is wrong by vowel harmony for half the dates it would render, and *"…-ig"* has the same defect.
     So the range reads with the dialog's OWN field labels instead — *Ettől* / *Eddig* — which also
     means the control and the form somebody is about to open use one vocabulary. */
  'agent.export.since': 'ettől: {day}',
  'agent.export.until': 'eddig: {day}',
  'agent.export.everything': 'teljes időszak',
  'notif.status.by': '{actor} átmozgatta a feladatodat',

  'nav.workspaceMenu': 'Workspace',
  'nav.userMenu': 'Fiók',
  'nav.dashboardsSection': 'Dashboardok',
  'nav.workspaceSection': 'Workspace',
  'title.home': 'Kezdőlap',
  'title.myTasks': 'Feladataim',
  'title.myProjects': 'Projektjeim',
  'nav.home': 'Kezdőlap',
  'nav.myTasks': 'Feladataim',
  'nav.contracts': 'Szerződések',
  'title.contracts': 'Szerződések',
  'contracts.search': 'Keresés kódra vagy névre…',
  'contract.owner.search': 'Keresés névre…',
  'contracts.toast.bulkStatus': '{count} szerződés státusza módosítva',
  'contracts.empty.title': 'Még nincs szerződés',
  'contracts.empty.body':
    'A szerződés a projekt alatt él, és sortételekre bomlik. Az órák a feladatokon keresztül jutnak el hozzá.',
  'contract.openEnded': 'nyitott',
  /* C56 · the mockup's own badge on a client-scoped contract. */
  'contract.noProject': 'Nincs projekt',
  'contract.scope.onProject': 'Projekthez tartozik',
  'contract.scope.onClient': 'Ügyfélhez tartozik',
  'contract.field.scope': 'Mihez tartozik',
  'contract.error.client': 'Válassz ügyfelet.',
  'contracts.mode': 'Nézet váltása',
  'task.field.lineItem': 'Sortétel',
  'task.field.lineItemNone':
    'Ennek a projektnek még nincs sortétele. Előbb hozz létre egy szerződést és benne sortételt.',
  'lineItem.assignment': 'Hozzárendelés',
  'lineItem.people': 'Személyek',
  'lineItem.people.anyone': 'Bárki',
  'lineItem.people.search': 'Személy keresése…',
  'lineItem.shared.tip': 'Ez a jegy más sortételen is szerepel: {names}',
  'lineItem.shared.open': 'Megnyitás: {name}',
  'lineItem.shared.unknown': 'egy másik sortétel',
  'lineItem.move.currentlyOn': 'jelenleg: {name}',
  'lineItem.move.title': 'Feladat áthelyezése',
  'lineItem.move.body':
    'A(z) {task} feladat és a rá rögzített órák ({hours}) átkerülnek a(z) {from} sortételről ide: {to}.',
  'lineItem.move.confirm': 'Áthelyezés',
  'lineItem.assignClosed':
    'Lezárt sortételhez nem rendelhető új feladat. Nyisd meg újra a sortételt a hozzárendeléshez.',
  'lineItem.error.internalBillable': 'Belső projekten csak nem számlázható sortétel lehet.',
  'lineItem.people.tip.what':
    'Ha személyeket választasz, a sortétel csak az ő óráikat számolja el, a saját időszakán belül. Ha senki nincs kiválasztva (Bárki), a sortétel feladatain bárki órái ide kerülnek.',
  'lineItem.people.tip.note':
    'A díj ettől a sortételé marad, nem a személy díjkártyájáé. Az az óra, amelyet egyik sortétel sem vesz fel, a teljes projektre számít, és figyelmeztetést ad.',
  'lineItem.error.peopleNeedTm':
    'Személyeket csak idő és anyag típusú sortétel nevezhet meg — előbb vedd ki a személyeket, aztán válts típust.',
  'lineItem.error.personNotMember':
    '{people} nem volt a projekt tagja a sortétel időszakában, ezért nem nevezhető meg rajta.',
  'worklog.unidentified':
    'Ez az óra egyik sortételre sem került: a feladat sortételei más személyeket neveznek meg, vagy téged csak más időszakra.',
  'lineItem.openContract': 'Szerződés megnyitása',
  'lineItem.tasks': 'Feladatok',
  'lineItem.tasks.empty':
    'Még nincs ide rendelt feladat. Az órák a feladatokon keresztül jutnak ide.',
  'lineItem.tasks.emptyFiltered': 'A szűrőkre egy feladat sem illeszkedik.',
  'lineItem.tasks.search': 'Keresés kulcsra, címre vagy felelősre…',
  'lineItem.tasks.unassigned': 'Nincs felelős',
  'lineItem.tasks.total': '{count} feladat · {hours}',
  'lineItem.taskKey': 'Kulcs',
  'lineItem.source': 'Forrás',
  'lineItem.assign': 'Feladat hozzárendelése',
  'lineItem.target.title': 'Szinkronizálás',
  'lineItem.target.hint':
    'Ennek a hibakövető-projektnek a feladatait gyűjti; az alábbi szabály szűkíti őket.',
  'lineItem.target.hintNone':
    'Nem szinkronizál. Válassz integrációt és projektet, és a feladatai ide érkeznek.',
  'lineItem.target.syncNow': 'Szinkronizálás most',
  'lineItem.target.instance': 'Integráció',
  /* 4 Nov 2026 · the filters of a target picked and not saved. */
  'lineItem.target.filtersFirst':
    'Mentés előtt állítsd be a szűrőket: csak az ezeknek megfelelő jegyek kerülnek a sortételre.',
  'lineItem.target.noInstance': 'Nincs',
  'lineItem.target.project': 'Hibakövető-projekt',
  'lineItem.target.noProjects': 'Ezen az integráción nincs elérhető projekt',
  'lineItem.target.unreachable': 'nem érhető el',
  'lineItem.target.unreachableBody':
    'A(z) {key} projekt nem érhető el: az integráció már nem látja, vagy archiválták. Amíg így marad, ez a sortétel nem szinkronizál.',
  'lineItem.target.writes': 'Írás a hibakövetőbe',
  'lineItem.target.issueType': 'Új feladat típusa: {type}',
  'lineItem.target.noIssueType': 'Az új feladatok típusa az első szinkronizáláskor dől el',
  'lineItem.target.issueTypeLabel': 'Új feladat típusa',
  'lineItem.target.issueTypeAuto': 'Az első szinkronizáláskor dől el',
  'lineItem.target.issueTypeSubtask': '{type} (alfeladat – szülő nélkül nem hozható létre)',
  'lineItem.target.issueTypesLoading': 'Típusok betöltése…',
  'lineItem.target.issueTypesFailed': 'Nem sikerült lekérni a típusokat a hibakövetőből',
  'lineItem.target.noIssueTypes': 'Ebben a projektben nincs választható típus',
  'lineItem.target.epicHint':
    'Az Epic létrehozásához a Jira néha extra mezőt kér (pl. Epic Name) – ha elutasítja, a futási előzményekben látod.',
  'lineItem.assignSearch': 'Keresés kulcsra vagy címre…',
  'lineItem.unassign': '{key} hozzárendelésének törlése',
  'lineItem.byHand': 'kézi',
  'lineItem.byRule': 'szabály',
  'lineItem.mode.manual': 'Kézzel',
  'lineItem.mode.manualLong': 'Kézi hozzárendelés',
  'lineItem.mode.question': 'Hogyan kerülnek ide a feladatok?',
  'lineItem.settings': 'A feladatok hozzárendelése',
  'lineItem.target.panelLabel': 'Szinkronizálás hibakövetőből',
  'lineItem.target.none': 'Nincs szinkronizálás',
  'lineItem.target.syncShort': 'Szinkronizálás',
  'rule.summary': 'Szabály: {rule}',
  'rule.summaryNone': 'nincs feltétel',
  'rule.summaryWhole': 'a teljes hibakövető-projekt',
  'rule.conditionsLead': 'Feltételek:',
  'rule.conditionsTail': 'teljesüljön',
  'lineItem.mode.rule': 'Szabály szerint',
  'lineItem.period': 'Időszak',
  'lineItem.open': 'Nyitott',
  'lineItem.closed': 'Lezárt',
  'lineItems.search': 'Keresés sortételre…',
  'lineItems.footer': '{count} sortétel',
  'lineItems.empty.title': 'Még nincs sortétel',
  'lineItems.empty.body': 'A sortétel a szerződés alatt él, és a feladatok rá hivatkoznak.',
  'contract.unknown': 'Ez a szerződés nem létezik, vagy nem látható ebben a workspace-ben.',
  'contract.progress': 'Becslés {estimate} · rögzítve {tracked}',
  'common.count': '{count} db',
  'calendar.months':
    'január,február,március,április,május,június,július,augusztus,szeptember,október,november,december',
  'calendar.weekdays': 'H,K,Sze,Cs,P,Szo,V',
  /**
   * ⚠️⚠️ **A rövid alakok külön kulcsok, mert más a céljuk.** A `calendar.weekdays` a
   * naptárrács fejléce — ott egy betű is elég, mert a hely oszlopot jelent. Egy listában
   * („júl 30. · csü”) a `Cs` nem olvasható nap-névként; a mockup `DOW`-ja három betű.
   */
  'calendar.weekdaysAbbr': 'hét,ke,sze,csü,pén,szo,vas',
  'calendar.monthsAbbr': 'jan,febr,márc,ápr,máj,jún,júl,aug,szept,okt,nov,dec',
  'calendar.today': 'Ma',
  'calendar.clear': 'Törlés',
  'calendar.previousMonth': 'Előző hónap',
  'calendar.nextMonth': 'Következő hónap',
  'calendar.placeholder': 'Válassz dátumot',
  'calendar.dialogLabel': 'Dátumválasztó',
  'common.more': 'További műveletek',
  'common.next': 'Következő oldal',
  'common.previous': 'Előző oldal',
  'title.contracts.one': 'Szerződés',
  'contract.action.close': 'Szerződés lezárása',
  /* C56 · the mockup's own wording for the menu item and its refusal. */
  'contract.action.detach': 'Leválasztás a projektről',
  'contract.detached': 'Szerződés leválasztva',
  'contract.detachedNote':
    '{name} mostantól az ügyfél alatt áll, és csak munka nélküli sortételt vehetsz fel rá.',
  'contract.error.stillBound':
    'A szerződésen {count} feladat és {hours} logolt idő tartozik a(z) {project} projekthez. Leválasztás előtt zárd le vagy tedd át ezeket.',
  'contract.action.reopen': 'Újraaktiválás',
  'contract.action.copyLink': 'Link másolása',
  'contract.action.openProject': 'Projekt megnyitása',
  'contract.action.rename': 'Átnevezés',
  'contract.attachment.add': 'Fájl csatolása',
  'contract.closedNotice':
    'A szerződés lezárt: a sortételeire nem vehető fel új feladat, és worklog sem rögzíthető rájuk.',
  'contract.every.month': 'Havonta',
  'contract.every.months': '{step} havonta',
  'contract.every.week': 'Hetente',
  'contract.every.weeks': '{step} hetente',
  'contract.recurrence.lastDay': 'Utolsó nap',
  'contract.occurrences': 'Időszakok',
  'contract.occurrence': 'Időszak',
  'occurrence.count': '{count} időszak',
  'occurrence.plan': 'Terv',
  'occurrence.effort': 'Ráfordítás',
  'occurrence.partial': 'részleges',
  'occurrence.state.planned': 'Tervezett',
  'occurrence.state.active': 'Aktív',
  'occurrence.state.closed': 'Lezárt',
  'occurrence.tip.planned': 'Tervezett időszak – még nem kezdődött el. Az idő nyitja meg.',
  'occurrence.tip.active': 'Aktív időszak – most zajlik, ide logolnak a kollégák.',
  'occurrence.tip.closed': 'Lezárt időszak – az idő lezárta.',
  'occurrence.collectorNote': 'ide logolnak az órák',
  'period.previous': 'Előző időszak',
  'period.next': 'Következő időszak',
  'period.export': 'Export',
  'period.reopened': 'újranyitva',
  'period.closedWhy':
    'Az időszak lejárt, ezért lezárult – nem kézzel zárta le senki. Ha valaki elmaradt a logolással, újranyithatod pótlásra.',
  'period.index': 'Sorszám',
  'period.people': 'Ember',
  'period.budget': 'Keret',
  'period.fact': 'Tény',
  'period.difference': 'Eltérés',
  'period.factOfBudget': 'Tény {fact} / keret {budget}',
  'period.reopen': 'Újranyitás pótlásra',
  /* 17 Oct 2026 · a period row's menu — the phase 2 mockup's `occMenu`. */
  /* 17 Oct 2026 · the period page's grouping, restored over the whole filtered set. */
  'period.group.none': 'Nincs csoport',
  'period.group.member': 'Ember szerint',
  'period.group.lineItem': 'Sortétel szerint',
  'period.group.day': 'Nap szerint',
  'period.group.task': 'Feladat szerint',
  'period.groupCount': '{count} bejegyzés',
  'period.menu.open': 'Megnyitás',
  'period.menu.openFull': 'Megnyitás teljes nézetben',
  'period.menu.exportCsv': 'Időlogok exportálása (CSV)',
  'period.menu.exportXlsx': 'Időlogok exportálása (XLSX)',
  'period.menu.openLogs': 'Időlogok megnyitása',
  'period.menu.copy': 'Időszak összesítő másolása',
  'period.menu.copied': 'Vágólapra másolva',
  'period.menu.missing': 'hiányzik {hours}',
  'period.menu.noneMissing': 'nincs hiányzó',
  'period.menu.stillMissing': 'még hiányzik {hours}',
  'period.menu.planMet': 'a terv teljesült',
  'period.menu.byTime': 'A státuszt az idő állítja',
  'logs.filter.contract': 'Szerződés',
  'period.close': 'Visszazárás',
  'period.reopenHint': 'A kollégák pótolhatják az elmaradt logolást, amíg vissza nem zárod.',
  'period.stillShort': 'Még hiányzik {hours}.',
  'period.metItsBudget': 'A keret teljesült, visszazárható.',
  'period.partialNote':
    'Az ismétlődés természetes vége {natural}, a szerződés viszont {actual} napig tart.',
  'period.noCollector': 'nincs ismétlődő feladat',
  'period.contractLevel': 'Szerződés szintű',
  'period.logs': 'Időlogok',
  'period.logCount': '{count} bejegyzés',
  'period.search': 'Keresés emberre vagy feladatra…',
  'period.who': 'Ki',
  'period.empty': 'Erre az időszakra még nincs időlog.',
  'period.emptyFiltered': 'A szűrőkre nem illeszkedik egy bejegyzés sem.',
  'period.total': '{count} bejegyzés · {hours}',
  'period.unknown': 'Ehhez a szerződéshez nem tartozik ilyen kezdetű időszak.',
  'period.exported': 'Az időszak időlogjai exportálva.',
  'contract.recurrence.times': '{count} alkalom',
  'contract.recurrence.until': 'vége {date}',
  'lineItem.actions': 'Sortétel műveletek',
  'lineItem.action.open': 'Megnyitás',
  'lineItem.action.close': 'Lezárás',
  'lineItem.action.reopen': 'Újranyitás',
  'lineItem.action.delete': 'Törlés',
  'lineItem.action.deleteBlocked': 'Törlés – {count} feladat van rajta',
  'lineItem.assignHint': 'Kézzel választod ki, melyik jegy tartozik ide.',
  'lineItem.assignedNote.rule': '{rule} szabály szerint · {manual} kézi · {excluded} kizárva',
  'lineItem.assignHintRule': 'A szabály hozza be a feladatokat – kézzel is hozzávehetsz egyet.',
  'lineItem.exclude': '{key} kizárása a szabályból',
  'lineItem.overlap':
    'Ezt a szabályt {count} feladat is kielégíti, de máshol számolódnak el – egy másik sortétel vitte el őket:',
  'rule.matches': 'Illeszkedik',
  'rule.matchesSuffix': 'feltételre',
  'rule.match.all': 'minden',
  'rule.match.any': 'bármelyik',
  'rule.field': 'Mező',
  'rule.op': 'Operátor',
  'rule.value': 'Érték',
  'rule.pickValue': 'Válassz…',
  'rule.addCondition': 'Feltétel hozzáadása',
  'rule.wholeProject':
    'Feltétel nélkül a hibakövető-projekt minden jegye ide kerül. A Projekt feltétel nem szűkít rajta: a sortétel céljánál van megadva.',
  'rule.removeCondition': 'Feltétel törlése',
  'rule.excluded': 'Kizárva:',
  'rule.excludedLabel': 'Kizárva',
  'rule.conditions': 'Feltételek',
  'rule.restore': '{key} kizárásának visszavonása',
  'rule.field.priority': 'Prioritás',
  'rule.field.assignee': 'Felelős',
  'rule.field.tag': 'Címke',
  'rule.op.eq': 'egyenlő',
  'rule.op.ne': 'nem egyenlő',
  'rule.op.contains': 'tartalmazza',
  'rule.placeholder.key': 'Pl. HSO-',
  'rule.placeholder.text': 'Szöveg',
  'rule.field.component': 'Komponens',
  'rule.field.fixVersion': 'Javítási verzió',
  'rule.field.epic': 'Epic',
  'rule.op.in': 'egyike ezeknek',
  'rule.pickValues': 'Válassz egyet vagy többet…',
  'rule.addValue': 'Hozzáadás…',
  'rule.removeValue': '{value} eltávolítása',
  'rule.placeholder.list': 'Enterrel vagy vesszővel elválasztva',
  'rule.searchValues': 'Keresés…',
  'rule.noValues': 'Nincs ilyen érték a projekt Jira-feladatain',
  'rule.moreValues': 'Még {count} érték megjelenítése',
  'rule.fewerValues': 'Kevesebb',
  'lineItem.assignedNote': '{count} kézzel hozzárendelt feladat',
  'lineItem.closedBadge': 'lezárt sortétel',
  'lineItem.option.closed': '{label} – lezárt',
  'lineItem.unknown': 'Ez a sortétel nem található ezen a szerződésen.',
  'contract.details': 'Részletek',
  'contract.finance': 'Pénzügy',
  'contract.attachments': 'Csatolmányok',
  'contract.metafields': 'Metafieldek',
  'contract.attachments.empty': 'Még nincs csatolmány ezen a szerződésen.',
  'lineItem.money': 'Pénzügy',
  'lineItem.money.rate': 'Egységár',
  'lineItem.money.currency': 'Pénznem',
  'lineItem.money.budget': 'Büdzsé',
  'lineItem.money.revenue': 'Bevétel',
  /* ── U-5 · per period on a recurring contract, and what that comes to ── */
  'lineItem.money.budgetPerPeriod': 'Büdzsé / időszak',
  'lineItem.money.revenuePerPeriod': 'Bevétel / időszak',
  'lineItem.money.totalOver': '× {count} időszak = {total}',
  'lineItem.money.tip.perPeriod':
    'Ismétlődő szerződésen időszakonként adod meg. A teljes összeg ennyi × a sortétel időszakainak száma.',
  'lineItem.money.tip.invoiced.whatRecurring':
    'Ismétlődő szerződésen időszakra számlázunk, nem sortételre. Minden időszak, amelyre van számla (a piszkozat is számít, a sztornózott nem), a sortétel egy időszakra jutó bevételével számít bele.',
  'lineItem.money.tip.invoiced.calcRecurring':
    'időszakonkénti bevétel × kiszámlázott időszakok + a sortételre korábban kiállított számlák',
  'lineItem.money.cost': 'Bekerülés',
  /* 17 Oct 2026 · spend in money, in every line item and period list. */
  'money.spend': 'Költés',
  'lineItem.money.margin': 'Árrés',
  'lineItem.money.invoiced': 'Kiszámlázva',
  'lineItem.money.remaining': 'Hátralévő',
  'lineItem.money.spent': 'A büdzsé {pct}%-a elköltve',
  'lineItem.money.unrated':
    '{count} dolgozónak nincs érvényes díjkártyája – az ő óráik nem számítanak bele a bekerülésbe.',
  'lineItem.money.tip.rate.what':
    'Amennyit az ügyfél egy óráért vagy egy napért fizet ezen a sortételen.',
  'lineItem.money.tip.rate.note':
    'Ebből és a logolt órákból adódik a bevétel. Napidíjnál egy nap 8 órának számít.',
  'lineItem.money.tip.budget.what':
    'A sortételre szánt belső költségkeret – ennyiből kellene kijönnie a munkának.',
  'lineItem.money.tip.budget.note':
    'A bekerüléssel vetjük össze: a kártya alján lévő sáv mutatja, mennyi fogyott el belőle.',
  'lineItem.money.tip.revenue.what': 'Az ügyféllel megállapodott ár – ennyit kapunk a sortételért.',
  'lineItem.money.tip.revenue.whatTm':
    'Amennyit a sortétel eddig megkeresett: a rá logolt órák az egységáron.',
  'lineItem.money.tip.revenue.calcTm': 'logolt óra × óradíj (napidíj ÷ 8)',
  'lineItem.money.tip.revenue.noteTm': 'Nem gépeled be, minden új időloggal nő.',
  'lineItem.money.tip.revenue.whatNone': 'Nem számlázható sortétel, ezért nem termel bevételt.',
  'lineItem.money.tip.currency.what': 'Ebben a pénznemben áll a sortétel minden összege.',
  'lineItem.money.tip.currency.note':
    'A bekerülést a dolgozók díjkártyájáról erre váltjuk át, a munkaterület árfolyamaival.',
  'lineItem.money.tip.cost.what': 'Mennyibe került nekünk eddig a sortételen végzett munka.',
  'lineItem.money.tip.cost.calc': 'Σ (logolt óra × a dolgozó aznapi költségdíja)',
  'lineItem.money.tip.cost.note':
    'Minden órát a rögzítés napján érvényes díjkártyával árazunk. Akinek nincs díjkártyája, annak az órái semmibe sem kerülnek, így a bekerülés ilyenkor alacsonyabb a valósnál.',
  'lineItem.money.tip.margin.what': 'A bevétel mekkora része marad meg, ha levonjuk a bekerülést.',
  'lineItem.money.tip.margin.calc': '(bevétel − bekerülés) / bevétel',
  'lineItem.money.tip.margin.note':
    'Bevétel nélkül nincs árrés. Negatív érték veszteséget jelez, ezért piros.',
  'lineItem.money.tip.invoiced.what':
    'A sortételre kiállított számlák összege – a piszkozat is beleszámít, a sztornózott nem.',
  'lineItem.money.tip.invoiced.note':
    'Az alatta lévő sáv mutatja, a bevétel hány százalékát számláztuk ki.',
  'lineItem.money.tip.remaining.what': 'Amennyit a bevételből még ki kell számláznunk.',
  'lineItem.money.tip.remaining.calc': 'bevétel − kiszámlázva, legalább 0',
  'lineItem.money.tip.remaining.note':
    'Ha előre számlázunk, nulla marad – a túlszámlázást az mutatja, hogy a kiszámlázott összeg nagyobb a bevételnél.',
  'lineItem.alloc': 'Erőforrás-allokációk',
  'lineItem.alloc.planned': 'Betervezett',
  'lineItem.alloc.logged': 'Rögzített',
  'lineItem.alloc.remaining': 'Maradék terv',
  'lineItem.alloc.ofEstimate': 'a becslés {pct}%-a',
  'lineItem.alloc.noEstimate': 'nincs becslés',
  'lineItem.alloc.ofPlan': 'a terv {pct}%-a',
  'lineItem.alloc.noPlan': 'nincs terv',
  'lineItem.alloc.estimate': 'becslés {hours}',
  'lineItem.scope': 'Hatókör',
  'lineItem.scope.retainer': 'Retainer',
  'lineItem.scope.billable': 'Kereten felüli',
  'lineItem.scope.retainerNote': 'A keret része: a becslése adja az időszaki keretet.',
  'lineItem.scope.billableNote':
    'Kereten felüli munka: nem visz és nem fogyaszt keretet, a bevétel az óradíjból számol.',
  extcost: 'Külső költségek',
  'extcost.new': 'Új külső költség',
  'extcost.search': 'Keresés névre vagy azonosítóra…',
  'extcost.empty':
    'Nincs külső költség ezen a sortételen. Beszállítói számla, licenc, utazás – itt vehető fel.',
  'extcost.emptyProject': 'Ennek a projektnek még nincs külső költsége.',
  'extcost.count': '{count} költség',
  'extcost.sum': 'Σ {amount} (a teljes futamidőre)',
  'extcost.column.name': 'Költség',
  'extcost.column.date': 'Dátum',
  'extcost.column.amount': 'Összeg',
  'extcost.column.link': 'Kapcsolódás',
  'extcost.column.recurrence': 'Ismétlődés',
  'extcost.column.owner': 'Felelős',
  'extcost.column.total': 'Teljes futamidő',

  'extcost.link.project': 'Projekt szintű',
  'extcost.link.lineItem': 'Sortétel',
  'extcost.link.occurrence': 'Időszak',

  'extcost.recur.once': 'Egyszeri',
  'extcost.recur.repeats': 'Ismétlődő',
  'extcost.recur.weekly': 'Hetente',
  'extcost.recur.monthly': 'Havonta',
  'extcost.recur.everyNWeeks': 'Minden {step}. hét',
  'extcost.recur.everyNMonths': 'Minden {step}. hónap',
  'extcost.recur.note': 'Ez az összeg minden érintett időszakra rákerül – nem oszlik szét köztük.',
  'extcost.periods': '{count} időszak · összesen {total}',

  'extcost.dialog.new': 'Új külső költség',
  'extcost.dialog.edit': 'Külső költség szerkesztése',
  'extcost.dialog.hint': 'Ami nem valakinek az ideje: beszállítói számla, licenc, utazás, eszköz.',
  'extcost.field.name': 'Megnevezés',
  'extcost.field.namePlaceholder': 'pl. Algolia keresési licenc',
  'extcost.field.amount': 'Összeg',
  'extcost.field.currency': 'Pénznem',
  'extcost.field.date': 'Dátum',
  'extcost.field.link': 'Kapcsolódás',
  'extcost.field.pickLineItem': 'Válassz sortételt…',
  'extcost.field.pickContract': 'Válassz szerződést…',
  'extcost.field.period': 'Időszak',
  'extcost.field.pickPeriod': 'Válassz időszakot…',
  'extcost.field.recurrence': 'Ismétlődés',
  'extcost.field.every': 'Minden',
  'extcost.field.unit': 'Egység',
  'extcost.unit.month': 'hónap',
  'extcost.unit.week': 'hét',
  'extcost.field.until': 'Meddig',
  'extcost.field.owner': 'Felelős',
  'extcost.action.edit': 'Szerkesztés',
  'extcost.action.delete': 'Költség törlése',
  'extcost.action.more': 'További műveletek',
  'extcost.action.full': 'Megnyitás teljes nézetben',
  'extcost.action.duplicate': 'Duplikálás',
  'extcost.action.openLink': 'Kapcsolódás megnyitása',

  /* ── C62 · a külső költség saját képernyője ──────────────────────────────── */
  'title.costs': 'Külső költség',
  'extcost.unknown': 'Nincs ilyen külső költség ebben a workspace-ben.',
  'extcost.amount': 'Összeg',
  'extcost.amount.recurNote':
    'Ez az összeg minden érintett időszakra rákerül – nem oszlik szét köztük.',
  'extcost.amount.onceNote': 'A költség a rögzített összeg, egyszer.',
  'extcost.total': 'Teljes futamidő',
  'extcost.periodCount': '{count} időszak',
  'extcost.details': 'Részletek',
  'extcost.field.created': 'Rögzítve',
  'extcost.recur.onceNote': 'Egyszeri költség.',
  'extcost.recur.fromOpen': '{from} óta, nyitott végű',
  'extcost.recur.fromTo': '{from} – {to}',
  'extcost.occurrences': 'Érintett időszakok',
  'extcost.occurrences.note': '{count} időszak · {amount} / időszak',
  'extcost.occurrences.empty':
    'Ez a költség ismétlődik, de a projekt mögött nincs olyan szerződés, aminek időszakai lennének – így egyszer terheli a projektet.',
  'extcost.occ.period': 'Időszak',
  'extcost.occ.state': 'Státusz',
  'extcost.occ.planned': 'Tervezett',
  'extcost.occ.active': 'Aktuális',
  'extcost.occ.closed': 'Lezárt',
  'extcost.files': 'Csatolmányok',
  'extcost.files.empty': 'Még nincs csatolt számla – a PDF az, ami alátámasztja az összeget.',
  'extcost.rowMenu': '{name} műveletei',

  /* ── C57 · Számlák ───────────────────────────────────────────────────────── */
  invoice: 'Számlák',
  'nav.invoices': 'Számlák',
  'title.invoices': 'Számlák',
  'invoice.new': 'Új számla',
  'invoice.search': 'Keresés számlaszámra, megjegyzésre…',
  'invoice.empty': 'Nincs számla ebben a nézetben',
  'invoice.emptyBody':
    'Számlát a szerződés sortételére vagy support időszakára állítasz ki – a sortétel és az időszak oldalán a Számlázás akcióval.',
  'invoice.unknown': 'Nincs ilyen számla ebben a workspace-ben.',
  'invoice.column.externalId': 'Számlaszám',
  'invoice.column.project': 'Projekt',
  'invoice.column.note': 'Megjegyzés',
  'invoice.column.date': 'Keltezés',
  'invoice.column.amount': 'Összeg',
  'invoice.column.target': 'Kapcsolódás',
  'invoice.column.status': 'Státusz',
  'invoice.column.owner': 'Kiállító',

  'invoice.view.all': 'Minden számla',
  'invoice.filter.project': 'Projekt',
  'invoice.filter.allProjects': 'Minden projekt',
  'invoice.filter.allTargets': 'Mind',

  'invoice.status.draft': 'Piszkozat',
  'invoice.status.issued': 'Kiállítva',
  'invoice.status.paid': 'Kifizetve',
  'invoice.status.void': 'Sztornó',

  'invoice.target.lineItem': 'Sortétel',
  'invoice.target.occurrence': 'Időszak',
  'invoice.target.gone': 'törölt sortétel',

  'invoice.action.open': 'Megnyitás',
  'invoice.action.edit': 'Szerkesztés',
  'invoice.action.delete': 'Számla törlése',
  'invoice.action.status': 'Számla státusza',
  'invoice.action.more': 'További műveletek',
  'invoice.action.openTarget': 'Kapcsolódás megnyitása',
  'invoice.action.issue': 'Számla kiállítása',

  'invoice.dialog.new': 'Új számla',
  'invoice.dialog.edit': 'Számla szerkesztése',
  'invoice.dialog.bill': 'Számlázás',
  'invoice.dialog.hint': 'Számlát sortételre vagy support időszakra állítunk ki.',
  'invoice.dialog.billHint':
    'A számla a kiválasztott forrásra kerül – utána eldöntheted, lezárod-e.',

  'invoice.field.externalId': 'Számlaszám',
  'invoice.field.externalIdPlaceholder': 'pl. LN-2026-0388',
  'invoice.field.amount': 'Összeg',
  'invoice.field.currency': 'Pénznem',
  'invoice.field.currencyNote': 'A forrás bevételének pénzneme.',
  'invoice.field.date': 'Keltezés',
  'invoice.field.status': 'Státusz',
  'invoice.field.target': 'Kapcsolódás',
  'invoice.field.pickLineItem': 'Válassz sortételt…',
  'invoice.field.contract': 'Szerződés',
  'invoice.field.pickContract': 'Válassz szerződést…',
  'invoice.field.pickPeriod': 'Válassz időszakot…',
  'invoice.field.note': 'Megjegyzés',
  'invoice.field.notePlaceholder': 'Mit tartalmaz a számla?',
  'invoice.field.owner': 'Kiállító',
  'invoice.field.source': 'Forrás',
  'invoice.field.created': 'Rögzítve',
  'invoice.field.remainingHint': 'Hátralévő bevétel a forráson: {amount}.',

  'invoice.close.lineItem': 'Lezárjuk a sortételt?',
  'invoice.close.lineItemHint': 'Ha lezárás után feladat vagy időlog kerül rá, arról alert szól.',
  'invoice.close.occurrence': 'Lezárjuk az időszakot?',
  'invoice.close.occurrenceHint': 'A lezárt időszakra nem megy több logolás, amíg újra nem nyitod.',

  'invoice.amount': 'Összeg',
  'invoice.amount.note': 'A számla kiállított összege.',
  'invoice.details': 'Részletek',
  'invoice.note': 'Megjegyzés',
  'invoice.note.placeholder': 'Mit tartalmaz a számla?',
  'invoice.files': 'Csatolmányok',
  'invoice.files.empty': 'Nincs csatolt dokumentum. Húzd be a kiállított számla PDF-jét.',
  'invoice.attach': 'Fájl csatolása ehhez: {id}',
  'invoice.attachments': 'Csatolt fájlok: {count}',

  'invoice.error.overRevenue': 'Az összeg nem lehet több a hátralévő bevételnél ({amount}).',
  'invoice.error.byPeriod':
    'Ismétlődő szerződésen időszakra számlázunk, nem sortételre – és a korábbi sortétel-számlák már nem módosíthatók.',

  'invoice.bill': 'Számlázás',
  'invoice.billMore': 'További számla',
  'invoice.none': 'Még nincs számlázva',
  'invoice.billedPct': 'A bevétel {pct}%-a kiszámlázva.',
  'invoice.section': 'Számlák',
  'invoice.section.note': '{count} számla · Σ {amount}',
  'invoice.section.emptyLineItem': 'Még nincs számla ezen a sortételen.',
  'invoice.section.byPeriod':
    'Ismétlődő szerződésen az időszakokra számlázunk – a kiszámlázott összeget a Pénzügy kártya mutatja.',
  'invoice.section.emptyPeriod': 'Még nincs számla ezen az időszakon.',

  /* ── C58 · a projekt Pénzügy fülje ──────────────────────────────────────── */
  'finance.calc': 'Számítás',
  'finance.pick.contract': 'Szerződés választása',
  'finance.pick.scope': 'Hatókör választása',
  'finance.pick.currency': 'Megjelenítés pénzneme',
  'finance.pick.more': 'További műveletek',
  'finance.action.openContract': 'Szerződés megnyitása',
  'finance.scope.whole': 'Teljes szerződés',
  'finance.scope.periodHint': 'Shift+kattintással a két időszak közötti összeset kijelölöd.',
  'finance.scope.periodSearch': 'Időszak keresése…',
  'finance.scope.periodNone': 'Nincs ilyen időszak.',
  'finance.scope.periodsMany': '{count} időszak',
  'finance.scope.itemHint': 'Shift+kattintással a két sortétel közötti összeset kijelölöd.',
  'finance.scope.itemSearch': 'Sortétel keresése…',
  'finance.scope.itemNone': 'Nincs ilyen sortétel.',
  'finance.scope.itemsMany': '{name} és még {count}',
  'finance.scope.period': 'Időszak',
  'finance.scope.lineItem': 'Sortétel',
  'finance.scope.project': 'Teljes projekt',
  'finance.scope.projectHint': 'Minden szerződés együtt, a sortétel nélküli feladatok óráival',
  'finance.currency.contract': 'A szerződés pénzneme',
  'finance.currency.contractHint': 'A sortételeken megadott pénznem – ebben van megállapodva.',
  'finance.currency.workspace': 'A munkaterület pénzneme',
  'finance.currency.workspaceHint': 'A szerződések eltérő pénznemeit erre váltjuk át.',
  'finance.empty.contractLabel': 'SZERZŐDÉS',
  'finance.empty.noContract': 'Ennek a projektnek nincs szerződése',
  'finance.empty.noContractBody':
    'A pénzügyi kimutatás egy szerződésről szól – bevételről, keretről és időszakokról. Vegyél fel egyet a Szerződések fülön.',
  'finance.empty.lineItemLabel': 'SORTÉTEL',
  'finance.empty.noLineItem': 'Ennek a szerződésnek nincs sortétele',
  'finance.empty.noLineItemBody':
    'Minden pénzügyi szám a sortételekből származik: azok hordozzák a keretet, a bevételt és a díjat.',
  'finance.eac.label': 'EAC képlete',
  'finance.eac.cpi': 'Trendtartó EAC',
  'finance.eac.cpiHint': 'büdzsé / CPI – a mostani hatékonyság végig megmarad',
  'finance.eac.etc': 'Tervtartó EAC',
  'finance.eac.etcHint': 'tény + (büdzsé − teljesített érték) – a maradék terv szerint készül',
  'finance.margin.plan': 'Tervezett margó',
  'finance.margin.planHint': 'bevétel − tervezett költés',
  'finance.margin.forecast': 'Előrejelzett margó',
  'finance.margin.forecastHint': 'előrejelzett bevétel − előrejelzett költés (EAC)',
  'finance.external.label': 'Külső költség',
  'finance.external.in': 'Beszámít a költésbe',
  'finance.external.inHint': 'a tény költés és az EAC tartalmazza',
  'finance.external.out': 'Kihagyva',
  'finance.external.outHint': 'csak a munkaóra alapú költés számol',
  'finance.kpi.revenue': 'SZERZŐDÉS BEVÉTEL',
  'finance.kpi.revenueFormula': 'Σ időszaki bevétel',
  'finance.kpi.ofWhichBillable': 'ebből elszámolható {amount}',
  'finance.kpi.budget': 'SZERZŐDÉS BÜDZSÉ',
  'finance.kpi.bac': 'BAC: teljes költségkeret',
  'finance.kpi.periodSpend': 'IDŐSZAK KÖLTÉS / BÜDZSÉ',
  'finance.kpi.runRate': 'FUTAMRÁTA',
  'finance.kpi.perDay': '{amount} / nap',
  'finance.kpi.allowedPerDay': 'engedett {amount} / nap',
  'finance.kpi.runRateFormula': '{amount} / {days} munkanap',
  'finance.kpi.overrun': 'ELŐREJELZETT TÚLLÉPÉS',
  'finance.kpi.overrunFormula': 'előrejelzés − büdzsé',
  'finance.kpi.projected': 'előrejelzés {amount}',
  'finance.kpi.periodPlanMargin': 'IDŐSZAK TERVEZETT MARGÓ',
  'finance.kpi.periodForecastMargin': 'IDŐSZAK ELŐREJELZETT MARGÓ',
  'finance.kpi.cpi': 'CPI',
  'finance.kpi.cpiSub': 'költséghatékonysági index',
  'finance.kpi.spi': 'SPI',
  'finance.kpi.layers': 'RETAINER · ELSZÁMOLHATÓ',
  'finance.kpi.layersFormula': 'margó rétegenként',
  'finance.kpi.layersSub': 'retainer | elszámolható',
  'finance.kpi.revenueFixed': 'BEVÉTEL',
  'finance.kpi.revenueFixedFormula': 'Σ sortétel bevétel',
  'finance.kpi.accrual': 'megállapodott bevétel',
  'finance.kpi.budgetFixed': 'BÜDZSÉ',
  'finance.kpi.plannedCost': 'TERVEZETT KÖLTÉS',
  'finance.kpi.plannedFormula': 'allokáció × költségdíj',
  'finance.kpi.fromFrame': 'keretből származtatva',
  'finance.kpi.thinPlan': 'az allokáció csak a hatókör részét fedi',
  'finance.kpi.actualCost': 'TÉNY KÖLTÉS',
  'finance.kpi.actualFormula': 'logolt × költségdíj',
  'finance.kpi.ofWhichExternal': 'ebből külső {amount}',
  'finance.kpi.planMargin': 'TERVEZETT MARGÓ',
  'finance.kpi.forecastMargin': 'ELŐREJELZETT MARGÓ',
  'finance.panel.utilisation': 'BÜDZSÉ-HASZNOSÍTÁS',
  'finance.panel.budgetIs': 'BÜDZSÉ {amount}',
  'finance.panel.profitability': 'PROFITABILITÁS',
  'finance.bar.budget': 'BÜDZSÉ',
  'finance.bar.planned': 'TERVEZETT KÖLTÉS',
  'finance.bar.actual': 'TÉNY KÖLTÉS',
  'finance.bar.projectExternal': 'Projekt szintű költség',
  'finance.chart.current': 'Aktuális időszak',
  'finance.tip.projectExternal.name': 'Projekt szintű külső költség',
  'finance.tip.projectExternal.what':
    'Olyan külső költség, ami a projekthez tartozik, de egyik szerződéshez sem.',
  'finance.tip.projectExternal.calc': 'Σ projekt szintű költség az időszakban',
  'finance.tip.projectExternal.note':
    'Nincs beleszámolva ennek a szerződésnek a költségébe – máskülönben a projekt minden szerződése kifizetné ugyanazt.',
  'finance.bar.external': 'KÜLSŐ KÖLTSÉG',
  'finance.bar.externalExcluded': 'KÜLSŐ KÖLTSÉG · KIHAGYVA',
  'finance.bar.earned': 'TELJESÍTETT ÉRTÉK',
  'finance.foot.etc': 'ETC: hátralévő költés',
  'finance.foot.vac': 'VAC: keret-eltérés',
  'finance.foot.cv': 'CV: költség-eltérés',
  'finance.foot.sv': 'SV: ütem-eltérés',
  'finance.foot.tcpi': 'TCPI: szükséges hatékonyság',
  'finance.foot.pv': 'PV: terv máig',
  'finance.list.revenue': 'BEVÉTEL',
  'finance.list.planMargin': 'TERVEZETT MARGÓ',
  'finance.list.planMarginPct': 'TERVEZETT MARGÓ %',
  'finance.list.forecastRevenue': 'ELŐREJELZETT BEVÉTEL',
  'finance.list.forecastMargin': 'ELŐREJELZETT MARGÓ',
  'finance.list.forecastMarginPct': 'ELŐREJELZETT MARGÓ %',
  'finance.list.actualRate': 'TÉNYLEGES ÓRADÍJ',
  'finance.list.targetRate': 'CÉL ÓRADÍJ',
  'finance.list.plannedRate': 'TERVEZETT ÓRADÍJ',
  'finance.list.invoiced': 'KISZÁMLÁZVA',
  'finance.list.invoicedPct': 'KISZÁMLÁZVA / BEVÉTEL',
  'finance.list.wip': 'WIP',
  'finance.chart.titleBudget': 'BÜDZSÉ ÉS FELHASZNÁLÁS',
  'finance.kinds.budgetTitle': 'Büdzsé tartás – eltérés a büdzsétől',
  'finance.kinds.profitTitle': 'Profit',
  'finance.kinds.total': 'Összesen',
  'finance.kinds.external': 'Külső költség',
  'finance.kinds.forecast': 'Előrejelzés (allokáció alapján)',
  'finance.kinds.actualsOnly': 'a lezárt időszakokból',
  'finance.chart.titleProfit': 'BEVÉTEL ÉS MARGÓ',
  'finance.chart.modeBudget': 'Büdzsé',
  'finance.chart.modeProfit': 'Profitabilitás',
  'finance.chart.perPeriod': 'Periódus',
  'finance.chart.cumulative': 'Kumulált',
  'finance.chart.loggedHours': 'LOGOLT ÓRA',
  'finance.chart.forecastHours': 'ELŐREJELZETT ÓRA',
  'finance.chart.plannedHours': 'TERVEZETT ÓRA',
  'finance.chart.budget': 'BÜDZSÉ',
  'finance.chart.used': 'FELHASZNÁLT BÜDZSÉ',
  'finance.chart.revenue': 'BEVÉTEL',
  'finance.chart.granDay': 'Nap',
  'finance.chart.granWeek': 'Hét',
  'finance.chart.granMonth': 'Hónap',
  'finance.chart.margin': 'MARGÓ',
  'finance.chart.empty': 'Ehhez a hatókörhöz nincs kirajzolható időszak.',
  'finance.fx.unconverted':
    'Nincs árfolyam ehhez: {codes} – ezek az összegek 1:1 arányban kerültek a(z) {currency} végösszegekbe, tehát az itt látható számok közelítőek. Vedd fel az árfolyamot a pénznem-beállításoknál.',
  'finance.chart.hoursCum': 'KUMULÁLT ÓRÁK',
  'finance.chart.hoursPeriod': 'IDŐSZAKI ÓRÁK',
  'finance.chart.forecast': 'ELŐREJELZÉS',
  /* 31 Oct 2026 · EV-10 · the band behind the line, and the rule a forecast runs past. */
  'finance.chart.range': 'ELŐREJELZÉSI SÁV',
  'finance.chart.deadline': 'Határidő',
  /* 19 Oct 2026 · which EAC a forecast row of the chart's tooltip follows. */
  'finance.chart.modeCpi': 'TREND',
  'finance.chart.modeEtc': 'TERV',
  'finance.table.periods': 'IDŐSZAKOK · PROFITABILITÁS',
  'finance.table.periodCount': '{count} időszak',
  'finance.table.lineItems': 'SORTÉTELEK · PROFITABILITÁS',
  'finance.table.lineItemCount': '{count} sortétel',
  'finance.table.externalExcluded':
    'A külső költség most ki van hagyva a KÖLTÉS oszlopból – a saját oszlopa változatlanul mutatja.',
  'finance.col.period': 'IDŐSZAK',
  'finance.col.status': 'STÁTUSZ',
  'finance.col.budget': 'BÜDZSÉ',
  'finance.col.revenue': 'BEVÉTEL',
  'finance.col.billable': 'ELSZÁMOLHATÓ',
  'finance.col.spend': 'KÖLTÉS',
  'finance.col.external': 'KÜLSŐ KÖLTSÉG',
  'finance.col.margin': 'MARGÓ',
  'finance.col.marginPct': 'MARGÓ %',
  'finance.col.invoiced': 'KISZÁMLÁZVA',
  'finance.col.invoice': 'SZÁMLA',
  'finance.col.name': 'NÉV',
  'finance.col.type': 'TÍPUS',
  'finance.col.planned': 'TERVEZETT KÖLTÉS',
  'finance.col.ev': 'EV',
  'finance.col.cpi': 'CPI',
  /* ⚠️ Round 5 item 24 — the periods table's status filter. */
  'finance.filter.state': 'Állapot',
  'finance.filter.type': 'Típus',
  'finance.state.past': 'Lezárt',
  'finance.state.current': 'Aktív',
  'finance.state.future': 'Tervezett',
  'finance.total.budget': 'Σ BÜDZSÉ',
  'finance.total.revenue': 'Σ BEVÉTEL',
  'finance.total.spend': 'Σ KÖLTÉS',
  'finance.total.margin': 'Σ MARGÓ',
  'finance.total.invoiced': 'Σ KISZÁMLÁZVA',
  'finance.breakdown.title': 'KÖLTÉS-BONTÁS',
  'finance.breakdown.byPerson': 'Ember',
  'finance.breakdown.byRole': 'Projektszerepkör',
  'finance.breakdown.col.people': 'Fő',
  'finance.breakdown.col.planned': 'Tervezett',
  'finance.breakdown.col.logged': 'Tény óra',
  'finance.breakdown.col.cost': 'Tény költés',
  'finance.breakdown.col.share': 'Arány',
  'finance.tip.bd.group.name': 'Csoport',
  'finance.tip.bd.group.what': 'A bontás dimenziója – a fenti választóval váltható.',
  'finance.tip.bd.people.name': 'Fő',
  'finance.tip.bd.people.what': 'Hány ember tartozik a csoportba a hatókörben.',
  'finance.tip.bd.people.calc': 'egyedi emberek száma',
  'finance.tip.bd.planned.name': 'Tervezett óra',
  'finance.tip.bd.planned.what': 'A csoport allokációja a hatókör időszakára.',
  'finance.tip.bd.planned.calc': 'Σ allokált óra',
  'finance.tip.bd.logged.name': 'Tény óra',
  'finance.tip.bd.logged.what': 'A csoport rögzített órái a hatókörben.',
  'finance.tip.bd.logged.calc': 'Σ időlog',
  'finance.tip.bd.cost.name': 'Tény költés',
  'finance.tip.bd.cost.what': 'A csoport rögzített óráinak belső költsége.',
  'finance.tip.bd.cost.calc': 'Σ (logolt óra × költségdíj)',
  'finance.tip.bd.cost.note':
    'Akinek nincs érvényes díjkártyája, annak az órái nem kerülnek bele – a panel fejléce jelzi.',
  'finance.tip.bd.share.name': 'Arány',
  'finance.tip.bd.share.what': 'A csoport része a hatókör teljes tény költéséből.',
  'finance.tip.bd.share.calc': 'csoport tény költése / összes tény költés',
  'finance.breakdown.noRole': 'Nincs projektszerepkör',
  'finance.breakdown.headcount': '{count} fő',
  'finance.breakdown.empty': 'Ebben a hatókörben még senki nem logolt és nincs betervezve.',
  'finance.breakdown.unrated':
    '{count} embernek nincs érvényes díjkártyája – az ő órájuk nulla forinttal szerepel.',
  'finance.tip.accrual':
    'A bevétel a megállapodott összeg: fix díjnál és licencnél az ár, T&M-nél az eddig logolt idő × díj plusz a hátralévő becslés a díjon. Hogy ebből mennyit számláztunk ki, azt a KISZÁMLÁZVA mutatja.',
  'finance.tip.marginSwitch': 'A felirat legördülőjében a másik margóra váltható.',
  'finance.tip.list.revenue': 'A hatókör megállapodott bevétele – nem a kiszámlázott összeg.',
  'finance.tip.col.edited': 'Szerkesztett érték mellett ceruza jel áll.',
  'finance.tip.revenue.name': 'Szerződés bevétel',
  'finance.tip.revenue.what':
    'A hatókörbe eső időszakok bevétele – retainer és elszámolható együtt.',
  'finance.tip.revenue.calc': 'Σ időszak bevétel + Σ időszak elszámolható bevétel',
  'finance.tip.revenue.note':
    'A számlázás ettől elmarad vagy előtte jár; azt a KISZÁMLÁZVA mutatja.',
  'finance.tip.revenueFixed.name': 'Bevétel',
  'finance.tip.revenueFixed.what':
    'A hatókör sortételeinek megállapodott bevétele – fix díjnál és licencnél a megadott összeg, T&M-nél az eddig logolt idő × díj plusz a hátralévő becsült órák a díjon.',
  'finance.tip.revenueFixed.calc': 'Σ sortétel bevétel',
  'finance.tip.revenueFixed.note':
    'Nem számlázható sortétel nem ad bevételt. Hogy mennyit számláztunk ki belőle, azt a KISZÁMLÁZVA mutatja.',
  'finance.tip.bac.name': 'BAC: teljes költségkeret',
  'finance.tip.bac.what':
    'A retainer munkára szánt belső költségkeret a hatókör összes időszakára.',
  'finance.tip.bac.calc': 'Σ időszak büdzsé (sortétel költségkeretéből származtatva)',
  'finance.tip.bac.note':
    'Ez nem a bevétel: a bevétel az ügyfélnek számlázott érték, a büdzsé a saját költéskeret.',
  'finance.tip.bacFixed.what': 'A hatókörre szánt belső költségkeret, a sortételeken megadva.',
  'finance.tip.bacFixed.calc': 'Σ sortétel költségkeret',
  'finance.tip.bacFixed.note':
    'T&M sortételnél nincs költségkeret, mert a bevétel a tényből számol. Ez nem a bevétel.',
  'finance.tip.periodSpend.name': 'Időszak költés / büdzsé',
  'finance.tip.periodSpend.what': 'Az aktuális időszakban elköltött pénz a keret arányában.',
  'finance.tip.periodSpend.calc': 'időszak tény költés / időszak büdzsé',
  'finance.tip.periodSpend.note':
    '100% felett az időszak túlköltött. Az alsó sáv az eltelt időt mutatja – ha a költés sávja hosszabb, gyorsabban fogy a keret, mint az idő.',
  'finance.tip.runRate.name': 'Futamráta',
  'finance.tip.runRate.what':
    'Mennyi pénzt költünk átlagosan egy munkanapon az aktuális időszakban.',
  'finance.tip.runRate.calc': 'időszak tény költés / eltelt munkanapok',
  'finance.tip.runRate.note':
    'Az engedett napi érték a büdzsé egyenletes elosztása. Ha a futamráta nagyobb, az időszak vége előtt kifut a keret.',
  'finance.tip.overrun.name': 'Előrejelzett túllépés',
  'finance.tip.overrun.what': 'Mennyivel lépi túl a mostani ütem az időszak büdzséjét a záráskor.',
  'finance.tip.overrun.calc': '(futamráta × időszak munkanapjai) − időszak büdzsé',
  'finance.tip.overrun.note': 'Pozitív érték túllépést jelez. Negatív esetén a keret alatt zárunk.',
  'finance.tip.periodPlanMargin.name': 'Időszak tervezett margó',
  'finance.tip.periodPlanMargin.what':
    'Mennyi maradna az aktuális időszakban, ha a költés a büdzsét pontosan kihasználja.',
  'finance.tip.periodPlanMargin.calc': '(időszak bevétel + elszámolható bevétel) − időszak büdzsé',
  'finance.tip.periodForecastMargin.name': 'Időszak előrejelzett margó',
  'finance.tip.periodForecastMargin.what':
    'Mennyi marad az aktuális időszak zárásakor a mostani futamráta alapján.',
  'finance.tip.periodForecastMargin.calc':
    '(időszak bevétel + elszámolható bevétel) − előrejelzett költés',
  'finance.tip.cpi.name': 'CPI: költséghatékonysági index',
  'finance.tip.cpi.what':
    'Egy logolt órára mennyi teljesített óra jut – a feladatok becsléséből, a státuszuk súlyával.',
  'finance.tip.cpi.whatRecurring':
    'Egy logolt órára mennyi teljesített óra jut – a feladatok becsléséből, a státuszuk súlyával. A Trendtartó EAC a büdzsét ezzel osztja.',
  'finance.tip.cpi.calc': 'teljesített óra / logolt óra',
  'finance.tip.cpi.note': '1,00 felett a keret alatt vagyunk, alatta túlköltünk. 0,92 alatt piros.',
  'finance.tip.spi.name': 'SPI: ütemhatékonysági index',
  'finance.tip.spi.what':
    'A teljesített érték a máig tervezett költéshez mérve – ütemben vagyunk-e.',
  'finance.tip.spi.calc': 'teljesített érték / máig tervezett költés (PV)',
  'finance.tip.spi.calcThin':
    'teljesített érték / PV; a PV itt a keret időarányos része, mert a múltra nincs allokáció',
  'finance.tip.spi.note': '1,00 felett a terv előtt járunk, alatta lemaradás. 0,92 alatt piros.',
  'finance.tip.eac.name': 'EAC: várható végösszeg',
  'finance.tip.eac.what': 'Mennyibe fog összesen kerülni a munka a befejezésig.',
  'finance.tip.eac.note':
    'A képlet a felirat legördülőjében váltható. VAC – várható keret-eltérés: {amount}.',
  'finance.tip.etc.name': 'ETC: hátralévő költés',
  'finance.tip.etc.what': 'Mennyi pénzt kell még elköltenünk a befejezésig.',
  'finance.tip.etc.calc': 'EAC − tény költés',
  'finance.tip.etc.note': 'Ha nagyobb, mint a keretből maradt rész, túlköltés felé tartunk.',
  'finance.tip.vac.name': 'VAC: várható keret-eltérés',
  'finance.tip.vac.what': 'Mennyivel zárunk a kereten belül vagy fölött.',
  'finance.tip.vac.calc': 'büdzsé − EAC',
  'finance.tip.vac.note': 'Negatív érték keret-túllépést jelez.',
  'finance.tip.cv.name': 'CV: költség-eltérés',
  'finance.tip.cv.what': 'A teljesített érték és a tény költés különbsége.',
  'finance.tip.cv.calc': 'teljesített érték − tény költés',
  'finance.tip.cv.note': 'Negatív érték azt jelenti, hogy a haladáshoz képest többet költöttünk.',
  'finance.tip.sv.name': 'SV: ütem-eltérés',
  'finance.tip.sv.what': 'A teljesített érték és a máig tervezett költés különbsége.',
  'finance.tip.sv.calc': 'teljesített érték − terv máig',
  'finance.tip.sv.note': 'Negatív érték lemaradást jelez a tervhez képest.',
  'finance.tip.tcpi.name': 'TCPI: szükséges hatékonyság',
  'finance.tip.tcpi.what':
    'Milyen hatékonysággal kell a maradékot elvégezni, hogy beleférjünk a keretbe.',
  'finance.tip.tcpi.calc': '(büdzsé − teljesített érték) / (büdzsé − tény költés)',
  'finance.tip.tcpi.note':
    '1,00 alatt a maradék kényelmesen belefér; 1,08 felett a keret tartása nem realista.',
  'finance.tip.pv.name': 'PV: máig tervezett költés',
  'finance.tip.pv.what': 'Amennyit a terv szerint mostanáig el kellett volna költenünk.',
  'finance.tip.pv.calc': 'Σ (allokált óra a mai napig × költségdíj)',
  'finance.tip.pv.calcThin':
    'büdzsé × eltelt idő aránya – a múltra nincs allokáció, ezért a keret időarányos része a bázis',
  'finance.tip.pv.note': 'A teljesített értékkel összevetve adja az ütem-eltérést (SV, SPI).',
  'finance.tip.ev.name': 'EV: teljesített érték',
  'finance.tip.ev.what': 'A készültséggel arányos büdzsé-érték.',
  'finance.tip.ev.whatRecurring':
    'Az eltelt időre jutó büdzsé – ismétlődő munkánál a teljesítés az idő múlásával arányos.',
  'finance.tip.ev.calc': 'büdzsé × (teljesített óra / becslés), a feladatstátuszok súlyaival',
  'finance.tip.ev.calcRecurring':
    'Σ lezárt időszak büdzséje + aktuális időszak büdzséje × eltelt munkanap arány',
  'finance.tip.ev.note': 'Ha a tény költés meghaladja, drágábban haladunk, mint terveztük.',
  'finance.tip.plannedCost.name': 'Tervezett költés',
  'finance.tip.plannedCost.what':
    'Mennyibe kerül a betervezett munka: az allokált órák az emberek költségdíjával.',
  'finance.tip.plannedCost.calc': 'Σ (allokált óra × ember költségdíja)',
  'finance.tip.plannedCost.calcThin':
    'a költségkeretből származtatva – az allokáció csak a hatókör részét fedi',
  'finance.tip.plannedCost.note': 'Ha meghaladja a büdzsét, már a terv sem fér bele.',
  'finance.tip.actualCost.name': 'AC: tény költés',
  'finance.tip.actualCost.what':
    'Mennyibe került eddig a munka: a logolt órák az emberek költségdíjával, plusz a hozzákötött külső költségek.',
  'finance.tip.actualCost.calc': 'Σ (logolt óra × ember költségdíja) + Σ külső költség',
  'finance.tip.actualCost.note':
    'Minden óra azon a napon érvényes díjjal, amikor logolták – egy áremelés nem árazza át a múltat.',
  'finance.tip.external.name': 'Külső költség',
  'finance.tip.external.what':
    'A nem munkaóra alapú költségek: licenc, alvállalkozó, utazás, hardver.',
  'finance.tip.external.calc':
    'Σ a hatókörhöz kötött külső költség, a projekt szintű költségekkel együtt',
  'finance.tip.external.noteIn':
    'Most beszámít a tény költésbe és az EAC-be – a soron álló választóval kihagyható.',
  'finance.tip.external.noteOut':
    'Most ki van hagyva a tény költésből – a soron álló választóval beszámítható.',
  'finance.tip.bar.budgetNote': 'Minden más sáv ehhez mérve olvasandó; a függőleges jel is ez.',
  'finance.tip.bar.plannedNote': 'Ha a jelen túlnyúlik, már a terv sem fér a keretbe.',
  'finance.tip.bar.actualNote': 'A keretjelen túlnyúló rész a túlköltés.',
  'finance.tip.bar.eacNote':
    'A keretjelen túlnyúló rész az előrejelzett túllépés. A képlet a soron álló választóval váltható.',
  'finance.tip.utilisation.name': 'Büdzsé-hasznosítás',
  'finance.tip.utilisation.what':
    'A terv, a tény, a teljesített érték és a várható végösszeg a költségkerethez mérve.',
  'finance.tip.utilisation.calc': 'a sáv hossza a pénzérték, a függőleges jel a büdzsé',
  'finance.tip.utilisation.note': 'A jelen túlnyúló sáv keret-túllépést jelez.',
  'finance.tip.profitability.name': 'Profitabilitás',
  'finance.tip.profitability.what':
    'A hatókör jövedelmezősége: bevétel, költés, margó, óradíjak és a számlázás állapota.',
  'finance.tip.profitability.note':
    'A margó a megállapodott bevételből számol, nem a kiszámlázott összegből.',
  'finance.tip.planMargin.name': 'Tervezett margó',
  'finance.tip.planMargin.what':
    'Mennyi maradna, ha a betervezett munka a terv szerint valósul meg.',
  'finance.tip.planMargin.calc': 'bevétel − tervezett költés',
  'finance.tip.planMargin.note':
    'Ez a terv jövedelmezősége; a záráskor várható értéket az előrejelzett margó mutatja.',
  'finance.tip.planMarginPct.name': 'Tervezett margó %',
  'finance.tip.planMarginPct.what': 'A tervezett margó a bevétel arányában.',
  'finance.tip.planMarginPct.calc': '(bevétel − tervezett költés) / bevétel',
  'finance.tip.planMarginPct.note':
    'Ha az előrejelzett margó % ennél alacsonyabb, a végrehajtás rontja a terv jövedelmezőségét.',
  'finance.tip.forecastRevenue.name': 'Előrejelzett bevétel',
  'finance.tip.forecastRevenue.what':
    'Mennyi bevétel áll össze a hatókör végéig, ha a hátralévő munka a terv szerint elkészül.',
  'finance.tip.forecastRevenue.calc':
    'fix díjnál a teljes szerződéses összeg; T&M-nél az eddigi bevétel + a hátralévő becsült órák a sortétel díján',
  'finance.tip.forecastRevenue.note':
    'Mivel a bevétel is a megállapodott összeg, nem ismétlődő szerződésnél a kettő megegyezik.',
  'finance.tip.forecastMargin.name': 'Előrejelzett margó',
  'finance.tip.forecastMargin.what':
    'Mennyi marad a záráskor: az előrejelzett bevétel és az előrejelzett költés különbsége.',
  'finance.tip.forecastMargin.calc': 'előrejelzett bevétel − előrejelzett költés (EAC)',
  'finance.tip.forecastMargin.note':
    'A margó soha nem lehet nagyobb a bevételnél; ha közel van hozzá, a hátralévő munkára még alig van költés betervezve.',
  'finance.tip.forecastMarginPct.name': 'Előrejelzett margó %',
  'finance.tip.forecastMarginPct.what':
    'A záráskor várható margó az előrejelzett bevétel arányában.',
  'finance.tip.forecastMarginPct.calc':
    '(előrejelzett bevétel − előrejelzett költés) / előrejelzett bevétel',
  'finance.tip.forecastMarginPct.note':
    'Ha a mostani margónál lényegesen alacsonyabb, a hátralévő munka rontja a jövedelmezőséget.',
  'finance.tip.actualRate.name': 'Tényleges óradíj',
  'finance.tip.actualRate.what': 'A megállapodott bevétel egy eddig logolt órára vetítve.',
  'finance.tip.actualRate.calc': 'bevétel / logolt óra',
  'finance.tip.actualRate.note':
    'Amíg a munka nem készült el, a teljes bevételt osztjuk az eddigi órákkal, ezért a végső értéknél magasabb. Ha a céldíj alatt van, a munkát a listaárnál olcsóbban adtuk.',
  'finance.tip.targetRate.name': 'Cél óradíj',
  'finance.tip.targetRate.what': 'A díjkártyákon szereplő céldíjak a logolt órákkal súlyozva.',
  'finance.tip.targetRate.calc': 'Σ (logolt óra × ember céldíja) / logolt óra',
  'finance.tip.targetRate.note': 'Ez a lista szerinti ár, amit a munkáért el kellene kérni.',
  'finance.tip.plannedRate.name': 'Tervezett óradíj',
  'finance.tip.plannedRate.what': 'A megállapodott bevétel egy allokált órára vetítve.',
  'finance.tip.plannedRate.calc': 'bevétel / allokált óra',
  'finance.tip.plannedRate.note':
    'A tényleges óradíjtól csak az osztóban különbözik: az a logolt, ez a betervezett órákkal oszt. A kettő eltérése megmutatja, hogy a munka több vagy kevesebb órát visz-e el, mint amennyit terveztek.',
  'finance.tip.invoiced.name': 'Kiszámlázva',
  'finance.tip.invoiced.what': 'A hatókörre kiállított, nem sztornó számlák összege.',
  'finance.tip.invoiced.calc': 'Σ számla összeg (sztornó nélkül)',
  'finance.tip.invoiced.note': 'A bevételtől eltérhet: előre vagy utólag számlázunk.',
  'finance.tip.invoicedPct.name': 'Kiszámlázva / bevétel',
  'finance.tip.invoicedPct.what': 'A bevétel mekkora részét számláztuk ki.',
  'finance.tip.invoicedPct.calc': 'kiszámlázva / bevétel',
  'finance.tip.invoicedPct.note':
    '100% alatt van még ki nem számlázott bevétel; 100% felett a megállapodottnál többet számláztunk.',
  'finance.tip.wip.name': 'WIP: még nem számlázott teljesítés',
  'finance.tip.wip.what': 'A megállapodott bevételből az, amit még nem számláztunk ki.',
  'finance.tip.wip.calc': 'bevétel − kiszámlázva',
  'finance.tip.wip.note':
    'Negatív érték azt jelenti, hogy a megállapodottnál többet számláztunk ki.',
  'finance.tip.layers.name': 'Retainer és elszámolható margó',
  'finance.tip.layers.what':
    'A keretes (retainer) és a kereten felüli, külön számlázott (elszámolható) munka margója külön.',
  'finance.tip.layers.calc': 'réteg bevétele − réteg tény költése',
  'finance.tip.layers.note':
    'Ez a szerződés fő állítása: a retainer túlköltését az elszámolható munka nyeresége kompenzálhatja.',
  'finance.tip.breakdown.name': 'Költés-bontás',
  'finance.tip.breakdown.what':
    'Hogyan oszlik meg a tervezett és a tény költés ember vagy szerepkör szerint.',
  'finance.tip.breakdown.calc':
    'a hatókör allokációi és logolt órái csoportosítva, a költségdíjakkal beárazva',
  'finance.tip.col.index.name': 'Időszak sorszáma',
  'finance.tip.col.index.what': 'Az ismétlődés által generált időszak sorszáma.',
  'finance.tip.col.period.name': 'Időszak',
  'finance.tip.col.period.what': 'Az időszak kezdő és záró napja.',
  'finance.tip.col.status.name': 'Státusz',
  'finance.tip.col.status.what':
    'Tervezett, Aktív vagy Lezárt – az időt követi, nem a felhasználó állítja.',
  'finance.tip.col.status.note': 'Hiányzó logolás esetén a lezárt időszak újranyitható.',
  'finance.tip.col.periodBudget.name': 'Időszak büdzsé',
  'finance.tip.col.periodBudget.what': 'Az időszakra jutó belső költségkeret.',
  'finance.tip.col.periodBudget.calc':
    'sortétel költségkeret / időszakok száma, az időszaknál felülírható',
  'finance.tip.col.periodRevenue.name': 'Időszak bevétel',
  'finance.tip.col.periodRevenue.what': 'Az időszakra jutó retainer bevétel.',
  'finance.tip.col.periodRevenue.calc':
    'sortétel bevétel / időszakok száma, az időszaknál felülírható',
  'finance.tip.col.billable.name': 'Elszámolható bevétel',
  'finance.tip.col.billable.what':
    'A kereten felüli, külön számlázott munka bevétele az időszakban.',
  'finance.tip.col.billable.note': 'Ez a retaineren felül van, nem visz keretet.',
  'finance.tip.col.spend.what': 'Az időszakban felmerült költség.',
  'finance.tip.col.external.calc': 'Σ az időszakhoz kötött külső költség',
  'finance.tip.col.margin.name': 'Margó',
  'finance.tip.col.margin.what': 'Bevétel mínusz költés.',
  'finance.tip.col.margin.note': 'Negatív érték veszteséges időszak.',
  'finance.tip.col.periodMargin.calc': '(bevétel + elszámolható) − költés',
  'finance.tip.col.marginPct.name': 'Margó %',
  'finance.tip.col.marginPct.what': 'A margó a bevétel arányában.',
  'finance.tip.col.periodMarginPct.calc': 'margó / (bevétel + elszámolható)',
  'finance.tip.col.periodInvoiced.what': 'Az időszakra kiállított számlák összege.',
  'finance.tip.col.invoice.name': 'Számla',
  'finance.tip.col.invoice.what': 'Az időszakhoz tartozó legutóbbi számla dokumentum-azonosítója.',
  'finance.tip.col.invoice.note': 'Üres, ha még nincs számla.',
  'finance.tip.col.item.name': 'Sortétel',
  'finance.tip.col.item.what': 'A szerződés vállalási egysége.',
  'finance.tip.col.itemType.name': 'Elszámolás típusa',
  'finance.tip.col.itemType.what': 'Fix díjas, Idő és anyag (T&M) vagy Nem számlázható.',
  'finance.tip.col.itemType.note':
    'T&M-nél nincs költségkeret; a bevétel a logolt időből és a hátralévő becslésből számol.',
  'finance.tip.col.itemRevenue.what': 'A sortétel megállapodott bevétele.',
  'finance.tip.col.itemRevenue.calc':
    'fix díjnál és licencnél a megadott összeg; T&M-nél logolt idő × díj + hátralévő becslés × díj',
  'finance.tip.col.itemRevenue.note': 'Nem számlázhatónál nincs bevétel.',
  'finance.tip.col.itemBudget.name': 'Büdzsé',
  'finance.tip.col.itemBudget.what': 'A sortételre szánt belső költségkeret.',
  'finance.tip.col.itemBudget.calc': 'a sortételen megadott költségkeret',
  'finance.tip.col.itemBudget.note': 'T&M-nél nincs.',
  'finance.tip.col.itemExternal.calc': 'Σ a sortételhez kötött külső költség',
  'finance.tip.col.itemCpi.note': '1,00 alatt túlköltés.',
  'finance.tip.col.itemCpi.calc': 'teljesített érték / tény költés',
  'finance.tip.col.itemCpi.what':
    'Egy elköltött forintra mennyi keret-érték jut ezen a sortételen.',
  'finance.tip.col.itemMargin.calc': 'bevétel − tény költés',
  'finance.tip.col.itemMarginPct.calc': 'margó / bevétel',
  'finance.tip.col.itemInvoiced.what': 'A sortételre kiállított számlák összege.',
  'finance.tip.col.itemInvoice.what': 'A sortétel legutóbbi számlájának dokumentum-azonosítója.',
  'occurrence.money': 'Pénzügy',
  'occurrence.money.overFrame': 'Kereten felül',
  'lineItem.attachments': 'Csatolmányok',
  'lineItem.attachments.empty': 'Ehhez a sortételhez még nincs csatolmány.',
  'person.attachments': 'Csatolmányok',
  'person.attachments.empty': 'Ehhez a dolgozóhoz még nincs csatolmány.',
  'contract.tasks': 'Feladat',
  'contract.createdAt': 'Létrehozva',
  'contract.lineItems': 'Sortételek',
  'contract.lineItem': 'Sortétel',
  'contract.lineItems.empty':
    'Még nincs sortétel. A feladatok ezeken keresztül tartoznak a szerződéshez.',
  'contract.lineItem.new': 'Új sortétel',
  'contract.lineItem.add': 'Hozzáadás',
  'contract.action.new': 'Új szerződés',
  /* C56 · the split menu's two verbs. */
  'contract.action.createNew': 'Új szerződés létrehozása',
  'contract.action.attachExisting': 'Meglévő szerződés hozzáadása',
  'contract.attach.title': 'Meglévő szerződés hozzáadása',
  'contract.attach.hint':
    'Csak projekt nélküli, ügyfél alatti szerződések közül választhatsz. A hozzáadáskor a szerződés kódot kap.',
  'contract.attach.none': 'Nincs hozzáadható szerződés. Előbb hozz létre egyet az ügyfél alatt.',
  'contract.attach.submit': 'Hozzáadás',
  'contract.attach.anyClient': 'Más ügyfelek szerződéseit is mutasd',
  'contract.action.create': 'Létrehozás',
  'contract.dialog.new': 'Új szerződés',
  'contract.dialog.stepHint': 'Hányanként ismétlődik – pl. 3 hónap = negyedévente.',
  'contract.field.name': 'Név',
  'contract.field.namePlaceholder': 'Pl. Haystack éves support',
  'contract.error.name': 'Adj nevet a szerződésnek.',
  'contract.error.project': 'Válassz projektet.',
  'contract.error.start': 'Add meg a kezdés dátumát.',
  'contract.error.dates': 'A vég dátuma nem lehet a kezdés előtt.',
  'lineItem.dialog.new': 'Új sortétel',
  'lineItem.field.namePlaceholder': 'Pl. Havi támogatás',
  'lineItem.field.estimatePerPeriod': 'Becslés / időszak',
  'lineItem.field.typeHint': 'Később a sortétel oldalán módosítható.',
  'lineItem.field.estimateHintRecurring':
    'Egy időszakra jutó becslés – ismétlődő szerződés alatt ennyi órát vállalunk periódusonként.',
  'lineItem.error.estimate': 'Az óraszámot nem sikerült értelmezni.',
  'lineItem.error.type': 'Válaszd ki az elszámolás típusát.',
  'common.hours': 'óra',
  'contract.lineItem.closed': 'lezárt',
  'contract.perPeriodNote':
    'A becslés, a büdzsé és a bevétel oszlop a teljes vállalást mutatja: az időszakra megadott érték × a sortétel időszakainak száma (a szerződésen {count} időszak).',
  'contract.estimateTotal': 'Időszakonként {perPeriod} · {count} időszak',
  'contract.recurrence': 'Ismétlődés',
  'contract.recurrence.unit': 'Egység',
  'contract.recurrence.step': 'Lépésköz',
  'contract.recurrence.startsOn': 'Kezdés',
  'contract.recurrence.endMode': 'Lezárás',
  'contract.recurrence.endsOn': 'Utolsó nap',
  'contract.recurrence.count': 'Darabszám',
  'contract.recurrence.week': 'Hét',
  'contract.recurrence.month': 'Hónap',
  'contract.recurrence.byDate': 'Dátumig',
  'contract.recurrence.byCount': 'Darabszám szerint',
  'contract.recurrence.periodsNote': 'Az időszakok ebből származnak – külön nem tároljuk őket.',
  'contract.error.notEnabled': 'A szerződések nem részei a csomagotoknak.',
  'contract.error.invalid': 'A megadott érték nem érvényes.',
  'contract.error.lineItemInUse':
    'A sortételhez még tartoznak feladatok vagy rögzített órák – zárd le törlés helyett.',
  'lineItem.type.fixed': 'Fix díjas',
  'lineItem.type.timeAndMaterial': 'Idő és anyag',
  'lineItem.type.nonBillable': 'Nem számlázható',
  /* C55 · munka nélküli bevétel — the mockup's own label. */
  'lineItem.type.license': 'Licenc és díj',
  /* C55.1 · a licenc saját feltételei, a munka-sorok helyén. */
  'contract.column.name': 'Szerződés',
  'contract.column.project': 'Projekt',
  'contract.column.client': 'Ügyfél',
  /* ── C57 · the 26 alert rules. The Hungarian is the mockup’s own, verbatim. ── */
  'alert.member_no_rate.name': 'Nincs díjkártya a projekttaghoz',
  'alert.member_no_rate.impact':
    'Tény költés, CPI, margó és a költés-bontás — a díj nélküli órák 0 Ft költséggel mennek be.',
  'alert.member_no_rate.fix':
    'Beállítások › Díjkártyák › Hozzárendelés — adj a dolgozónak érvényes díjat a logolás kezdetétől.',
  'alert.member_no_sched.name': 'Nincs munkarend a projekttaghoz',
  'alert.member_no_sched.impact':
    'Kapacitás, terhelés és a szabadságkeret — a dolgozó napi 0 órás kapacitással szerepel.',
  'alert.member_no_sched.fix':
    'Beállítások › Munkarendek › Hozzárendelés — válassz sablont vagy adj egyedi napi órákat.',
  'alert.task_multi_line.name': 'Egy feladat több sortételnek is megfelel',
  'alert.task_multi_line.impact':
    'Bevétel és keretfogyás — ugyanaz az óra mindkét sortételen megjelenik, tehát kétszer számolódik.',
  'alert.task_multi_line.fix':
    'Nyisd meg a feladatot, és hagyd meg az egyetlen érvényes sortétel-összerendelést.',
  'alert.task_no_line.name': 'Logolt feladat sortétel nélkül',
  'alert.task_no_line.impact':
    'Bevétel, keretkihasználtság és az EVM mutatók — az órák kimaradnak a szerződés elszámolásából.',
  'alert.task_no_line.fix':
    'Nyisd meg a feladatot, és kösd hozzá a szerződés megfelelő sortételét.',
  'alert.task_unrouted.name': 'Szinkronizált feladat sortétel nélkül',
  'alert.task_unrouted.impact':
    'Bevétel és keretkihasználtság — a feladat egyik projekten sincs rajta, így az órái egyik szerződésbe sem számítanak.',
  'alert.task_unrouted.fix':
    'Bővítsd a megfelelő sortétel szabályát, hogy fogadja a feladatot, vagy rendeld hozzá kézzel.',
  'alert.task_orphaned.name': 'Szinkronizált feladat, amelyet a szabálya már nem fogad',
  'alert.task_orphaned.impact':
    'Bevétel és keretkihasználtság — az órák a korábbi sortételen maradnak, pedig a szabálya már nem oda sorolná.',
  'alert.task_orphaned.fix':
    'Igazítsd a sortétel szabályát a feladathoz, vagy rendeld át kézzel a helyes sortételre.',
  'alert.li_no_budget.name': 'Sortételen nincs költségkeret',
  'alert.li_no_budget.impact':
    'Keretkihasználtság, CPI és VAC — nulla kerethez nem lehet eltérést számolni.',
  'alert.li_no_budget.fix':
    'Nyisd meg a sortételt, és add meg a büdzsét — vagy állítsd nem számlázhatóra.',
  'alert.contract_expired_log.name': 'Lejárt szerződésre érkezett idő',
  'alert.contract_expired_log.impact':
    'Bevétel és számlázás — a lejárat utáni órákra nincs mire számlát állítani.',
  'alert.contract_expired_log.fix':
    'Hosszabbítsd meg a szerződést, vagy tedd át az órákat élő sortételre.',
  'alert.wlog_duplicate.name': 'Kétszer rögzített időlog',
  'alert.wlog_duplicate.impact':
    'Tény óra, tény költés és a készültség — a duplázott óra mindhármat felfelé húzza.',
  'alert.wlog_duplicate.fix': 'Nyisd meg a feladatot, és töröld a felesleges bejegyzést.',
  'alert.inactive_member_log.name': 'Inaktív tag rögzített órát',
  'alert.inactive_member_log.impact':
    'Tény költés és a tagbontás — az órák olyan emberre esnek, aki már nincs a csapatban.',
  'alert.inactive_member_log.fix':
    'Aktiváld újra a tagot a projekt Emberek fülén, vagy tedd át az órákat.',
  'alert.ext_cost_unlinked.name': 'Külső költség sortétel nélkül',
  'alert.ext_cost_unlinked.impact':
    'Sortétel-szintű profitabilitás — a költség csak a projekt összesenben látszik.',
  'alert.ext_cost_unlinked.fix':
    'Nyisd meg a költséget, és kösd sortételhez vagy support időszakhoz.',
  'alert.license_not_invoiced.name': 'Licencdíj számla nélkül',
  'alert.license_not_invoiced.impact':
    'Bevétel és számlázottság — a lezárt időszak díja számla nélkül nem jelenik meg a hónapban.',
  'alert.license_not_invoiced.fix':
    'Nyisd meg a sortételt, és állítsd ki a hiányzó számlát az időszakra.',
  'alert.task_no_assignee.name': 'Feladat felelős nélkül',
  'alert.task_no_assignee.impact':
    'Kapacitásterv és a becslésből számolt előrejelzés — a feladat senki terhelésében nem szerepel.',
  'alert.task_no_assignee.fix': 'Nyisd meg a feladatot, és állíts be felelőst.',
  'alert.over_invoiced.name': 'Több lett kiszámlázva, mint a bevétel',
  'alert.over_invoiced.impact':
    'Kiszámlázva / bevétel arány és a számlázási hátralék — 100% fölé megy.',
  'alert.over_invoiced.fix':
    'Nyisd meg a számlát, és javítsd az összeget — vagy emeld a sortétel bevételét.',
  'alert.task_no_estimate.name': 'Feladat becslés nélkül',
  'alert.task_no_estimate.impact':
    'Keret, készültség, CPI és EAC — a becslés nélküli feladat kimarad a keretből és a teljesített értékből, ezért a projekt jobbnak látszik a valóságnál.',
  'alert.task_no_estimate.fix':
    'Nyisd meg a feladatot, és add meg a becslést — vagy tedd mérföldkővé (0 óra, kezdés = határidő).',
  'alert.task_no_dates.name': 'Feladaton nincs kezdés vagy határidő',
  'alert.task_no_dates.impact':
    'Lejárt feladatok, ütem-előrejelzés és a diagram teljesített-pontja — dátum nélkül a feladat nem tud lejárni, és nem esik időbucketbe.',
  'alert.task_no_dates.fix': 'Nyisd meg a feladatot, és add meg a kezdést és a határidőt.',
  'alert.li_no_window.name': 'Sortételen nincs időszak',
  'alert.li_no_window.impact':
    'Eltelt idő, terv máig (PV) és az SPI — időablak nélkül nincs mihez mérni az ütemet.',
  'alert.li_no_window.fix':
    'Nyisd meg a sortételt, és add meg a kezdő és záró dátumot — vagy add meg a szerződésen.',
  'alert.li_no_rate_tm.name': 'T&M sortételen nincs díj',
  'alert.li_no_rate_tm.impact':
    'Bevétel, margó és az előrejelzett bevétel — díj nélkül a logolt órákból nem számolható bevétel.',
  'alert.li_no_rate_tm.fix': 'Nyisd meg a sortételt, és add meg az óradíjat vagy a napidíjat.',
  'alert.fx_missing.name': 'Hiányzó árfolyam',
  'alert.fx_missing.impact':
    'Minden pénzes mutató a kevert pénznemű hatókörön — árfolyam nélkül a rendszer 1:1 arányban ad össze két pénznemet.',
  'alert.fx_missing.fix':
    'Beállítások › Pénznemek — vedd fel az árfolyamot a workspace-alap pénznemre.',
  'alert.alloc_no_rate.name': 'Allokált emberhez nincs díjkártya',
  'alert.alloc_no_rate.impact':
    'Tervezett költés, terv máig (PV) és az SPI — a díj nélküli allokáció csendben kimarad a tervezett költésből, ezért az ütem jobbnak látszik.',
  'alert.alloc_no_rate.fix':
    'Beállítások › Díjkártyák › Hozzárendelés — adj a dolgozónak érvényes díjat.',
  'alert.wlog_outside_li.name': 'Időlog a sortétel időszakán kívül',
  'alert.wlog_outside_li.impact':
    'Ablakolt tény, terv/tény összevetés és az időszaki elszámolás — az óra olyan időre esik, amit a sortétel nem fed.',
  'alert.wlog_outside_li.fix':
    'Igazítsd a sortétel időszakát, vagy tedd át az órákat a helyes sortételre.',
  'alert.wlog_no_occ.name': 'Időlog nem esik egyetlen időszakba sem',
  'alert.wlog_no_occ.impact':
    'Időszaki tény, futamráta és az előrejelzett túllépés — az óra kimarad a havi elszámolásból.',
  'alert.wlog_no_occ.fix':
    'Nyisd meg a szerződést, és igazítsd az ismétlődés határait — vagy javítsd a logolás dátumát.',
  'alert.wlog_multi_occ.name': 'Időlog több időszakba is beesik',
  'alert.wlog_multi_occ.impact':
    'Időszaki tény és a láblécösszegek — az átfedés miatt ugyanaz az óra kétszer számolódik.',
  'alert.wlog_multi_occ.fix': 'Nyisd meg a szerződést, és szüntesd meg az időszakok átfedését.',
  'alert.dep_date_violation.name': 'Függőség sérül a dátumokon',
  'alert.dep_date_violation.impact':
    'Előrejelzett befejezés, csúszás és a mérföldkő-esélyek — az ütemezés olyan sorrendet tételez, ami a dátumok szerint nem valósulhat meg.',
  'alert.dep_date_violation.fix':
    'Told az utód kezdését az előd vége utánra, vagy oldd fel a függőséget.',
  'alert.stale_progress.name': 'Folyamatban ragadt feladat',
  'alert.stale_progress.impact':
    'Teljesített érték, készültség, CPI és SPI — a folyamatban lévő feladat fél munkát ismer el akkor is, ha hetek óta nem történt rajta semmi.',
  'alert.stale_progress.fix':
    'Nyisd meg a feladatot, és állítsd a valós státuszra — vagy tedd vissza a backlogba.',
  'alert.log_on_todo.name': 'Nem indított feladatra érkezett idő',
  'alert.log_on_todo.impact':
    'Teljesített érték és a tény közti rés — a nulla súlyú feladat órái csak a költést növelik, készültséget nem adnak.',
  'alert.log_on_todo.fix':
    'Állítsd a feladatot folyamatban lévőre, vagy tedd át az órákat a helyes feladatra.',
  'alert.no_recent_logs.name': 'Nincs friss logolás a projekten',
  'alert.no_recent_logs.impact':
    'Tény óra, napi felhasználás, futamráta és minden előrejelzés — elavult tényből nem lehet ütemet becsülni.',
  'alert.no_recent_logs.fix':
    'Kérd a csapatot a hét pótlására — Idő › Logolás a projekt feladataira.',
  'alert.plan_thin.name': 'Az allokáció a keret felét sem fedi',
  'alert.plan_thin.impact':
    'Tervezett költés, terv máig (PV), SPI és az előrejelzett befejezés — a dashboard ilyenkor a keretre vált vissza tervbázisként.',
  'alert.plan_thin.fix':
    'Erőforrás fül — oszd be a hátralévő munkát, vagy pontosítsd a sortétel becslését.',
  'alert.li_closed_work.name': 'Munka érkezett egy lezárt sortételre',
  'alert.li_closed_work.impact':
    'Tény, keretfogyás és számlázás — a sortétel már ki van számlázva és le van zárva, az újonnan érkezett munka mégis rá könyvelődik.',
  'alert.li_closed_work.fix':
    'Tedd át a feladatot vagy az órákat egy nyitott sortételre — vagy nyisd újra a sortételt, ha még tart rajta a munka.',
  'alert.wlog_unidentified.name': 'Sortételhez nem köthető órák',
  'alert.wlog_unidentified.impact':
    'Tény, bevétel és számlázás — az órák a projekten megjelennek, de egyik sortétel sem számolja el őket, így egyik T&M sortétel bevételébe sem kerülnek bele.',
  'alert.wlog_unidentified.fix':
    'Nevezd meg a személyt egy sortételen, vagy igazítsd a sortétel időszakát úgy, hogy lefedje ezeket a napokat.',
  'alert.wlog_unestimated_share.name': 'Becslés nélküli feladatokon a ráfordítás',
  'alert.wlog_unestimated_share.impact':
    'CPI és EAC — a becslés nélküli feladat nem termel EV-t, így a CPI túl alacsony, az EAC túl magas.',
  'alert.wlog_unestimated_share.fix':
    'Becsüld meg azokat a feladatokat, amelyeken már van logolt óra.',
  'alert.wlog_unbound_share.name': 'Sok óra nincs sortételhez kötve',
  'alert.wlog_unbound_share.impact':
    'CPI és EAC — a sortétel nélküli órák kimaradnak a keretből, így a CPI túl jónak látszik.',
  'alert.wlog_unbound_share.fix':
    'Kösd a feladatokat sortételhez — a sortétel kötési szabályaival, vagy közvetlenül a feladaton.',
  'alert.li_no_tasks.name': 'Sortétel logolt órával, de feladat nélkül',
  'alert.li_no_tasks.impact':
    'EV és EAC — a sortétel készültsége a feladataiból jön, feladat nélkül az EV-je 0, miközben a ráfordítás nő.',
  'alert.li_no_tasks.fix': 'Hozz létre feladatokat a sortételen, vagy kösd hozzá a meglévőket.',
  'alert.task_reestimated_closed.name': 'Kész feladatot utólag újrabecsültek',
  'alert.task_reestimated_closed.impact':
    'EV és CPI — a kész feladat becslése az EV része, így az EV munka nélkül mozdult el.',
  'alert.task_reestimated_closed.fix':
    'Állítsd vissza a becslést, és a további munkát vedd fel új feladatként.',
  'alert.li_rule_on_status.name': 'Kötési feltétel státuszra vagy felelősre szűr',
  'alert.li_rule_on_status.impact':
    'Tény, EV és EAC — ha egy feladat státusza vagy felelőse változik, az órái átvándorolnak egy másik sortételre.',
  'alert.li_rule_on_status.fix':
    'Cseréld a feltételt egy stabil mezőre, vagy rendeld a feladatokat kézzel a sortételhez.',
  'alert.alloc_stale.name': 'Elavult allokáció',
  'alert.alloc_stale.impact':
    'Erőforrás-alapú EAC és befejezés — a hátralévő beosztás olyan kollégákon van, akik nem dolgoznak a projekten.',
  'alert.alloc_stale.fix':
    'Erőforrás fül — igazítsd a beosztást a projekten ténylegesen dolgozó csapathoz.',
  'alert.li_over_plan.name': 'A beosztás túllépi a sortétel keretét',
  'alert.li_over_plan.impact':
    'Ütemkorrigált EAC és SPI(t) — a terv nagyobb a keretnél, így a tervezett érték torz.',
  'alert.li_over_plan.fix':
    'Erőforrás fül — csökkentsd a beosztást, vagy emeld a sortétel becslését.',
  'alert.member_silent.name': 'Beosztott kolléga nem logol',
  'alert.member_silent.impact':
    'Erőforrás-alapú EAC és befejezés — a beosztás olyan munkát ígér, ami nem érkezik meg.',
  'alert.member_silent.fix': 'Kérd a kollégát a logolás pótlására, vagy vedd le a beosztásból.',
  'contract.column.type': 'Típus',
  'contract.column.startsOn': 'Kezdés',
  'contract.column.endsOn': 'Vég',
  'contract.column.lineItems': 'Sortétel',
  'contract.column.estimate': 'Becslés',
  'contract.column.tracked': 'Rögzített',
  'contract.column.owner': 'Felelős',
  'contract.column.status': 'Státusz',
  'contract.type.recurring': 'Ismétlődő',
  'contract.type.milestone': 'Mérföldkő alapú',
  'contract.type.service': 'Szolgáltatás',
  'contract.status.draft': 'Tervezet',
  'contract.status.active': 'Aktív',
  'contract.status.closed': 'Lezárt',
  'home.chart.previous': 'Korábbi időszak',
  'home.chart.next': 'Következő időszak',
  'home.section.log': 'Időlogok',
  'home.section.leave': 'Szabadság',
  'home.section.allocations': 'Allokációim',
  'home.section.overtime': 'Túlóra',
  'home.action.logTime': 'Idő rögzítése',
  'home.stat.today': 'Ma',
  'home.stat.thisWeek': 'Ez a hét',
  'home.stat.missingDays': 'Hiányos napok',
  'home.quota.days': 'nap',
  'home.leave.pending': 'Elbírálás alatt',
  'home.leave.upcoming': 'Közelgő távollétek',
  /* ⚠️ Codes on the wire, words here — the server cannot know the reader's language. */
  /* 16 Oct 2026 · the reworked homescreen. */
  'home.ask.caption': 'Ezekről az agentet is kérdezheted.',
  'home.stat.weekLeft': 'Hátra a héten',
  'home.stat.weekOver': 'Terven felül',
  'home.stat.ofWeeklyPlan': 'a heti tervből',
  'home.stat.onPlan': 'terv szerint',
  'home.stat.noSchedule': 'nincs munkarend',
  'home.stat.thisWeekLower': 'a héten',
  'home.requests.all': 'Összes kérelem',
  'home.overtime.approvedRecent': 'Elfogadva az elmúlt hónapban',
  'home.overtime.unpriced': 'Túlóra',
  'home.alloc.holiday': 'Ünnepnap',
  'home.alloc.leave': 'Szabadság',
  'home.alloc.none': 'Nincs allokáció',
  'home.alloc.noCapacity': 'Nincs kapacitás',
  'home.alloc.tip.day': 'Ezen a napon',
  'home.alloc.tip.billed': '{hours} · elszámolt {billed}',
  'home.alloc.tip.logged': 'Rögzítve aznap',
  'home.alloc.tip.week': 'A héten összesen',
  'home.alloc.tip.period': 'Időszak',
  'home.alloc.tip.periods': '{count} allokáció',
  'home.dow.1': 'H',
  'home.dow.2': 'K',
  'home.dow.3': 'Sze',
  'home.dow.4': 'Cs',
  'home.dow.5': 'P',
  'home.dow.6': 'Szo',
  'home.dow.7': 'V',
  /* ── C18: the sidebar's pinned lists. `0030` argues why these are favourites. ─────────── */
  'nav.projectsSection': 'Projektek',
  'nav.myProjects': 'Projektjeim',
  'nav.projectsEmpty':
    'Tűzz ki egy projektet a projektlistában vagy a projekt oldalán a csillaggal, és itt jelenik meg.',
  'nav.newProject': 'Új projekt',
  'nav.reportsSection': 'Riportok',
  'nav.myReports': 'Riportjaim',
  'nav.reportsEmpty': 'Tűzz ki egy riportot a riportlistában, és itt jelenik meg.',
  'nav.newReport': 'Új riport',
  'nav.noWorkspace': 'Nincs workspace',
  'title.projects': 'Projektek',
  'title.clients': 'Ügyfelek',

  /* ── things more than one screen says. One key, so two screens cannot word it differently. ─ */
  'common.cancel': 'Mégsem',
  'common.apply': 'Alkalmaz',
  'common.reset': 'Visszaállítás',
  'common.delete': 'Törlés',
  'common.save': 'Mentés',
  'common.create': 'Létrehozás',
  'common.back': 'Vissza',
  'common.close': 'Bezárás',
  'common.loading': 'Betöltés…',
  'common.edit': 'Szerkesztés',
  /* 26 Oct 2026 · a create/edit dialog's success toast. */
  'common.toast.created': '{name} létrehozva',
  'common.toast.saved': '{name} mentve',
  'common.toast.savedPlain': 'Mentve',
  'common.choose': 'Válassz',
  'common.pleaseChoose': 'Kérlek válassz',
  'common.all': 'Mind',
  'common.none': 'Nincs',
  'common.required': 'kötelező',
  'calendar.previousPeriod': 'Előző időszak',
  'calendar.nextPeriod': 'Következő időszak',
  'calendar.zoomOut': 'Nagyobb egység',
  'upload.drop': 'Húzd ide a fájlt a feltöltéshez',
  'drop.toEntity': 'Csatolás ide: {name}',
  'drop.toZone': 'Beszúrás ide: {name}',
  'drop.hint': 'A leírás vagy egy komment fölé húzva oda kerül.',
  'upload.browse': 'Vagy tallózz',
  'upload.busy': 'Feltöltés…',
  'common.color.custom': 'Egyéni szín',
  'common.color.hex': 'Hexadecimális színkód',
  'common.color.open': 'Színválasztó megnyitása',
  'common.decrease': 'Csökkentés',
  'common.increase': 'Növelés',
  'common.color': 'Szín',

  /* ── the list component's own strings. It reaches for no catalogue; the caller passes these. */
  /* R-57 · every identifier column, under one name. */
  'list.identifier': 'Azonosító',
  'list.search': 'Keresés és szűrés…',
  'list.filter': 'Szűrő',
  'list.views': 'Nézet',
  'list.columns': 'Oszlopok',
  'list.clearAll': 'Szűrők törlése',
  'list.searchOptions': 'Keresés…',
  'list.loadMore': 'Továbbiak betöltése',
  'list.previousPage': 'Előző oldal',
  'list.nextPage': 'Következő oldal',
  'list.pageSelector': 'Oldal',
  'list.pageOf': '{page} / {count}',
  /* ⚠️ **"találat", nem "összesen".** A szám azt mutatja, mennyi maradt a szűrők után, nem azt,
     hány sor van a táblában — `pagination.ts` erről szól hosszan. */
  'list.pageTotal': '{total} találat',
  'list.loadingMore': 'Betöltés…',
  /* ⚠️ A suffix never touches the placeholder — vowel harmony makes `{label}-t` wrong for half the
     column names. Every one of these is phrased so the name stands alone. */
  'list.clearFilter': 'Szűrő törlése: {label}',
  /* ⚠️ Item 16's range bounds — a deadline or an estimate is asked as two ends, not as a menu. */
  /* ⚠️ A file opened from a link in a description — `files/file-route.tsx`. */
  'file.fetching': 'Fájl letöltése…',
  'file.notAvailable': 'A fájl nem érhető el.',
  'file.deleted': 'Ezt a fájlt törölték. A rá mutató hivatkozás a szövegben maradt.',
  'file.saved': 'A fájl letöltve. Ez a lap bezárható.',
  /* ⚠️ 15 Sep · a file linked from the description is one file mentioned in two places. */
  'task.attachment.removeTitle': 'Csatolmány törlése',
  'task.attachment.linkedInDescription':
    'Erre a fájlra hivatkozik a leírás. A törléssel a hivatkozás is eltűnik a szövegből.',
  'list.rangeFrom': 'Ettől',
  'list.rangeTo': 'Eddig',
  'list.rangeBoth': '{from} – {to}',
  /* ⚠️ **NO SUFFIX ON A PLACEHOLDER.** `{from}-tól` is wrong for half of all values by vowel
     harmony — 8-tól, 2026-09-01-től — and the gate in `routes.test.tsx` refuses the shape outright.
     The preposition goes in front, where Hungarian lets it stand alone. */
  'list.rangeFromOnly': 'ettől: {from}',
  'list.rangeToOnly': 'eddig: {to}',
  'list.moveUp': 'Feljebb: {label}',
  'list.moveDown': 'Lejjebb: {label}',
  /* 17 Oct 2026 · the phone's ⋯ sheet. */
  'list.more': 'További lehetőségek',
  /* 20 Oct 2026 · LV-1 · a list as cards, and what the card shows. */
  'listLayout.view': 'Nézet',
  'listLayout.list': 'Lista',
  'listLayout.cards': 'Kártyák',
  'listLayout.settings': 'Kártya beállítása',
  'listLayout.badge': 'Jelvény (jobb felső sarok)',
  'listLayout.avatar': 'Felelős (jobb alsó sarok)',
  'listLayout.detail': '{index}. részlet',
  'listLayout.footer': 'Lábléc',
  'listLayout.none': 'Üres',
  'listLayout.reset': 'Alapértelmezett kártya',
  'listLayout.done': 'Kész',
  'listLayout.sort': 'Rendezés',
  'listLayout.title': 'Cím',
  'listLayout.context': 'Felső sor',
  'listLayout.preview': 'Előnézet',
  /* 17 Oct 2026 · the quota grid's copy from last year. */
  'settings.leave.copy': 'Másolás tavalyról ({count})',
  'settings.leave.pick': '{name} kijelölése',
  'settings.leave.pickAll': 'Mind kijelölése',
  'settings.leave.pickNone': 'Kijelölés törlése',
  /* 17 Oct 2026 · mobile: the landscape-only grids, and Erőforrás's folding figures. */
  'mobile.rotate': 'Fordítsd el a készüléket a táblázathoz',
  'chart.options': 'Diagram beállításai',
  'resource.stats.show': 'Számok mutatása',
  'resource.stats.hide': 'Számok elrejtése',
  'list.back': 'Vissza',
  'list.switchView': 'Nézet váltása',
  'list.table': 'Táblázat',
  'list.selectAll': 'Összes kijelölése',
  'list.selectRow': 'Sor kijelölése',
  'list.selectedCount': '{count} kijelölve',
  'list.clearSelection': 'Kijelölés törlése',
  'list.rowMenu': 'Sor műveletei',
  'list.sortBy': 'Rendezés: {label}',

  /* ── Ügyfelek ──────────────────────────────────────────────────────────────────────────── */
  'clients.heading': 'Ügyfelek',
  'clients.column.name': 'Név',
  'clients.column.code': 'Kód',
  'clients.column.contact': 'Kapcsolattartó',
  'clients.column.email': 'E-mail',
  'clients.column.industry': 'Iparág',
  'clients.column.country': 'Ország',
  'clients.column.state': 'Állapot',
  'clients.column.projects': 'Projektek',
  'clients.filter.state': 'Állapot',
  'clients.state.live': 'Aktív',
  'clients.state.archived': 'Archivált',
  'clients.action.create': 'Új ügyfél',
  'clients.action.archive': 'Archiválás',
  'clients.action.restore': 'Visszaállítás',
  'clients.dialog.new': 'Új ügyfél',
  'clients.dialog.edit': 'Ügyfél szerkesztése',
  'clients.dialog.newHint':
    'A rövid kód a projektek mellett jelenik meg, és később nem módosítható.',
  'clients.dialog.editHint': 'A rövid kód nem módosítható – a projektek arra hivatkoznak.',
  'clients.field.name': 'Ügyfél neve',
  'clients.field.namePlaceholder': 'Pl. Rossmann Magyarország Kft.',
  'clients.field.contact': 'Kapcsolattartó',
  'clients.field.contactPlaceholder': 'Név',
  'clients.field.email': 'E-mail',
  'clients.field.industry': 'Iparág',
  'clients.field.country': 'Ország',
  'clients.empty.title': 'Még nincs ügyfél',
  'clients.empty.body': 'Vegyél fel egyet, és a projektjei alatta gyűlnek.',
  /* The mockup's own validation sentences, unchanged. */
  'clients.error.name': 'Az ügyfél neve nem lehet üres.',
  'clients.error.email': 'Az e-mail formátuma nem jó.',
  'clients.error.contactName': 'Minden kapcsolattartónak legyen neve.',
  'clients.contacts.title': 'Kapcsolattartók',
  'clients.contacts.row': '{index}. kapcsolattartó',
  'clients.contacts.add': 'Új kapcsolattartó',
  'clients.contacts.remove': 'A(z) {index}. kapcsolattartó eltávolítása',
  'clients.contacts.saveFailed':
    'Az ügyfél elmentve, de a kapcsolattartók mentése nem sikerült: {reason}',
  'clients.error.industry': 'Adj meg iparágat – erre szűrünk a listában.',

  /* ── országok. A kód a listában külön látszik, tehát ez csak a név. ─────────────────────── */
  'country.HU': 'Magyarország',
  'country.DE': 'Németország',
  'country.AT': 'Ausztria',
  'country.SK': 'Szlovákia',
  'country.RO': 'Románia',
  'country.GB': 'Egyesült Királyság',
  'country.US': 'Egyesült Államok',

  /* ── Projektek ─────────────────────────────────────────────────────────────────────────── */
  'projects.heading': 'Projektek',
  'projects.column.name': 'Név',
  'projects.column.prefix': 'Prefix',
  'projects.column.client': 'Ügyfél',
  'projects.column.status': 'Státusz',
  'projects.column.pm': 'Projektvezető',
  'projects.column.range': 'Időszak',
  'projects.column.people': 'Emberek',
  'projects.column.done': 'Készültség',
  'projects.column.hours': 'Óra / becslés',
  'projects.column.start': 'Kezdés',
  'projects.column.end': 'Befejezés',
  'projects.view.all': 'Minden projekt',
  'projects.action.archiveProject': 'Projekt archiválása',
  'projects.action.unarchive': 'Archiválás visszavonása',
  'projects.view.active': 'Aktív',
  'projects.view.mine': 'Én vagyok a PM',
  'projects.view.late': 'Csúszó feladattal',
  'projects.done.of': '{done}/{total}',
  'projects.internal': 'Belső projekt',
  'projects.crumb': '{name} ({key})',
  'projects.action.create': 'Új projekt',
  'projects.action.menu': 'Projekt műveletei',
  'projects.sync.started': 'Szinkronizálás elindítva',
  'projects.sync.startedNote': 'A feladatok a háttérben érkeznek meg, a képernyő magától frissül.',
  'projects.sync.already': 'Már fut egy szinkronizálás',
  'projects.sync.failed': 'A szinkronizálás nem indult el.',
  'projects.action.pin': 'Kitűzés az oldalsávra',
  'projects.action.unpin': 'Levétel az oldalsávról',
  'projects.action.open': 'Megnyitás',
  'projects.action.back': 'Vissza a projektekhez',
  'projects.action.filterTasks': 'Feladatok szűrése erre',
  'projects.action.timeline': 'Idővonal',
  'projects.action.report': 'Riport erre a projektre',
  'projects.action.archive': 'Archiválás',
  'projects.action.export': 'Export',
  'projects.toasts': 'Projekt értesítések',
  'clients.toasts': 'Ügyfél értesítések',
  'projects.toast.archived': 'Projekt archiválva',
  /* 3 Nov 2026 · a project's status change carries its reason. */
  'projects.statusReason.title': 'Miért változik a státusz?',
  'projects.statusReason.label': 'Indok',
  'projects.statusReason.placeholder': 'Pl. az ügyfél szünetelteti a munkát',
  'projects.statusReason.required': 'A státusz módosításához add meg az indokát.',
  'projects.statusReason.save': 'Státusz módosítása',
  'projects.statusReason.bulkArchive': '{count} projekt archiválása — egy indok mindegyikhez.',
  'projects.statusReason.bulkUnarchive': '{count} projekt visszaállítása — egy indok mindegyikhez.',
  'projects.pm.notEligible': 'Ő nem vezethet projektet.',
  'projects.toast.archivedMany': '{count} projekt archiválva',
  'projects.toast.unarchived': 'Projekt visszaállítva',
  'projects.toast.unarchivedMany': '{count} projekt visszaállítva',
  'projects.dialog.new': 'Új projekt',
  'projects.dialog.edit': 'Projekt szerkesztése',
  'projects.dialog.newHint': 'A prefix adja a feladatkulcsokat, pl. ROSS-1043.',
  'projects.dialog.editHint':
    'A kulcs-prefix nem módosítható – a meglévő feladatkulcsok arra hivatkoznak.',
  'projects.field.name': 'Projekt neve',
  'projects.field.namePlaceholderGeneric': 'A projekt neve',
  'projects.field.newClient': 'Új ügyfél',
  'projects.field.namePlaceholder': 'Pl. Rossmann StoreAssist',
  'projects.field.prefix': 'Kulcs-prefix',
  'projects.field.prefixHint': '2–6 nagybetű',
  'projects.field.client': 'Ügyfél',
  'projects.field.internal': 'Belső projekt',
  'projects.field.pickClient': 'Válassz ügyfelet…',
  'projects.field.noClient': 'Nincs ügyfél',
  'projects.field.pm': 'Projektvezető',
  'projects.field.status': 'Státusz',
  'projects.field.code': 'Hivatkozási kód',
  'projects.field.start': 'Kezdés',
  'projects.field.end': 'Befejezés',
  'projects.empty.title': 'Még nincs projekt',
  'projects.empty.body': 'Hozz létre egyet, és a feladatai a prefixe alá kerülnek.',
  'projects.error.name': 'A projekt neve nem lehet üres.',
  'projects.error.prefix': 'A prefix 2–6 nagybetű legyen – ez adja a feladatkulcsokat.',
  'projects.error.dates': 'A befejezés nem lehet a kezdés előtt.',
  'projects.error.client': 'Válassz ügyfelet, vagy jelöld belső projektnek.',
  'projects.status.draft': 'Tervezet',
  'projects.status.active': 'Aktív',
  'projects.status.on_hold': 'Felfüggesztve',
  'projects.status.completed': 'Befejezett',
  'projects.status.archived': 'Archivált',

  /* ── egy projekt ───────────────────────────────────────────────────────────────────────── */
  'project.gate.title': 'A projekt még nem használható',
  'project.gate.body':
    'Minden feladat egy sortételhez tartozik, ezért a projektnek kell egy szerződés, rajta legalább egy sortétellel.',
  'project.gate.bodyNoAccess':
    'Minden feladat egy sortételhez tartozik, ezért a projektnek kell egy szerződés, rajta legalább egy sortétellel. Ezt a szerződéseket kezelő munkatársak vehetik fel.',
  'project.gate.step.contract': 'Szerződés a projekten',
  'project.gate.step.lineItem': 'Sortétel a szerződésen',
  'project.tab.overview': 'Áttekintés',
  'project.tab.resource': 'Erőforrás',
  'project.tab.finance': 'Pénzügy',
  'project.resource.estimate': 'Becslés',
  'project.resource.planned': 'Betervezett',
  'project.resource.tracked': 'Rögzített',
  'project.resource.remaining': 'Maradék terv',
  'project.tab.people': 'Emberek',
  'project.fact.people': 'Jelenlegi tagok',
  /* ⚠️ Az oldal kimondja, mi hiányzik és miért. Egy kész kinézetű, üres oldalról az olvasó azt
     hinné, hogy a projekten nincs munka — nem azt, hogy a felület még nem készült el. */
  'project.overview.pending':
    'A készültség, a rögzített idő és az ütemterv a feladatokkal és az órákkal együtt érkezik – addig itt nem jelenik meg szám, mert nem lenne igaz.',
  'project.people.lede':
    'Aki itt szerepel, jogosult órát rögzíteni a projekt feladataira – és csak rá osztható feladat. Ez nem névsor, hanem jogosultság.',
  'project.people.add': 'Hozzáadás',
  'project.people.toast.added': '{name} hozzáadva a projekthez',
  'project.people.addAction': 'Hozzáadás a projekthez',
  'project.people.active': 'Aktív',
  'project.people.past': 'Korábbi',
  'project.people.close': 'Tagság lezárása mai nappal',
  /* 3 Nov 2026 · ending a membership today: it lasts until midnight, and can end now. */
  'project.people.lastDayToday': 'Utolsó nap: ma',
  'project.people.toast.endsTonight': '{name} tagsága éjfélkor megszűnik a projekten.',
  'project.people.toast.endNow': 'Megszüntetés most',
  'project.people.toast.endNowFailed': 'Nem sikerült megszüntetni',
  'project.people.toast.ended': '{name} tagsága megszűnt.',
  'project.people.reopen': 'Tagság újranyitása',
  'project.people.remove': 'Eltávolítás a projektről',
  'project.people.column.name': 'Név',
  'project.people.column.email': 'E-mail',
  'project.people.column.role': 'Szerep',
  'project.people.column.projectRole': 'Projekt szerep',
  'project.people.column.open': 'Nyitott',
  'project.people.pmTag': '{name} · PM',
  /* U-10 · somebody who does not log time: refused by the picker, marked on the list. */
  'project.people.notTracking': 'Nem rögzít időt',
  'project.people.notTrackingHint':
    'Nem rögzít időt, ezért új tagként már nem adható hozzá. Korábbról maradt a projekten, vagy azóta kapcsolták ki az időrögzítését.',
  'project.people.notTrackingOption': '{name} – nem rögzít időt',
  /* 4 Nov 2026 · the window, editable — and a reopen asks for a new start. */
  'project.people.edit': 'Tagság szerkesztése',
  'project.people.upcoming': 'Még nem kezdődött',
  'project.people.windowOrder': 'A tagság nem érhet véget a kezdete előtt.',
  'project.people.column.from': 'Ettől',
  'project.people.column.until': 'Eddig',
  'project.people.column.state': 'Állapot',
  'project.people.empty.title': 'Még senki nincs a projekten',
  'project.people.empty.body':
    'Add hozzá azokat, akik dolgozni fognak rajta – enélkül nem tudnak órát rögzíteni.',

  /* ── C7 refusals. Egy kód, egy mondat, és a szám a mondatban van. ───────────────────────── */
  'error.unavailable': 'A művelet most nem érhető el. Ellenőrizd a kapcsolatot, majd próbáld újra.',
  'error.duplicate_code': 'Ez a kód már használatban van: {value}',
  'error.client_in_use':
    'Az ügyfélhez még tartozik élő projekt, szám szerint {count}. Előbb zárd le azokat.',
  'error.internal_project_billable':
    'A projektnek {count} számlázható sortétele van, így nem lehet belső. Előbb állítsd őket nem számlázhatóra.',
  'error.holds_assigned_tasks':
    'Ennyi feladat van még rá osztva ezen a projekten: {count}. Előbb rendezd azokat.',
  /* U-10 · ⚠️ An ADDED code (rule 8) — `member-tracks-time.ts`. */
  'error.member_not_tracking':
    'Nem rögzít időt, ezért nem adható a projekthez: {name}. Előbb kapcsold be az időrögzítését.',

  /* ══ C8: feladatok ════════════════════════════════════════════════════════════════════════ */

  'nav.tasks': 'Feladatok',
  'title.tasks': 'Feladatok',

  'task.activity': 'Aktivitás',
  'task.tab.comments': 'Kommentek',
  /* ⚠️ A harmadik fül — Időlogok — hiányzik, mert a worklog a C11 entitása. Egy üres fül azt
     mondaná, hogy „nincs rögzítés", pedig az igazság az, hogy még nem lehet rögzíteni. */
  /* ⚠️ A `task.logs.pending` kulcs a C11-gyel MEGSZŰNT: a fül megérkezett. */
  'task.tab.logs': 'Időlogok',
  'task.logs.total': 'Összes rögzített idő',
  'task.logs.empty': 'Erre a feladatra még senki nem rögzített időt.',
  /* ⚠️ Egy lapozó lista megmondja, hogy lapozik — state-rules.md 6. */
  'task.logs.partial':
    'A legutóbbi {count} bejegyzés látszik. Az összesítés a teljes időt mutatja.',

  'task.field.title': 'Cím',
  'task.field.description': 'Leírás',
  'task.field.status': 'Státusz',
  'task.field.priority': 'Prioritás',
  'task.field.assignee': 'Felelős',
  'task.field.project': 'Projekt',
  'task.field.estimate': 'Becslés',
  'task.field.estimateHint': 'Elfogad 8, 8h, 8:00 vagy 8,0 alakot.',
  'task.field.start': 'Kezdés',
  'task.column.tracked': 'Rögzített',
  'task.column.subtasks': 'Részfeladatok',
  'task.column.tags': 'Címkék',
  'task.field.due': 'Határidő',
  'task.field.tags': 'Címkék',
  /* J-2 · a synced task whose Jira status nothing maps: the status is locked until it is. */
  'task.status.unmapped': 'Jira: {status} – nincs leképezve',
  'task.status.unmappedHelp':
    'A státusz addig nem módosítható, amíg a Jira „{status}” státusza nincs leképezve az integráció Státuszok lépésében.',
  /* J-1 · what the tracker files a synced task under — Jira's own words, read-only. */
  'task.field.components': 'Komponensek',
  'task.field.fixVersions': 'Fix verziók',
  'task.field.epic': 'Epic',
  'task.field.tagsPlaceholder': 'új címke',
  /* J-6 · egy kapcsolt jegy címkéi a hibakövető címkéi: listából, nem gépelve. */
  'task.field.tagsTrackerPick': 'Címke a hibakövetőből…',
  'task.field.tagsTrackerNone':
    'A hibakövető címkéi még nem olvasódtak be – a következő szinkron behozza őket.',
  'task.noProject': 'Projekt nélkül',

  'task.description.empty': 'Nincs leírás. Írd le, mi a kész állapot.',
  'task.action.editDescription': 'Leírás szerkesztése',
  'task.action.renameTitle': 'Cím átírása',
  'task.action.removeTag': 'Címke eltávolítása',
  'task.action.back': 'Vissza',

  'task.subtasks': 'Alfeladatok',
  'task.subtasks.progress': '{done} / {total} kész',
  'task.dependencies': 'Függőségek',
  'task.dependency.waitsFor': 'Erre vár',
  'task.dependency.blocks': 'Erre várnak',
  'task.dependency.lag': '{days} nap',
  'task.links': 'Kapcsolódó feladatok',
  'task.subtasks.add': 'Hozzáadás',
  'task.subtasks.placeholder': 'Mit kell még megcsinálni?',
  'task.subtasks.detachOne': '{key} leválasztása',
  'task.subtasks.inherits': 'Az alfeladat a(z) {project} projektbe kerül, a szülővel együtt.',
  'task.link.add': 'Feladat linkelése',
  'task.link.empty': 'Nincs kapcsolódó feladat.',
  'task.link.removeOne': '{key} kapcsolatának törlése',
  'task.dependency.add': 'Függőség hozzáadása',
  'task.dependency.empty': 'Nincs függőség.',
  'task.dependency.lagLabel': 'Csúsztatás (nap)',
  'task.dependency.removeOne': '{key} függőségének törlése',
  'task.picker.search': 'Keresés kulcsra vagy címre…',
  'task.picker.empty': 'Nincs találat.',
  'task.picker.scope.project': 'Csak a(z) {project} projekt feladatai kapcsolhatók ide.',
  'task.picker.scope.none': 'Csak projekthez nem tartozó feladatok kapcsolhatók ide.',

  'task.attachment.add': 'Csatolmány',
  'task.attachment.uploading': 'Feltöltés…',
  'task.attachment.open': '{name} megnyitása teljes méretben',
  'task.attachment.download': '{name} letöltése',
  'task.attachment.remove': '{name} eltávolítása',
  'task.attachment.tooLarge': 'Ez a fájl túl nagy (legfeljebb 15 MB).',
  'task.attachment.infected': 'A víruskereső visszautasította ezt a fájlt.',
  'task.attachment.scannerDown': 'Most nem tudjuk ellenőrizni a fájlokat. Próbáld újra hamarosan.',
  'task.attachment.failed': 'A feltöltés nem sikerült.',
  'task.comments.empty': 'Még nincs komment.',
  'task.comment.write': 'Komment írása',
  'comment.bold': 'Félkövér',
  'comment.italic': 'Dőlt',
  'comment.strike': 'Áthúzott',
  'comment.code': 'Kód',
  'comment.heading': 'Címsor',
  'comment.bullets': 'Felsorolás',
  'comment.numbers': 'Számozott lista',
  'comment.quote': 'Idézet',
  'comment.codeBlock': 'Kódblokk',
  'comment.link': 'Hivatkozás',
  'comment.undo': 'Visszavonás',
  'comment.redo': 'Újra',
  'task.comment.placeholder': 'Írd le, mi történt. @ a megjelöléshez.',
  'task.comment.label': 'Komment szerkesztő',
  'task.comment.mentions': 'Találatok a megjelöléshez',
  'task.comment.hint': 'Küldés: Ctrl+Enter',
  'task.comment.attachRemove': '{name} levétele a kommentről',
  'task.comment.mention': 'Munkatárs megjelölése',
  'task.comment.send': 'Küldés',
  'task.comment.edited': 'szerkesztve',

  'task.mode.drawer': 'Kicsinyítés drawerre',
  'task.mode.modal': 'Nagyítás',
  'task.mode.full': 'Teljes képernyő',

  'task.error.estimate': 'Ezt nem tudom időként értelmezni. Elfogad 8, 8h, 8:00 vagy 8,0 alakot.',
  'error.assignee_not_a_project_member':
    'Nem tagja a feladat projektjének, ezért nem kaphatja meg: {name}',
  'error.would_cycle': 'Ez kört zárna be – a feladat közvetve önmagára hivatkozna.',
  'error.line_item_closed':
    'A(z) {name} sortétel le van zárva, ezért semmi új nem kerülhet rá. Előbb nyisd újra.',
  'error.member_not_active': '{name} nem aktív tag, ezért semmit nem kaphat meg.',
  'error.owner_not_a_project_member': '{name} nem tagja a projektnek, ezért nem lehet felelős.',
  'error.contract_closed':
    'A(z) {name} szerződés le van zárva: a sortételeire nem kerülhet új feladat, és idő sem rögzíthető rájuk.',
  'error.line_item_takes_no_work':
    'Ez a sortétel licenc és díj típusú – nem kerülhet rá feladat, óra és allokáció.',
  'error.task_status_unmapped':
    'A Jira-státusz ({status}) nincs leképezve, ezért a státusz itt nem módosítható. Képezd le az integráció Státuszok lépésében.',
  'error.task_tag_not_in_tracker':
    'A hibakövetőben nincs „{tag}” címke. Kapcsolt jegyre csak a hibakövető saját címkéi kerülhetnek.',

  /* ── a projekt Feladatok füle ───────────────────────────────────────────────────────────── */
  'project.tab.tasks': 'Feladatok',
  'project.tasks.empty.title': 'Ezen a projekten még nincs feladat',
  'project.tasks.empty.body': 'Hozz létre egyet – a kulcsát a projekt prefixéből kapja.',
  'project.tasks.create': 'Új feladat',
  'project.tasks.column.key': 'Kulcs',
  'project.tasks.column.title': 'Cím',
  'project.tasks.column.status': 'Státusz',
  'project.tasks.column.priority': 'Prioritás',
  'project.tasks.column.assignee': 'Felelős',
  'project.tasks.column.due': 'Határidő',
  'project.tasks.column.estimate': 'Becslés',
  'project.tasks.footer': '{count} feladat · {open} nyitott',
  'project.tasks.footerPaged': '{count} látszik {total} feladatból · {open} nyitott',

  /* ── a projekt áttekintő számai, amelyek feladatot számolnak ────────────────────────────── */
  'project.fact.progress': 'Készültség',
  'project.fact.progressNote': '{done} / {total} feladat',
  'project.fact.late': 'Késésben',
  'project.fact.lateNone': 'nincs késés',
  'project.fact.lateCount': '{count} feladat',
  'project.fact.nextDue': 'Következő határidő',
  'project.fact.nextDueNone': 'nincs közelgő határidő',
  'project.fact.breakdown': 'Státusz szerint',
  'project.fact.schedule': 'Ütemterv',
  'project.fact.scheduleOk': 'rendben',
  'project.fact.scheduleLate': '{count} késés',
  'project.fact.participants': 'Résztvevők',
  'project.fact.participantsUnit': 'fő',
  'project.panel.effort': 'Terhelés és ráfordítás',
  'project.panel.effortPerson': 'Ember',
  'project.panel.effortShare': 'Részarány',
  'project.panel.effortTracked': 'Rögzített',
  'project.panel.effortEstimate': 'Becslés',
  'project.panel.effortMeta': '{open} nyitott · {closed} lezárt',
  'project.panel.effortPm': ' · PM',
  'project.panel.effortEmpty': 'Ehhez a projekthez még senki nem rögzített időt.',
  'project.panel.upcoming': 'Következő határidők',
  'project.overtime.note': 'Elfogadott és függő túlóra a projekten',
  'project.overtime.none': 'Nincs túlóra a projekten',
  'project.overtime.accounted': 'elszámolt {hours}',
  'project.overtime.pending': 'függőben',
  'project.panel.upcomingEmpty': 'Nincs közelgő határidő.',
  'project.panel.recent': 'Legutóbbi bejegyzések',
  'project.panel.recentEmpty': 'Még nincs rögzített idő.',
  'project.status.menu': 'Projekt státusza',
  'project.status.lastChanged': 'Utoljára módosítva: {date} · {name} · Indok: {reason}',
  /* ⚠️ A `project.overview.pendingHours` kulcs a C11-gyel MEGSZŰNT: a rögzített idő és a
   * becslés-sáv megjelent, tehát a magyarázat, ami a hiányukat indokolta, már nem igaz. */

  'project.people.column.share': 'Részarány',
  'project.tab.time': 'Idő',
  'project.fact.tracked': 'Rögzített idő',
  'project.fact.trackedOf': '/ {estimate} becslés',
  'project.fact.noEstimate': 'nincs becslés',

  'project.chart.period': '{from} – {to} · összesen {total}',
  'project.chart.earlier': 'Korábbi időszak',
  'project.chart.anchor': 'Időszak kezdete',
  'project.chart.later': 'Következő időszak',
  'project.chart.granularity': 'Időszak felbontása',
  'project.chart.weekly': 'Heti',
  'project.chart.monthly': 'Havi',
  'project.chart.empty': 'Ehhez a projekthez még nincs rögzített idő.',

  'project.time.empty': 'Nincs rögzítés',
  'project.time.emptyNote': 'Erre a projektre még senki nem rögzített időt.',
  'project.time.footer': '{count} bejegyzés · összesen {hours}',
  /* ⚠️ Két külön tény: mennyi látszik, és mennyi a projekt teljes ideje. Egy szám nem tud
   * mindkettő lenni — így születik az "50 bejegyzés · 46:00", ami egy nem látható halmazt ír le. */
  'project.time.footerPartial': '{count} bejegyzés látszik · a projekt összesen {hours}',
  'project.time.noMatch': 'Nincs találat',
  'project.time.noMatchNote': 'Módosítsd a keresést vagy a rögzítő szűrőt.',

  'time.column.date': 'Dátum',
  'time.column.task': 'Feladat',
  'time.column.note': 'Megjegyzés',
  'time.column.member': 'Rögzítő',
  'time.column.hours': 'Óra',
  'time.column.overtime': 'Túlóra',
  'time.log.noOvertime': 'Nem túlóra',
  'time.log.isOvertime': 'Túlóra',

  /* ── az új feladat dialógus ──────────────────────────────────────────────────────────────── */
  'task.dialog.new': 'Új feladat',
  'task.dialog.newHint':
    'A kulcsot a projekt prefixéből kapja. Ütemezés nélkül az alapértelmezett státuszba kerül.',
  'task.dialog.titlePlaceholder': 'Pl. Gantt frissítés a Q4 mérföldkövekhez',
  'task.error.title': 'A feladat címe nem lehet üres.',
  'task.error.lineItem': 'Válassz sortételt: minden feladat egy sortételhez tartozik.',
  'task.action.create': 'Feladat létrehozása',
  'task.action.open': 'Megnyitás',

  /* ══ C9: a négy feladatnézet ═══════════════════════════════════════════════════════════════ */

  'tasks.heading': 'Feladatok',
  'tasks.footer': '{count} feladat',
  'tasks.footerPage': 'Ezen az oldalon {count} feladat',
  /* ⚠️ Item 11 · TWO titles, because they are two situations — and this is the one place in the
     tasks screen that can tell them apart. A board COLUMN cannot: it is handed the cards that
     survived the query. `state-rules.md` §4 asks a screen's empty state to say which it is. */
  'tasks.empty.title': 'Még nincs feladat',
  'tasks.empty.body': 'Vegyél fel egy új feladatot.',
  'tasks.empty.filteredTitle': 'Nincs a szűrésnek megfelelő feladat',
  'tasks.empty.filteredBody': 'Törölj egy szűrőt, vagy vegyél fel egy új feladatot.',

  /* A mockup TASK_VIEWS listája, változatlanul. */
  'tasks.view.list': 'Lista',
  'tasks.view.board': 'Kanban',
  'tasks.view.timeline': 'Idővonal',
  'tasks.view.grid': 'Rács',

  /* ── Kanban ─────────────────────────────────────────────────────────────────────────────── */
  'tasks.board': 'Kanban tábla',
  'tasks.board.column': '{name} oszlop',
  'tasks.board.move': 'Áthelyezés',
  'tasks.board.moveTo': 'Áthelyezés ide: {name}',
  'tasks.board.empty': 'Nincs itt feladat. Húzz ide egyet, vagy vegyél fel újat.',
  /* ⚠️⚠️ **THIS USED TO CLAIM CARDS HAD BEEN HIDDEN, WHICH THE SCREEN CANNOT KNOW — 14 Sep, item
     11.** It was shown whenever ANY filter was set, including the `?projectId=` a *Feladatok
     szűrése erre* link seeds, so an oszlop that was empty all along announced that a filter had
     emptied it. The client holds the filtered set and nothing else; the honest sentence says what
     is true of what it has. */
  'tasks.board.emptyFiltered': 'Nincs a szűrőknek megfelelő feladat ebben az oszlopban.',
  'tasks.moved': '{key} → {name}',
  'tasks.moved.undo': 'Visszavonás',
  /* ⚠️ A visszavonás is elbukhat — a `Toaster` kötelezővé teszi, hogy legyen rá mondat. */
  'tasks.moved.undoFailed': 'A visszavonás nem sikerült',
  'tasks.rescheduled': '{key} átütemezve: {from} → {to}',

  /* ── Idővonal ───────────────────────────────────────────────────────────────────────────── */
  'tasks.timeline': 'Idővonal',
  'tasks.timeline.bar': '{key} · {title}',
  /* ⚠️ A design system kérése: a függőségi ütközést szavakban mondjuk ki, nem csak színnel. */
  'tasks.timeline.conflicts': '{count} függőség sérül',
  'tasks.timeline.milestone': 'Mérföldkő: {key} · {title} · egynapos, becslés nélküli munka.',
  'tasks.timeline.empty': 'Egyetlen feladatnak sincs kezdése és határideje is.',
  /* ⚠️ Amit az idővonal nem tud megmutatni, azt kimondja — nem hallgatja el. */
  'tasks.timeline.unscheduled':
    'Ennyi feladat nem szerepel az idővonalon, mert nincs kezdése vagy határideje: {count}',
  'tasks.zoom.week': 'Heti felbontás',
  'tasks.zoom.month': 'Havi felbontás',
  'tasks.zoom.quarter': 'Negyedéves felbontás',
  'tasks.today': 'Mai időszakra',

  /* ── Rács ───────────────────────────────────────────────────────────────────────────────── */
  'tasks.grid': 'Adatrács',
  'tasks.grid.readOnly': 'A {label} oszlop nem szerkeszthető.',
  'tasks.grid.required': 'A {label} nem lehet üres.',
  'tasks.grid.unknownStatus': 'Nincs ilyen státusz: {value}',
  'tasks.grid.unknownAssignee': 'Nincs ilyen felelős: {value}',
  'tasks.grid.notAnOption': '{column}: a(z) „{value}” nem a felkínált értékek egyike.',
  'tasks.toasts': 'Értesítések',

  /* ══ C14: dolgozók ════════════════════════════════════════════════════════════════════════ */

  'nav.people': 'Dolgozók',
  'title.people': 'Dolgozók',
  'title.person': 'Dolgozó',

  'people.column.person': 'Dolgozó',
  'people.column.email': 'E-mail',
  'people.column.roles': 'Jogosultság',
  'people.column.status': 'Állapot',
  'people.column.tracks': 'Időrögzítés',
  'people.column.team': 'Csapat',
  'people.column.employment': 'Munkaviszony',
  'people.column.projects': 'Projekt',
  'people.column.month': 'E havi óra',

  'people.status.active': 'Aktív',
  'people.status.invited': 'Meghívott',
  'people.status.deactivated': 'Deaktivált',
  'people.status.deleted': 'Törölt',

  'people.employment.internal': 'Belső',
  'people.employment.contractor': 'Alvállalkozó',

  'people.tracks.yes': 'Rögzít időt',
  'people.tracks.no': 'Nem rögzít',

  'people.filter.deleted': 'Töröltek',
  'people.filter.liveOnly': 'Csak élő',
  'people.filter.withDeleted': 'Töröltekkel',

  'people.action.open': 'Megnyitás',
  'people.action.activate': 'Aktiválás',
  'people.action.deactivate': 'Deaktiválás',
  'people.action.delete': 'Törlés',
  'people.action.deleteNeedsDeactivate': 'Előbb deaktiváld – csak deaktivált személy törölhető.',

  /* ── C35 · távoli fiókok emberekhez kötve (§5.10). ────────────────────────────────────────
   *
   * ⚠️⚠️ A párosítatlan sor a drága: `worklogs.member_id` NOT NULL, tehát egy be nem azonosított
   * távoli felhasználó órája **egyáltalán nem tárolható**. */
  'identities.title': 'Távoli fiókok',
  'identities.unpaired': 'ennyi fiókot nem tudunk emberhez kötni: {count}',
  'identities.allPaired': 'Minden felderített fiók emberhez van kötve.',
  'identities.match': 'Párosítás e-mail alapján',
  'identities.matched': 'ennyi új párosítás: {count}',
  'identities.noMatch': 'Nincs új találat e-mail alapján.',
  'identities.failed': 'A párosítás nem sikerült.',
  'identities.none': 'nincs párosítva',
  'identities.manual': 'kézzel',
  'people.empty': 'Nincs dolgozó',
  'people.emptyNote': 'Módosítsd a szűrőket, vagy hívj meg valakit.',
  /* ⚠️ Amit a mockup listája tud és ez nem — különben a reader keresné a meghívókat. */

  /* ── a dolgozó lapja ─────────────────────────────────────────────────────────────────────── */

  'people.tabs': 'A dolgozó adatai',
  'people.facts': 'Törzsadatok',
  'people.metafields': 'Metafieldek',
  'people.deletedNote':
    'Ez a dolgozó törölve van. A neve megmarad, hogy az óra-riportok olvashatók legyenek; minden más adata törlődött, és a lap nem szerkeszthető.',

  'people.kpi.monthHours': 'E havi rögzített',
  'people.kpi.monthHoursHint': 'a hónap elseje óta',
  'people.kpi.openTasks': 'Nyitott feladat',
  'people.kpi.openTasksHint': '{total} összesen',
  'people.kpi.projects': 'Projekt',
  'people.kpi.projectsHint': 'ahol ma tagja',
  'people.kpi.noRole': 'Nincs jogosultsági szint',
  'people.kpi.noTracksHint': 'Kimarad az óra-riportokból',
  'people.kpi.billable': 'Számlázható',
  'people.kpi.billableHint': '{hours} számlázható sortételen',
  'people.kpi.billableNone': 'Ebben a hónapban még nincs rögzített óra',

  /* ── C63 · Elszámolás — e hónap ──────────────────────────────────────────── */
  'people.split.title': 'Elszámolás · e hónap',
  'people.split.empty': 'Ebben a hónapban még nincs rögzített óra.',
  'people.split.fixed': 'Fix áras',
  'people.split.tm': 'Idő és anyag',
  'people.split.nonBillable': 'Nem számlázható',
  'people.split.none': 'Sortétel nélkül',

  'people.field.team': 'Csapat',
  'people.field.noTeam': 'Nincs csapat',
  'people.field.employment': 'Munkaviszony',
  'people.field.seniority': 'Szenioritás',
  'person.seniority.none': 'Nincs megadva',
  'person.seniority.junior': 'Junior',
  'person.seniority.medior': 'Medior',
  'person.seniority.senior': 'Senior',
  'person.seniority.lead': 'Lead',
  'person.seniority.principal': 'Principal',
  'finance.breakdown.bySeniority': 'Szenioritás',
  'people.field.manager': 'Közvetlen vezető',
  'people.field.leave': 'Szabadság',
  'people.leave.managed': 'A rendszerben kezelt',
  'people.leave.external': 'Nem a rendszerben kezelt',
  'people.field.noManager': 'Nincs',
  'people.field.tracks': 'Időrögzítés',
  /* ux-pass-2 · *Erőforrás* — the phase 5 mockup's `persPlan*`, `peoplePlanF` and row menu. */
  'people.field.resource': 'Erőforrás',
  'people.column.plan': 'Erőforrás terv',
  'people.plan.yes': 'Tervezett',
  'people.plan.yesLong': 'Tervezett erőforrás',
  'people.plan.no': 'Nem tervezett',
  'people.plan.yesHint': 'Megjelenik az erőforrás-tervezésben, allokálható sortételre.',
  'people.plan.noHint': 'Kimarad az erőforrás-nézetekből.',
  'people.plan.needsTracking': 'Csak időt rögzítő dolgozó tervezhető erőforrásként.',
  'people.action.planOff': 'Erőforrás-tervezés kikapcsolása',
  'people.action.planOn': 'Erőforrás-tervezés bekapcsolása',
  'people.toast.planOff': 'Nem tervezett erőforrás',
  'people.toast.planOffNote': '{name} kikerül az erőforrás-nézetekből.',
  'people.toast.planOn': 'Tervezett erőforrás',
  'people.toast.planOnNote': '{name} megjelenik az erőforrás-nézetekben.',
  'onboarding.plan.label': 'Erőforrását tervezzük',
  'onboarding.plan.hint.on': 'Bekerül az erőforrás-tervezésbe: sortételekre allokálható.',
  'onboarding.plan.hint.off':
    'Kimarad az erőforrás-tervezésből — nem allokálható szerződés sortételre.',
  'people.field.phone': 'Telefon',
  'people.field.location': 'Helyszín',
  'people.field.familyName': 'Vezetéknév',
  'people.field.givenName': 'Utónév',
  'people.field.email': 'E-mail',
  'people.field.timezone': 'Időzóna',
  'people.field.language': 'Nyelv',
  'people.field.startsOn': 'Belépés',
  'people.field.endsOn': 'Kilépés',

  'people.language.hu': 'Magyar',
  'people.language.en': 'English',

  'people.tab.projects': 'Projektek',
  'people.skills.failed': 'A skill mentése nem sikerült.',
  'people.skills.title': 'Skillek',
  'people.skills.add': 'Skill',
  'people.skills.search': 'Skill keresése',
  'people.skills.empty': 'Még nincs felvett skillje.',
  'people.tab.tasks': 'Feladatok',
  'people.tab.logs': 'Időlogok',
  'people.projects.emptyTitle': 'Nincs projekt',
  'people.projects.empty': 'Nincs projekt – a projekt Emberek fülén adható hozzá.',
  'people.tasks.emptyTitle': 'Nincs feladat',
  'people.tasks.empty': 'Nincs rá osztott feladat.',
  'people.logs.month': 'Hónap',
  'people.logs.search': 'Keresés feladatra vagy megjegyzésre…',
  'people.projects.search': 'Keresés projektre…',
  'people.tasks.search': 'Keresés feladatra…',
  'people.logs.emptyTitle': 'Nincs időlog',
  'people.logs.empty': 'Még nem rögzített időt.',
  'people.list.noMatch': 'Nincs találat',
  'people.list.noMatchBody': 'Egyik sor sem felel meg a keresésnek és a szűrőknek.',
  'people.copy.email': 'E-mail másolása',
  'people.copy.phone': 'Telefonszám másolása',
  'people.status.activeNote': 'Belép, rögzít időt, és látszik a riportokban.',
  /* ── C39 cleanup · a személy Jira-fiókja, a saját lapján. ── */
  'people.integration.title': 'Integrációs beállítások',
  'people.integration.none': 'nincs hozzárendelve',
  'people.integration.search': 'Fiók keresése',
  'people.integration.hint':
    'Ez dönti el, kihez tartoznak a Jirából érkező órák. Csak olyan fiók választható, amit még senkihez nem rendeltek.',
  'people.integration.proven':
    'A kolléga maga csatlakoztatta a Jira-fiókját, így ezt nem lehet felülírni.',
  'people.status.deactivatedNote': 'Nem lép be és nem rögzít időt. A korábbi worklogjai maradnak.',
  'people.status.invitedNote':
    'Meghívót kapott, de még nem fogadta el. Az elfogadásig nem lép be és nem rögzít időt.',
  'logs.column.day': 'Nap',
  'people.tab.noProjects': 'Nincs projekt',
  'people.tab.noProjectsNote': 'Ez a dolgozó egyetlen projektnek sem tagja ma.',
  'people.tab.noTasks': 'Nincs feladat',
  'people.tab.noTasksNote': 'Nincs rá osztott feladat.',
  'people.tab.noLogs': 'Nincs rögzítés',
  'people.tab.noLogsNote': 'Ez a dolgozó még nem rögzített időt.',

  'people.error.stillActive': 'Előbb deaktiváld: {name}.',
  'people.error.unknownSkill': 'Ez a skill vagy szint nem létezik ebben a workspace-ben.',
  'people.error.deleted': 'Ez a dolgozó törölve van, és nem módosítható.',
  'people.error.notPlannable': '{name} nem rögzít időt, ezért nem tervezhető erőforrásként.',
  'people.error.managerSelf': 'Valaki nem lehet a saját vezetője.',
  'people.error.managerCycle': 'Ez körkörös vezetői láncot hozna létre.',

  /* ── a hét lépéses onboarding ────────────────────────────────────────────────────────────── */

  /* ── C15: riportok ─────────────────────────────────────────────────────────────────────── */

  /* ── C16: workspace beállítások ────────────────────────────────────────────────────────── */

  'status.category.open': 'Nyitott',
  'settings.weights.title': 'Súlyok',
  'settings.weights.lede':
    'A feladat becslésének hány százalékát számítjuk teljesítettnek az egyes státuszkategóriákban. Ebből számol a teljesített érték, a CPI és a burn diagramok.',
  'settings.weights.fixed': 'rögzített',
  'settings.weights.column': 'Súly (%)',
  'settings.weights.ariaLabel': '{category} súlya, százalékban',
  'status.category.inProgress': 'Folyamatban',
  'status.category.inReview': 'Ellenőrzés alatt',
  'status.category.closed': 'Lezárt',
  'worklog.unit.min15': '15 perc',
  'worklog.unit.min30': '30 perc',
  'worklog.unit.decimal': 'Tizedes óra',

  /* ── C17: az egy közös export ─────────────────────────────────────────────────────────── */

  /* ── C17: az import ──────────────────────────────────────────────────────────────────────── */

  'io.actions': 'Adatok',
  'import.action': 'Import',
  /* ⚠️ Item 28 · the MENU says what pressing it starts, matching `export.run` beside it.
     `import.action` stays as the dialog's own title, which is a heading rather than a verb. */
  'import.run': 'Import indítása',
  /* ⚠️ Item 21 · a SHAPE, not a made-up company. `ROSS` read as a real prefix somebody had
     to match, which is the trap a placeholder exists to avoid. */
  'common.prefixPlaceholder': 'pl. ABC',
  'import.title.task': 'Feladatok importálása',
  'import.title.worklog': 'Időbejegyzések importálása',
  'import.desc.task': 'Meglévő kulcsot frissít, új kulcsot létrehoz.',
  'import.desc.worklog': 'A bejegyzések a te nevedre kerülnek.',

  'import.drop': 'Húzd ide a fájlt, vagy tallózz',
  /* ⚠️ CSV, és nem XLSX — a mockup mindkettőt kínálja, de xlsx-et itt semmi nem tud olvasni.
   * Egy accept lista, ami átengedi és utána elutasítja, rosszabb. */
  'import.dropNote': 'CSV, legfeljebb {mb} MB. Vessző és pontosvessző is jó.',
  'import.sample': 'Minta fájl',
  'import.cols.task': 'kulcs, cím, projekt, becslés, kezdés, határidő',
  'import.cols.worklog': 'dátum, feladatkulcs, óra, megjegyzés',
  'import.tooBig': 'Ez a fájl nagyobb {mb} MB-nál. Vágd szét, vagy szűkíts az időszakon.',

  'import.working': 'Feldolgozás…',
  'import.workingNote':
    'Minden sor ugyanazokon a szabályokon megy át, mint a kézi rögzítés – ezért tart, ameddig tart.',

  'import.done': '{ok} sor bekerült',
  'import.partial': '{bad} sor hibás – {ok} sor bekerült',
  'import.tally': 'Új: {created} · Frissítve: {updated} · Kimaradt (már szerepelt): {skipped}',
  'import.line': '{line}. sor',
  'import.downloadErrors': 'Hibalista letöltése',
  'import.csv.line': 'sor',
  'import.csv.error': 'hiba',
  'import.newFile': 'Új fájl',

  'import.error.columns': 'A fájlból hiányzik: {columns}. Töltsd le a minta fájlt.',
  /* ⚠️⚠️ A mockup ötödik állapota („e-mailben szólunk”) nincs megépítve: ahhoz a fájlnak túl kell
   * élnie a kérést, objektumtároló pedig nincs. Ezért elutasítunk, és megmondjuk, hány sor volt. */
  'import.defaultLineItem': 'Alapértelmezett sortétel az új feladatokhoz',
  'import.defaultLineItemNone': 'Nincs – minden sor nevezze meg a sortetel oszlopban',
  'import.error.tooLarge':
    'Ez a fájl {rows} sor – egy kérésben ennyit nem dolgozunk fel. Vágd szét kisebb fájlokra.',

  'import.toast.sample': 'Minta fájl letöltve',
  'import.toast.sampleNote': 'Töltsd ki, és húzd vissza az ablakba.',
  'import.toast.done': 'Import kész',

  'common.done': 'Kész',

  'export.title': '{what} exportálása',
  'export.lede':
    'Kis exportot azonnal letöltesz, nagyot e-mailben küldünk – a szerver dönti el, melyik.',
  'export.field.period': 'Időszak',
  'export.field.from': 'Ettől',
  'export.field.to': 'Eddig',
  'export.run': 'Export indítása',
  'export.running': 'Készül…',
  /* ⚠️⚠️ Kimondjuk, hogy csak CSV van. Egy egyelemű választó olyan vezérlő, ami nem tesz semmit; egy
   * CSV-ből átnevezett .xlsx pedig hazugság, amit az Excel biztonsági figyelmeztetéssel nyit meg. */
  'export.format': 'Formátum',
  'export.noPeriod': 'A(z) {what} teljes listája kerül a fájlba – ehhez nincs időszak.',

  'export.period.week': 'Ez a hét',
  'export.period.lastweek': 'Előző hét',
  'export.period.month': 'Ez a hónap',
  'export.period.lastmonth': 'Előző hónap',
  'export.period.quarter': 'Ez a negyedév',
  'export.period.all': 'Teljes idő',
  'export.period.custom': 'Egyéni időszak',

  'export.domain.task': 'Feladatok',
  'export.domain.worklog': 'Időbejegyzések',
  'export.domain.project': 'Projektek',
  'export.domain.client': 'Ügyfelek',
  'export.domain.member': 'Dolgozók',
  'export.domain.report': 'Riport',

  'export.toast.ready': 'Az export letöltve',
  'export.toast.rows': '{rows} sor.',
  'export.toast.queued': 'Az export készül',
  'export.toast.queuedNote': '{rows} sor – e-mailben küldjük a {email} címre, csatolva.',
  'export.error.tooLarge':
    'Ennyi sort ({rows}) nem tudunk e-mailben elküldeni. Szűkíts az időszakon.',

  'report.action.export': 'CSV export',
  'report.action.exportFile': 'Exportálás',
  'settings.export.note':
    'Kis export azonnal letöltődik; nagyot összeállítunk és e-mailben küldünk csatolva.',

  'title.billing': 'Számlázás',
  'settings.nav.billing': 'Számlázás',

  /* ⚠️⚠️ A mockup hat számlázási paneljéből négy egy fizetési szolgáltató adata. Nincs
   * szolgáltató — egy kiszürkített "Professional · 148 000 Ft/hó" és egy Visa ••••4242 attól még
   * azt állítja, hogy van csomagod és kártyád. Egyik sem igaz. */
  'billing.noProvider.title': 'Nincs fizetési szolgáltató bekötve',
  /* ⚠️ **ÁTÍRVA az S6-ban, mert egy fele már nem igaz.** A csomagot és az árát mostantól nem a
     szolgáltató adja, hanem az üzemeltető állítja be — a megújulás, a kártya és a számlák viszont
     továbbra is a szolgáltatóé, és nincs szolgáltató. Csak azt sorolja fel, ami valóban hiányzik. */
  'billing.noProvider.body':
    'A megújulás, a kártya és a számlák a fizetési szolgáltatótól jönnek – az a következő fázis. A csomagot és a helyek számát az üzemeltető állítja be; a számlázási adat pedig a tiéd, és akkor is ez lesz, amikor a szolgáltató megérkezik.',

  /* ── a csomag, amit az üzemeltető adott (S6) ────────────────────────────────────────────── */
  'billing.plan.title': 'Csomag',
  'billing.plan.lede': 'Ezt a csomagot kaptad. Fizetés nincs mögötte.',
  'billing.plan.none': 'Nincs csomag',
  /* ⚠️ Kimondva, hogy ne tűnjön hibának: próbaidőben ez a szokásos állapot. */
  'billing.plan.noneNote':
    'Próbaidőben ez a szokásos: csomag nélkül nincs helykorlát. A csomagot az üzemeltető állítja be.',
  'billing.plan.perSeat': '{price} / hely / hó',

  'billing.trial.title': 'Próbaidő',
  'billing.trial.body':
    'A próbaidő eddig tart: {date}. Utána a munkaterület felfüggesztésre kerül.',

  'billing.seats.title': 'Seat-ek',
  'billing.seats.lede': 'Ahány aktív tag helyet foglal most.',
  'billing.seats.unit': 'aktív tag',
  /* ⚠️ **ÁTÍRVA az S6-ban**: a keret mostantól ismert, ha van csomag — a régi mondat („a
     szolgáltató tudja”) éppen az ellenkezőjét állította annak, amit a képernyő ma mutat. */
  'billing.seats.note':
    'A deaktivált tagok nem foglalnak helyet. A még el nem fogadott meghívók viszont igen.',
  'billing.seats.limit': 'ennyi hely a csomagban: {limit}',
  'billing.seats.unlimited': 'Korlát nélkül',
  'billing.seats.pending': 'meghívó lefoglalva: {count}',
  /* ⚠️ A munkaterület és a szervezet két különböző szám, ha egyszer több munkaterület lesz. */
  'billing.seats.tenant': 'a szervezet egészében: {count}',

  'billing.details.title': 'Számlázási adatok',
  'billing.details.lede': 'Ez kerül a számlára. A szolgáltató ezt fogja olvasni.',
  'billing.field.company': 'Cégnév',
  'billing.field.email': 'Számlázási e-mail',
  'billing.field.emailNote': 'Ide megy a számla – nem feltétlenül a te fiókod címe.',
  'billing.field.address': 'Cím',
  'billing.field.country': 'Ország',
  'billing.field.vat': 'Adószám',
  'billing.field.vatOk': 'Érvényes formátum.',
  'billing.error.email': 'Érvényes e-mail címet írj be.',
  'billing.error.vatHu': 'Magyar adószám formátuma: HU + 8 számjegy.',
  'billing.error.vatEu': 'Országkód + 8–12 karakter, pl. DE123456789.',
  'billing.error.rejected': 'A szerver nem fogadta el az adószámot vagy az e-mail címet.',

  'billing.country.HU': 'Magyarország',
  'billing.country.DE': 'Németország',
  'billing.country.AT': 'Ausztria',
  'billing.country.RO': 'Románia',
  'billing.country.GB': 'Egyesült Királyság',
  'billing.country.US': 'Egyesült Államok',

  'title.settings': 'Beállítások',
  'breadcrumb.label': 'Morzsamenü',
  'breadcrumb.copy': '{key} másolása',
  'breadcrumb.copied': 'Másolva',
  'breadcrumb.copyFailed': 'Nem sikerült másolni',
  'breadcrumb.copyFailedBody':
    'A böngésző nem engedte a vágólapra írást. Jelöld ki és másold kézzel.',
  'breadcrumb.toasts': 'Másolás értesítései',
  'breadcrumb.home': 'Kezdőlap',
  'title.profileSettings': 'Profil beállítások',
  'title.settingsGeneral': 'Általános',
  'title.settingsTasks': 'Taskok',
  'title.settingsWorklog': 'Worklog szabályok',
  'title.settingsIntegrations': 'Integrációk',
  'title.settingsData': 'Adatok',

  'settings.nav.label': 'Workspace',
  'settings.nav.general': 'Általános',
  'settings.nav.permissions': 'Jogosultságok',
  'settings.nav.tasks': 'Taskok',
  'settings.nav.metafields': 'Metafieldek',
  'settings.nav.projectRoles': 'Projekt szerepek',
  'title.settingsProjectRoles': 'Projekt szerepek',
  'settings.projectRoles.title': 'Projekt szerepek',
  'settings.projectRoles.new': 'Új szerep',
  'settings.projectRoles.edit': 'Szerep szerkesztése',
  'settings.projectRoles.name': 'Név',
  'settings.projectRoles.namePlaceholder': 'Pl. Tech Lead',
  'settings.projectRoles.usage': '{count} tagságon',
  'settings.projectRoles.empty': 'Még nincs projekt szerep ebben a munkaterületben.',
  'settings.projectRoles.column.name': 'Megnevezés',
  'settings.projectRoles.search': 'Szerep keresése',
  'settings.projectRoles.emptyBody': 'Vegyél fel egyet, hogy a projekttagok szerepet kapjanak.',
  'settings.projectRoles.delete': 'Szerep törlése',
  'settings.projectRoles.deleteUnused': 'A(z) {name} szerepet senki sem viseli. Törölhető.',
  'settings.projectRoles.deleteUsed':
    'A(z) {name} szerepet {count} projekttagság viseli. A törléssel ők a projekten maradnak, csak a címkéjük tűnik el.',
  'settings.projectRoles.error.name': 'Adj nevet a szerepnek.',
  'settings.projectRoles.error.duplicate': 'Ez a szerep már létezik.',
  'project.people.noRole': 'Nincs szerep',
  'project.people.roleDefault': 'Az alapértelmezettje',
  'people.field.defaultProjectRole': 'Alap projekt szerep',
  /* R-60 · Csapatok — a munkaterület listája; egy munkatárs több csapatban is lehet. */
  'settings.nav.teams': 'Csapatok',
  'title.settingsTeams': 'Csapatok',
  'settings.teams.new': 'Új csapat',
  'settings.teams.edit': 'Csapat szerkesztése',
  'settings.teams.name': 'Név',
  'settings.teams.namePlaceholder': 'Pl. Delivery',
  'settings.teams.column.name': 'Megnevezés',
  'settings.teams.column.people': 'Tagok',
  'settings.teams.search': 'Csapat keresése',
  'settings.teams.empty': 'Még nincs csapat ebben a munkaterületben.',
  'settings.teams.emptyBody': 'Vegyél fel egyet, aztán a munkatársak adatlapján tedd bele őket.',
  'settings.teams.delete': 'Csapat törlése',
  'settings.teams.deleteUnused': 'A(z) {name} csapatban senki sincs. Törölhető.',
  'settings.teams.deleteUsed':
    'A(z) {name} csapatban {count} munkatárs van. A törléssel kikerülnek belőle – a többi csapatuk és minden más adatuk megmarad.',
  'settings.teams.error.name': 'Adj nevet a csapatnak.',
  'people.field.teams': 'Csapatok',
  'people.team.add': 'Csapatba tesz',
  'people.team.remove': 'Kivesz a csapatból',
  'filter.team': 'Csapat',
  'filter.allTeams': 'Minden csapat',
  'report.filter.teams': 'Csapat',
  /* 17 Oct 2026 · the person screen's *Alap szerep* row. */
  'people.field.defaultRole': 'Alap szerep',
  'people.field.noDefaultRole': 'Nincs megadva',
  'people.error.unknownProjectRole': 'Ez a projekt szerep már nem létezik.',
  'history.field.defaultProjectRole': 'Alap szerep',
  'settings.nav.worklog': 'Worklog szabályok',
  'settings.nav.holidays': 'Ünnepnapok',
  'settings.nav.schedules': 'Munkarendek',
  'settings.nav.rates': 'Díjkártyák',
  'settings.nav.currency': 'Pénznemek',
  'title.settingsRates': 'Díjkártyák',
  'title.settingsCurrency': 'Pénznemek',

  'settings.currency.title': 'Pénznemek',
  'settings.currency.add': 'Pénznem hozzáadása',
  'settings.currency.column.code': 'Kód',
  'settings.currency.column.name': 'Név',
  'settings.currency.column.symbol': 'Jel',
  'settings.currency.column.rate': 'Árfolyam',
  'settings.currency.column.status': 'Állapot',
  'settings.currency.search': 'Pénznem keresése',
  'settings.currency.empty': 'Nincs pénznem',
  'settings.currency.emptyBody': 'Vegyél fel legalább egyet, és jelöld alapértelmezettnek.',
  'settings.currency.default': 'Alapértelmezett',
  'settings.currency.isBase': 'alap',
  'settings.currency.active': 'Aktív',
  'settings.currency.inactive': 'Inaktív',
  'settings.currency.makeDefault': 'Legyen az alapértelmezett',
  'settings.currency.activate': 'Aktiválás',
  'settings.currency.deactivate': 'Deaktiválás',
  'settings.currency.rowMenu': '{name} műveletei',
  'settings.currency.dialog.new': 'Új pénznem',
  'settings.currency.dialog.known': 'Ismert pénznemek',
  'settings.currency.dialog.pick': 'Válassz…',
  'settings.currency.error.inUse':
    'Ezt a pénznemet díjkártya használja. Előbb írd át azokat, utána törölhető.',
  'settings.currency.error.isDefault':
    'Ez az alapértelmezett pénznem. Előbb jelölj ki másikat alapértelmezettnek.',

  'settings.rates.title': 'Díjkártyák',
  'settings.rates.tab.templates': 'Sablonok',
  'settings.rates.tab.assignments': 'Hozzárendelések',
  'settings.rates.new': 'Sablon létrehozása',
  'settings.rates.assign': 'Díjkártya hozzárendelése',
  'settings.rates.assignShort': 'Hozzárendelés',
  'settings.rates.searchTemplates': 'Keresés sablon nevére…',
  'settings.rates.searchPeople': 'Keresés névre…',
  'settings.rates.column.id': 'ID',
  'settings.rates.column.name': 'Név',
  'settings.rates.column.cost': 'Költségdíj',
  'settings.rates.column.target': 'Céldíj',
  'settings.rates.column.unit': 'Egységtípus',
  'settings.rates.allUnits': 'Minden egységtípus',
  'settings.rates.column.currency': 'Pénznem',
  'settings.rates.column.status': 'Állapot',
  'settings.rates.column.person': 'Dolgozó',
  'settings.rates.column.card': 'Díjkártya',
  'settings.rates.column.source': 'Forrás',
  'settings.rates.column.from': 'Érvényes',
  'settings.rates.sub': '{periods} időszak · {people} fő',
  'settings.rates.unit.hour': 'Óradíj',
  'settings.rates.unit.day': 'Napidíj',
  'settings.rates.fromTemplate': 'Sablon',
  'settings.rates.fromCustom': 'Egyéni',
  'settings.rates.customRate': 'Egyéni díj',
  'settings.rates.noRate': 'Nincs díjkártyája',
  'settings.rates.rowMenu': '{name} műveletei',
  'settings.rates.empty': 'Még nincs díjkártya. Hozz létre egyet.',
  'settings.rates.emptyBody': 'Hozz létre egyet, és rendeld hozzá a dolgozókat.',
  'settings.rates.emptyAssignments': 'Nincs hozzárendelt díj',
  'settings.rates.emptyAssignmentsBody':
    'Díj nélkül az órák 0 Ft-tal mennek be — a tényköltség és az árrés is hibás lesz.',
  'settings.rates.periods': 'Időszakok',
  'settings.rates.addPeriod': 'Időszak hozzáadása',
  'settings.rates.removePeriod': 'Időszak törlése',
  'settings.rates.activeLabel': 'Aktív · hozzárendelhető',
  'settings.rates.namePlaceholder': 'pl. Senior fejlesztő',
  'settings.rates.pickPerson': 'Válassz dolgozót…',
  'settings.rates.pickTemplate': 'Válassz sablont…',
  'settings.rates.dialog.new': 'Új díjkártya',
  'settings.rates.dialog.edit': 'Díjkártya szerkesztése',
  'settings.rates.dialog.assign': 'Díjkártya hozzárendelése',
  'settings.rates.dialog.reassign': 'Hozzárendelés szerkesztése',
  'settings.rates.error.inUse':
    'Ehhez a sablonhoz {count} hozzárendelés tartozik. Előbb rendeld át őket.',
  'nav.dashResource': 'Erőforrás',
  'title.dashResource': 'Erőforrás',
  /* 17 Oct 2026 · Utilizáció — the fourth dashboard. */
  'nav.dashUtil': 'Utilizáció',
  'title.dashUtil': 'Utilizáció',
  'util.chart.title': 'Kihasználtság',
  'util.legend.billable': 'Számlázható',
  'util.legend.nonBillable': 'Nem számlázható',
  'util.legend.soft': 'Soft booking',
  'util.legend.capacity': 'Munkaidő (100%)',
  'util.tip.capacity': 'Munkaidő {hours}',
  'util.tip.free': 'Szabad (soft is)',
  'util.tip.value': '{percent}% · {hours}',
  'util.tip.others': '+{count} további projekt',
  'util.group.role': 'Szerepkör szerint',
  'util.group.seniority': 'Senioritás szerint',
  'util.group.free': 'Felszabaduló emberek',
  'util.group.skill': 'Skillek szerint',
  'util.search': 'Keresés névre, szerepkörre, skillre, projektre…',
  'util.filter.role': 'Szerepkör',
  'util.filter.allRoles': 'Minden szerepkör',
  'util.filter.seniority': 'Senioritás',
  'util.filter.allSeniority': 'Mind',
  'util.noRole': 'Nincs szerepkör',
  'util.noSeniority': 'Nincs senioritás',
  'util.line.soft': 'Soft',
  'util.row.group': 'Csoport kinyitása és összecsukása',
  'util.row.overPlanned': 'Túltervezve legalább egy oszlopban',
  'util.alloc.soft': 'soft',
  'util.alloc.nonBillable': 'nem számlázható',
  'util.alloc.crossRole': 'más szerepkör',
  'util.alloc.noProject': 'Projekt nélkül',
  'util.empty': 'A szűrők minden dolgozót kiszűrtek',
  'util.tableEmpty': 'A keresés minden sort kiszűrt ebben a nézetben.',

  'resource.search': 'Keresés névre, szerepre…',
  'resource.empty': 'Nincs tervezett dolgozó ebben a nézetben',
  'resource.week': 'H {week}',

  'resource.stat.people': 'Dolgozók száma',
  'resource.stat.under': 'Rendelkezik alultervezéssel',
  'resource.stat.over': 'Rendelkezik túltervezéssel',
  'resource.stat.load': 'Allokációk átlaga',
  'resource.stat.planned': 'Betervezett',
  'resource.stat.overtime': 'Túlóra hányad',

  'resource.line.capacity': 'Munkaidő',
  'resource.line.allocated': 'Kiosztott',
  'resource.line.leave': 'Szabadság',

  'resource.filter.contract': 'Szerződés',
  'resource.filter.allContracts': 'Minden szerződés',
  'resource.filter.load': 'Terhelés',
  'resource.filter.anyLoad': 'Mind',
  'resource.filter.overtime': 'Túlóra',
  'resource.filter.overtimeOnly': 'Csak túlóra-allokáció',
  'resource.load.under': 'Alultervezett',
  'resource.load.over': 'Túltervezett',

  'resource.row.breakdown': 'Allokációk ki- és összecsukása',
  'resource.cell.summary': 'Kapacitás {capacity} · kiosztva {allocated}',
  'resource.cell.edit': '{name} · {column}',
  'resource.item.menu': '{name} műveletei',
  'resource.item.distribution': 'Napi elosztás',
  'resource.dist.even': 'Egyenlő elosztás a napok között',
  'resource.dist.earliest': 'Erőforrás legkorábbi allokálása',
  'resource.item.overtimeLabel': 'Tervezett túlóra',
  /**
   * ⚠️⚠️ **THE ROW'S INFO POP-OVER — round 5 item 28.** The mockup draws it and the app had
   * nothing; these are its nine lines. The four that already had a key elsewhere reuse it
   * (`contract.column.startsOn`, `endsOn`, `estimate`, `tracked`) — a fifth spelling of *Kezdés*
   * is how two screens end up disagreeing about a word.
   */
  'resource.tip.title': 'Sortétel összegzése: {name}',
  'resource.tip.ratio': 'Terv / becslés',
  'resource.tip.plan': 'Terv',
  'resource.tip.overtime': 'Túlóra',
  'resource.tip.remaining': 'Maradék',
  'resource.tip.open': 'Sortétel megnyitása',
  /** ⚠️ Round 5 item 29 — the overview table behind the filter's info button. */
  'resource.overview.open': 'Áttekintő táblázat',
  'resource.overview.title': 'Sortételek áttekintője',
  'resource.overview.empty': 'Ennek a projektnek nincs sortétele.',
  'resource.overview.people': 'Emberek',
  'resource.item.noOvertime': 'Nincs túlóra',
  'resource.item.remove': 'Allokáció törlése',
  'resource.item.add': 'Sortétel hozzáadása',
  /* U-15 · a projekt Erőforrás fülének sortétel szerinti nézete. */
  'resource.group.items': 'Sortételek',
  'resource.group.people': 'Emberek',
  'resource.person.add': 'Személy hozzáadása',
  'resource.pickPerson.search': 'Keresés névre, szerepkörre…',
  'resource.pickPerson.none': 'A projekt minden tervezett tagja szerepel már ezen a sortételen.',
  'resource.row.people': 'Emberek ki- és összecsukása',
  'resource.emptyItems': 'Ennek a projektnek nincs tervezhető sortétele',
  'resource.footer.lineItems': '{count} sortétel',
  'resource.error.notAMember':
    'Ebben az időszakban nem tagja a projektnek, ezért nem tervezhető rá: {name}',
  /* 4 Nov 2026 · a line item the person cannot be planned onto, on hover in the picker. */
  'resource.pick.notMember':
    '{name} ebben az időszakban nem tagja a(z) {project} projektnek, ezért nem tervezhető rá. Előbb add hozzá a projekt Emberek fülén.',
  'resource.item.overtime': '{name} ×{multiplier}',
  'resource.cell.title': '{name} · {column}',
  'resource.toast.saved': 'Allokáció mentve',
  'resource.toast.cleared': 'Allokáció törölve',
  'resource.toast.copied': 'Érték másolva {count} oszlopra',
  'resource.toast.clearedRange': 'Allokáció törölve {count} oszlopból',
  'resource.toast.removed': 'Allokáció törölve mától',
  'resource.toast.overtimeOn': 'Túlóraként tervezve',
  'resource.toast.overtimeOff': 'Túlóra kikapcsolva',
  'resource.toast.priority': 'Napi prioritás módosítva',
  'resource.toast.hard': 'Hard bookingra állítva',
  'resource.toast.soft': 'Soft bookingra állítva',
  'resource.toasts': 'Erőforrás-értesítések',
  'resource.pick.search': 'Keresés sortételre, szerződésre…',
  'resource.pick.estimate': 'becslés {hours}',
  'resource.pick.none': 'Nincs több nyitott sortétel, amire tervezni lehetne.',
  'resource.pick.noMatch': 'Nincs találat.',
  'resource.range.copy': 'Másolás eddig a napig…',
  'resource.range.clear': 'Törlés eddig a napig…',
  'resource.range.apply': 'Kattints ide a művelet befejezéséhez, {column}',
  'resource.cell.hours': '{name} órái ebben az oszlopban',
  'resource.cell.total': 'Összesen ebben az oszlopban',
  /* 27 Oct 2026 · the pop-over's bin asks which: this column, or the whole allocation. */
  'resource.cell.removeMenu': 'Eltávolítás',
  'resource.cell.clear': 'Ürítés ebben az oszlopban: {column}',
  'resource.cell.clearFromToday': 'Ürítés ebben az oszlopban mától: {column}',
  'resource.cell.removeWhole': 'A teljes allokáció törlése mától',
  'resource.cell.removeWholeSpan': '{from} – {to}',
  'resource.cell.dropDraft': 'Sor eltávolítása',
  'resource.cell.add': 'Új allokáció hozzáadása',
  'resource.cell.plain': 'Sima munkaidő',
  'resource.cell.priority': 'Napi prioritás',
  'resource.cell.priorityIs': 'Napi prioritás: {mode}',
  'resource.cell.booking': 'Foglalás',
  'resource.cell.bookingIs': 'Foglalás: {kind}',
  'resource.cell.hard': 'Fix',
  'resource.cell.hardHint': 'Elkötelezett kapacitás.',
  'resource.cell.soft': 'Előzetes',
  'resource.cell.softHint': 'Előjegyzés – jelzésértékű, a másolás is örökli.',
  'resource.error.noWorkingDay':
    'Ebben az oszlopban nincs olyan nap, amin ez a dolgozó dolgozna – válassz más időszakot.',
  'settings.nav.leave': 'Szabadságkezelés',
  'settings.nav.overtime': 'Túlóra',
  'title.settingsOvertime': 'Túlóra',
  'settings.overtime.addType': 'Új típus',
  'settings.overtime.column.name': 'Megnevezés',
  'settings.overtime.column.multiplier': 'Szorzó',
  'settings.overtime.column.when': 'Mikor',
  'settings.overtime.when.any': 'Bármely nap',
  'settings.overtime.when.weekend': 'Hétvége',
  'settings.overtime.when.holiday': 'Ünnep- és munkaszüneti nap',
  'settings.overtime.default': 'Alapértelmezett',
  'settings.overtime.deleteType': '{name} törlése',
  'settings.overtime.editType': '{name} szerkesztése',
  'settings.overtime.empty': 'Még nincs túlóra típus. Vegyél fel egyet.',
  'settings.overtime.dialog.new': 'Új túlóra típus',
  'settings.overtime.dialog.edit': 'Túlóra típus szerkesztése',
  'settings.overtime.claims': '{count} kérelem',
  'settings.overtime.noClaims': 'nincs kérelem',
  'settings.overtime.makeDefault': 'Legyen az alapértelmezett',
  'settings.overtime.isDefault': 'Ez az alapértelmezett',
  'settings.overtime.namePlaceholder': 'Pl. Hétvégi túlóra',
  'settings.overtime.search': 'Típus keresése',
  'settings.overtime.emptyBody': 'Vegyél fel egyet, hogy a túlóra kérelmek szorzót kapjanak.',
  'logs.row.markOvertime': 'Túlóra jelölése',
  'logs.row.markOvertimeClaimed': 'Ez az óra már túlóraként van jelölve.',
  'logs.overtime.raised': 'Túlóra kérelem beadva',
  'logs.overtime.error.already': 'Erre a bejegyzésre már van túlóra kérelem.',
  'logs.overtime.error.noType':
    'Nincs aktív túlóra típus. Előbb vegyél fel egyet a beállításokban.',
  'nav.requests': 'Saját kérelmeim',
  'nav.toDecide': 'Elbírálandó kérelmek',
  'nav.approvals': 'Kérelmek',
  'nav.dashLogging': 'Logolás',
  'nav.dashLeave': 'Szabadságok',
  'title.dashLogging': 'Logolás',
  'title.dashLeave': 'Szabadságok',
  'dash.from': 'Kezdet',
  'dash.to': 'Vége',
  'dash.column.person': 'Dolgozó',
  'dash.column.variance': 'Eltérés',
  'dash.row.frame': '{hours} óra/hét',
  'dash.column.days': 'Nap',
  'dash.filter.manager': 'Vezető',
  'dash.search': 'Keresés névre, munkakörre…',
  'dash.leave.search': 'Keresés névre…',
  'range.apply': 'Alkalmaz',
  'range.reset': 'Visszaállítás',
  'range.trigger': 'Időszak',
  'range.today': 'Ma',
  'range.todayHint': 'Ugrás mára',
  'range.preset.default': 'Alapértelmezett',
  'range.preset.thisWeek': 'Ez a hét',
  'range.preset.thisMonth': 'Ebben a hónapban',
  'range.preset.thisQuarter': 'Ebben a negyedévben',
  'range.preset.nextMonth': 'Következő hónap',
  'range.preset.nextQuarter': 'Következő negyedév',
  'range.preset.lastMonth': 'Múlt hónap',
  'range.preset.lastQuarter': 'Múlt negyedév',
  'dash.view.week': 'Heti nézet',
  'dash.view.day': 'Napi nézet',
  'dash.filter.project': 'Projekt',
  'dash.filter.allProjects': 'Minden projekt',
  'dash.line.planned': 'Munkaidő',
  'dash.line.logged': 'Rögzített',
  'dash.line.leave': 'Szabadság',
  'dash.row.breakdown': 'Projektbontás',
  'dash.row.shortDays': '{count} hiányos nap az időszakban',
  'dash.row.unknownHours': 'Ismeretlen sortételű jegyekre logolt idő az időszakban: {hours}',
  'dash.row.unknownLineItem': 'Ismeretlen sortétel',
  'dash.row.openUnknown': 'Ismeretlen sortételű logok megnyitása ({hours})',
  'people.logs.lineItem': 'Sortétel',
  'people.logs.unknownLineItems': 'Ismeretlen sortételek',
  'nav.time.unknownHours': 'Az elmúlt 30 napban ismeretlen sortételű jegyekre logolt idő: {hours}',
  'nav.time.shortDays': '{count} hiányos nap az elmúlt 30 napban',
  'dash.row.noLogs': 'Nincs rögzítés ebben az időszakban.',
  'dash.footer.people': '{count} dolgozó',
  'dash.footer.columns': '{count} időszak',
  'dash.filter.everybody': 'Mindenki',
  'dash.filter.schedule': 'Munkarend',
  'dash.filter.allSchedules': 'Minden munkarend',
  'dash.filter.varianceOnly': 'Csak eltéréssel',
  'dash.cell.hours': '{logged} / {planned} óra',
  'dash.verdict.under': 'kevés',
  'dash.verdict.ok': 'terv szerint',
  'dash.verdict.over': 'terv felett',
  'dash.verdict.off': 'pihenőnap',
  'dash.verdict.future': 'még hátra van',
  'dash.verdict.free': 'nincs napi terv',
  'dash.verdict.leave': 'szabadság',
  'dash.verdict.holiday': 'munkaszüneti nap',
  'dash.empty.logging': 'Senki nem esik a szűrőbe.',
  'dash.empty.leave': 'Ebben az időszakban senki nincs szabadságon.',
  'title.requests': 'Kérelmeim',
  'title.approvals': 'Kérelmek',
  'leave.new': 'Új kérelem',
  'leave.view.all': 'Minden kérelem',
  'leave.menu.open': 'Megnyitás',
  'leave.menu.openTask': 'Feladat megnyitása',
  'leave.column.year': 'Keretév',
  'leave.column.created': 'Beadva',
  'leave.column.dayAndTask': 'Nap és feladat',
  'leave.column.hoursTimes': 'Óra × szorzó',
  'leave.column.accounted': 'Elszámolt',
  'leave.filter.allTypes': 'Minden típus',
  'leave.filter.allYears': 'Minden keretév',
  'leave.empty.overtime': 'Nincs túlóra-kérelmed ebben a nézetben',
  'leave.empty.overtimeBody':
    'Túlórát az időrögzítésnél jelölsz – a bejegyzésből automatikusan kérelem lesz.',
  /* ⚠️ The home screen's own overtime tile still draws these two — the pair the requests page
     dropped with its figure cards were `approvedMonth`, `accountedHint` and `pendingHint`. */
  /* 16 Oct 2026 · the Túlóra tab's figure strip. */
  'leave.ot.approvedYear': 'Elfogadva — idén',
  'leave.ot.thisMonth': 'E hónap',
  'leave.claim.new': 'Új túlóra-kérelem',
  'leave.claim.newHint':
    'Válassz egy rögzített bejegyzést – a dátum, az óra és a feladat abból jön.',
  'leave.claim.pickLog': 'Melyik bejegyzésre?',
  'leave.claim.searchPlaceholder': 'Keresés feladatra, megjegyzésre vagy dátumra',
  'leave.claim.noLogs': 'Nincs választható bejegyzés',
  'leave.claim.noLogsBody':
    'Az elmúlt 90 napból minden bejegyzésedre van már túlóra-kérelem, vagy a szűrők nem hagytak semmit.',
  'leave.claim.picked': 'Kiválasztva: {task} · {date} · {hours}',
  'leave.claim.allTasks': 'Minden feladat',
  'leave.claim.typeFromDay': 'A nap szerint',
  'leave.claim.notePlaceholder': 'Miért volt túlóra?',
  'leave.claim.submit': 'Kérelem beadása',
  'leave.column.person': 'Dolgozó',
  'leave.column.kind': 'Fajta',
  'leave.column.amount': 'Mérték',
  'leave.kind.leave': 'Szabadság',
  'leave.kind.overtime': 'Túlóra',
  'leave.filter.kind': 'Kérelem fajtája',
  'leave.filter.allKinds': 'Mindkét fajta',
  'leave.amount.days': '{days} nap',
  'leave.amount.accounted': '{hours} óra · ×{multiplier}',
  'leave.column.type': 'Típus',
  'leave.column.period': 'Időszak',
  'leave.column.days': 'Munkanap',
  'leave.column.status': 'Állapot',
  'leave.column.decidedBy': 'Elbírálta',
  'leave.status.pending': 'Függőben',
  'leave.status.approved': 'Elfogadva',
  'leave.status.rejected': 'Elutasítva',
  'leave.status.revoked': 'Visszavonva',
  'leave.filter.year': 'Keretév',
  'leave.filter.status': 'Állapot',
  'leave.filter.allStatuses': 'Minden állapot',
  'leave.action.approve': 'Elfogadás',
  'leave.action.reject': 'Elutasítás',
  'leave.action.revoke': 'Kérelem visszavonása',
  'leave.balance.used': '{used} / {quota} nap',
  'leave.balance.unlimited': 'Korlátlan',
  /* 27 Oct 2026 · the balance card's hover: what each of its three figures is. */
  'leave.balance.tip.available': 'Még kivehető',
  'leave.balance.tip.pending': 'Jóváhagyásra vár',
  'leave.balance.tip.used': 'Már jóváhagyva',
  'leave.balance.tip.frame': 'Teljes keret',
  'leave.balance.tip.days': '{days} nap',
  'leave.balance.pending': 'függőben: {days} nap',
  'leave.balance.carried': 'hozott: {days} nap',
  'leave.balance.carriedUntil': 'hozott: {days} nap · eddig: {date}',
  'leave.empty.mine': 'Még nincs kérelmed',
  'leave.empty.mineBody': 'Szabadságot itt igényelsz, a dátumok megadásával.',
  'leave.empty.queue': 'Nincs elbírálandó kérelem',
  'leave.empty.queueBody': 'Ha valaki szabadságot igényel, itt jelenik meg.',
  'leave.dialog.new': 'Új szabadságkérelem',
  'leave.dialog.newHint': 'Válaszd ki a napokat – a kvótád élőben követi a kijelölést.',
  'leave.dialog.submit': 'Kérelem beadása',
  'leave.dialog.pickDays': 'Válassz napokat',
  'leave.dialog.nothingPicked': 'nincs nap kijelölve',
  'leave.dialog.notePlaceholder': 'Mit érdemes tudni róla?',
  'leave.dialog.myQuota': 'Kvótám',
  'leave.dialog.myQuotaIn': 'Kvótám · {year}',
  'leave.dialog.noFrame': 'Ebből a típusból nincs több kivehető nap.',
  'leave.dialog.noQuotaRow': 'Ehhez a típushoz nincs kvóta ebben a keretévben.',
  'leave.dialog.frame': 'Keret',
  'leave.dialog.overdrawn': 'Több napot jelöltél ki, mint amennyi a kereteden van.',
  'leave.dialog.alreadyBooked': 'Erre a napra már van kérelmed.',
  'leave.dialog.remaining': 'Marad',
  'leave.field.type': 'Típus',
  'leave.field.from': 'Kezdet',
  'leave.field.to': 'Vége',
  'leave.field.attachment': 'Csatolmány',
  /* ⚠️ Enforced at APPROVAL rather than at submission — a rejection needs no certificate, and
     refusing to reject would trap the request in the queue for ever. P3-9. */
  'leave.dialog.attachmentRequiredNote': 'Ehhez a típushoz jóváhagyáskor kell igazolás.',
  'leave.field.note': 'Megjegyzés',
  'leave.decide.count': '{count} kérelem érintett',
  'leave.decided': '{count} kérelem elbírálva',
  'leave.decidedSome': '{count} elbírálva, {refused} nem sikerült',
  'leave.error.cannot_decide_own':
    'A saját kérelmedet nem bírálhatod el – ezt egy másik kollégának kell megtennie.',
  'leave.error.quota_exceeded': 'Ennyi nap nincs a kereteden – nézd meg a jobb oldali egyenleget.',
  'leave.error.frame_closed':
    'Ez a keretév már nem fedi a választott napokat – a továbbvihető rész lejárt.',
  'leave.error.overlaps': 'Erre az időszakra már van kérelmed.',
  'leave.error.not_revocable':
    'Ez a kérelem már el van utasítva vagy vissza van vonva, így nem vonható vissza.',
  /* 4 Nov 2026 · approved leave that has begun is taken, not withdrawn. */
  'leave.error.already_started':
    'Ez a szabadság már elkezdődött, ezért nem vonható vissza. Javításhoz fordulj a vezetődhöz vagy a HR-hez.',
  'leave.error.backdate_not_allowed': 'Ez a típus nem igényelhető visszamenőleg.',
  'leave.error.leave_not_managed': 'A te szabadságodat nem ez a rendszer kezeli.',
  'leave.error.type_inactive': 'Ez a típus nem aktív.',
  'leave.detail.back': 'Vissza a kérelmekhez',
  'leave.detail.thread': 'Beszélgetés',
  'leave.detail.noComments': 'Még nincs hozzászólás.',
  'leave.detail.send': 'Küldés',
  'leave.detail.edited': 'szerkesztve',
  'leave.detail.attachments': 'Csatolmányok',
  'file.drop': 'Húzd ide a fájlt, vagy kattints a tallózáshoz',
  'leave.detail.attachmentRequiredNote': 'Ehhez a típushoz csatolmány kell az elfogadáshoz.',
  'leave.detail.attachmentRequired': 'kötelező',
  'leave.detail.noAttachments': 'Nincs csatolmány.',
  'leave.detail.decision': 'Döntés',
  'leave.detail.decidedByName': 'Elbíráló: {name}',
  'leave.detail.details': 'Részletek',
  'leave.detail.decidedAt': 'Elbírálva',
  'leave.detail.quota': 'Kvóta',
  'leave.detail.used': 'Felhasználva',
  'leave.detail.noWorkingDays': 'Ebben az időszakban nincs munkanapod.',
  'leave.detail.noTask': 'Nincs feladat',
  'leave.detail.decisionNote': 'Indoklás (nem kötelező)',
  'leave.settle.label': 'Jóváhagyás módja',
  'leave.settle.payout': 'Kifizetés',
  'leave.settle.credit': 'Jóváírás kvótára',
  'leave.settle.creditTo': 'Melyik kvótára',
  'leave.settle.rate': 'Túlóra típusa',
  'leave.settle.rateFromDay': 'A nap szerint',
  'leave.settle.payoutHint': 'Az órák a szorzóval a projekt költségébe kerülnek.',
  'leave.settle.credited': '{days} nap jóváírva · {type}',
  'leave.balance.accrued': 'túlórából',
  'leave.detail.awaiting': 'Elbírálásra vár.',
  'leave.detail.decidedBy': '{status} · {name}',
  'leave.detail.noNote': 'Nincs megjegyzés.',
  'leave.detail.open': 'Kérelem megnyitása',
  'leave.error.attachment_required': 'Ehhez a típushoz csatolmány kell az elfogadáshoz.',
  'title.request': 'Kérelem',
  'title.settingsLeave': 'Szabadságkezelés',
  'settings.leave.tab.types': 'Szabadság típusok',
  'settings.leave.tab.quotas': 'Szabadság kvóták',
  'settings.leave.addType': 'Új típus',
  'settings.leave.column.name': 'Megnevezés',
  'settings.leave.column.code': 'Kód',
  'settings.leave.column.quota': 'Kvóta',
  'settings.leave.column.carry': 'Átvitel',
  'settings.leave.column.rows': 'Kvóták',
  'settings.leave.column.person': 'Dolgozó',
  'settings.leave.column.type': 'Típus',
  'settings.leave.allTypes': 'Minden típus',
  'settings.leave.column.quotas': 'Kvóták',
  'settings.leave.column.frame': 'Keret',
  'settings.leave.column.used': 'Felhasznált',
  'settings.leave.column.left': 'Maradék',
  'settings.leave.searchTypes': 'Típus keresése',
  'settings.leave.emptyTypesBody': 'Vegyél fel egyet, hogy a dolgozók kérelmet adhassanak be.',
  'settings.leave.emptyQuotas': 'Nincs kvóta',
  'settings.leave.emptyQuotasBody': 'Keret nélkül a kérelem beadható, de a maradék nem látszik.',
  'settings.leave.qsrc.quota': 'Szabadság kvóta',
  'settings.leave.qsrc.unlimited': 'Korlátlan (nincs keret)',
  'settings.leave.qsrc.overtime': 'Túlóra kvóta',
  'settings.leave.qsrc.none': 'Nem igényel kvótát',
  'settings.leave.carry.none': 'Nincs',
  'settings.leave.carry.full': 'Teljes maradék átvihető',
  'settings.leave.carry.max5': 'Maximum 5 nap',
  'settings.leave.carryMonth': 'Meddig használható fel',
  'settings.leave.carryTo': '{month}. hónapig',
  'settings.leave.note': 'Megjegyzés',
  'settings.leave.attachment': 'Csatolmány',
  'settings.leave.rule.optional': 'Opcionális',
  'settings.leave.rule.required': 'Kötelező',
  'settings.leave.rule.hidden': 'Nem kérhető',
  'settings.leave.backdate': 'Visszamenőleg is rögzíthető',
  'settings.leave.codeNote': 'Nagybetűk és számok, például ANNUAL.',
  'settings.leave.cell': '{name} · {type}',
  'settings.leave.carried': '+{days} hozott',
  'settings.leave.used': '{days} felhasználva',
  'settings.leave.noQuota': 'Nincs beállítva kvóta',
  'settings.leave.pending': '{days} függő',
  'settings.leave.deleteType': '{name} törlése',
  'settings.leave.editType': '{name} szerkesztése',
  'settings.leave.emptyTypes': 'Még nincs szabadság típus. Vegyél fel egyet.',
  'settings.leave.noQuotaTypes': 'Egyik típus sem kvótás, így nincs mit kiosztani.',
  'settings.leave.everybodyManaged': 'Mindenki szabadságát ez a rendszer kezeli.',
  'settings.leave.excluded': 'Nem ez a rendszer kezeli: {names}',
  'settings.leave.dialog.new': 'Új szabadság típus',
  'settings.leave.dialog.edit': 'Szabadság típus szerkesztése',
  'settings.leave.error.taken': 'Ez a kód már foglalt.',
  'settings.leave.error.notManaged': 'Ennek a dolgozónak a szabadságát nem ez a rendszer kezeli.',
  'settings.leave.typesNote': '{total} típus · {active} aktív · {quota} kvótás',
  'settings.leave.typesHint':
    'A sorrend a kérelem űrlapján is ez lesz. Használatban lévő típust nem lehet törölni – inaktiváld, így új kérelemhez már nem választható.',
  'settings.leave.searchPeople': 'Keresés dolgozóra…',
  'settings.leave.column.total': 'Összesen',
  'settings.leave.noPeopleMatch': 'Nincs találat erre a névre.',
  'settings.leave.symbol': 'Szimbólum',
  'settings.leave.namePlaceholder': 'Pl. Éves szabadság',
  'settings.leave.rulesForward':
    'A szabályok csak az ezután beadott kérelmekre érvényesek – a meglévő kérelmeket nem írják át.',
  'people.field.leaveManaged': 'Szabadságkezelés',
  'people.field.leaveManaged.yes': 'A rendszerben kezelt',
  'people.field.leaveManaged.no': 'Nem a rendszerben kezelt',
  'people.field.rate': 'Díj',
  'people.field.noRate': 'Nincs díjkártya',
  'people.rate.change': 'Módosítás',
  'people.rate.costHour': 'Óradíj · költségdíj',
  'people.rate.costDay': 'Napidíj · költségdíj',
  'people.rate.target': 'Céldíj',
  'people.rate.validity': 'Érvényesség',
  'people.rate.from': '{from} óta',
  'people.rate.pending': 'Ennek a díjkártyának még nem indult el egy időszaka sem.',
  'people.field.schedule': 'Munkarend',
  'people.field.noSchedule': 'Nincs munkarend',
  'people.schedule.change': 'Módosítás',
  'people.field.scheduleFrom': '{hours} óra / hét · kezdet: {from}',
  'title.settingsSchedules': 'Munkarendek',
  'settings.schedules.tab.templates': 'Sablonok',
  'settings.schedules.tab.assignments': 'Beosztások',
  'settings.schedules.addTemplate': 'Új sablon',
  'settings.schedules.addAssignment': 'Új beosztás',
  'settings.schedules.search': 'Keresés névre…',
  'settings.schedules.type': 'Típus',
  'settings.schedules.type.all': 'Minden típus',
  'settings.schedules.type.fix': 'Fix',
  'settings.schedules.type.flex': 'Rugalmas',
  'settings.schedules.status': 'Státusz',
  'settings.schedules.status.all': 'Minden státusz',
  'settings.schedules.status.active': 'Aktív',
  'settings.schedules.status.inactive': 'Inaktív',
  'settings.schedules.column.name': 'Megnevezés',
  'settings.schedules.column.type': 'Típus',
  'settings.schedules.column.weekly': 'Heti',
  'settings.schedules.column.assigned': 'Kire',
  'settings.schedules.column.person': 'Dolgozó',
  'settings.schedules.column.from': 'Érvényes',
  'settings.schedules.column.schedule': 'Munkarend',
  'settings.schedules.weekly': 'Heti keret (óra)',
  'settings.schedules.days': 'Napi órák',
  'settings.schedules.frame': '{hours} óra keret',
  'settings.schedules.people': '{count} fő',
  'settings.schedules.custom': 'Egyedi',
  'settings.schedules.flexNote': 'A rugalmas munkarendnek nincs napi terve – csak heti kerete.',
  'settings.schedules.deleteTemplate': '{name} törlése',
  'settings.schedules.editTemplate': '{name} szerkesztése',
  'settings.schedules.deleteAssignment': 'Törlés',
  'settings.schedules.editAssignment': 'Szerkesztés',
  'settings.schedules.emptyTemplates': 'Még nincs sablon. Vegyél fel egyet.',
  'settings.schedules.emptyAssignments': 'Még senki nincs beosztva.',
  'settings.schedules.dialog.newTemplate': 'Új sablon',
  'settings.schedules.dialog.editTemplate': 'Sablon szerkesztése',
  'settings.schedules.dialog.assign': 'Beosztás',
  'settings.schedules.error.inUse': 'A sablonra még van beosztás. Kapcsold inaktívra helyette.',
  'settings.schedules.newTemplate': 'Sablon létrehozása',
  'settings.schedules.assign': 'Munkarend hozzárendelése',
  'settings.schedules.searchTemplates': 'Keresés sablon nevére…',
  'settings.schedules.searchPeople': 'Keresés névre…',
  'settings.schedules.column.source': 'Forrás',
  'settings.schedules.column.status': 'Állapot',
  'settings.schedules.emptyTemplatesBody':
    'Hozz létre egyet, és rendeld hozzá a dolgozókat — kapacitás nélkül a terhelés 0 órával számol.',
  'settings.schedules.note': '{templates} sablon · {assignments} beosztás',
  'settings.schedules.assignedNote': '{count} dolgozónak van munkarendje · {missing} hiányzik',
  'settings.schedules.hint':
    'A fix munkarend napi óraszámot ad – ehhez mérjük a rögzített időt a kezdőlapon és a profilon. A rugalmasnál nincs napi elvárás, csak heti keret.',
  'settings.schedules.duplicate': 'Duplikálás',
  'settings.schedules.copyOf': '{name} másolat',
  'settings.schedules.assignToPerson': 'Hozzárendelés dolgozóhoz',
  'settings.schedules.activate': 'Aktiválás',
  'settings.schedules.deactivate': 'Inaktiválás',
  'settings.schedules.openProfile': 'Profil megnyitása',
  'settings.schedules.fromTemplate': 'Sablon',
  'settings.schedules.fromTemplateHint': 'Válassz meglévő munkarend sablonból',
  'settings.schedules.customHint': 'Egyedi munkarend erre a felhasználóra',
  'settings.schedules.fixHint': 'Pontos óraszám minden napra',
  'settings.schedules.flexHint': 'Nincs napi elvárás, csak heti keret',
  'settings.schedules.templateName': 'Sablon neve',
  'settings.schedules.namePlaceholder': 'Pl. Full-time (40h)',
  'settings.schedules.pickPerson': 'Válassz felhasználót',
  'settings.schedules.weekTotal': '{hours} óra / hét',
  'settings.schedules.dialog.editAssignment': 'Munkarend módosítása',
  'title.settingsHolidays': 'Ünnepnapok',
  'settings.general.country': 'Ország',
  'settings.holidays.year': 'Év',
  'settings.holidays.search': 'Keresés névre…',
  'settings.holidays.kind': 'Típus',
  'settings.holidays.kind.all': 'Minden típus',
  'settings.holidays.kind.public': 'Ünnepnap',
  'settings.holidays.kind.rest': 'Munkaszüneti nap',
  'settings.holidays.kind.workday': 'Áthelyezett munkanap',
  'settings.holidays.import': 'Ünnepnapok szinkronizálása',
  'settings.holidays.importing': 'Szinkronizálás…',
  'settings.holidays.imported': '{imported} hivatalos nap szinkronizálva · {country} {year}',
  'settings.holidays.add': 'Nap hozzáadása',
  'settings.holidays.column.date': 'Dátum',
  'settings.holidays.column.name': 'Megnevezés',
  'settings.holidays.column.kind': 'Típus',
  'settings.holidays.column.source': 'Forrás',
  'settings.holidays.source.nager': 'Szinkronizált',
  'settings.holidays.source.custom': 'Saját',
  'settings.holidays.delete': '{name} törlése',
  'settings.holidays.empty':
    'Erre az évre még nincs nap. Szinkronizáld az ünnepnapokat, vagy vegyél fel sajátot.',
  'settings.holidays.emptyBody':
    'Szinkronizáld a hivatalos naptárt, vagy vedd fel a napokat kézzel.',
  'settings.holidays.counts':
    '{public} ünnepnap · {rest} munkaszüneti · {workday} áthelyezett munkanap',
  'settings.holidays.sourceNote': 'Szinkronizált naptár · {country}',
  'settings.holidays.hint':
    'Az ünnep- és munkaszüneti napokon nulla a napi elvárás. Az áthelyezett munkanap fordítva: hétvégén is munkanapnak számít.',
  'settings.holidays.dialog.new': 'Nap hozzáadása',
  'settings.holidays.dialog.edit': 'Nap szerkesztése',
  'settings.holidays.error.taken': 'Erre a napra már van bejegyzés.',
  'settings.holidays.column.day': 'Nap',
  'settings.holidays.rowMenu': '{name} műveletei',
  'settings.holidays.namePlaceholder': 'Pl. Céges szabadnap',
  'settings.holidays.hint.public': 'Hivatalos ünnep – nem munkanap.',
  'settings.holidays.hint.rest': 'Céges szabadnap – nem munkanap.',
  'settings.holidays.hint.workday': 'Ledolgozós szombat – munkanap a munkarend szerint.',
  'settings.nav.integrations': 'Integrációk',
  'settings.nav.data': 'Adatok',

  'settings.denied.title': 'Nincs jogosultságod',
  'settings.denied.body':
    'A workspace beállításait csak a megfelelő szinttel rendelkezők szerkeszthetik. Ha kell valami, szólj egy adminnak.',

  'settings.unsaved': 'Mentetlen módosítás',
  'settings.saved': 'Minden mentve',
  'settings.default': 'alapértelmezett',
  'settings.reorderHint': 'Húzd a sorrendezéshez',
  'settings.makeDefault': 'Legyen alapértelmezett',
  'settings.taskCount': '{count} feladat',
  'settings.rowMenu': '{name} műveletei',
  /* ── C54 · Skill menedzsment ──────────────────────────────────────────────────────────── */
  'settings.nav.skills': 'Skill menedzsment',
  'title.settingsSkills': 'Skill menedzsment',
  'settings.skills.title': 'Skillek',
  'settings.skills.categories': 'Kategóriák',
  'settings.skills.levels': 'Szintek',
  'settings.skills.newSkill': 'Új skill',
  'settings.skills.newCategory': 'Új kategória',
  'settings.skills.newLevel': 'Új szint',
  'settings.skills.editSkill': 'Skill szerkesztése',
  'settings.skills.editCategory': 'Kategória szerkesztése',
  'settings.skills.editLevel': 'Szint szerkesztése',
  'settings.skills.name': 'Név',
  'settings.skills.category': 'Kategória',
  'settings.skills.color': 'Szín',
  'settings.skills.description': 'Leírás',
  'settings.skills.skillHint': 'A skill egy kategóriába tartozik, és onnan kapja a színét.',
  'settings.skills.categoryHint':
    'A kategória fogja össze a rokon skilleket, és ő adja a színüket.',
  'settings.skills.levelHint': 'A leírás mondja meg, mit jelent ez a szint a gyakorlatban.',
  /* ⚠️ A row's use count, which is what makes a refused törlés legible before it is tried. */
  'settings.skills.heldBy': '{count} dolgozó',
  'settings.skills.heldByNobody': 'nincs használatban',
  'settings.skills.holdsSkills': '{count} skill',
  'settings.skills.empty': 'üres',
  'settings.skills.atLevel': '{count} hozzárendelés',
  /* ⚠️ A disabled törlés that says only "Törlés" is a dead end; these say what to do first. */
  'settings.skills.deleteHeld': 'Törlés: előbb vedd le a dolgozókról',
  'settings.skills.deleteFull': 'Törlés: előbb ürítsd ki',
  'settings.skills.deleteLast': 'Törlés: az utolsó nem törölhető',
  /* ⚠️⚠️ The one sentence that says the order means something. Nothing else on the screen would. */
  'settings.skills.search': 'Keresés skillre…',
  'settings.skills.allCategories': 'Minden kategória',
  'settings.skills.noMatch': 'Nincs találat. Írj be mást, vagy vegyél fel új skillt.',
  /* ⚠️ The settings table's empty state takes a title and a body — the three tabs' own. */
  'settings.skills.noMatchTitle': 'Nincs találat',
  'settings.skills.emptySkills': 'Még nincs skill ebben a munkaterületben.',
  'settings.skills.emptySkillsBody': 'Vegyél fel egyet, hogy a dolgozókhoz skilleket rendelhess.',
  'settings.skills.emptyCategories': 'Még nincs skillkategória.',
  'settings.skills.emptyCategoriesBody': 'Vegyél fel egyet, mielőtt skilleket rögzítesz.',
  'settings.skills.emptyLevels': 'Még nincs skillszint.',
  'settings.skills.emptyLevelsBody':
    'Vegyél fel egyet, hogy a dolgozók skilljeihez szintet rendelhess.',
  /* ⚠️⚠️ The mockup's three hints, two of them SHORTENED. Its category and level
     sentences both end on *"a dolgozói adatlapon"* — the person screen's skill block, whose
     accessor the mockup's own markup binds ZERO times. Pointing somebody at a surface that is
     not drawn is the account tab's mistake again, so both clauses come off. */
  'settings.skills.hintSkills':
    'Ebből a listából választanak a dolgozók a fiókjukban, és ezt ajánlod fel onboardingkor. Minden skill pontosan egy kategóriába tartozik.',
  'settings.skills.hintCategories':
    'A kategória színe adja a skill színét mindenhol. Húzd át a sorokat a sorrendhez.',
  'settings.skills.hintLevels':
    'A sorrend a rangsor: a legfelső a leggyengébb, a legalsó a legerősebb. Átrendezéskor mindenki skilljei újrarendeződnek.',
  'settings.skills.countSkills': '{count} skill',
  'settings.skills.countCategories': '{count} kategória',
  'settings.skills.countLevels': '{count} szint',
  /* ⚠️ The three refusals the skill screen can receive, each naming the action that clears it. */
  'settings.error.nameTaken': 'Ez a név már foglalt.',
  'settings.error.skillInUse': 'Még használatban van — előbb vedd le róla, ami rajta van.',
  'settings.moveUp': '{name} feljebb',
  'settings.moveDown': '{name} lejjebb',
  'settings.toasts': 'Beállítás-értesítések',
  'demo.banner.text':
    'Ez a munkaterület mintaadatokat tartalmaz, hogy kipróbálhasd a Forecastifyt. A saját adataid mellettük is biztonságban vannak.',
  'demo.banner.remove': 'Mintaadatok eltávolítása',
  'demo.remove.title': 'Mintaadatok eltávolítása',
  'demo.remove.lede':
    'A mintaügyfelek, -projektek, -feladatok, -órák és -kollégák törlődnek. Amit módosítottál, vagy amire a saját adataid hivatkoznak, megmarad.',
  'demo.remove.removable': 'Törlődő mintaelemek: {count}',
  'demo.remove.kept':
    'Megmaradó elemek: {count} — ezeket módosítottad, vagy a saját adataid hivatkoznak rájuk.',
  'demo.remove.final': 'Az eltávolítás végleges, nem vonható vissza.',
  'demo.remove.confirm': 'Eltávolítás',
  'demo.remove.working': 'Eltávolítás…',
  'demo.error.incomplete':
    'Néhány mintaadatot nem sikerült eltávolítani, ezért semmi sem törlődött. Próbáld újra később.',
  'demo.toast.removed': 'Mintaadatok eltávolítva',
  'demo.toast.kept': 'Megmaradt elemek: {count}',
  'demo.toasts': 'Mintaadat-értesítések',
  'firstRun.step': '{current}. lépés / {total}',
  'firstRun.next': 'Tovább',
  'firstRun.back': 'Vissza',
  'firstRun.what.title': 'Üdv a Forecastifyban!',
  'firstRun.what.body':
    'A Forecastify egy helyen tartja az ügyfeleidet, a projektjeidet, a feladatokat és a ledolgozott órákat, és ezekből mutatja meg, hol tartotok, és mire számíthattok.',
  'firstRun.flow.title': 'Így áll össze',
  'firstRun.flow.body':
    'Az ügyfelekhez projektek tartoznak, a projektekben feladatok vannak, a feladatokra órákat rögzítetek, a riportok és a dashboardok pedig mindezből számolnak.',
  'firstRun.flow.clients': 'Ügyfelek',
  'firstRun.flow.projects': 'Projektek',
  'firstRun.flow.tasks': 'Feladatok',
  'firstRun.flow.hours': 'Órák',
  'firstRun.flow.reports': 'Riportok',
  'firstRun.demo.title': 'Mintaadatokkal indulsz',
  'firstRun.demo.body':
    'Kitaláltunk néhány ügyfelet, projektet és kollégát, hogy legyen mit kipróbálni. Ha már nincs rájuk szükség, a felső sávból eltávolíthatók, és ami közben a tiéd lett, megmarad.',
  'firstRun.tour.title': 'Körbevezessünk?',
  'firstRun.tour.body':
    'Pár lépésben megmutatjuk, mi hol található. Bármikor kiléphetsz, és később a profilmenüből újra elindíthatod.',
  'firstRun.tour.start': 'Mutasd meg',
  'firstRun.tour.skip': 'Most nem',
  'firstRun.start.title': 'Kezdhetjük?',
  'firstRun.start.body': 'Minden a bal oldali menüből érhető el.',
  'firstRun.start.go': 'Kezdjük',
  'nav.takeTour': 'Körbevezetés',
  'tour.next': 'Tovább',
  'tour.previous': 'Vissza',
  'tour.finish': 'Kész',
  'tour.close': 'Körbevezetés bezárása',
  'tour.step': '{current} / {total}',
  'tour.skipSection': 'Fejezet átugrása',
  'tour.busy': 'A lépés előkészítése…',
  'tour.chapter.demo': 'Mintaadatok',
  'tour.chapter.settings': 'Beállítások',
  'tour.chapter.home': 'Kezdőlap',
  'tour.chapter.tasks': 'Feladatok',
  'tour.chapter.task': 'Egy feladat',
  'tour.chapter.projects': 'Projektek',
  'tour.chapter.time': 'Időrögzítés',
  'tour.chapter.requests': 'Kérelmek',
  'tour.chapter.contracts': 'Szerződések',
  'tour.chapter.people': 'Munkatársak',
  'tour.chapter.reports': 'Riportok',
  'tour.chapter.dashboards': 'Dashboardok',
  'tour.chapter.agent': 'Agent',
  'tour.chapter.end': 'Vége',
  'tour.demo.title': 'Ez mintaadat',
  'tour.demo.body':
    'Minden, amit látsz — ügyfelek, projektek, feladatok és négy kitalált kolléga — mintaadat, hogy legyen mit kipróbálni. Ha végeztél, ebből a sávból eltávolíthatod; amit közben módosítottál vagy hozzáadtál, megmarad.',
  'tour.settingsMenu.title': 'Hol vannak a beállítások',
  'tour.settingsMenu.body':
    'A beállítások a munkaterület neve mögött találhatók. Minden, ami itt van, az egész munkaterületre vonatkozik.',
  'tour.settingsIdentity.title': 'Név és kép',
  'tour.settingsIdentity.body':
    'A név és a kép, amelyet a csapat a munkaterület-váltóban és az oldalsávban lát.',
  'tour.settingsDefaults.title': 'Új tagok alapértékei',
  'tour.settingsDefaults.body':
    'Az új kollégák ezzel a nyelvvel és időzónával indulnak. Utána mindenki átállíthatja a sajátját.',
  'tour.settingsCountry.title': 'Ország',
  'tour.settingsCountry.body':
    'Ez dönti el, melyik ország munkaszüneti napjait hozza be az Ünnepnapok oldal.',
  'tour.settingsPermissions.title': 'Jogosultsági szintek',
  'tour.settingsPermissions.body':
    'A jogosultsági szint jogok csomagja. Mindenkinek van legalább egy; ha több is, mindig a legbővebb érvényes.',
  'tour.settingsLevel.title': 'Nincs, Saját vagy Teljes',
  'tour.settingsLevel.body':
    'Minden műveletre — feladatok megtekintése, időrögzítés, projektek kezelése — a szint Nincs, Saját (csak a sajátjai) vagy Teljes (a munkaterület összes sora) jogot ad. A beépített szintek csak olvashatók; sajátot az Új szint gombbal hozhatsz létre.',
  'tour.settingsStatuses.title': 'Feladatstátuszok',
  'tour.settingsStatuses.body':
    'A feladattábla oszlopai, sorrendben. Mindegyik egy kategóriába tartozik — Nyitott, Folyamatban, Ellenőrzés alatt vagy Lezárt —, és ez dönti el, mikor számít késznek egy feladat.',
  'tour.settingsPriorities.title': 'Prioritások',
  'tour.settingsPriorities.body':
    'A prioritásskála, és hogy egy új feladat alapértelmezésként melyiket kapja.',
  'tour.settingsProjectRoles.title': 'Projektszerepek',
  'tour.settingsProjectRoles.body':
    'A szerepek, amelyeket valaki egy projekten betölt — Tech Lead, Frontend, QA. Azt mondják meg, ki mit csinál, nem azt, hogy ki mit tehet meg.',
  'tour.settingsMetafields.title': 'Saját mezők',
  'tour.settingsMetafields.body':
    'Adj mezőket a feladatokhoz, projektekhez, munkatársakhoz, ügyfelekhez, szerződésekhez, számlákhoz — szöveget, számot, dátumot vagy választót. A „Listákban látszik” beállítással oszlop lesz belőle.',
  'tour.settingsSkills.title': 'Készségek',
  'tour.settingsSkills.body':
    'Készségszótár — készségek, kategóriák és szintek —, amellyel a munkatársak profilja címkézhető.',
  'tour.settingsSchedules.title': 'Munkarendek',
  'tour.settingsSchedules.body':
    'Hány órát dolgozik valaki naponta. A kötött munkarend napra pontosan, a rugalmas csak heti keretet ad meg. Ebből számol az időtábla figyelmeztetése és a kapacitás.',
  'tour.settingsAssignments.title': 'Ki melyik munkarendben',
  'tour.settingsAssignments.body': 'Ki melyik munkarendben dolgozik, és mely naptól.',
  'tour.settingsRates.title': 'Óradíjkártyák',
  'tour.settingsRates.body':
    'Mennyibe kerül egy óra, és mennyit számlázol érte, személyenként, dátumozott időszakokban — így egy emelés nem írja át a múltat.',
  'tour.settingsCurrency.title': 'Pénznemek',
  'tour.settingsCurrency.body':
    'A pénznemek, amelyekben a pénzügyek vezetve vannak. Egy az alapértelmezett, a többinek árfolyama van hozzá.',
  'tour.settingsLeaveTypes.title': 'Távolléttípusok',
  'tour.settingsLeaveTypes.body':
    'A kérhető távollétfajták: van-e éves kerete, kell-e megjegyzés vagy igazolás, és mi visz át a következő évre.',
  'tour.settingsQuotas.title': 'Keretek',
  'tour.settingsQuotas.body':
    'Mindenki éves kerete típusonként, a felhasznált és a maradék napokkal. A keretet közvetlenül a cellába írhatod.',
  'tour.settingsOvertime.title': 'Túlóratípusok',
  'tour.settingsOvertime.body':
    'A túlóra fajtái és hogy egy órájuk mennyit ér. Az alapértelmezett típus előre ki van választva, amikor valaki túlórát jelöl.',
  'tour.settingsHolidays.title': 'Ünnepnapok',
  'tour.settingsHolidays.body':
    'Behozza az országod hivatalos munkaszüneti napjait. A céges szabadnapokat és az áthelyezett munkanapokat kézzel adhatod hozzá.',
  'tour.settingsWorklog.title': 'Kerekítés és napi maximum',
  'tour.settingsWorklog.body':
    'Hogyan kerekít a rendszer a rögzített időn, és legfeljebb hány órát rögzíthet valaki egy napra.',
  'tour.settingsWorklogLock.title': 'Visszamenőleges rögzítés és zárás',
  'tour.settingsWorklogLock.body':
    'Hány napra visszamenőleg lehet rögzíteni, és a hónap melyik napja után zárul le az előző hónap.',
  'tour.settingsIntegrations.title': 'Integrációk',
  'tour.settingsIntegrations.body':
    'Kösd be a Jirát vagy a YouTracket, hogy a feladatok és a rögzített idő mindkét irányba átjöjjön.',
  'tour.settingsData.title': 'Adataid',
  'tour.settingsData.body':
    'Bármikor exportálhatod a projekteket, feladatokat, órákat vagy a munkatársakat. Itt a munkaterület törlése is kérhető, 30 napos türelmi idővel.',
  'tour.settingsBilling.title': 'Számlázás',
  'tour.settingsBilling.body':
    'Az előfizetésed, a használt helyek, és az adatok, amelyekre a számláid szólnak.',
  'tour.homeAgent.title': 'Kérdezz bármit',
  'tour.homeAgent.body':
    'Írd ide a kérdésed — az Agent a munkaterület adataiból válaszol, és csatolhatsz hozzá táblázatot vagy képernyőképet.',
  'tour.homeLog.title': 'Az órád',
  'tour.homeLog.body':
    'A mai nap és a hét a terved szerint: mennyi van még hátra, és hány nap maradt hiányos.',
  'tour.homeLeave.title': 'A szabadságod',
  'tour.homeLeave.body': 'A döntésre váró kérelmeid és a közelgő távolléteid.',
  'tour.homeAllocations.title': 'Amire be vagy tervezve',
  'tour.homeAllocations.body':
    'Mire vagy betervezve ezen a héten, naponként és sortételenként. Egy sor fölé állva látod a részleteket.',
  'tour.homeOvertime.title': 'A túlórád',
  'tour.homeOvertime.body':
    'A még elbírálásra váró túlóráid, és amit az elmúlt hónapban elfogadtak.',
  'tour.tasksSearch.title': 'Keresés és szűrés',
  'tour.tasksSearch.body':
    'Keress kulcs vagy cím szerint, és adj hozzá szűrőket — státusz, projekt, felelős, határidő, becslés vagy saját mező. Minden aktív szűrő címkeként látszik.',
  'tour.tasksViews.title': 'Négy nézet',
  'tour.tasksViews.body':
    'Ugyanazok a feladatok listában, kanban táblán, idővonalon vagy szerkeszthető rácsban.',
  'tour.tasksBoard.title': 'Kanban',
  'tour.tasksBoard.body': 'Státuszonként egy oszlop. Egy kártyát áthúzva megváltozik a státusza.',
  'tour.tasksTimeline.title': 'Idővonal',
  'tour.tasksTimeline.body': 'A dátumozott feladatok sávokként. Egy sávot áthúzva átütemezheted.',
  'tour.tasksNew.title': 'Új feladat',
  'tour.tasksNew.body': 'Feladat létrehozása: projekt és sortétel, felelős, becslés és dátumok.',
  'tour.taskOpen.title': 'Egy megnyitott feladat',
  'tour.taskOpen.body':
    'A feladat a lista fölött nyílik meg. Kinagyíthatod teljes képernyőre, vagy összecsukhatod oldalsó panelre.',
  'tour.taskTitle.title': 'Cím és leírás',
  'tour.taskTitle.body':
    'Kattints bármelyikre a szerkesztéshez. A leírásra húzott fájl csatolódik, és hivatkozás is lesz rá a szövegben.',
  'tour.taskProperties.title': 'Státusz, prioritás, felelős',
  'tour.taskProperties.body': 'Válassz értéket, és azonnal mentődik.',
  'tour.taskLineItem.title': 'Sortétel',
  'tour.taskLineItem.body':
    'Melyik szerződés melyik sortételére megy ez a munka — ettől lesznek az órái számlázhatók vagy sem.',
  'tour.taskEstimate.title': 'Becslés, dátumok, címkék',
  'tour.taskEstimate.body':
    'A becslést írhatod úgy, hogy 8, 8h vagy 8:00, és itt adod meg a kezdést és a határidőt.',
  'tour.taskSubtasks.title': 'Részfeladatok',
  'tour.taskSubtasks.body':
    'Bontsd le a munkát. A részfeladat önálló feladat ugyanabban a projektben.',
  'tour.taskDependencies.title': 'Függőségek és kapcsolatok',
  'tour.taskDependencies.body':
    'Mire vár ez a feladat, mit akaszt meg — napos csúszással —, és mivel függ össze.',
  'tour.taskAttachments.title': 'Csatolmányok',
  'tour.taskAttachments.body': 'A feladat fájljai. Bárhova a feladatra húzva hozzáadhatsz egyet.',
  'tour.taskComments.title': 'Hozzászólások',
  'tour.taskComments.body':
    'Beszéljétek meg a feladatot. @ jellel megemlíthetsz egy kollégát, és értesítést kap.',
  'tour.taskLogs.title': 'Idő a feladaton',
  'tour.taskLogs.body':
    'Minden, amit erre a feladatra rögzítettek, és egy űrlap, amellyel közvetlenül rá rögzíthetsz.',
  'tour.projectsList.title': 'Minden projekt',
  'tour.projectsList.body':
    'Az összes projekt ügyféllel, vezetővel, előrehaladással és Trust Score-ral. A mentett nézetek szűkítenek az aktívakra, a tieidre vagy a késésben lévőkre.',
  'tour.projectOpen.title': 'Egy projekt',
  'tour.projectOpen.body':
    'Egy projekt minden része, fülekre bontva. A fejlécben a státusza, és ha te vezeted, a szerkesztés menüje.',
  'tour.projectOverview.title': 'Áttekintés',
  'tour.projectOverview.body':
    'Megszolgált érték órában: mennyi fogyott a keretből, és ebben a tempóban mikor készül el a munka.',
  'tour.projectTasks.title': 'Feladatok',
  'tour.projectTasks.body':
    'A projekt feladatai, a Feladatok oldal összes nézetével és szűrőjével.',
  'tour.projectTime.title': 'Idő',
  'tour.projectTime.body': 'Minden óra, amit a projektre rögzítettek: ki és mire.',
  'tour.projectContracts.title': 'Szerződések',
  'tour.projectContracts.body':
    'Amiben az ügyféllel megállapodtatok. A szerződés sortételekből áll — fix áras, óradíjas vagy nem számlázható —, és minden feladat egy sortételhez tartozik.',
  'tour.projectMoney.title': 'Pénzügyek',
  'tour.projectMoney.body':
    'Felhasznált keret, megszolgált és kiszámlázott bevétel, szerződésenként, időszakonként vagy sortételenként.',
  'tour.projectCosts.title': 'Külső költségek',
  'tour.projectCosts.body': 'Amit a projekt nem munkaórára költ — licencek, alvállalkozók, utazás.',
  'tour.projectInvoices.title': 'Számlák',
  'tour.projectInvoices.body': 'Amit a projekt sortételeire és időszakaira kiszámláztatok.',
  'tour.projectResourcing.title': 'Erőforrások',
  'tour.projectResourcing.body':
    'Ki van betervezve a projektre hétről hétre, a kapacitásához mérve.',
  'tour.projectPeople.title': 'Csapat',
  'tour.projectPeople.body':
    'A projekt csapata a projektszerepekkel. Csak a tagok rögzíthetnek időt a feladataira.',
  'tour.projectAlerts.title': 'Riasztások',
  'tour.projectAlerts.body':
    'Adathibák, amelyek miatt a fenti számok nem megbízhatók — becslés nélküli feladatok, lezárt sortételre rögzített idő.',
  'tour.projectEdit.title': 'A projekt szerkesztése',
  'tour.projectEdit.body': 'Itt szerkesztheted a projekt adatait, vezetőjét és dátumait.',
  'tour.timeStrip.title': 'Válassz napot',
  'tour.timeStrip.body': 'A napok alatti sáv mutatja, hogy a terv alatt, szerint vagy fölött vagy.',
  'tour.timeDay.title': 'A nap',
  'tour.timeDay.body':
    'Amit a kiválasztott napra rögzítettél. Az órákat helyben javíthatod, utána Visszavonás is felajánlódik.',
  'tour.timeTask.title': 'Időrögzítés: a feladat',
  'tour.timeTask.body': 'Először keresd meg a feladatot — kulcs vagy cím szerint.',
  'tour.timeDuration.title': 'Időrögzítés: mennyi ideig',
  'tour.timeDuration.body': 'Az időtartamot írhatod bárhogy: 8, 8h, 8:00, 8,0 vagy 90m.',
  'tour.timeOvertime.title': 'Időrögzítés: túlóra',
  'tour.timeOvertime.body': 'Ha túlórának jelölöd, kérelem indul jóváhagyásra.',
  'tour.timeLog.title': 'Időrögzítés: megjegyzés, majd Rögzítés',
  'tour.timeLog.body': 'Írd le, min dolgoztál, majd nyomd meg a Rögzítést.',
  'tour.timeRecents.title': 'Legutóbbiak',
  'tour.timeRecents.body':
    'Egy korábbi sorra kattintva újra kitölti az űrlapot — így a leggyorsabb a tegnapi munkát ismét rögzíteni.',
  'tour.timeWeek.title': 'Ez a hét',
  'tour.timeWeek.body': 'Az eddigi heted, projektenként.',
  'tour.logsList.title': 'Időbejegyzések: lista',
  'tour.logsList.body':
    'Minden bejegyzés, kereséssel és szűrőkkel. Egy sorból megnyithatod a feladatot, mára másolhatod a bejegyzést, vagy túlórának jelölheted.',
  'tour.logsTimesheet.title': 'Időtábla',
  'tour.logsTimesheet.body':
    'A hét feladatok és napok rácsaként. Egy cellára kattintva látod, javítod vagy bővíted a bejegyzéseket; fölé állva átmásolhatod az órákat a hét többi napjára.',
  'tour.logsCalendar.title': 'Naptár',
  'tour.logsCalendar.body':
    'A heted naptárként. Egy óraoszlopon lefelé húzva rögzítheted azt az időszakot.',
  'tour.logsGrid.title': 'Rács',
  'tour.logsGrid.body': 'Dátumok, órák és megjegyzések szerkesztése táblázatként.',
  'tour.requestBalances.title': 'A kereteid',
  'tour.requestBalances.body':
    'Távolléttípusonként: mennyi áll rendelkezésre, mennyi függőben, és mekkora az éves keret.',
  'tour.requestNew.title': 'Szabadság kérése',
  'tour.requestNew.body': 'Itt indíthatsz szabadságkérelmet.',
  'tour.requestCalendar.title': 'Válaszd ki a napokat',
  'tour.requestCalendar.body':
    'Válaszd ki a típust, majd kattints az első és az utolsó napra. A hétvégék és ünnepnapok nem számítanak bele.',
  'tour.requestQuota.title': 'A kereted, élőben',
  'tour.requestQuota.body':
    'A kereted a napok kiválasztásával együtt frissül, így küldés előtt látod, mennyi marad.',
  'tour.requestNote.title': 'Megjegyzés és melléklet',
  'tour.requestNote.body': 'Írj megjegyzést, és csatolj igazolást, ha a típus kéri.',
  'tour.requestOvertime.title': 'Túlóra igénylése',
  'tour.requestOvertime.body':
    'Már rögzített órákra igényelhetsz túlórát: válaszd ki a bejegyzést, és írd meg, miért. A díjat a jóváhagyó határozza meg.',
  'tour.requestOpen.title': 'Egy megnyitott kérelem',
  'tour.requestOpen.body': 'Minden kérelemhez tartozik beszélgetés, mellékletek és állapot.',
  'tour.requestDecide.title': 'Elbírálandó',
  'tour.requestDecide.body':
    'A hozzád tartozó kollégák kérelmei. Egyenként jóváhagyhatod vagy elutasíthatod őket, vagy többet kijelölve egyszerre dönthetsz.',
  'tour.contractsList.title': 'Szerződések',
  'tour.contractsList.body':
    'Az összes szerződés projekttel és ügyféllel, a becslés és a rögzített órák aránya, és az állapot.',
  'tour.invoicesList.title': 'Számlák',
  'tour.invoicesList.body':
    'Az összes számla és az állapota — piszkozat, kiállított, kifizetett vagy érvénytelen. Számlát a szerződés egy sortételéről vagy időszakáról lehet kiállítani.',
  'tour.peopleList.title': 'A csapatod',
  'tour.peopleList.body':
    'Mindenki a jogosultsági szintjével és projektjeivel. Innen hívhatsz meg új kollégát.',
  'tour.personOpen.title': 'Egy munkatárs',
  'tour.personOpen.body':
    'A hónapja számokban, a munkarendje és óradíja, a készségei, feladatai és órái.',
  'tour.reportsList.title': 'Mentett riportok',
  'tour.reportsList.body':
    'A mentett riportjaid. A sajátjaidat csak te látod; bármelyik exportálható CSV-be.',
  'tour.reportSource.title': 'Riport készítése: mit számoljunk',
  'tour.reportSource.body':
    'Kezdd a forrással — időbejegyzések, feladatok, szerződések, sortételek vagy időszakok.',
  'tour.reportPeriod.title': 'Az időszak',
  'tour.reportPeriod.body':
    'Ez a hét, hónap, negyedév vagy év, vagy saját dátumok. A tipp megmondja, melyik dátumra szűr az időszak.',
  'tour.reportMetric.title': 'A mérték',
  'tour.reportMetric.body':
    'Mit adjunk össze — itt a rögzített órákat. Darabszám, becslés és túlóra is választható, és bevétel és keret, ha látod a pénzügyeket.',
  'tour.reportDimension.title': 'Bontás',
  'tour.reportDimension.body':
    'Mi szerint bontsuk a számot — itt projekt szerint. Munkatárs, ügyfél, státusz vagy saját mező is lehet.',
  'tour.reportDisplay.title': 'Megjelenítés',
  'tour.reportDisplay.body':
    '25 diagram közül választhatsz, és a riportkészítő jobbakat is javasol az adataidhoz. A diagram alatti táblázat ki-be kapcsolható.',
  'tour.reportFilters.title': 'Szűrők',
  'tour.reportFilters.body': 'Szűkítsd a riportot egy projektre, munkatársra vagy mezőértékre.',
  'tour.reportSave.title': 'Elnevezés és mentés',
  'tour.reportSave.body':
    'Itt nevezheted el a riportot, majd Mentés. A Frissítés a mentett riportot a mai adatokon futtatja újra.',
  'tour.dashLogging.title': 'Időrögzítés',
  'tour.dashLogging.body':
    'Mindenki rögzített órái a tervéhez mérve, hétről hétre. Szűrhetsz azokra, akiknél eltérés van.',
  'tour.dashResource.title': 'Erőforrások',
  'tour.dashResource.body':
    'Mindenki kapacitása a betervezett órákhoz mérve, az alul- és túltervezés egy pillantásra. A vezetők a rácsban szerkeszthetik a tervet.',
  'tour.dashLeave.title': 'Távollét',
  'tour.dashLeave.body': 'Ki mikor nincs bent, idővonalon heti, havi vagy negyedéves bontásban.',
  'tour.agentComposer.title': 'Kérdezz egyszerűen',
  'tour.agentComposer.body':
    'Kérdezz bármit a munkátokról — „hány órát rögzítettünk a webshopra múlt héten?”. Minden szám valódi lekérdezésből jön.',
  'tour.agentAttach.title': 'Fájl csatolása',
  'tour.agentAttach.body': 'Húzz be egy táblázatot vagy képet a beszélgetésbe, és kérdezz róla.',
  'tour.agentProposals.title': 'Minden változást te hagysz jóvá',
  'tour.agentProposals.body':
    'Az Agent magától semmit nem módosít. Ha időt rögzíttetsz vagy feladatot hozatsz létre vele, kártyát készít: Rögzítés, Javítás vagy Elvetés.',
  'tour.agentHistory.title': 'Korábbi beszélgetések',
  'tour.agentHistory.body': 'Folytass egy korábbi beszélgetést, vagy kezdj újat.',
  'tour.end.title': 'Ennyi volt a körbevezetés',
  'tour.end.body':
    'Bármikor újraindíthatod a profilmenüből — a mintaadatokat pedig a felső sávból távolíthatod el, ha már nincs rájuk szükség.',
  'settings.toast.saved': 'Beállítások mentve',

  /* ── Általános ──────────────────────────────────────────────────────────────────────────── */

  'settings.general.identity': 'Workspace',
  /* ⚠️⚠️ Kimondjuk, miért nincs ikonfeltöltés: objektumtároló kell hozzá (bucket, policy, aláírt
   * URL, MIME- és méretellenőrzés), az pedig alrendszer, nem űrlapmező. Egy letiltott fájlválasztó
   * rosszabb lenne, mint semmi. */
  'settings.general.logoDeferred':
    'Az ikonfeltöltés még nincs kész – objektumtároló kell hozzá. Addig a névből képzett kezdőbetű látszik.',
  'settings.general.name': 'Név',
  'settings.general.slug': 'Webcím',
  'settings.general.defaults': 'Alapértelmezések új tagoknak',
  'settings.general.language': 'Felület nyelve',
  'settings.general.timezone': 'Időzóna',
  'settings.general.notifications': 'Értesítések',
  'settings.general.notificationEmails': 'E-mail-értesítések küldése',
  'settings.general.notificationEmailsLede':
    'Kikapcsolva a workspace senkinek sem küld értesítő e-mailt. Az alkalmazáson belüli értesítések, a meghívók és a fiókbiztonsági levelek továbbra is mennek.',

  /* ⚠️ R-38 · a slug most már a CÍM is: minden képernyő útvonala ezzel kezdődik. A régi szöveg
     az exportokat és a meghívókat említette, ami igaz, de már nem a legfontosabb következmény —
     az azonosító átírása minden korábbi linket elavulttá tesz. */
  'settings.slug.idle': 'Ez a workspace címe: minden oldal útvonala ezzel kezdődik.',
  'settings.slug.checking': 'Ellenőrzés…',
  'settings.slug.taken': 'Ez a cím már foglalt. Válassz másikat.',
  /* ⚠️ 0098 · a „foglalt” hamis állítás egy fenntartott szóról: senki sem foglalta el, és nem is
     lehet. Külön kulcs, hogy ne induljon el senki azt keresni, ki vitte el. */
  'settings.slug.reserved': 'Ezt a címet az alkalmazás használja. Válassz másikat.',
  'settings.slug.invalid':
    'Csak kisbetű, szám és kötőjel, legalább három karakter, és nem kezdődhet vagy végződhet kötőjelre.',
  'settings.slug.free': 'Szabad: {slug}',

  /* ── Taskok ─────────────────────────────────────────────────────────────────────────────── */

  'settings.statuses.title': 'Feladatstátuszok',
  'settings.statuses.new': 'Új státusz',
  'settings.priorities.title': 'Prioritások',
  'settings.priorities.new': 'Új prioritás',
  'settings.statuses.column.name': 'Megnevezés',
  'settings.statuses.column.category': 'Kategória',
  'settings.statuses.allCategories': 'Minden kategória',
  'settings.statuses.search': 'Státusz keresése',
  'settings.statuses.empty': 'Nincs feladatstátusz',
  'settings.statuses.emptyBody':
    'Vegyél fel egyet — státusz nélkül a feladatok nem kerülnek a táblára.',
  'settings.priorities.search': 'Prioritás keresése',
  'settings.priorities.empty': 'Nincs prioritás',
  'settings.priorities.emptyBody': 'Vegyél fel egyet, hogy a feladatok rangsorolhatók legyenek.',

  'settings.field.name': 'Név',
  'settings.field.color': 'Szín',
  'settings.field.category': 'Kategória',

  'settings.delete.rowTitle': '{name} törlése',
  'settings.delete.rowBody':
    '{count} feladat van benne. Meg kell adni, hova kerüljenek – törölni nem törlünk feladatot.',
  'settings.delete.reassign': 'A feladatok ide kerülnek',

  /* ── Worklog szabályok ──────────────────────────────────────────────────────────────────── */

  'settings.worklog.taskOnly':
    'Worklogot csak feladathoz lehet rögzíteni. Ez nem kapcsolható ki – a riportok erre épülnek.',
  'settings.worklog.title': 'Rögzítés',
  'settings.worklog.unit': 'Kerekítés',
  'settings.worklog.unitNote15': 'Minden bejegyzés a legközelebbi 15 percre kerekül.',
  'settings.worklog.unitNote30': 'Minden bejegyzés a legközelebbi 30 percre kerekül.',
  'settings.worklog.unitNoteDecimal': 'A bevitt idő tizedes órában tárolódik, kerekítés nélkül.',
  'settings.worklog.maxPerDay': 'Naponta rögzíthető maximum',
  'settings.worklog.hoursPerDay': 'óra / nap / fő',
  'settings.worklog.maxNote': 'Ennél többet egy napra senki nem tud rögzíteni.',
  'settings.worklog.maxInvalid': '0 és 24 óra között kell lennie.',
  'settings.worklog.backdate': 'Visszamenőleges rögzítés',
  'settings.worklog.daysBack': 'nappal ezelőttig',
  'settings.worklog.noLimit': 'Nincs korlát',
  'settings.worklog.backdateNote': 'Ennél régebbi napra csak a megfelelő szint tud rögzíteni.',
  'settings.worklog.backdateFreeNote': 'Bármilyen régi napra lehet rögzíteni.',
  'settings.worklog.backdateInvalid': 'Legalább egy nap.',
  'settings.worklog.lockDay': 'Zárónap',
  /* ⚠️ **`napja` — 22 Sep.** The select reads *A hónap 5.*, so without it the row said *a hónap
     5. után zárul*, which is not a Hungarian sentence. */
  'settings.worklog.lockDayAfter': 'napja után zárul az előző hónap',
  'settings.worklog.noLock': 'Nincs zárás',
  'settings.worklog.dayOfMonth': '{day}.',
  /* ⚠️⚠️ A figyelmeztetés arról szól, mit tesz a szabály az EMBEREKKEL — ezt egy számból senki nem
   * olvassa ki. */
  'settings.worklog.lockWarning':
    'A hónap {day}. napja után az előző hónapra már senki nem tud rögzíteni. Aki lemaradt, adminhoz kell fordulnia.',

  /* ── Integrációk ────────────────────────────────────────────────────────────────────────── */

  'settings.integrations.title': 'Integrációk · hamarosan',
  'settings.integrations.body':
    'Ide kerülnek a workspace szintű kapcsolatok: API kulcsok, webhookok, naptár- és számlázószinkron. A tartalom a következő fázisban készül el.',

  /* ── Adatok ─────────────────────────────────────────────────────────────────────────────── */

  'settings.export.title': 'Workspace adatainak exportálása',
  'settings.export.lede': 'A kért körből egy zip készül, letöltési linket e-mailben küldünk.',
  /* ⚠️⚠️ T-10: minden export EGY közös szerveroldali útvonalon megy, és az a következő fázis.
   * Egy űrlap, ami begyűjti a kört és sikert toastol, a legrosszabb fajta placeholder. */
  'settings.export.deferred':
    'Az export a következő fázisban készül el, egy közös útvonalon – ugyanaz szolgálja ki a listák exportját is. Amíg nincs kész, itt nincs mit indítani.',

  'settings.delete.title': 'Workspace törlése',
  'settings.delete.note':
    'A(z) {name} workspace, {projects} projekt, {tasks} feladat és minden worklog törlésre kerül. 30 nap türelmi idő után az adat visszaállíthatatlan.',
  'settings.delete.button': 'Workspace törlése',
  'settings.delete.dialogBody':
    'Ez a művelet törlést KÉR, nem töröl azonnal: 30 nap türelmi idő van, addig bármikor visszavonható.',
  'settings.delete.confirmLabel': 'Írd be a workspace nevét: {name}',
  'settings.delete.confirm': 'Törlés kérése',
  'settings.delete.pending': 'Törlés kérve',
  'settings.delete.pendingBy': '{name} kérte, {at}. Visszavonható.',
  'settings.delete.pendingAt': 'Kérve: {at}. Visszavonható.',
  'settings.delete.cancel': 'Kérés visszavonása',
  'settings.delete.now': 'Törlés most',
  'settings.delete.nowBody':
    'A {name} workspace most, a türelmi idő kivárása nélkül törlődik: {projects} projekt, {tasks} feladat, minden óra, kérelem és beállítás. Ez nem vonható vissza.',
  'settings.delete.nowConfirm': 'Végleges törlés',

  /* ⚠️ 0098 · „ebben a tenantban” törölve: az egyediség globális, így a cím tulajdonosa olyan
     tenantban is lehet, amit ez a felhasználó nem lát. */
  'settings.error.slugTaken': 'Ez a cím már foglalt.',
  'settings.error.slugReserved': 'Ezt a címet az alkalmazás használja. Válassz másikat.',
  'settings.error.confirmMismatch': 'A beírt név nem egyezik a workspace nevével.',
  'settings.error.deletionState': 'A törlés állapota közben megváltozott. Töltsd újra.',
  /* ⚠️ Shared by C8's statuses and C54's skill categories and levels, so it names no
     feature — each screen's own disabled menu item says what to do about ITS list. */
  'settings.error.lastOfItsKind': 'Az utolsót nem lehet törölni.',
  'settings.error.staleOrder': 'A sorrend közben megváltozott. Töltsd újra.',

  'settings.toast.deletionRequested': 'Törlés kérve',
  'settings.toast.deletionGrace': '30 nap türelmi idő – addig bármikor visszavonható.',
  'settings.toast.deletionCancelled': 'A törlési kérés visszavonva',

  'common.discard': 'Elvetés',
  'common.saving': 'Mentés…',

  'nav.reports': 'Riportok',
  'title.reports': 'Riportok',
  'title.myReports': 'Riportjaim',
  'title.report': 'Riport',
  'title.reportNew': 'Új riport',

  'report.action.new': 'Új riport',
  'report.action.open': 'Megnyitás',
  'report.action.pin': 'Kitűzés az oldalsávra',
  'report.action.unpin': 'Levétel az oldalsávról',
  'report.action.duplicate': 'Duplikálás',
  'report.action.delete': 'Törlés',
  'report.action.refresh': 'Frissítés',
  'report.action.back': 'Vissza a riportokhoz',
  'report.actions': 'Riport műveletek',
  'report.newName': 'Új riport',
  'report.copyName': '{name} (másolat)',
  'report.field.name': 'Riport neve',
  'report.field.from': 'Kezdő dátum',
  'report.field.to': 'Vég dátum',
  'report.controls': 'Vezérlők',
  'report.table': 'Táblázat',
  /* 16 Oct 2026 · the list layout and Kockázatkezelés. */
  'report.layout.summary': 'Összesítő',
  'report.layout.list': 'Lista',
  /* 17 Oct 2026 · the timeline layout, Munkaidő and the ready-made reports. */
  'report.source.workday': 'Munkaidő',
  'report.source.allocation': 'Erőforrások',
  'report.metric.expected': 'Elvárt óra',
  'report.metric.missing': 'Hiányzó óra',
  'report.metric.leave': 'Szabadság óra',
  'report.metric.missingCost': 'Hiányzó óra költsége',
  'report.dim.task': 'Feladat',
  'report.group.noLineItem': 'Nincs sortétel',
  'report.group.noContract': 'Nincs szerződés',
  'report.group.noClient': 'Ügyfél nélkül',
  'report.error.periodTooLong':
    'Ez az időszak túl hosszú az időbeli nézethez — válassz rövidebbet vagy szélesebb bontást.',
  'report.period.last3Months': 'Utolsó 3 hónap',
  'report.period.last6Months': 'Utolsó 6 hónap',
  'report.period.last12Months': 'Utolsó 12 hónap',
  'report.period.byWorkday': 'a lezárt munkanapok szerint, tegnapig',
  'report.period.byAllocationDay': 'a tervezett napok szerint',
  'report.filter.softBookings': 'Soft booking',
  'report.filter.softInclude': 'Beleszámít',
  'report.filter.softExclude': 'Nem számít bele',
  'report.layout.timeline': 'Időbeli',
  'report.rail.rows': 'Sorok',
  'report.rail.rowFirst': 'Első sor',
  'report.rail.rowSecond': 'Második sor',
  'report.rail.rowNone': '— nincs második sor —',
  'report.rail.granularity': 'Bontás',
  'report.granularity.week': 'Hét',
  'report.granularity.month': 'Hónap',
  'report.granularity.quarter': 'Negyedév',
  'report.rail.extras': 'Kiegészítő oszlopok',
  'report.rail.addExtra': 'Oszlop hozzáadása',
  'report.rail.noExtras': 'Nincs kiegészítő oszlop.',
  'report.rail.removeExtra': '{label} eltávolítása',
  'report.extra.countTask': 'Jegyek száma',
  'report.extra.countAssignee': 'Logolók száma',
  'report.extra.countLineitem': 'Sortételek száma',
  'report.extra.countProject': 'Projektek száma',
  'report.extra.listTask': 'Jegyek',
  'report.extra.listAssignee': 'Logolók',
  'report.extra.listLineitem': 'Sortételek',
  'report.extra.listProject': 'Projektek',
  'report.timeline.person': 'Munkatárs',
  'report.timeline.code': 'Kód',
  'report.timeline.total': 'Összesen',
  'report.timeline.lapsed': 'lejárt',
  'report.timeline.lapsedTip':
    'Erre az időszakra már nem lehet rögzíteni — a havi zárás vagy a visszamenőleges határ lezárta.',
  'report.timeline.week': '{week}. hét · {day}',
  'report.timeline.quarter': '{year}. Q{quarter}',
  'report.action.fromTemplate': 'Sablonból',
  'report.template.unknownWorklogs': 'Ismeretlen logolások',
  'report.template.unknownTasks': 'Ismeretlen jegyek',
  'report.template.loggingGaps': 'Logolási hiányok',
  'report.template.lineItemLogs': 'Logolások sortételenként',
  'report.template.unknownByPerson': 'Ismeretlen logolások – munkatárs szerint',
  'report.template.unknownByTask': 'Ismeretlen logolások – jegy szerint',
  'report.layout.label': 'Riport elrendezése',
  'report.error.unknownColumn': 'Ez az oszlop ennél a riportnál nem elérhető.',
  'report.error.badFilter': 'Az egyik szűrő értéke érvénytelen.',
  'report.list.empty': 'Nincs a szűrőknek megfelelő sor',
  'report.list.emptyBody': 'Bővítsd az időszakot, vagy vegyél el egy szűrőt.',
  'report.vocab.lineItemOpen': 'Nyitott',
  'report.vocab.lineItemClosed': 'Lezárt',
  'report.filter.projects': 'Projektek',
  'report.filter.clients': 'Ügyfelek',
  'report.filter.loggedBy': 'Rögzítő',
  'report.filter.assignee': 'Felelős',
  'report.filter.owner': 'Szerződésfelelős',
  'report.filter.contracts': 'Szerződések',
  'report.filter.lineItems': 'Sortételek',
  'report.filter.priorities': 'Prioritás',
  'report.filter.statuses': 'Állapot',
  'report.col.workedOn': 'Dátum',
  'report.col.hours': 'Óra',
  'report.col.member': 'Rögzítő',
  'report.col.team': 'Csapat',
  'report.col.note': 'Megjegyzés',
  'report.col.enteredAt': 'Rögzítve',
  'report.col.overtimeType': 'Túlóra típusa',
  'report.col.cost': 'Költség',
  'report.col.targetValue': 'Eladási érték',
  'report.col.taskKey': 'Feladat',
  'report.col.taskTitle': 'Feladat címe',
  'report.col.taskDescription': 'Leírás',
  'report.col.taskStatus': 'Állapot',
  'report.col.taskPriority': 'Prioritás',
  'report.col.taskAssignee': 'Felelős',
  'report.col.taskEstimate': 'Becslés',
  'report.col.taskStartsOn': 'Kezdés',
  'report.col.taskDueOn': 'Határidő',
  'report.col.taskComponents': 'Komponensek',
  'report.col.taskFixVersions': 'Javítási verziók',
  'report.col.taskEpic': 'Epic',
  'report.col.taskLogged': 'Rögzített óra',
  'report.col.taskRemaining': 'Hátralévő óra',
  'report.col.taskCreatedAt': 'Létrehozva',
  'report.col.taskUpdatedAt': 'Módosítva',
  'report.col.project': 'Projekt',
  'report.col.client': 'Ügyfél',
  'report.col.contract': 'Szerződés',
  'report.col.lineItem': 'Sortétel',
  'report.col.contractCode': 'Kód',
  'report.col.contractName': 'Szerződés neve',
  'report.col.contractType': 'Típus',
  'report.col.contractStatus': 'Állapot',
  'report.col.owner': 'Felelős',
  'report.col.startsOn': 'Kezdés',
  'report.col.endsOn': 'Vége',
  'report.col.lineItemCount': 'Sortételek',
  'report.col.estimate': 'Becslés',
  'report.col.budget': 'Büdzsé',
  'report.col.revenue': 'Bevétel',
  'report.col.invoiced': 'Kiszámlázva',
  'report.col.lineItemNumber': 'Sorszám',
  'report.col.lineItemType': 'Típus',
  'report.col.lineItemScope': 'Elszámolás',
  'report.col.lineItemStatus': 'Állapot',
  'report.col.closedAt': 'Lezárva',
  'report.col.syncTarget': 'Szinkron cél',
  'report.col.rate': 'Óradíj',
  'report.col.periodFrom': 'Időszak kezdete',
  'report.col.periodTo': 'Időszak vége',
  'report.total': 'összesen',
  'report.toasts': 'Riport-értesítések',

  'report.column.name': 'Riport',
  'report.column.source': 'Forrás',
  'report.column.dimension': 'Dimenzió',
  'report.column.metric': 'Mérték',
  'report.column.period': 'Időszak',
  'report.column.filters': 'Szűrők',
  'report.column.hours': 'Rögzített óra',
  'report.column.entries': 'Bejegyzés',
  'report.column.tasks': 'Feladat',
  'report.column.estimate': 'Becslés',
  'report.column.share': 'Arány',

  'report.view.all': 'Minden riport',
  'report.view.worklog': 'Óra-riportok',
  'report.view.task': 'Feladat-riportok',

  'report.source.worklog': 'Worklogok',
  'report.source.task': 'Feladatok',
  'report.source.contract': 'Szerződések',
  'report.source.lineitem': 'Sortételek',
  'report.source.occurrence': 'Időszakok',
  'report.dim.role': 'Projekt szerep',
  'report.dim.contract': 'Szerződés',
  'report.dim.lineitem': 'Sortétel',
  'report.dim.scope': 'Hatókör (retainer / elszámolható)',
  'report.dim.occurrence': 'Időszak (ismétlődés)',
  /* C60.1 · a mockup `risk` dimenziója, most már számtott érték fölött. */
  'report.dim.risk': 'Trust Score sáv',
  'report.group.unscanned': 'Még nem vizsgált',
  'report.metric.revenue': 'Bevétel',
  'report.metric.budget': 'Büdzsé',
  'report.money.locked': 'zárolt',
  'report.money.lockedTitle': 'Pénzes riporthoz jogosultság kell',
  'report.money.lockedNote':
    'A költség, bevétel, büdzsé és margó mértékekhez „Pénzes riportok” jogosultság kell. A saját szintedhez ez nincs bekapcsolva – kérd az adminodtól.',

  'report.dim.project': 'Projekt',
  'report.dim.client': 'Ügyfél',
  'report.dim.assignee': 'Felelős',
  'report.dim.status': 'Státusz',
  'report.dim.metafield': 'Metamező',

  'report.metric.hours': 'Rögzített óra összege',
  'report.metric.count': 'Darabszám',
  'report.metric.estimate': 'Becslés összege',
  'report.metric.overtime': 'Túlóra (elfogadott)',
  'report.metric.overtimeCost': 'Túlóra szorzóval',

  'report.chart.barShort': 'Oszlop',
  'report.chart.groupFeatured': 'Kiemelt',
  'report.chart.suggested': 'Ez így olvasna jobban',
  'report.reco.single': 'egyetlen érték – nagy számként olvasható',
  'report.reco.ordered': 'az időszakok sorrendje trendet ad',
  'report.reco.rhythm': 'napi óra – a naptár megmutatja a ritmust',
  'report.reco.many': '{count} kategória – vízszintesen elfér a név',
  'report.reco.few': '{count} kategória – tiszta arány',
  'report.reco.dominant': 'egy kategória adja a nagyját',
  'report.reco.companion': 'a mérték és a párja egymás mellett',
  'report.reco.months': 'hónapok szerinti bontás mátrixban',
  'report.chart.groupBasic': 'Alap',
  'report.chart.treemap': 'Treemap',
  'report.chart.waterfall': 'Vízesés',
  'report.chart.calendar': 'Naptár heatmap',
  'report.chart.sunburst': 'Sunburst',
  'report.chart.bubble': 'Buborék',
  'report.chart.scatter': 'Pontdiagram',
  'report.chart.radar': 'Radar',
  'report.chart.barline': 'Oszlop és vonal',
  'report.chart.line': 'Vonal',
  'report.chart.area': 'Területdiagram',
  'report.chart.vbar': 'Egyszerű vertikális',
  'report.chart.vstack': 'Halmozott vertikális',
  'report.chart.vgroup': 'Csoportosított vertikális',
  'report.chart.hbar': 'Egyszerű horizontális',
  'report.chart.hstack': 'Halmozott horizontális',
  'report.chart.hgroup': 'Csoportosított horizontális',
  'report.chart.listmetric': 'Lista mérték szerint',
  'report.chart.listdim': 'Lista dimenzió szerint',
  'report.chart.metric': 'Nagy szám',
  'report.chart.tableChart': 'Csak táblázat',
  'report.chart.gauge': 'Cél-mérő',
  'report.chart.donut': 'Donut',
  'report.chart.breakdown': 'Megosztás sáv',
  'report.chart.histogram': 'Hisztogram (gyakoriság)',
  'report.chart.heatmap': 'Heatmap',
  'report.chart.needsCompanion':
    'Összehasonlítható mérték kell hozzá – az óra a becsléssel, a pénz a büdzsével áll párban. A darabszámnak nincs párja.',
  'report.chart.needsDays':
    'Napi bontás kell hozzá – ehhez a riporthoz nincs dátum, amire a napokat rá lehetne tenni.',
  'report.chart.needsMatrix':
    'Hónap szerinti bontás kell hozzá – ehhez a riporthoz nincs dátum, amire a hónapokat rá lehetne tenni.',
  'report.chart.acrossGroups': '{count} csoport összesen',
  'report.chart.leaderShare': 'A legnagyobb csoport: {name}',
  'report.chart.histogramNote': 'A csoportok darabszáma sávonként – nem az egyes bejegyzéseké.',
  'report.chart.calendarNote': '{days} nap',
  'report.chart.tableOnly':
    'Ez a nézet csak a táblázatot mutatja – a Megjelenítés alatt kapcsolható be.',
  'report.chart.pieShort': 'Kör',
  'report.chart.bars': 'Oszlopdiagram',
  'report.chart.pie': 'Kördiagram',

  'report.period.week': 'Ez a hét',
  'report.period.month': 'Ez a hónap',
  'report.period.quarter': 'Ez a negyedév',
  'report.period.year': 'Ez az év',
  'report.period.all': 'Teljes idő',
  'report.period.custom': 'Egyéni',
  'report.period.none': 'nincs időkorlát',
  /* ⚠️ Nem ragozunk placeholderre: a magyar toldalék hangrendje a névtől függ, egy dátumhoz pedig
   * nem is illik. Külön szó elé kerül. */
  'report.period.since': '{date} óta',
  'report.period.until': '{date} előtt',
  /* ⚠️⚠️ Kimondjuk, MELYIK dátumra szűr — worklognál a rögzítés napja, feladatnál a határidő. Egy
   * dátumszűrő, ami nem árulja el, mit szűr, pontosan az a vezérlő, amit egyszer megnéz valaki, nem
   * stimmel neki, és utána kerülő úton dolgozik. */
  'report.period.byWorkedOn': 'a rögzítés napja szerint',
  'report.period.byDueOn': 'a határidő szerint – határidő nélküli feladat kimarad',

  'report.filter.project': 'Projekt',
  'report.filter.member': 'Felelős / rögzítő',
  'report.filter.metafield': 'Metamező',
  'report.filter.metafieldValue': 'Metamező értéke',
  'report.filters.none': 'nincs',

  'report.rail.metrics': 'Metrikák',
  'report.rail.dimensions': 'Dimenziók',
  'report.rail.display': 'Megjelenítés',
  'report.rail.filters': 'Szűrők',
  'report.rail.addMetric': 'Mérték hozzáadása',
  'report.rail.pickDimension': 'Dimenzió választása',
  'report.rail.addFilter': 'Szűrő hozzáadása',
  'report.rail.noMetric': 'Nincs mérték kiválasztva.',
  'report.rail.noFilters': 'Nincs szűrő: minden adat benne van.',
  'report.rail.removeFilter': '{label} szűrő eltávolítása',

  'report.noMetric.title': 'Kezdd egy mértékkel',
  'report.noMetric.body':
    'Válassz metrikát a jobb oldali Metrikák szekcióban – utána jön a dimenzió és a szűrők.',
  'report.empty.title': 'Nincs adat',
  'report.empty.body': 'Lazíts a szűrőkön – a jelenlegi kombinációra egy sor sem esik.',
  'report.emptyList.title': 'Nincs mentett riport',
  'report.emptyList.body': 'Állíts össze egyet, és mentsd el a szűrőkkel.',

  /* ⚠️⚠️ A csonkolást kimondjuk. A diagram a nyolc legnagyobbat mutatja; anélkül egy huszonhárom
   * projektes riport úgy néz ki, mint egy nyolc projektes. */
  'report.truncated': 'A diagram a {shown} legnagyobb csoportot mutatja, összesen {total} van.',
  'report.trimmedNote':
    'A riport a jogosultság-szűrt lekérdezésen fut – a számai megegyeznek a listák összesítőivel.',
  /* ⚠️ P-3: a riport csak a szerzőjének látszik. Ott mondjuk ki, ahol valaki különben a kollégája
   * riportjait keresné. */
  'report.ranAt': 'lefutott: {at}',
  'report.rowCount': '{count} sor',

  'report.toast.saved': 'Riport mentve',
  'report.toast.renamed': 'Riport átnevezve',
  'report.toast.refreshed': 'Riport újrafuttatva',
  'report.toast.duplicated': 'Riport duplikálva',
  'report.toast.deleted': 'Riport törölve',

  'report.error.inverted': 'A vég dátum a kezdés előtt van.',
  'report.error.notGroupable': 'Erre a metamező-típusra nem lehet bontani vagy szűrni.',
  'report.error.unknownMetafield': 'Ilyen metamező nincs ebben a workspace-ben.',
  'report.error.unknownFilter': 'A szűrő olyan elemre hivatkozik, ami itt nem létezik.',

  /* ⚠️⚠️ NULL a szerverről: a "nincs érték" csoport címkéje dimenziónként MÁS mondat. Egy
   * kliens-nélküli projekt a mockup szerint *"Belső"*, a projekt nélküli feladat viszont
   * *"Projekt nélkül"* — ugyanaz a null, két különböző mondat. */
  'report.group.noProject': 'Projekt nélkül',
  'report.group.internal': 'Belső',
  'report.group.unassigned': 'Nincs felelős',
  'tasks.unassign': 'Felelős törlése',
  'tasks.toast.bulkEdited': '{count} feladat módosítva',
  'tasks.toast.bulkDeleted': '{count} feladat törölve',
  /* ⚠️ **A tétel, nem a kiválasztás számát mondja.** A szerver soronként válaszol, és a
     `state-rules.md` §5 szerint a részleges eredményt nem nevezzük teljesnek. */
  'bulk.someRefused': '{total} közül nem mind sikerült',
  'people.team.clear': 'Csapat törlése',
  'people.field.timezoneSearch': 'Keresés időzónára…',
  'people.invitationSub': 'meghívó elküldve',
  'people.toast.bulkEdited': '{count} személy módosítva',
  'people.toast.bulkDeleted': '{count} személy inaktiválva',
  'clients.toast.bulkArchived': '{count} ügyfél archiválva',
  'clients.toast.created': '{name} ügyfél létrehozva',
  'clients.toast.updated': '{name} mentve',
  'projects.toast.bulkArchived': '{count} projekt archiválva',
  'report.group.noValue': 'Nincs érték',

  'people.action.back': 'Vissza a dolgozókhoz',
  'people.action.onboardStart': 'Onboarding indítása',
  'people.action.onboardReopen': 'Onboarding újranyitása',
  'onboarding.title': '{name} onboardingja',
  /* ⚠️⚠️ Semmi nem íródik ki a záró lépésig — ezt ki is mondjuk, mert egy félbehagyott varázsló
   * után joggal kérdezné bárki, mi maradt hátra. Semmi. */
  'onboarding.subtitle':
    'Tíz lépés. Semmi nem mentődik el a záró lépésig – ha most bezárod, nem változik semmi.',
  'onboarding.steps': 'Lépések',
  'onboarding.step.basic': 'Alapadatok',
  'onboarding.step.org': 'Közvetlen vezető',
  'onboarding.step.perm': 'Jogosultság',
  'onboarding.step.env': 'Munkakörnyezet',
  'onboarding.step.time': 'Időrögzítés',
  'onboarding.step.sched': 'Munkarend',
  'onboarding.step.rate': 'Díjkártya',
  /* ── C54 · the Skillek step ───────────────────────────────────────────────────────────── */
  'onboarding.step.skill': 'Skillek',
  'onboarding.skill.picked': 'Felvett skillek',
  'onboarding.skill.empty': 'Még nincs felvett skill. Kattints a fenti listából valamelyikre.',
  'onboarding.skill.noneLeft':
    'Nincs több választható skill. Adminként a Beállítások › Skill menedzsment alatt vehetsz fel újat.',
  'onboarding.skill.noVocabulary':
    'Ebben a workspace-ben még nincsenek skillek. A Beállítások › Skill menedzsment alatt vehetők fel.',
  'onboarding.skill.levelOf': '{name} szintje',
  'onboarding.skill.remove': '{name} eltávolítása',
  'onboarding.step.leave': 'Szabadság',
  'onboarding.step.meta': 'Metafieldek',
  'onboarding.step.done': 'Összegzés',

  'onboarding.permHint':
    'Több szint is adható: a tényleges jogosultság a szintek uniója, a bővebb nyer.',
  'onboarding.noRoleWarning':
    'Szint nélkül a dolgozó semmit nem tud megnyitni. Ez megengedett – később is beállítható.',
  'onboarding.timeHint': 'Ha nem rögzít időt, kimarad az óra-riportokból.',
  'onboarding.noMetafields': 'Ehhez a workspace-hez nincs dolgozó-metafield.',

  /* ── C64 · a munkarend lépés ─────────────────────────────────────────────── */
  'onboarding.sched.source': 'Munkarend forrása',
  'onboarding.sched.fromTemplate': 'Sablonból',
  'onboarding.sched.fromCustom': 'Egyedi',
  'onboarding.sched.from': 'Érvényes ettől',
  'onboarding.sched.template': 'Sablon',
  'onboarding.sched.pickTemplate': 'Válassz sablont',
  'onboarding.sched.pickNote': 'Válassz sablont – a napi terv ebből jön.',
  'onboarding.sched.templateNote': '{type} · {hours} / hét',
  'onboarding.sched.type': 'Típus',
  'onboarding.sched.fix': 'Fix',
  'onboarding.sched.flex': 'Rugalmas',
  'onboarding.sched.weekly': 'Heti óraszám',
  'onboarding.sched.days': 'Napi órák',
  'onboarding.sched.fixTotal': '{hours} / hét',
  'onboarding.sched.flexTotal': '{hours} / hét – rugalmas, napi bontás nélkül',
  'onboarding.sched.notNeeded':
    'Ez a dolgozó nem rögzít időt, így munkarendet sem kap – nincs mihez mérni a napjait. Az Időrögzítés lépésen állítható át.',
  'onboarding.sched.notNeededShort': 'nem kell',
  'onboarding.sched.skipped': 'kihagyva',
  'onboarding.sched.locked': 'Munkarendhez jogosultság kell',
  'onboarding.sched.lockedHint':
    'A munkarend a workspace beállításaihoz tartozik. Az onboarding többi lépése mehet – a munkarendet egy adminisztrátor adja majd meg.',

  /* ── C64 · a szabadság lépés ─────────────────────────────────────────────── */
  'onboarding.leave.managed': 'Szabadságkezelés a rendszerben',
  'onboarding.leave.managedHint.on': 'Kap kvótát, és beadhat szabadságkérelmet.',
  'onboarding.leave.managedHint.off': 'Kimarad a kvótákból, nem ad be kérelmet.',
  'onboarding.leave.quotas': 'Kvóták · {year}',
  'onboarding.leave.days': 'nap',
  'onboarding.leave.dayCount': '{count} nap',
  'onboarding.leave.noTypes':
    'Ebben a workspace-ben nincs kvótás szabadságtípus. A Beállítások › Szabadságkezelés alatt vehető fel.',
  'onboarding.leave.noQuotas': 'nincs megadva kvóta',
  'onboarding.leave.notManaged': 'nem a rendszerben',
  'onboarding.leave.locked': 'Kvótához jogosultság kell',
  'onboarding.leave.lockedHint':
    'A kvóta a workspace beállításaihoz tartozik. A szabadságkezelés kapcsolója ettől még állítható.',

  'onboarding.rate.skip': 'Díjkártya kihagyása',
  'onboarding.rate.skipHint.on':
    'Kihagyva – a dolgozónak nem lesz díja, később a Beállítások › Díjkártyák alatt pótolható.',
  'onboarding.rate.skipHint.off': 'A dolgozó díja – sablonból vagy egyénileg is megadható.',
  'onboarding.rate.source': 'Díj forrása',
  'onboarding.rate.fromTemplate': 'Sablonból',
  'onboarding.rate.fromCustom': 'Egyéni díj',
  'onboarding.rate.from': 'Érvényesség kezdete',
  'onboarding.rate.template': 'Díjkártya sablon',
  'onboarding.rate.templateSearch': 'Keresés sablonra…',
  'onboarding.rate.pickTemplate': 'Sablon kiválasztása',
  'onboarding.rate.pickNote':
    'Válassz sablont – a dolgozó mindig az épp érvényes időszak díját kapja.',
  'onboarding.rate.noPeriod': 'Ehhez a sablonhoz még nincs időszak.',
  'onboarding.rate.periodNote': '{unit} · {cost} költség / {target} cél',
  'onboarding.rate.unit': 'Egység típusa',
  'onboarding.rate.currency': 'Pénznem',
  'onboarding.rate.cost': 'Költségdíj',
  'onboarding.rate.target': 'Céldíj',
  'onboarding.rate.skipped': 'kihagyva',
  'onboarding.rate.locked': 'Díjkártyához jogosultság kell',
  'onboarding.rate.lockedHint':
    'A díj beállítása a workspace beállításaihoz tartozik. Az onboarding többi lépése mehet – a díjat egy adminisztrátor adja majd meg.',

  'onboarding.back': 'Vissza',
  'onboarding.next': 'Tovább',
  'onboarding.finish': 'Onboarding lezárása',
  'onboarding.done': 'Onboarding lezárva',
  'onboarding.doneNote': '{name} minden alapadata megvan.',
  'onboarding.error.ownRoles': 'A saját jogosultságodat másnak kell módosítania.',
  'onboarding.error.unknownRole': 'Ilyen jogosultsági szint nincs ebben a workspace-ben.',
  'onboarding.error.unknownTemplate': 'Ilyen díjkártya nincs ebben a workspace-ben.',
  'onboarding.error.unknownCurrency': 'Ez a pénznem nincs beállítva ebben a workspace-ben.',
  'onboarding.error.unknownSchedule': 'Ilyen munkarend nincs ebben a workspace-ben.',
  'onboarding.error.unknownLeaveType': 'Ilyen szabadságtípus nincs ebben a workspace-ben.',
  'people.onboarded': 'Onboardolva',
  'people.notOnboarded': 'Nincs onboardolva',

  'people.toast.saved': 'Mentve',
  'people.toast.deactivated': 'Deaktiválva',
  'people.toast.activated': 'Aktiválva',
  'people.toast.deleted': 'Törölve, a neve megmarad',

  /* ══ C13: worklog szabályok ═══════════════════════════════════════════════════════════════ */

  /* ⚠️ Mindkét szám a szerverről jön — helyben újraszámolva két szám lenne, és a képernyőn a rossz. */
  'time.error.dayFull': 'Ezzel a nap {total} órára jönne ki, a napi maximum viszont {max}.',
  'time.error.dayFullShort': 'A nap túllépné a napi maximumot.',
  'time.error.tooFarBack': 'Ennél régebbi napra nem rögzíthetsz. A legkorábbi: {earliest}.',
  'time.error.tooFarBackShort': 'Ez a nap kívül van a visszamenőleges ablakon.',
  'time.error.monthClosed':
    'A(z) {day} lezárt hónapba esik – ott sem módosítani, sem rögzíteni nem lehet.',
  'time.error.monthClosedShort': 'Ez a hónap lezárult.',

  /* ══ C12: a négy időlog-felület ════════════════════════════════════════════════════════════ */

  'nav.logs': 'Időlogok',
  'title.logs': 'Időlogok',
  'logs.section.overview': 'Áttekintés',
  'logs.section.logs': 'Időlogok',
  'logs.section.switch': 'Időrögzítés nézete',
  'logs.section.overviewNote': 'A mai nap, a heti keret és a projektbontás.',
  'logs.section.logsNote': 'Minden bejegyzés listában, timesheetben, naptárban vagy rácsban.',

  'logs.view.list': 'Lista',
  'logs.view.timesheet': 'Timesheet',
  'logs.week.previous': 'Előző hét',
  'logs.week.next': 'Következő hét',
  'logs.week.thisWeek': 'Ez a hét',
  'logs.view.calendar': 'Naptár',
  'logs.view.grid': 'Rács',

  'logs.search': 'Keresés megjegyzésre, feladatra…',
  'logs.range.all': 'Minden rögzítés',
  'logs.range.week': 'Ez a hét',
  'logs.range.month': 'Ez a hónap',

  'logs.empty': 'Nincs rögzítés ebben a nézetben',
  'logs.emptyNote': 'Váltsd az időszakot, vagy vigyél fel egy bejegyzést az Áttekintésen.',
  /* ⚠️ Csak azt mondja meg, mennyi látszik. A teljes idő aggregátumból jön, nem egy lapból. */
  'logs.footer': '{count} bejegyzés látszik',

  'logs.row.openTask': 'Feladat megnyitása',
  'logs.row.duplicate': 'Duplikálás mára',
  'logs.toast.logged': '{hours} rögzítve',
  'logs.toast.duplicated': 'Rögzítés duplikálva',
  'logs.toast.deletedMany': '{count} bejegyzés törölve',
  /* ⚠️ Ha nem mind sikerült, a toast megmondja — a részleges eredményt egésznek mutatni hazugság. */
  'logs.toast.deletedSome': 'A kijelölt {total} közül. A többit a szerver visszautasította.',

  'logs.timesheet.table': 'Heti timesheet',
  /* ⚠️ Its OWN name, distinct from the cell input's — two controls with one name is what a screen
     reader cannot tell apart. See `timesheet.tsx`. */
  'logs.timesheet.cellEntries': '{task} · {day}: bejegyzések',
  'logs.timesheet.total': 'Összesen',
  'logs.timesheet.daily': 'Napi összesen',
  'logs.timesheet.addTaskSearch': 'Keresés feladatra…',
  'logs.timesheet.addTask': 'Feladat hozzáadása',
  'logs.timesheet.taskClosed': 'A feladat le van zárva, ezért nem választható.',
  /* ⚠️ Mindkét koordináta kell: egy hét × egy tucat feladat 84 mező, és külön-külön egyik sem
   * azonosít. Vessző, nem toldalék — a magyar illeszkedés miatt. */
  'logs.timesheet.cell': '{task}, {day}',
  /* ⚠️ Round 5 item 32 — the cell modal. */
  'logs.cell.overtime': 'Túlóra',
  'time.error.overtimeApproved':
    'Ez az óra jóváhagyott túlórakérelemhez tartozik – nem módosítható és nem törölhető.',
  'logs.cell.add': 'Új bejegyzés hozzáadása',
  'logs.cell.total': 'Összesen',
  'logs.timesheet.removeAlloc': '{label} törlése',
  'logs.timesheet.removeRow': '{task} sorának eltávolítása',
  'logs.timesheet.empty': 'Ezen a héten nincs mire rögzíteni. Adj hozzá egy feladatot.',
  'logs.timesheet.copyOnwards': 'Másolás a következő napokra',
  'logs.timesheet.clearRange': 'Napok ürítése',

  'logs.grid.table': 'Időlog rács',

  /* ⚠️ A naptár billentyűzetes útja. Drag nélkül is elérhető kell legyen. */
  'logs.calendar.add': 'Idő rögzítése · {day}',

  'logs.entry.create': 'Idő rögzítése',
  'logs.entry.edit': 'Rögzítés módosítása',
  'logs.entry.hint': 'A kezdés és az időtartam adja meg a sávot a naptárban.',
  'logs.entry.willSave': '{hours} kerül rögzítésre.',
  'logs.entry.badDuration': 'Add meg az időtartamot, például 2:30 vagy 2,5.',

  /* ══ C11: időrögzítés ═════════════════════════════════════════════════════════════════════ */

  'nav.time': 'Időrögzítés',
  'title.time': 'Időrögzítés',
  /* ── C18: the time section's header dropdown — one sidebar row, two sections. ────────── */
  'time.section.group': 'Időrögzítés',
  'time.section.overview': 'Áttekintés',
  'time.section.overviewDesc': 'A mai nap, a heti keret és a projektbontás.',
  'time.section.logs': 'Időlogok',
  'time.section.logsDesc': 'Minden bejegyzés listában, timesheetben, naptárban vagy rácsban.',
  'time.toasts': 'Értesítések',

  /* ⚠️ A hét napjai HÉT-tel kezdődnek, nem VAS-sal — a magyar hét hétfőn indul. */
  'time.dow.mon': 'Hét',
  'time.dow.tue': 'Ke',
  'time.dow.wed': 'Sze',
  'time.dow.thu': 'Csü',
  'time.dow.fri': 'Pén',
  'time.dow.sat': 'Szo',
  'time.dow.sun': 'Vas',

  'time.week.range': 'Időszak',
  'time.week.title': 'A hét összesítése',
  /* 16 Oct 2026 · the reworked Időrögzítés: the period card, and the week's allocations. */
  'time.period.title': 'Időlogok',
  'time.period.week': '{from} – {to} · {week}. hét',
  'time.period.weekPicker': '{week}. hét · {from} – {to}',
  'time.period.span': 'Nézet',
  'time.period.previous': 'Korábbi időszak',
  'time.period.next': 'Következő időszak',
  'time.period.today': 'Ma',
  'time.period.pick': 'Időszak választása',
  'time.span.week': 'Heti',
  'time.span.month': 'Havi',
  'time.week.summary': 'A hét összesítése · {from} – {to}',
  'time.week.capacity': '{allocated} / {capacity} kapacitás',
  'time.week.noAllocation': 'Erre a hétre nincs allokációd.',
  'time.week.overtime': '{name} ×{multiplier} · elszámolt {billed}',

  /* ⚠️ *"Projekt nélkül"*, nem *"—"*: a projekt nélküli feladat nem hiányzó adat, hanem egy eset. */
  'time.noProject': 'Projekt nélkül',

  'time.day.empty': 'Erre a napra nincs rögzítés. Zárás előtt pótold.',
  'time.day.nothingExpected': 'Erre a napra nem kell rögzítened – {reason}.',
  'time.day.plan': 'Terv {hours}',
  'time.day.logged': 'rögzítve {hours}',
  'time.closed.month': 'Ez a hónap lezárult – ide sem rögzíteni, sem módosítani nem lehet.',
  'time.closed.backdate': 'Visszamenőleg {days} napra lehet rögzíteni, ez a nap ennél régebbi.',
  'time.entry.hours': 'Rögzített idő',
  'time.entry.remove': 'Eltávolítás',
  'time.hours.help': 'Elfogad 8, 8h, 8:00 vagy 8,0 alakot – H:MM alakban jelenik meg.',

  'time.log.task': 'Feladat',
  'time.log.search': 'Keress kulcsra vagy címre – pl. HSO-1 vagy Proof',
  'time.log.noResults': 'Nincs találat – próbáld a kulccsal.',
  'time.log.otherTask': 'Másik feladat',
  'time.log.hours': 'Óra',
  'time.log.startedAt': 'Kezdés',
  'time.log.duration': 'Időtartam',
  'time.log.note': 'Megjegyzés',
  'time.log.notePlaceholder': 'Mit csináltál?',
  'time.log.submit': 'Rögzítés',

  'time.recent.title': 'Korábbi bejegyzések',
  'time.recent.hint': 'Kattints az újrarögzítéshez',
  'time.recent.dismiss': '{task} elrejtése a javaslatokból',
  'time.recent.empty': 'Még nincs korábbi bejegyzésed.',

  'time.toast.logged': 'Idő rögzítve',
  'time.toast.loggedOvertime': 'Idő rögzítve · túlórakérelem elindítva',
  'time.toast.changed': 'Rögzítés módosítva',
  'time.toast.deleted': 'Rögzítés törölve',
  'time.undo': 'Visszavonás',
  'time.undo.failed': 'A visszavonás nem sikerült',

  /* ⚠️ P-1 két elutasítása két külön mondat — az egyik kérés, a másik tény. */
  'time.error.notAMember': 'Nem vagy tagja ennek a projektnek. Kérd, hogy vegyenek fel rá.',
  'time.error.notLogging': '{name} nem rögzít időt, ezért nem rögzíthető neki idő.',
  'time.error.outsideWindow':
    'Ezen a napon még nem vagy már nem voltál a projekt tagja. Válassz másik napot, vagy kérd a tagság kiterjesztését.',
  'time.error.notPermitted': 'Máshoz tartozó időt nem rögzíthetsz.',
  'time.error.hours': 'Nagyobbnak kell lennie 0-nál. Elfogad 8, 8h, 8:00 vagy 8,0 alakot.',

  /* ══ C10: metafieldek ═════════════════════════════════════════════════════════════════════ */

  'nav.metafields': 'Metafieldek',
  'title.metafields': 'Metafieldek · Beállítások',

  'common.yes': 'Igen',
  'common.no': 'Nem',

  'metafields.heading': 'Metafieldek',
  'metafields.entities': 'Entitások',
  'metafields.create': 'Új {entity} metaadat',
  'metafields.duplicate': 'Duplikálás',
  'metafields.delete': 'Törlés',
  'metafields.required': 'kötelező',
  'metafields.column.name': 'Megnevezés',
  'metafields.column.type': 'Típus',
  'metafields.allTypes': 'Minden típus',
  'metafields.search': 'Metaadat keresése',
  'metafields.emptyBody': 'Vegyél fel egyet, hogy az űrlapon megjelenjen.',
  'metafields.inList': 'listákban',
  'metafields.none': 'nincs',
  'metafields.someone': 'kiválasztott felhasználó',
  /* ⚠️⚠️ **The mockup's own string GLUES a suffix to a placeholder** — `label + '-szinten még
     nincs metafield'` — and Hungarian inflects by vowel harmony, so `-szinten` is wrong for half
     of the five entity names. Transcribing it reproduced the defect, and the vowel-harmony guard
     in `i18n.test.ts` is what caught it. Rephrased so the placeholder ends the clause. */
  'metafields.empty': 'Még nincs metafield ehhez: {entity}. Vegyél fel egyet.',
  'metafields.removeOption': 'Érték eltávolítása: {value}',
  'metafields.addOption': 'Hozzáadás',

  /* ── entitások ──────────────────────────────────────────────────────────────────────────── */
  'metafields.entity.task': 'Feladat',
  'metafields.entity.person': 'Dolgozó',
  'metafields.entity.project': 'Projekt',
  'metafields.entity.client': 'Ügyfél',
  'metafields.entity.worklog': 'Worklog',
  'metafields.entity.contract': 'Szerződés',
  'metafields.entity.externalCost': 'Külső költség',
  'metafields.entity.invoice': 'Számla',
  'metafields.entity.lineitem': 'Sortétel',

  /* ── a kilenc típus, a mockup neveivel ──────────────────────────────────────────────────── */
  'metafields.type.text': 'Rövid szöveg',
  'metafields.type.textarea': 'Hosszú szöveg',
  'metafields.type.number': 'Szám',
  'metafields.type.date': 'Dátum',
  'metafields.type.select': 'Választó',
  'metafields.type.multi': 'Többszörös választó',
  'metafields.type.checkbox': 'Jelölő',
  'metafields.type.user': 'Felhasználó',
  'metafields.type.url': 'Link',

  /* ── a dialógus ─────────────────────────────────────────────────────────────────────────── */
  'metafields.dialog.new': 'Új {entity} metaadat',
  'metafields.dialog.edit': 'Metafield szerkesztése',
  'metafields.field.name': 'Név',
  'metafields.field.key': 'Kulcs',
  'metafields.field.keyWarning':
    'A kulcs átírása eltöri a rá hivatkozó integrációkat és exportokat.',
  'metafields.field.entity': 'Entitás',
  'metafields.field.type': 'Típus',
  /* ⚠️ A típusváltás eldobja a már kitöltött értékeket — ezt előre kimondjuk, a számmal. */
  'metafields.field.typeWarning':
    'A típus átírása eldobja a már kitöltött értékeket, szám szerint {count}.',
  'metafields.field.help': 'Súgó',
  'metafields.field.required': 'Kötelező',
  'metafields.field.inList': 'Megjelenik a listákban',
  'metafields.field.options': 'Választható értékek',
  'metafields.field.optionPlaceholder': 'új érték',

  /* ── törlés ─────────────────────────────────────────────────────────────────────────────── */
  'metafields.delete.title': 'Metafield törlése',
  'metafields.delete.unused': 'A(z) „{name}" mező törlődik. Nincs rá kitöltött érték.',
  /* ⚠️ A mockup saját mondata, a számmal és a kulccsal — enélkül a dialógusra nem lehet válaszolni. */
  'metafields.delete.used':
    'A(z) „{name}" mezőn ennyi rekordon van kitöltve érték: {count}. A törléssel ezek is eltűnnek, és a kulcs ({key}) kiesik az exportokból.',

  /* ── hibák. Egy mondat típusonként; a szerver ugyanezt a szabályt futtatja. ──────────────── */
  'metafields.error.name': 'Adj nevet a mezőnek.',
  'metafields.error.key':
    'A kulcs kisbetűvel kezdődjön, és csak kisbetűt, számot, alsóvonást tartalmazzon.',
  'metafields.error.duplicate': 'Ez a kulcs már használatban van: {key}',
  'metafields.error.noOptions': 'Adj meg legalább egy választható értéket.',
  'metafields.error.optionExists': 'Ez az érték már a listában van.',
  'metafields.error.required': 'Kötelező mező.',
  'metafields.error.number': 'Ez nem szám. Tizedesvessző is használható.',
  'metafields.error.date': 'Ez nem létező nap.',
  'metafields.error.url': 'Csak http:// vagy https:// hivatkozás adható meg.',
  'metafields.error.option': 'Ez az érték nincs a listán: {value}',
  'metafields.error.shape': 'Ez az érték nem illik a mező típusához.',

  /* ── a metafieldek a feladat űrlapján ───────────────────────────────────────────────────── */
  'task.properties': 'Tulajdonságok',
  'task.metafields': 'Metafieldek',
  'task.attachments': 'Csatolmányok',
  'task.attachments.empty': 'Ehhez a feladathoz még nincs csatolmány.',
  'task.metafields.empty':
    'Feladat-szinten még nincs metafield. Beállítások → Metafieldek alatt vehetsz fel.',

  /* ⚠️ Item 68 · a marketplace szövegei innen kikerültek — a telepítés kapcsoló lett az
     Integrációk kártyán, és 94 kulcs vált halottá vele együtt. Ami maradt, azt az integrációs
     képernyők használják. */
  'settings.integrations.installed': 'Telepített kiegészítők',
  'settings.integrations.installedLede':
    'Ezek futnak ebben a munkaterületben. A példányok és a hitelesítés a következő ütemben érkezik.',
  'settings.integrations.installedEmpty':
    'Még nincs telepített kiegészítő. Nézd meg, mi érhető el a Kiegészítők alatt.',
  'settings.integrations.browse': 'Kiegészítők böngészése',
  'settings.integrations.pending': 'Beállítás hamarosan',

  /* ── C32 · a kiegészítők szövege. A tények a `shared` katalógusban vannak. ──────────────── */
  'ext.jira.name': 'Jira',
  'ext.jira.tagline': 'Issue- és worklog-szinkron Jira Cloud példányokkal.',
  'ext.jira.about':
    'A kiegészítő a Jira tereit (space) köti a Forecastify projektekhez: az issue-kból feladat lesz, a Forecastify-ban rögzített órák felkerülnek az issue-ra. Egy munkaterületben több példány is futhat – külön a sajátod és külön az ügyfél Jira-ja. Szerződés és sortétel nincs a Jira-ban, és nem is kell: az a miénk.',
  'ext.jira.can.1':
    'Issue-ból feladat, feladatból issue – mindkét oldal a saját kulcsát tartja meg, a párosítást mi vezetjük.',
  'ext.jira.can.2':
    'Worklog kétirányban: a nálunk rögzített H:MM felkerül az issue-ra, a távoli időbejegyzés bejön a feladatra.',
  'ext.jira.can.3':
    'Státuszcsoportok: több Jira-státusz mutathat egy Forecastify státuszra, és te mondod meg, melyiket írjuk vissza.',
  'ext.jira.can.4':
    'Amit nem tárolunk – issue típus, bejelentő, story pont, komponens – metafieldbe köthető.',
  'ext.jira.how.1':
    'Telepítés után a munkaterület beállítások → Integrációk alatt veszel fel egy példányt: URL és egy admin API token.',
  'ext.jira.how.2':
    'A varázsló végigvezet a tér-, státusz-, ember- és mezőpárosításon, és megmutatja, hány teret lát a token.',
  'ext.jira.how.3':
    'A változások webhookon érkeznek, percek helyett másodpercek alatt; naponta egyszer teljes egyeztetés is fut.',
  'ext.jira.how.4':
    'A saját órád a saját neveden megy fel, ezért a fiókbeállítások alatt egyszer össze kell kötnöd a Jira-fiókodat.',
  'ext.jira.cred':
    'A munkaterület kapcsolata megvan. Kösd össze a Jira-fiókodat, hogy a te órád a te neveden kerüljön fel – amíg nincs összekötve, a te worklogjaid várakoznak.',

  'ext.youtrack.name': 'YouTrack',
  'ext.youtrack.tagline':
    'Feladatok és időbejegyzések szinkronja YouTrack Cloud vagy on-prem példányokkal.',
  'ext.youtrack.about':
    'YouTrack projektek kötése Forecastify projektekhez, saját lekérdezéssel szűkítve. A YouTrack időbejegyzései a feladat worklogjai közé kerülnek. Szerződés és sortétel itt sincs, és nem is kell.',
  'ext.youtrack.can.1': 'Lekérdezés alapú behúzás: csak az számít be, ami a szűrőnek megfelel.',
  'ext.youtrack.can.2':
    'Időbejegyzések kétirányban – és a szerző a megfelelő ember marad, külön felhasználói kulcs nélkül.',
  'ext.youtrack.can.3':
    'Státuszcsoportok: több YouTrack állapot mutathat egy Forecastify státuszra, megadott alapértelmezettel.',
  'ext.youtrack.can.4':
    'Amit nem tárolunk – típus, alrendszer, verzió, becslés – metafieldbe köthető.',
  'ext.youtrack.how.1':
    'Telepítés után Integrációk alatt add meg a példány URL-jét, egy admin tokent és a lekérdezést.',
  'ext.youtrack.how.2':
    'A projekteket kézzel kötöd össze; ami nincs összekötve, az nem jön át, és a futás naplója megmondja, miért.',
  'ext.youtrack.how.3':
    'A YouTrack-nek nincs webhookja, ezért ütemezetten kérdezünk rá – a példány lapja mutatja, mikor futott.',
  'ext.youtrack.cred':
    'A YouTrack-en a munkaterület kulcsa a te nevedben is tud órát rögzíteni, ezért itt általában nincs teendőd.',

  /* ── C33 · integrációs példányok. `2fazis.markup.html` 2462–2795, 3966–4170. ───────────── */
  'title.integrationProvider': 'Példányok · Beállítások',
  'title.integrationInstance': 'Példány · Beállítások',
  'integrations.heading': 'Integrációk',
  'integrations.lede':
    'Kösd össze a munkaterületet a fejlesztői rendszerekkel. Egy rendszerhez több példány is tartozhat – külön a sajátod és külön az ügyfélé.',
  'integrations.notInstalled': 'Kikapcsolva',
  'integrations.notFound': 'Ilyen kiegészítő nincs.',
  'integrations.enable': 'Bekapcsolás ebben a munkaterületben',
  'integrations.toast.enabled': 'Bekapcsolva',
  'integrations.toast.enabledBody': 'Most már vehetsz fel hozzá példányt.',
  'integrations.toast.disabled': 'Kikapcsolva',
  'integrations.inUse': 'Előbb töröld a példányait: {names}',
  'integrations.noInstances': 'Nincs bekötve',
  'integrations.instanceCount': '{count} példány',
  'integrations.instanceCountBad': '{count} példány · ennyi hibás: {bad}',
  'integrations.manage': 'Kezelés',
  'integrations.connect': 'Bekötés',
  'integrations.new': 'Új példány',
  'integrations.empty':
    'Még nincs példány bekötve. Egy munkaterülethez több is tartozhat – például külön a saját és az ügyfél rendszere.',
  'integrations.col.instance': 'Példány',
  'integrations.col.state': 'Állapot',
  'integrations.col.last': 'Utolsó szinkron',
  'integrations.col.schedule': 'Ütemezés',
  'integrations.never': '–',
  'integrations.state.setup': 'Beállítás alatt',
  'integrations.state.active': 'Aktív',
  'integrations.state.paused': 'Szüneteltetve',
  'integrations.state.error': 'Hiba',
  'integrations.active': 'Aktív',
  'integrations.syncRecent': 'Friss szinkron',
  'integrations.sync.group': 'Szinkronizálás',
  'integrations.syncWindow.open': 'Szinkron megadott időszakra…',
  'integrations.syncWindow.title': 'Szinkron megadott időszakra',
  'integrations.syncWindow.description':
    'A feladatokat és worklogokat a nyomkövetőben történt módosítás ideje szerint olvassuk be: egy júniusi óra, amit szeptemberben szerkesztettek, szeptemberi változás.',
  'integrations.syncWindow.floor': 'A kapcsolat kezdődátuma {date} – korábbról nem olvasunk.',
  'integrations.syncWindow.confirm': 'Szinkronizálás',
  'integrations.syncWindow.preset.week': 'Utolsó 1 hét',
  'integrations.syncWindow.preset.month': 'Utolsó 1 hónap',
  'integrations.syncWindow.preset.quarter': 'Utolsó 3 hónap',
  'integrations.syncWindow.preset.sinceStart': 'A kezdődátumtól',
  'integrations.actions': 'Műveletek',
  'integrations.open': 'Megnyitás',
  'integrations.pause': 'Szüneteltetés',
  'integrations.resume': 'Aktiválás',
  'integrations.reopenWizard': 'Varázsló újraindítása',
  'integrations.delete': 'Példány törlése',
  'integrations.deleteTitle': 'Példány törlése',
  'integrations.deleteBody':
    'A kapcsolat, a státusz- és emberpárosítás, valamint a szinkronnapló törlődik. A már átszinkronizált feladatok és órák a munkaterületben maradnak, de nem frissülnek tovább.',
  'integrations.dangerTitle': 'Példány törlése',

  /* ── C34 · a kapcsolási postaláda. `jira.md` §9.5. ─────────────────────────────────────────
   *
   * ⚠️⚠️ **KAPCSOLÁS, SOHA NEM IMPORT.** Egyetlen szöveg sem ígéri, hogy a távoli rendszerből
   * projekt LESZ nálunk — a különbség az, hogy az ügyfél Jira-ja megtöltheti-e a munkaterületet
   * olyan projektekkel, amit senki nem kért.
   *
   * ⚠️ A főnév szolgáltatónként más (P2-23): az Atlassian átnevezte a Jira projektet *tér*-re, a
   * YouTrack nem. Ezért van két fejlécünk ugyanarra a fogalomra. */
  'integrations.tab.links': 'Terek és projektek',
  'integrations.links.heading.space': 'Jira terek',
  'integrations.links.heading.project': 'Távoli projektek',
  'integrations.links.never.space': 'Még nem néztük meg, mit lát a kapcsolat. Indíts felderítést.',
  'integrations.links.never.project':
    'Még nem néztük meg, mit lát a kapcsolat. Indíts felderítést.',
  'integrations.links.discoveredAt': 'Utolsó felderítés: {at}',
  'integrations.links.discover': 'Felderítés',
  'integrations.links.discovered': 'Felderítés kész',
  'integrations.links.failed': 'Nem sikerült',
  'integrations.links.archived': 'archivált',
  'integrations.links.invisible': 'a kapcsolat már nem látja',
  /* ── C39 · 0074 · amit a Jira mond a projektről. Csak az `active` szinkronizál. ── */
  'integrations.links.remote.archived': 'Jirában archiválva',
  'integrations.links.remote.trashed': 'Jira kukájában',
  'integrations.links.remote.deleted': 'Jirából törölve',
  'integrations.links.writes': 'Írás engedélyezve',
  'integrations.links.manual': 'kézi besorolás',
  'integrations.links.noneTargeted': 'Még egy sortétel sem gyűjt ebből a kapcsolatból.',
  'integrations.links.untargeted': 'Egy sortétel sem gyűjti',
  'integrations.links.allTargeted':
    'Amit a kapcsolat lát, abból mindből gyűjt legalább egy sortétel.',
  'integrations.links.whereToTarget':
    'A gyűjtést a sortétel adatlapján, a Szinkronizálás részben lehet beállítani. Amit egy sortétel sem gyűjt, arról nem érkezik feladat.',
  'integrations.links.note':
    'Egy távoli projektből több sortétel is gyűjthet, akár különböző Forecastify projekteken. Ami egyik sortétel szabályának sem felel meg, besorolatlan feladatként érkezik, és jelzést ad azokon a projekteken, amelyek ebből a távoli projektből gyűjtenek.',
  /* ── C36 · a beragadt órák postaládája ────────────────────────────────────────────────────
   *
   * ⚠️⚠️ **A KÉT OK KÉT KÜLÖNBÖZŐ EMBERT KERES.** A `no_member` a Munkatársak képernyőn dől el,
   * a `no_task` az összekötéseknél — egyetlen *„nem sikerült behozni”* mindkettőt rossz helyre
   * küldené.
   */
  /* ⚠️⚠️ **A TÁVOLI KULCS, MERT A MIÉNK UGYANÚGY NÉZ KI ÉS MÁST JELENT.** Egy `HSO` előtagú
     projekt egy `HSO` kulcsú Jira térhez kötve azt adja, hogy `HSO-51` mindkét rendszerben létező
     azonosító — és két különböző munkára mutat. */
  'task.field.remoteIssue': 'Kapcsolt jegy',
  'task.remoteIssue.pending': 'még nincs kulcsa',
  /* J-7 · a szinkronizált feladat a hibakövető kulcsát viseli; amíg a jegy készül, a sajátunk
     ideiglenes, és a sor ezt mondja ki. */
  'task.field.key': 'Kulcs',
  'task.key.pending': 'ideiglenes · a hibakövető kulcsára vár',
  'integrations.tab.parked': 'Beragadt órák',
  'integrations.parked.heading': 'Beragadt órák',
  'integrations.parked.none': 'Minden beérkezett óra a helyére került.',
  'integrations.parked.emptyBody': 'Nincs mire várni.',
  'integrations.parked.byAuthor': 'Kinek az órái',
  /* J-3 · a szinkronizált feladatok, amelyeket egyik sortétel szabálya sem fog. */
  'integrations.tab.unrouted': 'Besorolatlan feladatok',
  'integrations.unrouted.heading': 'Besorolatlan feladatok',
  'integrations.unrouted.none': 'Minden szinkronizált feladat sortételre került.',
  'integrations.unrouted.waiting':
    '{count} szinkronizált feladatot egyik sortétel szabálya sem fog',
  'integrations.unrouted.emptyBody': 'Nincs besorolatlan feladat.',
  'integrations.unrouted.explain':
    'Ezek a feladatok megvannak, és órát is lehet rájuk írni, de egyik szerződéshez sem számítanak. Adj a sortételhez olyan szabályt, amely elkapja őket (Szerződések), vagy nyisd meg a feladatot, és válassz neki sortételt kézzel.',
  'integrations.unrouted.toContracts': 'Szerződések',
  'integrations.unrouted.col.project': 'Jira-projekt',
  'integrations.unrouted.col.issue': 'Jegy',
  'integrations.unrouted.col.title': 'Feladat',
  'integrations.unrouted.col.components': 'Komponensek',
  'integrations.unrouted.col.status': 'Jira-státusz',
  /* ⚠️ Az ÓRÁK száma is ott van, nem csak a soroké: egy hónapból hiányzó 19 órát az ember
     felismeri, tizenkét sort nem. */
  'integrations.parked.waiting': 'Várakozik: {count} tétel, összesen {hours}',
  'integrations.parked.reason.noTask': 'ismeretlen feladat',
  /* 17 Oct 2026 · the parked table's columns, search and filters. */
  'integrations.parked.col.issue': 'Jegy',
  'integrations.parked.col.author': 'Logoló',
  'integrations.parked.col.reason': 'Ok',
  'integrations.parked.col.note': 'Megjegyzés',
  'integrations.parked.discardSelected': 'Kijelöltek elvetése',
  'integrations.parked.noMatch': 'A szűrők minden órát kiszűrtek.',
  'integrations.parked.search': 'Keresés jegyre, logolóra, megjegyzésre…',
  'integrations.parked.reason.noMember': 'ismeretlen munkatárs',
  'integrations.parked.fix.noTask': 'Sortételek beállítása',
  'integrations.parked.fix.noMember': 'Munkatársak párosítása',
  'integrations.parked.authorUnknown': 'nincs megadva, ki írta',
  'integrations.parked.discarded': 'Eldobva',
  'integrations.parked.ran': 'Egyeztetés kész',
  'integrations.parked.failed': 'Nem sikerült',
  /* ⚠️ A napi korlát nem hiba: megmondja, mikor lehet újra. */
  'integrations.parked.tooSoon': 'Ma már futott egy egyeztetés.',
  /* ── C39 · élő frissítés. A mockup ezt nem rajzolja: az ő „webhook"-ja kimenő, és a beállítások
     képernyőn a következő fázisra vár. ── */
  'integrations.webhook.title': 'Élő frissítés',
  'integrations.webhook.live': 'Bekapcsolva',
  'integrations.webhook.moved': 'Rossz címre mutat',
  'integrations.webhook.off': 'Kikapcsolva',
  'integrations.webhook.liveNote':
    'A Jira azonnal szól, ha valami változik. A napi egyeztetés emellett is fut – az a biztosíték.',
  'integrations.webhook.offNote':
    'A változások csak a napi egyeztetéskor érkeznek meg. A Jirában törölt feladatokról így nem értesülünk.',
  'integrations.webhook.movedNote':
    'A Jira olyan címre küldi az értesítéseket, amelyen ez a telepítés már nem válaszol. Semmi nem érkezik, és hibaüzenet sincs róla. Az újracsatlakoztatás megjavítja.',
  'integrations.webhook.address': 'Cím',
  'integrations.webhook.registeredAt': 'Bekapcsolva',
  'integrations.webhook.lastDelivery': 'Utolsó jelzés',
  'integrations.webhook.never': 'Még nem érkezett',
  'integrations.webhook.rejected':
    'Elutasított jelzések: {count}. Vagy lecserélték a titkot, vagy valaki próbálkozik.',
  'integrations.webhook.turnOn': 'Élő frissítés bekapcsolása',
  'integrations.webhook.repair': 'Újracsatlakoztatás',
  'integrations.webhook.turnOff': 'Kikapcsolás',
  'integrations.webhook.registered': 'Az élő frissítés bekapcsolva.',
  'integrations.webhook.disabled': 'Az élő frissítés kikapcsolva.',
  'integrations.webhook.failed': 'Az élő frissítést nem sikerült beállítani.',
  'integrations.webhook.stale': 'Elavult szűrő',
  'integrations.webhook.staleNote':
    'A Jira azóta felvett projektekről nem küld értesítést – a feliratkozás még a régi projektlistát tartalmazza. Az újracsatlakoztatás frissíti.',
  'integrations.webhook.drift':
    'A legutóbbi egyeztetés {count} olyan változást talált, amelyről nem érkezett értesítés. Ha ez tartósan így marad, a feliratkozás nem működik.',
  'integrations.webhook.driftSince': '{at} óta.',
  'integrations.webhook.byHand': 'Beállítás kézzel',
  'integrations.webhook.byHandNote':
    'Ezt a feliratkozást kézzel vették fel a Jirában, így innen nem módosítható. A projektszűrőt ugyanott kell frissíteni.',
  'integrations.webhook.manualTitle': 'Másold be ezt a Jira beállításai közé',
  'integrations.webhook.manualNote':
    'Jira → Beállítások → Rendszer → WebHooks → Create a WebHook. A titkot csak most mutatjuk meg egyszer.',
  'integrations.webhook.manualUrl': 'URL',
  'integrations.webhook.manualSecret': 'Titok',
  'integrations.webhook.manualEvents': 'Események',
  'integrations.webhook.manualFilter': 'Szűrő (JQL)',
  'integrations.webhook.manualDone': 'Kész',
  'integrations.webhook.manualWarn':
    '⚠️ A titok csak most látható. Ha bezárod, újra kell generálni – és akkor a Jirába is újra be kell másolni.',
  'integrations.tab.overview': 'Áttekintés',
  'integrations.tab.runs': 'Szinkronok',
  'integrations.tab.settings': 'Beállítások',

  'integrations.stat.last': 'Utolsó szinkron',
  'integrations.stat.lastNever': 'még nem futott',
  'integrations.stat.schedule': 'Ütemezés',
  'integrations.stat.paused': 'szünetel',
  /* ⚠️ P2-24: nem intervallum, hanem napszak — a teljes egyeztetés naponta egyszer fut. */
  'integrations.overwatchAt': 'Minden nap {at}',
  'integrations.stat.scheduleOff': 'ütemezés leállítva',
  'integrations.stat.in': 'Átjött',
  'integrations.stat.inNote': 'ennyi ment ki: {count}',
  'integrations.stat.errors': 'Hibás futás',
  'integrations.stat.errorsNote': 'nézd meg a naplót',
  'integrations.stat.errorsNone': 'nincs hiba',

  'integrations.connection': 'Kapcsolat',
  'integrations.conn.url': 'Cím',
  'integrations.conn.auth': 'Hitelesítés',
  'integrations.conn.account': 'Felhasználó',
  'integrations.conn.token': 'Token',
  'integrations.conn.oauth': 'OAuth 2.0 (3LO)',
  'integrations.conn.test': 'Kapcsolat tesztelése',
  'integrations.conn.testing': 'Tesztelés…',
  'integrations.conn.testHint': 'A mentés előtt ellenőrizzük, hogy a hozzáférés működik-e.',
  'integrations.conn.testOk': 'Rendben · {count} projekt látható',
  'integrations.conn.testAs': 'Hitelesítve: {name}',
  'integrations.conn.fieldsPartial':
    'A mezők listája részleges: a teszt csak néhány projektet olvasott be, a teljes lista a mentés után érkezik.',
  'integrations.fields.partial':
    'A lista részleges: nem fért bele minden Jira-projekt az időkeretbe. Egy újabb megnyitás a többit is beolvashatja.',
  'integrations.error.untested':
    'Előbb teszteld a kapcsolatot. Egy jól kinéző cím és token még semmit nem bizonyít.',
  'integrations.conn.tokenKind': 'API token',
  'integrations.conn.oauthAccount': 'A kapcsolat él',
  'integrations.conn.noToken': 'nem szükséges',

  'integrations.whatSyncs': 'Mit szinkronizál',
  'integrations.conflict': 'Ütközésnél: {winner}',
  'integrations.conflict.remote': 'a távoli rendszer nyer',
  'integrations.conflict.local': 'a Forecastify nyer',
  'integrations.map.statuses': 'Státuszok',
  'integrations.map.priorities': 'Prioritások',
  'integrations.map.prioritiesWarn': 'A párosítatlan prioritásoknál a Forecastify sajátja marad.',
  'integrations.map.openPeople': 'Emberek',
  'integrations.map.people': 'Emberek',
  'integrations.map.count': 'ennyi párosítva: {mapped} / {total}',
  'integrations.map.warn': 'A párosítatlanok kimaradnak a szinkronból.',
  'integrations.map.ok': 'Minden elem össze van kötve.',
  'integrations.map.none': 'nincs párosítatlan',
  'integrations.map.projects': 'Projektek',
  'integrations.map.projectsOk': 'Ennyi projekt szinkronizál ezzel a példánnyal.',
  'integrations.map.noProjects': 'Egy projekt sincs bekötve – így semmi nem szinkronizál.',

  'integrations.rule.taskOut': 'Forecastify feladat → issue',
  'integrations.rule.taskOut.desc': 'Az itt létrehozott feladat kimegy a távoli rendszerbe.',
  'integrations.rule.statusBoth': 'Státuszváltás kétirányban',
  'integrations.rule.statusBoth.desc':
    'A státusz mindkét oldalon követi a másikat – ütközésnél a konfliktusszabály dönt.',

  'integrations.runs.col.at': 'Időpont',
  'integrations.runs.col.trigger': 'Indítás',
  'integrations.runs.col.result': 'Eredmény',
  'integrations.runs.col.in': 'Be',
  'integrations.runs.col.out': 'Ki',
  'integrations.runs.empty':
    'Még nem futott szinkron. Indíts egyet kézzel, vagy várd meg az ütemezettet.',
  'integrations.runs.all': 'Minden futás',
  'integrations.run.state.running': 'Fut',
  'integrations.run.state.ok': 'Sikeres',
  'integrations.run.state.partial': 'Részleges',
  'integrations.run.state.error': 'Hiba',
  'integrations.run.trigger.scheduled': 'ütemezett',
  'integrations.run.trigger.manual': 'manuális',
  'integrations.run.trigger.webhook': 'webhook',
  /* ⚠️ Item 67 · *újrapróbálás* → *automatikus*. A retry is something the system starts by itself,
     and naming the mechanism told a reader who had never seen the queue nothing. */
  'integrations.run.trigger.retry': 'automatikus',
  'integrations.run.trigger.backfill': 'visszatöltés',
  /* J-8 · egy projekt egészben letöltve, mert egy tétel megcélozta; a sor mondja, melyik. */
  'integrations.run.trigger.target': 'projekt',
  'integrations.run.elapsed': '{minutes} perce fut',
  'integrations.run.inBreakdown': '{tasks} feladat · {worklogs} worklog',
  'integrations.run.untilNow': 'máig',
  'integrations.run.window': 'Időszak',
  'integrations.runs.filter.label': 'Futások szűrése',
  'integrations.runs.filter.people': 'Indított és ütemezett',
  'integrations.runs.filter.webhook': 'Webhook kézbesítések',
  'integrations.runs.filter.all': 'Minden futás',
  'integrations.runs.total': '{total} futás',
  'integrations.run.onTask': 'Feladat:',
  'integrations.run.inProject': 'Projekt:',
  'integrations.run.noPayload': 'Ehhez a sorhoz nincs eltárolt adat.',
  'integrations.run.errorCount': '{count} hiba',
  'integrations.run.noteCount': '{count} megjegyzés',
  'integrations.run.in': 'Beérkezett',
  'integrations.run.out': 'Kiküldött',
  'integrations.run.tasks': 'feladat',
  'integrations.run.worklogs': 'worklog',
  'integrations.run.errors': 'Hibák',
  'integrations.run.noRecords':
    'Ez a futás egyetlen rekordot sem vitt át. A hibák fent olvashatók.',
  'integrations.run.col.entity': 'Entitás',
  'integrations.run.col.key': 'Azonosító',
  'integrations.run.col.dir': 'Irány',
  'integrations.run.col.action': 'Művelet',
  'integrations.run.col.result': 'Eredmény',
  'integrations.run.dir.in': 'Be',
  'integrations.run.dir.out': 'Ki',
  'integrations.run.entity.task': 'Feladat',
  'integrations.run.entity.worklog': 'Worklog',
  'integrations.run.entity.comment': 'Komment',
  'integrations.run.action.created': 'létrehozva',
  'integrations.run.action.updated': 'frissítve',
  'integrations.run.action.skipped': 'kihagyva',
  'integrations.run.result.ok': 'sikeres',
  'integrations.run.result.skip': 'kihagyva',
  'integrations.run.result.error': 'hiba',
  'integrations.note.stub': 'Próbafutás – ez a kiegészítő még nem beszél a távoli rendszerrel.',
  'integrations.note.worklog_in': 'óra behozva',
  'integrations.note.worklog_deleted': 'a távoli rendszerben törölték',
  'integrations.note.worklog_unparked': 'törölték, mielőtt a helyére került volna',
  'integrations.note.parked_no_task': 'beragadt: ismeretlen feladat',
  'integrations.note.parked_no_member': 'beragadt: ismeretlen munkatárs',
  'integrations.error.unmapped_status': 'Nincs párosított státusz: {detail}',
  'integrations.error.unmapped_user': 'Nincs párosított felhasználó: {detail}',
  'integrations.error.no_rules_enabled':
    'Egyetlen szabály sincs bekapcsolva, így a futás nem vitt át semmit.',

  'integrations.wizard.newTitle': 'Új példány',
  'integrations.wizard.editTitle': 'Példány beállítása',
  'integrations.wizard.desc':
    'Nyolc lépés: kapcsolat, feladatok, státuszok, prioritások, emberek, mezők, szabályok, ellenőrzés.',
  'integrations.wizard.singleDesc': 'Csak ez a lépés módosul, a többi beállítás marad.',
  'integrations.wizard.next': 'Tovább',
  'integrations.wizard.back': 'Vissza',
  'integrations.wizard.finish': 'Bekötés',
  'integrations.wizard.saveEdit': 'Mentés',
  'integrations.wizard.steps': 'Lépések',
  /* ── C34 · a hetedik varázslólépés: extra mezők metafieldbe (P2-20). ──────────────────────
   *
   * ⚠️⚠️ **ALAPBÓL SEMMI NEM SZINKRONIZÁL.** Húsz előre bepipált mező azt jelentené, hogy aki a
   * Tovább gombot megnyomja, olyanba egyezett bele, amit el sem olvasott. */
  'integrations.step.fields': 'Mezők',
  'integrations.fields.loading': 'Mezők betöltése a távoli rendszerből…',
  'integrations.fields.summary': 'Extra Jira mezők metafieldbe kötve. Megnyitáskor kérdezzük le.',
  'integrations.fields.saveFirst':
    'Előbb mentsd a kapcsolatot. A mezőket a példányhoz kötjük, ezért csak akkor tudjuk eltárolni, ha a példány már létezik – nyisd meg újra a varázslót, és itt lesznek.',
  'integrations.fields.lede':
    'Amit a Jira tárol és mi nem – issue típus, bejelentő, story pont – metafieldbe köthető. Ami itt marad üresen, az nem jön át.',
  'integrations.fields.none': 'nem szinkronizál',
  'integrations.fields.create': '+ Új metafield',
  'integrations.fields.newName': 'Új metafield neve',
  'integrations.fields.newType': 'Típus',
  'integrations.fields.newOptions': 'Választható értékek',
  'integrations.fields.newOptionsHint': 'Vesszővel elválasztva',
  'integrations.fields.newOptionsRequired':
    'Egy választós mezőnek legalább egy értékre szüksége van.',
  'integrations.fields.requiredNo': 'kötelező, ezért nem fogadhat',
  'integrations.fields.empty': 'Nincs olyan mező, amit ide be tudnánk kötni.',
  'integrations.fields.others': 'További {count} mező, amit nem kínálunk',
  'integrations.fields.claimed': 'saját oszlopunk van rá',
  'integrations.fields.unsupported': 'nem tudjuk tárolni',
  'integrations.fields.note':
    'A behúzás egyirányú: a Jira értéke ide kerül. Ha törlöd a metafieldet, a szinkron megáll – ez nem hiba.',
  'integrations.step.connection': 'Kapcsolat',
  'integrations.step.tasks': 'Feladatok',
  'integrations.step.statuses': 'Státuszok',
  'integrations.step.priorities': 'Prioritások',
  'integrations.step.people': 'Emberek',
  'integrations.people.lede':
    'Kösd össze a távoli fiókokat a munkatársakkal. Amelyik fiók párosítatlan marad, annak az óráit nem tudjuk hova tenni.',
  'integrations.people.paired': '{paired} / {count} fiók párosítva.',
  'integrations.people.unpaired': '{count} fiók még párosítatlan.',
  'integrations.people.nobody': 'nincs párosítva',
  'integrations.people.pairWith': '{name} párosítása',
  'integrations.people.none': 'Ezen a példányon még nincs behúzott távoli fiók.',
  'integrations.people.preview':
    'A kapcsolat {count} aktív fiókot lát – párosítani a mentés után lehet.',
  'integrations.people.saveFirst':
    'Előbb mentsd a kapcsolatot. A fiókokat a példányhoz kötjük, ezért csak akkor tudjuk eltárolni, ha a példány már létezik – nyisd meg újra a varázslót, és itt lesznek.',
  'integrations.step.rules': 'Szabályok',
  'integrations.step.review': 'Ellenőrzés',

  'integrations.field.name': 'Példány neve',
  'integrations.field.namePlaceholder': 'Pl. LogiNet Jira (prod)',
  'integrations.field.url': 'Cím',
  'integrations.field.auth': 'Hitelesítés',
  'integrations.field.email': 'Felhasználó e-mail címe',
  'integrations.field.token': 'API token',
  'integrations.field.tokenKept':
    'A tárolt token megmarad, amíg nem írsz be újat. Kiolvasni nem lehet – csak felülírni.',
  'integrations.field.query': 'Szűrő',
  'integrations.field.since': 'Kezdő dátum',
  'integrations.field.sinceHint': 'Ennél régebbi issue-kat és worklogokat nem hozunk át.',
  'integrations.field.overwatch': 'Napi egyeztetés ideje',
  'integrations.field.conflict': 'Ütközésnél melyik oldal nyer',
  'integrations.oauthNote':
    'A böngésző átirányít az Atlassianhoz, ott hagyod jóvá a hozzáférést. Token nem kerül a munkaterületbe; a kapcsolat azzal a fiókkal olvas, amelyik jóváhagyta.',
  'integrations.oauth.saveFirst':
    'Előbb mentsd a kapcsolatot, utána tudod jóváhagyni az Atlassiannál.',
  'integrations.oauth.connect': 'Jóváhagyás az Atlassiannál',
  'integrations.oauth.connecting': 'Átirányítás…',
  'integrations.oauth.reconnect': 'Újra jóváhagyás',
  'integrations.oauth.connected': 'Összekötve: {account}',
  'integrations.oauth.webhookNote':
    'OAuth-kapcsolat nem tud admin webhookot regisztrálni: a változások lekérdezéssel és a napi egyeztetéssel érkeznek, vagy egy Jira-admin kézzel állítja be a webhookot.',

  'integrations.statuses.lede':
    'Kösd a távoli státuszokat a munkaterület státuszaihoz. A párosítatlan státuszban álló issue-k kimaradnak a szinkronból.',
  'integrations.statuses.none': 'nem szinkronizál',
  /* ── C39 · a Jira státusz már listából választható, nem kézzel írandó. ── */
  'integrations.statuses.pick': 'Válassz státuszt…',
  'integrations.statuses.emptyKnown':
    'Még nincs megfeleltetés. A Jira státuszait egy kattintással behozhatod.',
  'integrations.statuses.addAll': 'A Jira státuszainak behozása ({count})',
  'integrations.statuses.cat.new': 'nyitott',
  'integrations.statuses.cat.doing': 'folyamatban',
  'integrations.statuses.cat.done': 'lezárt',
  'integrations.statuses.cat.unknown': 'egyéb',
  'integrations.statuses.note': 'ennyi párosítatlan státusz: {count}',
  'integrations.statuses.default': 'Alapértelmezett',
  'integrations.statuses.defaultHint':
    'Több távoli státusz is mutathat ugyanarra a Forecastify státuszra. Ez az, amelyiket kifelé írjuk, amikor itt változik az állapot.',
  'integrations.statuses.add': 'Státusz hozzáadása',
  'integrations.statuses.remote': 'Távoli státusz neve',
  'integrations.statuses.empty':
    'Még nincs státusz felvéve. Add hozzá azokat, amelyeket a távoli rendszer használ.',

  /* ── J-5 · a prioritás-leképezés, a státuszok párja. ── */
  'integrations.priorities.lede':
    'Kösd a távoli prioritásokat a munkaterület prioritásaihoz. A párosítatlan prioritású issue-k bejönnek, de a Forecastify saját prioritását kapják.',
  'integrations.priorities.none': 'nem vezérli a miénket',
  'integrations.priorities.pick': 'Válassz prioritást…',
  'integrations.priorities.emptyKnown':
    'Még nincs megfeleltetés. A távoli rendszer prioritásait egy kattintással behozhatod.',
  'integrations.priorities.addAll': 'A távoli prioritások behozása ({count})',
  'integrations.priorities.note': 'ennyi párosítatlan prioritás: {count}',
  'integrations.priorities.default': 'Alapértelmezett',
  'integrations.priorities.defaultHint':
    'Több távoli prioritás is mutathat ugyanarra a Forecastify prioritásra. Ez az, amelyiket kifelé írjuk, amikor itt változik a prioritás.',
  'integrations.priorities.add': 'Prioritás hozzáadása',
  'integrations.priorities.remote': 'Távoli prioritás neve',
  'integrations.priorities.empty':
    'Még nincs prioritás felvéve. Add hozzá azokat, amelyeket a távoli rendszer használ.',

  'integrations.review.name': 'Példány',
  'integrations.review.url': 'Cím',
  'integrations.review.auth': 'Hitelesítés',
  'integrations.review.query': 'Szűrő',
  'integrations.review.queryEmpty': 'nincs · minden issue',
  'integrations.review.statuses': 'Státuszok',
  'integrations.review.priorities': 'Prioritások',
  'integrations.review.people': 'Emberek',
  'integrations.review.rules': 'Szabályok',
  'integrations.review.rulesEmpty': 'nincs bekapcsolt szabály',
  'integrations.review.schedule': 'Ütemezés',
  'integrations.review.note':
    'Az első futás a kezdő dátumtól kezdve tölt be. Nagy projektnél ez pár percig tarthat – a Szinkronok fülön követheted.',

  'integrations.error.name': 'Adj nevet a példánynak – több példány közül ez különbözteti meg.',
  'integrations.error.url': 'A cím teljes URL legyen, https-sel.',
  'integrations.error.email': 'A Jira API tokenhez a felhasználó e-mail címe is kell.',
  'integrations.error.token': 'Írd be a tokent.',
  'integrations.error.duplicate': 'Ilyen nevű példány már van.',
  'integrations.run.discoveredProjects': '{detail} projekt látható a kapcsolattal.',
  'integrations.run.discoveredPeople': '{detail} felhasználó a távoli rendszerben.',
  'integrations.run.projectUnchanged': '{detail} issue változatlan – nem volt mit behozni.',
  'integrations.run.awaitingReread': '{detail} feladat a következő teljes szinkronra vár.',
  'integrations.run.webhookIgnored': 'Figyelmen kívül hagyott esemény: {detail}',
  'integrations.run.webhookDuplicate': 'Ismételt kézbesítés: {detail}',
  'integrations.error.stale':
    'Időközben valaki más módosította ezt a példányt. Frissítettük – nézd át és mentsd újra.',
  'integrations.error.statusDefault':
    'Egy Forecastify-státuszra több Jira-státusz mutat. Jelöld meg köztük az alapértelmezettet – azt írjuk ki a Jirába, amikor itt változik a státusz.',
  'integrations.error.priorityDefault':
    'Egy Forecastify-prioritásra több távoli prioritás mutat. Jelöld meg köztük az alapértelmezettet – azt írjuk ki, amikor itt változik a prioritás.',
  'integrations.error.validation': 'A beállítás így nem menthető. Nézd át a megjelölt sorokat.',
  'integrations.toast.created': 'Integráció bekötve',
  'integrations.toast.saved': 'Integráció mentve',
  'integrations.toast.deleted': 'Példány törölve',
  'integrations.toast.deletedBody': 'A már átszinkronizált feladatok és órák megmaradnak.',
  'integrations.toast.paused': 'Integráció szüneteltetve',
  'integrations.toast.resumed': 'Integráció aktiválva',
  'integrations.toast.queued': 'Szinkron elindítva',
  'integrations.toast.queuedBody': 'A Szinkronok lapon követhető.',
  'integrations.toast.alreadyRunning': 'Már fut egy ilyen szinkron',
  /* ── C42 · munkaterületek: a váltó és az új munkaterület ─────────────────────────────────── */
  'workspaces.create': 'Új munkaterület',
  'workspaces.createHint':
    'Saját feladatokkal, emberekkel és szerződésekkel. A mostani munkaterület nem változik.',
  'workspaces.name': 'Munkaterület neve',
  'workspaces.namePlaceholder': 'Pl. Ügyfélprojektek',
  'workspaces.slugPreview': 'Címe: {slug}',
  'workspaces.slugPending': 'A címet a névből képezzük.',
  'workspaces.icon': 'Ikon',
  'workspaces.pickIcon': 'Kép választása',
  'workspaces.iconHint': 'PNG, JPEG vagy WebP, legfeljebb 2 MB. Enélkül a név kezdőbetűje látszik.',
  'workspaces.timezone': 'Időzóna',
  'workspaces.error.name': 'Adj nevet a munkaterületnek.',
  'workspaces.error.slug': 'Ebből a névből nem képezhető cím. Írj bele betűket vagy számokat.',
  'workspaces.error.iconSize': 'A kép legfeljebb 2 MB lehet.',

  /* ── R-38 · a cím olyan workspace-t nevez meg, amelyben ez a felhasználó nincs benne ────── */
  /* ⚠️⚠️ **EGY SZÖVEG KÉT OKRA, SZÁNDÉKOSAN.** Lehet, hogy a workspace nem létezik, és lehet,
     hogy létezik, csak nem az övé. A CLAUDE.md 5. szabálya szerint a létezés maga is információ —
     a „nincs hozzáférésed ehhez a workspace-hez" megerősítené, hogy létezik. */
  'workspace.unknown.title': 'Ez a cím nem vezet sehova',
  'workspace.unknown.body':
    'Nincs ilyen workspace, vagy nem tagja vagy. Ellenőrizd a linket, vagy térj vissza oda, ahol dolgoztál.',
  'workspace.unknown.back': 'Vissza: {workspace}',

  /* ── C65 · the project Dashboard, in hours ─────────────────────────────── */
  'effort.workdayShort': '{count} mn',
  'effort.unit.workday': 'munkanap',
  'effort.unit.day': 'nap',
  'effort.slip.label': 'Csúszás egysége',
  'effort.slip.workday': 'munkanap',
  'effort.slip.calendar': 'naptári nap',
  'effort.eac.cpiHint': 'keret / CPI – a mostani hatékonyság végig megmarad',
  'effort.eac.etcHint': 'tény + (keret − teljesített) – a maradék a becslés szerint készül',
  'effort.kpi.periodFrame': 'IDŐSZAK KERETE',
  'effort.kpi.periodFrameFormula': '{total} / {count} időszak',
  'effort.kpi.periodSpent': 'IDŐSZAK TÉNY / KERET',
  'effort.kpi.periodElapsed': 'IDŐSZAK ELTELT',
  'effort.kpi.rate': 'NAPI FELHASZNÁLÁS',
  'effort.kpi.overrun': 'ELŐREJELZETT TÚLLÉPÉS',
  'effort.kpi.billable': 'ELSZÁMOLHATÓ ÓRA',
  'effort.kpi.billableFormula': 'kereten felüli sortételek',
  'effort.kpi.contractFrame': 'SZERZŐDÉS KERETE',
  'effort.kpi.contractFrameRetainer': 'Σ retainer sortétel becslés',
  'effort.kpi.contractFrameAll': 'Σ sortétel becslés',
  'effort.kpi.spent': 'TÉNY / KERET',
  'effort.kpi.elapsed': 'ELTELT IDŐ',
  'effort.kpi.planned': 'TERV',
  'effort.kpi.plannedFormula': 'Σ allokáció',
  'effort.kpi.carryOver': 'ÁTVITEL',
  'effort.kpi.carryOverFormula': 'Σ (keret − tény) lezárt',
  'effort.kpi.frame': 'KERET',
  'effort.kpi.frameFormula': 'Σ becslés',
  'effort.kpi.done': 'KÉSZÜLTSÉG',
  'effort.kpi.cpi': 'CPI',
  'effort.kpi.slip': 'CSÚSZÁS',
  'effort.kpi.slipFormula': 'előrejelzett befejezés − határidő',
  'effort.panel.frame': 'TERV / TÉNY / ELŐREJELZÉS',
  'effort.panel.series': 'SOROZAT',
  'effort.panel.schedule': 'ÜTEM',
  'effort.bar.frame': 'KERET',
  'effort.bar.plan': 'TERV',
  'effort.bar.actual': 'TÉNY',
  'effort.bar.projected': 'ELŐREJELZÉS',
  'effort.bar.closedFrame': 'LEZÁRT IDŐSZAKOK KERETE',
  'effort.bar.earned': 'TELJESÍTETT',
  'effort.bar.eacTrend': 'TRENDTARTÓ EAC',
  'effort.bar.eacPlan': 'TERVTARTÓ EAC',
  'effort.foot.rate': 'Napi felhasználás (RATE)',
  'effort.foot.allowedRate': 'Engedett napi ütem (RATE_OK)',
  'effort.foot.rateDelta': 'Ráta-eltérés (ΔRATE)',
  'effort.foot.remaining': 'Maradék keret (R)',
  'effort.foot.remainingFrame': 'Maradék keret',
  'effort.foot.burnOut': 'Keret kifutása',
  'effort.foot.workdaysLeft': 'Hátralévő munkanap',
  'effort.foot.periodBudget': 'Időszaki keret (P)',
  'effort.foot.averageUsage': 'Átlagos felhasználás',
  'effort.foot.periodsLeft': 'Hátralévő időszak',
  'effort.foot.overtime': 'Elszámolt túlóra (OT)',
  'effort.foot.etc': 'Hátralévő munka (ETC)',
  'effort.foot.vac': 'Keret-eltérés (VAC)',
  'effort.foot.tcpi': 'Szükséges hatékonyság (TCPI)',
  'effort.foot.spi': 'Ütemhatékonyság (SPI)',
  'effort.foot.cv': 'Költség-eltérés (CV)',
  'effort.foot.sv': 'Ütem-eltérés (SV)',
  'effort.list.closedPeriods': 'LEZÁRT IDŐSZAK',
  'effort.list.averageUsage': 'ÁTLAGOS FELHASZNÁLÁS',
  'effort.list.averageShare': 'ÁTLAG / KERET',
  'effort.list.carryOver': 'ÁTVITEL',
  'effort.list.periodsLeft': 'HÁTRALÉVŐ IDŐSZAK',
  'effort.list.nextPlan': 'KÖVETKEZŐ IDŐSZAK TERVE',
  'effort.list.nextPlanVariance': 'KÖVETKEZŐ TERV-ELTÉRÉS',
  'effort.list.billable': 'ELSZÁMOLHATÓ ÓRA',
  'effort.list.deadline': 'HATÁRIDŐ',
  'effort.list.forecastFinish': 'ELŐREJELZETT BEFEJEZÉS',
  'effort.list.workdaysLeft': 'HÁTRALÉVŐ MUNKANAP',
  'effort.list.slip': 'CSÚSZÁS',
  'effort.list.lateTasks': 'LEJÁRT FELADAT',
  'effort.list.lateHours': 'LEJÁRT HÁTRALÉVŐ',
  'effort.list.openTaskAge': 'NYITOTT FELADAT KORA',
  'effort.tip.panel.name': 'Terv / tény / előrejelzés',
  'effort.tip.panel.what':
    'A hatókör órái egy skálán: mit terveztünk, mi valósult meg, és hol zárul.',
  'effort.tip.panel.note': 'A függőleges vonal a keret – a fölé nyúló sáv túllépés.',
  'effort.tip.series.name': 'Sorozat – az időszakok összesítője',
  'effort.tip.series.what': 'A szerződés lezárt és hátralévő időszakainak összefoglalása.',
  'effort.tip.schedule.name': 'Ütem – időbeli állás',
  'effort.tip.schedule.what': 'A hatókör határideje, előrejelzett befejezése és lejárt munkája.',
  'effort.tip.periodFrame.name': 'P: időszaki keret',
  'effort.tip.periodFrame.what':
    'Az egy időszakra jutó óra a hatókör keretéből – csak a retainer sortételekből.',
  'effort.tip.periodFrame.calc': 'retainer becslés / érintett időszakok száma',
  'effort.tip.periodFrame.note':
    'Minden időszak kerete ugyanannyi. A kereten felüli (elszámolható) munka nem növeli és nem fogyasztja a keretet.',
  'effort.tip.periodSpent.name': 'U / P: tény per időszaki keret',
  'effort.tip.periodSpent.what': 'Az időszakban logolt órák a keret arányában.',
  'effort.tip.periodSpent.calc': 'időszak tény / időszak kerete',
  'effort.tip.periodSpent.note':
    '100% felett az időszak túllépte a keretet. A halvány sáv az eltelt munkanapok aránya: ha a tény-sáv hosszabb, a keret az idő előtt fogy.',
  'effort.tip.elapsed.name': 'Eltelt munkanapok aránya (mn = munkanap)',
  'effort.tip.elapsed.calc': 'eltelt munkanap / az összes munkanap',
  'effort.tip.periodElapsed.what': 'Hol tart az időszak időben.',
  'effort.tip.periodElapsed.note':
    'Ha nagyobb, mint a tény/keret arány, még van tartalék az időszakban.',
  'effort.tip.contractElapsed.what': 'Hol tart a szerződés időben.',
  'effort.tip.contractElapsed.note':
    'Ha az eltelt idő nagyobb a tény/keret aránynál, a keret lassabban fogy, mint ahogy az idő telik.',
  'effort.tip.plainElapsed.what': 'Hol tart a hatókör időben.',
  'effort.tip.plainElapsed.note':
    'Ha nagyobb a készültségnél, a munka az időhöz képest le van maradva.',
  'effort.tip.rate.name': 'RATE: napi felhasználás',
  'effort.tip.rate.what': 'Átlagosan mennyi óra fogy a keretből egy munkanapon.',
  'effort.tip.rate.calc': 'időszak tény / eltelt munkanap',
  'effort.tip.rate.note':
    'Az engedett napi ütem (RATE_OK) = keret / összes munkanap. Ha a napi felhasználás ennél nagyobb, az időszak túllépéssel zár.',
  'effort.tip.allowedRate.name': 'RATE_OK: engedett napi ütem',
  'effort.tip.allowedRate.what': 'Ez a napi ütem tartja pontosan a keretet.',
  'effort.tip.allowedRate.calc': 'időszak kerete / az időszak összes munkanapja',
  'effort.tip.rateDelta.name': 'ΔRATE: ráta-eltérés',
  'effort.tip.rateDelta.what': 'A valós és az engedett napi ütem különbsége.',
  'effort.tip.rateDelta.calc': 'napi felhasználás − engedett napi ütem',
  'effort.tip.rateDelta.note':
    'Pozitív: naponta ennyivel többet fogyasztunk a keretből, mint amit elbír.',
  'effort.tip.remaining.name': 'R: maradék keret',
  'effort.tip.remaining.what': 'Mennyi óra van még az időszak keretében.',
  'effort.tip.remaining.calc': 'időszak kerete − időszak tény',
  'effort.tip.remaining.note': 'Negatív érték: a keretet már túlléptük.',
  'effort.tip.contractRemaining.what': 'Mennyi óra van még a szerződés teljes keretében.',
  'effort.tip.contractRemaining.calc': 'szerződés kerete − összes tény',
  'effort.tip.burnOut.name': 'Keret kifutása (burn-out dátum)',
  'effort.tip.burnOut.what': 'Az a nap, amelyre a mostani ütem mellett elfogy a maradék keret.',
  'effort.tip.burnOut.calc': 'maradék keret / napi felhasználás, munkanapokban előre számolva',
  'effort.tip.burnOut.note': 'Ha az időszak vége előtt van, az időszak túllépéssel zár.',
  'effort.tip.workdaysLeft.name': 'Hátralévő munkanap',
  'effort.tip.workdaysLeft.calc': 'ma → a hatókör vége, munkaszünetek nélkül',
  'effort.tip.periodWorkdaysLeft.what': 'Az időszakból hátralévő munkanapok száma.',
  'effort.tip.contractWorkdaysLeft.what': 'A szerződés végéig hátralévő munkanapok száma.',
  'effort.tip.deadlineWorkdaysLeft.what': 'A határidőig hátralévő munkanapok száma.',
  'effort.tip.overrun.name': 'PROJ − P: előrejelzett végösszeg mínusz keret',
  'effort.tip.overrun.what': 'Mennyivel lép túl az időszak a kereten, ha a mostani ütem marad.',
  'effort.tip.overrun.calc': 'PROJ − P, ahol PROJ = RATE × az időszak összes munkanapja',
  'effort.tip.overrun.note': 'Pozitív: kifutunk a keretből; nulla vagy negatív: belül maradunk.',
  'effort.tip.projected.name': 'PROJ: előrejelzett végösszeg',
  'effort.tip.projected.what':
    'Mennyi lesz az időszak végére a tény, ha a mostani napi ütem marad.',
  'effort.tip.projected.calc': 'napi felhasználás × az időszak összes munkanapja',
  'effort.tip.projected.note': 'A keret fölé nyúló érték túllépést jelez.',
  'effort.tip.bac.name': 'BAC: teljes keret',
  'effort.tip.contractFrame.what':
    'A szerződés teljes vállalása órában – csak a retainer sortételekből.',
  'effort.tip.contractFrame.calc': 'Σ retainer sortétel becslése',
  'effort.tip.contractFrame.note':
    'A kereten felüli (elszámolható) sortételek nem visznek keretet; azok órája külön mutatóban látszik.',
  'effort.tip.frame.what': 'A hatókörbe eső munka teljes vállalása órában.',
  'effort.tip.frame.calc': 'Σ sortétel becslése a hatókörben',
  'effort.tip.spent.name': 'AC / BAC: tény per keret',
  'effort.tip.spent.what': 'Az eddig logolt órák a keret arányában.',
  'effort.tip.spent.calc': 'tény / keret',
  'effort.tip.spent.note': '100% felett a hatókör túllépte a keretet.',
  'effort.tip.contractSpent.what': 'A szerződésre eddig logolt órák a teljes keret arányában.',
  'effort.tip.contractSpent.calc': 'összes tény / teljes keret',
  'effort.tip.contractSpent.note':
    '100% felett a szerződés túllépte a keretét. A halvány sáv az eltelt munkanapok aránya.',
  'effort.tip.done.name': 'EV / BAC: készültség',
  'effort.tip.done.what': 'Mennyi munka készült el óraértékben, a kerethez mérve.',
  'effort.tip.done.calc': 'EV / BAC, ahol EV = Σ feladat becslés × státuszsúly',
  'effort.tip.done.note':
    'Státuszsúlyok: backlog 0 · következik 0 · folyamatban 0,5 · ellenőrzés alatt 0,8 · kész 1.',
  'effort.tip.plan.name': 'PLAN: allokált erőforrás',
  'effort.tip.plan.what': 'Mennyi órát terveztek be emberekre a szerződés sortételeire.',
  'effort.tip.plan.calc': 'Σ allokáció a szerződés teljes időtartamára',
  'effort.tip.plan.note': 'A kereten túli terv már a tervezés szintjén túllépést jelent.',
  'effort.tip.periodPlan.calc': 'Σ allokáció az időszak napjaira',
  'effort.tip.plainPlan.what': 'A hatókörre betervezett órák emberenként összegezve.',
  'effort.tip.plainPlan.calc': 'Σ allokáció a hatókör napjaira',
  'effort.tip.actual.name': 'AC: tény',
  'effort.tip.actual.what': 'Az időszakban rögzített órák.',
  'effort.tip.actual.calc': 'Σ időlog a szerződés feladatain',
  'effort.tip.periodActual.calc': 'Σ időlog az időszak napjain',
  'effort.tip.contractActual.what': 'A szerződésre logolt összes óra.',
  'effort.tip.plainActual.what': 'A hatókörben logolt órák.',
  'effort.tip.bar.frameNote': 'A többi sáv ehhez mérve olvasandó.',
  'effort.tip.bar.planNote': 'A százalék az időszak keretéhez mért arány.',
  'effort.tip.bar.planTotalNote': 'A százalék a teljes kerethez mért arány.',
  'effort.tip.bar.planScopeNote': 'A százalék a kerethez mért arány.',
  'effort.tip.bar.actualNote': 'A százalék az időszak keretéhez mért arány; 100% felett túllépés.',
  'effort.tip.bar.actualTotalNote':
    'A százalék a teljes kerethez mért arány; 100% felett túllépés.',
  'effort.tip.bar.actualScopeNote': 'A százalék a kerethez mért arány; 100% felett túllépés.',
  'effort.tip.closedFrame.name': 'Lezárt időszakok kerete',
  'effort.tip.closedFrame.what': 'Eddig ennyi keret nyílt meg a szerződésen.',
  'effort.tip.closedFrame.calc': 'Σ időszaki keret a lezárt időszakokra',
  'effort.tip.closedFrame.note':
    'Ha a tény ennél nagyobb, a szerződés az ütemhez képest előre fogyasztja a keretet.',
  'effort.tip.ev.name': 'EV: teljesített érték',
  'effort.tip.ev.what': 'Az elkészült munka óraértéke.',
  'effort.tip.ev.calc': 'Σ feladat becslés × státuszsúly',
  'effort.tip.ev.note':
    'Ha a tény nagyobb a teljesítettnél, több óra ment el, mint amennyi munka elkészült.',
  'effort.tip.eacCpi.name': 'EAC: várható végösszeg (trendtartó)',
  'effort.tip.eacCpi.what':
    'Mennyi órába fog kerülni a hatókör, ha a mostani hatékonyság végig megmarad.',
  'effort.tip.eacCpi.note':
    'A konzervatívabb várakozás: az eddigi eltérés arányosan továbbél a hátralévő munkán is. A kereten túli érték túllépést jelez.',
  'effort.tip.eacEtc.name': 'EAC: várható végösszeg (tervtartó)',
  'effort.tip.eacEtc.what':
    'Mennyi órába fog kerülni a hatókör, ha a hátralévő munka a becslés szerint készül el.',
  'effort.tip.eacEtc.note':
    'Az optimistább várakozás: az eddigi eltérést egyszeri eseménynek tekinti. A képlet a sáv végén átállítható.',
  'effort.tip.cpi.name': 'CPI: költséghatékonyság',
  'effort.tip.cpi.what': 'Mennyi elkészült munkát kapunk egy ledolgozott óráért.',
  'effort.tip.cpi.calc': 'EV / AC (teljesített érték / tényóra)',
  'effort.tip.cpi.note':
    '1 = terv szerint. 1 alatt drágábban készül a munka a becslésnél (0,92 alatt kiemelve); 1 fölött hatékonyabban.',
  'effort.tip.etc.name': 'ETC: hátralévő munka',
  'effort.tip.etc.what': 'Még ennyi óra kell a hatókör befejezéséhez.',
  'effort.tip.vac.name': 'VAC: keret-eltérés',
  'effort.tip.vac.what': 'Várhatóan mennyivel tér el a végösszeg a kerettől.',
  'effort.tip.vac.note': 'Negatív: a hatókör a keretnél több órába kerül.',
  'effort.tip.tcpi.name': 'TCPI: szükséges hatékonyság',
  'effort.tip.tcpi.what':
    'Milyen hatékonysággal kell a hátralévő munkát elvégezni, hogy a keret kitartson.',
  'effort.tip.tcpi.note':
    '1 alatt a keret a mostani ütemmel is tartható; 1 fölött csak a mostaninál jobb hatékonysággal.',
  'effort.tip.spi.name': 'SPI: ütemhatékonyság',
  'effort.tip.spi.what': 'A teljesített munka az időarányos tervhez mérve.',
  'effort.tip.spi.note': '1 = terv szerint; 1 alatt lassabban készül a munka a tervnél.',
  'effort.tip.cv.name': 'CV: költség-eltérés',
  'effort.tip.cv.what': 'Az elkészült munka és a ráfordított idő különbsége órában.',
  'effort.tip.cv.note': 'Negatív: több órát logoltunk, mint amennyi munka elkészült.',
  'effort.tip.sv.name': 'SV: ütem-eltérés',
  'effort.tip.sv.what': 'Az elkészült munka és a mára ütemezett terv különbsége órában.',
  'effort.tip.sv.note': 'Negatív: kevesebb munka készült el, mint amennyit a terv mára előírt.',
  'effort.tip.overtime.name': 'OT: elszámolt túlóra',
  'effort.tip.overtime.what': 'A jóváhagyott túlóra óraértéke a szorzóval.',
  'effort.tip.overtime.calc': 'Σ (túlóra óra × szorzó) a jóváhagyott kérelmekre',
  'effort.tip.billable.name': 'Elszámolható óra',
  'effort.tip.billable.what': 'A kereten felüli, külön számlázott munka órái.',
  'effort.tip.billable.calc': 'Σ időlog az elszámolható hatókörű sortételeken',
  'effort.tip.billable.note':
    'A keret számokat és ezt szándékosan külön mérjük: a keret túllépését nem fedi el az elszámolható munka.',
  'effort.tip.closedPeriods.name': 'Lezárt időszakok száma',
  'effort.tip.closedPeriods.what': 'Hány időszak zárult már le a szerződésen.',
  'effort.tip.closedPeriods.calc': 'ahol az időszak vége korábbi, mint ma',
  'effort.tip.averageUsage.name': 'Átlagos felhasználás',
  'effort.tip.averageUsage.what': 'Átlagosan mennyi óra fogyott egy lezárt időszakban.',
  'effort.tip.averageUsage.calc': 'Σ lezárt időszak tény / lezárt időszakok száma',
  'effort.tip.averageUsage.note': 'Az időszaki keret fölötti átlag rendszeres túllépést jelent.',
  'effort.tip.averageShare.name': 'Átlag per keret',
  'effort.tip.averageShare.what': 'Az átlagos felhasználás az átlagos időszaki keret arányában.',
  'effort.tip.averageShare.calc': 'átlagos tény / átlagos időszaki keret',
  'effort.tip.averageShare.note': '100% felett a szerződés átlagosan túllépi az időszaki keretet.',
  'effort.tip.carryOver.name': 'Átvitel (carry-over)',
  'effort.tip.carryOver.what':
    'A lezárt időszakokban fel nem használt – vagy túllépett – keret összege.',
  'effort.tip.carryOver.calc': 'Σ (időszak kerete − időszak tény) a lezárt időszakokra',
  'effort.tip.carryOver.note': 'Pozitív: maradt fel keret; negatív: összességében túllépés.',
  'effort.tip.periodsLeft.name': 'Hátralévő időszak',
  'effort.tip.periodsLeft.what': 'Az aktív és a még el nem kezdődött időszakok száma.',
  'effort.tip.periodsLeft.calc': 'aktív + tervezett időszakok',
  'effort.tip.nextPlan.name': 'Következő időszak terve',
  'effort.tip.nextPlan.what': 'Mennyi órát terveztek be a következő időszakra.',
  'effort.tip.nextPlan.calc': 'Σ allokáció a következő időszak napjaira',
  'effort.tip.nextPlanVariance.name': 'Következő terv-eltérés',
  'effort.tip.nextPlanVariance.what': 'A következő időszak terve és kerete közötti különbség.',
  'effort.tip.nextPlanVariance.calc': 'következő időszak terve − kerete',
  'effort.tip.nextPlanVariance.note':
    'Pozitív: már a terv is túllépi a keretet – érdemes újratervezni.',
  'effort.tip.deadline.name': 'Határidő',
  'effort.tip.deadline.what': 'A hatókör vége: a szerződés vagy a sortétel záró dátuma.',
  'effort.tip.forecastFinish.name': 'FF: előrejelzett befejezés',
  'effort.tip.forecastFinish.what':
    'Mikorra készül el a hátralévő munka a betervezett kapacitással.',
  'effort.tip.forecastFinish.calc':
    'a hátralévő munkát napról napra levonjuk az allokált kapacitásból; ha az elfogy, az átlagos napi tervvel számolunk',
  'effort.tip.forecastFinish.note': 'Ha túlfut a határidőn, piros – ez a Csúszás alapja.',
  'effort.tip.slip.name': 'Csúszás',
  'effort.tip.slip.what': 'Mennyivel készül el a munka a határidő után.',
  'effort.tip.slip.calc': 'előrejelzett befejezés − határidő',
  'effort.tip.slip.note':
    'Pozitív: késés; negatív: idő előtti befejezés. A mértékegység munkanap és naptári nap között váltható.',
  'effort.tip.lateTasks.name': 'Lejárt feladat',
  'effort.tip.lateTasks.what': 'Nyitott feladatok, amelyek határideje már elmúlt.',
  'effort.tip.lateTasks.calc': 'ahol határidő < ma és a státusz nem kész',
  'effort.tip.lateHours.name': 'Lejárt hátralévő munka',
  'effort.tip.lateHours.what': 'A lejárt feladatokból még el nem készült óraérték.',
  'effort.tip.lateHours.calc': 'Σ becslés × (1 − státuszsúly) a lejárt feladatokra',
  'effort.tip.lateHours.note': 'Ez a munka már biztosan átcsúszik a határidőn.',
  'effort.tip.openTaskAge.name': 'Nyitott feladatok átlagos kora',
  'effort.tip.openTaskAge.what': 'Mennyi ideje futnak átlagosan a még nyitott feladatok.',
  'effort.tip.openTaskAge.calc': 'Σ (kezdés → ma munkanapjai) / nyitott feladatok száma',
  'effort.tip.openTaskAge.note': 'A növekvő érték feltorlódást jelez.',
  'effort.chart.empty': 'Ehhez a hatókörhöz nincs megjeleníthető adat.',
  'effort.chart.title': 'TERV / TÉNY / ELŐREJELZÉS',
  'effort.burn.downTitle': 'Burn-down – hátralévő munka',
  'effort.burn.upTitle': 'Burn-up – elvégzett munka',
  'effort.burn.scope': 'Hatókör',
  'effort.burn.frame': 'Keret',
  'effort.burn.plan': 'Terv',
  'effort.burn.done': 'Teljesített',
  'effort.burn.used': 'Felhasznált',
  'effort.burn.remaining': 'Hátralévő',
  'effort.burn.plannedRemaining': 'Terv szerint hátralévő',
  'effort.burn.ideal': 'Ideális',
  'effort.burn.forecast': 'Előrejelzés',
  'effort.burn.today': 'Ma',
  'effort.burn.ahead': 'Még előttünk – csak a terv és az előrejelzés',
  'effort.burn.historyFrom':
    'A(z) {date} előtti állapot a legkorábbi rögzített változásból becsült.',
  'effort.burn.upWhat':
    'A költségvetett óraszám (hatókör), a terv (allokáció), az egyenletes ütem (ideális) és az elvégzett munka (teljesített érték) az idő függvényében. Ha a teljesített a terv alatt van, a munka késésben van.',
  'effort.burn.upWhatFrame':
    'A kiválasztott időszakok kerete, a terv (allokáció) és a keretet felhasználó órák az idő függvényében.',
  'effort.burn.upCalc':
    'Hatókör = a sortételek becsült óraszáma (BAC) · Teljesített = hatókör × készültség, ahol készültség = a feladatok becslés × státuszsúly összege ÷ becslésük összege (folyamatban 0,5, ellenőrzés 0,8, kész 1) · Terv = kumulált allokáció · Ideális = egyenletes ütem nullától a hatókörig',
  'effort.burn.downWhat':
    'Mennyi munka van még hátra, a terv és az ideális ütem szerint, és hogy a mostani tempóval mikor fogy el.',
  'effort.burn.downCalc':
    'Hátralévő = hatókör − teljesített · Ideális = a hatókörtől egyenesen nulláig a munkanapokon · Előrejelzés = a mai hátralévő, csökkentve az eddigi napi teljesítési ütemmel',
  'effort.chart.titleCumulative': 'TERV / TÉNY / ELŐREJELZÉS · KUMULÁLT',
  'effort.chart.day': 'Nap',
  'effort.chart.week': 'Hét',
  'effort.chart.month': 'Hónap',
  'effort.chart.perPeriod': 'Periódus',
  'effort.chart.cumulative': 'Kumulált',
  'effort.chart.current': 'most',
  'effort.chart.row.frame': 'KERET',
  'effort.chart.row.plan': 'TERV',
  'effort.chart.row.actual': 'TÉNY',
  'effort.chart.row.forecast': 'ELŐREJELZÉS',
  'effort.chart.row.overtime': 'TÚLÓRA',
  'effort.table.periods': 'IDŐSZAKOK',
  'effort.table.periodsNote': '{count} időszak · {over} túllépő',
  'effort.table.lineItems': 'SORTÉTELEK',
  'effort.table.lineItemsInPeriod': 'SORTÉTELEK AZ IDŐSZAKBAN',
  'effort.table.tasks': 'FELADATOK',
  'effort.table.noTasks': 'Ehhez a hatókörhöz nincs feladat.',
  /** ⚠️ Round 5 item 8 — the two ledgers became searchable, filterable tables. */
  'effort.tools.search': 'Keresés',
  'effort.tools.noMatch': 'Nincs találat a szűrőkre.',
  'effort.table.total': 'Összesen',
  'effort.state.past': 'Lezárt',
  'effort.state.current': 'Aktív',
  'effort.state.future': 'Tervezett',
  'effort.scope.retainer': 'Retainer',
  'effort.scope.billable': 'Elszámolható',
  'effort.col.period': 'IDŐSZAK',
  'effort.col.state': 'STÁTUSZ',
  'effort.col.frame': 'KERET',
  'effort.col.plan': 'TERV',
  'effort.col.actual': 'TÉNY',
  'effort.col.variance': 'ELTÉRÉS',
  'effort.col.share': 'TÉNY/KERET',
  'effort.col.overtime': 'TÚLÓRA',
  'effort.col.billable': 'ELSZ. ÓRA',
  'effort.col.lineItem': 'SORTÉTEL',
  'effort.col.billing': 'ELSZÁMOLÁS',
  'effort.col.scope': 'HATÓKÖR',
  'effort.col.earned': 'TELJESÍTETT',
  'effort.col.key': 'KULCS',
  'effort.col.task': 'FELADAT',
  'effort.col.assignee': 'FELELŐS',
  'effort.col.estimate': 'BECSLÉS',
  'effort.col.logged': 'RÖGZÍTETT',
  'effort.col.weight': 'SÚLY',
  'effort.col.due': 'HATÁRIDŐ',
  'effort.col.person': 'EMBER',
  'effort.col.planActual': 'TERV / TÉNY',
  'effort.col.utilisation': 'KIHASZNÁLTSÁG',
  'effort.col.ofFrame': 'KERETBŐL',
  'effort.panel.people': 'ERŐFORRÁS',
  /** ⚠️ Round 5 item 10 — how the people table is grouped. */
  'effort.group.label': 'Csoportosítás',
  'effort.group.person': 'Ember szerint',
  'effort.group.seniority': 'Szeneritás szerint',
  'effort.group.role': 'Szerepkör szerint',
  'effort.group.none': 'Nincs megadva',
  'effort.panel.headcount': '{count} fő',
  'effort.panel.noPeople': 'Ehhez a hatókörhöz nincs rögzített vagy tervezett idő.',
  'effort.panel.splitActual': 'RÖGZÍTETT MEGOSZLÁS',
  'effort.panel.splitPlan': 'TERV MEGOSZLÁS',
  'effort.panel.noSplit': 'Még nincs mit megosztani.',
  'effort.tip.periodIndex.name': 'Sorszám',
  'effort.tip.periodIndex.what': 'Az időszak sorszáma a szerződés ismétlődési szabálya szerint.',
  'effort.tip.periodSpan.name': 'Időszak',
  'effort.tip.periodSpan.what':
    'Az időszak kezdő és záró dátuma. A sorra kattintva a dashboard erre az időszakra szűkül.',
  'effort.tip.periodState.name': 'Státusz',
  'effort.tip.periodState.what':
    'Az időszak állapota: tervezett, aktív vagy lezárt – a dátumból származik.',
  'effort.tip.periodVariance.name': 'Eltérés',
  'effort.tip.periodVariance.what':
    'A tény és a keret különbsége. Pozitív (piros): az időszak túllépte a keretet.',
  'effort.tip.periodShare.name': 'Tény per keret',
  'effort.tip.periodShare.what': 'A rögzített órák a keret arányában; 100% felett túllépés.',
  'effort.tip.lineItemIndex.name': 'Sorszám',
  'effort.tip.lineItemIndex.what': 'A sortétel sorszáma a szerződésen.',
  'effort.tip.lineItem.name': 'Sortétel',
  'effort.tip.lineItem.what': 'A szerződés egy sortétele – ide kötődnek a feladatok és az órák.',
  'effort.tip.billing.name': 'Elszámolás típusa',
  'effort.tip.billing.what':
    'Hogyan számolunk el a sortételre logolt idővel: fix díjas, idő és anyag, vagy nem számlázható.',
  'effort.tip.scope.name': 'Hatókör',
  'effort.tip.scope.what':
    'A sortétel a keretbe tartozik (retainer), vagy kereten felüli, külön számlázott munka.',
  'effort.tip.itemFrame.name': 'Sortétel kerete',
  'effort.tip.itemFrame.what':
    'A sortétel becsléséből a hatókörre jutó rész. Elszámolható hatókörnél nem értelmezett.',
  'effort.tip.itemVariance.name': 'Eltérés',
  'effort.tip.itemVariance.what':
    'A tény és a keret különbsége a sortételen. Pozitív (piros): túllépés.',
  'effort.tip.taskKey.name': 'Feladat kulcsa',
  'effort.tip.taskKey.what': 'A feladat azonosítója (PROJEKT-sorszám).',
  'effort.tip.task.name': 'Feladat',
  'effort.tip.task.what': 'A feladat címe. A sorra kattintva megnyílik a feladat.',
  'effort.tip.assignee.name': 'Felelős',
  'effort.tip.assignee.what': 'Akire a feladat ki van osztva.',
  'effort.tip.taskStatus.name': 'Státusz',
  'effort.tip.taskStatus.what':
    'A feladat állapota – ez adja a státuszsúlyt a teljesített érték számításához.',
  'effort.tip.taskEstimate.name': 'Becslés (feladat BAC)',
  'effort.tip.taskEstimate.what': 'A feladat becsült óraszáma.',
  'effort.tip.taskLogged.name': 'Rögzített (feladat AC)',
  'effort.tip.taskLogged.what': 'A feladatra logolt órák.',
  'effort.tip.weight.name': 'Státuszsúly',
  'effort.tip.weight.what':
    'Mennyire tekintjük késznek a feladatot: backlog 0 · következik 0 · folyamatban 0,5 · ellenőrzés alatt 0,8 · kész 1.',
  'effort.tip.taskVariance.name': 'Eltérés',
  'effort.tip.taskVariance.what':
    'A ráfordított és az elkészült munka különbsége. Pozitív (piros): több óra ment el, mint amennyi munka elkészült.',
  'effort.tip.due.name': 'Határidő',
  'effort.tip.due.what': 'A feladat határideje. Lejárt, még nyitott feladatnál kiemelve.',
  'effort.tip.person.name': 'Ember',
  'effort.tip.person.what': 'Aki a hatókörben időt tervezett vagy rögzített.',
  'effort.tip.planActual.name': 'Terv / tény egymáson',
  'effort.tip.planActual.what':
    'A felső sáv a betervezett, az alsó a rögzített idő – közös skálán, hogy a túlnyúlás önmagában olvasható legyen.',
  'effort.tip.personPlan.name': 'TERV: allokált erőforrás',
  'effort.tip.personPlan.what': 'Mennyi órát terveztek be erre az emberre a hatókörben.',
  'effort.tip.personActual.name': 'TÉNY: logolt idő',
  'effort.tip.personActual.what': 'Mennyi órát rögzített az ember a hatókörben.',
  'effort.tip.personVariance.name': 'Eltérés',
  'effort.tip.personVariance.what':
    'A tény és a terv különbsége. Pozitív: többet dolgozott a rá tervezettnél.',
  'effort.tip.utilisation.name': 'Kihasználtság',
  'effort.tip.utilisation.what': 'A logolt idő a betervezett idő arányában; 108% felett kiemelve.',
  'effort.tip.ofFrame.name': 'Részesedés a keretből',
  'effort.tip.ofFrame.what':
    'Az ember órái a hatókör keretének arányában – megmutatja, kire támaszkodik a szállítás.',
  'effort.tip.split.name': 'Rögzített megoszlás',
  'effort.tip.split.what': 'Hogyan oszlik meg a hatókörben logolt idő az emberek között.',
  'effort.tip.split.calc': 'ember órái / összes logolt óra',
  'effort.tip.split.note': 'Ha egy szelet kiugróan nagy, a szállítás egy emberen áll.',
  'effort.tip.splitPlan.name': 'Terv megoszlás',
  'effort.tip.splitPlan.what': 'Hogyan oszlik meg a betervezett kapacitás az emberek között.',
  'effort.tip.splitPlan.calc': 'ember allokációja / összes allokáció',
  'effort.tip.splitPlan.note': 'Még nincs logolt óra, ezért a terv látszik.',
  'effort.pick.contract': 'Szerződés',
  'effort.pick.scope': 'Hatókör',
  'effort.pick.more': 'További műveletek',
  'effort.scope.whole': 'Teljes szerződés',
  'effort.scope.period': 'Időszak',
  'effort.action.openContract': 'Szerződés megnyitása',
  'effort.empty.contractLabel': 'SZERZŐDÉS',
  'effort.empty.noContract': 'Ehhez a projekthez még nincs szerződés',
  'effort.empty.noContractBody':
    'A dashboard egy szerződés keretét és felhasználását mutatja. Hozz létre egy szerződést, és itt megjelenik.',
  'effort.empty.lineItemLabel': 'SORTÉTEL',
  'effort.empty.noLineItem': 'A szerződésen még nincs sortétel',
  'effort.empty.noLineItemBody':
    'A keret a sortételek becsléséből áll össze. Vegyél fel sortételt a szerződésre, és itt megjelennek a számok.',
  'effort.chart.period': 'Időszak',
  'effort.col.startsOn': 'KEZDÉS',
  'effort.col.endsOn': 'VÉG',
  'effort.col.done': 'KÉSZ',
  'effort.col.slip': 'CSÚSZÁS',
  'effort.open': 'nyitott',
  /* ⚠️ ux-pass-2 — the effort registers became DataLists: filter labels, totals and empties. */
  'effort.filter.state': 'Állapot',
  'effort.filter.billing': 'Elszámolás',
  'effort.filter.scope': 'Hatókör',
  'effort.filter.startsOn': 'Kezdés',
  'effort.filter.endsOn': 'Vég',
  'effort.filter.assignee': 'Felelős',
  'effort.filter.status': 'Státusz',
  'effort.filter.category': 'Státuszcsoport',
  'effort.filter.unassigned': 'Nincs felelős',
  'effort.filter.role': 'Szerepkör',
  'effort.filter.seniority': 'Szenioritás',
  'effort.tools.noMatchBody': 'Lazíts a keresésen vagy a szűrőkön.',
  'effort.tools.noPeriods': 'Ennek a szerződésnek még nincs időszaka.',
  'effort.table.totalFiltered': 'Szűrt összesen',
  'effort.table.lateNote': '{late} lejárt',
  'effort.table.lineItemTotals':
    '{label} · keret {frame} · terv {plan} · tény {actual} · teljesített {earned} · CPI {cpi}',
  'effort.table.periodTotals':
    '{label} · keret {frame} · terv {plan} · tény {actual} · túlóra {overtime}',
  'effort.table.periodTotalsBillable':
    '{label} · keret {frame} · terv {plan} · tény {actual} · túlóra {overtime} · elsz. óra {billable}',
  'effort.tip.startsOn.name': 'Kezdés',
  'effort.tip.startsOn.what': 'A sortétel kezdő dátuma. Ha nincs sajátja, a szerződésé.',
  'effort.tip.endsOn.name': 'Vég',
  'effort.tip.endsOn.what':
    'A sortétel záró dátuma. Ha nincs megadva, „nyitott” – a szerződés végéig tart.',
  'effort.tip.doneTasks.name': 'Készültség feladatszámban',
  'effort.tip.doneTasks.what':
    'Hány feladat készült el a sortételen – kész feladat / összes feladat.',
  'effort.tip.itemSlip.name': 'Csúszás',
  'effort.tip.itemSlip.what': 'A sortétel legjobban késő nyitott feladatának késése, munkanapban.',
  /* ⚠️ U-4 — „Teljes projekt”: the overview over every contract of a project. */
  'effort.scope.project': 'Teljes projekt',
  'effort.scope.projectBody': 'A projekt összes szerződése együtt',
  'effort.col.contract': 'SZERZŐDÉS',
  'effort.tip.contract.name': 'Szerződés',
  'effort.tip.contract.what': 'Melyik szerződéshez tartozik a sor. A sorra kattintva megnyílik.',
  'effort.tip.taskLineItem.name': 'Sortétel',
  'effort.tip.taskLineItem.what':
    'A sortétel, amelyre a feladat órái mennek, alatta a szerződése. Sortétel nélküli feladat órái is beszámítanak.',
  'effort.task.noLineItem': 'Nincs sortétel',
  /* ⚠️ The people panel groups by PROJECT role; somebody with none is in this group. */
  'effort.role.none': 'Nincs projektszerepkör',
  'permissions.levels.deleteBuiltin': 'Beépített szint – nem törölhető.',
  'projects.copy.label': 'Másolás',
  'projects.copy.key': 'Kulcs másolása',
  'projects.copy.identifier': 'Azonosító másolása',
  'projects.copy.link': 'Hivatkozás másolása',
  'nav.requestsSection': 'Kérelmek',
  'title.toDecide': 'Elbírálandó kérelmek',

  /* ── C62 · Visszatekintés ────────────────────────────────────────────── */
  'tt.open': 'Visszatekintés — napi snapshot',
  'tt.loading': 'A projekt {date} napi állapotának betöltése…',
  'tt.loadingLive': 'A projekt mai állapotának betöltése…',
  'tt.slow':
    'Egy korábbi nap állapotának újraszámítása akár 15 másodpercig is eltarthat — kérlek, várj.',
  'eac.mode.budget': 'Terv',
  'eac.mode.trend': 'Trendtartó',
  'eac.mode.schedule': 'Ütemkorrigált',
  'eac.mode.resource': 'Erőforrás-alapú',
  'eac.mode.adjusted': 'Korrigált erőforrás-alapú',
  'eac.hint.budget':
    'Ami eddig elcsúszott, egyszeri volt; a hátralévő munka a becslés szerint megy. Optimista alsó határ.',
  'eac.hint.trend': 'Az eddigi hatékonyság marad. 30% készültség felett a legjobb egyedi képlet.',
  'eac.hint.schedule':
    'A csúszás költségbe fordul: túlóra, plusz ember, újranyitott munka. Felső határ.',
  'eac.hint.resource':
    'Az emberek pontosan azt logolják, amire be vannak osztva — a terv névértéke.',
  'eac.hint.adjusted':
    'Az emberek az eddigi megjelenési arányukkal logolnak (r = logolt ÷ allokált).',
  'eac.row': 'EAC · {mode}',
  'eac.reason.notStable':
    'A CPI még nem stabil: a készültség 30% alatt van. 30% felett oldódik fel.',
  'eac.reason.unestimated':
    'A tény több mint 25%-a becslés nélküli feladatokon van — becsüld meg őket.',
  'eac.reason.noCost': 'Még nincs logolt ráfordítás.',
  'eac.reason.singleItemOverPlan':
    'Az egyetlen sortétel tervezett órája a keret 1,5-szöröse felett van — ellenőrizd, mi kötődik hozzá.',
  'eac.reason.trendDisabled': 'A Trendtartóra épül, ami most nem elérhető.',
  'eac.reason.planThin':
    'Az allokáció túl vékony, a tervezett érték időarányos — allokálj legalább a keret felére.',
  'eac.reason.itemOverPlan':
    'Egy sortétel tervezett órája a keret 1,5-szöröse felett van — ez eltorzítja az ütemet.',
  'eac.reason.allocationEnded':
    'Az allokáció véget ért, de van még hátralévő munka — allokálj tovább.',
  'eac.reason.underAllocated':
    'A hátralévő allokáció a szükséges óra felénél kevesebb — allokálj többet.',
  'eac.reason.noSchedule': 'Egy betervezett kollégának nincs munkarendje — rendelj hozzá egyet.',
  'eac.reason.noCostRate': 'Egy betervezett kollégának nincs költségdíja — adj neki díjkártyát.',
  'eac.reason.resourceDisabled': 'Az Erőforrás-alapúra épül, ami most nem elérhető.',
  'eac.reason.noHistory':
    'Még nincs elég előzmény: legalább 4 hét és 40 allokált óra kell ugyanazoktól az emberektől.',
  'eac.reason.rOutOfRange':
    'A megvalósulási arány ({r}) a 0,5–1,5 sávon kívül esik — torz előzmény (placeholder allokáció vagy rossz kötés).',
  'eac.from': '{mode} alapján',
  'tt.higher': 'magasabb, mint akkor',
  'tt.lower': 'alacsonyabb, mint akkor',
  'tt.same': 'változatlan',
  'tt.openHint': 'A projekt korábbi napi állapotai',
  'tt.back': 'Vissza a jelenbe',
  'tt.backHint': 'Kilépés a visszatekintésből',
  'tt.delta': 'Eltérés a jelenhez',
  'tt.deltaHint': 'A mai érték a mutatók alatt',
  'tt.lockedAction': 'Múltbeli állapotban zárolva',
  'tt.track': 'Snapshot idővonal',
  'tt.present': 'Ma',
  'tt.prev': 'Előző snapshot',
  'tt.next': 'Következő snapshot',
  'tt.pick': 'Snapshot választása listából',
  'tt.search': 'Ugrás dátumra…',
  'tt.searchEmpty': 'Nincs ilyen nap.',
  'tt.now': 'MOST {value}',
  'tt.change.taskCreate': 'Új feladat',
  'tt.change.taskEstimate': 'Feladat-becslés módosítva',
  'tt.change.taskDelete': 'Feladat törölve',
  'tt.change.lineItemCreate': 'Új sortétel',
  'tt.change.lineItemUpdate': 'Sortétel-keret módosítva',
  'tt.change.lineItemDelete': 'Sortétel törölve',
  'tt.change.contractCreate': 'Új szerződés',
  'tt.change.contractUpdate': 'Szerződés módosítva',
  'tt.change.contractDelete': 'Szerződés törölve',
  'tt.change.allocation': 'Allokáció módosítva',
  'tt.change.other': 'A terv módosult',

  /* ── C60 · RISK ───────────────────────────────────────────────────── */
  'risk.label': 'Trust Score',
  'risk.band.ok': 'rendben',
  'risk.band.watch': 'figyelendő',
  'risk.band.risky': 'kockázatos',
  'risk.tip':
    'Trust Score {score} · {band} — 100 a legjobb, 0 a legrosszabb. A projekt Alertek fülén látszik, miből jön ki.',
  /* ⚠️ A legértékesebb mondat a három közül: a *nem tudjuk* és a *rendben van* nem ugyanaz. */
  'risk.unscanned': 'Még nem futott vizsgálat ezen a projekten, így nincs Trust Score-ja.',
  'risk.none': '–',
  'projects.column.risk': 'Trust Score',
  'projects.view.data': 'Adathiba',
  'projects.view.archived': 'Archivált projektek',
  'trackerConnection.title': 'A munkaidőd nem jut el a Jirába',
  'trackerConnection.body':
    'Ez a feladat a Jirával szinkronizál, de a saját Jira-fiókodat ({names}) még nem csatlakoztattad, ezért a rögzített idő itt marad, és nem kerül át. Amint csatlakoztatod, a várakozó idők is kimennek.',
  'trackerConnection.cta': 'Kapcsolati beállítások',
  'account.connections.warning':
    'Amíg nem kötöd össze a fiókodat, a Jirával szinkronizáló feladatokon rögzített időd itt marad, és nem kerül át a Jirába.',
  'trackerConnection.missing': 'A Jira-fiókod nincs csatlakoztatva',
  'projects.filter.risk': 'Trust Score',
  'projects.filter.risk.any': 'Minden',
  'projects.filter.risk.bad': 'Kockázatos (0–59)',
  'projects.filter.risk.warn': 'Figyelendő (60–84)',
  'projects.filter.risk.ok': 'Rendben (85+)',

  /* ── C59 · a projekt Alertek fülje ───────────────────────────────────────────────────────── */
  'project.tab.alerts': 'Alertek',
  'title.projectAlerts': 'Alertek',
  'alerts.list': 'Adatminőségi alertek',
  'alerts.search': 'Keresés alert névre, érintettre…',

  'alerts.view.active': 'Aktív',
  'alerts.view.archived': 'Archivált',
  'alerts.view.all': 'Mind',

  'alerts.filter.severity': 'Súlyosság',
  'alerts.filter.area': 'Terület',
  'alerts.filter.any': 'Mind',

  'alerts.column.severity': 'Súlyosság',
  'alerts.column.name': 'Alert',
  'alerts.column.subject': 'Érintett',
  'alerts.column.area': 'Terület',
  'alerts.column.penalty': 'Levonás',
  'alerts.column.detected': 'Észlelve',
  'alerts.column.state': 'Állapot',

  'alerts.severity.high': 'Magas',
  'alerts.severity.mid': 'Közepes',
  'alerts.severity.low': 'Alacsony',

  'alerts.area.contract': 'Szerződés',
  'alerts.area.currency': 'Pénznem',
  'alerts.area.external_cost': 'Külső költség',
  'alerts.area.invoice': 'Számla',
  'alerts.area.line_item': 'Sortétel',
  'alerts.area.member': 'Projekttag',
  'alerts.area.occurrence': 'Időszak',
  'alerts.area.rate_card': 'Díjkártya',
  'alerts.area.resource': 'Erőforrás',
  'alerts.area.schedule': 'Munkarend',
  'alerts.area.task': 'Feladat',
  'alerts.area.worklog': 'Időlog',

  /* ⚠️ Három állapot a hasábban, kettő az adatbázisban — a mellőzés P5-5 szerint külön sor. */
  'alerts.state.active': 'Aktív',
  'alerts.state.resolved': 'Megoldva',
  'alerts.state.waived': 'Mellőzve',
  'alerts.state.resolvedOn': '{date} — az anomália megszűnt',
  'alerts.state.waivedBy': '{name} mellőzte: {reason}',

  /* ⚠️ A levonás a Trust Score-ból megy, és a tetőzés típusonként értendő — a tooltip ezt mondja ki. */
  'alerts.penalty.tip': '{penalty} pont levonás a Trust Score-ból (típusonként legfeljebb {cap}).',
  'alerts.penalty.none': 'Nem számít a Trust Score-ba.',
  'alerts.penalty.value': '−{penalty}',
  'alerts.penalty.dash': '–',

  'alerts.empty.active.title': 'Nincs nyitott alert',
  'alerts.empty.active.body':
    'A projekt adatai konzisztensek — vagy a szűrő rejti el a találatokat.',
  'alerts.empty.active.action': 'Szűrő törlése',
  'alerts.empty.archived.title': 'Nincs archivált alert',
  'alerts.empty.archived.body':
    'Ide kerülnek a megszűnt és a mellőzött anomáliák — nem tűnnek el, csak kikerülnek a pontszámból.',
  'alerts.empty.archived.action': 'Aktívak mutatása',

  'alerts.action.open': 'Megnyitás',
  'alerts.action.dismiss': 'Mellőzés',
  'alerts.action.dismissAll': 'Összes mellőzése',
  'alerts.group.toggle': '{name} — {count} alert',
  'alerts.group.subjects': '{count} érintett',
  'alerts.group.active': '{count} aktív',
  'alerts.action.restore': 'Mellőzés visszavonása',
  'alerts.action.close': 'Bezárás',

  /* ⚠️ Az ugrás a lényeg: egy sor, amiből nem lehet cselekedni, panaszlista. */
  'alerts.go.member': 'Dolgozó megnyitása',
  'alerts.go.task': 'Feladat megnyitása',
  'alerts.go.line_item': 'Sortétel megnyitása',
  'alerts.go.contract': 'Szerződés megnyitása',
  'alerts.go.worklog': 'Időlog megnyitása',
  'alerts.go.external_cost': 'Külső költség megnyitása',
  'alerts.go.currency': 'Pénznemek megnyitása',
  'alerts.go.project': 'Projekt megnyitása',

  'alerts.detail.impact': 'Mit ront el',
  'alerts.detail.fix': 'Hogyan javítsd',
  'alerts.detail.subject': 'Érintett',
  'alerts.detail.detected': 'Észlelve {date}',
  'alerts.detail.detectedResolved': 'Észlelve {date} · megoldva {resolved}',
  'alerts.detail.severity': '{severity} súlyosság',
  'alerts.detail.archivedNote':
    'Ez az anomália megszűnt, ezért archiváltuk — a nyomonkövethetőség miatt a listában marad.',
  'alerts.detail.waivedNote':
    'Erre a szabályra és erre az érintettre {name} kivételt rögzített: {reason}. A vizsgálat továbbra is fut, az eredmény csak nem számít a pontszámba.',

  'alerts.dismiss.title': 'Alert mellőzése',
  'alerts.dismiss.hint':
    'A kivétel a szabályra és az érintettre szól, nem erre az egy találatra. Ha az adat javul, az alert magától megoldódik; ha visszatér, továbbra is mellőzve marad.',
  'alerts.dismiss.reason': 'Indok',
  'alerts.dismiss.reasonPlaceholder': 'Pl. belsős projekt, szándékosan nincs díjkártya',
  'alerts.dismiss.reasonRequired': 'Az indok kötelező.',
  'alerts.dismiss.submit': 'Mellőzés',
  'alerts.dismiss.submitAll': 'Az összes ilyen alert mellőzése ({count})',
  'alerts.toast.waived': 'Alert mellőzve',
  'alerts.toast.waivedAll': '{count} alert mellőzve',
  'alerts.toast.restored': 'Mellőzés visszavonva',
  'alerts.dismiss.cancel': 'Mégsem',
  'alerts.restore.title': 'Mellőzés visszavonása',
  'alerts.restore.body':
    'A kivétel megszűnik, és az alert a következő vizsgálattól újra beszámít a Trust Score-ba.',
  'alerts.restore.submit': 'Visszavonás',

  'alerts.error.denied': 'Nincs jogosultságod alertet mellőzni.',
  'alerts.error.failed': 'A mentés nem sikerült. Próbáld újra.',

  /* ── C59 · a 26 szabály neve, leírása, hatása és javítása. A nevek, hatások és javítások a
     mockup `ALERT_KIND` katalógusából valók szó szerint; a `body` a mi találatunk értékeiből
     áll össze, mert a prototípusban az per-sor kézzel írt próza volt. ── */
  'alert.member_no_rate.body':
    '{subject} nem rendelkezik érvényes díjkártyával a projekt logolt időszakára, így a ledolgozott órái 0 Ft költséggel kerülnek be minden számításba.',
  'alert.member_no_sched.body':
    '{subject} nem rendelkezik munkarenddel, ezért napi 0 órás kapacitással szerepel minden terhelési és kapacitásszámításban.',
  'alert.task_multi_line.body':
    '{subject} órái {count} sortétel-összerendelésnek is megfelelnek. Amíg mindegyik illeszkedik, ugyanaz az idő mindegyik sortétel bevételében és keretfogyásában megjelenik.',
  'alert.task_no_line.body':
    '{subject} feladatra érkezett idő, de a feladat egyetlen szerződéses sortételhez sem kapcsolódik, így az órák kimaradnak az elszámolásból.',
  'alert.task_unrouted.body':
    '{subject} a(z) {remote} hibakövető-projektből érkezett, amelyet ennek a projektnek egy sortétele céloz, de egyik sortétel szabálya sem fogadja, ezért egyik projekten sincs rajta.',
  'alert.task_orphaned.body':
    '{subject} megtartotta a sortételét és az óráit, de a szabály, amely oda sorolta, már nem fogadja.',
  'alert.li_no_budget.body':
    '{subject} sortételhez nincs megadva büdzsé, így nincs mihez mérni a rá fordított költséget.',
  'alert.contract_expired_log.body':
    '{subject} lejárata után {hours} óra érkezett, és erre az időre nincs mire számlát állítani.',
  'alert.wlog_duplicate.body':
    '{subject} — ugyanaz a {hours} óra kétszer szerepel azonos napon, azonos feladaton és azonos megjegyzéssel. Az egyik bejegyzés nagy eséllyel duplikátum.',
  'alert.inactive_member_log.body':
    '{subject} már nem aktív tagja a projektnek, mégis {hours} óra esik rá, így a költés olyan emberre terhelődik, aki nincs a csapatban.',
  'alert.ext_cost_unlinked.body':
    '{subject} költség nem kapcsolódik sortételhez vagy support időszakhoz, így csak a projekt összesenben látszik.',
  'alert.license_not_invoiced.body':
    '{subject} sortételen {periods} lezárt időszakhoz nem tartozik számla, így azok díja nem jelenik meg a hónapban.',
  'alert.task_no_assignee.body':
    '{subject} feladatnak nincs felelőse, ezért senki terhelésében nem szerepel, és a kapacitástervből kimarad.',
  'alert.over_invoiced.body':
    '{subject} sortételre {over} értékkel több számla készült, mint amennyi bevétel rá elszámolható.',
  'alert.task_no_estimate.body':
    '{subject} feladathoz nincs becslés, ezért kimarad a keretből és a teljesített értékből — a projekt jobbnak látszik a valóságnál.',
  'alert.task_no_dates.body':
    '{subject} feladatnak hiányzik a kezdése vagy a határideje, ezért nem tud lejárni, és egyetlen időbucketbe sem esik.',
  'alert.li_no_window.body':
    '{subject} sortételhez nincs kezdő és záró dátum, így nincs mihez mérni az ütemet.',
  'alert.li_no_rate_tm.body':
    '{subject} elszámolása idő és anyag alapú, de nincs rajta óradíj vagy napidíj, így a logolt órákból nem számolható bevétel.',
  'alert.fx_missing.body':
    '{subject} pénznemhez nincs árfolyam a workspace alappénzneméhez, ezért a rendszer 1:1 arányban ad össze két pénznemet.',
  'alert.alloc_no_rate.body':
    '{subject} allokálva van {hours} órára, de nincs hozzá érvényes díjkártya, ezért az allokáció csendben kimarad a tervezett költésből.',
  'alert.wlog_outside_li.body':
    '{subject} — az óra olyan napra esik, amit a hozzá tartozó sortétel ({lineItem}) időszaka nem fed.',
  'alert.wlog_no_occ.body':
    '{subject} — az óra a {lineItem} sortételhez tartozik, de egyetlen ismétlődő időszakába sem esik bele, így kimarad a havi elszámolásból.',
  'alert.wlog_multi_occ.body':
    '{subject} — az óra {count} ismétlődő időszakba is beleesik, mert azok átfedik egymást, így kétszer számolódik.',
  'alert.dep_date_violation.body':
    '{subject} — az utód a {blocker} befejezése előtt indul, tehát az ütemezés olyan sorrendet tételez, ami a dátumok szerint nem valósulhat meg.',
  'alert.stale_progress.body':
    '{subject} {days} munkanapja folyamatban van anélkül, hogy bármi történt volna rajta — a félkész állapot mégis fél munkát ismer el.',
  'alert.log_on_todo.body':
    '{subject} feladatra {hours} óra érkezett, miközben a feladat még el sem indult — az órák így csak a költést növelik, készültséget nem adnak.',
  'alert.no_recent_logs.body':
    '{subject} projektre {days} munkanapja nem érkezett idő, így minden előrejelzés elavult tényből számol.',
  'alert.plan_thin.body':
    '{subject} sortételre {planned} van beosztva a {frame} keretből, ezért a dashboard a keretre vált vissza tervbázisként.',
  'alert.li_closed_work.body':
    '{subject} sortétel {closedOn} óta le van zárva, mégis {tasks} feladat és {hours} óra került rá azóta.',
  'alert.wlog_unidentified.body':
    '{subject} {hours} órája ({from} – {to}) egyik sortételre sem került: a feladat sortételei más személyeket neveznek meg, vagy őt csak más időszakra.',
  'alert.wlog_unestimated_share.body':
    '{subject} projekten a ráfordítás {share} része becslés nélküli feladatokon van.',
  'alert.wlog_unbound_share.body':
    '{subject} projekten az órák {share} része nincs sortételhez kötve.',
  'alert.li_no_tasks.body':
    '{subject} projekten {count} sortételen van logolt óra, de nincs rajta feladat.',
  'alert.task_reestimated_closed.body':
    '{subject} projekten {count} kész feladat becslése módosult a lezárása után.',
  'alert.li_rule_on_status.body':
    '{subject} projekten {count} kötési feltétel státuszra vagy felelősre szűr.',
  'alert.alloc_stale.body':
    '{subject} projekten a hátralévő allokáció {share} része olyan kollégákon van, akik két hete nem logoltak ide.',
  'alert.li_over_plan.body':
    '{subject} projekten egy sortétel tervezett órája túllépi a keretét (szorzó: {ratio}).',
  'alert.member_silent.body':
    '{subject} projekten {count} kolléga 3+ munkanapra be volt osztva az elmúlt két hétben, de nem logolt.',
  'notif.someone': 'Valaki',
  'notif.leave.requested': '{actor} szabadságot kér',
  'notif.leave.approved': '{actor} jóváhagyta a szabadságodat',
  'notif.leave.rejected': '{actor} elutasította a szabadságkérelmedet',
  'notif.leave.withdrawn': '{actor} visszavonta a szabadságkérelmét',
  'notif.leave.revoked': '{actor} visszavonta a jóváhagyott szabadságát',
  'notif.leave.cancelled': '{actor} visszavonta a jóváhagyott szabadságodat',
  'notif.overtime.requested': '{actor} túlórát számolt el',
  'notif.overtime.approved': '{actor} jóváhagyta a túlórádat',
  'notif.overtime.rejected': '{actor} elutasította a túlóraigényedet',
  'notif.overtime.withdrawn': '{actor} visszavonta a túlóraigényét',
  'account.notif.group.tasks': 'Feladatok',
  'account.notif.group.requests': 'Szabadság és túlóra',
  'account.notif.event.approval': 'Elbírálásra váró kérelmek',
  'account.notif.event.approvalHint':
    'Beérkező szabadság- és túlóraigények, visszavonásuk és az emlékeztetők.',
  'account.notif.event.request': 'Saját kérelmeim',
  'account.notif.event.requestHint':
    'Döntés a kérelmeidről, a jóváhagyott szabadság visszavonása, a kezdés előtti nap és az év végi keret.',
  'account.notif.event.receipt': 'Visszaigazolások',
  'account.notif.event.receiptHint':
    'Másolat arról, amit te tettél: beadott kérelem, jóváhagyás, elutasítás. Csak e-mailben.',
  'account.notif.emailOnly': 'Csak e-mailben',
  'account.notif.group.time': 'Időrögzítés',
  'account.notif.group.workspace': 'Pénzügy és integrációk',
  'account.notif.event.logging': 'Hiányzó óráim',
  'account.notif.event.loggingHint':
    'Pénteken, ha a héten hiányzik idő, és a havi zárás előtt két munkanappal. Csak e-mailben.',
  'account.notif.event.team': 'A csapatom hiányzó órái',
  'account.notif.event.teamHint':
    'Pénteken a projektjeid tagjairól és a beosztottjaidról, ha valakinél hiányzik idő. Csak e-mailben.',
  'account.notif.event.finance': 'Pénzügy',
  'account.notif.event.financeHint':
    'Számlázható tételek hétfőnként, és ha sztornóznak egy számlát. Csak e-mailben.',
  'account.notif.event.integrations': 'Integrációk',
  'account.notif.event.integrationsHint':
    'Ha egy szinkron tartósan hibára fut, vagy újra kell csatlakoznod. Csak e-mailben.',
  'notif.contract.ending': '{days} nap múlva lejár a szerződés',
  'notif.contract.endedOpen': 'A szerződés lejárt, de még aktív',
  'notif.contract.budget': 'A szerződés keretének {percent}%-a elfogyott',
  'notif.contract.budgetSpent': 'Elfogyott a szerződés kerete',
  'notif.contract.burnEarly': 'A keret a lejárat előtt elfogy',
  'notif.contract.owner': '{actor} a szerződés felelősévé tett',
  'notif.project.riskWorsened': 'Csökkent a projekt Trust Score-ja',
  'notif.project.added': '{actor} hozzáadott a projekthez',
  'notif.project.pm': '{actor} a projekt PM-jévé tett',
  'notif.project.closed': '{actor} lezárta a projektet',
  'account.notif.group.projects': 'Projektek és szerződések',
  'account.notif.event.project': 'Projektjeim',
  'account.notif.event.projectHint':
    'Ha hozzáadnak egy projekthez, PM leszel, csökken a Trust Score-ja, vagy lezárják.',
  'account.notif.event.alerts': 'Napi projektjelzések',
  'account.notif.event.alertsHint':
    'A vezetett projektjeid új jelzései, naponta egyszer, az emlékeztetési időpontban. Csak e-mailben.',
  'account.notif.event.contract': 'Szerződéseim',
  'account.notif.event.contractHint':
    'Közelgő lejárat, fogyó keret, korai kifogyás, és ha te leszel a felelős.',
  /* ── ux-pass-2 · the one history and the audit log (`screens/history/`). HU. */
  'settings.nav.audit': 'Auditnapló',
  'title.settingsAudit': 'Auditnapló',
  'history.title': 'Előzmények',
  'history.search': 'Keresés: személy, azonosító, név, művelet…',
  'history.refresh': 'Frissítés',
  'history.open': 'Megnyitás',
  'history.system': 'Rendszer',
  'history.column.at': 'Mikor',
  'history.column.actor': 'Ki',
  'history.column.entity': 'Mi',
  'history.column.action': 'Művelet',
  'history.column.change': 'Változás',
  'history.filter.kind': 'Művelet',
  'history.filter.actor': 'Ki',
  'history.filter.entity': 'Típus',
  'history.filter.at': 'Időszak',
  'history.kind.create': 'Létrehozás',
  'history.kind.update': 'Módosítás',
  'history.kind.delete': 'Törlés',
  'history.kind.other': 'Egyéb',
  'history.channel.ui': 'Alkalmazás',
  'history.channel.api': 'API',
  'history.channel.agent': 'Agent',
  'history.channel.mcp': 'MCP-kliens',
  'history.summary.more': '+{count} mező',
  'history.empty.title': 'Még nincs előzmény',
  'history.empty.body':
    'Itt jelenik meg minden létrehozás, módosítás és törlés – ki, mikor, és mi volt előtte és utána.',
  'history.noMatch.title': 'Nincs találat',
  'history.noMatch.body': 'Ezekre a szűrőkre egy bejegyzés sem illik.',
  'history.detail.title': 'Részletek',
  'history.detail.back': 'Vissza az előzményekhez',
  'history.detail.channel': 'Csatorna',
  'history.detail.field': 'Mező',
  'history.detail.before': 'Előtte',
  'history.detail.after': 'Utána',
  'history.detail.note': 'Szöveg',
  'history.detail.noChanges': 'Ez a művelet egy mezőt sem módosított.',
  'history.entity.task': 'Feladat',
  'history.entity.comment': 'Hozzászólás',
  'history.entity.worklog': 'Időlog',
  'history.entity.project': 'Projekt',
  'history.entity.project_member': 'Projekttag',
  'history.entity.allocation': 'Allokáció',
  'history.entity.client': 'Ügyfél',
  'history.entity.contract': 'Szerződés',
  'history.entity.line_item': 'Sortétel',
  'history.entity.period': 'Időszak',
  'history.entity.invoice': 'Számla',
  'history.entity.external_cost': 'Külső költség',
  'history.entity.person': 'Dolgozó',
  'history.entity.leave_request': 'Szabadságkérelem',
  'history.entity.overtime_request': 'Túlórakérelem',
  'history.entity.file': 'Fájl',
  'history.entity.report': 'Riport',
  'history.entity.metafield': 'Metafield',
  'history.entity.role': 'Szerepkör',
  'history.entity.integration': 'Integráció',
  'history.entity.settings': 'Beállítás',
  'history.entity.workspace': 'Workspace',
  'history.entity.deal': 'Deal',
  'history.entity.contact': 'Kapcsolattartó',
  'history.entity.other': 'Egyéb',
  'history.action.reorder': 'átrendezés',
  'history.action.revoke': 'visszavonás',
  'history.action.export': 'export',
  'history.action.import': 'import',
  'history.action.restore': 'visszaállítás',
  'history.action.archive': 'archiválás',
  'history.action.attach': 'csatolás',
  'history.action.detach': 'leválasztás',
  'history.action.sync': 'szinkronizálás',
  'history.action.link': 'kapcsolás',
  'history.action.unlink': 'kapcsolat bontása',
  'history.action.decide': 'elbírálás',
  'history.action.request': 'kérelem beadása',
  'history.action.comment': 'hozzászólás',
  'history.action.commentEdit': 'hozzászólás szerkesztése',
  'history.action.commentDelete': 'hozzászólás törlése',
  'history.action.assign': 'hozzárendelés',
  'history.action.unassign': 'hozzárendelés megszüntetése',
  'history.action.memberAdd': 'tag hozzáadása',
  'history.action.memberRemove': 'tag eltávolítása',
  'history.action.memberUpdate': 'tagság módosítása',
  'history.action.duplicate': 'duplikálás',
  'history.action.setDefault': 'alapértelmezetté tétel',
  'history.action.activate': 'aktiválás',
  'history.action.deactivate': 'deaktiválás',
  'history.action.invite': 'meghívás',
  'history.action.setValues': 'értékek beállítása',
  'history.action.depend': 'függőség hozzáadása',
  'history.action.undepend': 'függőség törlése',
  'history.action.raise': 'jelzés',
  'history.action.dismiss': 'elvetés',
  'history.action.install': 'telepítés',
  'history.action.uninstall': 'eltávolítás',
  'history.action.pause': 'szüneteltetés',
  'history.action.resume': 'folytatás',
  'history.action.rolesSet': 'szerepkör beállítása',
  'history.action.onboarded': 'onboarding lezárva',
  'history.field.title': 'Cím',
  'history.field.name': 'Név',
  'history.field.givenName': 'Keresztnév',
  'history.field.familyName': 'Vezetéknév',
  'history.field.email': 'E-mail',
  'history.field.phone': 'Telefon',
  'history.field.status': 'Státusz',
  'history.field.priority': 'Prioritás',
  'history.field.assignee': 'Felelős',
  'history.field.project': 'Projekt',
  'history.field.client': 'Ügyfél',
  'history.field.contract': 'Szerződés',
  'history.field.estimate': 'Becslés',
  'history.field.startsOn': 'Kezdés',
  'history.field.endsOn': 'Vég',
  'history.field.dueOn': 'Határidő',
  'history.field.lineItem': 'Sortétel',
  'history.field.parentTask': 'Szülőfeladat',
  'history.field.description': 'Leírás',
  'history.field.amount': 'Összeg',
  'history.field.currency': 'Pénznem',
  'history.field.budget': 'Büdzsé',
  'history.field.revenue': 'Bevétel',
  'history.field.price': 'Ár',
  'history.field.unitPrice': 'Egységár',
  'history.field.quantity': 'Mennyiség',
  'history.field.unit': 'Egység',
  'history.field.type': 'Típus',
  'history.field.owner': 'Tulajdonos',
  'history.field.pm': 'PM',
  'history.field.role': 'Szerepkör',
  'history.field.team': 'Csapat',
  'history.field.manager': 'Közvetlen vezető',
  'history.field.tracksTime': 'Időt rögzít',
  'history.field.leaveType': 'Szabadság típusa',
  'history.field.overtimeType': 'Túlóra típusa',
  'history.field.from': 'Kezdete',
  'history.field.to': 'Vége',
  'history.field.hours': 'Óra',
  'history.field.workedOn': 'Nap',
  'history.field.note': 'Megjegyzés',
  'history.field.body': 'Szöveg',
  'history.field.tags': 'Címkék',
  'history.field.metafields': 'Egyedi mezők',
  'history.field.color': 'Szín',
  'history.field.code': 'Kód',
  'history.field.recurrence': 'Ismétlődés',
  'history.field.assignment': 'Hozzárendelés',
  'history.field.active': 'Aktív',
  'history.field.issuedOn': 'Kiállítás dátuma',
  'history.field.paidOn': 'Fizetés dátuma',
  'history.field.incurredOn': 'Felmerülés dátuma',
  'history.field.number': 'Sorszám',
  'history.field.employmentType': 'Foglalkoztatás',
  'history.field.weeklyHours': 'Heti óraszám',
  'history.field.settlement': 'Elszámolás',
  'history.field.multiplier': 'Szorzó',
  'history.field.isDefault': 'Alapértelmezett',
  'history.field.position': 'Sorrend',
  'history.field.rate': 'Díj',
  'history.field.billable': 'Számlázható',
  'history.field.member': 'Dolgozó',
  'history.field.invoice': 'Számla',
  'history.field.period': 'Időszak',
  /* Phase 6 · the deal screens, one file per area in `./deals/`. */
  ...DEALS_BASE_HU,
  ...DEALS_SETTINGS_HU,
  ...DEALS_BOARD_HU,
  ...DEALS_RECORD_HU,
  ...DEALS_PLAN_HU,
  ...DEALS_GENERATE_HU,
  ...FX_HU,
  ...DURATION_HU,
  ...DEALS_SCENARIO_HU,
} as const;

/**
 * ⚠️ **`Catalogue` is derived from `HU`, so HU is the source of truth for the key set.** Adding a
 * key to `HU` alone is a type error in `EN` — which is the point. The reverse is deliberately not
 * possible: an EN-only key would be a string no Hungarian reader ever sees.
 */
export type Catalogue = Record<keyof typeof HU, string>;
export type MessageKey = keyof typeof HU;

export const EN: Catalogue = {
  'app.name': 'Forecastify',
  'nav.settings': 'Settings',
  'nav.permissions': 'Permissions',
  'nav.skipToContent': 'Skip to content',
  'nav.collapseSidebar': 'Collapse sidebar',
  'nav.expandSidebar': 'Expand sidebar',

  'title.permissions': 'Permissions · Settings',

  'permissions.heading': 'Permissions',
  'permissions.levels.title': 'Levels',
  'permissions.level.missing': 'That permission level no longer exists.',
  'permissions.levels.note': '{levels} levels · {actions} permissions',
  'permissions.levels.new': 'New level',
  'permissions.levels.newHint':
    'Created empty, or as a copy of an existing level. You set what it may do next.',
  'permissions.levels.create': 'Create level',
  'permissions.levels.name': 'Name',
  'permissions.levels.namePlaceholder': 'e.g. Project lead',
  'permissions.levels.copyFrom': 'Copy from',
  'permissions.levels.copyNote':
    'Its permissions are copied. Changing them later affects only this level.',
  'permissions.levels.copyNone': 'Start empty',
  'permissions.levels.delete': 'Delete level',
  'permissions.levels.open': 'Open',
  'permissions.levels.emptyBody': 'No permission level yet.',
  'permissions.levels.deleteHeld': 'Held by {count} people – it cannot be deleted',
  'permissions.levels.error.taken': 'A level with that name already exists.',
  'permissions.levels.error.inUse': 'Somebody still holds this level.',
  'permissions.levels.actions': 'Level actions',
  'permissions.levels.builtin': 'built-in',
  'permissions.levels.granted': '{granted} / {total}',
  'permissions.levels.members': '{count} people',
  'permissions.detail.title': '{name}',
  'permissions.detail.note': '{granted} permissions on · {members} people hold this level',
  'permissions.detail.empty': 'This level allows nothing yet.',

  'permissions.scope.label': 'Scope: {action}',
  'permissions.scope.none': 'None',
  'permissions.scope.own': 'Own',
  'permissions.scope.workspace': 'Full',
  'permissions.scope.none.hint': 'Cannot do this.',
  'permissions.scope.own.hint': 'Only on rows that belong to them.',
  'permissions.scope.workspace.hint': 'On every row in the workspace.',
  'permissions.scope.project.own': 'Own projects',
  'permissions.scope.project.workspace': 'All projects',
  'permissions.scope.project.own.hint': 'Only on the projects they are a member of.',
  'permissions.scope.project.workspace.hint': 'On every project in the workspace.',

  'permissions.fields.title': 'Field rules',
  'permissions.fields.note':
    'Hiding a field is stronger than making it read-only: a hidden field does not render at all.',
  'permissions.fields.hidden': 'Hidden',
  'permissions.fields.read': 'Read-only',
  'permissions.fields.write': 'Writable',
  'permissions.fields.empty': 'No field rules: every field is writable.',
  'permissions.fields.label': 'Access: {field}',

  'permissions.group.task': 'Tasks',
  'permissions.group.worklog': 'Worklogs',
  'permissions.group.project': 'Projects and clients',
  'permissions.group.contract': 'Contracts',
  'permissions.group.people': 'People',
  'permissions.group.leave': 'Leave',
  'permissions.group.report': 'Reports',
  'permissions.group.settings': 'Settings',
  'permissions.group.menu': 'Workspace menu',
  'permissions.group.dashboard': 'Dashboards',

  'permissions.save': 'Save',
  'permissions.saving': 'Saving…',
  'permissions.discard': 'Discard',
  'permissions.saved': 'Level saved',
  'permissions.unsaved': 'Unsaved changes',

  'error.not_found': 'That item no longer exists.',
  'error.permission_denied': 'You do not have permission to do this.',
  'error.conflict': 'Somebody else changed this. Reload, then try again.',
  'error.conflict.reload': 'Reload',
  'error.validation_failed': 'The save failed because the data sent was not valid.',
  'error.unauthenticated': 'Your session expired. Sign in again.',
  'error.unknown': 'Something unexpected went wrong.',
  'error.requestId': 'Error id: {id}',

  'state.loading': 'Loading…',
  'invite.heading': 'Invitations',
  'invite.count': '{count} pending',
  'invite.empty': 'No invitations sent.',
  'invite.action': 'Invite',
  'invite.by': 'invited by {name}',
  'invite.resend': 'Resend',
  'invite.revoke': 'Revoke',
  'invite.send': 'Send the invitation',

  'invite.state.pending': 'Sent',
  'invite.state.accepted': 'Accepted',
  'invite.state.revoked': 'Revoked',
  'invite.state.expired': 'Expired',

  'invite.dialog.title': 'Invite a colleague',
  'invite.dialog.note': 'We will email them the link. It works for 14 days.',
  'invite.field.email': 'Email address',
  'invite.field.role': 'Role',
  'invite.field.pickRole': 'Choose a role…',
  'invite.error.alreadyMember': 'That address is already a member of this workspace.',
  'invite.error.alreadyInvited':
    'There is already a live invitation for that address. Resend it, or revoke it.',
  'invite.error.email': 'That is not a valid email address.',
  'invite.error.seatLimit':
    'Every seat on this plan is taken – invitations that nobody has accepted hold one too. Revoke an old one, or ask for more seats.',
  'people.error.seatLimit':
    'There is no free seat on this plan, and reactivating somebody takes one. Revoke an invitation, or ask for more seats.',

  'invite.accept.title': 'Invitation',
  'invite.accept.lead': '{inviter} invited you to the {workspace} workspace.',
  'invite.accept.leadNoInviter': 'You have been invited to the {workspace} workspace.',
  'invite.accept.role': 'Role: {role}',
  'invite.accept.join': 'Join',
  'invite.accept.signIn': 'Sign in',
  'invite.accept.needAccount':
    'Sign in – or sign up – with the address above, then come back to this link.',
  'invite.accept.wrongAccount':
    'This invitation is for the address above, and you are signed in as somebody else. Sign in with that address, or ask for a new invitation.',
  'invite.accept.expired': 'This invitation has expired. Ask for a new one.',
  'invite.accept.revoked': 'This invitation was revoked.',
  'invite.accept.used': 'This invitation has already been used.',
  'invite.accept.unknown': 'That link is not valid. Ask for a new invitation.',
  'invite.claim.lead': 'Set a password and you are in.',
  'invite.claim.password': 'Password',
  'invite.claim.confirm': 'Confirm the password',
  'invite.claim.action': 'Join the workspace',
  'invite.claim.haveAccount': 'I already have an account – sign in',
  'invite.claim.already':
    'That address already has an account. Sign in, then open this link again.',
  'invite.claim.weak': 'That password does not meet the policy.',
  'invite.claim.signInFailed':
    'The account was created but signing in failed. Sign in with the password you just set.',

  'state.retry': 'Try again',
  'state.denied.title': 'You do not have access to this',
  'state.denied.body': 'Ask whoever manages the workspace for access.',
  'state.notInPlan.title': 'This is not part of your plan',
  'state.notInPlan.body':
    'Your workspace’s plan does not include this. Whoever manages the subscription can change the plan.',

  'nav.account': 'My account',
  'nav.settingsSearch': 'Search settings',
  'nav.seeAll': 'See more',
  'title.account': 'My account · Settings',
  'account.section.profile': 'Profile',
  /* ── C54 · my skills ──────────────────────────────────────────────────────────────────── */
  'account.section.skills': 'My skills',
  'account.skills.add': 'Add a skill',
  'account.skills.count': '{count} skills',
  'account.skills.inCategories': 'in {count} categories',
  'account.skills.none':
    'You have no skills yet. Pick from the workspace list and say what level you are at.',
  'account.skills.empty': 'No skills yet. Add one from the workspace list.',
  'account.skills.hint':
    'You can only add skills the workspace already lists. If something is missing, ask an admin: they can add it under Settings › Skills.',
  'account.skills.levelOf': 'Level in {name}',
  'account.skills.remove': 'Remove {name}',
  'account.section.security': 'Security',
  'account.section.preferences': 'Preferences',
  'account.section.theme': 'Theme',
  'account.section.notifications': 'Notifications',
  'account.section.integrations': 'Integrations',
  'account.section.account': 'Account',
  'account.section.unsaved': 'Unsaved changes on this tab',
  'account.avatar.title': 'Profile picture',
  'account.avatar.upload': 'Upload a picture',
  'account.avatar.remove': 'Remove',
  'account.avatar.uploading': 'Uploading…',
  'account.avatar.tooLarge': 'That picture is too large (2 MB at most).',
  'account.avatar.wrongType': 'Upload a PNG, JPEG or WebP image.',
  'account.avatar.infected': 'The virus scanner refused that file.',
  'account.avatar.scannerDown': 'Files cannot be checked right now. Try again shortly.',
  'account.avatar.failed': 'The upload did not go through.',
  'account.avatar.rules': 'PNG or JPG, at least 256×256. We do not resize it for you.',
  'account.basics.title': 'Basics',
  'account.field.givenName': 'Given name',
  'account.field.familyName': 'Family name',
  'account.field.phone': 'Phone',
  'account.field.optional': 'optional',
  'account.field.email': 'Email address',
  'account.email.change': 'Change address',
  'account.email.newLabel': 'New email address',
  'account.email.send': 'Send the confirmation',
  'account.email.cancel': 'Cancel',
  'account.email.pending': 'Waiting for confirmation: {email}',
  'account.email.bothMustConfirm':
    'We emailed the old address and the new one – both links must be opened, and until then the old address stands.',
  'account.save': 'Save',
  'account.saving': 'Saving…',
  'account.saved': 'Saved',
  'account.discard': 'Discard',
  'account.password.title': 'Password',
  'account.password.current': 'Current password',
  'account.password.next': 'New password',
  'account.password.confirm': 'New password again',
  'account.password.submit': 'Change password',
  'account.password.rule.length': 'At least {min} characters',
  'account.password.rule.letters': 'Has both lower and upper case',
  'account.password.rule.digits': 'Has a digit',
  'account.password.rule.match': 'The two new passwords match',
  'account.password.wrongCurrent': 'That is not your current password.',
  'account.password.same': 'The new password must differ from the old one.',
  'account.password.weak': 'That password does not meet the rules.',
  'account.twofa.title': 'Two-factor authentication',
  'account.twofa.enable': 'Turn on',
  'account.twofa.disable': 'Turn off',
  'account.twofa.scan': 'Scan this with your authenticator app, then type what it shows.',
  'account.twofa.secret': 'If you cannot scan it, enter this by hand:',
  'account.twofa.code': 'Six-digit code',
  'account.twofa.verify': 'Verify and turn on',
  'account.twofa.notOnYet': 'Two-factor is not on until you enter a valid code.',
  'account.twofa.qrAlt': 'QR code for your authenticator app',
  'account.twofa.on': 'Turned on',
  'account.twofa.onNote': 'Signing in asks for a six-digit code after your password.',
  'account.twofa.offNote': 'A second step at sign-in, from an app on your phone.',
  'account.twofa.wrongCode': 'That code did not match. Try the next one.',
  'account.twofa.wrongPassword': 'That is not your password.',
  'account.twofa.disableNote':
    'After this, only your password protects the account. Your recovery keys are deleted too.',
  'account.twofa.keys.title': 'Recovery keys',
  'account.twofa.keys.once':
    'This is the only time you will see these. Save them – if you lose your phone, they are the way back in.',
  'account.twofa.keys.saved': 'I have saved them',
  'account.twofa.keys.download': 'Download',
  'account.twofa.keys.left': 'You have {count} unused recovery keys.',
  'account.twofa.keys.regenerate': 'New keys',
  'account.twofa.keys.regenerateNote':
    'We will issue a new set, and the old keys stop working immediately.',
  'common.copy': 'Copy',
  'common.copied': 'Copied',
  'account.passkeys.title': 'Passkeys',
  'account.passkeys.add': 'Add a passkey',
  'account.passkeys.empty': 'You have no passkeys yet.',
  'account.passkeys.adding': 'Waiting for your device…',
  'account.passkeys.created': 'Added {date}',
  'account.passkeys.lastUsed': 'Last used {when}',
  'account.passkeys.neverUsed': 'not used yet',
  'account.passkeys.remove': 'Delete',
  'account.passkeys.unsupported':
    'This browser does not support passkeys. Signing in with a password still works.',
  'account.sessions.title': 'Devices',
  'account.sessions.current': 'this device',
  'account.sessions.revoke': 'Sign out',
  'account.sessions.revokeOthers': 'Sign out every other device',
  'account.sessions.lastSeen': 'Last active {when}',
  'account.prefs.title': 'Language and formats',
  'account.prefs.locale': 'Language',
  'account.prefs.locale.hu': 'Magyar',
  'account.prefs.locale.en': 'English',
  'account.prefs.timezone': 'Time zone',
  'account.prefs.timezoneFilter': 'Filter',
  'account.prefs.dateFormat': 'Date format',
  'account.prefs.weekStart': 'First day of the week',
  'account.prefs.weekStart.mon': 'Monday',
  'account.prefs.weekStart.sun': 'Sunday',
  'account.prefs.sample': 'It will look like this: {sample}',
  'account.theme.title': 'Theme',
  'account.theme.system': 'Match the system',
  'account.theme.systemHint': 'Follows your machine – light by day, dark in the evening.',
  'account.theme.current': 'Selected',
  'account.theme.noPreview': 'The preview is built from the loaded stylesheet.',
  'theme.light.label': 'Forecastify Light',
  'theme.light.hint': 'The default. For everyday work.',
  'theme.dark.label': 'Forecastify Dark',
  'theme.dark.hint': 'The same palette, for an evening Gantt.',
  'theme.latte.label': 'Latte Violet',
  'theme.latte.hint': 'Warm light, with a violet accent.',
  'theme.mocha.label': 'Mocha Peach',
  'theme.mocha.hint': 'Soft dark, with a peach accent.',
  'theme.nord.label': 'Nord Blue',
  'theme.nord.hint': 'Cold dark, ice-blue accent.',
  'theme.frappe.label': 'Frappé Rose',
  'theme.frappe.hint': 'Cool dark, rose-quartz accent.',
  'theme.macchiato.label': 'Macchiato Pink',
  'theme.macchiato.hint': 'Deep blue dark, pink accent.',
  'theme.radix.label': 'Dark Violet',
  'theme.radix.hint': 'Near-black, in the brand violet.',
  'account.notif.title': 'Notifications',
  'account.notif.pause': 'Pause all notifications',
  'account.notif.pauseHint': 'Your settings are kept – we simply send nothing.',
  'account.notif.channelEmail': 'Email',
  'account.notif.channelApp': 'In the app',
  'account.notif.event.assigned': 'A task is assigned to me',
  'account.notif.event.assignedHint': 'As soon as somebody assigns you a ticket.',
  'account.notif.event.status': 'Status changed on my tasks',
  'account.notif.event.statusHint': 'Your tickets moving on the board.',
  'account.notif.event.comment': 'A comment or mention on my task',
  'account.notif.event.commentHint': '@mentions and every new comment.',
  'account.notif.event.due': 'A due date is coming up',
  'account.notif.event.dueHint': 'Only for open tickets assigned to you.',
  'account.notif.lead': 'How many days ahead',
  'account.notif.reminder': 'Daily reminder',
  'account.notif.reminderAt': 'Time',
  'account.notif.reminderDays': 'On which days',
  'account.notif.weekly': 'Weekly digest',
  'account.notif.day.1': 'Mo',
  'account.notif.day.2': 'Tu',
  'account.notif.day.3': 'We',
  'account.notif.day.4': 'Th',
  'account.notif.day.5': 'Fr',
  'account.notif.day.6': 'Sa',
  'account.notif.day.7': 'Su',
  'account.integrations.title': 'Calendar',
  'account.integrations.google': 'Google Calendar',
  'account.integrations.outlook': 'Outlook',
  /* ── C37 · connecting your own Jira account. Nobody pastes a token. ─────────────────────── */
  /* C37 · see the Hungarian block: a state, not an error, with the reason on hover. */
  'time.sync.pending': 'waiting to send',
  'time.sync.failed': 'did not send',
  'time.sync.detached': 'Jira copy deleted',
  'account.connections.title': 'Forecastify integration connections',
  'account.connections.connect': 'Connect',
  'account.connections.disconnect': 'Disconnect',
  'account.connections.connected': 'connected',
  'account.connections.notConnected': 'not connected',
  'account.connections.notDiscovered': 'needs discovery first',
  'account.connections.result.ok': 'Connected',
  'account.connections.result.cancelled': 'You cancelled the connection',
  'account.connections.result.refused': 'Atlassian refused the request',
  'account.connections.result.state': 'That request was invalid – report it if it happens again',
  'account.connections.result.expired': 'That took too long, try again',
  'account.connections.result.wrong_site':
    'You connected a different Jira than this integration uses',
  'account.connections.result.no_refresh':
    'That connection would die in an hour – allow offline access',
  'account.connections.result.not_configured': 'This server has no connection app configured',
  'account.connections.result.not_found': 'That integration no longer exists',
  'account.connections.result.malformed': 'The tracker sent back an incomplete answer',
  'account.connections.result.failed': 'The connection failed',
  /* 26 Oct 2026 · the return page — see the Hungarian block. */
  'account.connections.outcome.ok.title': 'Your Jira account is connected',
  'account.connections.outcome.ok.body':
    'The hours you log on tasks that sync with Jira now go up under your own name, and the ones that were waiting go out too.',
  'account.connections.outcome.cancelled.title': 'You cancelled the connection',
  'account.connections.outcome.cancelled.body':
    'Nothing has changed. If you do want to connect your account, try again.',
  'account.connections.outcome.refused.title': 'Atlassian refused the request',
  'account.connections.outcome.refused.body':
    'It was not you who declined – Atlassian did not allow the connection. If it happens again, tell your workspace administrator.',
  'account.connections.outcome.state.title': 'That request was not valid',
  'account.connections.outcome.state.body':
    'The returning request could not be verified, so nothing was connected. Start again from your integrations; if it happens again, report it.',
  'account.connections.outcome.expired.title': 'The connection timed out',
  'account.connections.outcome.expired.body':
    'More than ten minutes passed on Atlassian’s screen, so the request expired. Start again from your integrations.',
  'account.connections.outcome.wrong_site.title': 'You picked a different Jira site',
  'account.connections.outcome.wrong_site.body':
    'Atlassian offers every site your account can reach and leaves the choice to you. Try again, and pick the address marked below there.',
  'account.connections.outcome.wrong_site.picked': 'You picked',
  'account.connections.outcome.wrong_site.expected': 'Pick this one',
  'account.connections.outcome.no_refresh.title': 'Offline access was not granted',
  'account.connections.outcome.no_refresh.body':
    'Atlassian did not grant offline access, so the connection would stop working within the hour. Try again; if it happens again, tell your workspace administrator.',
  'account.connections.outcome.not_configured.title': 'Connecting is not set up',
  'account.connections.outcome.not_configured.body':
    'This server has no Atlassian app configured for connecting accounts. Tell your workspace administrator.',
  'account.connections.outcome.not_found.title': 'That Jira integration no longer exists',
  'account.connections.outcome.not_found.body':
    'Somebody removed it in the meantime. Your integrations show what you can connect now.',
  'account.connections.outcome.malformed.title': 'An incomplete answer came back',
  'account.connections.outcome.malformed.body':
    'Atlassian sent you back without what connecting needs. Try again.',
  'account.connections.outcome.failed.title': 'The connection failed',
  'account.connections.outcome.failed.body':
    'Something went wrong while connecting, and nothing was connected. Try again; if it happens again, tell your workspace administrator.',
  'account.connections.outcome.detail': 'Atlassian said:',
  'account.connections.outcome.retry': 'Try again',
  'account.connections.outcome.back': 'Back to integrations',
  'account.connections.outcome.done': 'Done',
  'account.tokens.title': 'Personal access tokens',
  'account.tokens.create': 'New token',
  'account.tokens.empty': 'You have no tokens yet.',
  'account.tokens.nameLabel': 'What it is for',
  'account.tokens.scopesLabel': 'What it opens',
  'account.tokens.scope.read': 'Read',
  'account.tokens.scope.write': 'Write',
  'account.tokens.scope.contracts': 'Contracts',
  'account.tokens.namePlaceholder': 'Monthly report script',
  'account.tokens.created': 'Created {date}',
  'account.tokens.lastUsed': 'Last used {when}',
  'account.tokens.never': 'never',
  'account.tokens.revoke': 'Revoke',
  'account.tokens.revoked': 'revoked',
  'account.tokens.revealTitle': 'Here is the token, copy it now',
  'account.tokens.revealHint':
    'This is the only time you will see it. Afterwards we cannot show it again, only issue a new one.',
  'account.tokens.copy': 'Copy',
  'account.tokens.copied': 'Copied',
  'account.tokens.done': 'I have copied it',
  'account.export.title': 'Data export',
  'account.export.hint': 'We export everything you can see and email you.',
  'account.export.request': 'Request an export',
  'account.export.requested': 'Request recorded – we will email you when it is ready.',
  'account.export.running': 'Building…',
  'account.export.download': 'Download',
  'account.export.notFulfilledYet':
    'Producing the archive is not built yet – your request is recorded, but no file comes of it for now.',
  'account.delete.title': 'Delete account',
  'account.delete.hint': 'This cannot be undone.',
  'account.delete.blocked':
    'Deleting an account is not built yet. Speak to whoever manages the workspace.',

  'signIn.email': 'Email address',
  'signIn.password': 'Password',
  'signIn.submit': 'Sign in',
  'signIn.working': 'Signing in…',
  'signIn.invalid': 'That does not match. Check the address and the password.',
  'signIn.rateLimited': 'Too many attempts. Wait a moment.',
  'signIn.unavailable': 'Signing in is unavailable right now.',
  'signIn.noWorkspace': 'Your account does not belong to any workspace yet.',
  'session.signOut': 'Sign out',
  'session.restoring': 'Checking your session…',
  'state.noWorkspace.title': 'You are not in any workspace',
  'state.noWorkspace.body':
    'Your account exists, but nobody has invited you to a workspace yet. Ask whoever manages the workspace. (In development this usually means the test suite emptied the database – run `pnpm seed:dev` again.)',
  'signIn.withPasskey': 'Sign in with a passkey',
  'signIn.noAccount': 'I do not have an account – sign up',

  /* ══ S2: sign-up and the confirmation landing ═════════════════════════════════════════════ */
  'signUp.title': 'Create an account',
  'signUp.lede': 'Enter your email address and we will send you a code.',
  'signUp.email': 'Email address',
  'signUp.submit': 'Send code',
  'signUp.working': 'One moment…',
  'signUp.haveAccount': 'I already have an account – sign in',
  'signUp.code.title': 'Enter the code',
  'signUp.code.lede': 'We sent a six-digit code to {email}',
  'signUp.code.label': 'Code',
  'signUp.code.submit': 'Continue',
  'signUp.code.resend': 'Send a new code',
  'signUp.code.resent': 'We sent a new code.',
  'signUp.code.changeEmail': 'Use a different address',
  'signUp.code.invalid': 'That code is wrong or has expired. Ask for a new one.',
  'signUp.code.existing': 'If this address already has an account, no code is sent.',
  'signUp.code.signIn': 'Sign in',
  'signUp.invalidEmail': 'That does not look like an email address.',
  'signUp.rateLimited': 'Too many attempts. Wait a moment.',
  'signUp.unavailable': 'Signing up is unavailable right now.',

  'verify.signup.title': 'Your email address is confirmed',
  'verify.signup.body': 'You can sign in now and create your first workspace.',
  'verify.emailChange.title': 'Your new email address is confirmed',
  'verify.emailChange.body':
    'An address change needs both addresses to confirm – if the other email is still unopened, open that one too.',
  'verify.other.title': 'The link was valid',
  'verify.other.body': 'The confirmation went through.',
  'verify.dead.title': 'This link can no longer be used',
  /* ⚠️ Not "expired": GoTrue answers `otp_expired` for expired, already-used and invalid alike. */
  'verify.dead.body':
    'It has expired, or it has already been used – we cannot tell which. If you have already confirmed your address, just sign in.',
  'verify.error.title': 'The confirmation did not work',
  'verify.error.body': 'The service returned this error:',
  'verify.unknown.title': 'This is where a confirmation link lands',
  'verify.unknown.body':
    'It looks like you opened this address directly. Open the link from your email instead.',
  'verify.toSignIn': 'Sign in',
  'verify.toAccount': 'Back to my account',
  'verify.toSignUp': 'Sign up again',

  /* ══ C52 · consent for an external application. See the Hungarian side for the argument. ══ */
  'consent.title': 'Allow access',
  'consent.signIn': 'Sign in so you can decide.',
  'consent.loading': 'Loading…',
  'consent.failed.title': 'This request is no longer valid',
  'consent.failed.body':
    'It has expired, or you have already decided. Start connecting again from that application.',
  'consent.lede': '{client} is asking for access to your Forecastify account.',
  'consent.field.client': 'Application',
  'consent.field.redirect': 'Where we send it',
  'consent.localhost':
    'This application only asks to be sent back to your own machine. That address cannot tell you which program will receive the access – only allow it if you started this.',
  'consent.scopes': 'What it will be able to do:',
  'consent.scope.read': 'See your tasks, hours and projects',
  'consent.scope.write': 'Propose changes – nothing is written without your approval',
  'consent.scope.contracts': 'See contracts',
  'consent.workspace': 'In which workspace',
  'consent.again': 'You have allowed this application before.',
  'consent.approve': 'Allow',
  'consent.decline': 'Do not allow',
  'consent.gate':
    'This application writes nothing on its own. Every change appears as a card in Forecastify and happens only when you press it.',
  'account.apps.title': 'Allowed applications',
  'account.apps.lede':
    'External applications you have given access to. Disconnecting takes effect immediately – the application is refused on its next request.',
  'account.apps.disconnect': 'Disconnect',

  /* ══ Later: tenancy — the first workspace ═════════════════════════════════════════════════ */
  /* ── `0202` · the first-run setup flow ── */
  'setup.title': 'Getting started',
  'setup.stepOf': 'Step {step} of {total}',
  'setup.signedIn': 'Signed in as {email}',
  'setup.continue': 'Continue',
  'setup.working': 'One moment…',
  'setup.skip': 'Skip',
  'setup.back': 'Back',
  'setup.signOut': 'Sign out',
  'setup.upload': 'Upload picture',
  'setup.replace': 'Replace',
  'setup.remove': 'Remove',
  'setup.error.alreadyMember': 'This account already belongs to a workspace. Reload the page.',
  'setup.profile.title': 'Set up your profile',
  'setup.profile.subtitle': 'This is how your colleagues will see you.',
  'setup.profile.family': 'Last name',
  'setup.profile.given': 'First name',
  'setup.profile.avatarHint': 'JPG or PNG, up to 2 MB. Optional.',
  'setup.profile.errType': 'Use JPG or PNG.',
  'setup.profile.errSize': 'Images can be up to 2 MB.',
  'setup.profile.password': 'Password',
  'setup.profile.confirm': 'Confirm the password',
  'setup.profile.blocked': 'Enter your last and first name.',
  'setup.profile.blockedPassword': 'The password does not meet the rules yet.',
  'setup.profile.haveAccount': 'I already have an account – sign in',
  'setup.workspace.title': 'Set up your workspace',
  'setup.workspace.subtitle': 'Everyone you invite will see this.',
  'setup.workspace.name': 'Workspace name',
  'setup.workspace.namePlaceholder': 'e.g. Acme Ltd.',
  'setup.workspace.logoHint': 'PNG or JPG, up to 2 MB. A square image works best.',
  'setup.workspace.errType': 'Use PNG or JPG.',
  'setup.workspace.errSize': 'Images can be up to 2 MB.',
  'setup.workspace.address': 'Address',
  'setup.workspace.checking': 'Checking the address…',
  'setup.workspace.available': 'Address available',
  'setup.workspace.taken': '{slug} is taken.',
  'setup.workspace.reserved': '{slug} is reserved and cannot be used.',
  'setup.workspace.suggest': 'Try:',
  'setup.workspace.errChars': 'Lowercase letters, digits and hyphens only.',
  'setup.workspace.errHyphen': 'It can’t start or end with a hyphen.',
  'setup.workspace.errShort': 'At least 3 characters.',
  'setup.workspace.timezone': 'Time zone',
  'setup.workspace.blockedName': 'Enter a workspace name.',
  'setup.workspace.blockedSlug': 'Enter an address.',
  'setup.workspace.blockedCheck': 'The address is being checked.',
  'setup.workspace.blockedTaken': 'Choose an available address.',
  'setup.workspace.currency': 'Currency',
  'setup.workspace.currencyHint':
    'This becomes the workspace’s default currency. You can add more in Settings.',
  'setup.workspace.currencyLocked':
    'The currency can no longer be changed here. Change it in Settings › Currencies.',
  'setup.currency.HUF': 'Hungarian forint',
  'setup.currency.EUR': 'Euro',
  'setup.currency.USD': 'US dollar',
  'setup.currency.GBP': 'British pound',
  'setup.currency.CHF': 'Swiss franc',
  'setup.currency.RON': 'Romanian leu',
  'setup.currency.PLN': 'Polish złoty',
  'setup.currency.CZK': 'Czech koruna',
  'setup.currency.SEK': 'Swedish krona',
  'setup.currency.NOK': 'Norwegian krone',
  'setup.currency.DKK': 'Danish krone',
  'setup.currency.JPY': 'Japanese yen',
  'setup.invite.title': 'Invite your team',
  'setup.invite.subtitle': 'Your colleagues get an invitation by email.',
  'setup.invite.emails': 'Email addresses',
  'setup.invite.emailsPlaceholder': 'name@company.com, separated by commas or spaces',
  'setup.invite.emailsMore': 'Add another…',
  'setup.invite.level': 'Permission for everyone',
  'setup.invite.levelAdmin': 'Admin',
  'setup.invite.levelPm': 'Project manager',
  'setup.invite.levelMember': 'Member',
  'setup.invite.levelExt': 'Subcontractor',
  'setup.invite.levelFor': 'Permission for {email}',
  'setup.invite.seats': '{n} / {m} seats used',
  'setup.invite.over': 'This is {k} over your plan’s {m} seats. Remove {k}, or upgrade the plan.',
  'setup.invite.tagMember': 'already a member',
  'setup.invite.tagSelf': 'that’s you',
  'setup.invite.memberNote': '{list}: already a member, no invitation is sent.',
  'setup.invite.errAt': 'The @ sign is missing.',
  'setup.invite.errDomain': 'The end of the domain is missing, e.g. .com.',
  'setup.invite.errIncomplete': 'Incomplete address.',
  'setup.invite.errChar': 'It contains an invalid character.',
  'setup.invite.removeChip': 'Remove {email}',
  'setup.invite.send': 'Send invitations',
  'setup.invite.blockedEmpty': 'Add at least one address, or skip this step.',
  'setup.invite.blockedInvalid': 'Fix or remove the red addresses.',
  'setup.invite.blockedOver': 'More invitations than free seats.',
  'setup.invite.failed': 'No invitation went to {list}. You can try again later from People.',
  'setup.done.title': 'You’re all set',
  'setup.done.subtitle': '{ws} is ready.',
  'setup.done.address': 'Address',
  'setup.done.invites': 'Invitations',
  'setup.done.sent': '{n} invitations sent',
  'setup.done.pending': '{n} invitations — sent when you go in',
  'setup.done.none': 'No invitations sent. You can invite people later from People.',
  'setup.done.next': 'A short guided tour will help you find your way around.',
  'setup.done.enter': 'Go to Forecastify',
  'setup.done.entering': 'Opening…',
  'setup.preview.yourName': 'Your name',
  'setup.preview.workspace': 'Workspace',
  'setup.preview.task1': 'Homepage wireframe',
  'setup.preview.task2': 'Update the quote template',
  'setup.preview.task3': 'Prepare the client kickoff',
  'setup.preview.task4': 'Weekly status report',
  'setup.preview.task5': 'Design system audit',
  'setup.preview.task6': 'Sprint planning',
  'setup.preview.task7': 'Contract renewal call',
  'setup.preview.task8': 'Tidy up the internal wiki',
  'setup.preview.task9': 'Q4 capacity plan',
  'setup.preview.project1': 'Website relaunch',
  'setup.preview.project2': 'Mobile app',
  'setup.preview.project3': 'Internal operations',
  'setup.preview.statusBacklog': 'Backlog',
  'setup.preview.statusNext': 'Up next',
  'setup.preview.statusDoing': 'In progress',
  'setup.preview.statusReview': 'In review',
  'setup.preview.statusDone': 'Done',
  'setup.preview.priorityUrgent': 'Urgent',
  'setup.preview.priorityHigh': 'High',
  'setup.preview.priorityMedium': 'Medium',
  'setup.preview.priorityLow': 'Low',
  'setup.preview.ownerRole': 'Owner',

  /* ── S3 ──────────────────────────────────────────────────────────────────────────────────── */
  'suspended.title': 'This workspace is suspended',
  'suspended.lede':
    'Access is paused for now. Your data is intact – the workspace picks up exactly where it left off once the suspension is lifted.',
  'suspended.contact': 'Contact whoever administers your account.',
  'suspended.signedInAs': 'Signed in as {email}',
  'suspended.signOut': 'Sign out',

  /* ── C18 ─────────────────────────────────────────────────────────────────────────────────── */
  'signIn.emailPlaceholder': 'name@loginet.hu',
  'signIn.forgot': 'Forgotten?',
  'signIn.showPassword': 'Show password',
  'signIn.hidePassword': 'Hide password',
  'signIn.remember': 'Stay signed in on this machine',
  'signIn.passkey': 'Passkey',
  'signIn.passkeyUnavailable':
    'This browser or this address does not support passkeys. Open the app at its usual address.',
  'signIn.google': 'Google',
  'signIn.continue': 'Continue',
  'signIn.changeEmail': 'Change',
  'signIn.loggingInAs': 'Logging in as',
  'signIn.code': 'Six-digit code',
  'signIn.codeHint': 'Your authenticator app is showing it.',
  'signIn.useRecoveryKey': 'Use a recovery key',
  'signIn.recoveryKey': 'Recovery key',
  'signIn.recoveryKeyHint': 'One of the keys you saved. Each works once.',
  'signIn.useCode': 'Use the code instead',
  'signIn.mfaInvalid': 'That code did not match. Try the next one.',
  'oauth.working': 'Signing you in…',
  'oauth.expired': 'That sign-in expired. Start again.',
  'oauth.backToSignIn': 'Back to sign in',
  'oauth.accountNotFound': "We couldn't find an account with that email.",
  'oauth.identityInUse': 'That Google account is already connected to another account.',
  'oauth.linking': 'Connecting your Google account…',
  'oauth.backToAccount': 'Back to my account',
  'account.google.title': 'Google account',
  'account.google.lede':
    'With a connected Google account you can also sign in with the Google button.',
  'account.google.connectedAs': 'Connected: {email}',
  'account.google.connected': 'Connected',
  'account.google.notConnected': 'Not connected',
  'account.google.connect': 'Connect',
  'account.google.disconnect': 'Disconnect',
  'account.google.linked': 'Google account connected',
  'account.google.unlinked': 'Google account disconnected',
  'account.google.failed': "That didn't work. Try again.",
  'account.google.last': "It's your only way to sign in, so it can't be disconnected.",
  'auth.footer.company': 'LogiNet Systems Kft.',
  'auth.footer.privacy': 'Privacy',
  'auth.footer.help': 'Help',

  'title.forgot': 'Forgotten password',
  'title.reset': 'New password',

  'forgot.title': 'Forgotten password',
  'forgot.lead': 'Enter your email address and we will send a link for setting a new password.',
  'forgot.submit': 'Send the link',
  'forgot.working': 'Sending…',
  /* ⚠️⚠️ **"IF THERE IS AN ACCOUNT" IS LOAD-BEARING.** The server answers identically for an
     address that exists and one that does not; "we have sent you an email" would be false half the
     time, in the half where somebody waits for a message that is never coming. */
  'forgot.sent': 'If there is an account for this address, a link is on its way.',
  'forgot.sentNote': 'The link works for 2 hours. Check the spam folder too.',
  'forgot.backToSignIn': 'Back to signing in',

  'reset.title': 'New password',
  'reset.lead': 'Set a new password for this account.',
  'reset.password': 'New password',
  'reset.confirm': 'Confirm the new password',
  'reset.submit': 'Set the password',
  'reset.working': 'Saving…',
  'reset.expired': 'This link has expired. Ask for a new one.',
  'reset.used': 'This link has already been used. Ask for a new one.',
  'reset.unknown': 'This link is not valid. Ask for a new one.',
  'reset.weak': 'That password does not meet the rules.',
  'reset.requestAnother': 'Ask for a new link',
  'reset.done': 'Done, the new password is live.',
  'reset.doneSessions':
    'We signed you out on your other devices, as a precaution. Sign in with the new password.',
  'reset.signIn': 'Sign in',

  /* ══ C7 ═══════════════════════════════════════════════════════════════════════════════════ */

  'nav.projects': 'Projects',
  'nav.clients': 'Clients',
  'nav.main': 'Main navigation',
  /* ── Item 71 · the ⌘K palette. ───────────────────────────────────────────────────────── */
  'palette.placeholder': 'Search, or jump to…',
  'palette.empty': 'Nothing matches {query}',
  'palette.searching': 'Searching…',
  'palette.group.recent': 'Recently opened',
  'palette.group.actions': 'Actions',
  'palette.group.go': 'Go to',
  /* 17 Oct 2026 · the settings palette: the headings inside the sections, and what they hold. */
  'palette.group.headings': 'Inside the settings',
  'palette.settings.status': 'Statuses',
  'palette.settings.priority': 'Priorities',
  'palette.settings.skill': 'Skills',
  'palette.settings.skillCategory': 'Skill categories',
  'palette.settings.skillLevel': 'Skill levels',
  'palette.settings.schedule': 'Work schedules',
  'palette.settings.rateCard': 'Rate cards',
  'palette.settings.currency': 'Currencies',
  'palette.settings.leaveType': 'Leave types',
  'palette.settings.overtimeType': 'Overtime types',
  'palette.settings.holiday': 'Holidays',
  'palette.settings.metafield': 'Metafields',
  'palette.settings.projectRole': 'Project roles',
  'palette.settings.role': 'Roles',
  'palette.settings.integration': 'Integrations',
  'palette.settings.token': 'Access tokens',
  'palette.settings.passkey': 'Passkeys',
  'palette.settings.mySkill': 'My skills',
  'palette.group.tasks': 'Tasks',
  'palette.group.projects': 'Projects',
  'palette.group.people': 'People',
  'palette.group.reports': 'Reports',
  'palette.group.contracts': 'Contracts',
  'palette.group.lineItems': 'Line items',
  'palette.action.newTask': 'New task',
  'palette.action.newProject': 'New project',
  'nav.search': 'Search…',
  'nav.notifications': 'Notifications',
  'nav.unread': '{count} unread',
  'nav.markAllRead': 'Mark all read',
  'nav.noNotifications': 'Nothing here.',
  'notif.mentioned': 'You were mentioned in a comment',
  'notif.mentioned.by': '{actor} mentioned you in a comment',
  'notif.commented': 'A new comment on your task',
  'notif.commented.by': '{actor} commented on your task',
  'notif.assigned': 'You were assigned to a task',
  'notif.assigned.by': '{actor} assigned you to a task',
  'notif.status': 'The status changed on your task',
  'notif.dueSoon': 'A deadline is approaching',

  'nav.agent': 'Agent',
  'title.agent': 'Agent',
  'agent.hello.morning1': 'Good morning, {name}.',
  'agent.hello.morning2': 'Morning, {name}.',
  'agent.hello.morning3': 'Good morning, {name}. Have a good one.',
  'agent.hello.morning4': 'Hi, {name}. Good to see you.',
  'agent.hello.morning5': "Good morning, {name}. Hope the day's off to a good start.",
  'agent.hello.morning6': 'Hello, {name}. Glad you stopped by.',
  'agent.hello.morning7': 'Good morning, {name}. How are you today?',
  'agent.hello.afternoon1': 'Good afternoon, {name}.',
  'agent.hello.afternoon2': 'Hi, {name}. Glad to see you.',
  'agent.hello.afternoon3': "Hi, {name}. How's the day going?",
  'agent.hello.afternoon4': 'Have a nice afternoon, {name}.',
  'agent.hello.afternoon5': "Hi, {name}. Hope your day's going well.",
  'agent.hello.afternoon6': 'Hi, {name}. Nice to see you.',
  'agent.hello.afternoon7': 'Good afternoon, {name}. Glad you stopped by.',
  'agent.hello.afternoon8': 'Hi, {name}. What can I help with this afternoon?',
  'agent.hello.afternoon9': "Hi, {name}. Hope it's been a good day.",
  'agent.hello.evening1': 'Good evening, {name}.',
  'agent.hello.evening2': 'Evening, {name}.',
  'agent.hello.evening3': 'Hi, {name}. How did the day go?',
  'agent.hello.evening4': 'Good evening, {name}. Glad you stopped by.',
  'agent.hello.evening5': 'Hi, {name}. Still at it?',
  'agent.hello.evening6': 'Good evening, {name}. What can I help with?',
  'agent.hello.evening7': 'Hi, {name}. Hope you had a good day.',
  'agent.hello.evening8': 'Good evening, {name}. Nice to see you.',
  'agent.hello.evening9': 'Hi, {name}. Shall we wrap the day up?',
  'agent.hello.night1': 'Good evening, {name}.',
  'agent.hello.night2': 'Hi, {name}. What can I help with?',
  'agent.hello.night3': "Hi, {name}. I'm here if you need anything.",
  'agent.hello.night4': 'Good evening, {name}. Shall we get to it?',
  'agent.greeting': 'Good day. What shall we work on?',
  'agent.placeholder': 'How can I help?',
  'agent.reply.placeholder': 'Write a reply…',
  'agent.send': 'Send',
  'agent.stop': 'Stop',
  'agent.notConnected':
    'The Agent is not connected yet – there is no model behind it, so sending is disabled.',
  'agent.notInPlan': 'The Agent is not part of this workspace’s plan.',
  'agent.readOnly':
    'The Agent reads tasks and hours, and asks before it writes anything. Every figure comes from a real query.',
  /* ── C27.2 · the tool rows. See the Hungarian side for the argument. ────────────────────── */
  'agent.tool.tasks_search.running': 'Searching tasks…',
  'agent.tool.tasks_search.done': '{count} tasks',
  'agent.tool.tasks_get.running': 'Reading the task…',
  'agent.tool.tasks_get.done': 'Task read',
  'agent.tool.tasks_activity.running': 'Reading the activity…',
  'agent.tool.tasks_activity.done': 'Activity read',
  'agent.tool.worklogs_search.running': 'Searching worklog entries…',
  'agent.tool.worklogs_search.done': '{count} entries',
  'agent.tool.worklogs_overview.running': 'Adding up the hours…',
  'agent.tool.worklogs_overview.done': 'Hours read',
  'agent.tool.worklogs_log.running': 'Preparing the entry…',
  'agent.tool.worklogs_log.done': 'Waiting for you',
  'agent.tool.tasks_update.running': 'Preparing the change…',
  'agent.tool.tasks_update.done': 'Waiting for you',
  'agent.tool.tasks_create.running': 'Preparing the task…',
  'agent.tool.tasks_create.done': 'Waiting for you',
  'agent.tool.worklogs_update.running': 'Preparing the correction…',
  'agent.tool.worklogs_update.done': 'Waiting for you',
  /* ⚠️ C49 · R-36's two. The *done* row says what every other write's says: nothing has gone yet. */
  'agent.tool.worklogs_delete.running': 'Preparing the deletion…',
  'agent.tool.worklogs_delete.done': 'Waiting for you',
  'agent.tool.tasks_delete.running': 'Preparing the deletion…',
  'agent.tool.tasks_delete.done': 'Waiting for you',
  'agent.tool.projects_search.running': 'Searching projects…',
  'agent.tool.projects_search.done': '{count} projects',
  'agent.tool.projects_get.running': 'Reading the project…',
  'agent.tool.projects_get.done': 'Project read',
  'agent.tool.people_search.running': 'Searching people…',
  'agent.tool.people_search.done': '{count} people',
  /* ── C44 · the contract spine's eight. See the Hungarian side. ──────────────────────────── */
  'agent.tool.contracts_search.running': 'Searching contracts…',
  'agent.tool.contracts_search.done': '{count} contracts',
  'agent.tool.contracts_get.running': 'Reading the contract…',
  'agent.tool.contracts_get.done': 'Contract read',
  'agent.tool.line_items_search.running': 'Reading the line items…',
  'agent.tool.line_items_search.done': '{count} line items',
  'agent.tool.contracts_worklogs.running': 'Reading the contract’s hours…',
  'agent.tool.contracts_worklogs.done': '{count} entries',
  'agent.tool.contracts_create.running': 'Preparing the contract…',
  'agent.tool.contracts_create.done': 'Waiting for you',
  'agent.tool.contracts_update.running': 'Preparing the contract change…',
  'agent.tool.contracts_update.done': 'Waiting for you',
  'agent.tool.line_items_create.running': 'Preparing the line item…',
  'agent.tool.line_items_create.done': 'Waiting for you',
  'agent.tool.line_items_update.running': 'Preparing the line item change…',
  'agent.tool.line_items_update.done': 'Waiting for you',
  /* ── Phase 3 and 4 · working time, resource planning, money, reports. See the Hungarian side. ── */
  'agent.tool.leave_requests_search.running': 'Searching leave requests…',
  'agent.tool.leave_requests_search.done': '{count} leave requests',
  'agent.tool.leave_requests_get.running': 'Reading the leave request…',
  'agent.tool.leave_requests_get.done': 'Leave request read',
  'agent.tool.leave_balances.running': 'Reading the leave balances…',
  'agent.tool.leave_balances.done': 'Leave balances read',
  'agent.tool.leave_requests_decide.running': 'Preparing the decision…',
  'agent.tool.leave_requests_decide.done': 'Waiting for you',
  'agent.tool.overtime_requests_search.running': 'Searching overtime claims…',
  'agent.tool.overtime_requests_search.done': '{count} overtime claims',
  'agent.tool.overtime_requests_decide.running': 'Preparing the decision…',
  'agent.tool.overtime_requests_decide.done': 'Waiting for you',
  'agent.tool.holidays_search.running': 'Reading the holiday calendar…',
  'agent.tool.holidays_search.done': '{count} calendar entries',
  'agent.tool.schedules_search.running': 'Reading the work schedules…',
  'agent.tool.schedules_search.done': 'Work schedules read',
  'agent.tool.allocations_search.running': 'Searching allocations…',
  'agent.tool.allocations_search.done': '{count} allocations',
  'agent.tool.allocations_create.running': 'Preparing the allocation…',
  'agent.tool.allocations_create.done': 'Waiting for you',
  /* R-46 · clients, projects and project members. */
  'agent.tool.clients_search.running': 'Searching clients…',
  'agent.tool.clients_search.done': '{count} clients',
  'agent.tool.project_members_search.running': 'Reading the project’s members…',
  'agent.tool.project_members_search.done': '{count} members',
  'agent.tool.projects_create.running': 'Preparing the project…',
  'agent.tool.projects_create.done': 'Waiting for you',
  'agent.tool.projects_update.running': 'Preparing the project changes…',
  'agent.tool.projects_update.done': 'Waiting for you',
  'agent.tool.project_members_add.running': 'Preparing the new project members…',
  'agent.tool.project_members_add.done': 'Waiting for you',
  'agent.tool.task_comments_add.running': 'Preparing the comment…',
  'agent.tool.task_comments_add.done': 'Waiting for you',
  'agent.tool.task_comments_update.running': 'Preparing the comment edit…',
  'agent.tool.task_comments_update.done': 'Waiting for you',
  'agent.tool.task_links_add.running': 'Preparing the link…',
  'agent.tool.task_links_add.done': 'Waiting for you',
  'agent.tool.task_dependencies_add.running': 'Preparing the dependency…',
  'agent.tool.task_dependencies_add.done': 'Waiting for you',
  'agent.tool.allocations_update.running': 'Preparing the allocation change…',
  'agent.tool.allocations_update.done': 'Waiting for you',
  'agent.tool.external_costs_update.running': 'Preparing the cost change…',
  'agent.tool.external_costs_update.done': 'Waiting for you',
  'agent.tool.overtime_requests_raise.running': 'Preparing the overtime claim…',
  'agent.tool.overtime_requests_raise.done': 'Waiting for you',
  'agent.tool.reports_update.running': 'Preparing the report change…',
  'agent.tool.reports_update.done': 'Waiting for you',
  'agent.tool.reports_duplicate.running': 'Preparing the report copy…',
  'agent.tool.reports_duplicate.done': 'Waiting for you',
  'agent.tool.reports_refresh.running': 'Preparing the report refresh…',
  'agent.tool.reports_refresh.done': 'Waiting for you',
  'agent.tool.clients_create.running': 'Preparing the client…',
  'agent.tool.clients_create.done': 'Waiting for you',
  'agent.tool.clients_update.running': 'Preparing the client changes…',
  'agent.tool.clients_update.done': 'Waiting for you',
  'agent.tool.project_members_update.running': 'Preparing the membership change…',
  'agent.tool.project_members_update.done': 'Waiting for you',
  'agent.tool.task_relations_search.running': 'Reading the links and dependencies…',
  'agent.tool.task_relations_search.done': '{count} links and dependencies',
  'agent.tool.task_links_delete.running': 'Preparing the link removal…',
  'agent.tool.task_links_delete.done': 'Waiting for you',
  'agent.tool.task_dependencies_delete.running': 'Preparing the dependency removal…',
  'agent.tool.task_dependencies_delete.done': 'Waiting for you',
  'agent.tool.task_comments_delete.running': 'Preparing the comment removal…',
  'agent.tool.task_comments_delete.done': 'Waiting for you',
  'agent.tool.allocations_delete.running': 'Preparing the allocation removal…',
  'agent.tool.allocations_delete.done': 'Waiting for you',
  'agent.tool.external_costs_delete.running': 'Preparing the cost removal…',
  'agent.tool.external_costs_delete.done': 'Waiting for you',
  'agent.tool.reports_delete.running': 'Preparing the report deletion…',
  'agent.tool.reports_delete.done': 'Waiting for you',
  'agent.tool.project_members_delete.running': 'Preparing the membership removal…',
  'agent.tool.project_members_delete.done': 'Waiting for you',
  'agent.tool.leave_requests_create.running': 'Preparing the leave request…',
  'agent.tool.leave_requests_create.done': 'Waiting for you',
  'agent.tool.invoices_search.running': 'Searching invoices…',
  'agent.tool.invoices_search.done': '{count} invoices',
  'agent.tool.invoices_create.running': 'Preparing the invoice…',
  'agent.tool.invoices_create.done': 'Waiting for you',
  'agent.tool.invoices_set_status.running': 'Preparing the status change…',
  'agent.tool.invoices_set_status.done': 'Waiting for you',
  'agent.tool.external_costs_search.running': 'Searching external costs…',
  'agent.tool.external_costs_search.done': '{count} external costs',
  'agent.tool.external_costs_create.running': 'Preparing the external cost…',
  'agent.tool.external_costs_create.done': 'Waiting for you',
  'agent.tool.contracts_finance.running': 'Reading the contract’s finances…',
  'agent.tool.contracts_finance.done': 'Finances read',
  'agent.tool.rate_cards_search.running': 'Reading the rate cards…',
  'agent.tool.rate_cards_search.done': 'Rate cards read',
  'agent.tool.reports_run.running': 'Running the report…',
  'agent.tool.reports_run.done': '{count} rows',
  'agent.tool.reports_search.running': 'Searching saved reports…',
  'agent.tool.reports_search.done': '{count} reports',
  'agent.tool.reports_get.running': 'Reading the report…',
  'agent.tool.reports_get.done': 'Report read',
  'agent.tool.reports_create.running': 'Preparing to save the report…',
  'agent.tool.reports_create.done': 'Waiting for you',
  'agent.tool.unknown.running': 'Reading…',
  'agent.tool.unknown.done': 'Read',
  'agent.tool.failed': 'That read did not work',
  'agent.tool.notRun': 'Not run – a confirmation is waiting',
  'agent.tool.repeated': '×{count}',

  /* ── C27.3 · the confirmation card. See the Hungarian side for the argument. ─────────────── */
  /* ── C43 · a file handed to the Agent. See the Hungarian side. ──────────────────────────── */
  'agent.attach': 'Attach a file',
  'agent.attachHint': 'Attach a CSV or Excel (.xlsx) file – I can read the rows of a table',

  /* ── C47 · a file belongs where it was sent, and it keeps ──────────────────────────────────
     ⚠️ C43's note here said every string about a file lives under `task.attachment.*`, because C22's
     component drew the list. That stopped being true: a chip above its own message, a strip above
     the composer and a browse panel are three surfaces `task.attachment.*` has no words for — one of
     them says *"not sent yet"* and another says *"which thread"*. The download string is a separate
     key on purpose: the same sentence, but the two point at different lists, and one shared key
     would move both the next time either is reworded. */
  'agent.files.browse': 'Earlier files',
  'agent.files.title': 'Files',
  'agent.files.thisThread': 'In this conversation',
  'agent.files.allThreads': 'All uploaded files',
  /* ⚠️ **Not decoration.** The chip leaves the composer the moment the question is sent, and if it
     never said where it was going, that disappearance reads as a lost upload. */
  'agent.files.notSentYet': 'not sent yet',
  'agent.files.sent': 'sent',
  'agent.files.noneHere': 'Nothing is attached to this conversation.',
  'agent.files.noneAtAll': 'You have not sent the Agent a file yet.',
  'agent.files.download': 'Download {name}',
  'agent.files.remove': 'Remove {name}',
  'agent.files.reuse': 'Attach {name} to this conversation',
  /* ⚠️ **The button says what happens, not what it is.** *"Re-use"* is a category; this is a
     sentence about the file going with the next question. */
  'agent.files.reuseHint': 'Attach to your next question',
  /* ⚠️⚠️ **C48-1 · IT SAYS WHAT IS ACCEPTED, not *"that did not work"*.** One sentence serves both
     the browser's own filter (a dropped `.pdf`, which never leaves the tab) and the server's
     `unsupported_file_type` refusal — one fact, one sentence, whichever side answered. */
  'agent.files.notTabular':
    'The Agent can read tables and images: CSV, TSV, TXT, .xlsx, PNG, JPG or WEBP.',
  /* ⚠️ Over a dragged file. Not *"release"* — that would be a button's label; this says the drop
     belongs here. */
  'agent.drop.title': 'Drop the file here',
  'agent.drop.hint': '{kinds} · {mb} MB at most',

  'agent.proposal.worklogs_log': 'Confirm the entry',
  'agent.proposal.tasks_update': 'Confirm the change',
  'agent.proposal.tasks_create': 'Confirm the new task',
  'agent.proposal.worklogs_update': 'Confirm the correction',
  /* ⚠️ C27.6 · a card covering more than one row. See the Hungarian side. */
  'agent.proposal.worklogs_log_many': 'Confirm the entries',
  'agent.proposal.tasks_update_many': 'Confirm the changes',
  'agent.proposal.tasks_create_many': 'Confirm the new tasks',
  'agent.proposal.worklogs_update_many': 'Confirm the corrections',
  /* ⚠️⚠️ C49 · R-36's two. **The heading names the act rather than the confirmation** — see the
     Hungarian side: this is the one card nothing takes back. */
  'agent.proposal.worklogs_delete': 'Delete the entry',
  'agent.proposal.worklogs_delete_many': 'Delete the entries',
  'agent.proposal.tasks_delete': 'Delete the task',
  'agent.proposal.tasks_delete_many': 'Delete the tasks',
  /* ⚠️ C44 · the contract spine's four. */
  'agent.proposal.contracts_create': 'Confirm the new contract',
  'agent.proposal.contracts_create_many': 'Confirm the new contracts',
  'agent.proposal.contracts_update': 'Confirm the contract change',
  'agent.proposal.contracts_update_many': 'Confirm the contract changes',
  'agent.proposal.line_items_create': 'Confirm the new line item',
  'agent.proposal.line_items_create_many': 'Confirm the new line items',
  'agent.proposal.line_items_update': 'Confirm the line item change',
  'agent.proposal.line_items_update_many': 'Confirm the line item changes',
  'agent.proposal.unknown': 'Confirm',
  'agent.proposal.accept': 'Record it',
  /* ⚠️⚠️ C49 · *"Record it"* on a card that deletes eight hours would be the most misleading control
     in the product. What somebody reads at the moment of pressing has to be what then happens. */
  'agent.proposal.accept_delete': 'Delete it',
  /* ⚠️ C46 · *"Correct"* rather than *"Save"*: nothing is saved — a new confirmation comes back. */
  'agent.proposal.revise': 'Correct it',
  'agent.proposal.dirty': 'Send the correction first',
  'agent.proposal.derived': 'The Agent’s suggestion',
  'agent.proposal.decline': 'Discard',
  'agent.proposal.working': 'Sending…',
  'agent.proposal.window': 'Valid for 15 minutes',
  /* ⚠️ Not *"Recorded"* — see the Hungarian side. What the write did is the reply below. */
  'agent.proposal.approved': 'Approved',
  'agent.proposal.declined': 'Discarded',
  'agent.proposal.expired': 'This confirmation expired – ask again',
  'agent.proposal.used': 'You have already answered this one',

  /* ── C27.2 · the data cards. ────────────────────────────────────────────────────────────── */
  'agent.data.tasks': 'Tasks',
  'agent.data.worklogs': 'Worklog entries',
  'agent.data.overview': 'Hours',
  'agent.data.readAt': 'Read at {at}',
  'agent.data.of': '{shown} of {total} – narrow the question to see the rest',
  'agent.data.first': 'The first {count} rows – there are more, and they are not counted',
  'agent.data.empty.title': 'Nothing matched',
  'agent.data.empty.body':
    'The Agent ran the search and found no rows. That is the answer, not a failure.',
  'agent.data.tasks.dropped': 'Project, priority and estimate are on the task list.',
  'agent.data.missing': '{count} of these rows are no longer visible to you.',
  'agent.data.subject.own': 'Your hours',
  'agent.data.subject.everyone': 'The workspace’s hours',
  'agent.data.noHours': 'Nothing logged this week.',
  'agent.thinking': 'Thinking',
  'agent.thinkingShow': 'Show reasoning',
  'agent.thinkingHide': 'Hide reasoning',
  'agent.you': 'You',
  'agent.agent': 'Agent',
  'agent.working': 'Working on it…',
  'agent.untitled': 'New conversation',
  /* 19 Oct 2026 · AG-1 · the dashboard chat on a project's Áttekintés and Pénzügy tabs. */
  'dashChat.placeholder': 'Ask about this dashboard…',
  'dashChat.reply': 'Reply…',
  'dashChat.title': 'Dashboard assistant',
  'dashChat.empty':
    'Ask anything about what this dashboard shows. Use @ to point at a contract, line item, period, person, task, invoice or external cost.',
  'dashChat.history': 'Earlier conversations',
  'dashChat.historySearch': 'Search conversations…',
  'dashChat.historyEmpty': 'No conversations about this project yet.',
  'dashChat.new': 'New conversation',
  'dashChat.mention': 'Mention (@)',
  'dashChat.send': 'Send',
  'dashChat.expand': 'Expand',
  'dashChat.shrink': 'Shrink',
  'dashChat.minimise': 'Minimise',
  'dashChat.thinking': 'Thinking…',
  'dashChat.reading': 'Reading the figures…',
  'dashChat.mentions': 'Things to mention',
  'dashChat.mentionsEmpty': 'No matches.',
  'dashChat.mentionsLoading': 'Loading…',
  'dashChat.more': 'Show {count} more…',
  'dashChat.kind.contract': 'Contracts',
  'dashChat.kind.lineItem': 'Line items',
  'dashChat.kind.period': 'Periods',
  'dashChat.kind.person': 'People',
  'dashChat.kind.task': 'Tasks',
  'dashChat.kind.invoice': 'Invoices',
  'dashChat.kind.externalCost': 'External costs',
  /* ── C27.4 · the history menu. See the Hungarian side. ───────────────────────────────────── */
  'agent.history.label': 'Earlier conversations',
  'agent.history.new': 'New conversation',
  'agent.history.empty': 'No conversations yet',
  'agent.history.search': 'Search conversations…',
  'agent.history.delete': 'Delete conversation',
  'agent.history.confirmDelete':
    'Delete this conversation? Its replies and any write waiting for confirmation go with it.',
  'agent.history.loadFailed': 'That conversation could not be opened.',
  'agent.history.rename': 'Rename conversation',
  'agent.history.renameField': 'Conversation name',
  'agent.history.renameSave': 'Save name',
  'agent.history.renameFailed': 'The conversation could not be renamed.',
  /* ── C53 · the setup prompt. See the Hungarian side. ─────────────────────────────────────── */
  'agent.setup.label': 'Set up your own agent',
  'agent.setup.copied': 'Copied, paste it in',
  'agent.setup.copiedToast': 'MCP connection instructions copied',
  'agent.setup.copiedHint': 'Paste them into the agent of your choice.',
  'agent.setup.failed': 'Copy failed',
  'agent.setup.instruction': 'Fetch and follow the Forecastify setup instructions at {url}',
  'agent.error.rate_limited':
    'This workspace has used up its Agent allowance for today. It is available again tomorrow.',
  'agent.error.turn_too_long':
    'This question ran too long and was stopped. Try again, or split it into smaller questions – your question is still in the conversation.',
  'agent.error.upstream_unavailable':
    'The model did not answer. Try again – your question is still in the conversation.',
  'agent.error.conversation_not_found': 'That conversation cannot be found.',
  'agent.error.proposal_not_found': 'This confirmation is no longer valid.',
  'agent.error.proposal_expired': 'This confirmation expired. Ask the Agent again.',
  'agent.error.proposal_already_used': 'You have already answered this confirmation.',
  'agent.error.conversation_not_renamable': 'A channel opened over MCP keeps its client’s name.',
  'agent.error.unknown': 'The Agent could not answer. Try again.',
  'agent.turn.noAnswer': 'This question got no answer.',
  'agent.turn.compacted':
    'The agent summarised the conversation so far – it remembers earlier details from that summary.',
  'agent.turn.download': 'Download this reply as a Markdown file',
  'agent.toasts': 'Agent notifications',
  'agent.export.run': 'Export',
  'agent.export.since': 'from {day}',
  'agent.export.until': 'until {day}',
  'agent.export.everything': 'all time',
  'notif.status.by': '{actor} moved your task',

  'nav.workspaceMenu': 'Workspace',
  'nav.userMenu': 'Account',
  'nav.dashboardsSection': 'Dashboardok',
  'nav.workspaceSection': 'Workspace',
  'title.home': 'Home',
  'title.myTasks': 'My tasks',
  'title.myProjects': 'My projects',
  'nav.home': 'Home',
  'nav.myTasks': 'My tasks',
  'nav.contracts': 'Contracts',
  'title.contracts': 'Contracts',
  'contracts.search': 'Search by code or name…',
  'contract.owner.search': 'Search by name…',
  'contracts.toast.bulkStatus': '{count} contracts updated',
  'contracts.empty.title': 'No contracts yet',
  'contracts.empty.body':
    'A contract lives under a project and breaks into line items. Hours reach it through the tasks.',
  'contract.openEnded': 'open',
  'contract.noProject': 'No project',
  'contract.scope.onProject': 'On a project',
  'contract.scope.onClient': 'On a client',
  'contract.field.scope': 'Belongs to',
  'contract.error.client': 'Choose a client.',
  'contracts.mode': 'Switch view',
  'task.field.lineItem': 'Line item',
  'task.field.lineItemNone':
    'This project has no line item yet. Create a contract with a line item first.',
  'lineItem.assignment': 'Assignment',
  'lineItem.people': 'People',
  'lineItem.people.anyone': 'Anyone',
  'lineItem.people.search': 'Search people…',
  'lineItem.shared.tip': 'This ticket is also on another line item: {names}',
  'lineItem.shared.open': 'Open {name}',
  'lineItem.shared.unknown': 'another line item',
  'lineItem.move.currentlyOn': 'now on {name}',
  'lineItem.move.title': 'Move task',
  'lineItem.move.body':
    '{task} and the hours logged on it ({hours}) will move from {from} to {to}.',
  'lineItem.move.confirm': 'Move',
  'lineItem.assignClosed': 'A closed line item takes no new tasks. Reopen it to assign one.',
  'lineItem.error.internalBillable': 'An internal project takes non-billable line items only.',
  'lineItem.people.tip.what':
    'Pick people and this line item counts only their hours, inside its own dates. With nobody picked (Anyone), it takes everyone’s hours on its tasks.',
  'lineItem.people.tip.note':
    'The rate is still the line item’s, not the person’s rate card. An hour no line item takes still counts towards the whole project, and raises an alert.',
  'lineItem.error.peopleNeedTm':
    'Only a time-and-material line item can name people — clear the people first, then change the type.',
  'lineItem.error.personNotMember':
    '{people} was not a member of the project during the line item’s dates, so cannot be named on it.',
  'worklog.unidentified':
    'This time bills to no line item: the task’s line items name other people, or name you only for other dates.',
  'lineItem.openContract': 'Open the contract',
  'lineItem.tasks': 'Tasks',
  'lineItem.tasks.empty':
    'No tasks are assigned here yet. Hours reach a line item through its tasks.',
  'lineItem.tasks.emptyFiltered': 'No task matches the filters.',
  'lineItem.tasks.search': 'Search a key, a title or an assignee…',
  'lineItem.tasks.unassigned': 'Unassigned',
  'lineItem.tasks.total': '{count} tasks · {hours}',
  'lineItem.taskKey': 'Key',
  'lineItem.source': 'Source',
  'lineItem.assign': 'Assign a task',
  'lineItem.target.title': 'Sync',
  'lineItem.target.hint':
    'Collects the issues of this tracker project; the rule below narrows them.',
  'lineItem.target.hintNone':
    'Syncs nothing. Pick an integration and a project, and its issues arrive here.',
  'lineItem.target.syncNow': 'Sync now',
  'lineItem.target.instance': 'Integration',
  'lineItem.target.filtersFirst':
    'Set the filters before saving: only the issues they match come onto the line item.',
  'lineItem.target.noInstance': 'None',
  'lineItem.target.project': 'Tracker project',
  'lineItem.target.noProjects': 'No project is reachable on this integration',
  'lineItem.target.unreachable': 'unreachable',
  'lineItem.target.unreachableBody':
    'The {key} project is unreachable: the integration can no longer see it, or it was archived. Until that changes, this line item syncs nothing.',
  'lineItem.target.writes': 'Write to the tracker',
  'lineItem.target.issueType': 'New issues are created as {type}',
  'lineItem.target.noIssueType': 'The type for new issues is chosen at the first sync',
  'lineItem.target.issueTypeLabel': 'New issue type',
  'lineItem.target.issueTypeAuto': 'Chosen at the first sync',
  'lineItem.target.issueTypeSubtask': '{type} (sub-task – cannot be created without a parent)',
  'lineItem.target.issueTypesLoading': 'Loading types…',
  'lineItem.target.issueTypesFailed': 'Could not read the types from the tracker',
  'lineItem.target.noIssueTypes': 'This project offers no type to choose',
  'lineItem.target.epicHint':
    'Jira sometimes asks for an extra field to create an Epic (such as Epic Name) – if it refuses, the run history shows it.',
  'lineItem.assignSearch': 'Search by key or title…',
  'lineItem.unassign': 'Unassign {key}',
  'lineItem.byHand': 'by hand',
  'lineItem.byRule': 'by rule',
  'lineItem.mode.manual': 'By hand',
  'lineItem.mode.manualLong': 'Assigned by hand',
  'lineItem.mode.question': 'How do tasks get here?',
  'lineItem.settings': 'How tasks are assigned',
  'lineItem.target.panelLabel': 'Sync from a tracker',
  'lineItem.target.none': 'Not synced',
  'lineItem.target.syncShort': 'Sync',
  'rule.summary': 'Rule: {rule}',
  'rule.summaryNone': 'no condition',
  'rule.summaryWhole': 'the whole tracker project',
  'rule.conditionsLead': 'Conditions:',
  'rule.conditionsTail': 'must hold',
  'lineItem.mode.rule': 'By rule',
  'lineItem.period': 'Period',
  'lineItem.open': 'Open',
  'lineItem.closed': 'Closed',
  'lineItems.search': 'Search line items…',
  'lineItems.footer': '{count} line items',
  'lineItems.empty.title': 'No line items yet',
  'lineItems.empty.body': 'A line item lives under a contract, and tasks point at it.',
  'contract.unknown': 'That contract does not exist, or is not visible in this workspace.',
  'contract.progress': 'Estimate {estimate} · tracked {tracked}',
  'common.count': '{count}',
  'calendar.months':
    'January,February,March,April,May,June,July,August,September,October,November,December',
  'calendar.weekdays': 'Mon,Tue,Wed,Thu,Fri,Sat,Sun',
  /** ⚠️ Short forms for lists, not for a calendar's column headings — see the Hungarian note. */
  'calendar.weekdaysAbbr': 'Mon,Tue,Wed,Thu,Fri,Sat,Sun',
  'calendar.monthsAbbr': 'Jan,Feb,Mar,Apr,May,Jun,Jul,Aug,Sep,Oct,Nov,Dec',
  'calendar.today': 'Today',
  'calendar.clear': 'Clear',
  'calendar.previousMonth': 'Previous month',
  'calendar.nextMonth': 'Next month',
  'calendar.placeholder': 'Pick a date',
  'calendar.dialogLabel': 'Date picker',
  'common.more': 'More actions',
  'common.next': 'Next page',
  'common.previous': 'Previous page',
  'title.contracts.one': 'Contract',
  'contract.action.close': 'Close the contract',
  'contract.action.detach': 'Take off the project',
  'contract.detached': 'Contract detached',
  'contract.detachedNote':
    '{name} now stands under the client, and can carry only work-free line items.',
  'contract.error.stillBound':
    '{count} tasks and {hours} of logged time tie this contract to {project}. Close or move them before detaching it.',
  'contract.action.reopen': 'Reactivate',
  'contract.action.copyLink': 'Copy link',
  'contract.action.openProject': 'Open the project',
  'contract.action.rename': 'Rename',
  'contract.attachment.add': 'Attach a file',
  'contract.closedNotice':
    'This contract is closed: no new task can go on its line items, and no time can be logged against them.',
  'contract.every.month': 'Monthly',
  'contract.every.months': 'Every {step} months',
  'contract.every.week': 'Weekly',
  'contract.every.weeks': 'Every {step} weeks',
  'contract.recurrence.lastDay': 'Last day',
  'contract.occurrences': 'Periods',
  'contract.occurrence': 'Period',
  'occurrence.count': '{count} periods',
  'occurrence.plan': 'Plan',
  'occurrence.effort': 'Effort',
  'occurrence.partial': 'partial',
  'occurrence.state.planned': 'Planned',
  'occurrence.state.active': 'Active',
  'occurrence.state.closed': 'Closed',
  'occurrence.tip.planned': 'A planned period – it has not started. Time opens it.',
  'occurrence.tip.active': 'The active period – it is running now, and this is where hours land.',
  'occurrence.tip.closed': 'A closed period – time closed it.',
  'occurrence.collectorNote': 'hours land here',
  'period.previous': 'Previous period',
  'period.next': 'Next period',
  'period.export': 'Export',
  'period.reopened': 'reopened',
  'period.closedWhy':
    'This period has ended, so it closed – nobody closed it by hand. If someone is behind on logging, you can reopen it for late entry.',
  'period.index': 'Number',
  'period.people': 'People',
  'period.budget': 'Budget',
  'period.fact': 'Actual',
  'period.difference': 'Difference',
  'period.factOfBudget': 'Actual {fact} of {budget}',
  'period.reopen': 'Reopen for late entry',
  /* 17 Oct 2026 · a period row's menu — the phase 2 mockup's `occMenu`. */
  /* 17 Oct 2026 · the period page's grouping, restored over the whole filtered set. */
  'period.group.none': 'No grouping',
  'period.group.member': 'By person',
  'period.group.lineItem': 'By line item',
  'period.group.day': 'By day',
  'period.group.task': 'By task',
  'period.groupCount': '{count} entries',
  'period.menu.open': 'Open',
  'period.menu.openFull': 'Open in full view',
  'period.menu.exportCsv': 'Export worklogs (CSV)',
  'period.menu.exportXlsx': 'Export worklogs (XLSX)',
  'period.menu.openLogs': 'Open worklogs',
  'period.menu.copy': 'Copy period summary',
  'period.menu.copied': 'Copied to the clipboard',
  'period.menu.missing': '{hours} missing',
  'period.menu.noneMissing': 'nothing missing',
  'period.menu.stillMissing': '{hours} still missing',
  'period.menu.planMet': 'the plan is met',
  'period.menu.byTime': 'The calendar sets the status',
  'logs.filter.contract': 'Contract',
  'period.close': 'Close it again',
  'period.reopenHint': 'Colleagues can fill in what they missed until you close it again.',
  'period.stillShort': '{hours} still missing.',
  'period.metItsBudget': 'The budget is met – it can be closed again.',
  'period.partialNote':
    'The recurrence would have ended on {natural}; the contract ends on {actual}.',
  'period.noCollector': 'no recurring task',
  'period.contractLevel': 'Contract level',
  'period.logs': 'Time logs',
  'period.logCount': '{count} entries',
  'period.search': 'Search a person or a task…',
  'period.who': 'Who',
  'period.empty': 'No time is logged in this period yet.',
  'period.emptyFiltered': 'Nothing matches the filters.',
  'period.total': '{count} entries · {hours}',
  'period.unknown': 'This contract has no period starting then.',
  'period.exported': 'The period’s time logs are exported.',
  'contract.recurrence.times': '{count} times',
  'contract.recurrence.until': 'until {date}',
  'lineItem.actions': 'Line item actions',
  'lineItem.action.open': 'Open',
  'lineItem.action.close': 'Close',
  'lineItem.action.reopen': 'Reopen',
  'lineItem.action.delete': 'Delete',
  'lineItem.action.deleteBlocked': 'Delete – {count} tasks are on it',
  'lineItem.assignHint': 'You pick by hand which tickets belong here.',
  'lineItem.assignedNote.rule': '{rule} by rule · {manual} by hand · {excluded} excluded',
  'lineItem.assignHintRule': 'The rule brings tasks in – you can add one by hand as well.',
  'lineItem.exclude': 'Exclude {key} from the rule',
  'lineItem.overlap':
    '{count} tasks match this rule but bill elsewhere – another line item claimed them first:',
  'rule.matches': 'Matches',
  'rule.matchesSuffix': 'condition',
  'rule.match.all': 'every',
  'rule.match.any': 'any',
  'rule.field': 'Field',
  'rule.op': 'Operator',
  'rule.value': 'Value',
  'rule.pickValue': 'Pick one…',
  'rule.addCondition': 'Add a condition',
  'rule.wholeProject':
    "With no condition every issue of the tracker project lands here. A Project condition cannot narrow it: the project is set on the line item's target.",
  'rule.removeCondition': 'Remove the condition',
  'rule.excluded': 'Excluded:',
  'rule.excludedLabel': 'Excluded',
  'rule.conditions': 'Conditions',
  'rule.restore': 'Put {key} back',
  'rule.field.priority': 'Priority',
  'rule.field.assignee': 'Assignee',
  'rule.field.tag': 'Tag',
  'rule.op.eq': 'is',
  'rule.op.ne': 'is not',
  'rule.op.contains': 'contains',
  'rule.placeholder.key': 'e.g. HSO-',
  'rule.placeholder.text': 'Text',
  'rule.field.component': 'Component',
  'rule.field.fixVersion': 'Fix version',
  'rule.field.epic': 'Epic',
  'rule.op.in': 'is one of',
  'rule.pickValues': 'Pick one or more…',
  'rule.addValue': 'Add…',
  'rule.removeValue': 'Remove {value}',
  'rule.placeholder.list': 'Separate with Enter or a comma',
  'rule.searchValues': 'Search…',
  'rule.noValues': 'No such value on this project’s Jira issues',
  'rule.moreValues': 'Show {count} more values',
  'rule.fewerValues': 'Show fewer',
  'lineItem.assignedNote': '{count} tasks assigned by hand',
  'lineItem.closedBadge': 'closed line item',
  'lineItem.option.closed': '{label} – closed',
  'lineItem.unknown': 'No line item with that number on this contract.',
  'contract.details': 'Details',
  'contract.finance': 'Finance',
  'contract.attachments': 'Attachments',
  'contract.metafields': 'Metafields',
  'contract.attachments.empty': 'Nothing is attached to this contract yet.',
  'lineItem.money': 'Finance',
  'lineItem.money.rate': 'Rate',
  'lineItem.money.currency': 'Currency',
  'lineItem.money.budget': 'Budget',
  'lineItem.money.revenue': 'Revenue',
  'lineItem.money.budgetPerPeriod': 'Budget / period',
  'lineItem.money.revenuePerPeriod': 'Revenue / period',
  'lineItem.money.totalOver': '× {count} periods = {total}',
  'lineItem.money.tip.perPeriod':
    'On a recurring contract you enter it per period. The total is this × the periods the line item spans.',
  'lineItem.money.tip.invoiced.whatRecurring':
    'A recurring contract is invoiced by period, not by line item. Every period with an invoice (drafts count, voided ones do not) counts at this line item’s revenue for one period.',
  'lineItem.money.tip.invoiced.calcRecurring':
    'revenue per period × invoiced periods + invoices raised on the line item before',
  'lineItem.money.cost': 'Cost',
  /* 17 Oct 2026 · spend in money, in every line item and period list. */
  'money.spend': 'Spend',
  'lineItem.money.margin': 'Margin',
  'lineItem.money.invoiced': 'Invoiced',
  'lineItem.money.remaining': 'Remaining',
  'lineItem.money.spent': '{pct}% of the budget spent',
  'lineItem.money.unrated':
    '{count} people have no rate card in force – their hours are not in the cost.',
  'lineItem.money.tip.rate.what': 'What the client pays per hour or per day on this line item.',
  'lineItem.money.tip.rate.note':
    'The revenue is worked out from this and the logged hours. A day rate counts a day as 8 hours.',
  'lineItem.money.tip.budget.what':
    'The internal cost frame set for this line item – what the work should cost us at most.',
  'lineItem.money.tip.budget.note':
    'It is compared with the cost: the bar at the bottom of the card shows how much of it is spent.',
  'lineItem.money.tip.revenue.what':
    'The price agreed with the client – what this line item earns.',
  'lineItem.money.tip.revenue.whatTm':
    'What this line item has earned so far: the hours logged against it at the rate.',
  'lineItem.money.tip.revenue.calcTm': 'logged hours × hourly rate (day rate ÷ 8)',
  'lineItem.money.tip.revenue.noteTm': 'Nobody types it – it grows with every new worklog.',
  'lineItem.money.tip.revenue.whatNone': 'A non-billable line item, so it earns nothing.',
  'lineItem.money.tip.currency.what': 'Every figure on this line item is in this currency.',
  'lineItem.money.tip.currency.note':
    "The cost is converted into it from each person's rate card, at the workspace's exchange rates.",
  'lineItem.money.tip.cost.what': 'What the work on this line item has cost us so far.',
  'lineItem.money.tip.cost.calc': "Σ (logged hours × the person's cost rate on that day)",
  'lineItem.money.tip.cost.note':
    'Each hour is priced at the rate card in force on the day it was logged. Hours by somebody with no rate card cost nothing, so the figure is lower than the real one then.',
  'lineItem.money.tip.margin.what': 'How much of the revenue is left once the cost is taken off.',
  'lineItem.money.tip.margin.calc': '(revenue − cost) / revenue',
  'lineItem.money.tip.margin.note':
    'With no revenue there is no margin. A negative figure is a loss, so it is red.',
  'lineItem.money.tip.invoiced.what':
    'The invoices raised against this line item – drafts count, voided ones do not.',
  'lineItem.money.tip.invoiced.note':
    'The bar under it shows how much of the revenue has been billed.',
  'lineItem.money.tip.remaining.what': 'What is still left to invoice out of the revenue.',
  'lineItem.money.tip.remaining.calc': 'revenue − invoiced, never below 0',
  'lineItem.money.tip.remaining.note':
    'Billing ahead keeps it at zero – over-billing shows as the invoiced figure exceeding the revenue.',
  'lineItem.alloc': 'Resource allocations',
  'lineItem.alloc.planned': 'Planned',
  'lineItem.alloc.logged': 'Logged',
  'lineItem.alloc.remaining': 'Plan remaining',
  'lineItem.alloc.ofEstimate': '{pct}% of the estimate',
  'lineItem.alloc.noEstimate': 'no estimate',
  'lineItem.alloc.ofPlan': '{pct}% of the plan',
  'lineItem.alloc.noPlan': 'no plan',
  'lineItem.alloc.estimate': 'estimate {hours}',
  'lineItem.scope': 'Scope',
  'lineItem.scope.retainer': 'Retainer',
  'lineItem.scope.billable': 'Over the frame',
  'lineItem.scope.retainerNote': 'Part of the frame: its estimate is what makes the period budget.',
  'lineItem.scope.billableNote':
    'Over-frame work: it neither carries nor consumes the frame, and earns from the rate.',
  extcost: 'External costs',
  'extcost.new': 'New external cost',
  'extcost.search': 'Search by name or id…',
  'extcost.empty':
    'No external cost on this line item. A supplier invoice, a licence, a trip – add one here.',
  'extcost.emptyProject': 'This project has no external costs yet.',
  'extcost.count': '{count} costs',
  'extcost.sum': 'Σ {amount} (over the whole run)',
  'extcost.column.name': 'Cost',
  'extcost.column.date': 'Date',
  'extcost.column.amount': 'Amount',
  'extcost.column.link': 'Attached to',
  'extcost.column.recurrence': 'Recurrence',
  'extcost.column.owner': 'Owner',
  'extcost.column.total': 'Whole run',

  'extcost.link.project': 'Project level',
  'extcost.link.lineItem': 'Line item',
  'extcost.link.occurrence': 'Period',

  'extcost.recur.once': 'One-off',
  'extcost.recur.repeats': 'Repeats',
  'extcost.recur.weekly': 'Weekly',
  'extcost.recur.monthly': 'Monthly',
  'extcost.recur.everyNWeeks': 'Every {step} weeks',
  'extcost.recur.everyNMonths': 'Every {step} months',
  'extcost.recur.note':
    'This amount is charged to every period it touches – it is not spread across them.',
  'extcost.periods': '{count} periods · {total} in total',

  'extcost.dialog.new': 'New external cost',
  'extcost.dialog.edit': 'Edit the external cost',
  'extcost.dialog.hint': 'What is not somebody’s time: a supplier invoice, a licence, a trip, kit.',
  'extcost.field.name': 'Name',
  'extcost.field.namePlaceholder': 'e.g. Algolia search licence',
  'extcost.field.amount': 'Amount',
  'extcost.field.currency': 'Currency',
  'extcost.field.date': 'Date',
  'extcost.field.link': 'Attached to',
  'extcost.field.pickLineItem': 'Choose a line item…',
  'extcost.field.pickContract': 'Choose a contract…',
  'extcost.field.period': 'Period',
  'extcost.field.pickPeriod': 'Choose a period…',
  'extcost.field.recurrence': 'Recurrence',
  'extcost.field.every': 'Every',
  'extcost.field.unit': 'Unit',
  'extcost.unit.month': 'months',
  'extcost.unit.week': 'weeks',
  'extcost.field.until': 'Until',
  'extcost.field.owner': 'Owner',
  'extcost.action.edit': 'Edit',
  'extcost.action.delete': 'Delete the cost',
  'extcost.action.more': 'More actions',
  'extcost.action.full': 'Open in full view',
  'extcost.action.duplicate': 'Duplicate',
  'extcost.action.openLink': 'Open what it is linked to',

  /* ── C62 · the external cost's own screen ────────────────────────────────── */
  'title.costs': 'External cost',
  'extcost.unknown': 'No such external cost in this workspace.',
  'extcost.amount': 'Amount',
  'extcost.amount.recurNote':
    'This amount is charged to every period it touches – it is not split between them.',
  'extcost.amount.onceNote': 'The cost is the amount, once.',
  'extcost.total': 'Whole term',
  'extcost.periodCount': '{count} periods',
  'extcost.details': 'Details',
  'extcost.field.created': 'Booked',
  'extcost.recur.onceNote': 'A one-off cost.',
  'extcost.recur.fromOpen': 'from {from} (open-ended)',
  'extcost.recur.fromTo': '{from} – {to}',
  'extcost.occurrences': 'Periods it lands on',
  'extcost.occurrences.note': '{count} periods · {amount} per period',
  'extcost.occurrences.empty':
    'This cost recurs, but the project has no contract with periods behind it – so it is charged once.',
  'extcost.occ.period': 'Period',
  'extcost.occ.state': 'Status',
  'extcost.occ.planned': 'Planned',
  'extcost.occ.active': 'Current',
  'extcost.occ.closed': 'Closed',
  'extcost.files': 'Attachments',
  'extcost.files.empty': 'No invoice attached yet – the PDF is the evidence for the figure.',
  'extcost.rowMenu': '{name} actions',

  /* ── C57 · Invoices ──────────────────────────────────────────────────────── */
  invoice: 'Invoices',
  'nav.invoices': 'Invoices',
  'title.invoices': 'Invoices',
  'invoice.new': 'New invoice',
  'invoice.search': 'Search by invoice number or note…',
  'invoice.empty': 'No invoice in this view',
  'invoice.emptyBody':
    'An invoice is raised against a contract’s line item or a support period – use Bill on the line item or the period.',
  'invoice.unknown': 'There is no such invoice in this workspace.',
  'invoice.column.externalId': 'Invoice number',
  'invoice.column.project': 'Project',
  'invoice.column.note': 'Note',
  'invoice.column.date': 'Issued',
  'invoice.column.amount': 'Amount',
  'invoice.column.target': 'Billed against',
  'invoice.column.status': 'Status',
  'invoice.column.owner': 'Issued by',

  'invoice.view.all': 'All invoices',
  'invoice.filter.project': 'Project',
  'invoice.filter.allProjects': 'Every project',
  'invoice.filter.allTargets': 'All',

  'invoice.status.draft': 'Draft',
  'invoice.status.issued': 'Issued',
  'invoice.status.paid': 'Paid',
  'invoice.status.void': 'Void',

  'invoice.target.lineItem': 'Line item',
  'invoice.target.occurrence': 'Period',
  'invoice.target.gone': 'deleted line item',

  'invoice.action.open': 'Open',
  'invoice.action.edit': 'Edit',
  'invoice.action.delete': 'Delete the invoice',
  'invoice.action.status': 'Invoice status',
  'invoice.action.more': 'More actions',
  'invoice.action.openTarget': 'Open what it bills',
  'invoice.action.issue': 'Issue the invoice',

  'invoice.dialog.new': 'New invoice',
  'invoice.dialog.edit': 'Edit the invoice',
  'invoice.dialog.bill': 'Bill',
  'invoice.dialog.hint': 'An invoice is raised against a line item or a support period.',
  'invoice.dialog.billHint':
    'The invoice goes against the source you opened this from – you can close it afterwards.',

  'invoice.field.externalId': 'Invoice number',
  'invoice.field.externalIdPlaceholder': 'e.g. LN-2026-0388',
  'invoice.field.amount': 'Amount',
  'invoice.field.currency': 'Currency',
  'invoice.field.currencyNote': 'The currency of the source’s revenue.',
  'invoice.field.date': 'Issued',
  'invoice.field.status': 'Status',
  'invoice.field.target': 'Billed against',
  'invoice.field.pickLineItem': 'Choose a line item…',
  'invoice.field.contract': 'Contract',
  'invoice.field.pickContract': 'Choose a contract…',
  'invoice.field.pickPeriod': 'Choose a period…',
  'invoice.field.note': 'Note',
  'invoice.field.notePlaceholder': 'What is on the invoice?',
  'invoice.field.owner': 'Issued by',
  'invoice.field.source': 'Source',
  'invoice.field.created': 'Recorded',
  'invoice.field.remainingHint': 'Left to bill on the source: {amount}.',

  'invoice.close.lineItem': 'Close the line item?',
  'invoice.close.lineItemHint':
    'If a task or a time log lands on it after closing, an alert says so.',
  'invoice.close.occurrence': 'Close the period?',
  'invoice.close.occurrenceHint': 'A closed period takes no more logging until you reopen it.',

  'invoice.amount': 'Amount',
  'invoice.amount.note': 'The amount the invoice was issued for.',
  'invoice.details': 'Details',
  'invoice.note': 'Note',
  'invoice.note.placeholder': 'What is on the invoice?',
  'invoice.files': 'Attachments',
  'invoice.files.empty': 'No document attached. Drop in the PDF of the issued invoice.',
  'invoice.attach': 'Attach a file to {id}',
  'invoice.attachments': 'Attached files: {count}',

  'invoice.error.overRevenue': 'The amount cannot be more than what is left to bill ({amount}).',
  'invoice.error.byPeriod':
    'A recurring contract is invoiced by period, not by line item – and its earlier line-item invoices can no longer be edited.',

  'invoice.bill': 'Bill',
  'invoice.billMore': 'Another invoice',
  'invoice.none': 'Not billed yet',
  'invoice.billedPct': '{pct}% of the revenue billed.',
  'invoice.section': 'Invoices',
  'invoice.section.note': '{count} invoices · Σ {amount}',
  'invoice.section.emptyLineItem': 'No invoice on this line item yet.',
  'invoice.section.byPeriod':
    'A recurring contract is invoiced by period – the Finance card shows what has been billed.',
  'invoice.section.emptyPeriod': 'No invoice on this period yet.',

  /* ── C58 · the project finance tab ─────────────────────────────────────── */
  'finance.calc': 'Calculation',
  'finance.pick.contract': 'Choose a contract',
  'finance.pick.scope': 'Choose a scope',
  'finance.pick.currency': 'Display currency',
  'finance.pick.more': 'More actions',
  'finance.action.openContract': 'Open the contract',
  'finance.scope.whole': 'The whole contract',
  'finance.scope.periodHint': 'Shift+click to select every period between two.',
  'finance.scope.periodSearch': 'Search periods…',
  'finance.scope.periodNone': 'No period matches.',
  'finance.scope.periodsMany': '{count} periods',
  'finance.scope.itemHint': 'Shift+click to select every line item between two.',
  'finance.scope.itemSearch': 'Search line items…',
  'finance.scope.itemNone': 'No line item matches.',
  'finance.scope.itemsMany': '{name} and {count} more',
  'finance.scope.period': 'Period',
  'finance.scope.lineItem': 'Line item',
  'finance.scope.project': 'Whole project',
  'finance.scope.projectHint':
    'Every contract together, with the hours on tasks that have no line item',
  'finance.currency.contract': 'The contract’s currency',
  'finance.currency.contractHint':
    'The currency its line items are priced in – the one it was agreed in.',
  'finance.currency.workspace': 'The workspace currency',
  'finance.currency.workspaceHint': 'Contracts agreed in other currencies are converted into it.',
  'finance.empty.contractLabel': 'CONTRACT',
  'finance.empty.noContract': 'This project has no contract',
  'finance.empty.noContractBody':
    'The finance view is about one contract – its revenue, its frame and its periods. Add one on the Contracts tab.',
  'finance.empty.lineItemLabel': 'LINE ITEM',
  'finance.empty.noLineItem': 'This contract has no line items',
  'finance.empty.noLineItemBody':
    'Every figure here comes from the line items: they carry the frame, the revenue and the rate.',
  'finance.eac.label': 'The EAC formula',
  'finance.eac.cpi': 'Trend-following EAC',
  'finance.eac.cpiHint': 'budget / CPI – today’s efficiency holds to the end',
  'finance.eac.etc': 'Plan-following EAC',
  'finance.eac.etcHint': 'actual + (budget − earned value) – the remainder goes to plan',
  'finance.margin.plan': 'Planned margin',
  'finance.margin.planHint': 'revenue − planned cost',
  'finance.margin.forecast': 'Forecast margin',
  'finance.margin.forecastHint': 'forecast revenue − forecast cost (EAC)',
  'finance.external.label': 'External cost',
  'finance.external.in': 'Counted into the cost',
  'finance.external.inHint': 'the actual cost and the EAC include it',
  'finance.external.out': 'Left out',
  'finance.external.outHint': 'only the hour-based cost counts',
  'finance.kpi.revenue': 'CONTRACT REVENUE',
  'finance.kpi.revenueFormula': 'Σ period revenue',
  'finance.kpi.ofWhichBillable': 'of which {amount} is over-frame',
  'finance.kpi.budget': 'CONTRACT BUDGET',
  'finance.kpi.bac': 'BAC: budget at completion',
  'finance.kpi.periodSpend': 'PERIOD SPEND / BUDGET',
  'finance.kpi.runRate': 'RUN RATE',
  'finance.kpi.perDay': '{amount} / day',
  'finance.kpi.allowedPerDay': 'allowed {amount} / day',
  'finance.kpi.runRateFormula': '{amount} / {days} working days',
  'finance.kpi.overrun': 'FORECAST OVERRUN',
  'finance.kpi.overrunFormula': 'forecast − budget',
  'finance.kpi.projected': 'forecast {amount}',
  'finance.kpi.periodPlanMargin': 'PERIOD PLANNED MARGIN',
  'finance.kpi.periodForecastMargin': 'PERIOD FORECAST MARGIN',
  'finance.kpi.cpi': 'CPI',
  'finance.kpi.cpiSub': 'cost performance index',
  'finance.kpi.spi': 'SPI',
  'finance.kpi.layers': 'RETAINER · OVER-FRAME',
  'finance.kpi.layersFormula': 'margin by layer',
  'finance.kpi.layersSub': 'retainer | over-frame',
  'finance.kpi.revenueFixed': 'REVENUE',
  'finance.kpi.revenueFixedFormula': 'Σ line-item revenue',
  'finance.kpi.accrual': 'agreed income',
  'finance.kpi.budgetFixed': 'BUDGET',
  'finance.kpi.plannedCost': 'PLANNED COST',
  'finance.kpi.plannedFormula': 'allocation × cost rate',
  'finance.kpi.fromFrame': 'derived from the frame',
  'finance.kpi.thinPlan': 'the allocation covers only part of the scope',
  'finance.kpi.actualCost': 'ACTUAL COST',
  'finance.kpi.actualFormula': 'logged × cost rate',
  'finance.kpi.ofWhichExternal': 'of which {amount} is external',
  'finance.kpi.planMargin': 'PLANNED MARGIN',
  'finance.kpi.forecastMargin': 'FORECAST MARGIN',
  'finance.panel.utilisation': 'BUDGET UTILISATION',
  'finance.panel.budgetIs': 'BUDGET {amount}',
  'finance.panel.profitability': 'PROFITABILITY',
  'finance.bar.budget': 'BUDGET',
  'finance.bar.planned': 'PLANNED COST',
  'finance.bar.actual': 'ACTUAL COST',
  'finance.bar.projectExternal': 'Project-level cost',
  'finance.chart.current': 'Current period',
  'finance.tip.projectExternal.name': 'Project-level external cost',
  'finance.tip.projectExternal.what':
    'An external cost that belongs to the project, and to none of its contracts.',
  'finance.tip.projectExternal.calc': 'Σ project-level costs in the window',
  'finance.tip.projectExternal.note':
    'Not counted in this contract’s cost – otherwise every contract on the project would pay for the same thing.',
  'finance.bar.external': 'EXTERNAL COST',
  'finance.bar.externalExcluded': 'EXTERNAL COST · LEFT OUT',
  'finance.bar.earned': 'EARNED VALUE',
  'finance.foot.etc': 'ETC: estimate to complete',
  'finance.foot.vac': 'VAC: variance at completion',
  'finance.foot.cv': 'CV: cost variance',
  'finance.foot.sv': 'SV: schedule variance',
  'finance.foot.tcpi': 'TCPI: required efficiency',
  'finance.foot.pv': 'PV: planned value to date',
  'finance.list.revenue': 'REVENUE',
  'finance.list.planMargin': 'PLANNED MARGIN',
  'finance.list.planMarginPct': 'PLANNED MARGIN %',
  'finance.list.forecastRevenue': 'FORECAST REVENUE',
  'finance.list.forecastMargin': 'FORECAST MARGIN',
  'finance.list.forecastMarginPct': 'FORECAST MARGIN %',
  'finance.list.actualRate': 'ACTUAL HOURLY RATE',
  'finance.list.targetRate': 'TARGET HOURLY RATE',
  'finance.list.plannedRate': 'PLANNED HOURLY RATE',
  'finance.list.invoiced': 'INVOICED',
  'finance.list.invoicedPct': 'INVOICED / REVENUE',
  'finance.list.wip': 'WIP',
  'finance.chart.titleBudget': 'BUDGET AND USE',
  'finance.kinds.budgetTitle': 'Budget held – difference from budget',
  'finance.kinds.profitTitle': 'Profit',
  'finance.kinds.total': 'Total',
  'finance.kinds.external': 'External cost',
  'finance.kinds.forecast': 'Forecast (from the allocation)',
  'finance.kinds.actualsOnly': 'from the periods already closed',
  'finance.chart.titleProfit': 'REVENUE AND MARGIN',
  'finance.chart.modeBudget': 'Budget',
  'finance.chart.modeProfit': 'Profitability',
  'finance.chart.perPeriod': 'Per period',
  'finance.chart.cumulative': 'Cumulative',
  'finance.chart.loggedHours': 'LOGGED HOURS',
  'finance.chart.forecastHours': 'FORECAST HOURS',
  'finance.chart.plannedHours': 'PLANNED HOURS',
  'finance.chart.budget': 'BUDGET',
  'finance.chart.used': 'BUDGET USED',
  'finance.chart.revenue': 'REVENUE',
  'finance.chart.granDay': 'Day',
  'finance.chart.granWeek': 'Week',
  'finance.chart.granMonth': 'Month',
  'finance.chart.margin': 'MARGIN',
  'finance.chart.empty': 'This scope has no periods to draw.',
  'finance.fx.unconverted':
    'No exchange rate for {codes} – those amounts went into the {currency} totals at 1:1, so every figure here is approximate. Add the rate in the currency settings.',
  'finance.chart.hoursCum': 'CUMULATIVE HOURS',
  'finance.chart.hoursPeriod': 'HOURS PER PERIOD',
  'finance.chart.forecast': 'FORECAST',
  'finance.chart.range': 'FORECAST RANGE',
  'finance.chart.deadline': 'Deadline',
  /* 19 Oct 2026 · which EAC a forecast row of the chart's tooltip follows. */
  'finance.chart.modeCpi': 'TREND',
  'finance.chart.modeEtc': 'PLAN',
  'finance.table.periods': 'PERIODS · PROFITABILITY',
  'finance.table.periodCount': '{count} periods',
  'finance.table.lineItems': 'LINE ITEMS · PROFITABILITY',
  'finance.table.lineItemCount': '{count} line items',
  'finance.table.externalExcluded':
    'External cost is currently left out of the SPEND column – its own column still shows it.',
  'finance.col.period': 'PERIOD',
  'finance.col.status': 'STATUS',
  'finance.col.budget': 'BUDGET',
  'finance.col.revenue': 'REVENUE',
  'finance.col.billable': 'OVER-FRAME',
  'finance.col.spend': 'SPEND',
  'finance.col.external': 'EXTERNAL COST',
  'finance.col.margin': 'MARGIN',
  'finance.col.marginPct': 'MARGIN %',
  'finance.col.invoiced': 'INVOICED',
  'finance.col.invoice': 'INVOICE',
  'finance.col.name': 'NAME',
  'finance.col.type': 'TYPE',
  'finance.col.planned': 'PLANNED COST',
  'finance.col.ev': 'EV',
  'finance.col.cpi': 'CPI',
  'finance.filter.state': 'State',
  'finance.filter.type': 'Type',
  'finance.state.past': 'Closed',
  'finance.state.current': 'Active',
  'finance.state.future': 'Planned',
  'finance.total.budget': 'Σ BUDGET',
  'finance.total.revenue': 'Σ REVENUE',
  'finance.total.spend': 'Σ SPEND',
  'finance.total.margin': 'Σ MARGIN',
  'finance.total.invoiced': 'Σ INVOICED',
  'finance.breakdown.title': 'COST BREAKDOWN',
  'finance.breakdown.byPerson': 'Person',
  'finance.breakdown.byRole': 'Project role',
  'finance.breakdown.col.people': 'People',
  'finance.breakdown.col.planned': 'Planned',
  'finance.breakdown.col.logged': 'Logged',
  'finance.breakdown.col.cost': 'Actual cost',
  'finance.breakdown.col.share': 'Share',
  'finance.tip.bd.group.name': 'Group',
  'finance.tip.bd.group.what': 'What the breakdown groups by – switched with the tabs above.',
  'finance.tip.bd.people.name': 'People',
  'finance.tip.bd.people.what': 'How many people fall into this group within the scope.',
  'finance.tip.bd.people.calc': 'distinct people',
  'finance.tip.bd.planned.name': 'Planned hours',
  'finance.tip.bd.planned.what': 'What the group is allocated across the scope.',
  'finance.tip.bd.planned.calc': 'Σ allocated hours',
  'finance.tip.bd.logged.name': 'Logged hours',
  'finance.tip.bd.logged.what': 'What the group has actually logged in the scope.',
  'finance.tip.bd.logged.calc': 'Σ worklogs',
  'finance.tip.bd.cost.name': 'Actual cost',
  'finance.tip.bd.cost.what': 'What the group’s logged hours cost internally.',
  'finance.tip.bd.cost.calc': 'Σ (logged hours × cost rate)',
  'finance.tip.bd.cost.note':
    'Somebody with no rate card in force contributes no money – the panel header says how many.',
  'finance.tip.bd.share.name': 'Share',
  'finance.tip.bd.share.what': 'This group’s part of the scope’s whole actual cost.',
  'finance.tip.bd.share.calc': 'group actual cost / total actual cost',
  'finance.breakdown.noRole': 'No project role',
  'finance.breakdown.headcount': '{count} people',
  'finance.breakdown.empty': 'Nobody has logged time or been allocated in this scope yet.',
  'finance.breakdown.unrated':
    '{count} people have no rate card in force – their hours are priced at nothing.',
  'finance.tip.accrual':
    'Revenue is the agreed income: the price on a fixed fee or a licence, and on T&M the time logged so far × rate plus the remaining estimate at the rate. How much of it has been billed is what INVOICED shows.',
  'finance.tip.marginSwitch': 'The label’s dropdown switches to the other margin.',
  'finance.tip.list.revenue': 'The income agreed for the scope – not what has been invoiced.',
  'finance.tip.col.edited': 'A pencil marks a figure somebody typed.',
  'finance.tip.revenue.name': 'Contract revenue',
  'finance.tip.revenue.what':
    'The income of the periods in scope – the retainer and the over-frame work together.',
  'finance.tip.revenue.calc': 'Σ period revenue + Σ period over-frame revenue',
  'finance.tip.revenue.note':
    'Invoicing runs behind or ahead of this; INVOICED is the figure for that.',
  'finance.tip.revenueFixed.name': 'Revenue',
  'finance.tip.revenueFixed.what':
    'The income agreed on the scope’s line items – the agreed sum on a fixed price or a licence, and on T&M the time logged so far × rate plus the remaining estimated hours at the rate.',
  'finance.tip.revenueFixed.calc': 'Σ line-item revenue',
  'finance.tip.revenueFixed.note':
    'A non-billable line item earns nothing. How much of it has been billed is what INVOICED shows.',
  'finance.tip.bac.name': 'BAC: budget at completion',
  'finance.tip.bac.what':
    'The internal cost frame for the retainer work, across every period in scope.',
  'finance.tip.bac.calc': 'Σ period budget (derived from the line items’ frames)',
  'finance.tip.bac.note':
    'This is not the revenue: revenue is what the client is charged, the budget is our own spend frame.',
  'finance.tip.bacFixed.what': 'The internal cost frame for the scope, set on its line items.',
  'finance.tip.bacFixed.calc': 'Σ line-item cost frame',
  'finance.tip.bacFixed.note':
    'A T&M line item has no frame, because its revenue comes from the fact. This is not the revenue.',
  'finance.tip.periodSpend.name': 'Period spend / budget',
  'finance.tip.periodSpend.what': 'What the current period has spent, against its frame.',
  'finance.tip.periodSpend.calc': 'period actual cost / period budget',
  'finance.tip.periodSpend.note':
    'Over 100% the period has overspent. The lower bar is elapsed time – a longer spend bar means the frame is running out faster than the month.',
  'finance.tip.runRate.name': 'Run rate',
  'finance.tip.runRate.what': 'What a working day costs on average in the current period.',
  'finance.tip.runRate.calc': 'period actual cost / elapsed working days',
  'finance.tip.runRate.note':
    'The allowed daily figure is the frame spread evenly. A higher run rate means the frame runs out before the period does.',
  'finance.tip.overrun.name': 'Forecast overrun',
  'finance.tip.overrun.what':
    'By how much today’s pace will exceed the period’s frame by the close.',
  'finance.tip.overrun.calc': '(run rate × the period’s working days) − period budget',
  'finance.tip.overrun.note':
    'A positive figure is an overrun. A negative one closes inside the frame.',
  'finance.tip.periodPlanMargin.name': 'Period planned margin',
  'finance.tip.periodPlanMargin.what':
    'What the current period would keep if the spend used the frame exactly.',
  'finance.tip.periodPlanMargin.calc': '(period revenue + over-frame revenue) − period budget',
  'finance.tip.periodForecastMargin.name': 'Period forecast margin',
  'finance.tip.periodForecastMargin.what':
    'What the current period will keep at its close, at today’s run rate.',
  'finance.tip.periodForecastMargin.calc': '(period revenue + over-frame revenue) − forecast cost',
  'finance.tip.cpi.name': 'CPI: cost performance index',
  'finance.tip.cpi.what':
    'How many earned hours each logged hour bought – from the tasks’ estimates, weighted by their status.',
  'finance.tip.cpi.whatRecurring':
    'How many earned hours each logged hour bought – from the tasks’ estimates, weighted by their status. The trend EAC divides the budget by it.',
  'finance.tip.cpi.calc': 'earned hours / logged hours',
  'finance.tip.cpi.note':
    'Above 1.00 we are inside the frame, below it we are overspending. Red under 0.92.',
  'finance.tip.spi.name': 'SPI: schedule performance index',
  'finance.tip.spi.what':
    'Earned value against what the plan said we would have spent by now – are we on schedule.',
  'finance.tip.spi.calc': 'earned value / planned value to date (PV)',
  'finance.tip.spi.calcThin':
    'earned value / PV; PV here is the frame’s elapsed share, because the past has no allocation',
  'finance.tip.spi.note': 'Above 1.00 we are ahead of plan, below it behind. Red under 0.92.',
  'finance.tip.eac.name': 'EAC: estimate at completion',
  'finance.tip.eac.what': 'What the work will cost in total by the time it is done.',
  'finance.tip.eac.note':
    'The formula switches in the label’s dropdown. VAC – variance at completion: {amount}.',
  'finance.tip.etc.name': 'ETC: estimate to complete',
  'finance.tip.etc.what': 'How much is still to be spent before it is done.',
  'finance.tip.etc.calc': 'EAC − actual cost',
  'finance.tip.etc.note':
    'Larger than what is left of the frame means we are heading for an overrun.',
  'finance.tip.vac.name': 'VAC: variance at completion',
  'finance.tip.vac.what': 'By how much we will close inside or outside the frame.',
  'finance.tip.vac.calc': 'budget − EAC',
  'finance.tip.vac.note': 'A negative figure is an overrun.',
  'finance.tip.cv.name': 'CV: cost variance',
  'finance.tip.cv.what': 'The difference between earned value and actual cost.',
  'finance.tip.cv.calc': 'earned value − actual cost',
  'finance.tip.cv.note': 'A negative figure means we spent more than the progress is worth.',
  'finance.tip.sv.name': 'SV: schedule variance',
  'finance.tip.sv.what': 'The difference between earned value and the planned value to date.',
  'finance.tip.sv.calc': 'earned value − planned to date',
  'finance.tip.sv.note': 'A negative figure means we are behind the plan.',
  'finance.tip.tcpi.name': 'TCPI: to-complete performance index',
  'finance.tip.tcpi.what': 'How efficiently the remainder must be done to land inside the frame.',
  'finance.tip.tcpi.calc': '(budget − earned value) / (budget − actual cost)',
  'finance.tip.tcpi.note':
    'Under 1.00 the remainder fits comfortably; over 1.08 holding the frame is not realistic.',
  'finance.tip.pv.name': 'PV: planned value to date',
  'finance.tip.pv.what': 'What the plan said we would have spent by now.',
  'finance.tip.pv.calc': 'Σ (hours allocated up to today × cost rate)',
  'finance.tip.pv.calcThin':
    'budget × the elapsed share – the past has no allocation, so the frame’s time share is the base',
  'finance.tip.pv.note': 'Compared against earned value it gives the schedule variance (SV, SPI).',
  'finance.tip.ev.name': 'EV: earned value',
  'finance.tip.ev.what': 'The share of the frame the work done is worth.',
  'finance.tip.ev.whatRecurring':
    'The elapsed share of the frame – on recurring work, delivery tracks the calendar.',
  'finance.tip.ev.calc': 'budget × (earned hours / estimate), weighted by task status',
  'finance.tip.ev.calcRecurring':
    'Σ closed periods’ budgets + the current period’s budget × its elapsed working-day share',
  'finance.tip.ev.note':
    'If the actual cost exceeds it, we are progressing more expensively than planned.',
  'finance.tip.plannedCost.name': 'Planned cost',
  'finance.tip.plannedCost.what':
    'What the planned work costs: the allocated hours at each person’s cost rate.',
  'finance.tip.plannedCost.calc': 'Σ (allocated hours × that person’s cost rate)',
  'finance.tip.plannedCost.calcThin':
    'derived from the frame – the allocation covers only part of the scope',
  'finance.tip.plannedCost.note': 'Larger than the budget means even the plan does not fit.',
  'finance.tip.actualCost.name': 'AC: actual cost',
  'finance.tip.actualCost.what':
    'What the work has cost so far: the logged hours at each person’s cost rate, plus the external costs attached to it.',
  'finance.tip.actualCost.calc': 'Σ (logged hours × that person’s cost rate) + Σ external cost',
  'finance.tip.actualCost.note':
    'Every hour at the rate in force on the day it was logged – a raise does not re-price the past.',
  'finance.tip.external.name': 'External cost',
  'finance.tip.external.what':
    'What is not somebody’s time: a licence, a subcontractor, a trip, kit.',
  'finance.tip.external.calc':
    'Σ the external costs attached to this scope, the project-level costs included',
  'finance.tip.external.noteIn':
    'Currently counted into the actual cost and the EAC – the switch on this row takes it out.',
  'finance.tip.external.noteOut':
    'Currently left out of the actual cost – the switch on this row counts it in.',
  'finance.tip.bar.budgetNote':
    'Every other bar is read against this; the vertical mark is it too.',
  'finance.tip.bar.plannedNote': 'Overhanging the mark means even the plan does not fit the frame.',
  'finance.tip.bar.actualNote': 'Whatever overhangs the mark is the overspend.',
  'finance.tip.bar.eacNote':
    'Whatever overhangs the mark is the forecast overrun. The switch on this row changes the formula.',
  'finance.tip.utilisation.name': 'Budget utilisation',
  'finance.tip.utilisation.what':
    'The plan, the fact, the earned value and the estimate at completion, all against the frame.',
  'finance.tip.utilisation.calc': 'the bar’s length is the money, the vertical mark is the budget',
  'finance.tip.utilisation.note': 'A bar overhanging the mark is an overrun.',
  'finance.tip.profitability.name': 'Profitability',
  'finance.tip.profitability.what':
    'How profitable the scope is: revenue, spend, margin, hourly rates and where the invoicing stands.',
  'finance.tip.profitability.note':
    'The margin is taken against the agreed income, not against what has been invoiced.',
  'finance.tip.planMargin.name': 'Planned margin',
  'finance.tip.planMargin.what':
    'What would be kept if the planned work happened exactly as planned.',
  'finance.tip.planMargin.calc': 'revenue − planned cost',
  'finance.tip.planMargin.note':
    'This is the plan’s profitability; the forecast margin is what the close will bring.',
  'finance.tip.planMarginPct.name': 'Planned margin %',
  'finance.tip.planMarginPct.what': 'The planned margin as a share of revenue.',
  'finance.tip.planMarginPct.calc': '(revenue − planned cost) / revenue',
  'finance.tip.planMarginPct.note':
    'A lower forecast margin % means the execution is eroding the plan’s profitability.',
  'finance.tip.forecastRevenue.name': 'Forecast revenue',
  'finance.tip.forecastRevenue.what':
    'What the scope will have earned by its end, if the remaining work is done as planned.',
  'finance.tip.forecastRevenue.calc':
    'the whole agreed sum on a fixed price; on T&M, the revenue so far + the remaining estimated hours at the line item’s rate',
  'finance.tip.forecastRevenue.note':
    'Since revenue is the agreed income too, the two are the same on a contract without periods.',
  'finance.tip.forecastMargin.name': 'Forecast margin',
  'finance.tip.forecastMargin.what':
    'What the close will keep: forecast revenue minus forecast cost.',
  'finance.tip.forecastMargin.calc': 'forecast revenue − forecast cost (EAC)',
  'finance.tip.forecastMargin.note':
    'The margin can never exceed the revenue; close to it means almost no cost is planned for the remaining work.',
  'finance.tip.forecastMarginPct.name': 'Forecast margin %',
  'finance.tip.forecastMarginPct.what':
    'The margin expected at the close, as a share of the forecast revenue.',
  'finance.tip.forecastMarginPct.calc': '(forecast revenue − forecast cost) / forecast revenue',
  'finance.tip.forecastMarginPct.note':
    'Materially below the current margin means the remaining work erodes profitability.',
  'finance.tip.actualRate.name': 'Actual hourly rate',
  'finance.tip.actualRate.what': 'The agreed income per hour logged so far.',
  'finance.tip.actualRate.calc': 'revenue / logged hours',
  'finance.tip.actualRate.note':
    'Until the work is done the whole income is divided by the hours so far, so it reads higher than it will end. Below the target rate means the work went out cheaper than list price.',
  'finance.tip.targetRate.name': 'Target hourly rate',
  'finance.tip.targetRate.what':
    'The target rates on the rate cards, weighted by the hours actually logged.',
  'finance.tip.targetRate.calc': 'Σ (logged hours × that person’s target rate) / logged hours',
  'finance.tip.targetRate.note': 'This is the list price the work should have been sold at.',
  'finance.tip.plannedRate.name': 'Planned hourly rate',
  'finance.tip.plannedRate.what': 'The agreed income per allocated hour.',
  'finance.tip.plannedRate.calc': 'revenue / allocated hours',
  'finance.tip.plannedRate.note':
    'It differs from the actual hourly rate only in the divisor: that one divides by logged hours, this one by planned hours. The gap shows whether the work takes more or fewer hours than were planned.',
  'finance.tip.invoiced.name': 'Invoiced',
  'finance.tip.invoiced.what': 'The non-void invoices raised against this scope.',
  'finance.tip.invoiced.calc': 'Σ invoice amount (void excluded)',
  'finance.tip.invoiced.note': 'It may differ from the revenue: we bill in advance or in arrears.',
  'finance.tip.invoicedPct.name': 'Invoiced / revenue',
  'finance.tip.invoicedPct.what': 'How much of the revenue has been billed.',
  'finance.tip.invoicedPct.calc': 'invoiced / revenue',
  'finance.tip.invoicedPct.note':
    'Under 100% some of the agreed income is not billed yet; over 100% we have billed more than was agreed.',
  'finance.tip.wip.name': 'WIP: delivered and not yet billed',
  'finance.tip.wip.what': 'The part of the agreed income not invoiced yet.',
  'finance.tip.wip.calc': 'revenue − invoiced',
  'finance.tip.wip.note': 'A negative figure means more has been invoiced than was agreed.',
  'finance.tip.layers.name': 'Retainer and over-frame margin',
  'finance.tip.layers.what':
    'The margin on the framed (retainer) work and on the separately billed over-frame work, side by side.',
  'finance.tip.layers.calc': 'the layer’s revenue − the layer’s actual cost',
  'finance.tip.layers.note':
    'This is the contract’s central claim: a retainer overspend can be paid for by the over-frame work’s profit.',
  'finance.tip.breakdown.name': 'Cost breakdown',
  'finance.tip.breakdown.what': 'How the planned and actual cost split by person or by role.',
  'finance.tip.breakdown.calc':
    'the scope’s allocations and logged hours, grouped and priced at the cost rates',
  'finance.tip.col.index.name': 'Period number',
  'finance.tip.col.index.what':
    'The position of the period in the series the recurrence generates.',
  'finance.tip.col.period.name': 'Period',
  'finance.tip.col.period.what': 'The period’s first and last day.',
  'finance.tip.col.status.name': 'Status',
  'finance.tip.col.status.what':
    'Planned, Active or Closed – it follows the calendar, nobody sets it.',
  'finance.tip.col.status.note': 'A closed period can be reopened for a late entry.',
  'finance.tip.col.periodBudget.name': 'Period budget',
  'finance.tip.col.periodBudget.what': 'The share of the internal frame that falls on this period.',
  'finance.tip.col.periodBudget.calc':
    'line-item frame / number of periods, overridable on the period',
  'finance.tip.col.periodRevenue.name': 'Period revenue',
  'finance.tip.col.periodRevenue.what': 'The retainer revenue that falls on this period.',
  'finance.tip.col.periodRevenue.calc':
    'line-item revenue / number of periods, overridable on the period',
  'finance.tip.col.billable.name': 'Over-frame revenue',
  'finance.tip.col.billable.what':
    'What the separately billed work outside the frame earned in this period.',
  'finance.tip.col.billable.note': 'This sits on top of the retainer and consumes none of it.',
  'finance.tip.col.spend.what': 'What was spent in this window.',
  'finance.tip.col.external.calc': 'Σ the external costs attached to this period',
  'finance.tip.col.margin.name': 'Margin',
  'finance.tip.col.margin.what': 'Revenue minus spend.',
  'finance.tip.col.margin.note': 'A negative figure is a loss-making period.',
  'finance.tip.col.periodMargin.calc': '(revenue + over-frame) − spend',
  'finance.tip.col.marginPct.name': 'Margin %',
  'finance.tip.col.marginPct.what': 'The margin as a share of the revenue.',
  'finance.tip.col.periodMarginPct.calc': 'margin / (revenue + over-frame)',
  'finance.tip.col.periodInvoiced.what': 'The invoices raised against this period.',
  'finance.tip.col.invoice.name': 'Invoice',
  'finance.tip.col.invoice.what': 'The document number of the most recent invoice on this period.',
  'finance.tip.col.invoice.note': 'Empty when there is no invoice yet.',
  'finance.tip.col.item.name': 'Line item',
  'finance.tip.col.item.what': 'The unit of work the contract commits to.',
  'finance.tip.col.itemType.name': 'Billing type',
  'finance.tip.col.itemType.what': 'Fixed price, Time and material (T&M) or Non-billable.',
  'finance.tip.col.itemType.note':
    'T&M has no frame; its revenue comes from the time logged and the remaining estimate.',
  'finance.tip.col.itemRevenue.what': 'The income agreed on this line item.',
  'finance.tip.col.itemRevenue.calc':
    'the agreed sum on a fixed price or a licence; on T&M, logged time × rate + remaining estimate × rate',
  'finance.tip.col.itemRevenue.note': 'A non-billable one earns nothing.',
  'finance.tip.col.itemBudget.name': 'Budget',
  'finance.tip.col.itemBudget.what': 'The internal cost frame set on this line item.',
  'finance.tip.col.itemBudget.calc': 'the frame set on the line item',
  'finance.tip.col.itemBudget.note': 'T&M has none.',
  'finance.tip.col.itemExternal.calc': 'Σ the external costs attached to this line item',
  'finance.tip.col.itemCpi.note': 'Under 1.00 is an overspend.',
  'finance.tip.col.itemCpi.calc': 'earned value / actual cost',
  'finance.tip.col.itemCpi.what': 'How much frame-value a spent forint bought on this line item.',
  'finance.tip.col.itemMargin.calc': 'revenue − actual cost',
  'finance.tip.col.itemMarginPct.calc': 'margin / revenue',
  'finance.tip.col.itemInvoiced.what': 'The invoices raised against this line item.',
  'finance.tip.col.itemInvoice.what':
    'The document number of this line item’s most recent invoice.',
  'occurrence.money': 'Money',
  'occurrence.money.overFrame': 'Over the frame',
  'lineItem.attachments': 'Attachments',
  'lineItem.attachments.empty': 'Nothing attached to this line item yet.',
  'person.attachments': 'Attachments',
  'person.attachments.empty': 'Nothing attached to this person yet.',
  'contract.tasks': 'Task',
  'contract.createdAt': 'Created',
  'contract.lineItems': 'Line items',
  'contract.lineItem': 'Line item',
  'contract.lineItems.empty': 'No line items yet. Tasks belong to a contract through these.',
  'contract.lineItem.new': 'New line item',
  'contract.lineItem.add': 'Add',
  'contract.action.new': 'New contract',
  'contract.action.createNew': 'Create a contract',
  'contract.action.attachExisting': 'Add an existing contract',
  'contract.attach.title': 'Add an existing contract',
  'contract.attach.hint':
    'Only contracts with no project can be added. It gets a code when you add it.',
  'contract.attach.none': 'Nothing to add. Create a contract under the client first.',
  'contract.attach.submit': 'Add',
  'contract.attach.anyClient': 'Show contracts from other clients too',
  'contract.action.create': 'Create',
  'contract.dialog.new': 'New contract',
  'contract.dialog.stepHint': 'How many units apart – e.g. 3 months = quarterly.',
  'contract.field.name': 'Name',
  'contract.field.namePlaceholder': 'e.g. Haystack annual support',
  'contract.error.name': 'Give the contract a name.',
  'contract.error.project': 'Pick a project.',
  'contract.error.start': 'Give it a start date.',
  'contract.error.dates': 'The end date cannot be before the start.',
  'lineItem.dialog.new': 'New line item',
  'lineItem.field.namePlaceholder': 'e.g. Monthly support',
  'lineItem.field.estimatePerPeriod': 'Estimate / period',
  'lineItem.field.typeHint': 'Changeable later on the line item’s own page.',
  'lineItem.field.estimateHintRecurring':
    'Per period – under a recurring contract this is what is committed each period.',
  'lineItem.error.estimate': 'That is not a number of hours we can read.',
  'lineItem.error.type': 'Choose a billing type.',
  'common.hours': 'hours',
  'contract.lineItem.closed': 'closed',
  'contract.perPeriodNote':
    'The estimate, budget and revenue columns show the whole commitment: the per-period figure × the periods each line item spans ({count} on the contract).',
  'contract.estimateTotal': '{perPeriod} per period · {count} periods',
  'contract.recurrence': 'Recurrence',
  'contract.recurrence.unit': 'Unit',
  'contract.recurrence.step': 'Every',
  'contract.recurrence.startsOn': 'Starts',
  'contract.recurrence.endMode': 'Ends',
  'contract.recurrence.endsOn': 'Last day',
  'contract.recurrence.count': 'Count',
  'contract.recurrence.week': 'Week',
  'contract.recurrence.month': 'Month',
  'contract.recurrence.byDate': 'On a date',
  'contract.recurrence.byCount': 'After a count',
  'contract.recurrence.periodsNote':
    'Periods are derived from this – they are not stored separately.',
  'contract.error.notEnabled': 'Contracts are not part of your plan.',
  'contract.error.invalid': 'That value is not valid.',
  'contract.error.lineItemInUse':
    'The line item still has tasks or logged hours – close it instead of deleting it.',
  'lineItem.type.fixed': 'Fixed price',
  'lineItem.type.timeAndMaterial': 'Time and material',
  'lineItem.type.nonBillable': 'Non-billable',
  /* C55 · work-free revenue. */
  'lineItem.type.license': 'Licence and fees',
  'contract.column.name': 'Contract',
  'contract.column.project': 'Project',
  'contract.column.client': 'Client',
  /* ── C57 · the 26 alert rules. ── */
  'alert.member_no_rate.name': 'No rate card for a project member',
  'alert.member_no_rate.impact':
    'Actual spend, CPI, margin and the cost breakdown — hours with no rate go in at 0 Ft.',
  'alert.member_no_rate.fix':
    'Settings › Rate cards › Assign — give the person a valid rate from the day they started logging.',
  'alert.member_no_sched.name': 'No work schedule for a project member',
  'alert.member_no_sched.impact':
    'Capacity, load and the leave allowance — the person counts as having 0 hours a day.',
  'alert.member_no_sched.fix':
    'Settings › Work schedules › Assign — pick a template or set their daily hours.',
  'alert.task_multi_line.name': 'One task matches several line items',
  'alert.task_multi_line.impact':
    'Revenue and frame consumption — the same hour appears on both line items, so it is counted twice.',
  'alert.task_multi_line.fix':
    'Open the task and leave only the one line-item assignment that is right.',
  'alert.task_no_line.name': 'Logged task with no line item',
  'alert.task_no_line.impact':
    'Revenue, frame usage and the EVM figures — the hours are left out of the contract altogether.',
  'alert.task_no_line.fix': 'Open the task and attach the contract’s right line item to it.',
  'alert.task_unrouted.name': 'Synced task with no line item',
  'alert.task_unrouted.impact':
    'Revenue and frame usage — the task sits on no project, so its hours count towards no contract.',
  'alert.task_unrouted.fix':
    'Widen the right line item’s rule so it claims the task, or attach it by hand.',
  'alert.task_orphaned.name': 'Synced task its rule no longer claims',
  'alert.task_orphaned.impact':
    'Revenue and frame usage — the hours stay on the old line item although its rule would no longer file them there.',
  'alert.task_orphaned.fix':
    'Adjust the line item’s rule to the task, or move the task to the right line item by hand.',
  'alert.li_no_budget.name': 'Line item with no budget',
  'alert.li_no_budget.impact':
    'Frame usage, CPI and VAC — a variance cannot be computed against a frame of nothing.',
  'alert.li_no_budget.fix': 'Open the line item and set its budget — or make it non-billable.',
  'alert.contract_expired_log.name': 'Time logged against an expired contract',
  'alert.contract_expired_log.impact':
    'Revenue and invoicing — there is nothing to bill the hours after the end date against.',
  'alert.contract_expired_log.fix': 'Extend the contract, or move the hours onto a live line item.',
  'alert.wlog_duplicate.name': 'The same hour logged twice',
  'alert.wlog_duplicate.impact':
    'Actual spend and frame consumption — the same work is counted twice everywhere.',
  'alert.wlog_duplicate.fix': 'Open the time log and delete the duplicate entry.',
  'alert.inactive_member_log.name': 'An inactive member logged time',
  'alert.inactive_member_log.impact':
    'The per-person and cost breakdowns count somebody who is no longer on the team.',
  'alert.inactive_member_log.fix':
    'Reactivate the person on the project, or move the hours to whoever did the work.',
  'alert.ext_cost_unlinked.name': 'External cost with no line item',
  'alert.ext_cost_unlinked.impact':
    'Cost, margin and the cost breakdown — the amount lands on the project but on no line item.',
  'alert.ext_cost_unlinked.fix':
    'Open the cost and link it to the line item or period it belongs to.',
  'alert.license_not_invoiced.name': 'Licence fee with no invoice',
  'alert.license_not_invoiced.impact':
    'Invoiced revenue — a period has passed with nothing billed for it.',
  'alert.license_not_invoiced.fix':
    'Issue the invoice for the period, or correct the line item’s window.',
  'alert.task_no_assignee.name': 'Task with no assignee',
  'alert.task_no_assignee.impact':
    'Load and the per-person breakdown — the work belongs to nobody, so it is planned for nobody.',
  'alert.task_no_assignee.fix': 'Open the task and give it an assignee.',
  'alert.over_invoiced.name': 'More has been invoiced than the revenue',
  'alert.over_invoiced.impact':
    'Revenue and margin — invoices exceed what the contract says is earned.',
  'alert.over_invoiced.fix':
    'Check the invoices against the line item, and correct whichever is wrong.',
  'alert.task_no_estimate.name': 'Task with no estimate',
  'alert.task_no_estimate.impact':
    'Planning, EVM and the completion figures — progress cannot be measured against nothing.',
  'alert.task_no_estimate.fix': 'Open the task and give it an estimate.',
  'alert.task_no_dates.name': 'Task with no start or due date',
  'alert.task_no_dates.impact': 'Scheduling and the timeline — the task sits outside every period.',
  'alert.task_no_dates.fix': 'Open the task and set its start and due dates.',
  'alert.li_no_window.name': 'Line item with no period',
  'alert.li_no_window.impact': 'Period allocation — hours cannot be placed in time without one.',
  'alert.li_no_window.fix':
    'Open the line item and set its start and end, or inherit the contract’s.',
  'alert.li_no_rate_tm.name': 'Time-and-materials line item with no rate',
  'alert.li_no_rate_tm.impact': 'Revenue — without a rate the logged hours earn nothing.',
  'alert.li_no_rate_tm.fix': 'Open the line item and set its hourly or daily rate.',
  'alert.fx_missing.name': 'Missing exchange rate',
  'alert.fx_missing.impact':
    'Every figure in the base currency — amounts in this currency cannot be converted.',
  'alert.fx_missing.fix': 'Settings › Currencies — set the rate, or refresh it from the web.',
  'alert.alloc_no_rate.name': 'No rate card for an allocated person',
  'alert.alloc_no_rate.impact': 'Planned cost and the forecast — the planned hours go in at 0 Ft.',
  'alert.alloc_no_rate.fix': 'Settings › Rate cards › Assign — give the person a valid rate.',
  'alert.wlog_outside_li.name': 'Time logged outside the line item’s period',
  'alert.wlog_outside_li.impact':
    'Period allocation and frame usage — the hour falls outside what it is meant to consume.',
  'alert.wlog_outside_li.fix': 'Correct the date, or widen the line item’s window.',
  'alert.wlog_no_occ.name': 'Time log falls in no period',
  'alert.wlog_no_occ.impact':
    'Period figures — the hour belongs to no occurrence of the recurring contract.',
  'alert.wlog_no_occ.fix': 'Correct the date, or check the contract’s recurrence.',
  'alert.wlog_multi_occ.name': 'Time log falls in several periods',
  'alert.wlog_multi_occ.impact':
    'Period figures — the same hour is counted in more than one occurrence.',
  'alert.wlog_multi_occ.fix': 'Check the contract’s recurrence: its periods overlap.',
  'alert.dep_date_violation.name': 'A dependency is broken by the dates',
  'alert.dep_date_violation.impact':
    'Scheduling — a task starts before the one it depends on is due.',
  'alert.dep_date_violation.fix': 'Move one of the two dates so the order holds.',
  'alert.stale_progress.name': 'Task stuck in progress',
  'alert.stale_progress.impact':
    'Completion and the forecast — an in-progress task nobody has logged against for a fortnight.',
  'alert.stale_progress.fix': 'Close the task, or log what has been done on it.',
  'alert.log_on_todo.name': 'Time logged against a task nobody started',
  'alert.log_on_todo.impact':
    'Status and completion — hours exist on a task that is still on the backlog.',
  'alert.log_on_todo.fix': 'Move the task into progress, or correct where the hours went.',
  'alert.no_recent_logs.name': 'No recent logging on the project',
  'alert.no_recent_logs.impact':
    'Every actual figure — an active project with nothing logged for a fortnight.',
  'alert.no_recent_logs.fix':
    'Check whether the work is really running, or the logging has stopped.',
  'alert.plan_thin.name': 'The allocation covers less than half the frame',
  'alert.plan_thin.impact': 'The forecast — far less is planned than the contract commits to.',
  'alert.plan_thin.fix': 'Open the resource plan and allocate the rest of the frame.',
  'alert.li_closed_work.name': 'Work reached a closed line item',
  'alert.li_closed_work.impact':
    'Actuals, frame usage and invoicing — the line item is invoiced and closed, yet the new work still books against it.',
  'alert.li_closed_work.fix':
    'Move the task or the hours to an open line item — or reopen the line item if work on it goes on.',
  'alert.wlog_unidentified.name': 'Hours no line item takes',
  'alert.wlog_unidentified.impact':
    'Actuals, revenue and invoicing — the hours count on the project, but no line item bills them, so no time-and-material line item earns from them.',
  'alert.wlog_unidentified.fix':
    'Name the person on a line item, or widen a line item’s dates to cover these days.',
  'alert.wlog_unestimated_share.name': 'Spend on unestimated tasks',
  'alert.wlog_unestimated_share.impact':
    'CPI and the EAC — an unestimated task earns no EV, so CPI reads too low and the EAC too high.',
  'alert.wlog_unestimated_share.fix': 'Estimate the tasks that already have hours logged on them.',
  'alert.wlog_unbound_share.name': 'Many hours bill to no line item',
  'alert.wlog_unbound_share.impact':
    'CPI and the EAC — hours on no line item fall outside the frame, so CPI reads too good.',
  'alert.wlog_unbound_share.fix':
    'Bind the tasks to a line item — through its routing rules, or on the task itself.',
  'alert.li_no_tasks.name': 'Line item with hours but no tasks',
  'alert.li_no_tasks.impact':
    'EV and the EAC — a line item’s progress comes from its tasks, so without any its EV is 0 while its spend grows.',
  'alert.li_no_tasks.fix': 'Create tasks on the line item, or bind the existing ones to it.',
  'alert.task_reestimated_closed.name': 'Closed task re-estimated afterwards',
  'alert.task_reestimated_closed.impact':
    'EV and CPI — a closed task’s estimate is part of EV, so EV moved with no work behind it.',
  'alert.task_reestimated_closed.fix':
    'Set the estimate back, and file any further work as a new task.',
  'alert.li_rule_on_status.name': 'Routing condition on status or assignee',
  'alert.li_rule_on_status.impact':
    'Actuals, EV and the EAC — when a task’s status or assignee changes, its hours move to another line item.',
  'alert.li_rule_on_status.fix':
    'Switch the condition to a field that does not move, or assign the tasks to the line item by hand.',
  'alert.alloc_stale.name': 'Stale allocation',
  'alert.alloc_stale.impact':
    'Resource-based EAC and finish — the remaining allocation sits on people who are not working on the project.',
  'alert.alloc_stale.fix':
    'Resource tab — match the allocation to the people actually working on it.',
  'alert.li_over_plan.name': 'Allocation over the line item’s budget',
  'alert.li_over_plan.impact':
    'Schedule-adjusted EAC and SPI(t) — the plan exceeds the frame, so the planned value is distorted.',
  'alert.li_over_plan.fix':
    'Resource tab — reduce the allocation, or raise the line item’s estimate.',
  'alert.member_silent.name': 'Allocated people not logging',
  'alert.member_silent.impact':
    'Resource-based EAC and finish — the allocation promises work that does not arrive.',
  'alert.member_silent.fix': 'Ask them to catch up on logging, or take them off the allocation.',
  'contract.column.type': 'Type',
  'contract.column.startsOn': 'Start',
  'contract.column.endsOn': 'End',
  'contract.column.lineItems': 'Line items',
  'contract.column.estimate': 'Estimate',
  'contract.column.tracked': 'Tracked',
  'contract.column.owner': 'Owner',
  'contract.column.status': 'Status',
  'contract.type.recurring': 'Recurring',
  'contract.type.milestone': 'Milestone-based',
  'contract.type.service': 'Service',
  'contract.status.draft': 'Draft',
  'contract.status.active': 'Active',
  'contract.status.closed': 'Closed',
  'home.chart.previous': 'Earlier period',
  'home.chart.next': 'Later period',
  'home.section.log': 'Time logs',
  'home.section.leave': 'Leave',
  'home.section.allocations': 'My allocations',
  'home.section.overtime': 'Overtime',
  'home.action.logTime': 'Log time',
  'home.stat.today': 'Today',
  'home.stat.thisWeek': 'This week',
  'home.stat.missingDays': 'Short days',
  'home.quota.days': 'days',
  'home.leave.pending': 'Awaiting a decision',
  'home.leave.upcoming': 'Upcoming leave',
  /* 16 Oct 2026 · the reworked homescreen. */
  'home.ask.caption': 'You can ask the Agent about all of these too.',
  'home.stat.weekLeft': 'Left this week',
  'home.stat.weekOver': 'Over plan',
  'home.stat.ofWeeklyPlan': 'of the weekly plan',
  'home.stat.onPlan': 'on plan',
  'home.stat.noSchedule': 'no schedule',
  'home.stat.thisWeekLower': 'this week',
  'home.requests.all': 'All requests',
  'home.overtime.approvedRecent': 'Approved in the last month',
  'home.overtime.unpriced': 'Overtime',
  'home.alloc.holiday': 'Holiday',
  'home.alloc.leave': 'Leave',
  'home.alloc.none': 'Nothing planned',
  'home.alloc.noCapacity': 'No capacity',
  'home.alloc.tip.day': 'On this day',
  'home.alloc.tip.billed': '{hours} · billed {billed}',
  'home.alloc.tip.logged': 'Logged that day',
  'home.alloc.tip.week': 'This week in total',
  'home.alloc.tip.period': 'Period',
  'home.alloc.tip.periods': '{count} allocations',
  'home.dow.1': 'Mon',
  'home.dow.2': 'Tue',
  'home.dow.3': 'Wed',
  'home.dow.4': 'Thu',
  'home.dow.5': 'Fri',
  'home.dow.6': 'Sat',
  'home.dow.7': 'Sun',
  'nav.projectsSection': 'Projects',
  'nav.myProjects': 'My projects',
  'nav.projectsEmpty':
    'Pin a project from the project list, or with the star on its page, to have it appear here.',
  'nav.newProject': 'New project',
  'nav.reportsSection': 'Reports',
  'nav.myReports': 'My reports',
  'nav.reportsEmpty': 'Pin a report from the report list to have it appear here.',
  'nav.newReport': 'New report',
  'nav.noWorkspace': 'No workspace',
  'title.projects': 'Projects',
  'title.clients': 'Clients',

  'common.apply': 'Apply',
  'common.reset': 'Reset',
  'common.cancel': 'Cancel',
  'common.delete': 'Delete',
  'common.save': 'Save',
  'common.create': 'Create',
  'common.back': 'Back',
  'common.close': 'Close',
  'common.loading': 'Loading…',
  'common.edit': 'Edit',
  'common.toast.created': '{name} created',
  'common.toast.saved': '{name} saved',
  'common.toast.savedPlain': 'Saved',
  'common.choose': 'Choose',
  'common.pleaseChoose': 'Please choose',
  'common.all': 'All',
  'common.none': 'None',
  'common.required': 'required',
  'calendar.previousPeriod': 'Previous period',
  'calendar.nextPeriod': 'Next period',
  'calendar.zoomOut': 'Zoom out',
  'upload.drop': 'Drop your file here to upload',
  'drop.toEntity': 'Attach to {name}',
  'drop.toZone': 'Insert into {name}',
  'drop.hint': 'Drop over the description or a comment to put it there instead.',
  'upload.browse': 'Or browse files',
  'upload.busy': 'Uploading…',
  'common.color.custom': 'Custom colour',
  'common.color.hex': 'Hex colour code',
  'common.color.open': 'Open the colour picker',
  'common.decrease': 'Decrease',
  'common.increase': 'Increase',
  'common.color': 'Colour',

  'list.identifier': 'Identifier',
  'list.search': 'Search and filter…',
  'list.filter': 'Filter',
  'list.views': 'View',
  'list.columns': 'Columns',
  'list.clearAll': 'Clear filters',
  'list.searchOptions': 'Search…',
  'list.loadMore': 'Load more',
  'list.previousPage': 'Previous page',
  'list.nextPage': 'Next page',
  'list.pageSelector': 'Page',
  'list.pageOf': '{page} / {count}',
  /* ⚠️ **"results", not "total".** The figure counts what the filters left rather than what the
     table holds — `pagination.ts` argues it at length. */
  'list.pageTotal': '{total} results',
  'list.loadingMore': 'Loading…',
  'list.clearFilter': 'Clear filter: {label}',
  'file.fetching': 'Downloading the file…',
  'file.notAvailable': 'That file is not available.',
  'file.deleted': 'This file has been deleted. The link to it was left behind in the text.',
  'file.saved': 'The file has been saved. You can close this tab.',
  'task.attachment.removeTitle': 'Remove attachment',
  'task.attachment.linkedInDescription':
    'The description links to this file. Removing it takes the link out of the text as well.',
  'list.rangeFrom': 'From',
  'list.rangeTo': 'To',
  'list.rangeBoth': '{from} – {to}',
  'list.rangeFromOnly': 'from {from}',
  'list.rangeToOnly': 'to {to}',
  'list.moveUp': 'Move up: {label}',
  'list.moveDown': 'Move down: {label}',
  /* 17 Oct 2026 · the phone's ⋯ sheet. */
  'list.more': 'More options',
  /* 20 Oct 2026 · LV-1 · a list as cards, and what the card shows. */
  'listLayout.view': 'View',
  'listLayout.list': 'List',
  'listLayout.cards': 'Cards',
  'listLayout.settings': 'Card settings',
  'listLayout.badge': 'Badge (top right)',
  'listLayout.avatar': 'Owner (bottom right)',
  'listLayout.detail': 'Detail {index}',
  'listLayout.footer': 'Footer',
  'listLayout.none': 'Empty',
  'listLayout.reset': 'Default card',
  'listLayout.done': 'Done',
  'listLayout.sort': 'Sort',
  'listLayout.title': 'Title',
  'listLayout.context': 'Top line',
  'listLayout.preview': 'Preview',
  /* 17 Oct 2026 · the quota grid's copy from last year. */
  'settings.leave.copy': 'Copy from last year ({count})',
  'settings.leave.pick': 'Select {name}',
  'settings.leave.pickAll': 'Select all',
  'settings.leave.pickNone': 'Clear selection',
  /* 17 Oct 2026 · mobile: the landscape-only grids, and Erőforrás's folding figures. */
  'mobile.rotate': 'Turn the device sideways to see the table',
  'chart.options': 'Chart options',
  'resource.stats.show': 'Show the figures',
  'resource.stats.hide': 'Hide the figures',
  'list.back': 'Back',
  'list.switchView': 'Switch view',
  'list.table': 'Table',
  'list.selectAll': 'Select all',
  'list.selectRow': 'Select row',
  'list.selectedCount': '{count} selected',
  'list.clearSelection': 'Clear selection',
  'list.rowMenu': 'Row actions',
  'list.sortBy': 'Sort by: {label}',

  'clients.heading': 'Clients',
  'clients.column.name': 'Name',
  'clients.column.code': 'Code',
  'clients.column.contact': 'Contact',
  'clients.column.email': 'Email',
  'clients.column.industry': 'Industry',
  'clients.column.country': 'Country',
  'clients.column.state': 'State',
  'clients.column.projects': 'Projects',
  'clients.filter.state': 'State',
  'clients.state.live': 'Live',
  'clients.state.archived': 'Archived',
  'clients.action.create': 'New client',
  'clients.action.archive': 'Archive',
  'clients.action.restore': 'Restore',
  'clients.dialog.new': 'New client',
  'clients.dialog.edit': 'Edit client',
  'clients.dialog.newHint':
    'The short code appears beside the projects and cannot be changed later.',
  'clients.dialog.editHint': 'The short code cannot be changed – the projects refer to it.',
  'clients.field.name': 'Client name',
  'clients.field.namePlaceholder': 'e.g. Rossmann Magyarország Kft.',
  'clients.field.contact': 'Contact',
  'clients.field.contactPlaceholder': 'Name',
  'clients.field.email': 'Email',
  'clients.field.industry': 'Industry',
  'clients.field.country': 'Country',
  'clients.empty.title': 'No clients yet',
  'clients.empty.body': 'Add one, and its projects will collect underneath it.',
  'clients.error.name': 'A client name cannot be empty.',
  'clients.error.email': 'That email address is not in a valid format.',
  'clients.error.contactName': 'Every contact needs a name.',
  'clients.contacts.title': 'Contacts',
  'clients.contacts.row': 'Contact {index}',
  'clients.contacts.add': 'New contact',
  'clients.contacts.remove': 'Remove contact {index}',
  'clients.contacts.saveFailed': 'The client was saved, but its contacts were not: {reason}',
  'clients.error.industry': 'Give an industry – the list filters on it.',

  'country.HU': 'Hungary',
  'country.DE': 'Germany',
  'country.AT': 'Austria',
  'country.SK': 'Slovakia',
  'country.RO': 'Romania',
  'country.GB': 'United Kingdom',
  'country.US': 'United States',

  'projects.heading': 'Projects',
  'projects.column.name': 'Name',
  'projects.column.prefix': 'Prefix',
  'projects.column.client': 'Client',
  'projects.column.status': 'Status',
  'projects.column.pm': 'Project manager',
  'projects.column.range': 'Dates',
  'projects.column.people': 'People',
  'projects.column.done': 'Progress',
  'projects.column.hours': 'Hours / estimate',
  'projects.column.start': 'Start',
  'projects.column.end': 'End',
  'projects.view.all': 'All projects',
  'projects.action.archiveProject': 'Archive project',
  'projects.action.unarchive': 'Unarchive project',
  'projects.view.active': 'Active',
  'projects.view.mine': "I'm the PM",
  'projects.view.late': 'With late tasks',
  'projects.done.of': '{done}/{total}',
  'projects.internal': 'Internal project',
  'projects.crumb': '{name} ({key})',
  'projects.action.create': 'New project',
  'projects.action.menu': 'Project actions',
  'projects.sync.started': 'Sync started',
  'projects.sync.startedNote': 'Tasks arrive in the background; the screen updates itself.',
  'projects.sync.already': 'A sync is already running',
  'projects.sync.failed': 'The sync did not start.',
  'projects.action.pin': 'Pin to the sidebar',
  'projects.action.unpin': 'Unpin from the sidebar',
  'projects.action.open': 'Open',
  'projects.action.back': 'Back to projects',
  'projects.action.filterTasks': 'Filter tasks by this',
  'projects.action.timeline': 'Timeline',
  'projects.action.report': 'Report on this project',
  'projects.action.archive': 'Archive',
  'projects.action.export': 'Export',
  'projects.toasts': 'Project notifications',
  'clients.toasts': 'Client notifications',
  'projects.toast.archived': 'Project archived',
  'projects.statusReason.title': 'Why is the status changing?',
  'projects.statusReason.label': 'Reason',
  'projects.statusReason.placeholder': 'E.g. the client has paused the work',
  'projects.statusReason.required': 'A status change needs a reason.',
  'projects.statusReason.save': 'Change status',
  'projects.statusReason.bulkArchive': 'Archiving {count} projects — one reason for all of them.',
  'projects.statusReason.bulkUnarchive': 'Restoring {count} projects — one reason for all of them.',
  'projects.pm.notEligible': "This person can't manage projects.",
  'projects.toast.archivedMany': '{count} projects archived',
  'projects.toast.unarchived': 'Project restored',
  'projects.toast.unarchivedMany': '{count} projects restored',
  'projects.dialog.new': 'New project',
  'projects.dialog.edit': 'Edit project',
  'projects.dialog.newHint': 'The prefix is what task keys are made of, e.g. ROSS-1043.',
  'projects.dialog.editHint':
    'The key prefix cannot be changed – the existing task keys refer to it.',
  'projects.field.name': 'Project name',
  'projects.field.namePlaceholderGeneric': 'The project name',
  'projects.field.newClient': 'New client',
  'projects.field.namePlaceholder': 'e.g. Rossmann StoreAssist',
  'projects.field.prefix': 'Key prefix',
  'projects.field.prefixHint': '2–6 capital letters',
  'projects.field.client': 'Client',
  'projects.field.internal': 'Internal project',
  'projects.field.pickClient': 'Choose a client…',
  'projects.field.noClient': 'No client',
  'projects.field.pm': 'Project manager',
  'projects.field.status': 'Status',
  'projects.field.code': 'Reference code',
  'projects.field.start': 'Start',
  'projects.field.end': 'End',
  'projects.empty.title': 'No projects yet',
  'projects.empty.body': 'Create one, and its tasks will sit under its prefix.',
  'projects.error.name': 'A project name cannot be empty.',
  'projects.error.prefix':
    'The prefix is 2–6 capital letters – this is what task keys are made of.',
  'projects.error.dates': 'The end cannot come before the start.',
  'projects.error.client': 'Choose a client, or mark it as an internal project.',
  'projects.status.draft': 'Draft',
  'projects.status.active': 'Active',
  'projects.status.on_hold': 'On hold',
  'projects.status.completed': 'Completed',
  'projects.status.archived': 'Archived',

  'project.gate.title': 'This project is not ready yet',
  'project.gate.body':
    'Every task belongs to a line item, so the project needs a contract with at least one line item on it.',
  'project.gate.bodyNoAccess':
    'Every task belongs to a line item, so the project needs a contract with at least one line item on it. Someone who manages contracts can add them.',
  'project.gate.step.contract': 'A contract on the project',
  'project.gate.step.lineItem': 'A line item on the contract',
  'project.tab.overview': 'Overview',
  'project.tab.resource': 'Resourcing',
  'project.tab.finance': 'Money',
  'project.resource.estimate': 'Estimate',
  'project.resource.planned': 'Planned',
  'project.resource.tracked': 'Logged',
  'project.resource.remaining': 'Plan remaining',
  'project.tab.people': 'People',
  'project.fact.people': 'Current members',
  'project.overview.pending':
    'Progress, tracked time and schedule arrive with tasks and hours – until then no figure is shown here, because none would be true.',
  'project.people.lede':
    'Anybody listed here may log time against this project’s tasks, and only they can be assigned to them. This is a permission, not a roster.',
  'project.people.add': 'Add',
  'project.people.toast.added': '{name} added to the project',
  'project.people.addAction': 'Add to project',
  'project.people.active': 'Active',
  'project.people.past': 'Past',
  'project.people.close': 'End membership today',
  'project.people.lastDayToday': 'Last day: today',
  'project.people.toast.endsTonight': '{name} leaves the project at midnight.',
  'project.people.toast.endNow': 'End membership now',
  'project.people.toast.endNowFailed': 'Couldn’t end it',
  'project.people.toast.ended': '{name}’s membership has ended.',
  'project.people.reopen': 'Reopen membership',
  'project.people.remove': 'Remove from project',
  'project.people.column.name': 'Name',
  'project.people.column.email': 'Email',
  'project.people.column.role': 'Role',
  'project.people.column.projectRole': 'Project role',
  'project.people.column.open': 'Open',
  'project.people.pmTag': '{name} · PM',
  'project.people.notTracking': 'Does not log time',
  'project.people.notTrackingHint':
    'Does not log time, so they can no longer be added as a new member. They were on the project before, or their time tracking was switched off since.',
  'project.people.notTrackingOption': '{name} – does not log time',
  'project.people.edit': 'Edit membership',
  'project.people.upcoming': 'Not started yet',
  'project.people.windowOrder': 'A membership cannot end before it starts.',
  'project.people.column.from': 'From',
  'project.people.column.until': 'Until',
  'project.people.column.state': 'State',
  'project.people.empty.title': 'Nobody is on this project yet',
  'project.people.empty.body':
    'Add the people who will work on it – without that they cannot log time.',

  'error.unavailable': 'That is not available right now. Check your connection, then try again.',
  'error.duplicate_code': 'That code is already in use: {value}',
  'error.client_in_use': 'The client still has live projects – {count} of them. Close those first.',
  'error.internal_project_billable':
    'The project has {count} billable line items, so it cannot be internal. Make them non-billable first.',
  'error.holds_assigned_tasks':
    'That person still holds assigned tasks on this project – {count} of them. Sort those out first.',
  'error.member_not_tracking':
    '{name} does not log time, so they cannot be added to a project. Turn their time tracking on first.',

  /* ══ C8 ═══════════════════════════════════════════════════════════════════════════════════ */

  'nav.tasks': 'Tasks',
  'title.tasks': 'Tasks',

  'task.activity': 'Activity',
  'task.tab.comments': 'Comments',
  'task.tab.logs': 'Time logs',
  'task.logs.total': 'Total logged',
  'task.logs.empty': 'Nobody has logged time against this task yet.',
  'task.logs.partial':
    'Showing the {count} most recent entries. The total above covers all of them.',

  'task.field.title': 'Title',
  'task.field.description': 'Description',
  'task.field.status': 'Status',
  'task.field.priority': 'Priority',
  'task.field.assignee': 'Assignee',
  'task.field.project': 'Project',
  'task.field.estimate': 'Estimate',
  'task.field.estimateHint': 'Accepts 8, 8h, 8:00 or 8,0.',
  'task.field.start': 'Start',
  'task.column.tracked': 'Logged',
  'task.column.subtasks': 'Subtasks',
  'task.column.tags': 'Labels',
  'task.field.due': 'Due',
  'task.field.tags': 'Tags',
  /* J-2 · a synced task whose Jira status nothing maps: the status is locked until it is. */
  'task.status.unmapped': 'Jira: {status} – not mapped',
  'task.status.unmappedHelp':
    'The status cannot be changed until Jira’s “{status}” status is mapped in the integration’s Statuses step.',
  /* J-1 · what the tracker files a synced task under — Jira's own words, read-only. */
  'task.field.components': 'Components',
  'task.field.fixVersions': 'Fix versions',
  'task.field.epic': 'Epic',
  'task.field.tagsPlaceholder': 'new tag',
  /* J-6 · a linked task's tags are the tracker's labels: picked, not typed. */
  'task.field.tagsTrackerPick': 'Label from the tracker…',
  'task.field.tagsTrackerNone':
    'The tracker’s labels have not been read yet – the next sync brings them in.',
  'task.noProject': 'No project',

  'task.description.empty': 'No description. Write down what done looks like.',
  'task.action.editDescription': 'Edit description',
  'task.action.renameTitle': 'Rename',
  'task.action.removeTag': 'Remove tag',
  'task.action.back': 'Back',

  'task.subtasks': 'Subtasks',
  'task.subtasks.progress': '{done} / {total} done',
  'task.dependencies': 'Dependencies',
  'task.dependency.waitsFor': 'Waits for',
  'task.dependency.blocks': 'Waited on by',
  'task.dependency.lag': '{days} days',
  'task.links': 'Related tasks',
  'task.subtasks.add': 'Add',
  'task.subtasks.placeholder': 'What else needs doing?',
  'task.subtasks.detachOne': 'Detach {key}',
  'task.subtasks.inherits': 'The subtask joins {project}, with its parent.',
  'task.link.add': 'Link a task',
  'task.link.empty': 'No related tasks.',
  'task.link.removeOne': 'Remove the link to {key}',
  'task.dependency.add': 'Add a dependency',
  'task.dependency.empty': 'No dependencies.',
  'task.dependency.lagLabel': 'Lag (days)',
  'task.dependency.removeOne': 'Remove the dependency on {key}',
  'task.picker.search': 'Search by key or title…',
  'task.picker.empty': 'Nothing matched.',
  'task.picker.scope.project': 'Only tasks in {project} can be related here.',
  'task.picker.scope.none': 'Only tasks with no project can be related here.',

  'task.attachment.add': 'Attach',
  'task.attachment.uploading': 'Uploading…',
  'task.attachment.open': 'Open {name} full size',
  'task.attachment.download': 'Download {name}',
  'task.attachment.remove': 'Remove {name}',
  'task.attachment.tooLarge': 'That file is too large (15 MB at most).',
  'task.attachment.infected': 'The virus scanner refused that file.',
  'task.attachment.scannerDown': 'Files cannot be checked right now. Try again shortly.',
  'task.attachment.failed': 'The upload did not go through.',
  'task.comments.empty': 'No comments yet.',
  'task.comment.write': 'Write a comment',
  'comment.bold': 'Bold',
  'comment.italic': 'Italic',
  'comment.strike': 'Strikethrough',
  'comment.code': 'Code',
  'comment.heading': 'Heading',
  'comment.bullets': 'Bullet list',
  'comment.numbers': 'Numbered list',
  'comment.quote': 'Quote',
  'comment.codeBlock': 'Code block',
  'comment.link': 'Link',
  'comment.undo': 'Undo',
  'comment.redo': 'Redo',
  'task.comment.placeholder': 'Say what happened. Type @ to mention somebody.',
  'task.comment.label': 'Comment editor',
  'task.comment.mentions': 'Mention suggestions',
  'task.comment.hint': 'Send with Ctrl+Enter',
  'task.comment.attachRemove': 'Take {name} off this comment',
  'task.comment.mention': 'Mention a colleague',
  'task.comment.send': 'Send',
  'task.comment.edited': 'edited',

  'task.mode.drawer': 'Shrink to the drawer',
  'task.mode.modal': 'Enlarge',
  'task.mode.full': 'Full screen',

  'task.error.estimate': 'That is not an amount of time. Accepts 8, 8h, 8:00 or 8,0.',
  'error.assignee_not_a_project_member':
    'Not a member of the task’s project, so cannot be assigned to it: {name}',
  'error.would_cycle': 'That would close a loop – the task would depend on itself indirectly.',
  'error.line_item_closed': '{name} is closed, so nothing new can be added to it. Reopen it first.',
  'error.member_not_active': '{name} is not an active member, so nothing can be assigned to them.',
  'error.owner_not_a_project_member': '{name} is not a member of the project, so cannot be owner.',
  'error.contract_closed':
    '{name} is closed: no new task can go on its line items, and no time can be logged against them.',
  'error.line_item_takes_no_work':
    'This line item is a licence and fee – no task, hour or allocation can go on it.',
  'error.task_status_unmapped':
    'The Jira status ({status}) is not mapped, so the status cannot be changed here. Map it in the integration’s Statuses step.',
  'error.task_tag_not_in_tracker':
    'The tracker has no “{tag}” label. A synced task can only carry the tracker’s own labels.',

  'project.tab.tasks': 'Tasks',
  'project.tasks.empty.title': 'No tasks on this project yet',
  'project.tasks.empty.body': 'Create one – its key comes from the project’s prefix.',
  'project.tasks.create': 'New task',
  'project.tasks.column.key': 'Key',
  'project.tasks.column.title': 'Title',
  'project.tasks.column.status': 'Status',
  'project.tasks.column.priority': 'Priority',
  'project.tasks.column.assignee': 'Assignee',
  'project.tasks.column.due': 'Due',
  'project.tasks.column.estimate': 'Estimate',
  'project.tasks.footer': '{count} tasks · {open} open',
  'project.tasks.footerPaged': '{count} of {total} tasks shown · {open} open',

  'project.fact.progress': 'Progress',
  'project.fact.progressNote': '{done} / {total} tasks',
  'project.fact.late': 'Late',
  'project.fact.lateNone': 'nothing late',
  'project.fact.lateCount': '{count} tasks',
  'project.fact.nextDue': 'Next deadline',
  'project.fact.nextDueNone': 'no deadline coming up',
  'project.fact.breakdown': 'By status',
  'project.fact.schedule': 'Schedule',
  'project.fact.scheduleOk': 'on track',
  'project.fact.scheduleLate': '{count} late',
  'project.fact.participants': 'Participants',
  'project.fact.participantsUnit': 'people',
  'project.panel.effort': 'Load and effort',
  'project.panel.effortPerson': 'Person',
  'project.panel.effortShare': 'Share',
  'project.panel.effortTracked': 'Tracked',
  'project.panel.effortEstimate': 'Estimate',
  'project.panel.effortMeta': '{open} open · {closed} closed',
  'project.panel.effortPm': ' · PM',
  'project.panel.effortEmpty': 'Nobody has logged time on this project yet.',
  'project.panel.upcoming': 'Upcoming deadlines',
  'project.overtime.note': 'Approved and pending overtime on this project',
  'project.overtime.none': 'No overtime claimed on this project',
  'project.overtime.accounted': 'accounted {hours}',
  'project.overtime.pending': 'pending',
  'project.panel.upcomingEmpty': 'No upcoming deadline.',
  'project.panel.recent': 'Recent entries',
  'project.panel.recentEmpty': 'No time logged yet.',
  'project.status.menu': 'Project status',
  'project.status.lastChanged': 'Last changed: {date} by {name} · Reason: {reason}',
  'project.people.column.share': 'Share',
  'project.tab.time': 'Time',
  'project.fact.tracked': 'Tracked time',
  'project.fact.trackedOf': '/ {estimate} estimated',
  'project.fact.noEstimate': 'no estimate',

  'project.chart.period': '{from} – {to} · {total} in total',
  'project.chart.earlier': 'Earlier period',
  'project.chart.anchor': 'Period start',
  'project.chart.later': 'Later period',
  'project.chart.granularity': 'Chart period',
  'project.chart.weekly': 'Weekly',
  'project.chart.monthly': 'Monthly',
  'project.chart.empty': 'No time logged against this project yet.',

  'project.time.empty': 'Nothing logged',
  'project.time.emptyNote': 'Nobody has logged time against this project yet.',
  'project.time.footer': '{count} entries · {hours} in total',
  'project.time.footerPartial': 'Showing {count} entries · {hours} on this project in total',
  'project.time.noMatch': 'No matches',
  'project.time.noMatchNote': 'Adjust the search or the member filter.',

  'time.column.date': 'Date',
  'time.column.task': 'Task',
  'time.column.note': 'Note',
  'time.column.member': 'Logged by',
  'time.column.hours': 'Hours',
  'time.column.overtime': 'Overtime',
  'time.log.noOvertime': 'Not overtime',
  'time.log.isOvertime': 'Overtime',

  'task.dialog.new': 'New task',
  'task.dialog.newHint':
    'Its key comes from the project’s prefix. Without a schedule it starts in the default status.',
  'task.dialog.titlePlaceholder': 'e.g. Gantt update for the Q4 milestones',
  'task.error.title': 'A task title cannot be empty.',
  'task.error.lineItem': 'Choose a line item: every task belongs to one.',
  'task.action.create': 'Create task',
  'task.action.open': 'Open',

  /* ══ C9 ═══════════════════════════════════════════════════════════════════════════════════ */

  'tasks.heading': 'Tasks',
  'tasks.footer': '{count} tasks',
  'tasks.footerPage': '{count} tasks on this page',
  'tasks.empty.title': 'No tasks yet',
  'tasks.empty.body': 'Add a task to get started.',
  'tasks.empty.filteredTitle': 'No tasks match the filter',
  'tasks.empty.filteredBody': 'Clear a filter, or add a task.',

  'tasks.view.list': 'List',
  'tasks.view.board': 'Kanban',
  'tasks.view.timeline': 'Timeline',
  'tasks.view.grid': 'Grid',

  'tasks.board': 'Kanban board',
  'tasks.board.column': '{name} column',
  'tasks.board.move': 'Move',
  'tasks.board.moveTo': 'Move to {name}',
  'tasks.board.empty': 'Nothing here. Drag a task in, or add one.',
  'tasks.board.emptyFiltered': 'No task in this column matches the filters.',
  'tasks.moved': '{key} → {name}',
  'tasks.moved.undo': 'Undo',
  'tasks.moved.undoFailed': 'The undo did not go through',
  'tasks.rescheduled': '{key} rescheduled: {from} → {to}',

  'tasks.timeline': 'Timeline',
  'tasks.timeline.bar': '{key} · {title}',
  /* ⚠️ Count-neutral: the catalogue has no plural rules by design (see the head of this file),
     and '1 dependency conflicts' is what the naive phrasing renders the moment one arrow breaks. */
  'tasks.timeline.conflicts': 'Dependency conflicts: {count}',
  'tasks.timeline.milestone': 'Milestone: {key} · {title} · a single day with no estimate.',
  'tasks.timeline.empty': 'No task has both a start and a due date.',
  'tasks.timeline.unscheduled':
    'Tasks missing from the timeline because they have no start or no due date: {count}',
  'tasks.zoom.week': 'By week',
  'tasks.zoom.month': 'By month',
  'tasks.zoom.quarter': 'By quarter',
  'tasks.today': 'Back to today',

  'tasks.grid': 'Data grid',
  'tasks.grid.readOnly': 'The {label} column cannot be edited.',
  'tasks.grid.required': '{label} cannot be empty.',
  'tasks.grid.unknownStatus': 'No such status: {value}',
  'tasks.grid.unknownAssignee': 'No such assignee: {value}',
  'tasks.grid.notAnOption': '{column}: “{value}” is not one of the offered values.',
  'tasks.toasts': 'Notifications',

  /* ══ C14 ══════════════════════════════════════════════════════════════════════════════════ */

  'nav.people': 'People',
  'title.people': 'People',
  'title.person': 'Person',

  'people.column.person': 'Person',
  'people.column.email': 'Email',
  'people.column.roles': 'Permissions',
  'people.column.status': 'Status',
  'people.column.tracks': 'Time tracking',
  'people.column.team': 'Team',
  'people.column.employment': 'Employment',
  'people.column.projects': 'Projects',
  'people.column.month': 'Hours this month',

  'people.status.active': 'Active',
  'people.status.invited': 'Invited',
  'people.status.deactivated': 'Deactivated',
  'people.status.deleted': 'Deleted',

  'people.employment.internal': 'Internal',
  'people.employment.contractor': 'Contractor',

  'people.tracks.yes': 'Logs time',
  'people.tracks.no': 'Does not log',

  'people.filter.deleted': 'Deleted',
  'people.filter.liveOnly': 'Live only',
  'people.filter.withDeleted': 'Including deleted',

  'people.action.open': 'Open',
  'people.action.activate': 'Activate',
  'people.action.deactivate': 'Deactivate',
  'people.action.delete': 'Delete',
  'people.action.deleteNeedsDeactivate':
    'Deactivate them first – only a deactivated person can be deleted.',

  'identities.title': 'Remote accounts',
  'identities.unpaired': '{count} accounts we cannot place',
  'identities.allPaired': 'Every discovered account is paired with somebody.',
  'identities.match': 'Match by email',
  'identities.matched': '{count} newly matched',
  'identities.noMatch': 'No new matches by email.',
  'identities.failed': 'The match could not be run.',
  'identities.none': 'not paired',
  'identities.manual': 'by hand',
  'people.empty': 'Nobody here',
  'people.emptyNote': 'Adjust the filters, or invite somebody.',

  'people.tabs': 'This person’s data',
  'people.facts': 'Details',
  'people.metafields': 'Metafields',
  'people.deletedNote':
    'This person has been deleted. Their name is kept so the hour reports stay readable; everything else was cleared, and this page cannot be edited.',

  'people.kpi.monthHours': 'Logged this month',
  'people.kpi.monthHoursHint': 'since the 1st',
  'people.kpi.openTasks': 'Open tasks',
  'people.kpi.openTasksHint': '{total} in total',
  'people.kpi.projects': 'Projects',
  'people.kpi.projectsHint': 'a member of today',
  'people.kpi.noRole': 'No permission level',
  'people.kpi.noTracksHint': 'Left out of the hour reports',
  'people.kpi.billable': 'Billable',
  'people.kpi.billableHint': '{hours} on billable line items',
  'people.kpi.billableNone': 'Nothing logged this month yet',

  /* ── C63 · what this month's hours were worth ────────────────────────────── */
  'people.split.title': 'Billing · this month',
  'people.split.empty': 'Nothing logged this month yet.',
  'people.split.fixed': 'Fixed price',
  'people.split.tm': 'Time and material',
  'people.split.nonBillable': 'Non-billable',
  'people.split.none': 'No line item',

  'people.field.team': 'Team',
  'people.field.noTeam': 'No team',
  'people.field.employment': 'Employment',
  'people.field.seniority': 'Seniority',
  'person.seniority.none': 'Not set',
  'person.seniority.junior': 'Junior',
  'person.seniority.medior': 'Medior',
  'person.seniority.senior': 'Senior',
  'person.seniority.lead': 'Lead',
  'person.seniority.principal': 'Principal',
  'finance.breakdown.bySeniority': 'Seniority',
  'people.field.manager': 'Manager',
  'people.field.leave': 'Leave',
  'people.leave.managed': 'Managed here',
  'people.leave.external': 'Managed elsewhere',
  'people.field.noManager': 'None',
  'people.field.tracks': 'Time tracking',
  'people.field.resource': 'Resource',
  'people.column.plan': 'Resource plan',
  'people.plan.yes': 'Planned',
  'people.plan.yesLong': 'Planned resource',
  'people.plan.no': 'Not planned',
  'people.plan.yesHint': 'Shown in resource planning and can be allocated to line items.',
  'people.plan.noHint': 'Left out of the resource views.',
  'people.plan.needsTracking': 'Only somebody who logs time can be planned as a resource.',
  'people.action.planOff': 'Stop resource planning',
  'people.action.planOn': 'Start resource planning',
  'people.toast.planOff': 'Not a planned resource',
  'people.toast.planOffNote': '{name} is left out of the resource views.',
  'people.toast.planOn': 'Planned resource',
  'people.toast.planOnNote': '{name} now appears in the resource views.',
  'onboarding.plan.label': 'Plan their capacity',
  'onboarding.plan.hint.on': 'Included in resource planning: can be allocated to line items.',
  'onboarding.plan.hint.off':
    'Left out of resource planning — cannot be allocated to a contract line item.',
  'people.field.phone': 'Phone',
  'people.field.location': 'Location',
  'people.field.familyName': 'Family name',
  'people.field.givenName': 'Given name',
  'people.field.email': 'Email',
  'people.field.timezone': 'Timezone',
  'people.field.language': 'Language',
  'people.field.startsOn': 'Started',
  'people.field.endsOn': 'Left',

  'people.language.hu': 'Magyar',
  'people.language.en': 'English',

  'people.tab.projects': 'Projects',
  'people.skills.failed': 'Saving the skill failed.',
  'people.skills.title': 'Skills',
  'people.skills.add': 'Skill',
  'people.skills.search': 'Search skills',
  'people.skills.empty': 'No skills recorded yet.',
  'people.tab.tasks': 'Tasks',
  'people.tab.logs': 'Time logs',
  'people.projects.emptyTitle': 'No projects',
  'people.projects.empty': 'No projects – add them on a project’s People tab.',
  'people.tasks.emptyTitle': 'No tasks',
  'people.tasks.empty': 'Nothing is assigned to them.',
  'people.logs.month': 'Month',
  'people.logs.search': 'Search by task or note…',
  'people.projects.search': 'Search projects…',
  'people.tasks.search': 'Search tasks…',
  'people.logs.emptyTitle': 'No time logs',
  'people.logs.empty': 'No time has been logged yet.',
  'people.list.noMatch': 'No matches',
  'people.list.noMatchBody': 'Nothing matches the search and filters.',
  'people.copy.email': 'Copy the email address',
  'people.copy.phone': 'Copy the phone number',
  'people.status.activeNote': 'Signs in, logs time, and appears in the reports.',
  /* ── C39 cleanup · this person's tracker account, on their own page. ── */
  'people.integration.title': 'Integration settings',
  'people.integration.none': 'not assigned',
  'people.integration.search': 'Search accounts',
  'people.integration.hint':
    'This decides whose hours arrive from Jira. Only accounts nobody has been assigned yet can be chosen.',
  'people.integration.proven':
    'They connected their own Jira account, so this cannot be overridden.',
  'people.status.deactivatedNote': 'Cannot sign in and logs no time. Their earlier worklogs stay.',
  'people.status.invitedNote':
    'Invited, and has not accepted yet. Until then they cannot sign in or log time.',
  'logs.column.day': 'Day',
  'people.tab.noProjects': 'No projects',
  'people.tab.noProjectsNote': 'This person is not on any project today.',
  'people.tab.noTasks': 'No tasks',
  'people.tab.noTasksNote': 'Nothing is assigned to them.',
  'people.tab.noLogs': 'Nothing logged',
  'people.tab.noLogsNote': 'This person has not logged any time yet.',

  'people.error.stillActive': 'Deactivate {name} first.',
  'people.error.unknownSkill': 'That skill or level does not exist in this workspace.',
  'people.error.deleted': 'This person has been deleted and cannot be changed.',
  'people.error.notPlannable': '{name} does not log time, so they cannot be planned as a resource.',
  'people.error.managerSelf': 'Somebody cannot be their own manager.',
  'people.error.managerCycle': 'That would create a circular management chain.',

  'status.category.open': 'Open',
  'settings.weights.title': 'Weights',
  'settings.weights.lede':
    "How much of a task's estimate counts as earned in each status category. Earned value, CPI and the burn charts are counted from these.",
  'settings.weights.fixed': 'fixed',
  'settings.weights.column': 'Weight (%)',
  'settings.weights.ariaLabel': '{category} weight, in percent',
  'status.category.inProgress': 'In progress',
  'status.category.inReview': 'In review',
  'status.category.closed': 'Closed',
  'worklog.unit.min15': '15 minutes',
  'worklog.unit.min30': '30 minutes',
  'worklog.unit.decimal': 'Decimal hours',

  'io.actions': 'Data',
  'import.action': 'Import',
  'import.run': 'Start Import',
  'common.prefixPlaceholder': 'e.g. ABC',
  'import.title.task': 'Import tasks',
  'import.title.worklog': 'Import time entries',
  'import.desc.task': 'An existing key is updated; a blank one creates a task.',
  'import.desc.worklog': 'The entries are recorded in your name.',

  'import.drop': 'Drop the file here, or browse',
  'import.dropNote': 'CSV, up to {mb} MB. Comma or semicolon both work.',
  'import.sample': 'Sample file',
  'import.cols.task': 'key, title, project, estimate, start, due',
  'import.cols.worklog': 'date, task key, hours, note',
  'import.tooBig': 'That file is larger than {mb} MB. Split it, or narrow the period.',

  'import.working': 'Working…',
  'import.workingNote':
    'Every row goes through the same rules as manual entry – which is why it takes as long as it does.',

  'import.done': '{ok} rows imported',
  'import.partial': '{bad} rows failed – {ok} rows imported',
  'import.tally': 'New: {created} · Updated: {updated} · Skipped (already there): {skipped}',
  'import.line': 'line {line}',
  'import.downloadErrors': 'Download the error list',
  'import.csv.line': 'line',
  'import.csv.error': 'error',
  'import.newFile': 'Another file',

  'import.error.columns': 'The file is missing: {columns}. Download the sample file.',
  'import.defaultLineItem': 'Default line item for new tasks',
  'import.defaultLineItemNone': 'None – every row names one in its line_item column',
  'import.error.tooLarge':
    'That file has {rows} rows – more than one request will process. Split it into smaller files.',

  'import.toast.sample': 'Sample file downloaded',
  'import.toast.sampleNote': 'Fill it in and drop it back into the dialog.',
  'import.toast.done': 'Import finished',

  'common.done': 'Done',

  'export.title': 'Export {what}',
  'export.lede':
    'A small export downloads immediately; a large one is emailed – the server decides which.',
  'export.field.period': 'Period',
  'export.field.from': 'From',
  'export.field.to': 'To',
  'export.run': 'Start export',
  'export.running': 'Building…',
  'export.format': 'Format',
  'export.noPeriod': 'The whole {what} list goes into the file – there is no period for this one.',

  'export.period.week': 'This week',
  'export.period.lastweek': 'Last week',
  'export.period.month': 'This month',
  'export.period.lastmonth': 'Last month',
  'export.period.quarter': 'This quarter',
  'export.period.all': 'All time',
  'export.period.custom': 'Custom period',

  'export.domain.task': 'Tasks',
  'export.domain.worklog': 'Time entries',
  'export.domain.project': 'Projects',
  'export.domain.client': 'Clients',
  'export.domain.member': 'People',
  'export.domain.report': 'Report',

  'export.toast.ready': 'The export was downloaded',
  'export.toast.rows': '{rows} rows.',
  'export.toast.queued': 'The export is being built',
  'export.toast.queuedNote': '{rows} rows – we will email it to {email} as an attachment.',
  'export.error.tooLarge':
    'That many rows ({rows}) cannot be delivered by email. Narrow the period.',

  'report.action.export': 'CSV export',
  'report.action.exportFile': 'Export',
  'settings.export.note':
    'A small export downloads straight away; a large one is built and emailed as an attachment.',

  'title.billing': 'Billing',
  'settings.nav.billing': 'Billing',

  'billing.noProvider.title': 'No payment provider is connected',
  /* ⚠️ **REWRITTEN IN S6, because half of it stopped being true.** The plan and its price now come
     from the Control Panel rather than from a provider; the renewal, the card and the invoices still
     do, and there is still no provider. It now lists only what is genuinely missing. */
  'billing.noProvider.body':
    'The renewal, the card and the invoices come from a payment provider – that is the next phase. Your plan and your seats are set by the operator; the invoicing data is yours, and it stays the same when a provider arrives.',

  'billing.plan.title': 'Plan',
  'billing.plan.lede': 'The plan you have been given. Nothing charges you for it.',
  'billing.plan.none': 'No plan',
  'billing.plan.noneNote':
    'That is the ordinary trial state: with no plan there is no seat limit. The operator sets the plan.',
  'billing.plan.perSeat': '{price} / seat / month',

  'billing.trial.title': 'Trial',
  'billing.trial.body': 'The trial runs until {date}. After that the workspace is suspended.',

  'billing.seats.title': 'Seats',
  'billing.seats.lede': 'How many active members hold a seat right now.',
  'billing.seats.unit': 'active members',
  /* ⚠️ **REWRITTEN IN S6**: the allowance is known now, when there is a plan — the old sentence
     ("the provider's number") claimed the opposite of what the screen shows. */
  'billing.seats.note':
    'Deactivated members do not hold a seat. Invitations nobody has accepted do.',
  'billing.seats.limit': 'seats on this plan: {limit}',
  'billing.seats.unlimited': 'No limit',
  'billing.seats.pending': 'held by invitations: {count}',
  'billing.seats.tenant': 'across the whole organisation: {count}',

  'billing.details.title': 'Invoicing details',
  'billing.details.lede': 'This goes on the invoice. The provider will read it.',
  'billing.field.company': 'Company name',
  'billing.field.email': 'Invoicing email',
  'billing.field.emailNote': 'Invoices go here – not necessarily your own account address.',
  'billing.field.address': 'Address',
  'billing.field.country': 'Country',
  'billing.field.vat': 'VAT number',
  'billing.field.vatOk': 'Valid format.',
  'billing.error.email': 'Enter a valid email address.',
  'billing.error.vatHu': 'A Hungarian VAT number is HU followed by 8 digits.',
  'billing.error.vatEu': 'Country code plus 8–12 characters, e.g. DE123456789.',
  'billing.error.rejected': 'The server rejected the VAT number or the email address.',

  'billing.country.HU': 'Hungary',
  'billing.country.DE': 'Germany',
  'billing.country.AT': 'Austria',
  'billing.country.RO': 'Romania',
  'billing.country.GB': 'United Kingdom',
  'billing.country.US': 'United States',

  'title.settings': 'Settings',
  'breadcrumb.label': 'Breadcrumb',
  'breadcrumb.copy': 'Copy {key}',
  'breadcrumb.copied': 'Copied',
  'breadcrumb.copyFailed': 'Could not copy',
  'breadcrumb.copyFailedBody':
    'The browser did not allow writing to the clipboard. Select it and copy it by hand.',
  'breadcrumb.toasts': 'Copy notifications',
  'breadcrumb.home': 'Home',
  'title.profileSettings': 'Profile settings',
  'title.settingsGeneral': 'General',
  'title.settingsTasks': 'Tasks',
  'title.settingsWorklog': 'Worklog rules',
  'title.settingsIntegrations': 'Integrations',
  'title.settingsData': 'Data',

  'settings.nav.label': 'Workspace',
  'settings.nav.general': 'General',
  'settings.nav.permissions': 'Permissions',
  'settings.nav.tasks': 'Tasks',
  'settings.nav.metafields': 'Metafields',
  'settings.nav.projectRoles': 'Project roles',
  'title.settingsProjectRoles': 'Project roles',
  'settings.projectRoles.title': 'Project roles',
  'settings.projectRoles.new': 'New role',
  'settings.projectRoles.edit': 'Edit the role',
  'settings.projectRoles.name': 'Name',
  'settings.projectRoles.namePlaceholder': 'e.g. Tech Lead',
  'settings.projectRoles.usage': 'on {count}',
  'settings.projectRoles.empty': 'This workspace has no project roles yet.',
  'settings.projectRoles.column.name': 'Name',
  'settings.projectRoles.search': 'Search roles',
  'settings.projectRoles.emptyBody': 'Add one so project members can carry a role.',
  'settings.projectRoles.delete': 'Delete the role',
  'settings.projectRoles.deleteUnused': 'Nobody wears {name}. It can be deleted.',
  'settings.projectRoles.deleteUsed':
    '{count} project memberships wear {name}. Deleting it keeps those people on their projects – only the label goes.',
  'settings.projectRoles.error.name': 'Give the role a name.',
  'settings.projectRoles.error.duplicate': 'A role with that name already exists.',
  'project.people.noRole': 'No role',
  'project.people.roleDefault': 'Their default',
  'people.field.defaultProjectRole': 'Default project role',
  /* R-60 · Teams. */
  'settings.nav.teams': 'Teams',
  'title.settingsTeams': 'Teams',
  'settings.teams.new': 'New team',
  'settings.teams.edit': 'Edit the team',
  'settings.teams.name': 'Name',
  'settings.teams.namePlaceholder': 'e.g. Delivery',
  'settings.teams.column.name': 'Name',
  'settings.teams.column.people': 'People',
  'settings.teams.search': 'Search teams',
  'settings.teams.empty': 'This workspace has no teams yet.',
  'settings.teams.emptyBody': 'Add one, then put people in it from their profile.',
  'settings.teams.delete': 'Delete the team',
  'settings.teams.deleteUnused': 'Nobody is in {name}. It can be deleted.',
  'settings.teams.deleteUsed':
    '{count} people are in {name}. Deleting it takes them out of it – their other teams and everything else about them stay.',
  'settings.teams.error.name': 'Give the team a name.',
  'people.field.teams': 'Teams',
  'people.team.add': 'Add to team',
  'people.team.remove': 'Remove from team',
  'filter.team': 'Team',
  'filter.allTeams': 'All teams',
  'report.filter.teams': 'Team',
  /* 17 Oct 2026 · the person screen's *Alap szerep* row. */
  'people.field.defaultRole': 'Default role',
  'people.field.noDefaultRole': 'Not set',
  'people.error.unknownProjectRole': 'That project role no longer exists.',
  'history.field.defaultProjectRole': 'Default role',
  'settings.nav.worklog': 'Worklog rules',
  'settings.nav.holidays': 'Holidays',
  'settings.nav.schedules': 'Work schedules',
  'settings.nav.rates': 'Rate cards',
  'settings.nav.currency': 'Currencies',
  'title.settingsRates': 'Rate cards',
  'title.settingsCurrency': 'Currencies',

  'settings.currency.title': 'Currencies',
  'settings.currency.add': 'Add a currency',
  'settings.currency.column.code': 'Code',
  'settings.currency.column.name': 'Name',
  'settings.currency.column.symbol': 'Symbol',
  'settings.currency.column.rate': 'Rate',
  'settings.currency.column.status': 'Status',
  'settings.currency.search': 'Search currencies',
  'settings.currency.empty': 'No currency',
  'settings.currency.emptyBody': 'Add at least one and mark it as the base.',
  'settings.currency.default': 'Default',
  'settings.currency.isBase': 'base',
  'settings.currency.active': 'Active',
  'settings.currency.inactive': 'Inactive',
  'settings.currency.makeDefault': 'Make it the default',
  'settings.currency.activate': 'Activate',
  'settings.currency.deactivate': 'Deactivate',
  'settings.currency.rowMenu': '{name} actions',
  'settings.currency.dialog.new': 'New currency',
  'settings.currency.dialog.known': 'Known currencies',
  'settings.currency.dialog.pick': 'Choose…',
  'settings.currency.error.inUse':
    'A rate card is priced in this currency. Re-price it first, then this can go.',
  'settings.currency.error.isDefault':
    'That is the default currency. Make another one the default first.',

  'settings.rates.title': 'Rate cards',
  'settings.rates.tab.templates': 'Templates',
  'settings.rates.tab.assignments': 'Assignments',
  'settings.rates.new': 'New template',
  'settings.rates.assign': 'Assign a rate card',
  'settings.rates.assignShort': 'Assign',
  'settings.rates.searchTemplates': 'Search by template name…',
  'settings.rates.searchPeople': 'Search by name…',
  'settings.rates.column.id': 'ID',
  'settings.rates.column.name': 'Name',
  'settings.rates.column.cost': 'Cost rate',
  'settings.rates.column.target': 'Target rate',
  'settings.rates.column.unit': 'Unit',
  'settings.rates.allUnits': 'All unit types',
  'settings.rates.column.currency': 'Currency',
  'settings.rates.column.status': 'Status',
  'settings.rates.column.person': 'Person',
  'settings.rates.column.card': 'Rate card',
  'settings.rates.column.source': 'Source',
  'settings.rates.column.from': 'From',
  'settings.rates.sub': '{periods} periods · {people} people',
  'settings.rates.unit.hour': 'Hourly',
  'settings.rates.unit.day': 'Daily',
  'settings.rates.fromTemplate': 'Template',
  'settings.rates.fromCustom': 'Custom',
  'settings.rates.customRate': 'Custom rate',
  'settings.rates.noRate': 'No rate card',
  'settings.rates.rowMenu': '{name} actions',
  'settings.rates.empty': 'No rate cards yet. Create one.',
  'settings.rates.emptyBody': 'Create one and assign people to it.',
  'settings.rates.emptyAssignments': 'No rate assigned',
  'settings.rates.emptyAssignmentsBody':
    'Without a rate, hours go in at zero — actual cost and margin are both wrong.',
  'settings.rates.periods': 'Periods',
  'settings.rates.addPeriod': 'Add a period',
  'settings.rates.removePeriod': 'Remove the period',
  'settings.rates.activeLabel': 'Active · can be assigned',
  'settings.rates.namePlaceholder': 'e.g. Senior developer',
  'settings.rates.pickPerson': 'Choose a person…',
  'settings.rates.pickTemplate': 'Choose a template…',
  'settings.rates.dialog.new': 'New rate card',
  'settings.rates.dialog.edit': 'Edit the rate card',
  'settings.rates.dialog.assign': 'Assign a rate card',
  'settings.rates.dialog.reassign': 'Edit the assignment',
  'settings.rates.error.inUse': '{count} assignments point at this template. Move them first.',
  'nav.dashResource': 'Resourcing',
  'title.dashResource': 'Resourcing',
  /* 17 Oct 2026 · Utilizáció — the fourth dashboard. */
  'nav.dashUtil': 'Utilisation',
  'title.dashUtil': 'Utilisation',
  'util.chart.title': 'Utilisation',
  'util.legend.billable': 'Billable',
  'util.legend.nonBillable': 'Non-billable',
  'util.legend.soft': 'Soft booking',
  'util.legend.capacity': 'Working time (100%)',
  'util.tip.capacity': 'Working time {hours}',
  'util.tip.free': 'Free (soft included)',
  'util.tip.value': '{percent}% · {hours}',
  'util.tip.others': '+{count} other projects',
  'util.group.role': 'By role',
  'util.group.seniority': 'By seniority',
  'util.group.free': 'Freeing up',
  'util.group.skill': 'By skill',
  'util.search': 'Search name, role, skill, project…',
  'util.filter.role': 'Role',
  'util.filter.allRoles': 'All roles',
  'util.filter.seniority': 'Seniority',
  'util.filter.allSeniority': 'All',
  'util.noRole': 'No role',
  'util.noSeniority': 'No seniority',
  'util.line.soft': 'Soft',
  'util.row.group': 'Expand and collapse the group',
  'util.row.overPlanned': 'Over-planned in at least one column',
  'util.alloc.soft': 'soft',
  'util.alloc.nonBillable': 'non-billable',
  'util.alloc.crossRole': 'other role',
  'util.alloc.noProject': 'No project',
  'util.empty': 'The filters left nobody',
  'util.tableEmpty': 'The search left no rows in this view.',

  'resource.search': 'Search by name or role…',
  'resource.empty': 'Nobody is planned in this view',
  'resource.week': 'W{week}',

  'resource.stat.people': 'People',
  'resource.stat.under': 'With under-planning',
  'resource.stat.over': 'With over-planning',
  'resource.stat.load': 'Average load',
  'resource.stat.planned': 'Planned',
  'resource.stat.overtime': 'Overtime share',

  'resource.line.capacity': 'Capacity',
  'resource.line.allocated': 'Planned',
  'resource.line.leave': 'Leave',

  'resource.filter.contract': 'Contract',
  'resource.filter.allContracts': 'All contracts',
  'resource.filter.load': 'Load',
  'resource.filter.anyLoad': 'Any',
  'resource.filter.overtime': 'Overtime',
  'resource.filter.overtimeOnly': 'Overtime plans only',
  'resource.load.under': 'Under-planned',
  'resource.load.over': 'Over-planned',

  'resource.row.breakdown': 'Show and hide the allocations',
  'resource.cell.summary': 'Capacity {capacity} · planned {allocated}',
  'resource.cell.edit': '{name} · {column}',
  'resource.item.menu': '{name} actions',
  'resource.item.distribution': 'Daily spread',
  'resource.dist.even': 'Spread evenly across the days',
  'resource.dist.earliest': 'Fill the earliest days first',
  'resource.item.overtimeLabel': 'Planned overtime',
  'resource.tip.title': 'Line item summary: {name}',
  'resource.tip.ratio': 'Plan / estimate',
  'resource.tip.plan': 'Plan',
  'resource.tip.overtime': 'Overtime',
  'resource.tip.remaining': 'Remaining',
  'resource.tip.open': 'Open the line item',
  'resource.overview.open': 'Overview table',
  'resource.overview.title': 'Line item overview',
  'resource.overview.empty': 'This project has no line items.',
  'resource.overview.people': 'People',
  'resource.item.noOvertime': 'No overtime',
  'resource.item.remove': 'Delete the allocation',
  'resource.item.add': 'Add a line item',
  'resource.group.items': 'Line items',
  'resource.group.people': 'People',
  'resource.person.add': 'Add a person',
  'resource.pickPerson.search': 'Search a name or a role…',
  'resource.pickPerson.none': 'Every planned member of the project is already on this line item.',
  'resource.row.people': 'Show and hide the people',
  'resource.emptyItems': 'This project has no line items to plan',
  'resource.footer.lineItems': '{count} line items',
  'resource.error.notAMember':
    'Not a member of the project for this period, so cannot be planned onto it: {name}',
  'resource.pick.notMember':
    '{name} is not a member of {project} in this period, so cannot be planned onto it. Add them on the project’s People tab first.',
  'resource.item.overtime': '{name} ×{multiplier}',
  'resource.cell.title': '{name} · {column}',
  'resource.toast.saved': 'Allocation saved',
  'resource.toast.cleared': 'Allocation cleared',
  'resource.toast.copied': 'Copied to {count} columns',
  'resource.toast.clearedRange': 'Cleared from {count} columns',
  'resource.toast.removed': 'Allocation deleted from today',
  'resource.toast.overtimeOn': 'Planned as overtime',
  'resource.toast.overtimeOff': 'Overtime switched off',
  'resource.toast.priority': 'Daily priority changed',
  'resource.toast.hard': 'Set to hard booking',
  'resource.toast.soft': 'Set to soft booking',
  'resource.toasts': 'Resource notifications',
  'resource.pick.search': 'Search a line item or a contract…',
  'resource.pick.estimate': 'estimate {hours}',
  'resource.pick.none': 'No open line items left to plan onto.',
  'resource.pick.noMatch': 'Nothing matches.',
  'resource.range.copy': 'Copy up to a day…',
  'resource.range.clear': 'Clear up to a day…',
  'resource.range.apply': 'Click here to finish, {column}',
  'resource.cell.hours': '{name} hours in this column',
  'resource.cell.total': 'Total in this column',
  'resource.cell.removeMenu': 'Remove',
  'resource.cell.clear': 'Clear {column}',
  'resource.cell.clearFromToday': 'Clear {column} from today',
  'resource.cell.removeWhole': 'Delete the whole allocation from today',
  'resource.cell.removeWholeSpan': '{from} – {to}',
  'resource.cell.dropDraft': 'Remove this row',
  'resource.cell.add': 'Add another allocation',
  'resource.cell.plain': 'Ordinary hours',
  'resource.cell.priority': 'Daily priority',
  'resource.cell.priorityIs': 'Daily priority: {mode}',
  'resource.cell.booking': 'Booking',
  'resource.cell.bookingIs': 'Booking: {kind}',
  'resource.cell.hard': 'Hard',
  'resource.cell.hardHint': 'Committed capacity.',
  'resource.cell.soft': 'Soft',
  'resource.cell.softHint': 'A pencilled-in reservation – indicative, and inherited by a copy.',
  'resource.error.noWorkingDay':
    'That column has no day this person works – choose a different span.',
  'settings.nav.leave': 'Leave',
  'settings.nav.overtime': 'Overtime',
  'title.settingsOvertime': 'Overtime',
  'settings.overtime.addType': 'New type',
  'settings.overtime.column.name': 'Name',
  'settings.overtime.column.multiplier': 'Multiplier',
  'settings.overtime.column.when': 'When',
  'settings.overtime.when.any': 'Any day',
  'settings.overtime.when.weekend': 'Weekend',
  'settings.overtime.when.holiday': 'Public and company holidays',
  'settings.overtime.default': 'Default',
  'settings.overtime.deleteType': 'Delete {name}',
  'settings.overtime.editType': 'Edit {name}',
  'settings.overtime.empty': 'No overtime types yet. Add one.',
  'settings.overtime.dialog.new': 'New overtime type',
  'settings.overtime.dialog.edit': 'Edit overtime type',
  'settings.overtime.claims': '{count} claims',
  'settings.overtime.noClaims': 'no claims',
  'settings.overtime.makeDefault': 'Make it the default',
  'settings.overtime.isDefault': 'This is the default',
  'settings.overtime.namePlaceholder': 'e.g. Weekend overtime',
  'settings.overtime.search': 'Search types',
  'settings.overtime.emptyBody': 'Add one so overtime claims can carry a multiplier.',
  'logs.row.markOvertime': 'Mark as overtime',
  'logs.row.markOvertimeClaimed': 'This hour is already marked as overtime.',
  'logs.overtime.raised': 'Overtime claim raised',
  'logs.overtime.error.already': 'That entry already has an overtime claim.',
  'logs.overtime.error.noType': 'No active overtime type. Add one in settings first.',
  'nav.requests': 'My requests',
  'nav.toDecide': 'To decide',
  'nav.approvals': 'Requests',
  'nav.dashLogging': 'Logging',
  'nav.dashLeave': 'Absence',
  'title.dashLogging': 'Logging',
  'title.dashLeave': 'Absence',
  'dash.from': 'From',
  'dash.to': 'To',
  'dash.column.person': 'Person',
  'dash.column.variance': 'Variance',
  'dash.row.frame': '{hours} h/week',
  'dash.column.days': 'Days',
  'dash.filter.manager': 'Manager',
  'dash.search': 'Search by name or role…',
  'dash.leave.search': 'Search by name…',
  'range.apply': 'Apply',
  'range.reset': 'Reset',
  'range.trigger': 'Period',
  'range.today': 'Today',
  'range.todayHint': 'Jump to today',
  'range.preset.default': 'Default',
  'range.preset.thisWeek': 'This week',
  'range.preset.thisMonth': 'This month',
  'range.preset.thisQuarter': 'This quarter',
  'range.preset.nextMonth': 'Next month',
  'range.preset.nextQuarter': 'Next quarter',
  'range.preset.lastMonth': 'Last month',
  'range.preset.lastQuarter': 'Last quarter',
  'dash.view.week': 'Weekly',
  'dash.view.day': 'Daily',
  'dash.filter.project': 'Project',
  'dash.filter.allProjects': 'All projects',
  'dash.line.planned': 'Planned',
  'dash.line.logged': 'Logged',
  'dash.line.leave': 'Leave',
  'dash.row.breakdown': 'Project breakdown',
  'dash.row.shortDays': '{count} short days in this window',
  'dash.row.unknownHours': 'Logged on tasks with an unknown line item in this window: {hours}',
  'dash.row.unknownLineItem': 'Unknown line item',
  'dash.row.openUnknown': 'Open the logs on an unknown line item ({hours})',
  'people.logs.lineItem': 'Line item',
  'people.logs.unknownLineItems': 'Unknown line items',
  'nav.time.unknownHours': 'Logged on tasks with an unknown line item in the last 30 days: {hours}',
  'nav.time.shortDays': '{count} short days in the last 30 days',
  'dash.row.noLogs': 'Nothing logged in this window.',
  'dash.footer.people': '{count} people',
  'dash.footer.columns': '{count} periods',
  'dash.filter.everybody': 'Everybody',
  'dash.filter.schedule': 'Schedule',
  'dash.filter.allSchedules': 'All schedules',
  'dash.filter.varianceOnly': 'Only with variance',
  'dash.cell.hours': '{logged} / {planned} hours',
  'dash.verdict.under': 'under',
  'dash.verdict.ok': 'to plan',
  'dash.verdict.over': 'over plan',
  'dash.verdict.off': 'rest day',
  'dash.verdict.future': 'still ahead',
  'dash.verdict.free': 'no daily plan',
  'dash.verdict.leave': 'leave',
  'dash.verdict.holiday': 'public holiday',
  'dash.empty.logging': 'Nobody matches the filter.',
  'dash.empty.leave': 'Nobody is away in this window.',
  'title.requests': 'My requests',
  'title.approvals': 'Requests',
  'leave.new': 'New request',
  'leave.view.all': 'All requests',
  'leave.menu.open': 'Open',
  'leave.menu.openTask': 'Open task',
  'leave.column.year': 'Frame year',
  'leave.column.created': 'Submitted',
  'leave.column.dayAndTask': 'Day and task',
  'leave.column.hoursTimes': 'Hours × rate',
  'leave.column.accounted': 'Accounted',
  'leave.filter.allTypes': 'All types',
  'leave.filter.allYears': 'All frame years',
  'leave.empty.overtime': 'No overtime requests in this view',
  'leave.empty.overtimeBody': 'Overtime is marked on a logged entry – the claim is raised from it.',
  /* 16 Oct 2026 · the Túlóra tab's figure strip. */
  'leave.ot.approvedYear': 'Approved — this year',
  'leave.ot.thisMonth': 'This month',
  'leave.claim.new': 'New overtime claim',
  'leave.claim.newHint': 'Pick a logged entry – the date, the hours and the task come from it.',
  'leave.claim.pickLog': 'Which entry?',
  'leave.claim.searchPlaceholder': 'Search by task, note or date',
  'leave.claim.noLogs': 'No entry to pick',
  'leave.claim.noLogsBody':
    'Everything in the last 90 days already carries a claim, or the filters left nothing.',
  'leave.claim.picked': 'Picked: {task} · {date} · {hours}',
  'leave.claim.allTasks': 'All tasks',
  'leave.claim.typeFromDay': 'From the day',
  'leave.claim.notePlaceholder': 'Why was this overtime?',
  'leave.claim.submit': 'Raise claim',
  'leave.column.person': 'Person',
  'leave.column.kind': 'Kind',
  'leave.column.amount': 'Amount',
  'leave.kind.leave': 'Leave',
  'leave.kind.overtime': 'Overtime',
  'leave.filter.kind': 'Kind',
  'leave.filter.allKinds': 'Both kinds',
  'leave.amount.days': '{days} days',
  'leave.amount.accounted': '{hours}h · ×{multiplier}',
  'leave.column.type': 'Type',
  'leave.column.period': 'Period',
  'leave.column.days': 'Days',
  'leave.column.status': 'Status',
  'leave.column.decidedBy': 'Decided by',
  'leave.status.pending': 'Pending',
  'leave.status.approved': 'Approved',
  'leave.status.rejected': 'Rejected',
  'leave.status.revoked': 'Withdrawn',
  'leave.filter.year': 'Quota year',
  'leave.filter.status': 'Status',
  'leave.filter.allStatuses': 'All statuses',
  'leave.action.approve': 'Approve',
  'leave.action.reject': 'Reject',
  'leave.action.revoke': 'Withdraw the request',
  'leave.balance.used': '{used} / {quota} days',
  'leave.balance.unlimited': 'Unlimited',
  'leave.balance.tip.available': 'Still available',
  'leave.balance.tip.pending': 'Awaiting approval',
  'leave.balance.tip.used': 'Already approved',
  'leave.balance.tip.frame': 'Total allowance',
  'leave.balance.tip.days': '{days} days',
  'leave.balance.pending': 'pending: {days} days',
  'leave.balance.carried': 'carried over: {days} days',
  'leave.balance.carriedUntil': 'carried over: {days} days · until {date}',
  'leave.empty.mine': 'You have no requests yet',
  'leave.empty.mineBody': 'Ask for leave here by giving the dates.',
  'leave.empty.queue': 'Nothing to decide',
  'leave.empty.queueBody': 'A request somebody files shows up here.',
  'leave.dialog.new': 'New leave request',
  'leave.dialog.newHint': 'Pick the days – your quota follows the selection.',
  'leave.dialog.submit': 'Submit request',
  'leave.dialog.pickDays': 'Pick days',
  'leave.dialog.nothingPicked': 'nothing selected',
  'leave.dialog.notePlaceholder': 'Anything worth knowing?',
  'leave.dialog.myQuota': 'My quota',
  'leave.dialog.myQuotaIn': 'My quota · {year}',
  'leave.dialog.noFrame': 'No days left to take from this type.',
  'leave.dialog.noQuotaRow': 'This type has no quota in the chosen frame.',
  'leave.dialog.frame': 'Frame',
  'leave.dialog.overdrawn': 'That is more days than your frame has left.',
  'leave.dialog.alreadyBooked': 'You already have a request covering this day.',
  'leave.dialog.remaining': 'Remaining',
  'leave.field.type': 'Type',
  'leave.field.from': 'From',
  'leave.field.to': 'To',
  'leave.field.attachment': 'Attachment',
  'leave.dialog.attachmentRequiredNote': 'This type needs a certificate before it can be approved.',
  'leave.field.note': 'Note',
  'leave.decide.count': '{count} requests in this decision',
  'leave.decided': '{count} requests decided',
  'leave.decidedSome': '{count} decided, {refused} could not be',
  'leave.error.cannot_decide_own': 'You cannot decide your own request – somebody else has to.',
  'leave.error.quota_exceeded':
    'That is more days than the frame has left – see the balance on the right.',
  'leave.error.frame_closed':
    'That frame year no longer covers the days you picked – what carried over has expired.',
  'leave.error.overlaps': 'You already have a request covering those days.',
  'leave.error.not_revocable':
    'This request has already been rejected or withdrawn, so there is nothing to withdraw.',
  'leave.error.already_started':
    'This leave has already started, so it cannot be withdrawn. Ask your manager or HR to correct it.',
  'leave.error.backdate_not_allowed': 'This type cannot be asked for retroactively.',
  'leave.error.leave_not_managed': 'This workspace does not manage your leave.',
  'leave.error.type_inactive': 'That type is not active.',
  'leave.detail.back': 'Back to requests',
  'leave.detail.thread': 'Conversation',
  'leave.detail.noComments': 'No messages yet.',
  'leave.detail.send': 'Send',
  'leave.detail.edited': 'edited',
  'leave.detail.attachments': 'Attachments',
  'file.drop': 'Drop a file here, or click to browse',
  'leave.detail.attachmentRequiredNote': 'This type needs an attachment before it can be approved.',
  'leave.detail.attachmentRequired': 'required',
  'leave.detail.noAttachments': 'Nothing attached.',
  'leave.detail.decision': 'Decision',
  'leave.detail.decidedByName': 'Decided by {name}',
  'leave.detail.details': 'Details',
  'leave.detail.decidedAt': 'Decided',
  'leave.detail.quota': 'Quota',
  'leave.detail.used': 'Used',
  'leave.detail.noWorkingDays': 'No working days in this range.',
  'leave.detail.noTask': 'No task',
  'leave.detail.decisionNote': 'Reason (optional)',
  'leave.settle.label': 'How to approve',
  'leave.settle.payout': 'Pay out',
  'leave.settle.credit': 'Credit to a quota',
  'leave.settle.creditTo': 'Which quota',
  'leave.settle.rate': 'Kind of overtime',
  'leave.settle.rateFromDay': 'What the day implies',
  'leave.settle.payoutHint': 'The hours go into project cost at the multiplier.',
  'leave.settle.credited': '{days} days credited · {type}',
  'leave.balance.accrued': 'from overtime',
  'leave.detail.awaiting': 'Awaiting a decision.',
  'leave.detail.decidedBy': '{status} · {name}',
  'leave.detail.noNote': 'No note.',
  'leave.detail.open': 'Open request',
  'leave.error.attachment_required': 'This type needs an attachment before it can be approved.',
  'title.request': 'Request',
  'title.settingsLeave': 'Leave',
  'settings.leave.tab.types': 'Leave types',
  'settings.leave.tab.quotas': 'Quotas',
  'settings.leave.addType': 'New type',
  'settings.leave.column.name': 'Name',
  'settings.leave.column.code': 'Code',
  'settings.leave.column.quota': 'Quota',
  'settings.leave.column.carry': 'Carry-over',
  'settings.leave.column.rows': 'Quotas',
  'settings.leave.column.person': 'Person',
  'settings.leave.column.type': 'Type',
  'settings.leave.allTypes': 'All types',
  'settings.leave.column.quotas': 'Quotas',
  'settings.leave.column.frame': 'Allowance',
  'settings.leave.column.used': 'Used',
  'settings.leave.column.left': 'Left',
  'settings.leave.searchTypes': 'Search types',
  'settings.leave.emptyTypesBody': 'Add one so people can file a request.',
  'settings.leave.emptyQuotas': 'No quota',
  'settings.leave.emptyQuotasBody':
    'Without an allowance a request can still be filed, but what is left cannot be shown.',
  'settings.leave.qsrc.quota': 'Draws on a quota',
  'settings.leave.qsrc.unlimited': 'Unlimited (no frame)',
  'settings.leave.qsrc.overtime': 'Earned from overtime',
  'settings.leave.qsrc.none': 'Needs no quota',
  'settings.leave.carry.none': 'None',
  'settings.leave.carry.full': 'The whole remainder carries',
  'settings.leave.carry.max5': 'At most 5 days',
  'settings.leave.carryMonth': 'Use it by',
  'settings.leave.carryTo': 'month {month}',
  'settings.leave.note': 'Note',
  'settings.leave.attachment': 'Attachment',
  'settings.leave.rule.optional': 'Optional',
  'settings.leave.rule.required': 'Required',
  'settings.leave.rule.hidden': 'Cannot be asked for',
  'settings.leave.backdate': 'May be filed for the past',
  'settings.leave.codeNote': 'Capitals and digits, for example ANNUAL.',
  'settings.leave.cell': '{name} · {type}',
  'settings.leave.carried': '+{days} carried',
  'settings.leave.used': '{days} used',
  'settings.leave.noQuota': 'No quota set',
  'settings.leave.pending': '{days} pending',
  'settings.leave.deleteType': 'Delete {name}',
  'settings.leave.editType': 'Edit {name}',
  'settings.leave.emptyTypes': 'No leave types yet. Add one.',
  'settings.leave.noQuotaTypes': 'No type draws on a quota, so there is nothing to allocate.',
  'settings.leave.everybodyManaged': 'This workspace manages everybody’s leave.',
  'settings.leave.excluded': 'Not managed here: {names}',
  'settings.leave.dialog.new': 'New leave type',
  'settings.leave.dialog.edit': 'Edit leave type',
  'settings.leave.error.taken': 'That code is already in use.',
  'settings.leave.error.notManaged': 'This workspace does not manage that person’s leave.',
  'settings.leave.typesNote': '{total} types · {active} active · {quota} on a quota',
  'settings.leave.typesHint':
    'The request form offers them in this order. A type somebody is entitled to cannot be deleted – deactivate it, and no new request can pick it.',
  'settings.leave.searchPeople': 'Search by name…',
  'settings.leave.column.total': 'Total',
  'settings.leave.noPeopleMatch': 'Nobody matches that name.',
  'settings.leave.symbol': 'Symbol',
  'settings.leave.namePlaceholder': 'e.g. Annual leave',
  'settings.leave.rulesForward':
    'The rules apply to requests filed from now on – they do not rewrite the ones already in.',
  'people.field.leaveManaged': 'Leave',
  'people.field.leaveManaged.yes': 'Managed here',
  'people.field.leaveManaged.no': 'Not managed here',
  'people.field.rate': 'Rate',
  'people.field.noRate': 'No rate card',
  'people.rate.change': 'Change',
  'people.rate.costHour': 'Hourly · cost rate',
  'people.rate.costDay': 'Daily · cost rate',
  'people.rate.target': 'Target rate',
  'people.rate.validity': 'In force',
  'people.rate.from': 'from {from}',
  'people.rate.pending': 'No period of this rate card has started yet.',
  'people.field.schedule': 'Work schedule',
  'people.field.noSchedule': 'No schedule',
  'people.schedule.change': 'Change',
  'people.field.scheduleFrom': '{hours}h / week · from {from}',
  'title.settingsSchedules': 'Work schedules',
  'settings.schedules.tab.templates': 'Templates',
  'settings.schedules.tab.assignments': 'Assignments',
  'settings.schedules.addTemplate': 'New template',
  'settings.schedules.addAssignment': 'New assignment',
  'settings.schedules.search': 'Search by name…',
  'settings.schedules.type': 'Type',
  'settings.schedules.type.all': 'All types',
  'settings.schedules.type.fix': 'Fixed',
  'settings.schedules.type.flex': 'Flexible',
  'settings.schedules.status': 'Status',
  'settings.schedules.status.all': 'All statuses',
  'settings.schedules.status.active': 'Active',
  'settings.schedules.status.inactive': 'Inactive',
  'settings.schedules.column.name': 'Name',
  'settings.schedules.column.type': 'Type',
  'settings.schedules.column.weekly': 'Weekly',
  'settings.schedules.column.assigned': 'On it',
  'settings.schedules.column.person': 'Person',
  'settings.schedules.column.from': 'From',
  'settings.schedules.column.schedule': 'Schedule',
  'settings.schedules.weekly': 'Weekly frame (hours)',
  'settings.schedules.days': 'Daily hours',
  'settings.schedules.frame': '{hours}h frame',
  'settings.schedules.people': '{count} people',
  'settings.schedules.custom': 'Custom',
  'settings.schedules.flexNote': 'A flexible schedule has no daily plan – only a weekly frame.',
  'settings.schedules.deleteTemplate': 'Delete {name}',
  'settings.schedules.editTemplate': 'Edit {name}',
  'settings.schedules.deleteAssignment': 'Delete',
  'settings.schedules.editAssignment': 'Edit',
  'settings.schedules.emptyTemplates': 'No templates yet. Add one.',
  'settings.schedules.emptyAssignments': 'Nobody is assigned yet.',
  'settings.schedules.dialog.newTemplate': 'New template',
  'settings.schedules.dialog.editTemplate': 'Edit template',
  'settings.schedules.dialog.assign': 'Assignment',
  'settings.schedules.error.inUse': 'The template is still assigned. Deactivate it instead.',
  'settings.schedules.newTemplate': 'Create template',
  'settings.schedules.assign': 'Assign a schedule',
  'settings.schedules.searchTemplates': 'Search template names…',
  'settings.schedules.searchPeople': 'Search by name…',
  'settings.schedules.column.source': 'Source',
  'settings.schedules.column.status': 'Status',
  'settings.schedules.emptyTemplatesBody':
    'Create one and assign people to it — without a capacity, load counts as zero hours.',
  'settings.schedules.note': '{templates} templates · {assignments} assignments',
  'settings.schedules.assignedNote': '{count} people have a schedule · {missing} missing',
  'settings.schedules.hint':
    'A fixed schedule sets daily hours – that is what logged time is measured against on the home screen and on a profile. A flexible one expects nothing on a given day, only a weekly frame.',
  'settings.schedules.duplicate': 'Duplicate',
  'settings.schedules.copyOf': '{name} copy',
  'settings.schedules.assignToPerson': 'Assign to a person',
  'settings.schedules.activate': 'Activate',
  'settings.schedules.deactivate': 'Deactivate',
  'settings.schedules.openProfile': 'Open profile',
  'settings.schedules.fromTemplate': 'Template',
  'settings.schedules.fromTemplateHint': 'Pick one of the workspace’s schedule templates',
  'settings.schedules.customHint': 'A schedule just for this person',
  'settings.schedules.fixHint': 'Exact hours for every day',
  'settings.schedules.flexHint': 'Nothing expected on a given day, only a weekly frame',
  'settings.schedules.templateName': 'Template name',
  'settings.schedules.namePlaceholder': 'e.g. Full-time (40h)',
  'settings.schedules.pickPerson': 'Pick a person',
  'settings.schedules.weekTotal': '{hours}h / week',
  'settings.schedules.dialog.editAssignment': 'Change schedule',
  'title.settingsHolidays': 'Holidays',
  'settings.general.country': 'Country',
  'settings.holidays.year': 'Year',
  'settings.holidays.search': 'Search by name…',
  'settings.holidays.kind': 'Kind',
  'settings.holidays.kind.all': 'All kinds',
  'settings.holidays.kind.public': 'Public holiday',
  'settings.holidays.kind.rest': 'Company day off',
  'settings.holidays.kind.workday': 'Moved working day',
  'settings.holidays.import': 'Synchronise holidays',
  'settings.holidays.importing': 'Synchronising…',
  'settings.holidays.imported': '{imported} official days synchronised · {country} {year}',
  'settings.holidays.add': 'Add a day',
  'settings.holidays.column.date': 'Date',
  'settings.holidays.column.name': 'Name',
  'settings.holidays.column.kind': 'Kind',
  'settings.holidays.column.source': 'Source',
  'settings.holidays.source.nager': 'Synchronised',
  'settings.holidays.source.custom': 'Own',
  'settings.holidays.delete': 'Delete {name}',
  'settings.holidays.empty':
    'Nothing for this year yet. Synchronise the holidays, or add your own.',
  'settings.holidays.emptyBody': 'Sync the official calendar, or add the days by hand.',
  'settings.holidays.counts':
    '{public} public · {rest} company days off · {workday} moved working days',
  'settings.holidays.sourceNote': 'Synchronised calendar · {country}',
  'settings.holidays.hint':
    'On a public holiday or a company day off the daily expectation is zero. A moved working day is the opposite: it counts as a working day even at a weekend.',
  'settings.holidays.dialog.new': 'Add a day',
  'settings.holidays.dialog.edit': 'Edit day',
  'settings.holidays.error.taken': 'That day already has an entry.',
  'settings.holidays.column.day': 'Day',
  'settings.holidays.rowMenu': 'Actions for {name}',
  'settings.holidays.namePlaceholder': 'e.g. Company day off',
  'settings.holidays.hint.public': 'An official holiday – not a working day.',
  'settings.holidays.hint.rest': 'A company day off – not a working day.',
  'settings.holidays.hint.workday': 'A Saturday worked in lieu – a working day, per the schedule.',
  'settings.nav.integrations': 'Integrations',
  'settings.nav.data': 'Data',

  'settings.denied.title': 'You do not have permission',
  'settings.denied.body':
    'Only somebody with the right level can edit the workspace settings. Ask an admin if you need something changed.',

  'settings.unsaved': 'Unsaved changes',
  'settings.saved': 'Everything saved',
  'settings.default': 'default',
  'settings.reorderHint': 'Drag to reorder',
  'settings.makeDefault': 'Make default',
  'settings.taskCount': '{count} tasks',
  'settings.rowMenu': 'Actions for {name}',
  /* ── C54 · skill management ───────────────────────────────────────────────────────────── */
  'settings.nav.skills': 'Skills',
  'title.settingsSkills': 'Skills',
  'settings.skills.title': 'Skills',
  'settings.skills.categories': 'Categories',
  'settings.skills.levels': 'Levels',
  'settings.skills.newSkill': 'New skill',
  'settings.skills.newCategory': 'New category',
  'settings.skills.newLevel': 'New level',
  'settings.skills.editSkill': 'Edit skill',
  'settings.skills.editCategory': 'Edit category',
  'settings.skills.editLevel': 'Edit level',
  'settings.skills.name': 'Name',
  'settings.skills.category': 'Category',
  'settings.skills.color': 'Colour',
  'settings.skills.description': 'Description',
  'settings.skills.skillHint': 'A skill belongs to one category, and takes its colour from it.',
  'settings.skills.categoryHint':
    'A category groups related skills together, and gives them their colour.',
  'settings.skills.levelHint': 'The description says what this level means in practice.',
  'settings.skills.heldBy': '{count} people',
  'settings.skills.heldByNobody': 'not in use',
  'settings.skills.holdsSkills': '{count} skills',
  'settings.skills.empty': 'empty',
  'settings.skills.atLevel': '{count} assignments',
  'settings.skills.deleteHeld': 'Delete: take it off the people first',
  'settings.skills.deleteFull': 'Delete: empty it first',
  'settings.skills.deleteLast': 'Delete: the last one cannot go',
  'settings.skills.search': 'Search skills…',
  'settings.skills.allCategories': 'All categories',
  'settings.skills.noMatch': 'Nothing matches. Try something else, or add a new skill.',
  'settings.skills.noMatchTitle': 'No matches',
  'settings.skills.emptySkills': 'This workspace has no skills yet.',
  'settings.skills.emptySkillsBody': 'Add one so people can be given skills.',
  'settings.skills.emptyCategories': 'No skill categories yet.',
  'settings.skills.emptyCategoriesBody': 'Add one before recording skills.',
  'settings.skills.emptyLevels': 'No skill levels yet.',
  'settings.skills.emptyLevelsBody': 'Add one so people’s skills can carry a level.',
  'settings.skills.hintSkills':
    'This is the list people pick from in their account, and the one you offer during onboarding. Every skill belongs to exactly one category.',
  'settings.skills.hintCategories':
    'A category gives its skills their colour everywhere. Drag the rows to reorder them.',
  'settings.skills.hintLevels':
    'The order is the ranking: the top one is the weakest, the bottom the strongest. Reordering them re-sorts everybody\u2019s skills.',
  'settings.skills.countSkills': '{count} skills',
  'settings.skills.countCategories': '{count} categories',
  'settings.skills.countLevels': '{count} levels',
  /* ⚠️ The three refusals the skill screen can receive, each naming the action that clears it. */
  'settings.error.nameTaken': 'That name is already taken.',
  'settings.error.skillInUse': 'It is still in use — take it off what holds it first.',
  'settings.moveUp': 'Move {name} up',
  'settings.moveDown': 'Move {name} down',
  'settings.toasts': 'Settings notifications',
  'demo.banner.text':
    'This workspace holds demo data so you can try Forecastify out. Anything you add is safe alongside it.',
  'demo.banner.remove': 'Remove demo data',
  'demo.remove.title': 'Remove demo data',
  'demo.remove.lede':
    'The demo clients, projects, tasks, hours and colleagues are deleted. Anything you changed, or that your own data refers to, stays.',
  'demo.remove.removable': 'Demo items to remove: {count}',
  'demo.remove.kept':
    'Items that stay: {count} — you changed them, or your own data refers to them.',
  'demo.remove.final': 'This is final and cannot be undone.',
  'demo.remove.confirm': 'Remove',
  'demo.remove.working': 'Removing…',
  'demo.error.incomplete':
    'Some demo data could not be removed, so nothing was deleted. Try again later.',
  'demo.toast.removed': 'Demo data removed',
  'demo.toast.kept': 'Items that stayed: {count}',
  'demo.toasts': 'Demo data notifications',
  'firstRun.step': 'Step {current} of {total}',
  'firstRun.next': 'Next',
  'firstRun.back': 'Back',
  'firstRun.what.title': 'Welcome to Forecastify',
  'firstRun.what.body':
    'Forecastify keeps your clients, projects, tasks and hours in one place, and shows you from them where you stand and what to expect.',
  'firstRun.flow.title': 'How it fits together',
  'firstRun.flow.body':
    'Clients have projects, projects have tasks, you log hours against tasks, and the reports and dashboards work everything out from there.',
  'firstRun.flow.clients': 'Clients',
  'firstRun.flow.projects': 'Projects',
  'firstRun.flow.tasks': 'Tasks',
  'firstRun.flow.hours': 'Hours',
  'firstRun.flow.reports': 'Reports',
  'firstRun.demo.title': 'You start with demo data',
  'firstRun.demo.body':
    'We made up a few clients, projects and colleagues so there is something to try. When you no longer need them, they can be removed from the bar at the top, and anything that became yours stays.',
  'firstRun.tour.title': 'Shall we show you around?',
  'firstRun.tour.body':
    'A few steps show you where everything is. You can leave at any point, and start it again later from your profile menu.',
  'firstRun.tour.start': 'Show me around',
  'firstRun.tour.skip': 'Not now',
  'firstRun.start.title': 'Ready to start?',
  'firstRun.start.body': 'Everything is in the menu on the left.',
  'firstRun.start.go': 'Get started',
  'nav.takeTour': 'Take the tour',
  'tour.next': 'Next',
  'tour.previous': 'Previous',
  'tour.finish': 'Finish',
  'tour.close': 'Close the tour',
  'tour.step': '{current} / {total}',
  'tour.skipSection': 'Skip this section',
  'tour.busy': 'Getting this step ready…',
  'tour.chapter.demo': 'Demo data',
  'tour.chapter.settings': 'Settings',
  'tour.chapter.home': 'Home',
  'tour.chapter.tasks': 'Tasks',
  'tour.chapter.task': 'A task',
  'tour.chapter.projects': 'Projects',
  'tour.chapter.time': 'Time logging',
  'tour.chapter.requests': 'Requests',
  'tour.chapter.contracts': 'Contracts',
  'tour.chapter.people': 'People',
  'tour.chapter.reports': 'Reports',
  'tour.chapter.dashboards': 'Dashboards',
  'tour.chapter.agent': 'Agent',
  'tour.chapter.end': 'The end',
  'tour.demo.title': 'This is demo data',
  'tour.demo.body':
    'Everything you see — clients, projects, tasks and four made-up colleagues — is sample data, so there is something to click. Remove it from this bar when you are done; anything you changed or added stays.',
  'tour.settingsMenu.title': 'Where the settings are',
  'tour.settingsMenu.body':
    'Settings live behind your workspace’s name. Everything in them applies to the whole workspace.',
  'tour.settingsIdentity.title': 'Name and picture',
  'tour.settingsIdentity.body':
    'The name and picture your team sees in the workspace switcher and the sidebar.',
  'tour.settingsDefaults.title': 'Defaults for new members',
  'tour.settingsDefaults.body':
    'The language and timezone a newly invited colleague starts with. Everybody can change their own afterwards.',
  'tour.settingsCountry.title': 'Country',
  'tour.settingsCountry.body': 'Decides which country’s public holidays the Holidays page imports.',
  'tour.settingsPermissions.title': 'Permission levels',
  'tour.settingsPermissions.body':
    'A permission level is a set of rights. Everyone holds one or more, and when they hold several, the most generous one wins.',
  'tour.settingsLevel.title': 'None, Own or Full',
  'tour.settingsLevel.body':
    'For every action — seeing tasks, logging time, managing projects — a level grants None, Own (only their own rows) or Full (every row in the workspace). Built-in levels are read-only; make your own with New level.',
  'tour.settingsStatuses.title': 'Task statuses',
  'tour.settingsStatuses.body':
    'The columns of your task board, in order. Each belongs to a category — Open, In progress, In review or Closed — and that is what decides when a task counts as done.',
  'tour.settingsPriorities.title': 'Priorities',
  'tour.settingsPriorities.body':
    'The priority scale, and which priority a new task gets by default.',
  'tour.settingsProjectRoles.title': 'Project roles',
  'tour.settingsProjectRoles.body':
    'The hats people wear on a project — Tech Lead, Frontend, QA. They say who does what, not who may do what.',
  'tour.settingsMetafields.title': 'Your own fields',
  'tour.settingsMetafields.body':
    'Add fields to tasks, projects, people, clients, contracts, invoices and more — text, numbers, dates or choices. Mark one “Show in lists” and it becomes a column.',
  'tour.settingsSkills.title': 'Skills',
  'tour.settingsSkills.body':
    'A skills vocabulary — skills, categories and levels — that people are tagged with on their profile.',
  'tour.settingsSchedules.title': 'Work schedules',
  'tour.settingsSchedules.body':
    'How many hours someone is expected to work each day. Fixed schedules set exact hours per day, flexible ones only a weekly total. They drive timesheet warnings and capacity.',
  'tour.settingsAssignments.title': 'Who works on which schedule',
  'tour.settingsAssignments.body': 'Each person’s schedule, and the date it applies from.',
  'tour.settingsRates.title': 'Rate cards',
  'tour.settingsRates.body':
    'What an hour costs you and what you bill for it, per person, in dated periods — so a raise does not rewrite the past.',
  'tour.settingsCurrency.title': 'Currencies',
  'tour.settingsCurrency.body':
    'The currencies money is kept in. One is the default; the others carry an exchange rate to it.',
  'tour.settingsLeaveTypes.title': 'Leave types',
  'tour.settingsLeaveTypes.body':
    'The kinds of leave people can ask for: whether each draws on a yearly quota, whether a note or a certificate is required, and what carries over to next year.',
  'tour.settingsQuotas.title': 'Quotas',
  'tour.settingsQuotas.body':
    'Each person’s allowance per type and year, with what is used and what is left. Type straight into the allowance cell.',
  'tour.settingsOvertime.title': 'Overtime types',
  'tour.settingsOvertime.body':
    'Kinds of overtime and what an hour of each is worth. The default type is preselected when somebody marks overtime.',
  'tour.settingsHolidays.title': 'Holidays',
  'tour.settingsHolidays.body':
    'Imports the official public holidays for your country. Add company days off or moved working days by hand.',
  'tour.settingsWorklog.title': 'Rounding and daily maximum',
  'tour.settingsWorklog.body':
    'How logged time is rounded, and the most anybody can log on one day.',
  'tour.settingsWorklogLock.title': 'Backdating and the monthly lock',
  'tour.settingsWorklogLock.body':
    'How many days back people may log, and the day of the month after which last month is closed.',
  'tour.settingsIntegrations.title': 'Integrations',
  'tour.settingsIntegrations.body':
    'Connect Jira or YouTrack to bring issues and logged time across, both ways.',
  'tour.settingsData.title': 'Your data',
  'tour.settingsData.body':
    'Export projects, tasks, time or people at any time. The workspace can also be deleted here, with a 30-day grace period.',
  'tour.settingsBilling.title': 'Billing',
  'tour.settingsBilling.body':
    'Your plan, the seats in use, and the details your invoices are made out to.',
  'tour.homeAgent.title': 'Ask anything',
  'tour.homeAgent.body':
    'Type your question here — the Agent answers from the workspace’s data, and you can attach a spreadsheet or a screenshot.',
  'tour.homeLog.title': 'Your time',
  'tour.homeLog.body':
    'Today and this week against your plan: what is left, and how many days fell short.',
  'tour.homeLeave.title': 'Your leave',
  'tour.homeLeave.body': 'Your requests waiting for a decision, and leave coming up.',
  'tour.homeAllocations.title': 'What you are planned on',
  'tour.homeAllocations.body':
    'What you are planned on this week, day by day and line item by line item. Hover a row for the details.',
  'tour.homeOvertime.title': 'Your overtime',
  'tour.homeOvertime.body':
    'Your overtime still waiting for a decision, and what was approved in the last month.',
  'tour.tasksSearch.title': 'Search and filter',
  'tour.tasksSearch.body':
    'Search by key or title, and add filters — status, project, assignee, due date, estimate or any of your own fields. Each active filter shows as a chip.',
  'tour.tasksViews.title': 'Four views',
  'tour.tasksViews.body':
    'The same tasks as a List, a Kanban board, a Timeline or an editable Grid.',
  'tour.tasksBoard.title': 'Kanban',
  'tour.tasksBoard.body': 'One column per status. Drag a card to change its status.',
  'tour.tasksTimeline.title': 'Timeline',
  'tour.tasksTimeline.body': 'Tasks with dates, as bars. Drag a bar to reschedule it.',
  'tour.tasksNew.title': 'New task',
  'tour.tasksNew.body': 'Create a task: its project and line item, assignee, estimate and dates.',
  'tour.taskOpen.title': 'A task, open',
  'tour.taskOpen.body':
    'A task opens over the list. Enlarge it to full screen, or shrink it to a side drawer.',
  'tour.taskTitle.title': 'Title and description',
  'tour.taskTitle.body':
    'Click either to edit. Drop a file on the description to attach it and link it in the text.',
  'tour.taskProperties.title': 'Status, priority, assignee',
  'tour.taskProperties.body': 'Pick a value and it saves straight away.',
  'tour.taskLineItem.title': 'Line item',
  'tour.taskLineItem.body':
    'Which line item of which contract this work counts against — that is what makes its hours billable or not.',
  'tour.taskEstimate.title': 'Estimate, dates, tags',
  'tour.taskEstimate.body':
    'Type an estimate as 8, 8h or 8:00, and set the start and the due date.',
  'tour.taskSubtasks.title': 'Subtasks',
  'tour.taskSubtasks.body':
    'Break the work down. A subtask is a task of its own, in the same project.',
  'tour.taskDependencies.title': 'Dependencies and links',
  'tour.taskDependencies.body':
    'What this task waits for or blocks, with a lag in days, and what it relates to.',
  'tour.taskAttachments.title': 'Attachments',
  'tour.taskAttachments.body': 'The task’s files. Drop one anywhere on the task to add it.',
  'tour.taskComments.title': 'Comments',
  'tour.taskComments.body':
    'Discuss the task. Type @ to mention a colleague and they are notified.',
  'tour.taskLogs.title': 'Time on the task',
  'tour.taskLogs.body': 'Everything logged on this task, and a form to log time on it directly.',
  'tour.projectsList.title': 'All projects',
  'tour.projectsList.body':
    'Every project with its client, manager, progress and Trust Score. Saved views narrow it to active ones, yours, or the ones with late tasks.',
  'tour.projectOpen.title': 'One project',
  'tour.projectOpen.body':
    'One project, all of it, in tabs. The header carries its status and, if you manage it, the edit menu.',
  'tour.projectOverview.title': 'Overview',
  'tour.projectOverview.body':
    'Earned value in hours: how much of the frame is used, and at this pace when the work finishes.',
  'tour.projectTasks.title': 'Tasks',
  'tour.projectTasks.body': 'The project’s tasks, with every view and filter of the Tasks page.',
  'tour.projectTime.title': 'Time',
  'tour.projectTime.body': 'Every hour logged on the project, by whom and on what.',
  'tour.projectContracts.title': 'Contracts',
  'tour.projectContracts.body':
    'What you agreed with the client. A contract is made of line items — fixed price, hourly or non-billable — and every task belongs to one.',
  'tour.projectMoney.title': 'Money',
  'tour.projectMoney.body':
    'Budget used, revenue earned and billed, per contract, period or line item.',
  'tour.projectCosts.title': 'External costs',
  'tour.projectCosts.body':
    'Money the project spends that is not time — licences, subcontractors, travel.',
  'tour.projectInvoices.title': 'Invoices',
  'tour.projectInvoices.body': 'What has been billed against the project’s line items and periods.',
  'tour.projectResourcing.title': 'Resourcing',
  'tour.projectResourcing.body':
    'Who is planned on the project, week by week, against their capacity.',
  'tour.projectPeople.title': 'People',
  'tour.projectPeople.body':
    'The project team with their project roles. Only members can log time on its tasks.',
  'tour.projectAlerts.title': 'Alerts',
  'tour.projectAlerts.body':
    'Data problems that make the numbers above untrustworthy — tasks without estimates, time on closed items.',
  'tour.projectEdit.title': 'Editing the project',
  'tour.projectEdit.body': 'Edit the project’s details, its manager and its dates.',
  'tour.timeStrip.title': 'Pick a day',
  'tour.timeStrip.body': 'The bar under each day says whether it is under, on or over plan.',
  'tour.timeDay.title': 'The day',
  'tour.timeDay.body':
    'What is logged on the chosen day. Change hours inline; Undo is offered right after.',
  'tour.timeTask.title': 'Log time: the task',
  'tour.timeTask.body': 'Start by finding the task — by its key or its title.',
  'tour.timeDuration.title': 'Log time: how long',
  'tour.timeDuration.body': 'Type the duration however you like: 8, 8h, 8:00, 8,0 or 90m.',
  'tour.timeOvertime.title': 'Log time: overtime',
  'tour.timeOvertime.body': 'Mark the hours as overtime and a request is raised for approval.',
  'tour.timeLog.title': 'Log time: note, then Log',
  'tour.timeLog.body': 'Say what you worked on, then press Log.',
  'tour.timeRecents.title': 'Recent entries',
  'tour.timeRecents.body':
    'Click a recent line to fill the form with it again — the fastest way to log the same work as yesterday.',
  'tour.timeWeek.title': 'This week',
  'tour.timeWeek.body': 'Your week so far, project by project.',
  'tour.logsList.title': 'Time logs: list',
  'tour.logsList.body':
    'Every entry, searchable and filterable. From a row you can open the task, duplicate the entry to today, or mark it as overtime.',
  'tour.logsTimesheet.title': 'Timesheet',
  'tour.logsTimesheet.body':
    'A week as a grid of tasks and days. Click a cell to see, change or add entries; hover to copy hours across the week.',
  'tour.logsCalendar.title': 'Calendar',
  'tour.logsCalendar.body': 'Your week as a calendar. Drag down an hour column to log that span.',
  'tour.logsGrid.title': 'Grid',
  'tour.logsGrid.body': 'Edit dates, hours and notes like a spreadsheet.',
  'tour.requestBalances.title': 'Your balances',
  'tour.requestBalances.body':
    'For each leave type: what is available, what is pending, and your yearly frame.',
  'tour.requestNew.title': 'Ask for leave',
  'tour.requestNew.body': 'Start a leave request here.',
  'tour.requestCalendar.title': 'Pick the days',
  'tour.requestCalendar.body':
    'Choose the type, then click the first and the last day. Weekends and holidays are not counted.',
  'tour.requestQuota.title': 'Your quota, live',
  'tour.requestQuota.body':
    'Your quota updates as you pick days, so you see what is left before you send.',
  'tour.requestNote.title': 'Note and attachment',
  'tour.requestNote.body': 'Add a note, and a certificate where the type asks for one.',
  'tour.requestOvertime.title': 'Claim overtime',
  'tour.requestOvertime.body':
    'Claim overtime for hours you have already logged: pick the entry and say why. Your approver decides the rate.',
  'tour.requestOpen.title': 'A request, open',
  'tour.requestOpen.body': 'Every request has a conversation, its attachments and its status.',
  'tour.requestDecide.title': 'To decide',
  'tour.requestDecide.body':
    'Requests from the people you look after. Approve or reject one, or select several and decide them together.',
  'tour.contractsList.title': 'Contracts',
  'tour.contractsList.body':
    'Every contract, its project and client, the estimate against tracked hours, and its status.',
  'tour.invoicesList.title': 'Invoices',
  'tour.invoicesList.body':
    'Every invoice and its status — draft, issued, paid or void. Invoices are raised from a line item or a period on the contract.',
  'tour.peopleList.title': 'Your team',
  'tour.peopleList.body':
    'Everybody, with their permission levels and projects. Invite a colleague from here.',
  'tour.personOpen.title': 'A person',
  'tour.personOpen.body':
    'Their month in numbers, their working week and rate, their skills, tasks and time.',
  'tour.reportsList.title': 'Saved reports',
  'tour.reportsList.body': 'Your saved reports. Only you see your own; export any of them as CSV.',
  'tour.reportSource.title': 'Build a report: what to count',
  'tour.reportSource.body':
    'Start with the source — worklogs, tasks, contracts, line items or periods.',
  'tour.reportPeriod.title': 'The period',
  'tour.reportPeriod.body':
    'This week, month, quarter or year, or your own dates. The hint says which date the period filters on.',
  'tour.reportMetric.title': 'The metric',
  'tour.reportMetric.body':
    'What to add up — here, tracked hours. Counts, estimates and overtime are here too, and revenue and budget where you can see money.',
  'tour.reportDimension.title': 'Break it down',
  'tour.reportDimension.body':
    'What to break the figure down by — here, by project. People, clients, statuses or your own fields work too.',
  'tour.reportDisplay.title': 'Display',
  'tour.reportDisplay.body':
    'Pick from 25 charts; the builder suggests better ones for your data. Show or hide the table under the chart.',
  'tour.reportFilters.title': 'Filters',
  'tour.reportFilters.body': 'Narrow the report to a project, a person or a field value.',
  'tour.reportSave.title': 'Name and save',
  'tour.reportSave.body':
    'Name the report here and Save. Refresh re-runs a saved report on today’s data.',
  'tour.dashLogging.title': 'Logging',
  'tour.dashLogging.body':
    'Everybody’s logged hours against their plan, week by week. Filter to the people with a variance.',
  'tour.dashResource.title': 'Resourcing',
  'tour.dashResource.body':
    'Capacity against planned hours for everybody, with under- and over-planning at a glance. Managers edit allocations right in the grid.',
  'tour.dashLeave.title': 'Absence',
  'tour.dashLeave.body': 'Who is away when, on a timeline by week, month or quarter.',
  'tour.agentComposer.title': 'Ask in plain words',
  'tour.agentComposer.body':
    'Ask anything about your work — “how many hours did we log on the webshop last week?”. Every figure comes from a real query.',
  'tour.agentAttach.title': 'Attach a file',
  'tour.agentAttach.body':
    'Drop a spreadsheet or a picture into the conversation and ask about it.',
  'tour.agentProposals.title': 'You confirm every change',
  'tour.agentProposals.body':
    'The Agent never changes anything on its own. Ask it to log time or create a task and it prepares a card: Record it, Correct it, or Discard.',
  'tour.agentHistory.title': 'Earlier conversations',
  'tour.agentHistory.body': 'Pick up an earlier conversation, or start a fresh one.',
  'tour.end.title': 'That is the tour',
  'tour.end.body':
    'Start it again any time from your profile menu — and remove the demo data from the top bar when you are ready.',
  'settings.toast.saved': 'Settings saved',

  'settings.general.identity': 'Workspace',
  'settings.general.logoDeferred':
    'Icon upload is not built yet – it needs object storage. Until then the initial from the name is shown.',
  'settings.general.name': 'Name',
  'settings.general.slug': 'Web address',
  'settings.general.defaults': 'Defaults for new members',
  'settings.general.language': 'Interface language',
  'settings.general.timezone': 'Timezone',
  'settings.general.notifications': 'Notifications',
  'settings.general.notificationEmails': 'Send notification emails',
  'settings.general.notificationEmailsLede':
    'When off, this workspace sends nobody a notification email. In-app notifications, invitations and account security emails still go out.',

  /* ⚠️ R-38 · the slug is the ADDRESS now — every screen's path begins with it. The old wording
     named exports and invitations, which is true but no longer the consequence that matters:
     renaming it retires every link anybody already has. */
  'settings.slug.idle': 'This is the workspace’s address: every page’s path begins with it.',
  'settings.slug.checking': 'Checking…',
  'settings.slug.taken': 'That address is taken. Pick another.',
  /* ⚠️ 0098 · “taken” is a lie about a reserved word: nobody took it and nobody can have it. */
  'settings.slug.reserved': 'That address is used by the application. Pick another.',
  'settings.slug.invalid':
    'Lower-case letters, digits and hyphens only, at least three characters, and it cannot start or end with a hyphen.',
  'settings.slug.free': 'Free: {slug}',

  'settings.statuses.title': 'Task statuses',
  'settings.statuses.new': 'New status',
  'settings.priorities.title': 'Priorities',
  'settings.priorities.new': 'New priority',
  'settings.statuses.column.name': 'Name',
  'settings.statuses.column.category': 'Category',
  'settings.statuses.allCategories': 'All categories',
  'settings.statuses.search': 'Search statuses',
  'settings.statuses.empty': 'No task status',
  'settings.statuses.emptyBody': 'Add one — without a status, tasks never reach the board.',
  'settings.priorities.search': 'Search priorities',
  'settings.priorities.empty': 'No priority',
  'settings.priorities.emptyBody': 'Add one so tasks can be ranked.',

  'settings.field.name': 'Name',
  'settings.field.color': 'Colour',
  'settings.field.category': 'Category',

  'settings.delete.rowTitle': 'Delete {name}',
  'settings.delete.rowBody':
    'It holds {count} tasks. They need somewhere to go – no task is deleted.',
  'settings.delete.reassign': 'Move the tasks to',

  'settings.worklog.taskOnly':
    'Time can only be logged against a task. This cannot be turned off – the reports depend on it.',
  'settings.worklog.title': 'Logging',
  'settings.worklog.unit': 'Rounding',
  'settings.worklog.unitNote15': 'Every entry rounds to the nearest 15 minutes.',
  'settings.worklog.unitNote30': 'Every entry rounds to the nearest 30 minutes.',
  'settings.worklog.unitNoteDecimal': 'Time is stored in decimal hours, unrounded.',
  'settings.worklog.maxPerDay': 'Maximum per day',
  'settings.worklog.hoursPerDay': 'hours / day / person',
  'settings.worklog.maxNote': 'Nobody can log more than this on one day.',
  'settings.worklog.maxInvalid': 'It has to be between 0 and 24 hours.',
  'settings.worklog.backdate': 'Backdated logging',
  'settings.worklog.daysBack': 'days back',
  'settings.worklog.noLimit': 'No limit',
  'settings.worklog.backdateNote': 'Anything older needs the right permission level.',
  'settings.worklog.backdateFreeNote': 'Any day, however old, can be logged.',
  'settings.worklog.backdateInvalid': 'At least one day.',
  'settings.worklog.lockDay': 'Lock day',
  'settings.worklog.lockDayAfter': 'closes the previous month',
  'settings.worklog.noLock': 'No lock',
  'settings.worklog.dayOfMonth': 'day {day}',
  'settings.worklog.lockWarning':
    'After day {day} of the month nobody can log against the previous month. Anybody who missed it has to ask an admin.',

  'settings.integrations.title': 'Integrations · coming soon',
  'settings.integrations.body':
    'Workspace-level connections land here: API keys, webhooks, calendar and invoicing sync. The content is built in the next phase.',

  'settings.export.title': 'Export the workspace data',
  'settings.export.lede': 'A zip is built from the scope you pick, and we email a download link.',
  'settings.export.deferred':
    'Export is built in the next phase, on one shared path – the same one that serves the lists. Until then there is nothing here to start.',

  'settings.delete.title': 'Delete the workspace',
  'settings.delete.note':
    'The {name} workspace, {projects} projects, {tasks} tasks and every worklog are queued for deletion. After a 30-day grace period the data cannot be recovered.',
  'settings.delete.button': 'Delete the workspace',
  'settings.delete.dialogBody':
    'This REQUESTS a deletion rather than performing one: there is a 30-day grace period, and it can be withdrawn at any point.',
  'settings.delete.confirmLabel': 'Type the workspace name: {name}',
  'settings.delete.confirm': 'Request deletion',
  'settings.delete.pending': 'Deletion requested',
  'settings.delete.pendingBy': 'Requested by {name} on {at}. Can be withdrawn.',
  'settings.delete.pendingAt': 'Requested on {at}. Can be withdrawn.',
  'settings.delete.cancel': 'Withdraw the request',
  'settings.delete.now': 'Delete now',
  'settings.delete.nowBody':
    'The {name} workspace is deleted now, without waiting out the grace period: {projects} projects, {tasks} tasks, every hour, request and setting. This cannot be undone.',
  'settings.delete.nowConfirm': 'Delete for good',

  /* ⚠️ 0098 · “in this tenant” is gone: uniqueness is global, so the holder may be in a tenant
     this person cannot see. */
  'settings.error.slugTaken': 'That address is already taken.',
  'settings.error.slugReserved': 'That address is used by the application. Pick another.',
  'settings.error.confirmMismatch': 'The typed name does not match the workspace.',
  'settings.error.deletionState': 'The deletion state changed. Reload and try again.',
  /* ⚠️ Shared by C8's statuses and C54's skill categories and levels, so it names no
     feature — each screen's own disabled menu item says what to do about ITS list. */
  'settings.error.lastOfItsKind': 'The last one cannot be deleted.',
  'settings.error.staleOrder': 'The order changed while you were dragging. Reload and try again.',

  'settings.toast.deletionRequested': 'Deletion requested',
  'settings.toast.deletionGrace': '30-day grace period – it can be withdrawn at any point.',
  'settings.toast.deletionCancelled': 'The deletion request was withdrawn',

  'common.discard': 'Discard',
  'common.saving': 'Saving…',

  'nav.reports': 'Reports',
  'title.reports': 'Reports',
  'title.myReports': 'My reports',
  'title.report': 'Report',
  'title.reportNew': 'New report',

  'report.action.new': 'New report',
  'report.action.open': 'Open',
  'report.action.pin': 'Pin to the sidebar',
  'report.action.unpin': 'Unpin from the sidebar',
  'report.action.duplicate': 'Duplicate',
  'report.action.delete': 'Delete',
  'report.action.refresh': 'Refresh',
  'report.action.back': 'Back to reports',
  'report.actions': 'Report actions',
  'report.newName': 'New report',
  'report.copyName': '{name} (copy)',
  'report.field.name': 'Report name',
  'report.field.from': 'Start date',
  'report.field.to': 'End date',
  'report.controls': 'Controls',
  'report.table': 'Table',
  /* 16 Oct 2026 · the list layout and Kockázatkezelés. */
  'report.layout.summary': 'Summary',
  'report.layout.list': 'List',
  /* 17 Oct 2026 · the timeline layout, Munkaidő and the ready-made reports. */
  'report.source.workday': 'Working time',
  'report.source.allocation': 'Resources',
  'report.metric.expected': 'Expected hours',
  'report.metric.missing': 'Missing hours',
  'report.metric.leave': 'Leave hours',
  'report.metric.missingCost': 'Cost of missing hours',
  'report.dim.task': 'Task',
  'report.group.noLineItem': 'No line item',
  'report.group.noContract': 'No contract',
  'report.group.noClient': 'No client',
  'report.error.periodTooLong':
    'That period is too long to draw over time — pick a shorter one or wider columns.',
  'report.period.last3Months': 'Last 3 months',
  'report.period.last6Months': 'Last 6 months',
  'report.period.last12Months': 'Last 12 months',
  'report.period.byWorkday': 'by finished working days, up to yesterday',
  'report.period.byAllocationDay': 'by planned days',
  'report.filter.softBookings': 'Soft booking',
  'report.filter.softInclude': 'Include',
  'report.filter.softExclude': 'Exclude',
  'report.layout.timeline': 'Over time',
  'report.rail.rows': 'Rows',
  'report.rail.rowFirst': 'First row',
  'report.rail.rowSecond': 'Second row',
  'report.rail.rowNone': '— no second row —',
  'report.rail.granularity': 'Columns',
  'report.granularity.week': 'Week',
  'report.granularity.month': 'Month',
  'report.granularity.quarter': 'Quarter',
  'report.rail.extras': 'Extra columns',
  'report.rail.addExtra': 'Add a column',
  'report.rail.noExtras': 'No extra columns.',
  'report.rail.removeExtra': 'Remove {label}',
  'report.extra.countTask': 'Number of tasks',
  'report.extra.countAssignee': 'Number of people',
  'report.extra.countLineitem': 'Number of line items',
  'report.extra.countProject': 'Number of projects',
  'report.extra.listTask': 'Tasks',
  'report.extra.listAssignee': 'Logged by',
  'report.extra.listLineitem': 'Line items',
  'report.extra.listProject': 'Projects',
  'report.timeline.person': 'Person',
  'report.timeline.code': 'Code',
  'report.timeline.total': 'Total',
  'report.timeline.lapsed': 'lapsed',
  'report.timeline.lapsedTip':
    'This period can no longer be logged — the monthly lock or the backdating limit has closed it.',
  'report.timeline.week': 'Week {week} · {day}',
  'report.timeline.quarter': 'Q{quarter} {year}',
  'report.action.fromTemplate': 'From a template',
  'report.template.unknownWorklogs': 'Unknown worklogs',
  'report.template.unknownTasks': 'Unknown tasks',
  'report.template.loggingGaps': 'Logging gaps',
  'report.template.lineItemLogs': 'Hours by line item',
  'report.template.unknownByPerson': 'Unknown worklogs – by person',
  'report.template.unknownByTask': 'Unknown worklogs – by task',
  'report.layout.label': 'Report layout',
  'report.error.unknownColumn': 'That column is not available for this report.',
  'report.error.badFilter': 'One of the filter values is not valid.',
  'report.list.empty': 'No rows match the filters',
  'report.list.emptyBody': 'Widen the period or remove a filter.',
  'report.vocab.lineItemOpen': 'Open',
  'report.vocab.lineItemClosed': 'Closed',
  'report.filter.projects': 'Projects',
  'report.filter.clients': 'Clients',
  'report.filter.loggedBy': 'Logged by',
  'report.filter.assignee': 'Assignee',
  'report.filter.owner': 'Contract owner',
  'report.filter.contracts': 'Contracts',
  'report.filter.lineItems': 'Line items',
  'report.filter.priorities': 'Priority',
  'report.filter.statuses': 'Status',
  'report.col.workedOn': 'Date',
  'report.col.hours': 'Hours',
  'report.col.member': 'Logged by',
  'report.col.team': 'Team',
  'report.col.note': 'Note',
  'report.col.enteredAt': 'Entered',
  'report.col.overtimeType': 'Overtime type',
  'report.col.cost': 'Cost',
  'report.col.targetValue': 'Billable value',
  'report.col.taskKey': 'Task',
  'report.col.taskTitle': 'Task title',
  'report.col.taskDescription': 'Description',
  'report.col.taskStatus': 'Status',
  'report.col.taskPriority': 'Priority',
  'report.col.taskAssignee': 'Assignee',
  'report.col.taskEstimate': 'Estimate',
  'report.col.taskStartsOn': 'Start',
  'report.col.taskDueOn': 'Due',
  'report.col.taskComponents': 'Components',
  'report.col.taskFixVersions': 'Fix versions',
  'report.col.taskEpic': 'Epic',
  'report.col.taskLogged': 'Logged',
  'report.col.taskRemaining': 'Remaining',
  'report.col.taskCreatedAt': 'Created',
  'report.col.taskUpdatedAt': 'Updated',
  'report.col.project': 'Project',
  'report.col.client': 'Client',
  'report.col.contract': 'Contract',
  'report.col.lineItem': 'Line item',
  'report.col.contractCode': 'Code',
  'report.col.contractName': 'Contract name',
  'report.col.contractType': 'Type',
  'report.col.contractStatus': 'Status',
  'report.col.owner': 'Owner',
  'report.col.startsOn': 'Start',
  'report.col.endsOn': 'End',
  'report.col.lineItemCount': 'Line items',
  'report.col.estimate': 'Estimate',
  'report.col.budget': 'Budget',
  'report.col.revenue': 'Revenue',
  'report.col.invoiced': 'Invoiced',
  'report.col.lineItemNumber': 'No.',
  'report.col.lineItemType': 'Type',
  'report.col.lineItemScope': 'Billing',
  'report.col.lineItemStatus': 'Status',
  'report.col.closedAt': 'Closed',
  'report.col.syncTarget': 'Sync target',
  'report.col.rate': 'Hourly rate',
  'report.col.periodFrom': 'Period start',
  'report.col.periodTo': 'Period end',
  'report.total': 'total',
  'report.toasts': 'Report notifications',

  'report.column.name': 'Report',
  'report.column.source': 'Source',
  'report.column.dimension': 'Dimension',
  'report.column.metric': 'Metric',
  'report.column.period': 'Period',
  'report.column.filters': 'Filters',
  'report.column.hours': 'Tracked hours',
  'report.column.entries': 'Entries',
  'report.column.tasks': 'Tasks',
  'report.column.estimate': 'Estimate',
  'report.column.share': 'Share',

  'report.view.all': 'All reports',
  'report.view.worklog': 'Hour reports',
  'report.view.task': 'Task reports',

  'report.source.worklog': 'Worklogs',
  'report.source.task': 'Tasks',
  'report.source.contract': 'Contracts',
  'report.source.lineitem': 'Line items',
  'report.source.occurrence': 'Periods',
  'report.dim.role': 'Project role',
  'report.dim.contract': 'Contract',
  'report.dim.lineitem': 'Line item',
  'report.dim.scope': 'Scope (retainer / over-frame)',
  'report.dim.occurrence': 'Period (recurrence)',
  'report.dim.risk': 'Trust Score band',
  'report.group.unscanned': 'Not yet scanned',
  'report.metric.revenue': 'Revenue',
  'report.metric.budget': 'Budget',
  'report.money.locked': 'locked',
  'report.money.lockedTitle': 'A money report needs permission',
  'report.money.lockedNote':
    'The cost, revenue, budget and margin measures need the “Money reports” permission. It is not switched on for your level – ask your admin.',

  'report.dim.project': 'Project',
  'report.dim.client': 'Client',
  'report.dim.assignee': 'Assignee',
  'report.dim.status': 'Status',
  'report.dim.metafield': 'Metafield',

  'report.metric.hours': 'Sum of tracked hours',
  'report.metric.count': 'Count',
  'report.metric.estimate': 'Sum of estimates',
  'report.metric.overtime': 'Overtime (approved)',
  'report.metric.overtimeCost': 'Overtime with multiplier',

  'report.chart.barShort': 'Bars',
  'report.chart.groupFeatured': 'Featured',
  'report.chart.suggested': 'This would read better as',
  'report.reco.single': 'a single value – it reads as a figure',
  'report.reco.ordered': 'the periods have an order, so a trend means something',
  'report.reco.rhythm': 'hours by day – a calendar shows the rhythm',
  'report.reco.many': '{count} categories – the names fit horizontally',
  'report.reco.few': '{count} categories – a clean proportion',
  'report.reco.dominant': 'one category is most of it',
  'report.reco.months': 'a month-by-month matrix',
  'report.reco.companion': 'the measure and its companion side by side',
  'report.chart.groupBasic': 'Basic',
  'report.chart.treemap': 'Treemap',
  'report.chart.waterfall': 'Waterfall',
  'report.chart.calendar': 'Calendar heatmap',
  'report.chart.sunburst': 'Sunburst',
  'report.chart.bubble': 'Bubble',
  'report.chart.scatter': 'Scatter',
  'report.chart.radar': 'Radar',
  'report.chart.barline': 'Bar and line',
  'report.chart.line': 'Line',
  'report.chart.area': 'Area',
  'report.chart.vbar': 'Simple vertical',
  'report.chart.vstack': 'Stacked vertical',
  'report.chart.vgroup': 'Grouped vertical',
  'report.chart.hbar': 'Simple horizontal',
  'report.chart.hstack': 'Stacked horizontal',
  'report.chart.hgroup': 'Grouped horizontal',
  'report.chart.listmetric': 'List by measure',
  'report.chart.listdim': 'List by dimension',
  'report.chart.metric': 'Big number',
  'report.chart.tableChart': 'Table only',
  'report.chart.gauge': 'Gauge',
  'report.chart.donut': 'Donut',
  'report.chart.breakdown': 'Breakdown bar',
  'report.chart.histogram': 'Histogram (frequency)',
  'report.chart.heatmap': 'Heatmap',
  'report.chart.needsCompanion':
    'Needs a measure with something to compare against – hours pair with the estimate, money with the budget. A count has no pair.',
  'report.chart.needsDays':
    'Needs a per-day series – this report has no date to place the days on.',
  'report.chart.needsMatrix':
    'Needs a month axis – this report has no date to bucket the months by.',
  'report.chart.acrossGroups': 'across {count} groups',
  'report.chart.leaderShare': 'The largest group: {name}',
  'report.chart.histogramNote': 'How many GROUPS fall in each band – not how many records.',
  'report.chart.calendarNote': '{days} days',
  'report.chart.tableOnly': 'This view shows the table alone – turn it on under Display.',
  'report.chart.pieShort': 'Pie',
  'report.chart.bars': 'Bar chart',
  'report.chart.pie': 'Pie chart',

  'report.period.week': 'This week',
  'report.period.month': 'This month',
  'report.period.quarter': 'This quarter',
  'report.period.year': 'This year',
  'report.period.all': 'All time',
  'report.period.custom': 'Custom',
  'report.period.none': 'no date limit',
  'report.period.since': 'since {date}',
  'report.period.until': 'until {date}',
  'report.period.byWorkedOn': 'by the day it was logged',
  'report.period.byDueOn': 'by due date – a task with no due date is left out',

  'report.filter.project': 'Project',
  'report.filter.member': 'Assignee / logger',
  'report.filter.metafield': 'Metafield',
  'report.filter.metafieldValue': 'Metafield value',
  'report.filters.none': 'none',

  'report.rail.metrics': 'Metrics',
  'report.rail.dimensions': 'Dimensions',
  'report.rail.display': 'Display',
  'report.rail.filters': 'Filters',
  'report.rail.addMetric': 'Add a metric',
  'report.rail.pickDimension': 'Pick a dimension',
  'report.rail.addFilter': 'Add a filter',
  'report.rail.noMetric': 'No metric selected.',
  'report.rail.noFilters': 'No filters: everything is included.',
  'report.rail.removeFilter': 'Remove the {label} filter',

  'report.noMetric.title': 'Start with a metric',
  'report.noMetric.body':
    'Pick a metric in the Metrics section on the right – the dimension and the filters come after it.',
  'report.empty.title': 'No data',
  'report.empty.body': 'Loosen the filters – nothing falls into this combination.',
  'report.emptyList.title': 'No saved reports',
  'report.emptyList.body': 'Build one and save it with its filters.',

  'report.truncated': 'The chart shows the {shown} largest groups; there are {total} in all.',
  'report.trimmedNote':
    'The report runs on the permission-filtered query – its figures match the totals on the lists.',
  'report.ranAt': 'ran at {at}',
  'report.rowCount': '{count} rows',

  'report.toast.saved': 'Report saved',
  'report.toast.renamed': 'Report renamed',
  'report.toast.refreshed': 'Report re-run',
  'report.toast.duplicated': 'Report duplicated',
  'report.toast.deleted': 'Report deleted',

  'report.error.inverted': 'The end date is before the start.',
  'report.error.notGroupable': 'That kind of metafield cannot group or filter a report.',
  'report.error.unknownMetafield': 'That metafield does not exist in this workspace.',
  'report.error.unknownFilter': 'The filter names something that does not exist here.',

  'report.group.noProject': 'No project',
  'report.group.internal': 'Internal',
  'report.group.unassigned': 'Unassigned',
  'tasks.unassign': 'Clear assignee',
  'tasks.toast.bulkEdited': '{count} tasks updated',
  'tasks.toast.bulkDeleted': '{count} tasks deleted',
  /* ⚠️ **Reports what landed, not what was selected.** The server answers per row, and
     `state-rules.md` §5 forbids calling a partial result a whole one. */
  'bulk.someRefused': 'Not all {total} went through',
  'people.team.clear': 'Clear team',
  'people.field.timezoneSearch': 'Search time zones…',
  'people.invitationSub': 'invitation sent',
  'people.toast.bulkEdited': '{count} people updated',
  'people.toast.bulkDeleted': '{count} people deactivated',
  'clients.toast.bulkArchived': '{count} clients archived',
  'clients.toast.created': 'Client {name} created',
  'clients.toast.updated': '{name} saved',
  'projects.toast.bulkArchived': '{count} projects archived',
  'report.group.noValue': 'No value',

  'people.action.back': 'Back to people',
  'people.action.onboardStart': 'Start onboarding',
  'people.action.onboardReopen': 'Reopen onboarding',
  'onboarding.title': 'Onboarding {name}',
  'onboarding.subtitle':
    'Ten steps. Nothing is saved until the last one – close this now and nothing changes.',
  'onboarding.steps': 'Steps',
  'onboarding.step.basic': 'Basics',
  'onboarding.step.org': 'Manager',
  'onboarding.step.perm': 'Permissions',
  'onboarding.step.env': 'Work environment',
  'onboarding.step.time': 'Time tracking',
  'onboarding.step.sched': 'Work schedule',
  'onboarding.step.rate': 'Rate card',
  /* ── C54 · the Skills step ────────────────────────────────────────────────────────────── */
  'onboarding.step.skill': 'Skills',
  'onboarding.skill.picked': 'Skills added',
  'onboarding.skill.empty': 'Nothing added yet. Click one from the list above.',
  'onboarding.skill.noneLeft':
    'No skills left to pick. As an admin you can add more under Settings › Skills.',
  'onboarding.skill.noVocabulary':
    'This workspace has no skills yet. They can be added under Settings › Skills.',
  'onboarding.skill.levelOf': 'Level in {name}',
  'onboarding.skill.remove': 'Remove {name}',
  'onboarding.step.leave': 'Leave',
  'onboarding.step.meta': 'Metafields',
  'onboarding.step.done': 'Summary',

  'onboarding.permHint':
    'More than one level can be given: effective permissions are the union, most permissive wins.',
  'onboarding.noRoleWarning':
    'With no level they cannot open anything. This is allowed – it can be set later.',
  'onboarding.timeHint': 'If they do not log time, they are left out of the hour reports.',
  'onboarding.noMetafields': 'This workspace defines no person metafields.',

  /* ── C64 · the work schedule step ────────────────────────────────────────── */
  'onboarding.sched.source': 'Schedule source',
  'onboarding.sched.fromTemplate': 'From a template',
  'onboarding.sched.fromCustom': 'Their own',
  'onboarding.sched.from': 'In force from',
  'onboarding.sched.template': 'Template',
  'onboarding.sched.pickTemplate': 'Choose a template',
  'onboarding.sched.pickNote': 'Choose a template – the daily plan comes from it.',
  'onboarding.sched.templateNote': '{type} · {hours} / week',
  'onboarding.sched.type': 'Type',
  'onboarding.sched.fix': 'Fixed',
  'onboarding.sched.flex': 'Flexible',
  'onboarding.sched.weekly': 'Weekly hours',
  'onboarding.sched.days': 'Hours per day',
  'onboarding.sched.fixTotal': '{hours} / week',
  'onboarding.sched.flexTotal': '{hours} / week – flexible, no daily breakdown',
  'onboarding.sched.notNeeded':
    'They do not track time, so they get no schedule – there would be nothing to measure their days against. The time tracking step is where that is changed.',
  'onboarding.sched.notNeededShort': 'not needed',
  'onboarding.sched.skipped': 'skipped',
  'onboarding.sched.locked': 'Schedules need permission',
  'onboarding.sched.lockedHint':
    'A work schedule belongs to the workspace settings. The rest of the onboarding works – an administrator can set the schedule afterwards.',

  /* ── C64 · the leave step ────────────────────────────────────────────────── */
  'onboarding.leave.managed': 'Leave managed here',
  'onboarding.leave.managedHint.on': 'They get a quota and can file leave requests.',
  'onboarding.leave.managedHint.off': 'No quota, no requests.',
  'onboarding.leave.quotas': 'Quotas · {year}',
  'onboarding.leave.days': 'days',
  'onboarding.leave.dayCount': '{count} days',
  'onboarding.leave.noTypes':
    'This workspace defines no quota-based leave types. They can be added under Settings › Leave.',
  'onboarding.leave.noQuotas': 'no quota set',
  'onboarding.leave.notManaged': 'not managed here',
  'onboarding.leave.locked': 'Quotas need permission',
  'onboarding.leave.lockedHint':
    'A quota belongs to the workspace settings. The leave switch above still works.',

  'onboarding.rate.skip': 'Skip the rate card',
  'onboarding.rate.skipHint.on':
    'Skipped – they will have no rate. It can be added later under Settings › Rate cards.',
  'onboarding.rate.skipHint.off': 'What they cost – from a rate card, or a rate of their own.',
  'onboarding.rate.source': 'Rate source',
  'onboarding.rate.fromTemplate': 'From a rate card',
  'onboarding.rate.fromCustom': 'Their own rate',
  'onboarding.rate.from': 'In force from',
  'onboarding.rate.template': 'Rate card',
  'onboarding.rate.templateSearch': 'Search rate cards…',
  'onboarding.rate.pickTemplate': 'Choose a rate card',
  'onboarding.rate.pickNote': 'Choose a card – they always get whichever period is in force.',
  'onboarding.rate.noPeriod': 'This card has no period yet.',
  'onboarding.rate.periodNote': '{unit} · {cost} cost / {target} target',
  'onboarding.rate.unit': 'Unit',
  'onboarding.rate.currency': 'Currency',
  'onboarding.rate.cost': 'Cost rate',
  'onboarding.rate.target': 'Target rate',
  'onboarding.rate.skipped': 'skipped',
  'onboarding.rate.locked': 'Rates need permission',
  'onboarding.rate.lockedHint':
    'Pricing somebody belongs to the workspace settings. The rest of the onboarding works – an administrator can add the rate afterwards.',

  'onboarding.back': 'Back',
  'onboarding.next': 'Next',
  'onboarding.finish': 'Finish onboarding',
  'onboarding.done': 'Onboarding finished',
  'onboarding.doneNote': '{name} has all their details.',
  'onboarding.error.ownRoles': 'Somebody else has to change your own permissions.',
  'onboarding.error.unknownRole': 'That permission level does not exist in this workspace.',
  'onboarding.error.unknownTemplate': 'That rate card does not exist in this workspace.',
  'onboarding.error.unknownCurrency': 'That currency is not set up in this workspace.',
  'onboarding.error.unknownSchedule': 'That work schedule does not exist in this workspace.',
  'onboarding.error.unknownLeaveType': 'That leave type does not exist in this workspace.',
  'people.onboarded': 'Onboarded',
  'people.notOnboarded': 'Not onboarded',

  'people.toast.saved': 'Saved',
  'people.toast.deactivated': 'Deactivated',
  'people.toast.activated': 'Activated',
  'people.toast.deleted': 'Deleted, the name is kept',

  /* ══ C13 ══════════════════════════════════════════════════════════════════════════════════ */

  'time.error.dayFull': 'That would take the day to {total} hours, and the daily maximum is {max}.',
  'time.error.dayFullShort': 'That day would go over the daily maximum.',
  'time.error.tooFarBack': 'You cannot log further back than {earliest}.',
  'time.error.tooFarBackShort': 'That day is outside the backdating window.',
  'time.error.monthClosed': '{day} falls in a closed month – it cannot be changed or added to.',
  'time.error.monthClosedShort': 'That month is closed.',

  /* ══ C12 ══════════════════════════════════════════════════════════════════════════════════ */

  'nav.logs': 'Time logs',
  'title.logs': 'Time logs',
  'logs.section.overview': 'Overview',
  'logs.section.logs': 'Time logs',
  'logs.section.switch': 'Time view',
  'logs.section.overviewNote': 'Today, the week’s hours and the project split.',
  'logs.section.logsNote': 'Every entry as a list, a timesheet, a calendar or a grid.',

  'logs.view.list': 'List',
  'logs.view.timesheet': 'Timesheet',
  'logs.week.previous': 'Previous week',
  'logs.week.next': 'Next week',
  'logs.week.thisWeek': 'This week',
  'logs.view.calendar': 'Calendar',
  'logs.view.grid': 'Grid',

  'logs.search': 'Search notes and tasks…',
  'logs.range.all': 'All entries',
  'logs.range.week': 'This week',
  'logs.range.month': 'This month',

  'logs.empty': 'Nothing logged in this view',
  'logs.emptyNote': 'Change the period, or add an entry from the Overview.',
  'logs.footer': 'Showing {count} entries',

  'logs.row.openTask': 'Open task',
  'logs.row.duplicate': 'Duplicate to today',
  'logs.toast.logged': '{hours} logged',
  'logs.toast.duplicated': 'Entry duplicated',
  'logs.toast.deletedMany': '{count} entries deleted',
  'logs.toast.deletedSome': 'Out of the {total} selected. The rest were refused.',

  'logs.timesheet.table': 'Weekly timesheet',
  'logs.timesheet.cellEntries': '{task} · {day}: entries',
  'logs.timesheet.total': 'Total',
  'logs.timesheet.daily': 'Daily total',
  'logs.timesheet.addTaskSearch': 'Search tasks…',
  'logs.timesheet.addTask': 'Add a task',
  'logs.timesheet.taskClosed': 'This task is closed, so it can’t be picked.',
  'logs.timesheet.cell': '{task}, {day}',
  'logs.cell.overtime': 'Overtime',
  'time.error.overtimeApproved':
    'This hour belongs to an approved overtime claim – it can no longer be changed or deleted.',
  'logs.cell.add': 'Add an entry',
  'logs.cell.total': 'Total',
  'logs.timesheet.removeAlloc': 'Delete {label}',
  'logs.timesheet.removeRow': 'Remove the row for {task}',
  'logs.timesheet.empty': 'Nothing to log against this week. Add a task.',
  'logs.timesheet.copyOnwards': 'Copy onwards',
  'logs.timesheet.clearRange': 'Clear these days',

  'logs.grid.table': 'Time log grid',

  'logs.calendar.add': 'Log time · {day}',

  'logs.entry.create': 'Log time',
  'logs.entry.edit': 'Edit entry',
  'logs.entry.hint': 'The start and the duration are what place the block in the calendar.',
  'logs.entry.willSave': '{hours} will be logged.',
  'logs.entry.badDuration': 'Give the duration, for example 2:30 or 2.5.',

  /* ══ C11 ══════════════════════════════════════════════════════════════════════════════════ */

  'nav.time': 'Time',
  'title.time': 'Time',
  'time.section.group': 'Time tracking',
  'time.section.overview': 'Overview',
  'time.section.overviewDesc': 'Today, the weekly capacity and the project breakdown.',
  'time.section.logs': 'Time logs',
  'time.section.logsDesc': 'Every entry as a list, a timesheet, a calendar or a grid.',
  'time.toasts': 'Notifications',

  'time.dow.mon': 'Mon',
  'time.dow.tue': 'Tue',
  'time.dow.wed': 'Wed',
  'time.dow.thu': 'Thu',
  'time.dow.fri': 'Fri',
  'time.dow.sat': 'Sat',
  'time.dow.sun': 'Sun',

  'time.week.range': 'Period',
  'time.week.title': 'This week',
  /* 16 Oct 2026 · the reworked Időrögzítés: the period card, and the week's allocations. */
  'time.period.title': 'Time logs',
  'time.period.week': '{from} – {to} · week {week}',
  'time.period.weekPicker': 'Week {week} · {from} – {to}',
  'time.period.span': 'View',
  'time.period.previous': 'Earlier period',
  'time.period.next': 'Later period',
  'time.period.today': 'Today',
  'time.period.pick': 'Choose a period',
  'time.span.week': 'Weekly',
  'time.span.month': 'Monthly',
  'time.week.summary': 'The week · {from} – {to}',
  'time.week.capacity': '{allocated} / {capacity} capacity',
  'time.week.noAllocation': 'Nothing is planned for you this week.',
  'time.week.overtime': '{name} ×{multiplier} · billed {billed}',

  'time.noProject': 'No project',

  'time.day.empty': 'Nothing logged for this day. Fill it in before the week closes.',
  'time.day.nothingExpected': 'Nothing to log for this day – {reason}.',
  'time.day.plan': 'Plan {hours}',
  'time.day.logged': 'logged {hours}',
  'time.closed.month': 'This month is closed – nothing can be logged or changed in it.',
  'time.closed.backdate': 'Time can be logged up to {days} days back, and this day is older.',
  'time.entry.hours': 'Logged time',
  'time.entry.remove': 'Remove',
  'time.hours.help': 'Accepts 8, 8h, 8:00 or 8,0 – shown as H:MM.',

  'time.log.task': 'Task',
  'time.log.search': 'Search by key or title – e.g. HSO-1 or Proof',
  'time.log.noResults': 'No matches – try the key.',
  'time.log.otherTask': 'Another task',
  'time.log.hours': 'Hours',
  'time.log.startedAt': 'Start',
  'time.log.duration': 'Duration',
  'time.log.note': 'Note',
  'time.log.notePlaceholder': 'What did you work on?',
  'time.log.submit': 'Log',

  'time.recent.title': 'Recent entries',
  'time.recent.hint': 'Click to log it again',
  'time.recent.dismiss': 'Hide {task} from the suggestions',
  'time.recent.empty': 'No earlier entries yet.',

  'time.toast.logged': 'Time logged',
  'time.toast.loggedOvertime': 'Time logged · overtime request raised',
  'time.toast.changed': 'Entry updated',
  'time.toast.deleted': 'Entry deleted',
  'time.undo': 'Undo',
  'time.undo.failed': 'The undo did not go through',

  'time.error.notAMember': 'You are not a member of this project. Ask to be added.',
  'time.error.notLogging': '{name} does not log time, so no time can be logged for them.',
  'time.error.outsideWindow':
    'You were not on this project on that day. Pick another day, or ask for your membership to be extended.',
  'time.error.notPermitted': 'You cannot log time for somebody else.',
  'time.error.hours': 'It has to be greater than 0. Accepts 8, 8h, 8:00 or 8,0.',

  /* ══ C10 ══════════════════════════════════════════════════════════════════════════════════ */

  'nav.metafields': 'Metafields',
  'title.metafields': 'Metafields · Settings',

  'common.yes': 'Yes',
  'common.no': 'No',

  'metafields.heading': 'Metafields',
  'metafields.entities': 'Entities',
  'metafields.create': 'New {entity} metafield',
  'metafields.duplicate': 'Duplicate',
  'metafields.delete': 'Delete',
  'metafields.required': 'required',
  'metafields.column.name': 'Name',
  'metafields.column.type': 'Type',
  'metafields.allTypes': 'All types',
  'metafields.search': 'Search fields',
  'metafields.emptyBody': 'Add one so it appears on the form.',
  'metafields.inList': 'in lists',
  'metafields.none': 'none',
  'metafields.someone': 'chosen user',
  'metafields.empty': 'No metafields on {entity} yet. Add one.',
  'metafields.removeOption': 'Remove value: {value}',
  'metafields.addOption': 'Add',

  'metafields.entity.task': 'Task',
  'metafields.entity.person': 'Person',
  'metafields.entity.project': 'Project',
  'metafields.entity.client': 'Client',
  'metafields.entity.worklog': 'Worklog',
  'metafields.entity.contract': 'Contract',
  'metafields.entity.externalCost': 'External cost',
  'metafields.entity.invoice': 'Invoice',
  'metafields.entity.lineitem': 'Line item',

  'metafields.type.text': 'Short text',
  'metafields.type.textarea': 'Long text',
  'metafields.type.number': 'Number',
  'metafields.type.date': 'Date',
  'metafields.type.select': 'Choice',
  'metafields.type.multi': 'Multiple choice',
  'metafields.type.checkbox': 'Checkbox',
  'metafields.type.user': 'User',
  'metafields.type.url': 'Link',

  'metafields.dialog.new': 'New {entity} metafield',
  'metafields.dialog.edit': 'Edit metafield',
  'metafields.field.name': 'Name',
  'metafields.field.key': 'Key',
  'metafields.field.keyWarning':
    'Changing the key breaks the integrations and exports that use it.',
  'metafields.field.entity': 'Entity',
  'metafields.field.type': 'Type',
  'metafields.field.typeWarning':
    'Changing the type discards the values already filled in – {count} of them.',
  'metafields.field.help': 'Help',
  'metafields.field.required': 'Required',
  'metafields.field.inList': 'Show in lists',
  'metafields.field.options': 'Available values',
  'metafields.field.optionPlaceholder': 'new value',

  'metafields.delete.title': 'Delete metafield',
  'metafields.delete.unused': '“{name}” will be deleted. Nothing has a value for it.',
  'metafields.delete.used':
    '“{name}” has a value on {count} records. Deleting it removes those too, and the key ({key}) drops out of the exports.',

  'metafields.error.name': 'Give the field a name.',
  'metafields.error.key':
    'A key starts with a lower-case letter and holds only lower-case letters, digits and underscores.',
  'metafields.error.duplicate': 'That key is already in use: {key}',
  'metafields.error.noOptions': 'Give at least one available value.',
  'metafields.error.optionExists': 'That value is already on the list.',
  'metafields.error.required': 'Required.',
  'metafields.error.number': 'That is not a number. A decimal comma works too.',
  'metafields.error.date': 'That is not a real day.',
  'metafields.error.url': 'Only http:// or https:// links are allowed.',
  'metafields.error.option': 'That value is not on the list: {value}',
  'metafields.error.shape': 'That value does not fit the field’s type.',

  'task.properties': 'Properties',
  'task.metafields': 'Metafields',
  'task.attachments': 'Attachments',
  'task.attachments.empty': 'Nothing attached to this task yet.',
  'task.metafields.empty': 'No metafields on tasks yet. Add one under Settings → Metafields.',

  'settings.integrations.installed': 'Installed extensions',
  'settings.integrations.installedLede':
    'These run in this workspace. Instances and credentials arrive in the next round.',
  'settings.integrations.installedEmpty':
    'No extensions installed yet. See what is available under Extensions.',
  'settings.integrations.browse': 'Browse the extensions',
  'settings.integrations.pending': 'Configuration coming soon',

  'ext.jira.name': 'Jira',
  'ext.jira.tagline': 'Issue and worklog syncing with Jira Cloud instances.',
  'ext.jira.about':
    'The extension ties Jira spaces to Forecastify projects: issues become tasks, and hours logged in Forecastify go up onto the issue. A workspace can run several instances – yours and the client’s Jira separately. Contracts and line items do not exist in Jira, and do not need to: they are ours.',
  'ext.jira.can.1':
    'Issue to task and task to issue – each side keeps its own key, and we keep the pairing.',
  'ext.jira.can.2':
    'Worklogs both ways: the H:MM logged here goes up onto the issue, and remote entries come back onto the task.',
  'ext.jira.can.3':
    'Status groups: several Jira statuses can point at one Forecastify status, and you say which one we write back.',
  'ext.jira.can.4':
    'What we have no column for – issue type, reporter, story points, components – can be mapped into a metafield.',
  'ext.jira.how.1':
    'After installing, add an instance under workspace settings → Integrations: a URL and an admin API token.',
  'ext.jira.how.2':
    'The wizard walks through space, status, people and field mapping, and shows how many spaces the token can actually see.',
  'ext.jira.how.3':
    'Changes arrive by webhook in seconds rather than minutes, and a full reconciliation runs once a day.',
  'ext.jira.how.4':
    'Your own hours go up under your own name, so you connect your Jira account once under account settings.',
  'ext.jira.cred':
    'The workspace connection is in place. Connect your Jira account so your hours go up under your name – until you do, your worklogs wait.',

  'ext.youtrack.name': 'YouTrack',
  'ext.youtrack.tagline': 'Task and time-entry syncing with YouTrack Cloud or on-prem instances.',
  'ext.youtrack.about':
    'Ties YouTrack projects to Forecastify projects, narrowed by your own query. YouTrack time entries join the task’s worklogs. Contracts and line items do not exist here either, and do not need to.',
  'ext.youtrack.can.1': 'Query-driven import: only what matches the filter counts.',
  'ext.youtrack.can.2':
    'Time entries both ways – and the author stays the right person, with no per-user key.',
  'ext.youtrack.can.3':
    'Status groups: several YouTrack states can point at one Forecastify status, with a nominated default.',
  'ext.youtrack.can.4':
    'What we have no column for – type, subsystem, versions, estimation – can be mapped into a metafield.',
  'ext.youtrack.how.1':
    'After installing, give the instance URL, an admin token and the query under Integrations.',
  'ext.youtrack.how.2':
    'You link the projects by hand; whatever is not linked does not come across, and the run log says why.',
  'ext.youtrack.how.3':
    'YouTrack has no webhooks, so we ask on a schedule – the instance page shows when it last ran.',
  'ext.youtrack.cred':
    'On YouTrack the workspace credential can log hours in your name too, so there is usually nothing for you to do here.',

  'title.integrationProvider': 'Instances · Settings',
  'title.integrationInstance': 'Instance · Settings',
  'integrations.heading': 'Integrations',
  'integrations.lede':
    'Connect the workspace to your development systems. One system can have several instances – yours and the client’s separately.',
  'integrations.notInstalled': 'Off',
  'integrations.notFound': 'There is no such extension.',
  'integrations.enable': 'Turn on for this workspace',
  'integrations.toast.enabled': 'Turned on',
  'integrations.toast.enabledBody': 'You can add an instance now.',
  'integrations.toast.disabled': 'Turned off',
  'integrations.inUse': 'Delete its instances first: {names}',
  'integrations.noInstances': 'Not connected',
  'integrations.instanceCount': '{count} instances',
  'integrations.instanceCountBad': '{count} instances · {bad} failing',
  'integrations.manage': 'Manage',
  'integrations.connect': 'Connect',
  'integrations.new': 'New instance',
  'integrations.empty':
    'No instance connected yet. A workspace can have several – yours and the client’s system separately.',
  'integrations.col.instance': 'Instance',
  'integrations.col.state': 'State',
  'integrations.col.last': 'Last sync',
  'integrations.col.schedule': 'Schedule',
  'integrations.never': '–',
  'integrations.state.setup': 'Being set up',
  'integrations.state.active': 'Active',
  'integrations.state.paused': 'Paused',
  'integrations.state.error': 'Failing',
  'integrations.active': 'Active',
  'integrations.syncRecent': 'Sync recent',
  'integrations.sync.group': 'Synchronise',
  'integrations.syncWindow.open': 'Sync a chosen period…',
  'integrations.syncWindow.title': 'Sync a chosen period',
  'integrations.syncWindow.description':
    'Tasks and worklogs are read by the time they changed in the tracker: a June hour edited in September is a September change.',
  'integrations.syncWindow.floor': 'The connection starts on {date} – nothing earlier is read.',
  'integrations.syncWindow.confirm': 'Synchronise',
  'integrations.syncWindow.preset.week': 'Past week',
  'integrations.syncWindow.preset.month': 'Past month',
  'integrations.syncWindow.preset.quarter': 'Past 3 months',
  'integrations.syncWindow.preset.sinceStart': 'Since the start date',
  'integrations.actions': 'Actions',
  'integrations.open': 'Open',
  'integrations.pause': 'Pause',
  'integrations.resume': 'Resume',
  'integrations.reopenWizard': 'Reopen the wizard',
  'integrations.delete': 'Delete the instance',
  'integrations.deleteTitle': 'Delete the instance',
  'integrations.deleteBody':
    'The connection, both maps and the sync log are deleted. The tasks and hours already synchronised stay in the workspace, but stop being updated.',
  'integrations.dangerTitle': 'Delete the instance',

  'integrations.tab.links': 'Spaces & projects',
  'integrations.links.heading.space': 'Jira spaces',
  'integrations.links.heading.project': 'Remote projects',
  'integrations.links.never.space':
    'We have not looked at what this connection can see yet. Run a discovery.',
  'integrations.links.never.project':
    'We have not looked at what this connection can see yet. Run a discovery.',
  'integrations.links.discoveredAt': 'Last discovered {at}',
  'integrations.links.discover': 'Discover',
  'integrations.links.discovered': 'Discovery finished',
  'integrations.links.failed': 'That did not work',
  'integrations.links.archived': 'archived',
  'integrations.links.invisible': 'the connection can no longer see it',
  /* ── C39 · 0074 · what Jira says the project is. Only `active` syncs. ── */
  'integrations.links.remote.archived': 'Archived in Jira',
  'integrations.links.remote.trashed': 'In Jira’s trash',
  'integrations.links.remote.deleted': 'Deleted in Jira',
  'integrations.links.writes': 'Writing allowed',
  'integrations.links.manual': 'manual filing',
  'integrations.links.noneTargeted': 'No line item collects from this connection yet.',
  'integrations.links.untargeted': 'No line item collects it',
  'integrations.links.allTargeted':
    'Every project this connection can see has a line item collecting from it.',
  'integrations.links.whereToTarget':
    'A line item collects from a tracker project through the Sync section on its own page. Nothing arrives from a project no line item collects.',
  'integrations.links.note':
    'Several line items may collect from one remote project, even on different Forecastify projects. An issue no line item’s rule claims arrives as an unrouted task, and raises an alert on every project collecting from that remote project.',
  /* ── C36 · the parked-hours inbox. See the Hungarian block for why the two causes differ. ── */
  /* ⚠️ See the Hungarian block: our key and the tracker's render identically and mean different
     work, so the task screen has to name both. */
  'task.field.remoteIssue': 'Linked issue',
  'task.remoteIssue.pending': 'no key yet',
  'task.field.key': 'Key',
  'task.key.pending': 'provisional · awaiting the tracker’s key',
  'integrations.tab.parked': 'Parked hours',
  'integrations.parked.heading': 'Parked hours',
  'integrations.parked.none': 'Every hour that arrived has been filed.',
  'integrations.parked.emptyBody': 'Nothing is waiting.',
  'integrations.parked.byAuthor': 'Whose hours',
  /* J-3 · synced tasks no line item's rule claims. */
  'integrations.tab.unrouted': 'Unrouted tasks',
  'integrations.unrouted.heading': 'Unrouted tasks',
  'integrations.unrouted.none': 'Every synced task is on a line item.',
  'integrations.unrouted.waiting': '{count} synced tasks no line item’s rule claims',
  'integrations.unrouted.emptyBody': 'Nothing is unrouted.',
  'integrations.unrouted.explain':
    'These tasks exist and can take hours, but count towards no contract. Give a line item a rule that catches them (Contracts), or open a task and pick its line item by hand.',
  'integrations.unrouted.toContracts': 'Contracts',
  'integrations.unrouted.col.project': 'Jira project',
  'integrations.unrouted.col.issue': 'Issue',
  'integrations.unrouted.col.title': 'Task',
  'integrations.unrouted.col.components': 'Components',
  'integrations.unrouted.col.status': 'Jira status',
  /* ⚠️ Count-neutral, like `tasks.timeline.conflicts`: the catalogue has no plural rules by
     design, and *"1 entries waiting"* is what the naive phrasing renders on the commonest day. */
  'integrations.parked.waiting': 'Waiting: {count}, {hours} in total',
  'integrations.parked.reason.noTask': 'unknown task',
  /* 17 Oct 2026 · the parked table's columns, search and filters. */
  'integrations.parked.col.issue': 'Issue',
  'integrations.parked.col.author': 'Logged by',
  'integrations.parked.col.reason': 'Reason',
  'integrations.parked.col.note': 'Note',
  'integrations.parked.discardSelected': 'Discard selected',
  'integrations.parked.noMatch': 'The filters left no hours.',
  'integrations.parked.search': 'Search issue, author, note…',
  'integrations.parked.reason.noMember': 'unknown person',
  'integrations.parked.fix.noTask': 'Set up line items',
  'integrations.parked.fix.noMember': 'Pair people',
  'integrations.parked.authorUnknown': 'no author given',
  'integrations.parked.discarded': 'Discarded',
  'integrations.parked.ran': 'Reconciled',
  'integrations.parked.failed': 'Failed',
  'integrations.parked.tooSoon': 'This integration has already reconciled today.',
  /* ── C39 · live updates. Not drawn by the mockup — its "webhook" is an outbound one on the
     settings screen, deferred there. ── */
  'integrations.webhook.title': 'Live updates',
  'integrations.webhook.live': 'On',
  'integrations.webhook.moved': 'Pointing elsewhere',
  'integrations.webhook.off': 'Off',
  'integrations.webhook.liveNote':
    'Jira tells us the moment something changes. The daily reconciliation still runs – that is the guarantee.',
  'integrations.webhook.offNote':
    'Changes arrive with the daily reconciliation only, and an issue deleted in Jira is never noticed.',
  'integrations.webhook.movedNote':
    'Jira is sending updates to an address this deployment no longer answers on. Nothing arrives, and nothing reports it. Reconnecting fixes it.',
  'integrations.webhook.address': 'Address',
  'integrations.webhook.registeredAt': 'Turned on',
  'integrations.webhook.lastDelivery': 'Last heard from',
  'integrations.webhook.never': 'Nothing yet',
  'integrations.webhook.rejected':
    'Rejected deliveries: {count}. Either the secret was rotated, or somebody is probing.',
  'integrations.webhook.turnOn': 'Turn on live updates',
  'integrations.webhook.repair': 'Reconnect',
  'integrations.webhook.turnOff': 'Turn off',
  'integrations.webhook.registered': 'Live updates are on.',
  'integrations.webhook.disabled': 'Live updates are off.',
  'integrations.webhook.failed': 'Live updates could not be set up.',
  'integrations.webhook.stale': 'Filter out of date',
  'integrations.webhook.staleNote':
    'Jira sends nothing for projects linked since – the subscription still names the old list. Reconnecting refreshes it.',
  'integrations.webhook.drift':
    'The last reconciliation found {count} changes nobody announced. If that stays high, deliveries are not arriving.',
  'integrations.webhook.driftSince': 'Since {at}.',
  'integrations.webhook.byHand': 'Set up by hand',
  'integrations.webhook.byHandNote':
    'This subscription was added by hand in Jira, so it cannot be changed from here. The project filter has to be updated there too.',
  'integrations.webhook.manualTitle': 'Paste this into Jira’s settings',
  'integrations.webhook.manualNote':
    'Jira → Settings → System → WebHooks → Create a WebHook. The secret is shown once, now.',
  'integrations.webhook.manualUrl': 'URL',
  'integrations.webhook.manualSecret': 'Secret',
  'integrations.webhook.manualEvents': 'Events',
  'integrations.webhook.manualFilter': 'Filter (JQL)',
  'integrations.webhook.manualDone': 'Done',
  'integrations.webhook.manualWarn':
    '⚠️ The secret is only visible now. Close this and it has to be generated again – and pasted into Jira again.',
  'integrations.tab.overview': 'Overview',
  'integrations.tab.runs': 'Syncs',
  'integrations.tab.settings': 'Settings',

  'integrations.stat.last': 'Last sync',
  'integrations.stat.lastNever': 'has not run yet',
  'integrations.stat.schedule': 'Schedule',
  'integrations.stat.paused': 'paused',
  /* ⚠️ P2-24: not an interval but a time of day — the full reconciliation runs once a day. */
  'integrations.overwatchAt': 'Daily at {at}',
  'integrations.stat.scheduleOff': 'the schedule is stopped',
  'integrations.stat.in': 'Came in',
  'integrations.stat.inNote': '{count} went out',
  'integrations.stat.errors': 'Failing runs',
  'integrations.stat.errorsNote': 'look at the log',
  'integrations.stat.errorsNone': 'no failures',

  'integrations.connection': 'Connection',
  'integrations.conn.url': 'Address',
  'integrations.conn.auth': 'Authentication',
  'integrations.conn.account': 'Account',
  'integrations.conn.token': 'Token',
  'integrations.conn.oauth': 'OAuth 2.0 (3LO)',
  'integrations.conn.test': 'Test connection',
  'integrations.conn.testing': 'Testing…',
  'integrations.conn.testHint': 'We check the credential works before anything is saved.',
  'integrations.conn.testOk': 'Working · {count} projects visible',
  'integrations.conn.testAs': 'Authenticated as {name}',
  'integrations.conn.fieldsPartial':
    'The field list is partial: the test read only a few projects, the full list arrives after saving.',
  'integrations.fields.partial':
    'The list is partial: not every Jira project fit in the time budget. Opening the step again may read the rest.',
  'integrations.error.untested':
    'Test the connection first. A well-formed address and token prove nothing on their own.',
  'integrations.conn.tokenKind': 'API token',
  'integrations.conn.oauthAccount': 'The connection is live',
  'integrations.conn.noToken': 'not needed',

  'integrations.whatSyncs': 'What it syncs',
  'integrations.conflict': 'On a clash: {winner}',
  'integrations.conflict.remote': 'the remote system wins',
  'integrations.conflict.local': 'Forecastify wins',
  'integrations.map.statuses': 'Statuses',
  'integrations.map.priorities': 'Priorities',
  'integrations.map.prioritiesWarn': 'An unmapped priority keeps Forecastify’s own.',
  'integrations.map.openPeople': 'People',
  'integrations.map.people': 'People',
  'integrations.map.count': '{mapped} / {total} mapped',
  'integrations.map.warn': 'Whatever is unmapped is left out of the sync.',
  'integrations.map.ok': 'Everything is connected.',
  'integrations.map.none': 'nothing unmapped',
  'integrations.map.projects': 'Projects',
  'integrations.map.projectsOk': 'That many projects sync through this instance.',
  'integrations.map.noProjects': 'No project is linked – so nothing syncs at all.',

  'integrations.rule.taskOut': 'Forecastify task → issue',
  'integrations.rule.taskOut.desc': 'A task created here goes out to the remote system.',
  'integrations.rule.statusBoth': 'Status changes both ways',
  'integrations.rule.statusBoth.desc':
    'Each side follows the other – on a clash the conflict rule decides.',

  'integrations.runs.col.at': 'When',
  'integrations.runs.col.trigger': 'Started',
  'integrations.runs.col.result': 'Result',
  'integrations.runs.col.in': 'In',
  'integrations.runs.col.out': 'Out',
  'integrations.runs.empty':
    'No sync has run yet. Start one by hand, or wait for the scheduled one.',
  'integrations.runs.all': 'All runs',
  'integrations.run.state.running': 'Running',
  'integrations.run.state.ok': 'Succeeded',
  'integrations.run.state.partial': 'Partial',
  'integrations.run.state.error': 'Failed',
  'integrations.run.trigger.scheduled': 'scheduled',
  'integrations.run.trigger.manual': 'manual',
  'integrations.run.trigger.webhook': 'webhook',
  'integrations.run.trigger.retry': 'automatic',
  'integrations.run.trigger.backfill': 'backfill',
  'integrations.run.trigger.target': 'project',
  'integrations.run.elapsed': 'running for {minutes} min',
  'integrations.run.inBreakdown': '{tasks} tasks · {worklogs} worklogs',
  'integrations.run.untilNow': 'until now',
  'integrations.run.window': 'Window',
  'integrations.runs.filter.label': 'Filter runs',
  'integrations.runs.filter.people': 'Started and scheduled',
  'integrations.runs.filter.webhook': 'Webhook deliveries',
  'integrations.runs.filter.all': 'All runs',
  'integrations.runs.total': '{total} runs',
  'integrations.run.onTask': 'Task:',
  'integrations.run.inProject': 'Project:',
  'integrations.run.noPayload': 'Nothing was stored for this line.',
  'integrations.run.errorCount': '{count} problems',
  'integrations.run.noteCount': '{count} notes',
  'integrations.run.in': 'Came in',
  'integrations.run.out': 'Went out',
  'integrations.run.tasks': 'tasks',
  'integrations.run.worklogs': 'worklogs',
  'integrations.run.errors': 'Problems',
  'integrations.run.noRecords':
    'This run carried no records at all. The problems are listed above.',
  'integrations.run.col.entity': 'Entity',
  'integrations.run.col.key': 'Key',
  'integrations.run.col.dir': 'Way',
  'integrations.run.col.action': 'Action',
  'integrations.run.col.result': 'Result',
  'integrations.run.dir.in': 'In',
  'integrations.run.dir.out': 'Out',
  'integrations.run.entity.task': 'Task',
  'integrations.run.entity.worklog': 'Worklog',
  'integrations.run.entity.comment': 'Comment',
  'integrations.run.action.created': 'created',
  'integrations.run.action.updated': 'updated',
  'integrations.run.action.skipped': 'skipped',
  'integrations.run.result.ok': 'ok',
  'integrations.run.result.skip': 'skipped',
  'integrations.run.result.error': 'failed',
  'integrations.note.stub': 'Dry run – this extension does not talk to the remote system yet.',
  'integrations.note.worklog_in': 'hour brought in',
  'integrations.note.worklog_deleted': 'deleted in the remote system',
  'integrations.note.worklog_unparked': 'deleted before it could be filed',
  'integrations.note.parked_no_task': 'parked: unknown task',
  'integrations.note.parked_no_member': 'parked: unknown person',
  'integrations.error.unmapped_status': 'No mapped status: {detail}',
  'integrations.error.unmapped_user': 'No mapped person: {detail}',
  'integrations.error.no_rules_enabled':
    'Not a single rule is switched on, so the run carried nothing.',

  'integrations.wizard.newTitle': 'New instance',
  'integrations.wizard.editTitle': 'Configure the instance',
  'integrations.wizard.desc':
    'Eight steps: connection, tasks, statuses, priorities, people, fields, rules, review.',
  'integrations.wizard.singleDesc': 'Only this step changes; the rest stays as it is.',
  'integrations.wizard.next': 'Next',
  'integrations.wizard.back': 'Back',
  'integrations.wizard.finish': 'Connect',
  'integrations.wizard.saveEdit': 'Save',
  'integrations.wizard.steps': 'Steps',
  'integrations.step.fields': 'Fields',
  'integrations.fields.loading': 'Loading fields from the remote system…',
  'integrations.fields.summary': 'Extra Jira fields mapped into metafields. Read when you open it.',
  'integrations.fields.saveFirst':
    'Save the connection first. Field maps belong to the instance, so we can only store them once it exists – reopen the wizard and they will be here.',
  'integrations.fields.lede':
    'What Jira stores and we do not – issue type, reporter, story points – can be mapped into a metafield. Whatever is left blank does not come across.',
  'integrations.fields.none': 'does not sync',
  'integrations.fields.create': '+ New metafield',
  'integrations.fields.newName': 'New metafield name',
  'integrations.fields.newType': 'Type',
  'integrations.fields.newOptions': 'Options',
  'integrations.fields.newOptionsHint': 'Comma separated',
  'integrations.fields.newOptionsRequired': 'A choice field needs at least one option.',
  'integrations.fields.requiredNo': 'required, so it cannot receive',
  'integrations.fields.empty': 'No field here can be mapped.',
  'integrations.fields.others': '{count} more fields we do not offer',
  'integrations.fields.claimed': 'we have our own column',
  'integrations.fields.unsupported': 'we cannot store it',
  'integrations.fields.note':
    'Reading only: the Jira value comes here. Deleting the metafield stops the sync, and that is not a fault.',
  'integrations.step.connection': 'Connection',
  'integrations.step.tasks': 'Tasks',
  'integrations.step.statuses': 'Statuses',
  'integrations.step.priorities': 'Priorities',
  'integrations.step.people': 'People',
  'integrations.people.lede':
    'Pair the remote accounts with your colleagues. Hours from an unpaired account have nowhere to go.',
  'integrations.people.paired': '{paired} of {count} accounts paired.',
  'integrations.people.unpaired': '{count} accounts are still unpaired.',
  'integrations.people.nobody': 'not paired',
  'integrations.people.pairWith': 'Pair {name}',
  'integrations.people.none': 'No remote account has come across on this instance yet.',
  'integrations.people.preview':
    'The connection sees {count} active accounts – pairing becomes available once it is saved.',
  'integrations.people.saveFirst':
    'Save the connection first. Accounts belong to the instance, so we can only store them once it exists – reopen the wizard and they will be here.',
  'integrations.step.rules': 'Rules',
  'integrations.step.review': 'Review',

  'integrations.field.name': 'Instance name',
  'integrations.field.namePlaceholder': 'e.g. LogiNet Jira (prod)',
  'integrations.field.url': 'Address',
  'integrations.field.auth': 'Authentication',
  'integrations.field.email': 'Account email',
  'integrations.field.token': 'API token',
  'integrations.field.tokenKept':
    'The stored token stays until you type a new one. It cannot be read back – only overwritten.',
  'integrations.field.query': 'Filter',
  'integrations.field.since': 'Start date',
  'integrations.field.sinceHint': 'Issues and worklogs older than this are not carried across.',
  'integrations.field.overwatch': 'Daily reconciliation at',
  'integrations.field.conflict': 'Which side wins on a clash',
  'integrations.oauthNote':
    'The browser sends you to Atlassian, where you approve the access. No token reaches the workspace; the connection reads as the account that approved it.',
  'integrations.oauth.saveFirst': 'Save the connection first, then approve it at Atlassian.',
  'integrations.oauth.connect': 'Approve at Atlassian',
  'integrations.oauth.connecting': 'Redirecting…',
  'integrations.oauth.reconnect': 'Approve again',
  'integrations.oauth.connected': 'Connected as {account}',
  'integrations.oauth.webhookNote':
    'An OAuth connection cannot register the admin webhook: changes arrive by polling and the daily reconciliation, or a Jira admin sets the webhook up by hand.',

  'integrations.statuses.lede':
    'Tie the remote statuses to the workspace’s own. Issues sitting in an unmapped status are left out of the sync.',
  'integrations.statuses.none': 'does not sync',
  /* ── C39 · a remote status is picked from the list now, not typed. ── */
  'integrations.statuses.pick': 'Choose a status…',
  'integrations.statuses.emptyKnown':
    'Nothing mapped yet. Jira’s own statuses can be brought in with one click.',
  'integrations.statuses.addAll': 'Bring in Jira’s statuses ({count})',
  'integrations.statuses.cat.new': 'open',
  'integrations.statuses.cat.doing': 'in progress',
  'integrations.statuses.cat.done': 'closed',
  'integrations.statuses.cat.unknown': 'other',
  'integrations.statuses.note': '{count} unmapped statuses',
  'integrations.statuses.default': 'Default',
  'integrations.statuses.defaultHint':
    'Several remote statuses can point at one Forecastify status. This is the one we write out when the status changes here.',
  'integrations.statuses.add': 'Add a status',
  'integrations.statuses.remote': 'Remote status name',
  'integrations.statuses.empty': 'No status added yet. Add the ones the remote system uses.',

  /* ── J-5 · the priority map, the statuses’ twin. ── */
  'integrations.priorities.lede':
    'Tie the remote priorities to the workspace’s own. Issues with an unmapped priority still arrive, with Forecastify’s own priority.',
  'integrations.priorities.none': 'does not drive ours',
  'integrations.priorities.pick': 'Choose a priority…',
  'integrations.priorities.emptyKnown':
    'Nothing mapped yet. The remote system’s priorities can be brought in with one click.',
  'integrations.priorities.addAll': 'Bring in the remote priorities ({count})',
  'integrations.priorities.note': '{count} unmapped priorities',
  'integrations.priorities.default': 'Default',
  'integrations.priorities.defaultHint':
    'Several remote priorities can point at one Forecastify priority. This is the one we write out when the priority changes here.',
  'integrations.priorities.add': 'Add a priority',
  'integrations.priorities.remote': 'Remote priority name',
  'integrations.priorities.empty': 'No priority added yet. Add the ones the remote system uses.',

  'integrations.review.name': 'Instance',
  'integrations.review.url': 'Address',
  'integrations.review.auth': 'Authentication',
  'integrations.review.query': 'Filter',
  'integrations.review.queryEmpty': 'none · every issue',
  'integrations.review.statuses': 'Statuses',
  'integrations.review.priorities': 'Priorities',
  'integrations.review.people': 'People',
  'integrations.review.rules': 'Rules',
  'integrations.review.rulesEmpty': 'no rule is switched on',
  'integrations.review.schedule': 'Schedule',
  'integrations.review.note':
    'The first run loads from the start date. On a large project that can take a few minutes – follow it on the Syncs tab.',

  'integrations.error.name': 'Give the instance a name – it is what tells several apart.',
  'integrations.error.url': 'The address has to be a full URL, with https.',
  'integrations.error.email': 'A Jira API token also needs the account’s email address.',
  'integrations.error.token': 'Type the token in.',
  'integrations.error.duplicate': 'An instance with that name already exists.',
  'integrations.run.discoveredProjects': '{detail} projects visible to this connection.',
  'integrations.run.discoveredPeople': '{detail} users in the remote system.',
  'integrations.run.projectUnchanged': '{detail} issues unchanged – nothing to bring across.',
  'integrations.run.awaitingReread': '{detail} tasks waiting for the next full sync.',
  'integrations.run.webhookIgnored': 'Event ignored: {detail}',
  'integrations.run.webhookDuplicate': 'Repeat delivery: {detail}',
  'integrations.error.stale':
    'Somebody else changed this instance while it was open. Reloaded – check it over and save again.',
  'integrations.error.statusDefault':
    'More than one Jira status points at the same Forecastify status. Mark one of them the default – that is the one we write to Jira when the status changes here.',
  'integrations.error.priorityDefault':
    'More than one remote priority points at the same Forecastify priority. Mark one of them the default – that is the one we write out when the priority changes here.',
  'integrations.error.validation':
    'These settings cannot be saved as they are. Look over the marked rows.',
  'integrations.toast.created': 'Integration connected',
  'integrations.toast.saved': 'Integration saved',
  'integrations.toast.deleted': 'Instance deleted',
  'integrations.toast.deletedBody': 'The tasks and hours already synchronised stay.',
  'integrations.toast.paused': 'Integration paused',
  'integrations.toast.resumed': 'Integration active',
  'integrations.toast.queued': 'Sync started',
  'integrations.toast.queuedBody': 'Follow it on the Syncs tab.',
  'integrations.toast.alreadyRunning': 'A sync of this kind is already running',
  'workspaces.create': 'New workspace',
  'workspaces.createHint':
    'With its own tasks, people and contracts. The workspace you are in does not change.',
  'workspaces.name': 'Workspace name',
  'workspaces.namePlaceholder': 'e.g. Client projects',
  'workspaces.slugPreview': 'Address: {slug}',
  'workspaces.slugPending': 'The address is derived from the name.',
  'workspaces.icon': 'Icon',
  'workspaces.pickIcon': 'Choose a picture',
  'workspaces.iconHint':
    'PNG, JPEG or WebP, at most 2 MB. Without one, the name\u2019s first letter shows.',
  'workspaces.timezone': 'Timezone',
  'workspaces.error.name': 'Give the workspace a name.',
  'workspaces.error.slug': 'No address can be made from that name. Use letters or numbers.',
  'workspaces.error.iconSize': 'The picture can be at most 2 MB.',

  /* ── R-38 · the address names a workspace this person is not in ─────────────────────────── */
  /* ⚠️⚠️ **ONE MESSAGE FOR TWO CAUSES, DELIBERATELY.** The workspace may not exist, or it may
     exist and not be theirs. CLAUDE.md rule 5: existence is itself information, so copy saying
     "you do not have access to this workspace" would confirm that it exists. */
  'workspace.unknown.title': 'That address does not lead anywhere',
  'workspace.unknown.body':
    'There is no such workspace, or you are not a member of it. Check the link, or go back to where you were working.',
  'workspace.unknown.back': 'Back to {workspace}',

  /* ── C65 · the project Dashboard, in hours ─────────────────────────────── */
  'effort.workdayShort': '{count} wd',
  'effort.unit.workday': 'working days',
  'effort.unit.day': 'days',
  'effort.slip.label': 'Slip unit',
  'effort.slip.workday': 'working day',
  'effort.slip.calendar': 'calendar day',
  'effort.eac.cpiHint': 'frame / CPI – today’s efficiency holds to the end',
  'effort.eac.etcHint': 'actual + (frame − earned) – the remainder goes to plan',
  'effort.kpi.periodFrame': 'PERIOD FRAME',
  'effort.kpi.periodFrameFormula': '{total} / {count} periods',
  'effort.kpi.periodSpent': 'PERIOD ACTUAL / FRAME',
  'effort.kpi.periodElapsed': 'PERIOD ELAPSED',
  'effort.kpi.rate': 'DAILY BURN',
  'effort.kpi.overrun': 'FORECAST OVERRUN',
  'effort.kpi.billable': 'BILLABLE HOURS',
  'effort.kpi.billableFormula': 'over-frame line items',
  'effort.kpi.contractFrame': 'CONTRACT FRAME',
  'effort.kpi.contractFrameRetainer': 'Σ retainer line item estimate',
  'effort.kpi.contractFrameAll': 'Σ line item estimate',
  'effort.kpi.spent': 'ACTUAL / FRAME',
  'effort.kpi.elapsed': 'TIME ELAPSED',
  'effort.kpi.planned': 'PLAN',
  'effort.kpi.plannedFormula': 'Σ allocation',
  'effort.kpi.carryOver': 'CARRY-OVER',
  'effort.kpi.carryOverFormula': 'Σ (frame − actual) closed',
  'effort.kpi.frame': 'FRAME',
  'effort.kpi.frameFormula': 'Σ estimate',
  'effort.kpi.done': 'COMPLETION',
  'effort.kpi.cpi': 'CPI',
  'effort.kpi.slip': 'SLIP',
  'effort.kpi.slipFormula': 'forecast finish − deadline',
  'effort.panel.frame': 'PLAN / ACTUAL / FORECAST',
  'effort.panel.series': 'SERIES',
  'effort.panel.schedule': 'SCHEDULE',
  'effort.bar.frame': 'FRAME',
  'effort.bar.plan': 'PLAN',
  'effort.bar.actual': 'ACTUAL',
  'effort.bar.projected': 'FORECAST',
  'effort.bar.closedFrame': 'FRAME OF CLOSED PERIODS',
  'effort.bar.earned': 'EARNED',
  'effort.bar.eacTrend': 'TREND EAC',
  'effort.bar.eacPlan': 'PLAN EAC',
  'effort.foot.rate': 'Daily burn (RATE)',
  'effort.foot.allowedRate': 'Allowed daily pace (RATE_OK)',
  'effort.foot.rateDelta': 'Rate gap (ΔRATE)',
  'effort.foot.remaining': 'Frame left (R)',
  'effort.foot.remainingFrame': 'Frame left',
  'effort.foot.burnOut': 'Frame burn-out',
  'effort.foot.workdaysLeft': 'Working days left',
  'effort.foot.periodBudget': 'Period frame (P)',
  'effort.foot.averageUsage': 'Average usage',
  'effort.foot.periodsLeft': 'Periods left',
  'effort.foot.overtime': 'Approved overtime (OT)',
  'effort.foot.etc': 'Work left (ETC)',
  'effort.foot.vac': 'Frame variance (VAC)',
  'effort.foot.tcpi': 'Required efficiency (TCPI)',
  'effort.foot.spi': 'Schedule index (SPI)',
  'effort.foot.cv': 'Cost variance (CV)',
  'effort.foot.sv': 'Schedule variance (SV)',
  'effort.list.closedPeriods': 'CLOSED PERIODS',
  'effort.list.averageUsage': 'AVERAGE USAGE',
  'effort.list.averageShare': 'AVERAGE / FRAME',
  'effort.list.carryOver': 'CARRY-OVER',
  'effort.list.periodsLeft': 'PERIODS LEFT',
  'effort.list.nextPlan': 'NEXT PERIOD PLAN',
  'effort.list.nextPlanVariance': 'NEXT PLAN VARIANCE',
  'effort.list.billable': 'BILLABLE HOURS',
  'effort.list.deadline': 'DEADLINE',
  'effort.list.forecastFinish': 'FORECAST FINISH',
  'effort.list.workdaysLeft': 'WORKING DAYS LEFT',
  'effort.list.slip': 'SLIP',
  'effort.list.lateTasks': 'OVERDUE TASKS',
  'effort.list.lateHours': 'OVERDUE WORK LEFT',
  'effort.list.openTaskAge': 'OPEN TASK AGE',
  'effort.tip.panel.name': 'Plan / actual / forecast',
  'effort.tip.panel.what':
    'The scope’s hours on one scale: what was planned, what happened, and where it lands.',
  'effort.tip.panel.note': 'The vertical mark is the frame – a bar past it is an overrun.',
  'effort.tip.series.name': 'Series – the periods summarised',
  'effort.tip.series.what': 'A summary of the contract’s closed and remaining periods.',
  'effort.tip.schedule.name': 'Schedule – where it stands in time',
  'effort.tip.schedule.what': 'The scope’s deadline, forecast finish and overdue work.',
  'effort.tip.periodFrame.name': 'P: period frame',
  'effort.tip.periodFrame.what':
    'One period’s share of the scope’s frame – retainer line items only.',
  'effort.tip.periodFrame.calc': 'retainer estimate / periods covered',
  'effort.tip.periodFrame.note':
    'Every period gets the same frame. Over-frame (billable) work neither adds to it nor consumes it.',
  'effort.tip.periodSpent.name': 'U / P: used per period frame',
  'effort.tip.periodSpent.what': 'Hours logged in the period as a share of its frame.',
  'effort.tip.periodSpent.calc': 'period actual / period frame',
  'effort.tip.periodSpent.note':
    'Over 100% the period has overrun its frame. The faint bar is elapsed working days: a longer actual bar means the frame is going faster than the time.',
  'effort.tip.elapsed.name': 'Share of working days elapsed (wd = working day)',
  'effort.tip.elapsed.calc': 'working days elapsed / working days in total',
  'effort.tip.periodElapsed.what': 'Where the period stands in time.',
  'effort.tip.periodElapsed.note':
    'Higher than the actual/frame share means there is still room in the period.',
  'effort.tip.contractElapsed.what': 'Where the contract stands in time.',
  'effort.tip.contractElapsed.note':
    'If elapsed time is ahead of the actual/frame share, the frame is going slower than the clock.',
  'effort.tip.plainElapsed.what': 'Where the scope stands in time.',
  'effort.tip.plainElapsed.note': 'Higher than completion means the work is behind the clock.',
  'effort.tip.rate.name': 'RATE: daily burn',
  'effort.tip.rate.what': 'How many hours come out of the frame on an average working day.',
  'effort.tip.rate.calc': 'period actual / working days elapsed',
  'effort.tip.rate.note':
    'The allowed pace (RATE_OK) = frame / working days in total. A higher burn than that closes the period over frame.',
  'effort.tip.allowedRate.name': 'RATE_OK: allowed daily pace',
  'effort.tip.allowedRate.what': 'The daily pace that lands exactly on the frame.',
  'effort.tip.allowedRate.calc': 'period frame / working days in the period',
  'effort.tip.rateDelta.name': 'ΔRATE: rate gap',
  'effort.tip.rateDelta.what': 'The difference between the real and the allowed daily pace.',
  'effort.tip.rateDelta.calc': 'daily burn − allowed daily pace',
  'effort.tip.rateDelta.note': 'Positive: this many hours a day more than the frame can take.',
  'effort.tip.remaining.name': 'R: frame left',
  'effort.tip.remaining.what': 'How many hours are left in the period’s frame.',
  'effort.tip.remaining.calc': 'period frame − period actual',
  'effort.tip.remaining.note': 'A negative value means the frame has already been overrun.',
  'effort.tip.contractRemaining.what': 'How many hours are left in the contract’s whole frame.',
  'effort.tip.contractRemaining.calc': 'contract frame − total actual',
  'effort.tip.burnOut.name': 'Frame burn-out date',
  'effort.tip.burnOut.what': 'The day the frame runs out if the current pace holds.',
  'effort.tip.burnOut.calc': 'frame left / daily burn, counted forward in working days',
  'effort.tip.burnOut.note': 'Before the period ends, the period closes over frame.',
  'effort.tip.workdaysLeft.name': 'Working days left',
  'effort.tip.workdaysLeft.calc': 'today → the end of the scope, holidays excluded',
  'effort.tip.periodWorkdaysLeft.what': 'Working days left in the period.',
  'effort.tip.contractWorkdaysLeft.what': 'Working days left until the contract ends.',
  'effort.tip.deadlineWorkdaysLeft.what': 'Working days left until the deadline.',
  'effort.tip.overrun.name': 'PROJ − P: projected total minus frame',
  'effort.tip.overrun.what': 'How far the period goes past its frame if the current pace holds.',
  'effort.tip.overrun.calc': 'PROJ − P, where PROJ = RATE × working days in the period',
  'effort.tip.overrun.note': 'Positive: the frame runs out; zero or negative: it holds.',
  'effort.tip.projected.name': 'PROJ: projected total',
  'effort.tip.projected.what':
    'What the actual will be by the end of the period if the daily pace holds.',
  'effort.tip.projected.calc': 'daily burn × working days in the period',
  'effort.tip.projected.note': 'A value past the frame signals an overrun.',
  'effort.tip.bac.name': 'BAC: total frame',
  'effort.tip.contractFrame.what':
    'What the contract commits to in hours – retainer line items only.',
  'effort.tip.contractFrame.calc': 'Σ retainer line item estimate',
  'effort.tip.contractFrame.note':
    'Over-frame (billable) line items carry no frame; their hours are reported separately.',
  'effort.tip.frame.what': 'What the work in scope commits to, in hours.',
  'effort.tip.frame.calc': 'Σ line item estimate in scope',
  'effort.tip.spent.name': 'AC / BAC: actual per frame',
  'effort.tip.spent.what': 'Hours logged so far as a share of the frame.',
  'effort.tip.spent.calc': 'actual / frame',
  'effort.tip.spent.note': 'Over 100% the scope has overrun its frame.',
  'effort.tip.contractSpent.what': 'Hours logged on the contract as a share of the whole frame.',
  'effort.tip.contractSpent.calc': 'total actual / total frame',
  'effort.tip.contractSpent.note':
    'Over 100% the contract has overrun its frame. The faint bar is elapsed working days.',
  'effort.tip.done.name': 'EV / BAC: completion',
  'effort.tip.done.what': 'How much work is finished in hour value, against the frame.',
  'effort.tip.done.calc': 'EV / BAC, where EV = Σ task estimate × status weight',
  'effort.tip.done.note':
    'Status weights: backlog 0 · up next 0 · in progress 0.5 · in review 0.8 · done 1.',
  'effort.tip.plan.name': 'PLAN: allocated capacity',
  'effort.tip.plan.what': 'Hours planned onto people for the contract’s line items.',
  'effort.tip.plan.calc': 'Σ allocation across the contract’s whole span',
  'effort.tip.plan.note': 'A plan past the frame is an overrun before the work starts.',
  'effort.tip.periodPlan.calc': 'Σ allocation across the period’s days',
  'effort.tip.plainPlan.what': 'Hours planned onto the scope, summed per person.',
  'effort.tip.plainPlan.calc': 'Σ allocation across the scope’s days',
  'effort.tip.actual.name': 'AC: actual',
  'effort.tip.actual.what': 'Hours logged in the period.',
  'effort.tip.actual.calc': 'Σ worklogs on the contract’s tasks',
  'effort.tip.periodActual.calc': 'Σ worklogs on the period’s days',
  'effort.tip.contractActual.what': 'All hours logged on the contract.',
  'effort.tip.plainActual.what': 'Hours logged in scope.',
  'effort.tip.bar.frameNote': 'Every other bar is read against this one.',
  'effort.tip.bar.planNote': 'The percentage is a share of the period’s frame.',
  'effort.tip.bar.planTotalNote': 'The percentage is a share of the whole frame.',
  'effort.tip.bar.planScopeNote': 'The percentage is a share of the frame.',
  'effort.tip.bar.actualNote':
    'The percentage is a share of the period’s frame; over 100% is an overrun.',
  'effort.tip.bar.actualTotalNote':
    'The percentage is a share of the whole frame; over 100% is an overrun.',
  'effort.tip.bar.actualScopeNote':
    'The percentage is a share of the frame; over 100% is an overrun.',
  'effort.tip.closedFrame.name': 'Frame of the closed periods',
  'effort.tip.closedFrame.what': 'How much frame the contract has released so far.',
  'effort.tip.closedFrame.calc': 'Σ period frame across the closed periods',
  'effort.tip.closedFrame.note':
    'A larger actual means the contract is consuming the frame ahead of schedule.',
  'effort.tip.ev.name': 'EV: earned value',
  'effort.tip.ev.what': 'The hour value of the work that is finished.',
  'effort.tip.ev.calc': 'Σ task estimate × status weight',
  'effort.tip.ev.note':
    'An actual larger than the earned value means more hours went in than work came out.',
  'effort.tip.eacCpi.name': 'EAC: estimate at completion (trend)',
  'effort.tip.eacCpi.what':
    'What the scope will cost in hours if today’s efficiency holds to the end.',
  'effort.tip.eacCpi.note':
    'The more conservative reading: the variance so far carries proportionally into the work left. A value past the frame signals an overrun.',
  'effort.tip.eacEtc.name': 'EAC: estimate at completion (plan)',
  'effort.tip.eacEtc.what':
    'What the scope will cost in hours if the work left goes exactly to estimate.',
  'effort.tip.eacEtc.note':
    'The more optimistic reading: it treats the variance so far as a one-off. The formula can be switched at the end of the bar.',
  'effort.tip.cpi.name': 'CPI: cost performance index',
  'effort.tip.cpi.what': 'How much finished work one logged hour buys.',
  'effort.tip.cpi.calc': 'EV / AC (earned value / actual hours)',
  'effort.tip.cpi.note':
    '1 = to plan. Under 1 the work is taking longer than estimated (highlighted under 0.92); over 1 it is going faster.',
  'effort.tip.etc.name': 'ETC: estimate to complete',
  'effort.tip.etc.what': 'The hours still needed to finish the scope.',
  'effort.tip.vac.name': 'VAC: variance at completion',
  'effort.tip.vac.what': 'How far the total is expected to land from the frame.',
  'effort.tip.vac.note': 'Negative: the scope takes more hours than the frame holds.',
  'effort.tip.tcpi.name': 'TCPI: to-complete performance index',
  'effort.tip.tcpi.what': 'The efficiency the remaining work needs for the frame to hold.',
  'effort.tip.tcpi.note':
    'Under 1 the frame holds at the current pace; over 1 only at a better one.',
  'effort.tip.spi.name': 'SPI: schedule performance index',
  'effort.tip.spi.what': 'Finished work measured against the plan due by today.',
  'effort.tip.spi.note': '1 = to plan; under 1 the work is going slower than planned.',
  'effort.tip.cv.name': 'CV: cost variance',
  'effort.tip.cv.what': 'The gap between finished work and time spent, in hours.',
  'effort.tip.cv.note': 'Negative: more hours logged than work finished.',
  'effort.tip.sv.name': 'SV: schedule variance',
  'effort.tip.sv.what': 'The gap between finished work and the plan due by today, in hours.',
  'effort.tip.sv.note': 'Negative: less is finished than the plan called for by today.',
  'effort.tip.overtime.name': 'OT: approved overtime',
  'effort.tip.overtime.what': 'The hour value of approved overtime, multiplier included.',
  'effort.tip.overtime.calc': 'Σ (overtime hours × multiplier) on approved requests',
  'effort.tip.billable.name': 'Billable hours',
  'effort.tip.billable.what': 'Hours of over-frame work, invoiced separately.',
  'effort.tip.billable.calc': 'Σ worklogs on billable-scope line items',
  'effort.tip.billable.note':
    'The frame figures and this one are measured apart on purpose: billable work must not hide a frame overrun.',
  'effort.tip.closedPeriods.name': 'Closed periods',
  'effort.tip.closedPeriods.what': 'How many periods of the contract have already closed.',
  'effort.tip.closedPeriods.calc': 'where the period ended before today',
  'effort.tip.averageUsage.name': 'Average usage',
  'effort.tip.averageUsage.what': 'How many hours a closed period consumed on average.',
  'effort.tip.averageUsage.calc': 'Σ closed-period actual / number of closed periods',
  'effort.tip.averageUsage.note': 'An average above the period frame means a standing overrun.',
  'effort.tip.averageShare.name': 'Average per frame',
  'effort.tip.averageShare.what': 'Average usage as a share of the average period frame.',
  'effort.tip.averageShare.calc': 'average actual / average period frame',
  'effort.tip.averageShare.note': 'Over 100% the contract overruns its period frame on average.',
  'effort.tip.carryOver.name': 'Carry-over',
  'effort.tip.carryOver.what': 'Frame left unused – or overrun – across the closed periods.',
  'effort.tip.carryOver.calc': 'Σ (period frame − period actual) across the closed periods',
  'effort.tip.carryOver.note': 'Positive: frame left on the table; negative: an overrun overall.',
  'effort.tip.periodsLeft.name': 'Periods left',
  'effort.tip.periodsLeft.what': 'The running period plus the ones not yet begun.',
  'effort.tip.periodsLeft.calc': 'active + planned periods',
  'effort.tip.nextPlan.name': 'Next period plan',
  'effort.tip.nextPlan.what': 'Hours planned into the next period.',
  'effort.tip.nextPlan.calc': 'Σ allocation across the next period’s days',
  'effort.tip.nextPlanVariance.name': 'Next plan variance',
  'effort.tip.nextPlanVariance.what': 'The gap between the next period’s plan and its frame.',
  'effort.tip.nextPlanVariance.calc': 'next period plan − its frame',
  'effort.tip.nextPlanVariance.note':
    'Positive: the plan alone already overruns the frame – worth replanning.',
  'effort.tip.deadline.name': 'Deadline',
  'effort.tip.deadline.what':
    'The end of the scope: the contract’s or the line item’s closing date.',
  'effort.tip.forecastFinish.name': 'FF: forecast finish',
  'effort.tip.forecastFinish.what': 'When the remaining work finishes at the planned capacity.',
  'effort.tip.forecastFinish.calc':
    'the work left is drawn down day by day against the allocation; once that runs out, the average planned day is used',
  'effort.tip.forecastFinish.note':
    'Past the deadline it turns red – this is what Slip is measured from.',
  'effort.tip.slip.name': 'Slip',
  'effort.tip.slip.what': 'How far past the deadline the work finishes.',
  'effort.tip.slip.calc': 'forecast finish − deadline',
  'effort.tip.slip.note':
    'Positive: late; negative: early. The unit switches between working and calendar days.',
  'effort.tip.lateTasks.name': 'Overdue tasks',
  'effort.tip.lateTasks.what': 'Open tasks whose due date has passed.',
  'effort.tip.lateTasks.calc': 'where the due date is before today and the status is not done',
  'effort.tip.lateHours.name': 'Overdue work left',
  'effort.tip.lateHours.what': 'The hour value still unfinished on the overdue tasks.',
  'effort.tip.lateHours.calc': 'Σ estimate × (1 − status weight) on the overdue tasks',
  'effort.tip.lateHours.note': 'This work is already certain to cross the deadline.',
  'effort.tip.openTaskAge.name': 'Average age of the open tasks',
  'effort.tip.openTaskAge.what': 'How long the still-open tasks have been running on average.',
  'effort.tip.openTaskAge.calc': 'Σ (start → today in working days) / number of open tasks',
  'effort.tip.openTaskAge.note': 'A rising figure signals a pile-up.',
  'effort.chart.empty': 'Nothing to chart for this scope.',
  'effort.chart.title': 'PLAN / ACTUAL / FORECAST',
  'effort.burn.downTitle': 'Burn-down – work remaining',
  'effort.burn.upTitle': 'Burn-up – work done',
  'effort.burn.scope': 'Scope',
  'effort.burn.frame': 'Frame',
  'effort.burn.plan': 'Plan',
  'effort.burn.done': 'Done',
  'effort.burn.used': 'Used',
  'effort.burn.remaining': 'Remaining',
  'effort.burn.plannedRemaining': 'Remaining by plan',
  'effort.burn.ideal': 'Ideal',
  'effort.burn.forecast': 'Forecast',
  'effort.burn.today': 'Today',
  'effort.burn.ahead': 'Still ahead – plan and forecast only',
  'effort.burn.historyFrom':
    'Before {date} the state is estimated from the earliest recorded change.',
  'effort.burn.upWhat':
    'The budgeted hours (scope), the plan (allocation), an even pace (ideal) and the work done (earned value) over time. Done below plan is behind schedule.',
  'effort.burn.upWhatFrame':
    'The picked periods’ frame, the plan (allocation) and the hours that used the frame, over time.',
  'effort.burn.upCalc':
    'Scope = the line items’ budgeted hours (BAC) · Done = scope × completion, where completion = the tasks’ estimate × status weight ÷ their estimates (in progress 0.5, in review 0.8, closed 1) · Plan = cumulative allocation · Ideal = an even pace from zero to scope',
  'effort.burn.downWhat':
    'How much work is left — against the plan and an even pace — and when it runs out at the pace so far.',
  'effort.burn.downCalc':
    'Remaining = scope − done · Ideal = scope straight down to zero over the working days · Forecast = what is left today, less the daily pace of work done so far',
  'effort.chart.titleCumulative': 'PLAN / ACTUAL / FORECAST · CUMULATIVE',
  'effort.chart.day': 'Day',
  'effort.chart.week': 'Week',
  'effort.chart.month': 'Month',
  'effort.chart.perPeriod': 'Period',
  'effort.chart.cumulative': 'Cumulative',
  'effort.chart.current': 'now',
  'effort.chart.row.frame': 'FRAME',
  'effort.chart.row.plan': 'PLAN',
  'effort.chart.row.actual': 'ACTUAL',
  'effort.chart.row.forecast': 'FORECAST',
  'effort.chart.row.overtime': 'OVERTIME',
  'effort.table.periods': 'PERIODS',
  'effort.table.periodsNote': '{count} periods · {over} over frame',
  'effort.table.lineItems': 'LINE ITEMS',
  'effort.table.lineItemsInPeriod': 'LINE ITEMS IN THE PERIOD',
  'effort.table.tasks': 'TASKS',
  'effort.table.noTasks': 'No tasks in this scope.',
  'effort.tools.search': 'Search',
  'effort.tools.noMatch': 'Nothing matches these filters.',
  'effort.table.total': 'Total',
  'effort.state.past': 'Closed',
  'effort.state.current': 'Active',
  'effort.state.future': 'Planned',
  'effort.scope.retainer': 'Retainer',
  'effort.scope.billable': 'Billable',
  'effort.col.period': 'PERIOD',
  'effort.col.state': 'STATE',
  'effort.col.frame': 'FRAME',
  'effort.col.plan': 'PLAN',
  'effort.col.actual': 'ACTUAL',
  'effort.col.variance': 'VARIANCE',
  'effort.col.share': 'ACTUAL/FRAME',
  'effort.col.overtime': 'OVERTIME',
  'effort.col.billable': 'BILLABLE',
  'effort.col.lineItem': 'LINE ITEM',
  'effort.col.billing': 'BILLING',
  'effort.col.scope': 'SCOPE',
  'effort.col.earned': 'EARNED',
  'effort.col.key': 'KEY',
  'effort.col.task': 'TASK',
  'effort.col.assignee': 'ASSIGNEE',
  'effort.col.estimate': 'ESTIMATE',
  'effort.col.logged': 'LOGGED',
  'effort.col.weight': 'WEIGHT',
  'effort.col.due': 'DUE',
  'effort.col.person': 'PERSON',
  'effort.col.planActual': 'PLAN / ACTUAL',
  'effort.col.utilisation': 'UTILISATION',
  'effort.col.ofFrame': 'OF FRAME',
  'effort.panel.people': 'RESOURCES',
  'effort.group.label': 'Grouping',
  'effort.group.person': 'By person',
  'effort.group.seniority': 'By seniority',
  'effort.group.role': 'By role',
  'effort.group.none': 'Not set',
  'effort.panel.headcount': '{count} people',
  'effort.panel.noPeople': 'Nobody has planned or logged time in this scope.',
  'effort.panel.splitActual': 'LOGGED SPLIT',
  'effort.panel.splitPlan': 'PLANNED SPLIT',
  'effort.panel.noSplit': 'Nothing to split yet.',
  'effort.tip.periodIndex.name': 'Index',
  'effort.tip.periodIndex.what': "The period's number under the contract's recurrence rule.",
  'effort.tip.periodSpan.name': 'Period',
  'effort.tip.periodSpan.what':
    'Its first and last day. Clicking the row narrows the dashboard to this period.',
  'effort.tip.periodState.name': 'State',
  'effort.tip.periodState.what': 'Planned, active or closed – derived from the dates.',
  'effort.tip.periodVariance.name': 'Variance',
  'effort.tip.periodVariance.what':
    'Actual minus frame. Positive (red): the period overran its frame.',
  'effort.tip.periodShare.name': 'Actual per frame',
  'effort.tip.periodShare.what': 'Logged hours as a share of the frame; over 100% is an overrun.',
  'effort.tip.lineItemIndex.name': 'Index',
  'effort.tip.lineItemIndex.what': "The line item's number on the contract.",
  'effort.tip.lineItem.name': 'Line item',
  'effort.tip.lineItem.what': 'A line of the contract – tasks and hours hang off it.',
  'effort.tip.billing.name': 'Billing type',
  'effort.tip.billing.what':
    'How time logged on the line item is settled: fixed price, time and material, or non-billable.',
  'effort.tip.scope.name': 'Scope',
  'effort.tip.scope.what':
    'Whether the line item is inside the frame (retainer) or over-frame work billed separately.',
  'effort.tip.itemFrame.name': "The line item's frame",
  'effort.tip.itemFrame.what':
    "Its estimate's share of the scope. Not meaningful on a billable line item.",
  'effort.tip.itemVariance.name': 'Variance',
  'effort.tip.itemVariance.what':
    'Actual minus frame on the line item. Positive (red): an overrun.',
  'effort.tip.taskKey.name': 'Task key',
  'effort.tip.taskKey.what': "The task's identifier (PROJECT-number).",
  'effort.tip.task.name': 'Task',
  'effort.tip.task.what': 'Its title. Clicking the row opens the task.',
  'effort.tip.assignee.name': 'Assignee',
  'effort.tip.assignee.what': 'Who the task is assigned to.',
  'effort.tip.taskStatus.name': 'Status',
  'effort.tip.taskStatus.what':
    "The task's state – this is what sets the status weight behind the earned value.",
  'effort.tip.taskEstimate.name': 'Estimate (task BAC)',
  'effort.tip.taskEstimate.what': 'The hours the task is estimated at.',
  'effort.tip.taskLogged.name': 'Logged (task AC)',
  'effort.tip.taskLogged.what': 'Hours logged on the task.',
  'effort.tip.weight.name': 'Status weight',
  'effort.tip.weight.what':
    'How finished the status counts as: backlog 0 · up next 0 · in progress 0.5 · in review 0.8 · done 1.',
  'effort.tip.taskVariance.name': 'Variance',
  'effort.tip.taskVariance.what':
    'Time spent minus work finished. Positive (red): more hours went in than work came out.',
  'effort.tip.due.name': 'Due date',
  'effort.tip.due.what':
    "The task's deadline. Highlighted where it has passed and the task is still open.",
  'effort.tip.person.name': 'Person',
  'effort.tip.person.what': 'Anyone who planned or logged time in this scope.',
  'effort.tip.planActual.name': 'Plan over actual',
  'effort.tip.planActual.what':
    'The upper rule is planned time, the lower one logged – on one scale, so the overhang reads on its own.',
  'effort.tip.personPlan.name': 'PLAN: allocated capacity',
  'effort.tip.personPlan.what': 'Hours planned onto this person in the scope.',
  'effort.tip.personActual.name': 'ACTUAL: logged time',
  'effort.tip.personActual.what': 'Hours this person logged in the scope.',
  'effort.tip.personVariance.name': 'Variance',
  'effort.tip.personVariance.what':
    'Actual minus plan. Positive: they worked more than was planned for them.',
  'effort.tip.utilisation.name': 'Utilisation',
  'effort.tip.utilisation.what': 'Logged time as a share of planned time; highlighted over 108%.',
  'effort.tip.ofFrame.name': 'Share of the frame',
  'effort.tip.ofFrame.what':
    "This person's hours as a share of the scope's frame – it shows who delivery rests on.",
  'effort.tip.split.name': 'Logged split',
  'effort.tip.split.what': 'How the time logged in this scope divides between people.',
  'effort.tip.split.calc': "a person's hours / all logged hours",
  'effort.tip.split.note': 'One outsized slice means delivery rests on one person.',
  'effort.tip.splitPlan.name': 'Planned split',
  'effort.tip.splitPlan.what': 'How the planned capacity divides between people.',
  'effort.tip.splitPlan.calc': "a person's allocation / all allocation",
  'effort.tip.splitPlan.note': 'Nothing has been logged yet, so the plan is shown instead.',
  'effort.pick.contract': 'Contract',
  'effort.pick.scope': 'Scope',
  'effort.pick.more': 'More actions',
  'effort.scope.whole': 'Whole contract',
  'effort.scope.period': 'Period',
  'effort.action.openContract': 'Open the contract',
  'effort.empty.contractLabel': 'CONTRACTS',
  'effort.empty.noContract': 'This project has no contract yet',
  'effort.empty.noContractBody':
    "The dashboard reads a contract's frame and what has been taken out of it. Create a contract and it appears here.",
  'effort.empty.lineItemLabel': 'LINE ITEMS',
  'effort.empty.noLineItem': 'The contract has no line items yet',
  'effort.empty.noLineItemBody':
    "The frame is the sum of the line items' estimates. Add one to the contract and the figures appear here.",
  'effort.chart.period': 'Period',
  'effort.col.startsOn': 'STARTS',
  'effort.col.endsOn': 'ENDS',
  'effort.col.done': 'DONE',
  'effort.col.slip': 'SLIP',
  'effort.open': 'open-ended',
  /* ⚠️ ux-pass-2 — the effort registers became DataLists: filter labels, totals and empties. */
  'effort.filter.state': 'State',
  'effort.filter.billing': 'Billing',
  'effort.filter.scope': 'Scope',
  'effort.filter.startsOn': 'Starts',
  'effort.filter.endsOn': 'Ends',
  'effort.filter.assignee': 'Assignee',
  'effort.filter.status': 'Status',
  'effort.filter.category': 'Status group',
  'effort.filter.unassigned': 'Unassigned',
  'effort.filter.role': 'Role',
  'effort.filter.seniority': 'Seniority',
  'effort.tools.noMatchBody': 'Loosen the search or the filters.',
  'effort.tools.noPeriods': 'This contract has no periods yet.',
  'effort.table.totalFiltered': 'Filtered total',
  'effort.table.lateNote': '{late} overdue',
  'effort.table.lineItemTotals':
    '{label} · frame {frame} · plan {plan} · actual {actual} · earned {earned} · CPI {cpi}',
  'effort.table.periodTotals':
    '{label} · frame {frame} · plan {plan} · actual {actual} · overtime {overtime}',
  'effort.table.periodTotalsBillable':
    '{label} · frame {frame} · plan {plan} · actual {actual} · overtime {overtime} · billable {billable}',
  'effort.tip.startsOn.name': 'Starts',
  'effort.tip.startsOn.what':
    "The line item's first day. Where it has none of its own, the contract's.",
  'effort.tip.endsOn.name': 'Ends',
  'effort.tip.endsOn.what':
    'Its last day. Where none is set it is open-ended and runs to the end of the contract.',
  'effort.tip.doneTasks.name': 'Completion in tasks',
  'effort.tip.doneTasks.what': 'How many of the line item’s tasks are finished – done / total.',
  'effort.tip.itemSlip.name': 'Slip',
  'effort.tip.itemSlip.what':
    "The worst slip among the line item's overdue open tasks, in working days.",
  'effort.scope.project': 'Whole project',
  'effort.scope.projectBody': 'Every contract of the project together',
  'effort.col.contract': 'CONTRACT',
  'effort.tip.contract.name': 'Contract',
  'effort.tip.contract.what': 'The contract this row belongs to. Click the row to open it.',
  'effort.tip.taskLineItem.name': 'Line item',
  'effort.tip.taskLineItem.what':
    "The line item the task's hours go to, with its contract beneath. Hours on a task with no line item count too.",
  'effort.task.noLineItem': 'No line item',
  'effort.role.none': 'No project role',
  'permissions.levels.deleteBuiltin': 'A built-in level cannot be deleted.',
  'projects.copy.label': 'Copy',
  'projects.copy.key': 'Copy the key',
  'projects.copy.identifier': 'Copy the identifier',
  'projects.copy.link': 'Copy the link',
  'nav.requestsSection': 'Requests',
  'title.toDecide': 'To decide',

  /* ── C59 · the project's Alerts tab ──────────────────────────────────────────────────────── */
  'project.tab.alerts': 'Alerts',
  'title.projectAlerts': 'Alerts',
  'alerts.list': 'Data quality alerts',
  'alerts.search': 'Search by alert or subject…',

  'alerts.view.active': 'Active',
  'alerts.view.archived': 'Archived',
  'alerts.view.all': 'All',

  'alerts.filter.severity': 'Severity',
  'alerts.filter.area': 'Area',
  'alerts.filter.any': 'All',

  'alerts.column.severity': 'Severity',
  'alerts.column.name': 'Alert',
  'alerts.column.subject': 'Subject',
  'alerts.column.area': 'Area',
  'alerts.column.penalty': 'Penalty',
  'alerts.column.detected': 'Detected',
  'alerts.column.state': 'State',

  'alerts.severity.high': 'High',
  'alerts.severity.mid': 'Medium',
  'alerts.severity.low': 'Low',

  'alerts.area.contract': 'Contract',
  'alerts.area.currency': 'Currency',
  'alerts.area.external_cost': 'External cost',
  'alerts.area.invoice': 'Invoice',
  'alerts.area.line_item': 'Line item',
  'alerts.area.member': 'Project member',
  'alerts.area.occurrence': 'Period',
  'alerts.area.rate_card': 'Rate card',
  'alerts.area.resource': 'Resource',
  'alerts.area.schedule': 'Work schedule',
  'alerts.area.task': 'Task',
  'alerts.area.worklog': 'Worklog',

  'alerts.state.active': 'Active',
  'alerts.state.resolved': 'Resolved',
  'alerts.state.waived': 'Waived',
  'alerts.state.resolvedOn': '{date} — the anomaly is gone',
  'alerts.state.waivedBy': 'Waived by {name}: {reason}',

  'alerts.penalty.tip': '{penalty} points off the Trust Score (at most {cap} per kind).',
  'alerts.penalty.none': 'Does not count towards the Trust Score.',
  'alerts.penalty.value': '−{penalty}',
  'alerts.penalty.dash': '–',

  'alerts.empty.active.title': 'No open alerts',
  'alerts.empty.active.body':
    "This project's data is consistent — or the filters are hiding the matches.",
  'alerts.empty.active.action': 'Clear filters',
  'alerts.empty.archived.title': 'No archived alerts',
  'alerts.empty.archived.body':
    'Resolved and waived anomalies land here — they are never deleted, they just leave the score.',
  'alerts.empty.archived.action': 'Show active',

  'alerts.action.open': 'Open',
  'alerts.action.dismiss': 'Waive',
  'alerts.action.dismissAll': 'Waive all',
  'alerts.group.toggle': '{name} — {count} alerts',
  'alerts.group.subjects': '{count} subjects',
  'alerts.group.active': '{count} active',
  'alerts.action.restore': 'Revoke waiver',
  'alerts.action.close': 'Close',

  'alerts.go.member': 'Open person',
  'alerts.go.task': 'Open task',
  'alerts.go.line_item': 'Open line item',
  'alerts.go.contract': 'Open contract',
  'alerts.go.worklog': 'Open worklog',
  'alerts.go.external_cost': 'Open external cost',
  'alerts.go.currency': 'Open currencies',
  'alerts.go.project': 'Open project',

  'alerts.detail.impact': 'What it breaks',
  'alerts.detail.fix': 'How to fix it',
  'alerts.detail.subject': 'Subject',
  'alerts.detail.detected': 'Detected {date}',
  'alerts.detail.detectedResolved': 'Detected {date} · resolved {resolved}',
  'alerts.detail.severity': '{severity} severity',
  'alerts.detail.archivedNote':
    'This anomaly is gone, so we archived it — it stays in the list so the history is auditable.',
  'alerts.detail.waivedNote':
    '{name} recorded an exception for this rule and this subject: {reason}. The check still runs; its result simply no longer counts towards the score.',

  'alerts.dismiss.title': 'Waive alert',
  'alerts.dismiss.hint':
    'The exception covers the rule and the subject, not this one finding. If the data is fixed the alert resolves by itself; if it comes back, it stays waived.',
  'alerts.dismiss.reason': 'Reason',
  'alerts.dismiss.reasonPlaceholder': 'E.g. internal project, deliberately has no rate card',
  'alerts.dismiss.reasonRequired': 'A reason is required.',
  'alerts.dismiss.submit': 'Waive',
  'alerts.dismiss.submitAll': 'Waive all alerts of this type ({count})',
  'alerts.toast.waived': 'Alert waived',
  'alerts.toast.waivedAll': '{count} alerts waived',
  'alerts.toast.restored': 'Waiver revoked',
  'alerts.dismiss.cancel': 'Cancel',
  'alerts.restore.title': 'Revoke waiver',
  'alerts.restore.body':
    'The exception goes away and the alert counts towards the Trust Score again from the next scan.',
  'alerts.restore.submit': 'Revoke',

  'alerts.error.denied': 'You are not allowed to waive alerts.',
  'alerts.error.failed': 'Saving failed. Try again.',

  /* ── C59 · the 26 rules. ── */
  'alert.member_no_rate.body':
    '{subject} has no valid rate card covering the period logged on this project, so their hours go into every figure at zero cost.',
  'alert.member_no_sched.body':
    '{subject} has no work schedule, so they carry zero daily capacity in every workload and capacity figure.',
  'alert.task_multi_line.body':
    "{subject}'s hours match {count} line item rules at once. While each of them matches, the same time shows up in every one of those line items' revenue and budget burn.",
  'alert.task_no_line.body':
    'Time was logged on {subject}, but the task is attached to no contract line item, so those hours fall out of the settlement entirely.',
  'alert.task_unrouted.body':
    '{subject} arrived from the {remote} tracker project that a line item of this project targets, but no line item’s rule claims it, so it sits on no project.',
  'alert.task_orphaned.body':
    '{subject} kept its line item and its hours, but the rule that filed it there no longer claims it.',
  'alert.li_no_budget.body':
    '{subject} has no budget set, so there is nothing to measure the spend against.',
  'alert.contract_expired_log.body':
    '{hours} hours arrived after {subject} ended, and there is nothing left to invoice them against.',
  'alert.wlog_duplicate.body':
    '{subject} — the same {hours} hours appear twice on the same day, the same task and with the same note. One of the two entries is very likely a duplicate.',
  'alert.inactive_member_log.body':
    '{subject} is no longer an active member of this project, yet {hours} hours land on them, charging the spend to somebody who is not on the team.',
  'alert.ext_cost_unlinked.body':
    '{subject} is attached to no line item and no support period, so it only ever shows up in the project total.',
  'alert.license_not_invoiced.body':
    '{subject} has {periods} closed periods with no invoice against them, so their fee never appears in the month.',
  'alert.task_no_assignee.body':
    '{subject} has no assignee, so it appears in nobody’s workload and falls out of the capacity plan.',
  'alert.over_invoiced.body':
    '{subject} has been invoiced {over} beyond the revenue that can be recognised on it.',
  'alert.task_no_estimate.body':
    '{subject} has no estimate, so it falls out of both the frame and the earned value — which makes the project look better than it is.',
  'alert.task_no_dates.body':
    '{subject} is missing a start or a due date, so it can never be overdue and lands in no time bucket.',
  'alert.li_no_window.body':
    '{subject} has no start and end date, so there is nothing to measure the schedule against.',
  'alert.li_no_rate_tm.body':
    '{subject} settles on time and materials but carries no hourly or daily rate, so no revenue can be computed from the logged hours.',
  'alert.fx_missing.body':
    "{subject} has no rate against the workspace's base currency, so the system adds two currencies together one to one.",
  'alert.alloc_no_rate.body':
    '{subject} is allocated {hours} hours but has no valid rate card, so the allocation silently drops out of planned spend.',
  'alert.wlog_outside_li.body':
    '{subject} — the hour falls on a day the line item it belongs to ({lineItem}) does not cover.',
  'alert.wlog_no_occ.body':
    '{subject} — the hour belongs to {lineItem} but falls into none of its recurring periods, so it is missing from the monthly settlement.',
  'alert.wlog_multi_occ.body':
    '{subject} — the hour falls into {count} recurring periods because they overlap, so it is counted twice.',
  'alert.dep_date_violation.body':
    '{subject} — the successor starts before {blocker} finishes, so the schedule assumes an order the dates cannot deliver.',
  'alert.stale_progress.body':
    '{subject} has been in progress for {days} working days with nothing happening on it — yet the half-done state still claims half the work.',
  'alert.log_on_todo.body':
    '{subject} has {hours} hours on it while the task has not started — those hours raise the spend and earn no progress.',
  'alert.no_recent_logs.body':
    'No time has arrived on {subject} for {days} working days, so every forecast is computed from stale actuals.',
  'alert.plan_thin.body':
    '{subject} has {planned} allocated out of a {frame} frame, so the dashboard falls back to the frame as its plan baseline.',
  'alert.li_closed_work.body':
    '{subject} was closed on {closedOn}, yet {tasks} tasks and {hours} hours have reached it since.',
  'alert.wlog_unidentified.body':
    '{subject} logged {hours} hours ({from} – {to}) that no line item takes: the task’s line items name other people, or name them only for other dates.',
  'alert.wlog_unestimated_share.body':
    'On {subject}, {share} of the spend is on tasks with no estimate.',
  'alert.wlog_unbound_share.body': 'On {subject}, {share} of the hours bill to no line item.',
  'alert.li_no_tasks.body': 'On {subject}, line items with logged hours but no tasks: {count}.',
  'alert.task_reestimated_closed.body':
    'On {subject}, closed tasks whose estimate changed after they closed: {count}.',
  'alert.li_rule_on_status.body':
    'On {subject}, routing conditions filtering on status or assignee: {count}.',
  'alert.alloc_stale.body':
    'On {subject}, {share} of the remaining allocation is on people who logged nothing here for two weeks.',
  'alert.li_over_plan.body': 'On {subject}, a line item is planned at {ratio}× its budget.',
  'alert.member_silent.body':
    'On {subject}, people allocated 3+ working days in the last two weeks who logged nothing: {count}.',

  /* ── C60 · RISK ───────────────────────────────────────────────────── */
  'risk.label': 'Trust Score',
  'risk.band.ok': 'fine',
  'risk.band.watch': 'watch',
  'risk.band.risky': 'at risk',
  'risk.tip':
    "Trust Score {score} · {band} — 100 is best, 0 is worst. The project's Alerts tab shows what it is made of.",
  'risk.unscanned': 'No scan has run on this project yet, so it has no Trust Score.',
  'risk.none': '–',
  'projects.column.risk': 'Trust Score',
  'projects.view.data': 'Data problem',
  'projects.view.archived': 'Archived projects',
  'trackerConnection.title': 'Your hours are not reaching Jira',
  'trackerConnection.body':
    'This task syncs with Jira, but you have not connected your own Jira account ({names}), so the time you log stays here and does not go across. Once you connect, the waiting hours go out too.',
  'trackerConnection.cta': 'Go to connection settings',
  'account.connections.warning':
    'Until you connect your account, the time you log on tasks that sync with Jira stays here and does not reach Jira.',
  'trackerConnection.missing': 'Your Jira account is not connected',
  'projects.filter.risk': 'Trust Score',
  'projects.filter.risk.any': 'All',
  'projects.filter.risk.bad': 'At risk (0–59)',
  'projects.filter.risk.warn': 'Watch (60–84)',
  'projects.filter.risk.ok': 'Fine (85+)',

  /* ── C62 · Looking back ─────────────────────────────────────────────── */
  'tt.open': 'Look back — daily snapshot',
  'tt.loading': 'Reading the project as it was on {date}…',
  'tt.loadingLive': 'Reading the project as it is today…',
  'tt.slow': 'Rebuilding a past day can take up to 15 seconds — please wait.',
  'eac.mode.budget': 'Plan',
  'eac.mode.trend': 'Trend',
  'eac.mode.schedule': 'Schedule-adjusted',
  'eac.mode.resource': 'Resource-based',
  'eac.mode.adjusted': 'Adjusted resource-based',
  'eac.hint.budget':
    'What slipped so far was a one-off; the rest goes as estimated. The optimistic floor.',
  'eac.hint.trend':
    'Today’s efficiency holds to the end. The best single formula past 30% complete.',
  'eac.hint.schedule':
    'The slip turns into cost: overtime, extra people, reopened work. The ceiling.',
  'eac.hint.resource': 'People log exactly what they are allocated — the plan at face value.',
  'eac.hint.adjusted': 'People log at their record so far (r = logged ÷ allocated).',
  'eac.row': 'EAC · {mode}',
  'eac.reason.notStable': 'CPI has not settled: under 30% complete. Unlocks past 30%.',
  'eac.reason.unestimated': 'Over 25% of the hours are on unestimated tasks — estimate them.',
  'eac.reason.noCost': 'Nothing has been logged yet.',
  'eac.reason.singleItemOverPlan':
    'The only line item is planned past 1.5× its budget — check what is routed to it.',
  'eac.reason.trendDisabled': 'Builds on Trend, which is unavailable.',
  'eac.reason.planThin':
    'The allocation is too thin and planned value falls back to elapsed time — allocate at least half the budget.',
  'eac.reason.itemOverPlan':
    'A line item is planned past 1.5× its budget, which distorts the schedule.',
  'eac.reason.allocationEnded': 'The allocation has ended with work still left — extend it.',
  'eac.reason.underAllocated':
    'The remaining allocation covers under half the hours needed — allocate more.',
  'eac.reason.noSchedule': 'A planned person has no work schedule — assign one.',
  'eac.reason.noCostRate': 'A planned person has no cost rate — give them a rate card.',
  'eac.reason.resourceDisabled': 'Builds on Resource-based, which is unavailable.',
  'eac.reason.noHistory':
    'Not enough history yet: it needs 4 weeks and 40 allocated hours from the same people.',
  'eac.reason.rOutOfRange':
    'The realisation rate ({r}) falls outside 0.5–1.5 — the history is distorted (a placeholder allocation or wrong routing).',
  'eac.from': 'from {mode}',
  'tt.higher': 'higher than then',
  'tt.lower': 'lower than then',
  'tt.same': 'unchanged',
  'tt.openHint': "The project's earlier daily states",
  'tt.back': 'Back to the present',
  'tt.backHint': 'Leave the historical view',
  'tt.delta': 'Difference from today',
  'tt.deltaHint': "Today's value under each figure",
  'tt.lockedAction': 'Locked in a historical view',
  'tt.track': 'Snapshot timeline',
  'tt.present': 'Now',
  'tt.prev': 'Previous snapshot',
  'tt.next': 'Next snapshot',
  'tt.pick': 'Choose a snapshot from the list',
  'tt.search': 'Jump to a date…',
  'tt.searchEmpty': 'No such day.',
  'tt.now': 'NOW {value}',
  'tt.change.taskCreate': 'Task added',
  'tt.change.taskEstimate': 'Task estimate changed',
  'tt.change.taskDelete': 'Task deleted',
  'tt.change.lineItemCreate': 'Line item added',
  'tt.change.lineItemUpdate': 'Line item budget changed',
  'tt.change.lineItemDelete': 'Line item deleted',
  'tt.change.contractCreate': 'Contract added',
  'tt.change.contractUpdate': 'Contract changed',
  'tt.change.contractDelete': 'Contract deleted',
  'tt.change.allocation': 'Allocation changed',
  'tt.change.other': 'The plan moved',
  'notif.someone': 'Someone',
  'notif.leave.requested': '{actor} requested leave',
  'notif.leave.approved': '{actor} approved your leave',
  'notif.leave.rejected': '{actor} rejected your leave request',
  'notif.leave.withdrawn': '{actor} withdrew their leave request',
  'notif.leave.revoked': '{actor} withdrew their approved leave',
  'notif.leave.cancelled': '{actor} cancelled your approved leave',
  'notif.overtime.requested': '{actor} claimed overtime',
  'notif.overtime.approved': '{actor} approved your overtime',
  'notif.overtime.rejected': '{actor} rejected your overtime claim',
  'notif.overtime.withdrawn': '{actor} withdrew their overtime claim',
  'account.notif.group.tasks': 'Tasks',
  'account.notif.group.requests': 'Leave and overtime',
  'account.notif.event.approval': 'Requests waiting for me',
  'account.notif.event.approvalHint':
    'Leave and overtime requests sent to you, their withdrawals, and reminders.',
  'account.notif.event.request': 'My requests',
  'account.notif.event.requestHint':
    'Decisions on your requests, a cancelled approval, the day before leave starts, and the year-end allowance.',
  'account.notif.event.receipt': 'Receipts',
  'account.notif.event.receiptHint':
    'A copy of what you did: a request submitted, approved or rejected. Email only.',
  'account.notif.emailOnly': 'Email only',
  'account.notif.group.time': 'Time logging',
  'account.notif.group.workspace': 'Finance and integrations',
  'account.notif.event.logging': 'My missing hours',
  'account.notif.event.loggingHint':
    'On Friday when the week is short, and two working days before the month closes. Email only.',
  'account.notif.event.team': 'My team’s missing hours',
  'account.notif.event.teamHint':
    'On Friday about your projects’ members and your reports, when anyone is short. Email only.',
  'account.notif.event.finance': 'Finance',
  'account.notif.event.financeHint':
    'Billable items on Mondays, and when an invoice is voided. Email only.',
  'account.notif.event.integrations': 'Integrations',
  'account.notif.event.integrationsHint':
    'When a sync keeps failing, or you need to reconnect. Email only.',
  'notif.contract.ending': 'The contract ends in {days} days',
  'notif.contract.endedOpen': 'The contract has ended but is still active',
  'notif.contract.budget': '{percent}% of the contract’s frame is spent',
  'notif.contract.budgetSpent': 'The contract’s frame is spent',
  'notif.contract.burnEarly': 'The frame runs out before the contract ends',
  'notif.contract.owner': '{actor} made you the contract’s owner',
  'notif.project.riskWorsened': 'The project’s Trust Score dropped',
  'notif.project.added': '{actor} added you to the project',
  'notif.project.pm': '{actor} made you the project’s PM',
  'notif.project.closed': '{actor} closed the project',
  'account.notif.group.projects': 'Projects and contracts',
  'account.notif.event.project': 'My projects',
  'account.notif.event.projectHint':
    'Added to a project, made its PM, its Trust Score dropped, or it was closed.',
  'account.notif.event.alerts': 'Daily project alerts',
  'account.notif.event.alertsHint':
    'New alerts on the projects you lead, once a day at your reminder time. Email only.',
  'account.notif.event.contract': 'My contracts',
  'account.notif.event.contractHint':
    'Ending soon, the frame running low or out early, and becoming the owner.',
  /* ── ux-pass-2 · the one history and the audit log (`screens/history/`). See the Hungarian side. */
  'settings.nav.audit': 'Audit log',
  'title.settingsAudit': 'Audit log',
  'history.title': 'History',
  'history.search': 'Search: person, identifier, name, action…',
  'history.refresh': 'Refresh',
  'history.open': 'Open',
  'history.system': 'System',
  'history.column.at': 'When',
  'history.column.actor': 'Who',
  'history.column.entity': 'What',
  'history.column.action': 'Action',
  'history.column.change': 'Change',
  'history.filter.kind': 'Action',
  'history.filter.actor': 'Who',
  'history.filter.entity': 'Type',
  'history.filter.at': 'Period',
  'history.kind.create': 'Created',
  'history.kind.update': 'Changed',
  'history.kind.delete': 'Deleted',
  'history.kind.other': 'Other',
  'history.channel.ui': 'App',
  'history.channel.api': 'API',
  'history.channel.agent': 'Agent',
  'history.channel.mcp': 'MCP client',
  'history.summary.more': '+{count} fields',
  'history.empty.title': 'No history yet',
  'history.empty.body':
    'Every creation, change and deletion appears here – who, when, and what it was before and after.',
  'history.noMatch.title': 'Nothing matches',
  'history.noMatch.body': 'No entry matches these filters.',
  'history.detail.title': 'Details',
  'history.detail.back': 'Back to the history',
  'history.detail.channel': 'Channel',
  'history.detail.field': 'Field',
  'history.detail.before': 'Before',
  'history.detail.after': 'After',
  'history.detail.note': 'Text',
  'history.detail.noChanges': 'This action changed no field.',
  'history.entity.task': 'Task',
  'history.entity.comment': 'Comment',
  'history.entity.worklog': 'Worklog',
  'history.entity.project': 'Project',
  'history.entity.project_member': 'Project member',
  'history.entity.allocation': 'Allocation',
  'history.entity.client': 'Client',
  'history.entity.contract': 'Contract',
  'history.entity.line_item': 'Line item',
  'history.entity.period': 'Period',
  'history.entity.invoice': 'Invoice',
  'history.entity.external_cost': 'External cost',
  'history.entity.person': 'Person',
  'history.entity.leave_request': 'Leave request',
  'history.entity.overtime_request': 'Overtime request',
  'history.entity.file': 'File',
  'history.entity.report': 'Report',
  'history.entity.metafield': 'Metafield',
  'history.entity.role': 'Role',
  'history.entity.integration': 'Integration',
  'history.entity.settings': 'Setting',
  'history.entity.workspace': 'Workspace',
  'history.entity.deal': 'Deal',
  'history.entity.contact': 'Contact',
  'history.entity.other': 'Other',
  'history.action.reorder': 'reordered',
  'history.action.revoke': 'revoked',
  'history.action.export': 'exported',
  'history.action.import': 'imported',
  'history.action.restore': 'restored',
  'history.action.archive': 'archived',
  'history.action.attach': 'attached',
  'history.action.detach': 'detached',
  'history.action.sync': 'synced',
  'history.action.link': 'linked',
  'history.action.unlink': 'unlinked',
  'history.action.decide': 'decided',
  'history.action.request': 'requested',
  'history.action.comment': 'commented',
  'history.action.commentEdit': 'comment edited',
  'history.action.commentDelete': 'comment deleted',
  'history.action.assign': 'assigned',
  'history.action.unassign': 'unassigned',
  'history.action.memberAdd': 'member added',
  'history.action.memberRemove': 'member removed',
  'history.action.memberUpdate': 'membership changed',
  'history.action.duplicate': 'duplicated',
  'history.action.setDefault': 'made the default',
  'history.action.activate': 'activated',
  'history.action.deactivate': 'deactivated',
  'history.action.invite': 'invited',
  'history.action.setValues': 'values set',
  'history.action.depend': 'dependency added',
  'history.action.undepend': 'dependency removed',
  'history.action.raise': 'raised',
  'history.action.dismiss': 'dismissed',
  'history.action.install': 'installed',
  'history.action.uninstall': 'uninstalled',
  'history.action.pause': 'paused',
  'history.action.resume': 'resumed',
  'history.action.rolesSet': 'role set',
  'history.action.onboarded': 'onboarded',
  'history.field.title': 'Title',
  'history.field.name': 'Name',
  'history.field.givenName': 'Given name',
  'history.field.familyName': 'Family name',
  'history.field.email': 'Email',
  'history.field.phone': 'Phone',
  'history.field.status': 'Status',
  'history.field.priority': 'Priority',
  'history.field.assignee': 'Assignee',
  'history.field.project': 'Project',
  'history.field.client': 'Client',
  'history.field.contract': 'Contract',
  'history.field.estimate': 'Estimate',
  'history.field.startsOn': 'Start',
  'history.field.endsOn': 'End',
  'history.field.dueOn': 'Due date',
  'history.field.lineItem': 'Line item',
  'history.field.parentTask': 'Parent task',
  'history.field.description': 'Description',
  'history.field.amount': 'Amount',
  'history.field.currency': 'Currency',
  'history.field.budget': 'Budget',
  'history.field.revenue': 'Revenue',
  'history.field.price': 'Price',
  'history.field.unitPrice': 'Unit price',
  'history.field.quantity': 'Quantity',
  'history.field.unit': 'Unit',
  'history.field.type': 'Type',
  'history.field.owner': 'Owner',
  'history.field.pm': 'PM',
  'history.field.role': 'Role',
  'history.field.team': 'Team',
  'history.field.manager': 'Manager',
  'history.field.tracksTime': 'Tracks time',
  'history.field.leaveType': 'Leave type',
  'history.field.overtimeType': 'Overtime type',
  'history.field.from': 'From',
  'history.field.to': 'To',
  'history.field.hours': 'Hours',
  'history.field.workedOn': 'Day',
  'history.field.note': 'Note',
  'history.field.body': 'Text',
  'history.field.tags': 'Tags',
  'history.field.metafields': 'Custom fields',
  'history.field.color': 'Colour',
  'history.field.code': 'Code',
  'history.field.recurrence': 'Recurrence',
  'history.field.assignment': 'Assignment',
  'history.field.active': 'Active',
  'history.field.issuedOn': 'Issued on',
  'history.field.paidOn': 'Paid on',
  'history.field.incurredOn': 'Incurred on',
  'history.field.number': 'Number',
  'history.field.employmentType': 'Employment type',
  'history.field.weeklyHours': 'Weekly hours',
  'history.field.settlement': 'Settlement',
  'history.field.multiplier': 'Multiplier',
  'history.field.isDefault': 'Default',
  'history.field.position': 'Position',
  'history.field.rate': 'Rate',
  'history.field.billable': 'Billable',
  'history.field.member': 'Person',
  'history.field.invoice': 'Invoice',
  'history.field.period': 'Period',
  ...DEALS_BASE_EN,
  ...DEALS_SETTINGS_EN,
  ...DEALS_BOARD_EN,
  ...DEALS_RECORD_EN,
  ...DEALS_PLAN_EN,
  ...DEALS_GENERATE_EN,
  ...FX_EN,
  ...DURATION_EN,
  ...DEALS_SCENARIO_EN,
};

export const CATALOGUES = { hu: HU as Catalogue, en: EN } as const;
export type Locale = keyof typeof CATALOGUES;
export const LOCALES: readonly Locale[] = ['hu', 'en'];
